import { createLedgerSafeEditSuggestionBuilder } from '../../lib/ledgerSafeEditSuggestions'
import React, { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  Anchor as AnchorIcon,
  BookOpen,
  Check,
  FileUp,
  GitBranch,
  Minus,
  MoreHorizontal,
  MoveHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Plus,
  Sparkles,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { AppStateBackupControls } from '../shared/AppStateBackupControls'
import { LedgerVerseCell } from './LedgerVerseCell'
import { LedgerVerseColumn } from './LedgerVerseColumn'
import './LedgerHeading.css'
import { LedgerRowHeading, type LedgerRowTitlePresentation } from './LedgerRowHeading'
import { formatLedgerCitation } from '../../lib/ledgerHeadingTypography'
import { verseSplitActions } from '../../lib/ledgerVerseEditing'
import { CharacterIndexEntryCard } from './CharacterIndexEntryCard'
import AgentCameraLucidaView, { type AgentCameraLucidaViewProps } from './AgentCameraLucidaView'
import { LedgerSafeFilteredRowRun } from './LedgerSafeFilteredRowRun'
import { LedgerSafeTraditionReaderWithStructure } from './LedgerSafeTraditionReaderWithStructure'
import type {
  CoreferenceChainV2FigureKind as CharacterIndexFigureKind,
  CharacterExtractionScope,
  PreparedSingleWitnessCoreferenceChainV2WindowCall,
  PreparedSingleWitnessCoreferenceChainWindowCall,
  PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
  PreparedSingleWitnessCharacterExtractionCall
} from '@variorum/character-index'
import type { CorpusAnalysisResult } from '../../hooks/useCorpusAnalysis'
import {
  requestWorkbenchPropagationPreview,
  WorkbenchPropagationPreviewError,
  requestWorkbenchTranslationBatchPreview,
  type TranslationBatchResumeState,
  type WorkbenchThinkingLevel,
  type WorkbenchPropagationDiagnostics,
  type WorkbenchPropagationFailure,
  WorkbenchTranslationBatchPreviewError,
} from '../../api/workbenchPropagationAPI'
import {
  requestWorkbenchSemanticAlignmentPreview,
  type WorkbenchSemanticAlignmentCandidate,
  type WorkbenchSemanticAlignmentPreview
} from '../../api/workbenchSemanticAlignmentAPI'
import {
  cancelWorkbenchCrossTextAlignmentPreview,
  requestWorkbenchCrossTextAlignmentPreview
} from '../../api/workbenchCrossTextTraditionAlignmentAPI'
import {
  createWorkbenchCharacterIndexDetectorAdapter,
  createWorkbenchCoreferenceChainDetectorAdapter,
  createWorkbenchCoreferenceChainV2DetectorAdapter,
  createWorkbenchFocalizationCharacterizationDetectorAdapter
} from '../../api/workbenchCharacterIndexAPI'
import {
  adaptPackageCoreferenceChainsToDetectorOutput,
  PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT,
} from '../../lib/characterIndexCoreferenceOutputAdapter'
import {
  adaptPackageCoreferenceChainV2ToDetectorOutput,
  adaptPackageFocalizationCharacterizationToDetectorOutput,
  PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
  PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
} from '../../lib/characterIndexLayeredOutputAdapters'
import {
  buildCharacterIndexSyntheticFigures,
  type CharacterIndexSyntheticFigureSourceRow,
} from '../../lib/characterIndexSyntheticFigures'
import {
  adaptPackageCharacterEvidenceToDetectorOutput,
  PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT,
} from '../../lib/characterIndexPreparedOutputAdapter'
import {
  CHARACTER_INDEX_EXTERNAL_MODEL_PAYLOAD_KIND,
  extractCharacterIndexExternalRawExtraction,
  extractCharacterIndexExternalResultWindows,
  parseCharacterIndexExternalClipboardJson,
  type CharacterIndexExternalModelPayload,
} from '../../lib/characterIndexExternalModelExchange'
import {
  requestStagedReferenceAlignmentSceneMetadata,
  requestStagedReferenceAlignmentWitnessReplay,
  type StagedReferenceAlignmentSceneMetadata
} from '../../api/stagedReferenceAlignmentAPI'
import { dbStorage } from '../../lib/indexedDBStorage'
import { persistLedgerSafeSegmentationEditAtomically } from '../../lib/ledgerSafeSegmentationEditPersistence'
import {
  loadLedgerSafeFragmentAnchorDrafts,
  saveLedgerSafeFragmentAnchorDrafts
} from '../../lib/ledgerSafeFragmentAnchorStore'
import type { WitnessLabel } from '../../lib/perWorkSiglum'
import {
  abortPiMatrixAgentAgent,
  applyPiMatrixAgentPreview,
  createPiMatrixAgentAgentSession,
  createPiMatrixAgentSession,
  getPiMatrixAgentChangeSet,
  getPiMatrixAgentProposal,
  getPiMatrixAgentAgentReport,
  getPiMatrixAgentOAuthLoginStatus,
  invokePiMatrixAgentTool,
  listPiMatrixAgentModels,
  listPiMatrixAgentOAuthProviders,
  promptPiMatrixAgentAgent,
  promptPiMatrixAgentAgentStream,
  requestPiStructuredGeneration,
  startPiMatrixAgentOAuthLogin,
  submitPiMatrixAgentOAuthLoginInput,
  syncPiMatrixAgentSnapshot
} from '../../api/piMatrixAgentAPI'
import {
  assertPiMatrixAgentPatchCurrent,
  isPiMatrixAgentProposalCurrent,
} from '../../lib/piMatrixAgentSnapshot'
import {
  LedgerSafeMatrixWindowSection,
  useLedgerSafeMatrixWindow,
} from '../../hooks/useLedgerSafeMatrixWindow'
import { useScheduledPiMatrixAgentSnapshot } from '../../hooks/useScheduledPiMatrixAgentSnapshot'
import {
  buildCameraLucidaAgentPrompt,
  cameraLucidaEventFromPiStream,
  changeSetIdFromPiStream,
  createCameraLucidaCompletionGate,
  proposalIdFromPiStream,
} from '../../lib/cameraLucidaAgent'
import { persistCameraLucidaInquiry } from '../../lib/cameraLucidaPersistence'
import {
  getLedgerSafeAuthorityHead,
  listLedgerSafeAgentChangeSets,
  persistLedgerSafeAgentChangeSet,
  synchronizeLedgerSafeAuthorityHead,
} from '../../lib/ledgerSafeAgentChangeSetStore'
import {
  buildLedgerSafeAgentPatch,
  commitPreparedLedgerSafeAgentApplication,
  commitPreparedLedgerSafeAgentUndo,
  prepareLedgerSafeAgentApplication,
  prepareLedgerSafeAgentUndo,
} from '../../lib/ledgerSafeAgentChangeSetApplication'
import { buildPiMatrixAgentChangeSetPreview } from '../../lib/piMatrixAgentChangeSetPresentation'
import { useBooksStore, type Book, type Chapter, type Witness } from '../../store/booksStore'
import { useAnchoredRowsStore } from '../../store/anchoredRowsStore'
import { useCharacterLayerStore } from '../../store/characterLayerStore'
import { useSceneStore } from '../../store/sceneStore'
import { useStructuralRangeStore } from '../../store/structuralRangeStore'
import { getRowLabel, type AnchoredRow, type AnchoredRowSceneMetadata, type UnitMapping } from '../../types/anchoredRowTypes'
import type {
  CharacterEntity,
  CharacterLayerDetectionRow,
  CharacterLayerDetectorAdapter,
  CharacterLayerDetectorOutput,
  CharacterLayerNoteAnchor,
  ChapterCharacterLayer,
  CharacterOccurrence
} from '../../types/characterLayerTypes'
import type {
  PiMatrixAgentAgentReport,
  PiMatrixAgentAgentReply,
  PiMatrixAgentAgentSession,
  PiMatrixAgentChangeSet,
  PiMatrixAgentModelOption,
  PiMatrixAgentOAuthLoginAttempt,
  PiMatrixAgentOAuthProvider,
  PiMatrixAgentPatch,
  PiMatrixAgentPreview,
  PiMatrixAgentPreviewEnvelope,
  PiStructuredGenerationExecution,
  PiMatrixAgentThinkingLevel,
  PiMatrixAgentWindowCell
} from '../../types/piMatrixAgent'
import type {
  LedgerSafeAgentChangeSetRecord,
  LedgerSafeAuthorityHeadRecord,
} from '../../types/ledgerSafeAgentChangeSets'
import type { CameraLucidaEvent, CameraLucidaFinding } from '../../types/cameraLucidaTypes'
import {
  STRUCTURAL_RANGE_KINDS,
  LEGACY_STRUCTURAL_RANGE_KINDS,
  type ChapterStructuralRangeLayer,
  type StructuralRange,
  type StructuralRangeKind,
} from '../../types/structuralRangeTypes'
import { STRUCTURAL_RANGE_KIND_HELP } from '../../lib/structuralRangeTaxonomy'
import type { MicroUnit } from '../../types/segmentationTypes'
import { isMorphNearMatch } from '../../lib/arabicMorph'
import { buildRichInlineOverlay, type EditOp, type RichOverlayToken } from '../../lib/diffUtils'
import { tokenizeComparisonWords } from '../../lib/comparisonTextCleaner'
import {
  buildWitnessUnitTextIndex,
  preprocessLedgerText,
  type WitnessUnitTextRecord
} from '../../lib/ledgerTextPreprocessing'
import { buildUnitReferenceIndexWithRemap } from '../../lib/manualUnitIdentity'
import { rebaseCharacterLayerAfterSegmentationEdit } from '../../lib/characterLayerSegmentationRebase'
import {
  buildApparatusOverlayTokens,
  buildApparatusSpans,
  type ApparatusOverlayToken,
  type ApparatusSpan,
  type ApparatusVariantKind
} from '../../lib/apparatusDiff'
import {
  applyLedgerSafeUnitSplitToSegmentation,
  applyLedgerSafeMergeUnitsToSegmentation,
  applyLedgerSafeBoundaryShiftToSegmentation,
  createLedgerSafeUnitSplitContext,
  previewLedgerSafeUnitSplit,
  previewLedgerSafeBoundaryShift,
  buildLedgerSafeEditSuggestionActionKey,
  buildLedgerSafeFragmentAnchorDraft,
  buildLedgerSafeFragmentAnchorKey,
  inferLedgerSafeFragmentAnchorsFromSequenceAudit,
  indexLedgerSafeEditSuggestionsByCell,
  reconcileLedgerSafeActiveWitnessProjection,
  reconcileLedgerSafeBoundaryEdit,
  applyLedgerSafeAutoPlaceFragments,
  compactLedgerSafeEmptyRows,
  applyLedgerSafeClearGap,
  applyLedgerSafeDetachMapping,
  applyLedgerSafeDetachMappings,
  applyLedgerSafeDetachRetiredMaterial,
  applyLedgerSafeRetainEmptyRowAfterReview,
  applyLedgerSafeInsertRowForFragment,
  applyLedgerSafeInsertRowAndMove,
  applyLedgerSafeMarkGap,
  applyLedgerSafeMarkTransposition,
  applyLedgerSafeMoveMapping,
  applyLedgerSafePlaceFragmentInRow,
  applyLedgerSafeRelocateRange,
  applyLedgerSafeRetiredMaterialDisposition,
  applyLedgerSafeShiftRange,
  applyLedgerSafeUnmarkTransposition,
  buildLedgerSafeRetiredMaterialAudit,
  deleteLedgerSafeEmptyRowAfterReview,
  getLedgerSafeCellState,
  isLedgerSafeRowTrulyEmpty,
  removeLedgerSafeFragmentAnchors,
  resolveLedgerSafeFragmentAnchors,
  selectLedgerSafeRowEditUpserts,
  summarizeLedgerSafeEmptyRowRuns,
  upsertLedgerSafeFragmentAnchor,
  type LedgerSafeBoundaryContext,
  type LedgerSafeBoundaryPreview,
  type LedgerSafeUnitSplitContext,
  type LedgerSafeUnitSplitPreview,
  type LedgerSafeEditSuggestion,
  type LedgerSafeUnassignedFragment,
  type LedgerSafeCellState,
  type LedgerSafeEmptyReadingRowIssue,
  type LedgerSafeEmptyRowRun,
  type LedgerSafeRetiredMaterialIssue,
  type LedgerSafeFragmentAnchorDraft,
  type LedgerSafeActiveWitnessProjectionObserver,
  type LedgerSafeActiveWitnessProjectionState,
  type LedgerSafeSelectedCell,
  type LedgerSafeSequenceOffender,
  type LedgerSafeSequenceUnplacedUnit
} from '../../lib/ledgerSafeCore'
import {
  applyWorkbenchBuildArtifactWitnessReplay,
  buildAppliedWitnessAuditPlan,
  buildAppliedWitnessRowAssignments,
  buildSelectedUnitPropagationRepairApplication,
  buildSelectedUnitPropagationRepairPreview,
  buildWorkbenchAppliedAuditPreview,
  buildWorkbenchPropagationApplication,
  buildWorkbenchPropagationPreview,
  buildWitnessScopedPropagationApplication,
  type SelectedUnitPropagationRepairPreview,
  type WorkbenchPropagationApplication,
  type WorkbenchPropagationPreview,
  type WorkbenchPropagationRowAssignment
} from '../../lib/workbenchWorkflowBoundary'
import {
  deriveTranslationPropagationPlan,
  summarizeTranslationFragmentDiagnostics,
  type TranslationCharAssignment,
} from '../../lib/workbenchTranslationAlignment'
import {
  buildLedgerSafeTranspositionDisplayModel,
  type LedgerSafeTranspositionDisplayRow,
} from '../../lib/ledgerSafeTranspositionProjection'
import {
  reconcileLedgerSafeRowProjection,
  type LedgerSafeRowProjection,
} from '../../lib/ledgerSafeRowProjection'
import {
  deriveLedgerSafeHiddenWitnessRowRuns,
  projectLedgerHeadersPastHiddenRows,
  type LedgerSafeHiddenWitnessRowRun,
} from '../../lib/ledgerSafeFilteredRowProjection'
import {
  captureLedgerSafeViewportAnchor,
  restoreLedgerSafeViewportAnchor,
  type LedgerSafeViewportAnchor,
} from '../../lib/ledgerSafeViewportAnchor'
import {
  buildManualStudioScopeOptions,
  describeManualStudioScope
} from '../../lib/manualStudioScopeOptions'
import {
  buildLedgerSafeEditorialRowSnapshots,
  createLedgerSafeChapterSnapshot,
  getLatestLedgerSafeChapterSnapshot,
  listLedgerSafeChapterSnapshots,
  listLedgerSafeEditorialEventsForChapter,
  recordLedgerSafeEditorialEvent
} from '../../lib/ledgerSafePersistenceBoundary'
import { enqueueLedgerSafePostgresTrialShadowEdit } from '../../lib/ledgerSafePostgresTrialShadow'
import { resolveStudioAnchorRows } from '../../lib/manualAnchorStoreUtils'
import { createLatestOperationGate } from '../../lib/latestOperationGate'
import { buildLedgerSafePropagationOracleExport } from '../../lib/ledgerSafeImportExportBoundary'
import {
  hasManualOrUnknownSegmentation,
  isAutoPropagatedSegmentation
} from '../../lib/manualStudioPropagationGuards'
import {
  applyLedgerColumnFilterTemplate,
  buildLedgerColumnFilterTemplate,
  parseLedgerColumnFilterTemplates,
  sanitizeLedgerColumnHiddenWitnessIds,
  parseStoredHiddenWitnessIds,
  serializeLedgerColumnFilterTemplates,
  type LedgerColumnFilterTemplate
} from '../../lib/ledgerColumnFilters'
import {
  buildLedgerVarianceCacheKey,
  getLedgerVarianceMetrics,
  type LedgerVarianceMetrics
} from '../../lib/ledgerOccupancyVariance'
import { safeStorageGet, safeStorageSet } from '../../lib/safeStorage'
import {
  applyCollationRowTitleUpdates,
  applyRoleAwareSidecarRepair,
  buildSidecarTitleUpdates,
  clearCollationRowTitles,
  getCollationRowParallelTitle,
  getCollationRowPrimaryTitle,
  parseCollationRowTitleSidecar,
  previewCollationRowTitleSidecar,
  type CollationRowTitleSidecarMatch,
  type CollationRowTitleSidecarMatchStatus,
  type CollationRowTitleSidecarPreview,
} from '../../lib/ledgerSafeRowTitles'
import {
  ROW_TITLE_GENERATION_BATCH_SIZE,
  appendLedgerSafeRowTitleGenerationBatch,
  buildRowTitleStructuredGenerationRequest,
  clearLedgerSafeRowTitleGenerationRun,
  createLedgerSafeRowTitleGenerationRun,
  fingerprintRowTitleGenerationInput,
  loadLedgerSafeRowTitleGenerationRun,
  parseRowTitleGenerationExecution,
  saveLedgerSafeRowTitleGenerationRun,
  updateLedgerSafeRowTitleGenerationBatchStatus,
  type LedgerSafeRowTitleGenerationBatch,
  type LedgerSafeRowTitleGenerationRun,
} from '../../lib/ledgerSafeRowTitleGeneration'
import {
  STRUCTURAL_RANGE_GENERATION_MAX_ROWS,
  buildStructuralRangeStructuredGenerationRequest,
  clearLedgerSafeStructuralRangeGenerationRun,
  createLedgerSafeStructuralRangeGenerationFailure,
  createLedgerSafeStructuralRangeGenerationRun,
  describeStructuralRangeGenerationFailure,
  fingerprintStructuralRangeGenerationInput,
  loadLedgerSafeStructuralRangeGenerationRun,
  parseStructuralRangeGenerationExecution,
  saveLedgerSafeStructuralRangeGenerationRun,
  saveLedgerSafeStructuralRangeGenerationFailure,
  updateLedgerSafeStructuralRangeGenerationRunStatus,
  type LedgerSafeStructuralRangeGenerationRun,
  type StructuralRangeTitleTarget,
} from '../../lib/ledgerSafeStructuralRangeGeneration'
import {
  STRUCTURAL_RANGE_KIND_LABELS,
  buildStructuralRowOrder,
  createChapterStructuralRangeLayer,
  createStructuralRange,
  findDefaultStructuralRangeParentId,
  findStructuralRangeParentOptions,
  removeStructuralRange,
  upsertStructuralRange,
  validateStructuralRangeLayer,
} from '../../lib/structuralRanges'
import {
  previewLegacySceneStructuralRangeMigration,
  previewSidecarStructuralRangeMigration,
} from '../../lib/structuralRangeMigration'
import { getPersistedUiPreference, setPersistedUiPreference } from '../../lib/uiPreferenceStorage'
import { formatEditorialTitleForDisplay } from '../../lib/editorialTypography'
import { buildStructuralRangeDisplayNumbers, formatStructuralRangeKindLabel } from '../../lib/structuralRangeDisplay'
import {
  appendCharacterLayerNote,
  buildCharacterCellKey,
  buildCharacterCoverageSummary,
  createEmptyChapterCharacterLayer,
  createManualCharacter,
  deleteCharacterRecord,
  getActiveCharacters,
  getActiveCharacterOccurrences,
  getCharacterOccurrenceReferentIds,
  reviewCharacterEntity,
  upsertManualCharacterOccurrence,
} from '../../lib/characterLayerHelpers'
import {
  createNameSweepCharacterLayerDetectorAdapter,
  mergeCharacterLayerDetectorOutput,
  type CharacterLayerDetectorMergeReport,
} from '../../lib/characterLayerDetector'
import {
  appendDurableCharacterDetectorRun,
  CHARACTER_DETECTOR_RUN_HISTORY_LIMIT,
  createCharacterDetectorRunId,
  readDurableCharacterDetectorRuns,
} from '../../lib/characterDetectorRunHistory'
import { runBackupRelevantWrite } from '../../lib/backupMutationCoordinator'
import { projectChapterCharacterLayerToRunningIndex } from '../../lib/characterIndexPackageAdapter'
import {
  applyLectioAdjudicationCommand,
  type LectioAdjudicationCommand,
} from '../../lib/readerCharacterIndexAdjudication'
import {
  buildFocusedCoreferenceChainWindowCalls,
  prepareLedgerSafeSingleWitnessCharacterExtraction,
  prepareLedgerSafeSingleWitnessCoreferenceChainWindowExtraction,
  prepareLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction,
  prepareLedgerSafeSingleWitnessFocalizationCharacterizationExtraction,
  type FocusedCoreferenceOccurrenceInput,
  type PreparedLedgerSafeSingleWitnessCharacterExtraction,
  type PreparedLedgerSafeSingleWitnessCoreferenceChainWindowExtraction,
  type PreparedLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction,
  type PreparedLedgerSafeSingleWitnessFocalizationCharacterizationExtraction,
} from '../../lib/characterIndexExtractionPreparation'
import {
  AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT,
  appendTranslationBatchRetryAttempt,
  buildLedgerSafeSelectedRepairResumeScopeId,
  buildLedgerSafeTranslationPreviewResumeId,
  buildTranslationPreviewResumeRecord,
  isTranslationPreviewResumeRecordCurrent,
  recoverLedgerSafeRepairCandidateRowIds,
  shouldAutoRetryTranslationBatchFailure,
  summarizeTranslationBatchRetryHistory,
} from '../../lib/translationPreviewResumePersistence'
import {
  buildVisibleWitnessIds,
  moveWitnessIdInOrder,
  parseStoredWitnessOrder,
  sanitizeWitnessOrder,
  type WitnessColumnDropPlacement
} from '../../lib/witnessVisibility'
import {
  applyLedgerCrossTraditionCandidateMappings,
  buildLedgerCrossTraditionRowSpineText,
  buildLedgerCrossTraditionSourceOptions,
  buildLedgerCrossTraditionSourceText,
  buildLedgerCrossTraditionWitnessImportPlan,
  LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
  LEDGER_CROSS_TRADITION_ROW_SPINE_TEXT_ID_PREFIX,
  LEDGER_CROSS_TRADITION_SCORER,
  mapCrossTextRunToLedgerCrossTraditionCandidates,
  setLedgerCrossTraditionCandidateStatus,
  type LedgerCrossTraditionCandidate,
  type LedgerCrossTraditionSourceOption,
  type LedgerCrossTraditionWitnessImportPlan
} from '../../lib/ledgerCrossTraditionCollation'
import { getCrossTextTierLabel } from '../../lib/crossTextTraditionAlignmentReview'
import { useRuntimeFeatureFlag } from '../../config/runtimeConfig'
import type {
  LedgerSafeEditorialEvent,
  LedgerSafeEditorialEventSource,
  LedgerSafeEditorialOperationKind
} from '../../types/ledgerSafeEditorialEvents'
import type { LedgerSafeChapterSnapshot, LedgerSafeSnapshotReason } from '../../types/ledgerSafeSnapshots'
import type { CharacterIndexReaderResumeTarget } from '../../lib/characterIndexProgress'
import { shouldPreserveReaderIntentDuringScopeHydration } from '../../lib/readerStudioRoute'

interface LedgerSafeManualStudioProps {
  analysisData: CorpusAnalysisResult | null
  witnesses: Witness[]
  onSelectWitness?: (sigla: string) => void
  initialSidePanelTab?: SidePanelTab | 'structure' | 'ledger' | 'pi'
  initialPropagationMode?: LedgerSafePropagationMode
  projectLabel?: string
  contextLabel?: string
  ledgerId?: string
  ledgerName?: string
  headerActions?: React.ReactNode
  mastheadActions?: React.ReactNode
  mastheadSupplementalActions?: React.ReactNode
  ledgerAreaFloatingAction?: React.ReactNode
  readOnly?: boolean
  readOnlyRows?: AnchoredRow[]
  readOnlyStructuralRangeLayer?: ChapterStructuralRangeLayer | null
  /** Display-only per-work editorial sigla (keyed by witness.id). When present,
   * column heads show the editorial siglum and the shelfmark on hover; the
   * internal identity key stays witness.sigla. */
  witnessLabels?: Map<string, WitnessLabel>
  crossTraditionBooks?: Book[]
  crossTraditionChapters?: Chapter[]
  crossTraditionWitnesses?: Witness[]
  onCreateCrossTraditionWitness?: (plan: LedgerCrossTraditionWitnessImportPlan) => Promise<string>
  initialReaderResumeTarget?: CharacterIndexReaderResumeTarget | null
  initialViewMode?: 'ledger' | 'camera'
  onOpenReader?: (witnessId: string) => void
  onCloseReader?: () => void
  onOpenCamera?: () => void
  onCloseCamera?: () => void
  onCameraInquiry?: AgentCameraLucidaViewProps['onInquiry']
}

type CharacterNoteTargetKind = 'character' | 'span' | 'cell' | 'collation-row' | 'row-selection' | 'witness'

type CharacterSpanSelection = {
  witnessId: string
  rowId: string
  localStart: number
  localEnd: number
  selectedText: string
}

type CharacterDetectorFeedback = {
  tone: 'neutral' | 'success' | 'warning' | 'error'
  message: string
  report?: CharacterLayerDetectorMergeReport
  detectorId?: string
  diagnostics?: Record<string, any>
}

type CharacterDetectorRunStatus = 'running' | 'canceling' | 'completed' | 'failed' | 'canceled' | 'stale'

type CharacterDetectorRunModelCallContext = {
  promptText: string
  sourceText?: string
  promptVersion?: string
  sourceTextLength: number
  windowId?: string
  windowIndex?: number
  windowCount?: number
  rowLabelRange?: { start: string; end: string } | null
  sourceRowIds?: readonly string[]
}

type CharacterDetectorRunProvenance = {
  detectorId: string
  detectorLabel: string
  extractionUnit: CharacterExtractionUnit
  sourceLabel: string
  witnessLabels: string[]
  modelId?: string
  scorer?: string
  temperature?: number
  thinkingLevel?: CharacterThinkingLevel
  promptVersion?: string
  promptPreview?: string
  modelCallContexts?: CharacterDetectorRunModelCallContext[]
  figureSource?: CharacterFocalizationFigureSource
  figureCount?: number
  focusedCharacterId?: string
  focusedCharacterName?: string
  rowCount: number
  windowCount: number
  rowLabels: string[]
}

type CharacterDetectorRunResultSet = {
  characterCount: number
  occurrenceCount: number
  noteCount: number
  addedCharacterCount: number
  addedOccurrenceCount: number
  addedNoteCount: number
  skippedOccurrenceCount: number
}

type CharacterDetectorRunRecord = {
  runId: string
  runMode: CharacterDetectorRunMode
  status: CharacterDetectorRunStatus
  startedAt: string
  completedAt?: string
  resumedFromRunId?: string
  retriedFromRunId?: string
  errorMessage?: string
  provenance: CharacterDetectorRunProvenance
  resultSet?: CharacterDetectorRunResultSet
  diagnostics?: Record<string, any>
}

type CharacterExtractionUnit = 'visible-ledger' | 'witness' | 'scene' | 'rows'
type CharacterIndexListScope = 'ledger' | 'witness' | 'scene' | 'rows'
type CharacterFocalizationFigureSource = 'character-index' | 'coreference-v2'
type CharacterThinkingLevel = 'medium' | 'high'
type CharacterDetectorRunMode = 'suggest' | 'refine' | 'improve-chain' | 'focus-layers' | 'external-import'
type PreparedSingleWitnessCharacterIndexCall =
  | PreparedSingleWitnessCharacterExtractionCall
  | PreparedSingleWitnessCoreferenceChainWindowCall
  | PreparedSingleWitnessCoreferenceChainV2WindowCall
  | PreparedSingleWitnessFocalizationCharacterizationExtractionCall
type PreparedCharacterExtractionState =
  | PreparedLedgerSafeSingleWitnessCharacterExtraction
  | PreparedLedgerSafeSingleWitnessCoreferenceChainWindowExtraction
  | PreparedLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction
  | PreparedLedgerSafeSingleWitnessFocalizationCharacterizationExtraction

type CharacterExternalModelExchangeContext = {
  detectorAdapter: CharacterLayerDetectorAdapter
  runMode: CharacterDetectorRunMode
  preparedExtraction: PreparedCharacterExtractionState
  calls: PreparedSingleWitnessCharacterIndexCall[]
  detectionRows: CharacterLayerDetectionRow[]
  validationMessage: string
  focusedCharacter?: CharacterEntity | null
}

type CharacterExtractionSceneOption = {
  sceneId: string
  label: string
  title: string
  rowCount: number
}

const CHARACTER_INDEX_MODEL_OPTIONS = [
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { id: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
]

const CHARACTER_INDEX_DEFAULT_MODEL_ID = 'gemini-3-flash-preview'
const CHARACTER_INDEX_DEFAULT_TEMPERATURE = 1
const CHARACTER_INDEX_DEFAULT_THINKING_LEVEL: CharacterThinkingLevel = 'medium'

const normalizeCharacterIndexIdentifier = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
)

const isCharacterRunAbortError = (error: unknown): boolean => (
  error instanceof Error && error.name === 'AbortError'
)

const formatCharacterRunTime = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const getCharacterRunStatusClass = (status: CharacterDetectorRunStatus): string => {
  if (status === 'completed') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-800'
  }
  if (status === 'failed') {
    return 'border-red-300 bg-red-50 text-red-800'
  }
  if (status === 'canceled' || status === 'stale') {
    return 'border-amber-300 bg-amber-50 text-amber-800'
  }
  return 'border-black/15 bg-white text-gray-600'
}

const formatCharacterRunDownloadFilePart = (value: string): string => (
  value.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'run'
)

const getCharacterDetectorRefinementSourceText = (diagnostics?: Record<string, any>): string => {
  const rawText = typeof diagnostics?.raw_text === 'string' ? diagnostics.raw_text.trim() : ''
  if (rawText) {
    return rawText
  }
  if (diagnostics?.raw_extraction && typeof diagnostics.raw_extraction === 'object') {
    try {
      return JSON.stringify(diagnostics.raw_extraction, null, 2)
    } catch {
      return ''
    }
  }
  return ''
}

type CoreferenceV2FocalizationFigureResolution = {
  figures: any[]
  message: string
  windowCount: number
  matchedWindowId?: string
}

type CoreferenceV2RunDiagnosticSummary = {
  figureCount: number
  windowCount: number
  emptyWindowCount: number
  failedWindowCount: number
  unassignedMentionCount: number
  groundingDiagnosticCount: number
  validationIssueCount: number
}

const isRecord = (value: unknown): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const getCoreferenceV2FiguresFromRawExtraction = (rawExtraction: unknown): any[] => (
  isRecord(rawExtraction) && Array.isArray(rawExtraction.figures) ? rawExtraction.figures : []
)

const getStringArray = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.map(item => typeof item === 'string' ? item.trim() : '').filter(Boolean)
    : []
)

const normalizeRowIdSet = (rowIds: readonly string[]): Set<string> => (
  new Set(rowIds.map(rowId => rowId.trim()).filter(Boolean))
)

const rowIdSetsEqual = (left: readonly string[], right: readonly string[]): boolean => {
  const leftSet = normalizeRowIdSet(left)
  const rightSet = normalizeRowIdSet(right)
  if (leftSet.size !== rightSet.size) {
    return false
  }
  return Array.from(leftSet).every(rowId => rightSet.has(rowId))
}

const getCoreferenceV2DiagnosticWindows = (diagnostics?: Record<string, any>): Array<Record<string, any>> => {
  const rawExtraction = diagnostics?.raw_extraction
  if (isRecord(rawExtraction) && Array.isArray(rawExtraction.windows)) {
    return rawExtraction.windows.filter(isRecord)
  }
  if (diagnostics && Array.isArray(diagnostics.windows)) {
    return diagnostics.windows.filter(isRecord)
  }
  return []
}

const getCoreferenceV2WindowRawExtraction = (windowEntry: Record<string, any>): unknown => {
  if ('raw_extraction' in windowEntry) {
    return windowEntry.raw_extraction
  }
  const diagnostics = windowEntry.diagnostics
  return isRecord(diagnostics) ? diagnostics.raw_extraction : undefined
}

const getCoreferenceV2WindowSourceRowIds = (windowEntry: Record<string, any>): string[] => {
  const window = isRecord(windowEntry.window) ? windowEntry.window : {}
  return getStringArray(window.sourceRowIds || window.source_row_ids)
}

const getCoreferenceV2WindowId = (windowEntry: Record<string, any>): string | undefined => {
  const window = isRecord(windowEntry.window) ? windowEntry.window : {}
  const rawWindowId = window.windowId || window.window_id
  return typeof rawWindowId === 'string' && rawWindowId.trim() ? rawWindowId.trim() : undefined
}

const getArrayLength = (value: unknown): number => (
  Array.isArray(value) ? value.length : 0
)

export const summarizeCoreferenceV2RunDiagnostics = (
  diagnostics?: Record<string, any>
): CoreferenceV2RunDiagnosticSummary => {
  const rawExtraction = diagnostics?.raw_extraction
  if (isRecord(rawExtraction) && Array.isArray(rawExtraction.figures)) {
    return {
      figureCount: rawExtraction.figures.length,
      windowCount: 1,
      emptyWindowCount: rawExtraction.figures.length === 0 ? 1 : 0,
      failedWindowCount: 0,
      unassignedMentionCount: getArrayLength(rawExtraction.unassigned_mentions),
      groundingDiagnosticCount: getArrayLength(diagnostics?.grounding_diagnostics),
      validationIssueCount: getArrayLength(diagnostics?.validation_issues),
    }
  }

  const windows = getCoreferenceV2DiagnosticWindows(diagnostics)
  const windowSummaries = windows.map(windowEntry => {
    const rawWindowExtraction = getCoreferenceV2WindowRawExtraction(windowEntry)
    const windowDiagnostics = isRecord(windowEntry.diagnostics) ? windowEntry.diagnostics : {}
    const figures = getCoreferenceV2FiguresFromRawExtraction(rawWindowExtraction)
    const unassignedMentions = isRecord(rawWindowExtraction)
      ? getArrayLength(rawWindowExtraction.unassigned_mentions)
      : 0
    return {
      figureCount: figures.length,
      empty: figures.length === 0,
      unassignedMentionCount: unassignedMentions,
      groundingDiagnosticCount: getArrayLength(windowDiagnostics.grounding_diagnostics),
      validationIssueCount: getArrayLength(windowDiagnostics.validation_issues),
    }
  })

  return {
    figureCount: windowSummaries.reduce((total, summary) => total + summary.figureCount, 0),
    windowCount: windows.length,
    emptyWindowCount: windowSummaries.filter(summary => summary.empty).length,
    failedWindowCount: getArrayLength(diagnostics?.window_failures),
    unassignedMentionCount: windowSummaries.reduce((total, summary) => total + summary.unassignedMentionCount, 0),
    groundingDiagnosticCount: windowSummaries.reduce((total, summary) => total + summary.groundingDiagnosticCount, 0),
    validationIssueCount: windowSummaries.reduce((total, summary) => total + summary.validationIssueCount, 0),
  }
}

const formatCoreferenceV2RunDiagnosticSummary = (
  summary: CoreferenceV2RunDiagnosticSummary
): string => [
  `${summary.figureCount} figures`,
  `${summary.windowCount} window${summary.windowCount === 1 ? '' : 's'}`,
  summary.emptyWindowCount > 0 ? `${summary.emptyWindowCount} empty` : '',
  summary.failedWindowCount > 0 ? `${summary.failedWindowCount} failed` : '',
  summary.unassignedMentionCount > 0 ? `${summary.unassignedMentionCount} unassigned` : '',
  summary.validationIssueCount > 0 ? `${summary.validationIssueCount} validation` : '',
  summary.groundingDiagnosticCount > 0 ? `${summary.groundingDiagnosticCount} grounding` : '',
].filter(Boolean).join(' · ')

export const resolveCoreferenceV2FiguresForFocalization = (
  diagnostics?: Record<string, any>,
  options: { sourceRowIds?: readonly string[] } = {}
): CoreferenceV2FocalizationFigureResolution => {
  const rawExtraction = diagnostics?.raw_extraction
  if (isRecord(rawExtraction) && Array.isArray(rawExtraction.figures)) {
    return {
      figures: rawExtraction.figures,
      message: rawExtraction.figures.length > 0
        ? ''
        : 'Coreference chains v2 returned 0 figures for this source. Inspect Run history, then retry v2 or choose a smaller unit.',
      windowCount: 1,
    }
  }

  const windows = getCoreferenceV2DiagnosticWindows(diagnostics)
  if (windows.length === 0) {
    return {
      figures: [],
      message: 'Run Coreference chains v2 for this unit first.',
      windowCount: 0,
    }
  }

  const sourceRowIds = getStringArray(options.sourceRowIds || [])
  const resolveWindow = (windowEntry: Record<string, any>, matchedWindowId?: string): CoreferenceV2FocalizationFigureResolution => {
    const figures = getCoreferenceV2FiguresFromRawExtraction(getCoreferenceV2WindowRawExtraction(windowEntry))
    return {
      figures,
      message: figures.length > 0
        ? ''
        : 'Coreference chains v2 returned 0 figures for the matching window. Inspect Run history, then retry v2 or choose a smaller unit.',
      windowCount: windows.length,
      ...(matchedWindowId ? { matchedWindowId } : {}),
    }
  }

  if (windows.length === 1) {
    const windowEntry = windows[0]
    const windowRowIds = getCoreferenceV2WindowSourceRowIds(windowEntry)
    const matchedByRows = sourceRowIds.length > 0 && rowIdSetsEqual(windowRowIds, sourceRowIds)
    return resolveWindow(windowEntry, matchedByRows ? getCoreferenceV2WindowId(windowEntry) : undefined)
  }

  if (sourceRowIds.length > 0) {
    const matchingWindow = windows.find(windowEntry => (
      rowIdSetsEqual(getCoreferenceV2WindowSourceRowIds(windowEntry), sourceRowIds)
    ))
    if (matchingWindow) {
      return resolveWindow(matchingWindow, getCoreferenceV2WindowId(matchingWindow))
    }
  }

  const totalFigureCount = windows.reduce((total, windowEntry) => (
    total + getCoreferenceV2FiguresFromRawExtraction(getCoreferenceV2WindowRawExtraction(windowEntry)).length
  ), 0)
  return {
    figures: [],
    message: totalFigureCount > 0
      ? `The Coreference chains v2 run has ${windows.length} windows. Run v2 on the same smaller Scene or Rows unit before focalization.`
      : 'Coreference chains v2 returned 0 figures for this source. Inspect Run history, then retry v2 or choose a smaller unit.',
    windowCount: windows.length,
  }
}

const buildCharacterIndexRefinementInstruction = (detectorId: string): string => {
  if (detectorId === 'llm-focalization-characterization') {
    return [
      'Refine the previous JSON output against the same source text and figures.',
      'Keep the same shape: { "focalization": [...], "characterizations": [...], "unmatched": [...] }.',
      'Remove unsupported spans, add clearly grounded missed spans, preserve figure ids and mention refs, and keep every span/context verbatim from the source.',
      'Return a complete replacement JSON object only.',
    ].join('\n')
  }
  if (detectorId === 'llm-coreference-chain-v2') {
    return [
      'Refine the previous JSON output against the same source text.',
      'Keep the same shape: { "figures": [...], "unassigned_mentions": [...] }.',
      'Improve figure chains, remove unsupported mentions, move unresolved mentions to unassigned_mentions, and keep every mention/context verbatim from the source.',
      'Return a complete replacement JSON object only.',
    ].join('\n')
  }
  if (detectorId === 'llm-coreference-chain') {
    return [
      'Refine the previous JSON output against the same source text.',
      'Keep the same shape: { "characters": [...] } with each character carrying anchor and chain.',
      'Improve coreference grouping, remove unsupported mentions, add clearly grounded missed mentions, and keep every mention/context verbatim from the source.',
      'Return a complete replacement JSON object only.',
    ].join('\n')
  }
  return [
    'Refine the previous JSON output against the same source text.',
    'Keep the same shape required by the prompt.',
    'Remove unsupported entries, add clearly grounded missed entries, preserve verbatim evidence, and do not invent characters.',
    'Return a complete replacement JSON object only.',
  ].join('\n')
}

const isLedgerFilterDebugEnabled = (): boolean => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return false
  }
  try {
    return new URLSearchParams(window.location.search).get('debugLedgerFilters') === '1'
  } catch {
    return false
  }
}

const logLedgerFilterDebug = (event: string, payload: Record<string, unknown>) => {
  if (!isLedgerFilterDebugEnabled()) {
    return
  }
  console.log(`[LedgerFilterDebug] ${event}`, payload)
}

type WitnessUnitTextLookup = Map<string, WitnessUnitTextRecord>
type WitnessUnitTextLookupByWitnessId = Map<string, WitnessUnitTextLookup>

const getResolvedMappingText = (
  row: AnchoredRow,
  witnessId: string,
  textLookupByWitnessId: WitnessUnitTextLookupByWitnessId
): WitnessUnitTextRecord | null => {
  const mapping = row.unitMappings.find(candidate => candidate.witnessId === witnessId && !candidate.excluded)
  if (!mapping) {
    return null
  }

  const witnessLookup = textLookupByWitnessId.get(witnessId)
  if (mapping.unitUuid && witnessLookup?.has(mapping.unitUuid)) {
    return witnessLookup.get(mapping.unitUuid) || null
  }

  const fallbackRawText = mapping.textSnapshot?.trim() || ''
  if (!fallbackRawText) {
    return null
  }

  const preprocessed = preprocessLedgerText(fallbackRawText)
  return {
    rawText: fallbackRawText,
    cleanSourceText: preprocessed.cleanSourceText,
    workspaceDisplayText: preprocessed.workspaceDisplayText,
    readingText: preprocessed.readingText,
    comparisonText: preprocessed.comparisonText
  }
}

const getSnapshotMappingText = (
  row: AnchoredRow,
  witnessId: string
): WitnessUnitTextRecord | null => {
  const mapping = row.unitMappings.find(candidate => candidate.witnessId === witnessId && !candidate.excluded)
  const fallbackRawText = mapping?.textSnapshot?.trim() || ''
  if (!fallbackRawText) {
    return null
  }
  const preprocessed = preprocessLedgerText(fallbackRawText)
  return {
    rawText: fallbackRawText,
    cleanSourceText: preprocessed.cleanSourceText,
    workspaceDisplayText: preprocessed.workspaceDisplayText,
    readingText: preprocessed.readingText,
    comparisonText: preprocessed.comparisonText
  }
}

const getWorkspaceMappingText = (
  row: AnchoredRow,
  witnessId: string,
  textLookupByWitnessId: WitnessUnitTextLookupByWitnessId
): string => (
  getResolvedMappingText(row, witnessId, textLookupByWitnessId)?.workspaceDisplayText || ''
)

const CHARACTER_EXTRACTION_UNIT_LABELS: Record<CharacterExtractionUnit, string> = {
  'visible-ledger': 'Visible ledger',
  witness: 'Witness',
  scene: 'Scene',
  rows: 'Selected rows',
}

const CHARACTER_INDEX_LIST_SCOPE_LABELS: Record<CharacterIndexListScope, string> = {
  ledger: 'Ledger',
  witness: 'Witness',
  scene: 'Scene',
  rows: 'Rows',
}

const CHARACTER_FOCALIZATION_FIGURE_SOURCE_LABELS: Record<CharacterFocalizationFigureSource, string> = {
  'character-index': 'Character index',
  'coreference-v2': 'Coreference v2',
}

const CHARACTER_INDEX_LIST_SCOPE_FROM_EXTRACTION_UNIT: Record<CharacterExtractionUnit, CharacterIndexListScope> = {
  'visible-ledger': 'ledger',
  witness: 'witness',
  scene: 'scene',
  rows: 'rows',
}

const buildPreparedCharacterDetectionRows = ({
  call,
  witness,
}: {
  call: PreparedSingleWitnessCharacterIndexCall
  witness: Witness
}): CharacterLayerDetectionRow[] => {
  const rows: CharacterLayerDetectionRow[] = []
  const textByRowId = new Map<string, string[]>()
  const rowLabelById = new Map<string, string>()

  call.sourceMap.forEach(span => {
    rowLabelById.set(span.rowId, span.rowLabel)
    if (span.spanKind !== 'text') {
      return
    }
    const text = call.sourceText.slice(span.start, span.end).trim()
    if (!text) {
      return
    }
    const bucket = textByRowId.get(span.rowId) || []
    bucket.push(text)
    textByRowId.set(span.rowId, bucket)
  })

  call.scope.selectedRowIds.forEach(rowId => {
    const text = (textByRowId.get(rowId) || []).join('\n').trim()
    if (!text) {
      return
    }
    rows.push({
      witnessId: call.scope.witnessId,
      witnessSigla: witness.sigla,
      rowId,
      rowLabel: rowLabelById.get(rowId) || rowId,
      text,
    })
  })
  return rows
}

const isPreparedCoreferenceWindowExtraction = (
  prepared: PreparedCharacterExtractionState | null
): prepared is PreparedLedgerSafeSingleWitnessCoreferenceChainWindowExtraction | PreparedLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction => (
  Boolean(prepared && 'calls' in prepared)
)

const buildPreparedCharacterDetectionRowsFromCalls = ({
  calls,
  witness,
}: {
  calls: PreparedSingleWitnessCharacterIndexCall[]
  witness: Witness
}): CharacterLayerDetectionRow[] => {
  const rowById = new Map<string, CharacterLayerDetectionRow>()
  calls.forEach(call => {
    buildPreparedCharacterDetectionRows({ call, witness }).forEach(row => {
      if (!rowById.has(row.rowId)) {
        rowById.set(row.rowId, row)
      }
    })
  })
  return Array.from(rowById.values())
}

const getPreparedCharacterExtractionWindowCount = (
  prepared: PreparedCharacterExtractionState | null
): number => (
  isPreparedCoreferenceWindowExtraction(prepared) ? prepared.calls.length : 0
)

const getCharacterDetectorOutputContract = (detectorId: string): string => {
  if (detectorId === 'llm-coreference-chain') {
    return PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT
  }
  if (detectorId === 'llm-coreference-chain-v2') {
    return PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT
  }
  if (detectorId === 'llm-focalization-characterization') {
    return PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT
  }
  return PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT
}

const normalizeCharacterDetectorName = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLowerCase() : ''
)

const characterOutputMatchesFocusedTarget = (
  character: CharacterEntity,
  targetNames: readonly string[],
): boolean => {
  const characterNames = [
    character.display_name,
    ...(character.aliases || []),
    character.metadata?.chain_anchor,
    character.metadata?.transliteration,
  ].map(normalizeCharacterDetectorName).filter(Boolean)
  return characterNames.some(name => targetNames.includes(name))
}

const applyFocusedCharacterImportTarget = (
  output: CharacterLayerDetectorOutput,
  focusedCharacter: CharacterEntity | null,
): CharacterLayerDetectorOutput => {
  if (!focusedCharacter) {
    return output
  }
  const targetNames = [
    focusedCharacter.display_name,
    ...(focusedCharacter.aliases || []),
  ].map(normalizeCharacterDetectorName).filter(Boolean)
  const matchingCharacterIds = new Set<string>()
  output.characters.forEach(character => {
    if (characterOutputMatchesFocusedTarget(character, targetNames)) {
      matchingCharacterIds.add(character.character_id)
    }
  })
  if (matchingCharacterIds.size === 0 && output.characters.length === 1) {
    matchingCharacterIds.add(output.characters[0].character_id)
  }
  const occurrences = output.occurrences
    .filter(occurrence => matchingCharacterIds.has(occurrence.character_id) || output.characters.length === 0)
    .map(occurrence => ({
      ...occurrence,
      character_id: focusedCharacter.character_id,
      metadata: {
        ...(occurrence.metadata || {}),
        focused_character_id: focusedCharacter.character_id,
        focused_character_name: focusedCharacter.display_name,
        source_character_id: occurrence.character_id,
      },
    }))
  return {
    ...output,
    characters: [],
    occurrences,
    metadata: {
      ...(output.metadata || {}),
      focused_character_id: focusedCharacter.character_id,
      focused_character_name: focusedCharacter.display_name,
      focused_candidate_character_count: output.characters.length,
      focused_retained_occurrence_count: occurrences.length,
    },
  }
}

const adaptExternalCharacterIndexRawOutput = ({
  chapterId,
  createdAt,
  existingLayer,
  focusedCharacter,
  outputContract,
  preparedCall,
  rawExtraction,
}: {
  chapterId: string
  createdAt: string
  existingLayer: ChapterCharacterLayer
  focusedCharacter: CharacterEntity | null
  outputContract: string
  preparedCall: PreparedSingleWitnessCharacterIndexCall
  rawExtraction: unknown
}): CharacterLayerDetectorOutput => {
  if (outputContract === PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT) {
    return applyFocusedCharacterImportTarget(adaptPackageCoreferenceChainsToDetectorOutput({
      rawExtraction,
      preparedCall: preparedCall as PreparedSingleWitnessCoreferenceChainWindowCall,
      chapterId,
      createdAt,
    }), focusedCharacter)
  }
  if (outputContract === PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT) {
    return adaptPackageCoreferenceChainV2ToDetectorOutput({
      rawExtraction,
      preparedCall: preparedCall as PreparedSingleWitnessCoreferenceChainV2WindowCall,
      chapterId,
      createdAt,
    })
  }
  if (outputContract === PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT) {
    return adaptPackageFocalizationCharacterizationToDetectorOutput({
      rawExtraction,
      preparedCall: preparedCall as PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
      existingLayer,
      createdAt,
    })
  }
  return adaptPackageCharacterEvidenceToDetectorOutput({
    rawExtraction,
    preparedCall: preparedCall as PreparedSingleWitnessCharacterExtractionCall,
    chapterId,
    createdAt,
  })
}

const getExternalModelPayloadFromUnknown = (value: unknown): CharacterIndexExternalModelPayload | null => {
  const record = isRecord(value) ? value : null
  if (!record) {
    return null
  }
  if (record.export_kind === CHARACTER_INDEX_EXTERNAL_MODEL_PAYLOAD_KIND && Array.isArray(record.calls)) {
    return record as unknown as CharacterIndexExternalModelPayload
  }
  const nestedCandidates = [
    record.payload,
    record.model_payload,
    record.modelPayload,
    record.request,
    record.variorum_payload,
  ]
  for (const candidate of nestedCandidates) {
    const nestedPayload = getExternalModelPayloadFromUnknown(candidate)
    if (nestedPayload) {
      return nestedPayload
    }
  }
  return null
}

const normalizeCharacterExtractionUnitValue = (value: unknown): CharacterExtractionUnit => {
  if (value === 'visible-ledger' || value === 'witness' || value === 'scene' || value === 'rows') {
    return value
  }
  return 'witness'
}

const buildExternalImportFallbackProvenance = ({
  payload,
  runMode,
}: {
  payload: CharacterIndexExternalModelPayload
  runMode: CharacterDetectorRunMode
}): CharacterDetectorRunProvenance => {
  const provenance = isRecord(payload.provenance) ? payload.provenance : {}
  const witnessLabels = Array.isArray(provenance.witnessLabels)
    ? provenance.witnessLabels.filter((label): label is string => typeof label === 'string')
    : []
  const rowLabels = Array.isArray(provenance.rowLabels)
    ? provenance.rowLabels.filter((label): label is string => typeof label === 'string')
    : []
  return {
    detectorId: payload.detector_id || 'external-character-index',
    detectorLabel: payload.detector_label || 'External Character Index',
    extractionUnit: normalizeCharacterExtractionUnitValue(provenance.extractionUnit),
    sourceLabel: typeof provenance.sourceLabel === 'string' ? provenance.sourceLabel : 'External prompt context',
    witnessLabels,
    ...(payload.model.model_id ? { modelId: payload.model.model_id } : {}),
    scorer: payload.detector_label || 'External model',
    ...(typeof payload.model.temperature === 'number' ? { temperature: payload.model.temperature } : {}),
    ...(payload.model.thinking_level === 'medium' || payload.model.thinking_level === 'high'
      ? { thinkingLevel: payload.model.thinking_level }
      : {}),
    ...(typeof provenance.promptVersion === 'string' ? { promptVersion: provenance.promptVersion } : {}),
    ...(typeof provenance.promptPreview === 'string' ? { promptPreview: provenance.promptPreview } : {}),
    ...(payload.focused_character ? {
      focusedCharacterId: payload.focused_character.character_id,
      focusedCharacterName: payload.focused_character.display_name,
    } : {}),
    rowCount: typeof provenance.rowCount === 'number' ? provenance.rowCount : rowLabels.length,
    windowCount: payload.calls.length > 1 ? payload.calls.length : 0,
    rowLabels,
  }
}

const getComparisonMappingText = (
  row: AnchoredRow,
  witnessId: string,
  textLookupByWitnessId: WitnessUnitTextLookupByWitnessId
): string => (
  getResolvedMappingText(row, witnessId, textLookupByWitnessId)?.comparisonText || ''
)

const getAppliedTranslationStatus = (
  row: AnchoredRow,
  witnessId: string,
): WorkbenchPropagationRowAssignment['translationStatus'] => (
  row.unitMappings.find(candidate => candidate.witnessId === witnessId && !candidate.excluded)?.translationStatus
)

const getCellMetaText = (row: AnchoredRow, witnessId: string): string => {
  const state = getLedgerSafeCellState(row, witnessId)
  if (state === 'excluded') {
    return '∅ gap'
  }
  return ''
}

const getLedgerSafePlaceholderLabel = (state: LedgerSafeCellState, metaText: string): string => {
  if (state === 'excluded') {
    return 'Deliberate gap'
  }
  return metaText || 'Empty collation cell'
}

type LedgerSafeSceneHeaderModel = {
  sceneKey: string
  testIdKey: string
  rowId: string
  rowIds: string[]
  label: string
  title: string
  description: string
  arabicTitle: string
  rowCount: number | null
}

type LedgerSafeSceneTitleEditor = {
  sceneKey: string
  title: string
  arabicTitle: string
}

type LedgerSafeRowTitleEditor = {
  rowId: string
  primaryText: string
  parallelText: string
}

type LedgerSafeStructuralRangeEditor = {
  rangeId: string | null
  title: string
  kind: StructuralRangeKind | ''
  startRowId: string
  endRowId: string
  parentRangeId: string
}

type LedgerSafeStructuralRangeMenu = 'title' | 'kind' | 'parent' | 'adjust'

type LedgerSafeStructuralRangeRibbonAnchor =
  | { kind: 'row'; id: string }
  | { kind: 'range'; id: string }

type LedgerSafeStructuralBoundaryPlacement = {
  edge: 'start' | 'end'
  rangeId: string | null
}

type LedgerSafeStructuralRangeRibbonPosition = {
  left: number
  top: number
  maxWidth: number
}

type LedgerSafeSceneBackfillUndoState = {
  id: string
  createdAt: string
  sourceRunDir: string
  summaryPath: string
  changedRowScenes: Array<{
    rowId: string
    previousScene: AnchoredRowSceneMetadata | null
  }>
}

type LedgerSafeLegacySceneRemovalUndoState = {
  id: string
  createdAt: string
  sceneKey: string
  title: string
  removedRowScenes: Array<{
    rowId: string
    scene: AnchoredRowSceneMetadata
  }>
}

type LedgerSafeToastMessage = {
  message: string
  action?: {
    label: string
    run: () => void
  }
}

const cloneAnchoredRowSceneMetadata = (
  scene: AnchoredRowSceneMetadata | null | undefined
): AnchoredRowSceneMetadata | null => {
  if (!scene) {
    return null
  }
  return JSON.parse(JSON.stringify(scene)) as AnchoredRowSceneMetadata
}

const getAnchoredRowSceneMetadata = (row: AnchoredRow): AnchoredRowSceneMetadata | null => {
  const scene = row.metadata?.scene
  if (!scene || typeof scene !== 'object' || Array.isArray(scene)) {
    return null
  }
  const title = typeof scene.title === 'string' ? scene.title.trim() : ''
  const sceneId = typeof scene.sceneId === 'string' ? scene.sceneId.trim() : ''
  if (!title && !sceneId && typeof scene.sceneNumber !== 'number' && typeof scene.sceneOrdinal !== 'number') {
    return null
  }
  return {
    ...scene,
    sceneId: sceneId || undefined,
    title: title || (sceneId || 'Scene'),
    description: typeof scene.description === 'string' ? scene.description.trim() : '',
    arabicTitle: typeof scene.arabicTitle === 'string' ? scene.arabicTitle.trim() : '',
  }
}

const getLedgerSafeSceneKey = (scene: AnchoredRowSceneMetadata): string => {
  if (typeof scene.sceneId === 'string' && scene.sceneId.trim()) {
    return scene.sceneId.trim()
  }
  if (typeof scene.sceneOrdinal === 'number') {
    return `scene-ordinal-${scene.sceneOrdinal}`
  }
  if (typeof scene.sceneNumber === 'number') {
    return `scene-number-${scene.sceneNumber}`
  }
  return `scene-title-${String(scene.title || 'scene').trim().toLowerCase()}`
}

const getLedgerSafeSceneTestIdKey = (sceneKey: string): string => (
  sceneKey.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'scene'
)

const getLedgerSafeSceneLabel = (scene: AnchoredRowSceneMetadata): string => {
  const ordinal = typeof scene.sceneOrdinal === 'number'
    ? scene.sceneOrdinal
    : typeof scene.sceneNumber === 'number'
      ? scene.sceneNumber
      : null
  return ordinal === null ? 'Scene' : `Scene ${ordinal}`
}

const getLedgerSafeCompactSceneLabel = (label: string): string => {
  const match = label.trim().match(/^Scene\s+(.+)$/i)
  return match ? `S${match[1]}` : label
}

const getLedgerSafeSceneChipLabel = (label: string): string => {
  const match = label.trim().match(/^Scene\s+(.+)$/i)
  if (!match) {
    return label.trim().toUpperCase()
  }
  const value = match[1].trim()
  return /^\d+$/.test(value) ? `SCENE ${value.padStart(2, '0')}` : `SCENE ${value.toUpperCase()}`
}

const buildLedgerSafeSceneHeaders = (rows: AnchoredRow[]): {
  byRowId: Map<string, LedgerSafeSceneHeaderModel>
  bySceneKey: Map<string, LedgerSafeSceneHeaderModel>
} => {
  const byRowId = new Map<string, LedgerSafeSceneHeaderModel>()
  const bySceneKey = new Map<string, LedgerSafeSceneHeaderModel>()
  let previousSceneKey = ''
  rows.forEach(row => {
    const scene = getAnchoredRowSceneMetadata(row)
    if (!scene) {
      previousSceneKey = ''
      return
    }
    const sceneKey = getLedgerSafeSceneKey(scene)
    let model = bySceneKey.get(sceneKey)
    if (!model) {
      model = {
        sceneKey,
        testIdKey: getLedgerSafeSceneTestIdKey(sceneKey),
        rowId: row.id,
        rowIds: [],
        label: getLedgerSafeSceneLabel(scene),
        title: String(scene.title || getLedgerSafeSceneLabel(scene)),
        description: String(scene.description || ''),
        arabicTitle: String(scene.arabicTitle || ''),
        rowCount: typeof scene.rowCount === 'number' ? scene.rowCount : null
      }
      bySceneKey.set(sceneKey, model)
    }
    model.rowIds.push(row.id)
    if (sceneKey !== previousSceneKey) {
      byRowId.set(row.id, model)
    }
    previousSceneKey = sceneKey
  })
  return { byRowId, bySceneKey }
}

const getLedgerSafeRowSourceRun = (row: AnchoredRow): string | null => {
  const metadata = row.metadata
  if (!metadata || typeof metadata !== 'object') {
    return null
  }
  for (const key of ['sourceRun', 'sourceRunDir', 'source_run_dir', 'outputRoot']) {
    const value = metadata[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return null
}

const getMostCommonLedgerSafeSourceRun = (rows: AnchoredRow[]): string | null => {
  const counts = new Map<string, number>()
  rows.forEach(row => {
    const sourceRun = getLedgerSafeRowSourceRun(row)
    if (sourceRun) {
      counts.set(sourceRun, (counts.get(sourceRun) || 0) + 1)
    }
  })
  let bestSourceRun = ''
  let bestCount = 0
  counts.forEach((count, sourceRun) => {
    if (count > bestCount) {
      bestSourceRun = sourceRun
      bestCount = count
    }
  })
  return bestSourceRun || null
}

const hasLedgerSafeStagedImportFingerprint = (rows: AnchoredRow[]): boolean => (
  rows.some(row => {
    if (getLedgerSafeRowSourceRun(row)) {
      return true
    }
    if (row.id.startsWith('build_import::') || row.id.startsWith('generated_alignment::')) {
      return true
    }
    const rowKind = typeof row.metadata?.rowKind === 'string' ? row.metadata.rowKind : ''
    return rowKind.includes('hybrid') || rowKind.includes('source_backed')
  })
)

const buildSceneMetadataByRowNumber = (
  scenes: StagedReferenceAlignmentSceneMetadata[],
  provenance: {
    sourceRunDir: string
    summaryPath: string
    summaryHash: string
    backfilledAt: string
  }
): Map<number, AnchoredRowSceneMetadata> => {
  const sceneByRowNumber = new Map<number, AnchoredRowSceneMetadata>()
  scenes.forEach(scene => {
    if (!Number.isFinite(scene.rowStart) || !Number.isFinite(scene.rowEnd) || scene.rowEnd < scene.rowStart) {
      return
    }
    for (let rowNumber = scene.rowStart; rowNumber <= scene.rowEnd; rowNumber += 1) {
      sceneByRowNumber.set(rowNumber, {
        ...scene,
        rowStart: scene.rowStart,
        rowEnd: scene.rowEnd,
        rowCount: typeof scene.rowCount === 'number' ? scene.rowCount : scene.rowEnd - scene.rowStart + 1,
        isSceneStart: rowNumber === scene.rowStart,
        source: 'scene_metadata_backfill',
        sourceRunDir: provenance.sourceRunDir,
        summaryPath: provenance.summaryPath,
        summaryHash: provenance.summaryHash,
        backfilledAt: provenance.backfilledAt,
      })
    }
  })
  return sceneByRowNumber
}

const GAP_CELL_STYLE: React.CSSProperties = {
  backgroundColor: '#f8fafc',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M-2 10L10 -2M-2 2L2 -2M6 10L10 6' stroke='%23000000' stroke-width='0.5' stroke-opacity='0.1'/%3E%3C/svg%3E\")"
}

const LEDGER_SAFE_ALIGNMENT_DEFAULT_MODEL_ID = 'gpt-5.6-sol'
const LEDGER_SAFE_ALIGNMENT_DEFAULT_THINKING_LEVEL: WorkbenchThinkingLevel = 'medium'
const LEDGER_SAFE_PROPAGATION_MODEL_STORAGE_KEY = 'ledgerSafe.propagationModelId.v2'
const LEDGER_SAFE_REPAIR_MODEL_STORAGE_KEY = 'ledgerSafe.repairModelId.v2'
const LEDGER_SAFE_PROPAGATION_THINKING_STORAGE_KEY = 'ledgerSafe.propagationThinkingLevel.v1'
const LEDGER_SAFE_REPAIR_THINKING_STORAGE_KEY = 'ledgerSafe.repairThinkingLevel.v1'
const LEDGER_SAFE_ROW_TITLE_MODEL_STORAGE_KEY = 'ledgerSafe.rowTitleModelId.v1'
const LEDGER_SAFE_ROW_TITLE_THINKING_STORAGE_KEY = 'ledgerSafe.rowTitleThinkingLevel.v1'
const LEDGER_SAFE_STRUCTURE_MODEL_STORAGE_KEY = 'ledgerSafe.structuralRangeModelId.v1'
const LEDGER_SAFE_STRUCTURE_THINKING_STORAGE_KEY = 'ledgerSafe.structuralRangeThinkingLevel.v1'
const StructuralKindLabel: React.FC<{
  kind?: StructuralRangeKind | null
  number?: number
  fallback?: string
  testId?: string
  variant?: 'ledger' | 'panel'
}> = ({ kind, number, fallback = 'Unclassified', testId, variant = 'panel' }) => (
  <span
    data-testid={testId}
    className={`inline-flex shrink-0 items-center whitespace-nowrap font-mono font-bold uppercase leading-none ${
      variant === 'ledger'
        ? 'justify-center border border-black bg-black px-1.5 py-0.5 text-[8px] tracking-[0.12em] text-white'
        : 'text-[8px] tracking-[0.12em] text-blue-700'
    }`}
  >
    {formatStructuralRangeKindLabel(kind, number, fallback)}
  </span>
)

const LEDGER_SAFE_SELECTED_REPAIR_BATCH_SIZE = 48
const LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS: ReadonlyArray<{
  value: WorkbenchThinkingLevel
  label: string
}> = [
  { value: 'off', label: 'Off' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra high' },
]
const LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS = [
  {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 Sol · Pi',
    runtime: 'pi',
    provider: 'openai-codex',
  },
  {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna · Pi',
    runtime: 'pi',
    provider: 'openai-codex',
  },
  {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 Terra · Pi',
    runtime: 'pi',
    provider: 'openai-codex',
  },
  {
    id: 'gemini-3.1-flash-lite-preview',
    label: 'Gemini 3.1 Flash Lite preview',
    runtime: 'gemini',
  },
  {
    id: 'gemini-3-flash-preview',
    label: 'Gemini 3 Flash preview',
    runtime: 'gemini',
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro preview',
    runtime: 'gemini',
  },
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    runtime: 'gemini',
  },
] as const
const LEDGER_SAFE_ROW_TITLE_MODEL_OPTIONS = LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS.filter(
  option => option.runtime === 'pi'
)
const LEDGER_SAFE_ROW_TITLE_MODEL_ID_SET = new Set<string>(
  LEDGER_SAFE_ROW_TITLE_MODEL_OPTIONS.map(option => option.id)
)
const normalizeLedgerSafeRowTitleModelId = (value: string | null | undefined): string => {
  const modelId = (value || '').trim()
  return LEDGER_SAFE_ROW_TITLE_MODEL_ID_SET.has(modelId) ? modelId : 'gpt-5.6-luna'
}
const normalizeLedgerSafeRowTitleThinkingLevel = (
  value: string | null | undefined,
): PiMatrixAgentThinkingLevel => {
  const level = (value || '').trim() as PiMatrixAgentThinkingLevel
  return LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.some(option => option.value === level)
    ? level
    : 'low'
}
const LEDGER_SAFE_ALIGNMENT_MODEL_ID_SET = new Set<string>(
  LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS.map(option => option.id)
)
const normalizeLedgerSafeAlignmentModelId = (value: string | null | undefined): string => {
  const trimmed = (value || '').trim()
  return LEDGER_SAFE_ALIGNMENT_MODEL_ID_SET.has(trimmed)
    ? trimmed
    : LEDGER_SAFE_ALIGNMENT_DEFAULT_MODEL_ID
}
const readStoredLedgerSafeAlignmentModelId = (storageKey: string): string => (
  normalizeLedgerSafeAlignmentModelId(safeStorageGet(storageKey))
)
const normalizeLedgerSafeAlignmentThinkingLevel = (
  value: string | null | undefined,
): WorkbenchThinkingLevel => {
  const trimmed = (value || '').trim() as WorkbenchThinkingLevel
  return LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.some(option => option.value === trimmed)
    ? trimmed
    : LEDGER_SAFE_ALIGNMENT_DEFAULT_THINKING_LEVEL
}
const readStoredLedgerSafeAlignmentThinkingLevel = (
  storageKey: string,
): WorkbenchThinkingLevel => (
  normalizeLedgerSafeAlignmentThinkingLevel(safeStorageGet(storageKey))
)
const getLedgerSafeAlignmentModelOption = (modelId: string) => (
  LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS.find(candidate => candidate.id === modelId)
  || LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS[0]
)
const isLedgerSafePiAlignmentModel = (modelId: string): boolean => (
  getLedgerSafeAlignmentModelOption(modelId).runtime === 'pi'
)
const getLedgerSafeAlignmentModelRequest = (
  modelId: string,
  thinkingLevel: WorkbenchThinkingLevel,
) => {
  const option = getLedgerSafeAlignmentModelOption(modelId)
  return option.runtime === 'pi'
    ? {
        modelId: option.id,
        inferenceRuntime: 'pi' as const,
        modelProvider: option.provider,
        thinkingLevel,
      }
    : {
        modelId: option.id,
        inferenceRuntime: 'gemini' as const,
      }
}

const LEDGER_OCCUPANCY_GAP_SLOT_STYLE: React.CSSProperties = {
  ...GAP_CELL_STYLE,
  backgroundColor: '#fffdfa',
  backgroundImage: 'repeating-linear-gradient(135deg, #fffdfa 0, #fffdfa 2px, #ede8de 2px, #ede8de 4px)',
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.20)'
}

const hasArabicText = (value: string): boolean => /[\u0600-\u06FF]/.test(value)
const areStringArraysEqual = (left: string[], right: string[]): boolean => (
  left.length === right.length && left.every((value, index) => value === right[index])
)

const getLedgerSafeSuggestionSourceLabel = (suggestion: LedgerSafeEditSuggestion): string => {
  const reason = suggestion.reason || ''
  const patterns = [
    /boundary correction in ([^.]+)\./,
    /same ledger row in ([^.]+)\./,
    /row operation in ([^.]+)\./,
    /because ([^.]+) was merged/,
    /because ([^.]+) was split/
  ]
  for (const pattern of patterns) {
    const match = reason.match(pattern)
    if (match?.[1]) {
      return match[1].trim()
    }
  }
  if (suggestion.sourceWitnessId) {
    return 'source'
  }
  return 'witness'
}

type LedgerSafeSuggestionEdgeFamily = 'right' | 'top' | 'bottom'
type LedgerSafeSuggestionPanelPlacement = 'right' | 'left' | 'top' | 'bottom'

const LEDGER_SAFE_SUGGESTION_PANEL_WIDTH = 280
const LEDGER_SAFE_SUGGESTION_PANEL_HEIGHT_ESTIMATE = 132
const LEDGER_SAFE_SUGGESTION_PANEL_GAP = 12
const LEDGER_SAFE_SUGGESTION_VIEWPORT_PADDING = 12

const getLedgerSafeSuggestionEdgeFamily = (suggestion: LedgerSafeEditSuggestion): LedgerSafeSuggestionEdgeFamily => {
  if (suggestion.kind === 'split_unit') {
    return 'top'
  }
  if (suggestion.kind === 'mark_gap' || suggestion.kind === 'clear_gap') {
    return 'bottom'
  }
  return 'right'
}

const getLedgerSafeSuggestionOperationLabel = (suggestion: LedgerSafeEditSuggestion): string => {
  if (suggestion.kind === 'boundary_shift') {
    const deltaWords = Number(suggestion.params.deltaWords || 0)
    return `BOUNDARY ${deltaWords > 0 ? '+' : ''}${Number.isFinite(deltaWords) ? deltaWords : ''}`.trim()
  }
  if (suggestion.kind === 'merge_units') {
    return `MERGE ${suggestion.rowIds.length || 2} ROWS`
  }
  if (suggestion.kind === 'split_unit') {
    return 'SPLIT UNIT'
  }
  if (suggestion.kind === 'move_mapping') {
    if (suggestion.params.sourceOperationKind === 'insert_row_and_move') {
      return `MOVE TO SLOT ${suggestion.params.direction === 'up' ? 'ABOVE' : 'BELOW'}`
    }
    return `MOVE ${String(suggestion.params.direction || '').toUpperCase()}`.trim()
  }
  if (suggestion.kind === 'shift_range') {
    return `SHIFT ${String(suggestion.params.direction || '').toUpperCase()}`.trim()
  }
  if (suggestion.kind === 'insert_row_and_move') {
    return 'INSERT ROW'
  }
  if (suggestion.kind === 'mark_gap') {
    return 'MARK GAP'
  }
  if (suggestion.kind === 'clear_gap') {
    return 'CLEAR GAP'
  }
  return suggestion.label.toUpperCase()
}

const getLedgerSafeSuggestionMarkerClasses = (
  edgeFamily: LedgerSafeSuggestionEdgeFamily,
  isDisplayArabic: boolean
): { dogEar: string; dogEarShade: string; edgeTick: string } => {
  if (edgeFamily === 'top') {
    return {
      dogEar: 'absolute top-0 left-0 h-0 w-0 border-solid border-t-[9px] border-r-0 border-b-0 border-l-[9px] border-t-transparent border-r-transparent border-b-transparent border-l-[rgba(180,32,20,0.92)] transition-[border-width,border-color] duration-150 ease-out group-hover/cell:border-t-[12px] group-hover/cell:border-l-[12px] group-hover/cell:border-l-[rgb(168,26,14)]',
      dogEarShade: 'pointer-events-none absolute top-0 left-0 h-[9px] w-[9px] shadow-[inset_1px_1px_0_rgba(60,8,0,0.30)] transition-[width,height] duration-150 ease-out group-hover/cell:h-[12px] group-hover/cell:w-[12px]',
      edgeTick: 'absolute top-0 left-[11px] h-px w-[14px] bg-[rgba(180,32,20,0.92)] transition-[width,background-color] duration-150 ease-out group-hover/cell:w-[22px] group-hover/cell:bg-[rgb(168,26,14)]'
    }
  }
  if (edgeFamily === 'bottom') {
    return {
      dogEar: 'absolute bottom-0 left-0 h-0 w-0 border-solid border-t-0 border-r-0 border-b-[9px] border-l-[9px] border-t-transparent border-r-transparent border-b-[rgba(180,32,20,0.92)] border-l-transparent transition-[border-width,border-color] duration-150 ease-out group-hover/cell:border-b-[12px] group-hover/cell:border-l-[12px] group-hover/cell:border-b-[rgb(168,26,14)]',
      dogEarShade: 'pointer-events-none absolute bottom-0 left-0 h-[9px] w-[9px] shadow-[inset_1px_-1px_0_rgba(60,8,0,0.30)] transition-[width,height] duration-150 ease-out group-hover/cell:h-[12px] group-hover/cell:w-[12px]',
      edgeTick: 'absolute bottom-0 left-[11px] h-px w-[14px] bg-[rgba(180,32,20,0.92)] transition-[width,background-color] duration-150 ease-out group-hover/cell:w-[22px] group-hover/cell:bg-[rgb(168,26,14)]'
    }
  }
  if (isDisplayArabic) {
    return {
      dogEar: 'absolute top-0 left-0 h-0 w-0 border-solid border-t-[9px] border-r-[9px] border-b-0 border-l-0 border-t-[rgba(180,32,20,0.92)] border-r-transparent border-b-transparent border-l-transparent transition-[border-width,border-color] duration-150 ease-out group-hover/cell:border-t-[12px] group-hover/cell:border-r-[12px] group-hover/cell:border-t-[rgb(168,26,14)]',
      dogEarShade: 'pointer-events-none absolute top-0 left-0 h-[9px] w-[9px] shadow-[inset_1px_1px_0_rgba(60,8,0,0.30)] transition-[width,height] duration-150 ease-out group-hover/cell:h-[12px] group-hover/cell:w-[12px]',
      edgeTick: 'absolute top-[11px] left-0 h-[14px] w-px bg-[rgba(180,32,20,0.92)] transition-[height,background-color] duration-150 ease-out group-hover/cell:h-[22px] group-hover/cell:bg-[rgb(168,26,14)]'
    }
  }
  return {
    dogEar: 'absolute top-0 right-0 h-0 w-0 border-solid border-t-0 border-r-[9px] border-b-[9px] border-l-0 border-t-transparent border-r-[rgba(180,32,20,0.92)] border-b-transparent border-l-transparent transition-[border-width,border-color] duration-150 ease-out group-hover/cell:border-r-[12px] group-hover/cell:border-b-[12px] group-hover/cell:border-r-[rgb(168,26,14)]',
    dogEarShade: 'pointer-events-none absolute top-0 right-0 h-[9px] w-[9px] shadow-[inset_-1px_1px_0_rgba(60,8,0,0.30)] transition-[width,height] duration-150 ease-out group-hover/cell:h-[12px] group-hover/cell:w-[12px]',
    edgeTick: 'absolute top-[11px] right-0 h-[14px] w-px bg-[rgba(180,32,20,0.92)] transition-[height,background-color] duration-150 ease-out group-hover/cell:h-[22px] group-hover/cell:bg-[rgb(168,26,14)]'
  }
}

const getLedgerSafeSuggestionDefaultPanelPlacement = (
  suggestion: LedgerSafeEditSuggestion,
  isDisplayArabic: boolean
): LedgerSafeSuggestionPanelPlacement => {
  const edgeFamily = getLedgerSafeSuggestionEdgeFamily(suggestion)
  if (edgeFamily === 'top') {
    return 'top'
  }
  if (edgeFamily === 'bottom') {
    return 'bottom'
  }
  return isDisplayArabic ? 'left' : 'right'
}

const resolveLedgerSafeSuggestionPanelPlacement = (
  cellRect: DOMRect,
  preferredPlacement: LedgerSafeSuggestionPanelPlacement
): LedgerSafeSuggestionPanelPlacement => {
  if (typeof window === 'undefined') {
    return preferredPlacement
  }

  const rightSpace = window.innerWidth - cellRect.right - LEDGER_SAFE_SUGGESTION_VIEWPORT_PADDING
  const leftSpace = cellRect.left - LEDGER_SAFE_SUGGESTION_VIEWPORT_PADDING
  const topSpace = cellRect.top - LEDGER_SAFE_SUGGESTION_VIEWPORT_PADDING
  const bottomSpace = window.innerHeight - cellRect.bottom - LEDGER_SAFE_SUGGESTION_VIEWPORT_PADDING
  const requiredSideSpace = LEDGER_SAFE_SUGGESTION_PANEL_WIDTH + LEDGER_SAFE_SUGGESTION_PANEL_GAP
  const requiredVerticalSpace = LEDGER_SAFE_SUGGESTION_PANEL_HEIGHT_ESTIMATE + LEDGER_SAFE_SUGGESTION_PANEL_GAP
  const fitsRight = rightSpace >= requiredSideSpace
  const fitsLeft = leftSpace >= requiredSideSpace
  const fitsTop = topSpace >= requiredVerticalSpace
  const fitsBottom = bottomSpace >= requiredVerticalSpace

  if (preferredPlacement === 'right') {
    if (!fitsRight && fitsLeft) {
      return 'left'
    }
    return !fitsRight && !fitsLeft && leftSpace > rightSpace ? 'left' : 'right'
  }

  if (preferredPlacement === 'left') {
    if (!fitsLeft && fitsRight) {
      return 'right'
    }
    return !fitsLeft && !fitsRight && rightSpace > leftSpace ? 'right' : 'left'
  }

  if (preferredPlacement === 'top') {
    if (!fitsTop && fitsBottom) {
      return 'bottom'
    }
    return !fitsTop && !fitsBottom && bottomSpace > topSpace ? 'bottom' : 'top'
  }

  if (!fitsBottom && fitsTop) {
    return 'top'
  }
  return !fitsBottom && !fitsTop && topSpace > bottomSpace ? 'top' : 'bottom'
}

const getLedgerSafeSuggestionPanelPositionClass = (
  placement: LedgerSafeSuggestionPanelPlacement
): string => {
  if (placement === 'left') {
    return 'right-full top-0 w-[292px] pr-3 translate-x-1 group-hover/cell:translate-x-0 group-focus-within/cell:translate-x-0'
  }
  if (placement === 'top') {
    return 'bottom-full left-0 w-[280px] pb-3 translate-y-1 group-hover/cell:translate-y-0 group-focus-within/cell:translate-y-0'
  }
  if (placement === 'bottom') {
    return 'left-0 top-full w-[280px] pt-3 -translate-y-1 group-hover/cell:translate-y-0 group-focus-within/cell:translate-y-0'
  }
  return 'left-full top-0 w-[292px] pl-3 -translate-x-1 group-hover/cell:translate-x-0 group-focus-within/cell:translate-x-0'
}

const getLedgerSafeSuggestionNumberParam = (
  suggestion: LedgerSafeEditSuggestion | null,
  key: string
): number | null => {
  const value = suggestion?.params?.[key]
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const splitLedgerSafeSuggestionText = (value: unknown): string[] =>
  String(value || '').split(/\s+/).map(token => token.trim()).filter(Boolean)

const buildLedgerSafeSplitSuggestionTokens = (
  suggestion: LedgerSafeEditSuggestion | null,
  fallbackText: string
): string[] => {
  if (suggestion?.kind !== 'split_unit') {
    return []
  }
  const leftTokens = splitLedgerSafeSuggestionText(suggestion.params.targetLeftText)
  const rightTokens = splitLedgerSafeSuggestionText(suggestion.params.targetRightText)
  if (leftTokens.length + rightTokens.length > 1) {
    return [...leftTokens, ...rightTokens]
  }
  return splitLedgerSafeSuggestionText(fallbackText)
}

const clampLedgerSafeSplitSuggestionOffset = (
  offset: number,
  tokenCount: number
): number => {
  if (tokenCount < 2) {
    return 0
  }
  return Math.max(1, Math.min(tokenCount - 1, offset))
}

type DiffAppearance = 'subtle' | 'medium' | 'high' | 'arabic'
type DiffMode = 'inline' | 'apparatus'
type ApparatusScope = 'chapter' | 'row'
type LedgerOccupancyMapLayer =
  | 'structural'
  | 'height'
  | 'thin-line'
  | 'drift'
  | 'bead'
  | 'flow'
  | 'spine'
  | 'variance'
  | 'triage'
type LedgerOccupancyVarianceBucket = 'unified' | 'near' | 'drifting' | 'conflict' | 'severe'
type BoundaryOption = {
  id: string
  label: string
  helperText: string
  context: LedgerSafeBoundaryContext
}

type InlineEditMode = 'idle' | 'boundary' | 'split'
type SidePanelTab = 'map' | 'inspector' | 'propagation' | 'titles' | 'sequence' | 'fragments' | 'log'
type LeftPanelTab = 'characters' | 'structure'
type LedgerSafePropagationMode = 'witness' | 'context' | 'semantic' | 'cross-tradition'

const LEDGER_SAFE_PI_DOCK_EXPANDED_STORAGE_KEY = 'ledgerSafe.piDock.expanded'
const LEDGER_SAFE_PI_DOCK_HEIGHT_STORAGE_KEY = 'ledgerSafe.piDock.height'
const LEDGER_CROSS_TRADITION_PREVIEW_RUN_STORAGE_PREFIX = 'ledgerSafe.crossTradition.previewRuns.v1'
const LEDGER_CROSS_TRADITION_PREVIEW_RUN_HISTORY_LIMIT = 8
const LEDGER_SAFE_PI_DOCK_DEFAULT_HEIGHT = 220
const LEDGER_SAFE_PI_DOCK_MIN_HEIGHT = 160
const LEDGER_SAFE_PI_DOCK_MAX_HEIGHT = 360
const LEDGER_SAFE_FRAGMENT_REVIEW_CONTEXT_RADIUS = 4

const witnessOrderSiglum = (zeroBasedIndex: number): string => {
  let position = Math.max(0, Math.floor(zeroBasedIndex)) + 1
  let siglum = ''
  while (position > 0) {
    position -= 1
    siglum = String.fromCharCode(65 + (position % 26)) + siglum
    position = Math.floor(position / 26)
  }
  return siglum
}

type LedgerSafeFragmentPlacementMode =
  | 'insert-after-previous'
  | 'insert-before-next'
  | 'place-in-previous'
  | 'place-in-next'

const getFragmentPlacementHint = (state: LedgerSafeCellState | null) => {
  if (state === 'excluded') {
    return 'Deliberate gap'
  }
  if (state === 'gap') {
    return 'Empty in this witness'
  }
  if (state === 'mapped') {
    return 'Already occupied'
  }
  return 'No row'
}

const getFragmentPlacementLabel = (row: AnchoredRow | null, state: LedgerSafeCellState | null, fallback: string) => {
  if (!row || !state) {
    return fallback
  }
  if (state === 'excluded') {
    return `Fill ${getRowLabel(row)} gap`
  }
  if (state === 'gap') {
    return `Place in ${getRowLabel(row)}`
  }
  return `Merge into ${getRowLabel(row)}`
}

type LedgerCrossTraditionPreviewRunStatus = 'running' | 'completed' | 'cancelled' | 'failed'

type LedgerCrossTraditionPreviewRunRecord = {
  runId: string
  status: LedgerCrossTraditionPreviewRunStatus
  startedAt: number
  endedAt?: number
  workId: string
  chapterId: string
  witnessIds: string[]
  witnessLabels: string[]
  sourceContextLabel: string
  targetContextLabel: string | null
  modelId: string | null
  candidateCount: number
  candidates: LedgerCrossTraditionCandidate[]
  error?: string | null
}

const createLedgerCrossTraditionPreviewRunId = (): string => (
  `xtrad-preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
)

const getLedgerCrossTraditionPreviewRunStorageKey = (chapterId: string): string => (
  chapterId ? `${LEDGER_CROSS_TRADITION_PREVIEW_RUN_STORAGE_PREFIX}.${chapterId}` : ''
)

const isLedgerCrossTraditionPreviewRunStatus = (
  value: unknown
): value is LedgerCrossTraditionPreviewRunStatus => (
  value === 'running' || value === 'completed' || value === 'cancelled' || value === 'failed'
)

const sanitizeLedgerCrossTraditionPreviewRunRecord = (
  value: unknown
): LedgerCrossTraditionPreviewRunRecord | null => {
  if (!value || typeof value !== 'object') {
    return null
  }
  const record = value as Partial<LedgerCrossTraditionPreviewRunRecord>
  const runId = String(record.runId || '').trim()
  const startedAt = Number(record.startedAt)
  if (!runId || !Number.isFinite(startedAt)) {
    return null
  }
  const status = isLedgerCrossTraditionPreviewRunStatus(record.status) ? record.status : 'failed'
  return {
    runId,
    status,
    startedAt,
    endedAt: Number.isFinite(Number(record.endedAt)) ? Number(record.endedAt) : undefined,
    workId: String(record.workId || ''),
    chapterId: String(record.chapterId || ''),
    witnessIds: Array.isArray(record.witnessIds) ? record.witnessIds.map(String).filter(Boolean) : [],
    witnessLabels: Array.isArray(record.witnessLabels) ? record.witnessLabels.map(String).filter(Boolean) : [],
    sourceContextLabel: String(record.sourceContextLabel || ''),
    targetContextLabel: record.targetContextLabel ? String(record.targetContextLabel) : null,
    modelId: record.modelId ? String(record.modelId) : null,
    candidateCount: Number.isFinite(Number(record.candidateCount)) ? Number(record.candidateCount) : 0,
    candidates: Array.isArray(record.candidates) ? record.candidates as LedgerCrossTraditionCandidate[] : [],
    error: record.error ? String(record.error) : null,
  }
}

const parseLedgerCrossTraditionPreviewRunHistory = (
  storageKey: string
): LedgerCrossTraditionPreviewRunRecord[] => {
  if (!storageKey) {
    return []
  }
  try {
    const parsed = JSON.parse(safeStorageGet(storageKey))
    return (Array.isArray(parsed) ? parsed : [])
      .map(sanitizeLedgerCrossTraditionPreviewRunRecord)
      .filter((record): record is LedgerCrossTraditionPreviewRunRecord => Boolean(record))
      .slice(0, LEDGER_CROSS_TRADITION_PREVIEW_RUN_HISTORY_LIMIT)
  } catch {
    return []
  }
}

const serializeLedgerCrossTraditionPreviewRunHistory = (
  storageKey: string,
  runs: LedgerCrossTraditionPreviewRunRecord[]
): void => {
  if (!storageKey) {
    return
  }
  safeStorageSet(
    storageKey,
    JSON.stringify(runs.slice(0, LEDGER_CROSS_TRADITION_PREVIEW_RUN_HISTORY_LIMIT))
  )
}

const upsertLedgerCrossTraditionPreviewRun = (
  runs: LedgerCrossTraditionPreviewRunRecord[],
  run: LedgerCrossTraditionPreviewRunRecord
): LedgerCrossTraditionPreviewRunRecord[] => [
  run,
  ...runs.filter(existing => existing.runId !== run.runId),
].slice(0, LEDGER_CROSS_TRADITION_PREVIEW_RUN_HISTORY_LIMIT)

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('name' in error)) {
    return false
  }
  return String((error as { name?: unknown }).name) === 'AbortError'
}

const formatLedgerCrossTraditionRunTime = (timestamp: number): string => {
  if (!Number.isFinite(timestamp)) {
    return 'Unknown time'
  }
  try {
    return new Date(timestamp).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Unknown time'
  }
}

const getLedgerCrossTraditionRunStatusLabel = (
  status: LedgerCrossTraditionPreviewRunStatus
): string => {
  if (status === 'completed') {
    return 'Complete'
  }
  if (status === 'cancelled') {
    return 'Cancelled'
  }
  if (status === 'failed') {
    return 'Failed'
  }
  return 'Running'
}

const getLedgerCrossTraditionRunStatusClassName = (
  status: LedgerCrossTraditionPreviewRunStatus
): string => {
  if (status === 'completed') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-700'
  }
  if (status === 'cancelled') {
    return 'border-gray-300 bg-gray-50 text-gray-600'
  }
  if (status === 'failed') {
    return 'border-red-300 bg-red-50 text-red-700'
  }
  return 'border-sky-300 bg-sky-50 text-sky-700'
}

type LedgerSafeInlineRibbonAction =
  | 'move-up'
  | 'move-down'
  | 'relocate'
  | 'undo-relocation'
  | 'insert-above'
  | 'insert-below'
  | 'mark-menu'
  | 'mark-gap'
  | 'mark-transposition'
  | 'unmark-transposition'
  | 'clear-gap'
  | 'split'
  | 'boundary'
  | 'merge'
  | 'shift-range-up'
  | 'shift-range-down'

type LedgerSafeInlineRibbonButton = {
  action: LedgerSafeInlineRibbonAction
  label: string
  glyph?: string
  title: string
  ariaLabel?: string
  testId: string
  primary?: boolean
  disabled?: boolean
}

type LedgerSafeInlineRibbonModel = {
  label: string
  buttons: LedgerSafeInlineRibbonButton[]
  markMenuOpen?: boolean
  canUnmarkTransposition?: boolean
}

export type LedgerSafeInlineRibbonPlacement = 'center' | 'start' | 'end'

const LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING = 12
const LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_SELECTOR = '[data-ledger-safe-matrix-viewport="true"]'

type LedgerSafeInlineRibbonViewportBounds = {
  left: number
  right: number
}

const estimateLedgerSafeInlineRibbonWidth = (ribbon: LedgerSafeInlineRibbonModel): number => {
  const labelWidth = Math.max(64, ribbon.label.length * 8 + 22)
  const buttonWidth = ribbon.buttons.reduce((total, button) => {
    const labelWidthEstimate = button.label.length * 9
    const glyphWidth = button.glyph ? 17 : 0
    return total + Math.max(64, labelWidthEstimate + glyphWidth + 22)
  }, 0)
  const gapsAndPadding = Math.max(0, ribbon.buttons.length) * 4 + 16
  return labelWidth + buttonWidth + gapsAndPadding
}

const getLedgerSafeInlineRibbonViewportBounds = (
  cellElement: HTMLElement
): LedgerSafeInlineRibbonViewportBounds | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const viewportElement = cellElement.closest<HTMLElement>(LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_SELECTOR)
  if (!viewportElement) {
    return {
      left: LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING,
      right: window.innerWidth - LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING
    }
  }

  const viewportRect = viewportElement.getBoundingClientRect()
  const verticalScrollbarWidth = Math.max(0, viewportElement.offsetWidth - viewportElement.clientWidth)
  const left = Math.max(
    LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING,
    viewportRect.left + LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING
  )
  const right = Math.min(
    window.innerWidth - LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING,
    viewportRect.right - verticalScrollbarWidth - LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING
  )

  if (right <= left) {
    return null
  }
  return { left, right }
}

export const resolveLedgerSafeInlineRibbonPlacement = (
  cellRect: DOMRect,
  ribbonWidth: number,
  viewportBounds?: LedgerSafeInlineRibbonViewportBounds | null
): LedgerSafeInlineRibbonPlacement => {
  if (typeof window === 'undefined') {
    return 'center'
  }

  const viewportLeft = viewportBounds?.left ?? LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING
  const viewportRight = viewportBounds?.right ?? window.innerWidth - LEDGER_SAFE_INLINE_RIBBON_VIEWPORT_PADDING
  const centerLeft = cellRect.left + (cellRect.width / 2) - (ribbonWidth / 2)
  const centerRight = centerLeft + ribbonWidth
  const startLeft = cellRect.left - 1
  const startRight = startLeft + ribbonWidth
  const endRight = cellRect.right + 1
  const endLeft = endRight - ribbonWidth
  const overflow = (left: number, right: number) => (
    Math.max(0, viewportLeft - left) + Math.max(0, right - viewportRight)
  )
  const centerOverflow = overflow(centerLeft, centerRight)

  if (centerOverflow === 0) {
    return 'center'
  }

  const startOverflow = overflow(startLeft, startRight)
  const endOverflow = overflow(endLeft, endRight)
  if (centerRight > viewportRight && endOverflow < centerOverflow) {
    return 'end'
  }
  if (centerLeft < viewportLeft && startOverflow < centerOverflow) {
    return 'start'
  }

  if (endOverflow < startOverflow) {
    return 'end'
  }
  return startOverflow < centerOverflow ? 'start' : 'center'
}

const normalizeInitialSidePanelTab = (tab: SidePanelTab | 'structure' | 'ledger' | 'pi' | undefined): SidePanelTab => (
  tab && tab !== 'ledger' && tab !== 'pi' && tab !== 'structure' ? tab : 'map'
)

const normalizeInitialLeftPanelTab = (
  tab: SidePanelTab | 'structure' | 'ledger' | 'pi' | undefined,
): LeftPanelTab => tab === 'structure' ? 'structure' : 'characters'

const clampPiDockHeight = (height: number): number => (
  Math.max(LEDGER_SAFE_PI_DOCK_MIN_HEIGHT, Math.min(LEDGER_SAFE_PI_DOCK_MAX_HEIGHT, height))
)

const readStoredPiDockExpanded = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.localStorage.getItem(LEDGER_SAFE_PI_DOCK_EXPANDED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

const readStoredPiDockHeight = (): number => {
  if (typeof window === 'undefined') {
    return LEDGER_SAFE_PI_DOCK_DEFAULT_HEIGHT
  }
  try {
    const storedHeight = Number(window.localStorage.getItem(LEDGER_SAFE_PI_DOCK_HEIGHT_STORAGE_KEY))
    return Number.isFinite(storedHeight)
      ? clampPiDockHeight(storedHeight)
      : LEDGER_SAFE_PI_DOCK_DEFAULT_HEIGHT
  } catch {
    return LEDGER_SAFE_PI_DOCK_DEFAULT_HEIGHT
  }
}

type BoundaryDragState = {
  startX: number
  startBoundaryWordIndex: number
}

type PiDriftWindow = {
  rowStart: number
  rowEnd: number
  rowIds: string[]
  affectedWitnessIds: string[]
}

type PiReviewLogEntry = {
  id: string
  kind: 'sync' | 'auth' | 'agent' | 'drift' | 'preview' | 'apply' | 'reject' | 'error'
  message: string
}

type LedgerSafeWitnessPropagationProposal = {
  targetWitnessId: string
  method: string
  strategy?: BatchAlignmentStrategy | 'ledger-conditioned' | 'single-source'
  segmentCount: number
  splitIndices: number[]
  exemplarWitnessIds: string[]
  rowAssignments: WorkbenchPropagationRowAssignment[]
  application: WorkbenchPropagationApplication
  notes?: string
  readOnly?: boolean
  translationProgress?: {
    completedBatches: number
    totalBatches: number
    complete: boolean
  }
  diagnostics?: WorkbenchPropagationDiagnostics
}

type LedgerSafeSemanticCandidateApplyResult = {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  selectedCell: LedgerSafeSelectedCell
}

const applySemanticCandidateToRows = ({
  rows,
  candidate
}: {
  rows: AnchoredRow[]
  candidate: WorkbenchSemanticAlignmentCandidate
}): LedgerSafeSemanticCandidateApplyResult => {
  const destinationRow = rows.find(row => row.id === candidate.sourceRowId)
  const originRow = rows.find(row => row.id === candidate.targetRowId)
  if (!destinationRow) {
    throw new Error('Semantic preview destination row was not found')
  }
  if (!originRow) {
    throw new Error('Semantic preview source unit row was not found')
  }
  if (destinationRow.id === originRow.id) {
    throw new Error('Semantic candidate is already in this row')
  }
  if (getLedgerSafeCellState(destinationRow, candidate.targetWitnessId) === 'mapped') {
    throw new Error('Destination cell already has a mapped unit for this witness')
  }

  const originMapping = originRow.unitMappings.find(mapping =>
    mapping.witnessId === candidate.targetWitnessId &&
    mapping.unitUuid === candidate.targetUnitUuid &&
    !mapping.excluded
  )
  if (!originMapping) {
    throw new Error('Semantic candidate unit is no longer available in its original row')
  }

  const timestamp = new Date().toISOString()
  const movedMapping: UnitMapping = {
    ...originMapping,
    textSnapshot: originMapping.textSnapshot || candidate.targetText,
    confidence: candidate.score,
    translationStatus: originMapping.translationStatus || 'mapped',
    excluded: false,
    addedAt: originMapping.addedAt || timestamp
  }
  const changedRowIds = Array.from(new Set([originRow.id, destinationRow.id]))
  const updatedRows = rows.map(row => {
    if (row.id === originRow.id) {
      return {
        ...row,
        unitMappings: row.unitMappings.filter(mapping => !(
          mapping.witnessId === candidate.targetWitnessId &&
          mapping.unitUuid === candidate.targetUnitUuid
        )),
        status: row.status === 'confirmed' ? 'needs-review' : row.status,
        updatedAt: timestamp
      }
    }
    if (row.id === destinationRow.id) {
      return {
        ...row,
        unitMappings: [
          ...row.unitMappings.filter(mapping => mapping.witnessId !== candidate.targetWitnessId),
          movedMapping
        ],
        status: row.status === 'confirmed' ? 'needs-review' : row.status,
        updatedAt: timestamp
      }
    }
    return row
  })

  return {
    updatedRows,
    changedRowIds,
    selectedCell: {
      rowId: destinationRow.id,
      witnessId: candidate.targetWitnessId
    }
  }
}

type BatchAlignmentStrategy = 'translation-aligned' | 'ledger-span-aligned'

type EnrollmentQueueStatus = 'pending' | 'rerun' | 'protected'

type EnrollmentQueueItem = {
  witness: Witness
  closestRepresentative: Witness | null
  similarity: number
  distance: number
  rank: number
  status: EnrollmentQueueStatus
}

type SelectedUnitRepairScope = {
  witness: Witness | null
  selectedRows: AnchoredRow[]
  requestRows: AnchoredRow[]
  contextRowIds: string[]
  anchorRowIds: string[]
  label: string
  reason: string | null
}

type SelectedRepairTargetSpan = {
  startChar: number
  endChar: number
  text: string
}

type SelectedUnitRepairRunState = {
  resumeScopeId: string
  targetWitnessId: string
  selectedRowIds: string[]
  requestRowIds: string[]
  repairCandidateRowIds: string[]
  contextRowIds: string[]
  anchorRowIds: string[]
  repairTargetSpan: SelectedRepairTargetSpan
  modelId: string
  thinkingLevel: WorkbenchThinkingLevel
  resumeState: TranslationBatchResumeState
  pendingResumeState: TranslationBatchResumeState | null
  failure: WorkbenchPropagationFailure | null
}

type SelectedUnitRepairUndoState = {
  id: string
  witnessId: string
  witnessSigla: string
  previousSegmentationResult: Witness['segmentation_result']
  previousProcessingStatus: Witness['processing_status']
  appliedSegmentationResult: Witness['segmentation_result']
  selectedRowIds: string[]
  changedRowIds: string[]
  beforeRows: AnchoredRow[]
  afterRows: AnchoredRow[]
  createdAt: string
}

type LedgerSafeCellPlacementMode =
  | {
      kind: 'transposition'
      originRowId: string
      witnessId: string
    }
  | {
      kind: 'relocation'
      sourceRowIds: string[]
      witnessId: string
      unitUuids: string[]
    }

type LedgerSafeRelocationDestination = {
  startRowId: string
  targetRowIds: string[]
  eligible: boolean
  sameLocation: boolean
  conflictRowIds: string[]
  reason: string | null
}

type LedgerSafeRelocationUndoState = {
  id: string
  witnessId: string
  witnessSigla: string
  sourceRowIds: string[]
  targetRowIds: string[]
  changedRowIds: string[]
  beforeRows: AnchoredRow[]
  afterRows: AnchoredRow[]
  createdAt: string
}

const cloneRowsForSelectedRepairUndo = (rows: AnchoredRow[]): AnchoredRow[] => (
  rows.map(row => JSON.parse(JSON.stringify(row)) as AnchoredRow)
)

const serializeSelectedRepairUndoValue = (value: unknown): string => JSON.stringify(value ?? null)

const normalizeSelectedRepairWitnessMappings = (
  row: AnchoredRow | undefined,
  witnessId: string
): Array<Omit<UnitMapping, 'addedAt'>> => (
  (row?.unitMappings || [])
    .filter(mapping => mapping.witnessId === witnessId)
    .map(mapping => ({
      witnessId: mapping.witnessId,
      unitUuid: mapping.unitUuid,
      ...(mapping.unitLabel ? { unitLabel: mapping.unitLabel } : {}),
      ...(mapping.textSnapshot ? { textSnapshot: mapping.textSnapshot } : {}),
      ...(typeof mapping.confidence === 'number' ? { confidence: mapping.confidence } : {}),
      ...(mapping.translationStatus ? { translationStatus: mapping.translationStatus } : {}),
      ...(mapping.excluded ? { excluded: true } : {}),
    }))
    .sort((left, right) => (
      left.unitUuid.localeCompare(right.unitUuid)
      || String(left.excluded || false).localeCompare(String(right.excluded || false))
      || String(left.translationStatus || '').localeCompare(String(right.translationStatus || ''))
    ))
)

const serializeSelectedRepairWitnessMappings = (
  row: AnchoredRow | undefined,
  witnessId: string
): string => JSON.stringify(normalizeSelectedRepairWitnessMappings(row, witnessId))

const serializeSelectedRepairSegmentationResult = (
  result: Witness['segmentation_result']
): string => {
  if (!result) {
    return 'null'
  }
  const units = (result.chunk_results || []).flatMap(chunk => (
    (chunk.micro_units || []).map(unit => ({
      unitUuid: String(unit.unit_uuid || unit.unit_id || ''),
      unitLabel: unit.unit_label || '',
      text: unit.unit_text_content || '',
      start: typeof unit.start_position === 'number' ? unit.start_position : null,
      end: typeof unit.end_position === 'number' ? unit.end_position : null,
    }))
  ))
  return JSON.stringify({
    idRemap: result.id_remap || null,
    units,
  })
}

const restoreSelectedRepairWitnessMappings = ({
  currentRow,
  beforeRow,
  expectedAfterRow,
  witnessId,
  timestamp,
}: {
  currentRow: AnchoredRow
  beforeRow: AnchoredRow
  expectedAfterRow: AnchoredRow | undefined
  witnessId: string
  timestamp: string
}): AnchoredRow => {
  const restoredMappings = cloneRowsForSelectedRepairUndo([beforeRow])[0]?.unitMappings
    .filter(mapping => mapping.witnessId === witnessId) || []
  return {
    ...currentRow,
    unitMappings: [
      ...currentRow.unitMappings.filter(mapping => mapping.witnessId !== witnessId),
      ...restoredMappings,
    ],
    status: expectedAfterRow && currentRow.status === expectedAfterRow.status
      ? beforeRow.status
      : currentRow.status,
    updatedAt: timestamp,
  }
}

const getOrderedMicroUnits = (witness: Witness): MicroUnit[] => (
  (witness.segmentation_result?.chunk_results || [])
    .flatMap(chunk => Array.isArray(chunk?.micro_units) ? chunk.micro_units as MicroUnit[] : [])
    .filter(unit => (
      typeof unit.start_position === 'number'
      && typeof unit.end_position === 'number'
      && unit.end_position > unit.start_position
    ))
    .sort((left, right) => {
      if (left.start_position !== right.start_position) {
        return left.start_position - right.start_position
      }
      return left.end_position - right.end_position
    })
)

const deriveSelectedRepairTargetSpan = ({
  witness,
  selectedRows,
}: {
  witness: Witness
  selectedRows: AnchoredRow[]
}): SelectedRepairTargetSpan | null => {
  const sourceText = witness.uploaded_file?.content || ''
  if (!sourceText.trim() || !witness.segmentation_result) {
    return null
  }
  const orderedUnits = getOrderedMicroUnits(witness)
  if (orderedUnits.length === 0) {
    return null
  }
  const unitIndex = buildUnitReferenceIndexWithRemap({
    units: orderedUnits,
    idRemap: witness.segmentation_result.id_remap,
  })
  const spans = selectedRows
    .map(row => {
      const mapping = row.unitMappings.find(candidate => candidate.witnessId === witness.id && !candidate.excluded)
      if (!mapping) {
        return null
      }
      const index = unitIndex.get(mapping.unitUuid)
      const unit = typeof index === 'number' ? orderedUnits[index] : undefined
      if (!unit) {
        return null
      }
      return {
        startChar: unit.start_position,
        endChar: unit.end_position,
      }
    })
    .filter((span): span is { startChar: number; endChar: number } => Boolean(span))

  if (spans.length !== selectedRows.length || spans.length === 0) {
    return null
  }
  const startChar = Math.min(...spans.map(span => span.startChar))
  const endChar = Math.max(...spans.map(span => span.endChar))
  if (startChar < 0 || endChar <= startChar || endChar > sourceText.length) {
    return null
  }
  const text = sourceText.slice(startChar, endChar)
  return text.trim() ? { startChar, endChar, text } : null
}

const isTranslationAlignmentWitness = (witness: Witness): boolean => {
  const normalizedLanguage = (witness.language || '').trim().toLowerCase()
  return !['', 'arabic', 'ar'].includes(normalizedLanguage)
}

const getWitnessLanguageCode = (witness: Witness): string => (
  (witness.language || '').trim().toLowerCase()
)

const getWitnessLanguageProbeText = (witness: Witness): string => {
  const sourceText = witness.uploaded_file?.content || ''
  if (sourceText) {
    return sourceText
  }
  return (witness.segmentation_result?.chunk_results || [])
    .flatMap(chunk => chunk.micro_units || [])
    .map(unit => String(unit.unit_text_content || ''))
    .join(' ')
}

const isArabicDiffWitness = (witness: Witness): boolean => {
  const language = getWitnessLanguageCode(witness)
  if (language) {
    return language === 'arabic' || language === 'ar'
  }
  return hasArabicText(getWitnessLanguageProbeText(witness))
}

const getBatchAlignmentStrategy = (witness: Witness): BatchAlignmentStrategy => (
  isTranslationAlignmentWitness(witness) ? 'translation-aligned' : 'ledger-span-aligned'
)

const getBatchAlignmentLabel = (strategy: BatchAlignmentStrategy): string => (
  strategy === 'ledger-span-aligned' ? 'collation span enrollment' : 'translation alignment'
)

const getBatchAlignmentReadyMessage = (strategy: BatchAlignmentStrategy): string => (
  strategy === 'ledger-span-aligned'
    ? 'Collation span enrollment preview ready.'
    : 'Translation alignment preview ready.'
)

const getBatchAlignmentStrategyFromMethod = (
  method: string | undefined,
  fallbackWitness: Witness,
): BatchAlignmentStrategy => (
  method?.endsWith('ledger-span-alignment')
    ? 'ledger-span-aligned'
    : getBatchAlignmentStrategy(fallbackWitness)
)

const tokenizeDistanceText = (value: string): string[] => (
  (value.toLowerCase().match(/[a-z0-9\u0600-\u06ff]+/g) || [])
)

const calculateWitnessSimilarity = (
  leftTokens: ReadonlySet<string>,
  rightTokens: ReadonlySet<string>,
): number => {
  if (leftTokens.size === 0 || rightTokens.size === 0) {
    return 0
  }
  let overlap = 0
  leftTokens.forEach(token => {
    if (rightTokens.has(token)) {
      overlap += 1
    }
  })
  return overlap / Math.max(leftTokens.size, rightTokens.size)
}

const deriveMappedWitnessIds = (rows: AnchoredRow[]): Set<string> => {
  const witnessIds = new Set<string>()
  rows.forEach(row => {
    row.unitMappings.forEach(mapping => {
      if (mapping.witnessId && !mapping.excluded) {
        witnessIds.add(mapping.witnessId)
      }
    })
  })
  return witnessIds
}

type LedgerSafeStudioRowProjection = {
  rowProjection: LedgerSafeRowProjection
  ledgerSceneHeaders: ReturnType<typeof buildLedgerSafeSceneHeaders>
  hasBuildImportRows: boolean
  hasStagedImportFingerprint: boolean
  sceneBackfillSourceRun: string | null
  sceneBackfillMissingRowCount: number
  enrollmentMappedWitnessIdSet: Set<string>
  emptyLedgerRows: AnchoredRow[]
  emptyLedgerRowRuns: LedgerSafeEmptyRowRun[]
}

const reconcileLedgerSafeStudioRowProjection = ({
  previous,
  scopeKey,
  chapterId,
  rows,
}: {
  previous: LedgerSafeStudioRowProjection | null
  scopeKey: string
  chapterId: string
  rows: AnchoredRow[]
}): LedgerSafeStudioRowProjection => {
  const rowProjection = reconcileLedgerSafeRowProjection({
    previous: previous?.rowProjection,
    scopeKey,
    rows,
  })
  if (previous?.rowProjection === rowProjection) {
    return previous
  }
  return {
    rowProjection,
    ledgerSceneHeaders: buildLedgerSafeSceneHeaders(rows),
    hasBuildImportRows: Boolean(chapterId)
      && rows.some(row => row.id.startsWith(`build_import::${chapterId}::`)),
    hasStagedImportFingerprint: hasLedgerSafeStagedImportFingerprint(rows),
    sceneBackfillSourceRun: getMostCommonLedgerSafeSourceRun(rows),
    sceneBackfillMissingRowCount: rows.filter(row => !getAnchoredRowSceneMetadata(row)).length,
    enrollmentMappedWitnessIdSet: deriveMappedWitnessIds(rows),
    emptyLedgerRows: rows.filter(isLedgerSafeRowTrulyEmpty),
    emptyLedgerRowRuns: summarizeLedgerSafeEmptyRowRuns(rows),
  }
}

const deriveLedgerRepresentativeWitnesses = ({
  rows,
  scopeWitnesses,
  targetWitnessIds,
  fallbackWitness,
}: {
  rows: AnchoredRow[]
  scopeWitnesses: Witness[]
  targetWitnessIds: string[]
  fallbackWitness: Witness
}): Witness[] => {
  const targetSet = new Set(targetWitnessIds)
  const witnessOrder = new Map(scopeWitnesses.map((witness, index) => [witness.id, index]))
  const mappingCounts = new Map<string, number>()
  rows.forEach(row => {
    row.unitMappings.forEach(mapping => {
      if (!mapping.witnessId || mapping.excluded || targetSet.has(mapping.witnessId)) {
        return
      }
      mappingCounts.set(mapping.witnessId, (mappingCounts.get(mapping.witnessId) || 0) + 1)
    })
  })
  const representatives = scopeWitnesses
    .filter(witness => mappingCounts.has(witness.id) && !isAutoPropagatedSegmentation(witness.segmentation_result))
    .sort((left, right) => (
      (mappingCounts.get(right.id) || 0) - (mappingCounts.get(left.id) || 0)
      || (witnessOrder.get(left.id) || 0) - (witnessOrder.get(right.id) || 0)
    ))
  return representatives.length > 0 ? representatives : [fallbackWitness]
}

const rankEnrollmentTargetsByRepresentativeDistance = ({
  targets,
  representatives,
}: {
  targets: Witness[]
  representatives: Witness[]
}): Array<Omit<EnrollmentQueueItem, 'rank' | 'status'>> => {
  const targetOrder = new Map(targets.map((witness, index) => [witness.id, index]))
  // Each full source text participates in many pairs. Tokenize it once for
  // this ranking pass; keeping the cache local prevents stale source edits.
  const tokensByText = new Map<string, Set<string>>()
  const tokensFor = (witness: Witness): Set<string> => {
    const text = witness.uploaded_file?.content || ''
    let tokens = tokensByText.get(text)
    if (!tokens) {
      tokens = new Set(tokenizeDistanceText(text))
      tokensByText.set(text, tokens)
    }
    return tokens
  }

  return targets
    .map(witness => {
      let closestRepresentative: Witness | null = null
      let similarity = 0
      representatives.forEach(representative => {
        const candidateSimilarity = calculateWitnessSimilarity(
          tokensFor(witness),
          tokensFor(representative),
        )
        if (candidateSimilarity >= similarity) {
          similarity = candidateSimilarity
          closestRepresentative = representative
        }
      })
      return {
        witness,
        closestRepresentative,
        similarity,
        distance: 1 - similarity,
      }
    })
    .sort((left, right) => (
      right.similarity - left.similarity
      || (targetOrder.get(left.witness.id) || 0) - (targetOrder.get(right.witness.id) || 0)
    ))
}

const combineTranslationAlignmentDiagnostics = (
  diagnostics: WorkbenchPropagationDiagnostics[],
): WorkbenchPropagationDiagnostics | undefined => {
  if (diagnostics.length === 0) {
    return undefined
  }
  const [first] = diagnostics
  const parseStatus = diagnostics.some(item => item.parseStatus === 'failed')
    ? 'failed'
    : diagnostics.some(item => item.parseStatus === 'recovered')
      ? 'recovered'
      : 'clean'
  const finishReasons = Array.from(new Set(diagnostics.map(item => item.finishReason).filter(Boolean)))
  const parseErrors = diagnostics.map(item => item.parseError).filter(Boolean)
  return {
    targetWitnessId: first.targetWitnessId,
    modelId: first.modelId,
    prompt: diagnostics
      .map((item, index) => `=== BATCH ${index + 1}/${diagnostics.length} ===\n${item.prompt}`)
      .join('\n\n'),
    systemInstruction: first.systemInstruction,
    generationConfig: {
      ...first.generationConfig,
      totalBatches: diagnostics.length,
    },
    rawText: diagnostics
      .map((item, index) => `=== BATCH ${index + 1}/${diagnostics.length} ===\n${item.rawText}`)
      .join('\n\n'),
    finishReason: finishReasons.length > 0 ? finishReasons.join(', ') : null,
    rawResponse: {
      batches: diagnostics.map(item => item.rawResponse || {
        raw_text: item.rawText,
        finish_reason: item.finishReason,
      }),
    },
    parseStatus,
    parseError: parseErrors.length > 0 ? parseErrors[parseErrors.length - 1] : null,
  }
}

const getTranslationResumeRowOffset = (resumeState: TranslationBatchResumeState): number => (
  typeof resumeState.rowOffset === 'number' ? resumeState.rowOffset : 0
)

const getCompletedTranslationBatchCount = ({
  batchIndex,
  totalBatches,
}: {
  batchIndex: number
  totalBatches: number
}): number => Math.max(0, Math.min(batchIndex, totalBatches))

const describeTranslationResumeState = ({
  resumeState,
  failure,
}: {
  resumeState: TranslationBatchResumeState
  failure?: WorkbenchPropagationFailure | null
}): string => {
  if (failure?.error) {
    return failure.error
  }
  const completedBatches = getCompletedTranslationBatchCount(resumeState)
  if (getTranslationResumeRowOffset(resumeState) > 0) {
    return `Previewed into batch ${resumeState.batchIndex + 1}/${resumeState.totalBatches}.`
  }
  if (completedBatches > 0) {
    return `Previewed batch ${completedBatches}/${resumeState.totalBatches}.`
  }
  return 'Alignment preview ready to resume.'
}

const buildTranslationRetryAttemptRecord = ({
  batchIndex,
  rowOffset,
  error,
  diagnostics,
  failedRowId,
}: {
  batchIndex: number
  rowOffset: number
  error: string
  diagnostics?: WorkbenchPropagationDiagnostics
  failedRowId?: string
}) => ({
  batchIndex,
  rowOffset,
  error,
  automatic: true,
  createdAt: Date.now(),
  ...(failedRowId ? { failedRowId } : {}),
  ...(diagnostics ? { diagnostics } : {}),
})

const mergeTranslationPlanNotes = (...parts: Array<string | null | undefined>): string | undefined => {
  const note = parts.filter((value): value is string => Boolean(value && value.trim())).join(' · ')
  return note || undefined
}

const deriveApiPlanSegmentCount = (plan: {
  splitIndices: number[]
  rowAssignments: Array<{ segmentIndex: number | null }>
}): number => {
  const hasMappedSegment = plan.rowAssignments.some(assignment => typeof assignment.segmentIndex === 'number')
  return hasMappedSegment || plan.splitIndices.length > 0 ? plan.splitIndices.length + 1 : 0
}

const deriveSelectablePropagationTargetWitnessIds = (
  preview: WorkbenchPropagationPreview,
): string[] => (
  preview.plans
    .filter(plan => (
      !plan.protected
      && plan.witness.id !== preview.sourceWitness.id
      && plan.segmentCount > 0
    ))
    .map(plan => plan.witness.id)
)

const mergeSelectablePropagationTargetWitnessIds = (
  previous: string[],
  preview: WorkbenchPropagationPreview,
  witnessId: string,
): string[] => {
  const selectableWitnessIdSet = new Set(deriveSelectablePropagationTargetWitnessIds(preview))
  const next = previous.filter(candidate => selectableWitnessIdSet.has(candidate))
  if (selectableWitnessIdSet.has(witnessId) && !next.includes(witnessId)) {
    next.push(witnessId)
  }
  return next
}

const replacePreviewPlan = (
  preview: WorkbenchPropagationPreview,
  witnessId: string,
  updater: (plan: WorkbenchPropagationPreview['plans'][number]) => WorkbenchPropagationPreview['plans'][number],
): WorkbenchPropagationPreview => ({
  ...preview,
  plans: preview.plans.map(plan => plan.witness.id === witnessId ? updater(plan) : plan),
})

type LedgerSafeEditorialDescriptor = {
  kind: LedgerSafeEditorialOperationKind
  params: Record<string, unknown>
  source?: LedgerSafeEditorialEventSource
  affectedWitnessIds?: string[]
  metadata?: Record<string, unknown>
}

const IS_LEDGER_SAFE_DEBUG_EXPORT_ENABLED = process.env.NODE_ENV !== 'production'
const IS_LEDGER_OCCUPANCY_VARIANCE_ENABLED = true
const LEDGER_OCCUPANCY_INK_COLOR = '#0f172a'
const LEDGER_OCCUPANCY_PAPER_COLOR = '#fffdfa'
const LEDGER_OCCUPANCY_PAPER_ALT_COLOR = '#ede8de'
const LEDGER_OCCUPANCY_PI_PREVIEW_COLOR = '#2563eb'
const LEDGER_OCCUPANCY_PI_APPLIED_COLOR = '#047857'
const LEDGER_OCCUPANCY_HEIGHT_EMPTY_ROW_PX = 3
const LEDGER_OCCUPANCY_HEIGHT_MIN_ROW_PX = 6
const LEDGER_OCCUPANCY_HEIGHT_MAX_ROW_PX = 28
const LEDGER_OCCUPANCY_HEIGHT_LOG_SCALE = 5

const LEDGER_OCCUPANCY_VARIANCE_COLORS: Record<LedgerOccupancyVarianceBucket, string> = {
  unified: '#ebe6db',
  near: '#c8c1b1',
  drifting: '#908577',
  conflict: '#4a4438',
  severe: '#1a1612'
}

const LEDGER_OCCUPANCY_REFERENCE_READING_COLOR = LEDGER_OCCUPANCY_INK_COLOR
const LEDGER_OCCUPANCY_REFERENCE_READING_STRIPES = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_INK_COLOR} 0, ${LEDGER_OCCUPANCY_INK_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_COLOR} 4px)`

const LEDGER_OCCUPANCY_VARIANCE_STOPS = [
  { score: 0, color: LEDGER_OCCUPANCY_VARIANCE_COLORS.unified },
  { score: 0.16, color: LEDGER_OCCUPANCY_VARIANCE_COLORS.near },
  { score: 0.34, color: LEDGER_OCCUPANCY_VARIANCE_COLORS.drifting },
  { score: 0.52, color: LEDGER_OCCUPANCY_VARIANCE_COLORS.conflict },
  { score: 0.72, color: LEDGER_OCCUPANCY_VARIANCE_COLORS.severe }
] as const

const LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE: React.CSSProperties = {
  outline: `1px solid ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`,
  outlineOffset: '1px'
}
const LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE: React.CSSProperties = {
  outline: `1px dashed ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`,
  outlineOffset: '1px'
}

const getLedgerOccupancyUnitTextLength = (value: string): number => (
  Array.from(value.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '')).length
)

const getLedgerOccupancyHeightRowPx = (rowMaxUnitTextLength: number): number => {
  if (rowMaxUnitTextLength <= 0) {
    return LEDGER_OCCUPANCY_HEIGHT_EMPTY_ROW_PX
  }
  return Math.max(
    LEDGER_OCCUPANCY_HEIGHT_MIN_ROW_PX,
    Math.min(
      LEDGER_OCCUPANCY_HEIGHT_MAX_ROW_PX,
      Math.round(LEDGER_OCCUPANCY_HEIGHT_MIN_ROW_PX + Math.log1p(rowMaxUnitTextLength) * LEDGER_OCCUPANCY_HEIGHT_LOG_SCALE)
    )
  )
}

const getLedgerOccupancyHeightRatio = (unitTextLength: number, rowMaxUnitTextLength: number): number => {
  if (unitTextLength <= 0 || rowMaxUnitTextLength <= 0) {
    return 0
  }
  return Math.max(0.08, Math.min(1, unitTextLength / rowMaxUnitTextLength))
}

const getLedgerOccupancyGlobalLengthRatio = (unitTextLength: number, globalMaxUnitTextLength: number): number => {
  if (unitTextLength <= 0 || globalMaxUnitTextLength <= 0) {
    return 0
  }
  return Math.max(0.06, Math.min(1, unitTextLength / globalMaxUnitTextLength))
}

const getLedgerOccupancyLengthTone = (ratio: number): 'short' | 'medium' | 'long' => {
  if (ratio >= 0.66) {
    return 'long'
  }
  if (ratio >= 0.34) {
    return 'medium'
  }
  return 'short'
}

const hexToRgb = (value: string): [number, number, number] => {
  const normalized = value.replace('#', '')
  const expanded = normalized.length === 3
    ? normalized.split('').map(char => `${char}${char}`).join('')
    : normalized
  return [
    Number.parseInt(expanded.slice(0, 2), 16),
    Number.parseInt(expanded.slice(2, 4), 16),
    Number.parseInt(expanded.slice(4, 6), 16)
  ]
}

const rgbToHex = (red: number, green: number, blue: number): string =>
  `#${[red, green, blue]
    .map(channel => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0'))
    .join('')}`

const interpolateHexColor = (start: string, end: string, progress: number): string => {
  const [startRed, startGreen, startBlue] = hexToRgb(start)
  const [endRed, endGreen, endBlue] = hexToRgb(end)
  return rgbToHex(
    startRed + ((endRed - startRed) * progress),
    startGreen + ((endGreen - startGreen) * progress),
    startBlue + ((endBlue - startBlue) * progress)
  )
}

const getLedgerVarianceColor = (score: number): string => {
  const clampedScore = Math.max(0, Math.min(1, score))
  for (let index = 0; index < LEDGER_OCCUPANCY_VARIANCE_STOPS.length - 1; index += 1) {
    const current = LEDGER_OCCUPANCY_VARIANCE_STOPS[index]
    const next = LEDGER_OCCUPANCY_VARIANCE_STOPS[index + 1]
    if (clampedScore <= next.score) {
      const range = next.score - current.score
      const progress = range > 0 ? (clampedScore - current.score) / range : 0
      return interpolateHexColor(current.color, next.color, progress)
    }
  }
  return LEDGER_OCCUPANCY_VARIANCE_STOPS[LEDGER_OCCUPANCY_VARIANCE_STOPS.length - 1].color
}

const getLedgerOccupancyStateLabel = ({
  cellState,
  rowIsStructurallyEmpty
}: {
  cellState: LedgerSafeCellState
  rowIsStructurallyEmpty: boolean
}): string => {
  if (rowIsStructurallyEmpty) {
    return 'empty row'
  }
  if (cellState === 'mapped') {
    return 'occupied'
  }
  return 'deliberate gap'
}

const getLedgerVarianceBucket = (score: number): LedgerOccupancyVarianceBucket => {
  if (score >= 0.72) {
    return 'severe'
  }
  if (score >= 0.52) {
    return 'conflict'
  }
  if (score >= 0.34) {
    return 'drifting'
  }
  if (score >= 0.16) {
    return 'near'
  }
  return 'unified'
}

const getLedgerVarianceLabel = (bucket: LedgerOccupancyVarianceBucket): string => {
  switch (bucket) {
    case 'severe':
      return 'severe conflict'
    case 'conflict':
      return 'major conflict'
    case 'drifting':
      return 'drifting'
    case 'near':
      return 'near match'
    default:
      return 'unified'
  }
}

const getSharedSiglaPrefix = (witnesses: Witness[]): string => {
  if (witnesses.length < 2) {
    return ''
  }
  const [firstWitness, ...restWitnesses] = witnesses
  const firstSigla = firstWitness?.sigla || ''
  if (!firstSigla) {
    return ''
  }
  let prefix = firstSigla
  restWitnesses.forEach(witness => {
    while (prefix && !witness.sigla.startsWith(prefix)) {
      prefix = prefix.slice(0, -1)
    }
  })
  while (prefix && /[A-Za-z0-9\u0600-\u06FF]$/.test(prefix)) {
    prefix = prefix.slice(0, -1)
  }
  return prefix.length >= 2 ? prefix : ''
}

const getDiffTokenClasses = (
  op: EditOp | undefined,
  appearance: DiffAppearance,
  morph?: 'near',
  substitutionStrength?: 'mild' | 'strong'
): string => {
  const baseUnderline = appearance === 'high' || appearance === 'arabic'
    ? 'underline decoration-4 underline-offset-2'
    : appearance === 'medium'
      ? 'underline decoration-2 underline-offset-2'
      : 'underline decoration-1 underline-offset-1'

  if (morph === 'near') {
    return appearance === 'arabic'
      ? `${baseUnderline} rounded-[3px] bg-emerald-100/80 px-0.5 text-emerald-900 decoration-dotted decoration-emerald-700/90`
      : `${baseUnderline} rounded-[3px] bg-emerald-100/80 px-0.5 text-emerald-900 decoration-dotted decoration-green-700/80`
  }

  if (op === 'insert') {
    return appearance === 'arabic'
      ? `${baseUnderline} rounded-[2px] bg-sky-200/80 px-0.5 decoration-sky-800/90`
      : `${baseUnderline} rounded-[2px] bg-blue-100/80 px-0.5 decoration-blue-600/80`
  }
  if (op === 'substitute') {
    return appearance === 'arabic'
      ? `${baseUnderline} rounded-[2px] bg-amber-200/80 px-0.5 decoration-amber-800/90 ${substitutionStrength === 'strong' ? 'font-semibold text-amber-950' : ''}`
      : `${baseUnderline} rounded-[2px] bg-amber-100/80 px-0.5 decoration-amber-700/80 ${substitutionStrength === 'strong' ? 'font-semibold text-amber-950' : ''}`
  }
  if (op === 'delete') {
    return appearance === 'arabic'
      ? 'rounded-[2px] bg-slate-100/70 px-0.5 text-slate-500/70 line-through decoration-slate-500/40 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.16)]'
      : 'rounded-[2px] bg-slate-100/70 px-0.5 text-slate-500/70 line-through decoration-slate-500/40 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.16)]'
  }
  return ''
}

const getApparatusVariantLabel = (variant: ApparatusVariantKind): string => {
  switch (variant) {
    case 'grammatical_shift':
      return 'Grammatical shift'
    case 'morph':
      return 'Morphological variation'
    case 'vocalization':
      return 'Vocalization variation'
    case 'orthographic':
      return 'Orthographic variation'
    case 'synonym':
      return 'Synonymous variation'
    case 'lexical_substitution':
      return 'Lexical substitution'
    case 'reordering':
      return 'Reordering'
    case 'block_substitution':
      return 'Block substitution'
    case 'insertion':
      return 'Insertion'
    case 'deletion':
      return 'Deletion'
    default:
      return variant
  }
}

type ApparatusTokenEmphasis = 'standard' | 'faint'

const getApparatusTokenClasses = (
  variant: ApparatusVariantKind | undefined,
  emphasis: ApparatusTokenEmphasis = 'standard'
): string => {
  if (!variant) {
    return ''
  }
  if (emphasis === 'faint' && variant !== 'insertion' && variant !== 'deletion') {
    switch (variant) {
      case 'grammatical_shift':
        return 'rounded-[2px] bg-indigo-50/55 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(79,70,229,0.24)]'
      case 'morph':
        return 'rounded-[2px] bg-emerald-50/55 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(31,122,66,0.24)]'
      case 'vocalization':
        return 'rounded-[2px] bg-teal-50/55 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(13,148,136,0.24)]'
      case 'orthographic':
        return 'rounded-[2px] bg-violet-50/55 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(109,40,217,0.24)]'
      case 'synonym':
        return 'rounded-[2px] bg-yellow-50/60 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(180,131,0,0.24)]'
      case 'lexical_substitution':
        return 'rounded-[2px] bg-amber-50/60 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(196,107,25,0.25)]'
      case 'reordering':
        return 'rounded-[2px] bg-fuchsia-50/65 px-0.5 text-slate-950 shadow-[inset_0_-1px_0_rgba(162,28,175,0.34)]'
      case 'block_substitution':
        return 'rounded-[2px] bg-rose-50/55 px-0.5 text-slate-900 shadow-[inset_0_-1px_0_rgba(190,24,93,0.24)]'
      default:
        return ''
    }
  }
  switch (variant) {
    case 'grammatical_shift':
      return 'rounded-[2px] bg-indigo-100/95 px-0.5 text-indigo-950 shadow-[inset_0_-1px_0_rgba(79,70,229,0.72)]'
    case 'morph':
      return 'rounded-[2px] bg-emerald-100/90 px-0.5 text-emerald-950 shadow-[inset_0_-1px_0_rgba(31,122,66,0.75)]'
    case 'vocalization':
      return 'rounded-[2px] bg-teal-100/90 px-0.5 text-teal-950 shadow-[inset_0_-1px_0_rgba(13,148,136,0.72)]'
    case 'orthographic':
      return 'rounded-[2px] bg-violet-100/95 px-0.5 text-violet-950 shadow-[inset_0_-1px_0_rgba(109,40,217,0.75)]'
    case 'synonym':
      return 'rounded-[2px] bg-yellow-100/95 px-0.5 text-yellow-950 shadow-[inset_0_-1px_0_rgba(180,131,0,0.75)]'
    case 'lexical_substitution':
      return 'rounded-[2px] bg-amber-100/95 px-0.5 text-amber-950 shadow-[inset_0_-1px_0_rgba(196,107,25,0.78)]'
    case 'reordering':
      return 'rounded-[2px] bg-fuchsia-100/95 px-0.5 text-fuchsia-950 shadow-[inset_0_-1px_0_rgba(162,28,175,0.72)]'
    case 'block_substitution':
      return 'rounded-[2px] bg-rose-100/95 px-0.5 text-rose-950 shadow-[inset_0_-1px_0_rgba(190,24,93,0.72)]'
    case 'insertion':
      return 'rounded-[2px] bg-sky-100/95 px-0.5 text-sky-950 shadow-[inset_0_-1px_0_rgba(29,78,216,0.72)]'
    case 'deletion':
      return 'rounded-[2px] bg-slate-100/95 px-0.5 text-slate-700 shadow-[inset_0_-1px_0_rgba(100,116,139,0.68)]'
    default:
      return ''
  }
}

const APPARATUS_PIVOT_KIND_PRIORITY: Record<ApparatusVariantKind, number> = {
  insertion: 0,
  deletion: 0,
  vocalization: 10,
  orthographic: 20,
  synonym: 30,
  morph: 40,
  grammatical_shift: 50,
  lexical_substitution: 60,
  block_substitution: 70,
  reordering: 80
}

const isPivotVisibleApparatusSpan = (span: ApparatusSpan): boolean => (
  span.kind !== 'insertion' &&
  span.kind !== 'deletion' &&
  span.leftStart !== undefined &&
  span.leftEnd !== undefined &&
  span.leftEnd > span.leftStart
)

const buildPivotApparatusTokens = (pivotText: string, spans: ApparatusSpan[]): ApparatusOverlayToken[] | null => {
  const uniqueSpans = new Map<string, ApparatusSpan>()
  spans.filter(isPivotVisibleApparatusSpan).forEach(span => {
    uniqueSpans.set(`${span.kind}:${span.leftStart}:${span.leftEnd}`, span)
  })
  const orderedSpans = Array.from(uniqueSpans.values()).sort((left, right) => {
    const priorityDelta = APPARATUS_PIVOT_KIND_PRIORITY[left.kind] - APPARATUS_PIVOT_KIND_PRIORITY[right.kind]
    if (priorityDelta !== 0) return priorityDelta
    return (left.leftStart || 0) - (right.leftStart || 0)
  })
  if (orderedSpans.length === 0) {
    return null
  }
  return buildApparatusOverlayTokens(pivotText, '', orderedSpans, 'left')
}

const useEventCallback = <Args extends unknown[], Result>(
  callback: (...args: Args) => Result
): ((...args: Args) => Result) => {
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback((...args: Args) => callbackRef.current(...args), [])
}

let activeWitnessProjectionObserver: LedgerSafeActiveWitnessProjectionObserver | undefined

export const setLedgerSafeActiveWitnessProjectionObserver = (
  observer: LedgerSafeActiveWitnessProjectionObserver | null
) => {
  activeWitnessProjectionObserver = observer || undefined
}

const getTextOffsetWithin = (
  root: HTMLElement,
  targetNode: Node,
  targetOffset: number
): number | null => {
  if (targetNode === root) {
    return Math.max(0, Math.min(targetOffset, root.textContent?.length || 0))
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let offset = 0
  let node = walker.nextNode()
  while (node) {
    const textLength = node.textContent?.length || 0
    if (node === targetNode) {
      return offset + Math.max(0, Math.min(targetOffset, textLength))
    }
    offset += textLength
    node = walker.nextNode()
  }
  return null
}

const readTextSelectionWithin = (root: HTMLElement): Pick<CharacterSpanSelection, 'localStart' | 'localEnd' | 'selectedText'> | null => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) {
    return null
  }
  const rawStart = getTextOffsetWithin(root, range.startContainer, range.startOffset)
  const rawEnd = getTextOffsetWithin(root, range.endContainer, range.endOffset)
  if (rawStart === null || rawEnd === null) {
    return null
  }
  const localStart = Math.min(rawStart, rawEnd)
  const localEnd = Math.max(rawStart, rawEnd)
  if (localEnd <= localStart) {
    return null
  }
  const selectedText = (root.textContent || '').slice(localStart, localEnd)
  if (!selectedText.trim()) {
    return null
  }
  return {
    localStart,
    localEnd,
    selectedText,
  }
}

const renderTextWithCharacterSpans = (
  text: string,
  spans: Array<{ localStart?: number; localEnd?: number; selected?: boolean }>,
  keyPrefix: string
): React.ReactNode => {
  const orderedSpans = spans
    .map(span => ({
      localStart: typeof span.localStart === 'number' ? Math.max(0, Math.min(text.length, Math.floor(span.localStart))) : null,
      localEnd: typeof span.localEnd === 'number' ? Math.max(0, Math.min(text.length, Math.floor(span.localEnd))) : null,
      selected: Boolean(span.selected),
    }))
    .filter((span): span is { localStart: number; localEnd: number; selected: boolean } => (
      span.localStart !== null &&
      span.localEnd !== null &&
      span.localEnd > span.localStart
    ))
    .sort((left, right) => left.localStart - right.localStart || left.localEnd - right.localEnd)

  if (orderedSpans.length === 0) {
    return text
  }

  const chunks: React.ReactNode[] = []
  let cursor = 0
  orderedSpans.forEach((span, spanIndex) => {
    if (span.localStart < cursor) {
      return
    }
    if (span.localStart > cursor) {
      chunks.push(
        <React.Fragment key={`${keyPrefix}:plain:${cursor}`}>
          {text.slice(cursor, span.localStart)}
        </React.Fragment>
      )
    }
    chunks.push(
      <span
        key={`${keyPrefix}:character-span:${spanIndex}:${span.localStart}`}
        className={`rounded-[2px] px-0.5 ${span.selected ? 'bg-amber-200 text-amber-950 shadow-[inset_0_0_0_1px_rgba(180,83,9,0.72),inset_0_-2px_0_rgba(180,83,9,0.48)]' : 'bg-amber-100 text-amber-950 shadow-[inset_0_-2px_0_rgba(217,119,6,0.38)]'}`}
      >
        {text.slice(span.localStart, span.localEnd)}
      </span>
    )
    cursor = span.localEnd
  })
  if (cursor < text.length) {
    chunks.push(
      <React.Fragment key={`${keyPrefix}:plain:${cursor}`}>
        {text.slice(cursor)}
      </React.Fragment>
    )
  }
  return chunks
}

type LedgerSafeMatrixCellProps = {
  verseSourceText?: string
  rowId: string
  sourceRowId: string
  witnessId: string
  witnessSigla: string
  text: string
  effectiveText: string
  effectiveMetaText: string
  effectiveState: 'mapped' | 'excluded' | 'gap'
  displayText: string
  isDisplayArabic: boolean
  isPiPreviewCell: boolean
  isPropagationPreviewCell: boolean
  isPropagationPreviewAdvisory?: boolean
  propagationPreviewKind?: 'propagation' | 'semantic'
  propagationPreviewStatus?: WorkbenchPropagationRowAssignment['translationStatus']
  appliedTranslationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
  isActive: boolean
  isAffected: boolean
  isSelectedCell: boolean
  isRangeSelectedCell: boolean
  isCharacterOccurrenceCell: boolean
  characterOccurrenceRoleLabel?: string
  characterOccurrenceSpans?: Array<{ localStart?: number; localEnd?: number; selected?: boolean }>
  isSplitSelectedCell: boolean
  isBoundaryOverlayAnchor: boolean
  isBoundaryApplyAnchor: boolean
  isTranspositionTargetCandidate?: boolean
  isTranspositionTargetOrigin?: boolean
  isTranspositionTargetAnchor?: boolean
  relocationPlacement?: 'source' | 'candidate' | 'preview' | 'conflict'
  isRelocationSourceStart?: boolean
  isRelocationTargetStart?: boolean
  isSequenceOffenderCell?: boolean
  retiredMaterialIssue?: LedgerSafeRetiredMaterialIssue | null
  hasInlineRibbonAbove?: boolean
  inlineRibbon?: LedgerSafeInlineRibbonModel | null
  isPiFocusedRow: boolean
  diffMode: DiffMode
  diffTokens: RichOverlayToken[] | null
  apparatusTokens: ApparatusOverlayToken[] | null
  apparatusEmphasis: ApparatusTokenEmphasis
  diffAppearance: DiffAppearance
  boundaryPreview: LedgerSafeBoundaryPreview | null
  splitPreview: LedgerSafeUnitSplitPreview | null
  splitContext: LedgerSafeUnitSplitContext | null
  selectedSplitBoundaryWordIndex: number | null
  editSuggestion?: LedgerSafeEditSuggestion | null
  isEditSuggestionActionAnchor?: boolean
  transpositionProjectionMode: 'none' | 'canonical-ghost' | 'projection-active' | 'projection-context-ghost'
  isInteractive: boolean
  isApplying: boolean
  onCellClick: (args: { rowId: string; witnessId: string; witnessSigla: string; shiftKey: boolean }) => void
  onCellHover?: (args: { rowId: string; witnessId: string; hovered: boolean }) => void
  onCellTextSelection?: (selection: CharacterSpanSelection) => void
  onRibbonAction?: (action: LedgerSafeInlineRibbonAction) => void
  onSelectSplitBoundaryWordIndex: (index: number | null) => void
  onApplySplit: () => void
  onApplyBoundary: () => void
  onAdjustBoundaryDelta: (delta: number) => void
  onBoundaryDragStart: (startX: number, startBoundaryWordIndex: number) => void
  onCancelBoundary: () => void
  onCancelSplit: () => void
  onApplyEditSuggestion?: (suggestion: LedgerSafeEditSuggestion) => void
  onDismissEditSuggestion?: (suggestionId: string) => void
}

type LedgerSafeMatrixCellRenderObserver = (cellKey: string) => void

let ledgerSafeMatrixCellRenderObserver: LedgerSafeMatrixCellRenderObserver | null = null

export const setLedgerSafeMatrixCellRenderObserver = (
  observer: LedgerSafeMatrixCellRenderObserver | null
): void => {
  ledgerSafeMatrixCellRenderObserver = observer
}

export const areLedgerSafeMatrixCellPropsEqual = (
  previous: LedgerSafeMatrixCellProps,
  next: LedgerSafeMatrixCellProps
): boolean => (
  previous.rowId === next.rowId &&
  previous.sourceRowId === next.sourceRowId &&
  previous.witnessId === next.witnessId &&
  previous.witnessSigla === next.witnessSigla &&
  previous.verseSourceText === next.verseSourceText &&
  previous.text === next.text &&
  previous.effectiveText === next.effectiveText &&
  previous.effectiveMetaText === next.effectiveMetaText &&
  previous.effectiveState === next.effectiveState &&
  previous.displayText === next.displayText &&
  previous.isDisplayArabic === next.isDisplayArabic &&
  previous.isPiPreviewCell === next.isPiPreviewCell &&
  previous.isPropagationPreviewCell === next.isPropagationPreviewCell &&
  previous.isPropagationPreviewAdvisory === next.isPropagationPreviewAdvisory &&
  previous.propagationPreviewKind === next.propagationPreviewKind &&
  previous.propagationPreviewStatus === next.propagationPreviewStatus &&
  previous.appliedTranslationStatus === next.appliedTranslationStatus &&
  previous.isActive === next.isActive &&
  previous.isAffected === next.isAffected &&
  previous.isSelectedCell === next.isSelectedCell &&
  previous.isRangeSelectedCell === next.isRangeSelectedCell &&
  previous.isCharacterOccurrenceCell === next.isCharacterOccurrenceCell &&
  previous.characterOccurrenceRoleLabel === next.characterOccurrenceRoleLabel &&
  previous.characterOccurrenceSpans === next.characterOccurrenceSpans &&
  previous.isSplitSelectedCell === next.isSplitSelectedCell &&
  previous.isBoundaryOverlayAnchor === next.isBoundaryOverlayAnchor &&
  previous.isBoundaryApplyAnchor === next.isBoundaryApplyAnchor &&
  previous.isTranspositionTargetCandidate === next.isTranspositionTargetCandidate &&
  previous.isTranspositionTargetOrigin === next.isTranspositionTargetOrigin &&
  previous.isTranspositionTargetAnchor === next.isTranspositionTargetAnchor &&
  previous.relocationPlacement === next.relocationPlacement &&
  previous.isRelocationSourceStart === next.isRelocationSourceStart &&
  previous.isRelocationTargetStart === next.isRelocationTargetStart &&
  previous.isSequenceOffenderCell === next.isSequenceOffenderCell &&
  previous.retiredMaterialIssue === next.retiredMaterialIssue &&
  previous.hasInlineRibbonAbove === next.hasInlineRibbonAbove &&
  previous.inlineRibbon === next.inlineRibbon &&
  previous.isPiFocusedRow === next.isPiFocusedRow &&
  previous.diffMode === next.diffMode &&
  previous.diffTokens === next.diffTokens &&
  previous.apparatusTokens === next.apparatusTokens &&
  previous.apparatusEmphasis === next.apparatusEmphasis &&
  previous.diffAppearance === next.diffAppearance &&
  previous.boundaryPreview === next.boundaryPreview &&
  previous.splitPreview === next.splitPreview &&
  previous.splitContext === next.splitContext &&
  previous.selectedSplitBoundaryWordIndex === next.selectedSplitBoundaryWordIndex &&
  previous.editSuggestion === next.editSuggestion &&
  previous.isEditSuggestionActionAnchor === next.isEditSuggestionActionAnchor &&
  previous.transpositionProjectionMode === next.transpositionProjectionMode &&
  previous.isInteractive === next.isInteractive &&
  previous.isApplying === next.isApplying &&
  previous.onCellClick === next.onCellClick &&
  previous.onCellHover === next.onCellHover &&
  previous.onCellTextSelection === next.onCellTextSelection &&
  previous.onRibbonAction === next.onRibbonAction &&
  previous.onSelectSplitBoundaryWordIndex === next.onSelectSplitBoundaryWordIndex &&
  previous.onApplySplit === next.onApplySplit &&
  previous.onApplyBoundary === next.onApplyBoundary &&
  previous.onAdjustBoundaryDelta === next.onAdjustBoundaryDelta &&
  previous.onBoundaryDragStart === next.onBoundaryDragStart &&
  previous.onCancelBoundary === next.onCancelBoundary &&
  previous.onCancelSplit === next.onCancelSplit &&
  previous.onApplyEditSuggestion === next.onApplyEditSuggestion &&
  previous.onDismissEditSuggestion === next.onDismissEditSuggestion
)

export const LedgerSafeMatrixCell = React.memo(({
  verseSourceText = '',
  rowId,
  sourceRowId,
  witnessId,
  witnessSigla,
  text,
  effectiveText,
  effectiveMetaText,
  effectiveState,
  displayText,
  isDisplayArabic,
  isPiPreviewCell,
  isPropagationPreviewCell,
  isPropagationPreviewAdvisory = false,
  propagationPreviewKind = 'propagation',
  propagationPreviewStatus,
  appliedTranslationStatus,
  isActive,
  isAffected,
  isSelectedCell,
  isRangeSelectedCell,
  isCharacterOccurrenceCell,
  characterOccurrenceRoleLabel,
  characterOccurrenceSpans = [],
  isSplitSelectedCell,
  isBoundaryOverlayAnchor,
  isBoundaryApplyAnchor,
  isTranspositionTargetCandidate = false,
  isTranspositionTargetOrigin = false,
  isTranspositionTargetAnchor = false,
  relocationPlacement,
  isRelocationSourceStart = false,
  isRelocationTargetStart = false,
  isSequenceOffenderCell = false,
  retiredMaterialIssue = null,
  hasInlineRibbonAbove = false,
  inlineRibbon = null,
  isPiFocusedRow,
  diffMode,
  diffTokens,
  apparatusTokens,
  apparatusEmphasis,
  diffAppearance,
  boundaryPreview,
  splitPreview,
  splitContext,
  selectedSplitBoundaryWordIndex,
  editSuggestion = null,
  isEditSuggestionActionAnchor = false,
  transpositionProjectionMode,
  isInteractive,
  isApplying,
  onCellClick,
  onCellHover = () => undefined,
  onCellTextSelection = () => undefined,
  onRibbonAction = () => undefined,
  onSelectSplitBoundaryWordIndex,
  onApplySplit,
  onApplyBoundary,
  onAdjustBoundaryDelta,
  onBoundaryDragStart,
  onCancelBoundary,
  onCancelSplit,
  onApplyEditSuggestion = () => undefined,
  onDismissEditSuggestion = () => undefined
}: LedgerSafeMatrixCellProps) => {
  ledgerSafeMatrixCellRenderObserver?.(`${rowId}:${witnessId}`)
  const cellRef = useRef<HTMLTableCellElement | null>(null)
  const effectiveTranslationStatus = propagationPreviewStatus || appliedTranslationStatus
  const propagationPreviewLabel = propagationPreviewKind === 'semantic' ? 'Semantic preview' : 'Preview'
  const propagationPreviewTestPrefix = propagationPreviewKind === 'semantic'
    ? 'ledger-semantic-preview'
    : 'ledger-propagation-preview'
  const isTranspositionGhostCell = transpositionProjectionMode === 'canonical-ghost' || transpositionProjectionMode === 'projection-context-ghost'
  const isProjectedActualCell = transpositionProjectionMode === 'projection-active'
  const transpositionMarkerMode = isProjectedActualCell
    ? 'projection'
    : transpositionProjectionMode === 'canonical-ghost'
      ? 'canonical'
      : transpositionProjectionMode === 'projection-context-ghost'
        ? 'context'
        : effectiveTranslationStatus === 'transposed'
          ? 'status'
          : null
  const hasTranspositionTreatment = Boolean(transpositionMarkerMode)
  // Marginal rule, apparatus-style: solid sienna where the unit actually reads,
  // dashed where a locus is only echoed (ghosts).
  const transpositionRuleClass = transpositionMarkerMode === 'projection'
    ? 'w-[3px] bg-[#a8411b]'
    : transpositionMarkerMode === 'status'
      ? 'w-[3px] bg-[#a8411b]/75'
      : transpositionMarkerMode === 'canonical'
        ? 'w-0 border-l-2 border-dashed border-[#a8411b]/55'
        : 'w-0 border-l-2 border-dashed border-[#a8411b]/25'
  const transpositionMarkerTestId = transpositionMarkerMode
    ? `ledger-transposition-marker-${transpositionMarkerMode}-${sourceRowId}-${witnessId}`
    : undefined
  const transpositionMarkerTitle = transpositionMarkerMode === 'projection'
    ? 'Transposition: this unit reads here in the witness order'
    : transpositionMarkerMode === 'canonical'
      ? 'Transposition: canonical locus — the unit is projected to its witness-order position'
      : transpositionMarkerMode === 'context'
        ? 'Context: this witness reads here in canonical order; text repeated beside the projection'
        : 'Marked as transposition: this unit sits outside the canonical order'
  const transpositionSiglum = transpositionMarkerMode ? (
    <span
      data-testid={transpositionMarkerTestId}
      title={transpositionMarkerTitle}
      className={`font-serif text-[12px] font-semibold italic leading-none tracking-normal normal-case ${
        transpositionMarkerMode === 'context' ? 'text-[#a8411b]/60' : 'text-[#a8411b]'
      }`}
    >
      tr.
    </span>
  ) : null
  // Surface treatments are computed as one ladder so a cell never carries two
  // competing background/ring utilities (winner would depend on stylesheet order).
  const transpositionTargetFieldClass = isTranspositionTargetOrigin
    ? 'bg-[#f3e3d3]'
    : isTranspositionTargetAnchor
      ? 'bg-[#f8eee1]'
      : isTranspositionTargetCandidate
        ? 'bg-[#faf4ea]'
        : ''
  const transpositionTargetOutlineClass = isTranspositionTargetOrigin
    ? 'outline outline-2 -outline-offset-2 outline-[#a8411b]'
    : isTranspositionTargetAnchor
      ? 'outline-dashed outline-1 -outline-offset-2 outline-[#a8411b]/70'
      : ''
  const relocationTargetFieldClass = relocationPlacement === 'source'
    ? 'bg-amber-50'
    : relocationPlacement === 'preview'
      ? 'bg-emerald-100'
      : relocationPlacement === 'conflict'
        ? 'bg-red-50'
        : relocationPlacement === 'candidate'
          ? 'bg-emerald-50/40'
          : ''
  const relocationTargetOutlineClass = relocationPlacement === 'source'
    ? 'outline outline-2 -outline-offset-2 outline-amber-600'
    : relocationPlacement === 'preview'
      ? 'outline outline-2 -outline-offset-2 outline-emerald-600'
      : relocationPlacement === 'conflict'
        ? 'outline outline-2 -outline-offset-2 outline-red-600'
        : relocationPlacement === 'candidate'
          ? 'outline-dashed outline-1 -outline-offset-2 outline-emerald-700/40'
          : ''
  const placementTargetFieldClass = transpositionTargetFieldClass || relocationTargetFieldClass
  const retiredMaterialFieldClass = retiredMaterialIssue
    ? retiredMaterialIssue.status === 'review'
      ? 'bg-amber-50/80'
      : retiredMaterialIssue.status === 'retained'
        ? 'bg-sky-50/70'
        : 'bg-slate-100/80'
    : ''
  const suppressSelectionSurface = Boolean(placementTargetFieldClass || retiredMaterialFieldClass)
  const cellFieldClass = placementTargetFieldClass || retiredMaterialFieldClass || (
    isProjectedActualCell
      ? 'bg-[#faf3eb]'
      : 'bg-transparent'
  )
  const transpositionInlineTestId = (
    transpositionProjectionMode === 'canonical-ghost'
      ? `ledger-transposition-ghost-inline-${sourceRowId}-${witnessId}`
      : transpositionProjectionMode === 'projection-active'
        ? `ledger-transposition-projection-inline-${sourceRowId}-${witnessId}`
        : transpositionProjectionMode === 'projection-context-ghost'
          ? `ledger-transposition-context-ghost-inline-${sourceRowId}-${witnessId}`
          : undefined
  )
  const suggestionSourceLabel = editSuggestion ? getLedgerSafeSuggestionSourceLabel(editSuggestion) : ''
  const suggestionEdgeFamily = editSuggestion ? getLedgerSafeSuggestionEdgeFamily(editSuggestion) : 'right'
  const suggestionOperationLabel = editSuggestion ? getLedgerSafeSuggestionOperationLabel(editSuggestion) : ''
  const suggestionMarkerClasses = getLedgerSafeSuggestionMarkerClasses(suggestionEdgeFamily, isDisplayArabic)
  const suggestionEdgeClass = ''
  const placeholderLabel = getLedgerSafePlaceholderLabel(effectiveState, effectiveMetaText)
  const isDeliberateGapPlaceholder = effectiveState === 'excluded'
  const placeholderKind = isDeliberateGapPlaceholder ? 'deliberate-gap' : 'empty'
  const defaultSuggestionPanelPlacement = editSuggestion
    ? getLedgerSafeSuggestionDefaultPanelPlacement(editSuggestion, isDisplayArabic)
    : 'right'
  const [suggestionPanelPlacement, setSuggestionPanelPlacement] = useState<LedgerSafeSuggestionPanelPlacement>(
    defaultSuggestionPanelPlacement
  )
  const [inlineRibbonPlacement, setInlineRibbonPlacement] = useState<LedgerSafeInlineRibbonPlacement>('center')
  const updateSuggestionPanelPlacement = useCallback(() => {
    if (!editSuggestion || !cellRef.current) {
      return
    }
    const nextPlacement = resolveLedgerSafeSuggestionPanelPlacement(
      cellRef.current.getBoundingClientRect(),
      getLedgerSafeSuggestionDefaultPanelPlacement(editSuggestion, isDisplayArabic)
    )
    setSuggestionPanelPlacement(currentPlacement => (
      currentPlacement === nextPlacement ? currentPlacement : nextPlacement
    ))
  }, [editSuggestion, isDisplayArabic])
  const updateInlineRibbonPlacement = useCallback(() => {
    if (!inlineRibbon || !cellRef.current) {
      return
    }
    const ribbonWidth = estimateLedgerSafeInlineRibbonWidth(inlineRibbon)
    const nextPlacement = resolveLedgerSafeInlineRibbonPlacement(
      cellRef.current.getBoundingClientRect(),
      ribbonWidth,
      getLedgerSafeInlineRibbonViewportBounds(cellRef.current)
    )
    setInlineRibbonPlacement(currentPlacement => (
      currentPlacement === nextPlacement ? currentPlacement : nextPlacement
    ))
  }, [inlineRibbon])
  useEffect(() => {
    setSuggestionPanelPlacement(defaultSuggestionPanelPlacement)
  }, [defaultSuggestionPanelPlacement, editSuggestion?.id])
  useLayoutEffect(() => {
    if (!inlineRibbon) {
      setInlineRibbonPlacement('center')
      return undefined
    }

    updateInlineRibbonPlacement()
    const frameId = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(updateInlineRibbonPlacement)
      : null

    window.addEventListener('resize', updateInlineRibbonPlacement)
    window.addEventListener('scroll', updateInlineRibbonPlacement, true)
    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
      window.removeEventListener('resize', updateInlineRibbonPlacement)
      window.removeEventListener('scroll', updateInlineRibbonPlacement, true)
    }
  }, [inlineRibbon, updateInlineRibbonPlacement])
  const suggestionPanelPositionClass = editSuggestion
    ? getLedgerSafeSuggestionPanelPositionClass(suggestionPanelPlacement)
    : ''
  const inlineRibbonPlacementClass = inlineRibbonPlacement === 'end'
    ? 'right-[-1px]'
    : inlineRibbonPlacement === 'start'
      ? 'left-[-1px]'
      : 'left-1/2 -translate-x-1/2'
  const suggestionMatchScore = Math.max(0, Math.min(1, Number(editSuggestion?.contentScore ?? 0)))
  const suggestionFilledMatchSegments = Math.max(1, Math.min(4, Math.round(suggestionMatchScore * 4)))
  const hasEditSuggestionPanel = Boolean(editSuggestion && isEditSuggestionActionAnchor)
  const isSplitEditSuggestion = editSuggestion?.kind === 'split_unit'
  const splitSuggestionTokens = useMemo(
    () => buildLedgerSafeSplitSuggestionTokens(editSuggestion, displayText),
    [displayText, editSuggestion]
  )
  const splitSuggestionTokenCount = splitSuggestionTokens.length
  const splitSuggestionLeftTextTokens = isSplitEditSuggestion
    ? splitLedgerSafeSuggestionText(editSuggestion?.params.targetLeftText)
    : []
  const splitSuggestionBaseOffset = clampLedgerSafeSplitSuggestionOffset(
    getLedgerSafeSuggestionNumberParam(editSuggestion, 'targetBoundaryOffset') ||
      (splitSuggestionLeftTextTokens.length > 0 ? splitSuggestionLeftTextTokens.length : 1),
    splitSuggestionTokenCount
  )
  const splitSuggestionMinAfterWordIndex = getLedgerSafeSuggestionNumberParam(editSuggestion, 'targetMinAfterWordIndex') || (
    getLedgerSafeSuggestionNumberParam(editSuggestion, 'afterWordIndex') && splitSuggestionBaseOffset
      ? Number(getLedgerSafeSuggestionNumberParam(editSuggestion, 'afterWordIndex')) - splitSuggestionBaseOffset + 1
      : 1
  )
  const splitSuggestionMaxOffsetFromParams = (() => {
    const maxAfterWordIndex = getLedgerSafeSuggestionNumberParam(editSuggestion, 'targetMaxAfterWordIndex')
    if (!maxAfterWordIndex || !splitSuggestionMinAfterWordIndex) {
      return splitSuggestionTokenCount - 1
    }
    return maxAfterWordIndex - splitSuggestionMinAfterWordIndex + 1
  })()
  const splitSuggestionMaxOffset = splitSuggestionTokenCount > 1
    ? Math.max(1, Math.min(splitSuggestionTokenCount - 1, splitSuggestionMaxOffsetFromParams))
    : 0
  const [splitSuggestionBoundaryOffset, setSplitSuggestionBoundaryOffset] = useState<number>(splitSuggestionBaseOffset)
  useEffect(() => {
    setSplitSuggestionBoundaryOffset(splitSuggestionBaseOffset)
  }, [editSuggestion?.id, splitSuggestionBaseOffset])
  const effectiveSplitSuggestionBoundaryOffset = isSplitEditSuggestion
    ? Math.max(1, Math.min(splitSuggestionMaxOffset || 1, splitSuggestionBoundaryOffset || splitSuggestionBaseOffset || 1))
    : 0
  const adjustedSplitSuggestionAfterWordIndex = isSplitEditSuggestion && effectiveSplitSuggestionBoundaryOffset
    ? splitSuggestionMinAfterWordIndex + effectiveSplitSuggestionBoundaryOffset - 1
    : null
  const adjustedSplitSuggestionLeftText = splitSuggestionTokens.slice(0, effectiveSplitSuggestionBoundaryOffset).join(' ')
  const adjustedSplitSuggestionRightText = splitSuggestionTokens.slice(effectiveSplitSuggestionBoundaryOffset).join(' ')
  const adjustedEditSuggestion = useMemo<LedgerSafeEditSuggestion | null>(() => {
    if (!editSuggestion || !isSplitEditSuggestion || !adjustedSplitSuggestionAfterWordIndex) {
      return editSuggestion
    }
    return {
      ...editSuggestion,
      label: `Split after word ${adjustedSplitSuggestionAfterWordIndex}`,
      params: {
        ...editSuggestion.params,
        afterWordIndex: adjustedSplitSuggestionAfterWordIndex,
        targetBoundaryOffset: effectiveSplitSuggestionBoundaryOffset,
        targetLeftText: adjustedSplitSuggestionLeftText,
        targetRightText: adjustedSplitSuggestionRightText
      }
    }
  }, [
    adjustedSplitSuggestionAfterWordIndex,
    adjustedSplitSuggestionLeftText,
    adjustedSplitSuggestionRightText,
    editSuggestion,
    effectiveSplitSuggestionBoundaryOffset,
    isSplitEditSuggestion
  ])
  const canMoveSuggestedSplitLeft = isSplitEditSuggestion && effectiveSplitSuggestionBoundaryOffset > 1
  const verseWordActions = splitContext ? verseSplitActions(displayText, splitContext.words) : null
  const canMoveSuggestedSplitRight = isSplitEditSuggestion && splitSuggestionMaxOffset > 0 && effectiveSplitSuggestionBoundaryOffset < splitSuggestionMaxOffset
  const adjustSuggestedSplitBoundary = useCallback((delta: number) => {
    setSplitSuggestionBoundaryOffset(currentOffset => {
      const baseOffset = currentOffset || splitSuggestionBaseOffset || 1
      return Math.max(1, Math.min(splitSuggestionMaxOffset || 1, baseOffset + delta))
    })
  }, [splitSuggestionBaseOffset, splitSuggestionMaxOffset])
  const handleTextSelectionMouseUp = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!isInteractive || displayText !== text || isAffected || isPiPreviewCell || isPropagationPreviewCell) {
      return
    }
    const selection = readTextSelectionWithin(event.currentTarget)
    if (!selection) {
      return
    }
    onCellTextSelection({
      witnessId,
      rowId: sourceRowId,
      ...selection,
    })
  }, [isInteractive, displayText, text, isAffected, isPiPreviewCell, isPropagationPreviewCell, onCellTextSelection, sourceRowId, witnessId])
  return (
  <td
    ref={cellRef}
    data-testid={`ledger-cell-${rowId}-${witnessId}`}
    data-ledger-selected={isSelectedCell && !suppressSelectionSurface ? 'true' : 'false'}
    style={!effectiveText && !effectiveMetaText
      ? (placementTargetFieldClass ? undefined : GAP_CELL_STYLE)
      : undefined}
    data-pi-preview-cell={isPiPreviewCell ? 'true' : 'false'}
    data-propagation-preview-advisory={isPropagationPreviewAdvisory ? 'true' : undefined}
    data-transposition-marker={transpositionMarkerMode || undefined}
    data-transposition-target={isTranspositionTargetOrigin ? 'origin' : isTranspositionTargetCandidate ? (isTranspositionTargetAnchor ? 'anchor' : 'candidate') : undefined}
    data-relocation-placement={relocationPlacement || undefined}
    data-retired-material={retiredMaterialIssue?.status || undefined}
    className={`group/cell relative select-none overflow-visible border-r border-b border-black/10 px-4 ${hasInlineRibbonAbove ? 'pt-[52px]' : 'pt-3'} pb-3 align-top ${inlineRibbon ? 'z-[60]' : ''} ${hasEditSuggestionPanel ? 'hover:z-[90] focus-within:z-[90]' : ''} ${isInteractive ? 'cursor-pointer active:bg-amber-50 active:ring-1 active:ring-amber-300 active:ring-inset' : 'cursor-default'} ${cellFieldClass} ${isAffected && isActive ? `${isBoundaryOverlayAnchor ? 'z-30' : 'z-20'}` : ''} ${isCharacterOccurrenceCell && !suppressSelectionSurface ? 'bg-amber-50/60 ring-1 ring-amber-300 ring-inset' : ''} ${isRangeSelectedCell && !suppressSelectionSurface ? 'bg-blue-50 ring-1 ring-blue-300 ring-inset' : ''} ${isSelectedCell && !suppressSelectionSurface ? 'bg-amber-50 ring-1 ring-amber-500 ring-inset' : ''} ${transpositionTargetOutlineClass} ${relocationTargetOutlineClass} ${suggestionEdgeClass} ${isPropagationPreviewCell && !isPiPreviewCell && !suppressSelectionSurface ? (isPropagationPreviewAdvisory ? 'bg-amber-50/80 ring-1 ring-amber-300/90 ring-inset' : effectiveTranslationStatus === 'transposed' ? 'bg-[#f7ede2] ring-1 ring-[#a8411b]/25 ring-inset' : 'bg-sky-50/70 ring-1 ring-sky-200/80 ring-inset') : ''} ${isPiPreviewCell && !suppressSelectionSurface ? 'bg-violet-50 ring-1 ring-violet-300 ring-inset' : ''} ${isPiFocusedRow && !isPiPreviewCell && !suppressSelectionSurface ? 'bg-blue-50/60 ring-1 ring-blue-200 ring-inset' : ''}`}
    onMouseDown={event => {
      if (!isInteractive) {
        return
      }
      if (event.shiftKey) {
        event.preventDefault()
      }
    }}
    onClick={event => {
      if (!isInteractive) {
        return
      }
      onCellClick({
        rowId,
        witnessId,
        witnessSigla,
        shiftKey: event.shiftKey
      })
    }}
    onMouseEnter={() => {
      updateSuggestionPanelPlacement()
      updateInlineRibbonPlacement()
      onCellHover({ rowId: sourceRowId, witnessId, hovered: true })
    }}
    onMouseLeave={() => onCellHover({ rowId: sourceRowId, witnessId, hovered: false })}
    onFocusCapture={() => {
      updateSuggestionPanelPlacement()
      updateInlineRibbonPlacement()
    }}
  >
    {isAffected && isActive && (
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -inset-x-1 z-0 -translate-y-[2px] rounded-[2px] bg-[#fffdfa] shadow-[0_8px_24px_rgba(15,23,42,0.10),0_2px_6px_rgba(15,23,42,0.06)] ring-1 ring-blue-200/80 ring-offset-1 ring-offset-white ${
          isBoundaryOverlayAnchor ? '-top-4 bottom-0' : 'top-0 -bottom-4'
        }`}
      />
    )}
    {hasTranspositionTreatment && (
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 z-[12] ${transpositionRuleClass}`}
      />
    )}
    {editSuggestion && (
      <span
        data-testid={`ledger-edit-suggestion-${rowId}-${witnessId}`}
        className="pointer-events-none absolute inset-0 z-20 block select-none"
        title={editSuggestion.reason}
        aria-label={editSuggestion.label}
      >
        <span aria-hidden="true" className={suggestionMarkerClasses.dogEar} />
        <span aria-hidden="true" className={suggestionMarkerClasses.dogEarShade} />
        <span aria-hidden="true" className={suggestionMarkerClasses.edgeTick} />
      </span>
    )}
    <div className="relative z-10">
    {isSequenceOffenderCell && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-sequence-offender-marker-${sourceRowId}-${witnessId}`}
          title="This unit is out of source order for this witness — see the Order panel"
          className="border border-[#7a2e2a]/40 bg-[#f6e7e5] px-1.5 py-0.5 text-[#7a2e2a]"
        >
          ⇅ Out of order
        </span>
      </div>
    )}
    {retiredMaterialIssue && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-retired-material-marker-${sourceRowId}-${witnessId}`}
          title={`${retiredMaterialIssue.reason} — review this cell in the Order panel`}
          className={retiredMaterialIssue.status === 'review'
            ? 'border border-amber-500 bg-amber-100 px-1.5 py-0.5 text-amber-950'
            : retiredMaterialIssue.status === 'retained'
              ? 'border border-sky-500 bg-sky-100 px-1.5 py-0.5 text-sky-950'
              : 'border border-slate-400 bg-slate-200 px-1.5 py-0.5 text-slate-700'}
        >
          {retiredMaterialIssue.status === 'review'
            ? 'Review source remnant'
            : retiredMaterialIssue.status === 'retained'
              ? 'Retained paratext'
              : 'Hidden remnant'}
        </span>
      </div>
    )}
    {isTranspositionTargetOrigin && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-transposition-target-${sourceRowId}-${witnessId}`}
          title="Transposition source; choose a distinct highlighted row as its target"
          className="bg-[#a8411b] px-1.5 py-0.5 text-white"
        >
          Transposition source
        </span>
      </div>
    )}
    {isTranspositionTargetAnchor && !isTranspositionTargetOrigin && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-transposition-target-${sourceRowId}-${witnessId}`}
          title="Align the transposed unit to this row"
          className="border border-dashed border-[#a8411b]/70 bg-white/70 px-1.5 py-0.5 text-[#96522b]"
        >
          Align here
        </span>
      </div>
    )}
    {relocationPlacement === 'source' && isRelocationSourceStart && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-relocation-source-${sourceRowId}-${witnessId}`}
          className="border border-amber-700 bg-amber-100 px-1.5 py-0.5 text-amber-900"
        >
          Relocation source
        </span>
      </div>
    )}
    {(relocationPlacement === 'preview' || relocationPlacement === 'conflict') && isRelocationTargetStart && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        <span
          data-testid={`ledger-relocation-target-${sourceRowId}-${witnessId}`}
          className={relocationPlacement === 'conflict'
            ? 'border border-red-700 bg-red-100 px-1.5 py-0.5 text-red-900'
            : 'border border-emerald-700 bg-emerald-100 px-1.5 py-0.5 text-emerald-900'}
        >
          {relocationPlacement === 'conflict' ? 'Blocked' : 'Place here'}
        </span>
      </div>
    )}
    {transpositionProjectionMode === 'canonical-ghost' && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        {transpositionSiglum}
        <span
          data-testid={`ledger-transposition-ghost-${sourceRowId}-${witnessId}`}
          className="text-[#96522b]/90"
        >
          Canonical locus
        </span>
        <span className="normal-case tracking-normal text-[#96522b]/70">reads at its witness-order row</span>
      </div>
    )}
    {transpositionProjectionMode === 'projection-active' && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        {transpositionSiglum}
        <span
          data-testid={`ledger-transposition-projection-${sourceRowId}-${witnessId}`}
          className="text-[#7c3413]"
        >
          Witness-order locus
        </span>
      </div>
    )}
    {transpositionProjectionMode === 'projection-context-ghost' && (
      <div className="mb-2 flex items-baseline gap-2">
        {transpositionSiglum}
        <span
          data-testid={`ledger-transposition-context-${sourceRowId}-${witnessId}`}
          title="This witness keeps the canonical order; its aligned text is repeated here for context"
          className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#96522b]/50"
        >
          Context
        </span>
      </div>
    )}
    {isPropagationPreviewCell && !isPiPreviewCell && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        {effectiveTranslationStatus === 'transposed' && transpositionMarkerMode === 'status' && transpositionSiglum}
        <span className={isPropagationPreviewAdvisory ? 'text-amber-800' : effectiveTranslationStatus === 'transposed' ? 'text-[#96522b]' : 'text-sky-700'}>
          {propagationPreviewLabel}
        </span>
        {isPropagationPreviewAdvisory && (
          <span className="border border-amber-300 bg-amber-100/90 px-1.5 py-0.5 text-amber-900">
            Review distance
          </span>
        )}
        {effectiveTranslationStatus === 'transposed' && (
          <span
            data-testid={`${propagationPreviewTestPrefix}-transposed-${rowId}-${witnessId}`}
            className="border border-[#a8411b]/35 bg-[#f7ede2] px-1.5 py-0.5 text-[#7c3413]"
          >
            Transposed
          </span>
        )}
        {effectiveTranslationStatus === 'uncertain' && (
          <span className="border border-amber-300 bg-amber-100/90 px-1.5 py-0.5 text-amber-800 shadow-sm">
            Uncertain
          </span>
        )}
      </div>
    )}
    {isCharacterOccurrenceCell && (
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-amber-800">
        <span className="border border-amber-300 bg-amber-100/90 px-1.5 py-0.5 text-amber-900 shadow-sm">
          Character
        </span>
        <span className="max-w-[180px] truncate text-amber-700" title={characterOccurrenceRoleLabel || undefined}>
          {characterOccurrenceRoleLabel || 'Role open'}
        </span>
      </div>
    )}
    {!isPropagationPreviewCell && !isPiPreviewCell && appliedTranslationStatus === 'transposed' && transpositionMarkerMode === 'status' && (
      <div className="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
        {transpositionSiglum}
        <span
          data-testid={`ledger-applied-transposed-${rowId}-${witnessId}`}
          title="Applied transposition mark: this unit sits outside the canonical order"
          className="text-[#96522b]"
        >
          Transposed
        </span>
      </div>
    )}
    {isPiPreviewCell && (
      <div
        data-testid={`ledger-pi-preview-inline-${rowId}-${witnessId}`}
        className="mb-3 border border-violet-300 bg-violet-50/90 px-3 py-2 shadow-sm"
      >
        <div className="flex items-center justify-between gap-2 text-[9px] font-bold uppercase tracking-widest text-violet-700">
          <span>Pi preview</span>
          <span className="font-mono text-violet-500">{effectiveState}</span>
        </div>
        {effectiveText ? (
          <div
            className={`mt-2 text-sm ${hasArabicText(effectiveText) ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
            dir={hasArabicText(effectiveText) ? 'rtl' : 'ltr'}
          >
            {effectiveText}
          </div>
        ) : (
          <div
            className="mt-4 flex h-4 items-center justify-center"
            aria-label={placeholderLabel}
            title={placeholderLabel}
            data-ledger-placeholder-kind={placeholderKind}
            data-ledger-cell-state={effectiveState}
            data-ledger-meta-text={effectiveMetaText || undefined}
          >
            <span
              data-testid={`ledger-pi-placeholder-${rowId}-${witnessId}`}
              aria-hidden="true"
              className={`block h-[2px] rounded-full ${isDeliberateGapPlaceholder ? 'w-10 border-t-2 border-dotted border-amber-700/75 bg-transparent' : 'w-8 bg-slate-500/70'} ${isTranspositionGhostCell ? 'opacity-45' : ''}`}
            />
          </div>
        )}
      </div>
    )}
    {retiredMaterialIssue ? (
      <div
        data-testid={`ledger-retired-material-content-${sourceRowId}-${witnessId}`}
        dir={hasArabicText(retiredMaterialIssue.snapshotText || retiredMaterialIssue.legacyText) ? 'rtl' : 'ltr'}
        className={`min-h-[52px] whitespace-pre-wrap text-sm ${
          hasArabicText(retiredMaterialIssue.snapshotText || retiredMaterialIssue.legacyText)
            ? 'font-reader-arabic text-right leading-8'
            : 'text-left leading-6'
        } ${retiredMaterialIssue.status === 'hidden' ? 'flex items-center font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500' : 'text-black'}`}
      >
        {retiredMaterialIssue.status === 'hidden'
          ? 'Text hidden · provenance retained'
          : retiredMaterialIssue.snapshotText || retiredMaterialIssue.legacyText || 'No surviving display text'}
      </div>
    ) : displayText ? (
      isSplitSelectedCell && splitContext ? (
        <div
          data-testid={`ledger-split-inline-${rowId}-${witnessId}`}
          dir={isDisplayArabic ? 'rtl' : 'ltr'}
          className={`min-h-[52px] text-sm text-black ${isDisplayArabic ? 'font-reader-arabic text-right leading-8' : 'text-left leading-6'}`}
        >
          <div
            className={`whitespace-pre-wrap ${isDisplayArabic ? 'text-right' : 'text-left'}`}
            dir={isDisplayArabic ? 'rtl' : 'ltr'}
          >
            <LedgerVerseCell source={verseSourceText} text={displayText}
              enabled={verseWordActions !== null}
              wordActions={verseWordActions || undefined}
              selectedBoundary={selectedSplitBoundaryWordIndex}
              onBoundarySelect={onSelectSplitBoundaryWordIndex}>
            {splitContext.words.map((word, wordIndex) => {
              const isSelectedSeam = selectedSplitBoundaryWordIndex === word.splitBoundaryWordIndex
              const isSelectable = word.splitBoundaryWordIndex !== null
              const wordToneClass = isSelectedSeam
                ? 'font-bold text-blue-700 underline decoration-blue-600 decoration-2 underline-offset-[6px]'
                : 'text-gray-900 hover:text-blue-700'
              return (
                <span
                  key={`${rowId}:${witnessId}:split-inline:${word.segmentableWordIndex}`}
                  className="inline align-baseline"
                >
                  {isSelectable ? (
                    <button
                      type="button"
                      data-testid={`ledger-split-word-${rowId}-${witnessId}-${wordIndex}`}
                      onClick={event => {
                        event.stopPropagation()
                        onSelectSplitBoundaryWordIndex(word.splitBoundaryWordIndex)
                      }}
                      className={`inline cursor-pointer border-0 bg-transparent p-0 align-baseline transition-colors ${wordToneClass}`}
                      title={`Split after ${word.text}`}
                      aria-label={`Split after ${word.text}`}
                    >
                      {word.text}
                    </button>
                  ) : (
                    <span className="inline text-gray-900">
                      {word.text}
                    </span>
                  )}
                  {isSelectedSeam && (
                    <>
                      <span
                        data-testid={`ledger-split-seam-${rowId}-${witnessId}-${wordIndex}`}
                        className="mx-1.5 inline-flex h-[1em] w-[2px] align-middle rounded-sm bg-blue-600/60"
                      />
                      <span className="mr-1.5 inline-flex -translate-y-[2px] items-center justify-center bg-blue-600 px-1 text-[10px] font-bold text-white shadow-[2px_2px_0_#000]">
                        <span className="mr-0.5 tracking-tighter opacity-50">◂</span>
                        {wordIndex + 1}
                        <span className="ml-0.5 tracking-tighter opacity-50">▸</span>
                      </span>
                    </>
                  )}
                  {wordIndex < splitContext.words.length - 1 && <span>{' '}</span>}
                </span>
              )
            })}
            </LedgerVerseCell>
          </div>
          {splitPreview && (
            <div
              data-testid={`ledger-split-bubble-${rowId}-${witnessId}`}
              className={`mt-3 inline-flex max-w-[260px] flex-col gap-2 border border-black bg-white/95 px-3 py-2 shadow-[3px_3px_0_#000] ${isDisplayArabic ? 'ml-auto items-end text-right' : 'items-start text-left'}`}
            >
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-500">New fragment</div>
              <div
                className={`mt-1 text-sm ${hasArabicText(splitPreview.newFragmentText) ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                dir={hasArabicText(splitPreview.newFragmentText) ? 'rtl' : 'ltr'}
              >
                <LedgerVerseCell source={verseSourceText} text={splitPreview.newFragmentText} enabled>
                  {splitPreview.newFragmentText}
                </LedgerVerseCell>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation()
                    onApplySplit()
                  }}
                  disabled={isApplying}
                  className="h-8 flex-1 border border-black bg-black px-2 text-[9px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  {isApplying ? 'Splitting…' : 'Create split fragment'}
                </button>
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation()
                    onSelectSplitBoundaryWordIndex(null)
                    onCancelSplit()
                  }}
                  className="h-8 border border-black bg-white px-3 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          data-testid={transpositionInlineTestId || (isPropagationPreviewCell ? `${propagationPreviewTestPrefix}-inline-${rowId}-${witnessId}` : undefined)}
          dir={isDisplayArabic ? 'rtl' : 'ltr'}
          onMouseUp={handleTextSelectionMouseUp}
          className={`min-h-[52px] select-text whitespace-pre-wrap text-sm ${isDisplayArabic ? 'font-reader-arabic text-right leading-8' : 'text-left leading-6'} ${isPropagationPreviewCell && !isPiPreviewCell && isPropagationPreviewAdvisory ? 'text-amber-950/90' : isPropagationPreviewCell && !isPiPreviewCell && effectiveTranslationStatus !== 'transposed' ? 'text-sky-950/85' : 'text-black'} ${isTranspositionGhostCell ? 'opacity-45' : ''}`}
        >
          <LedgerVerseCell source={verseSourceText} text={displayText} onTextSelection={selection => {
            // Preview offsets belong to provisional text, not the persisted cell.
            if (isInteractive && displayText === text && !isAffected && !isPiPreviewCell && !isPropagationPreviewCell) onCellTextSelection?.({ witnessId, rowId: sourceRowId, ...selection })
          }} enabled={
            !diffTokens && !apparatusTokens
          } marks={displayText === text ? [...characterOccurrenceSpans.flatMap(span =>
            typeof span.localStart === 'number' && typeof span.localEnd === 'number' ? [{
              start: span.localStart, end: span.localEnd,
              className: span.selected ? 'bg-amber-200 text-amber-950' : 'bg-amber-100 text-amber-950',
            }] : []), ...Array.from(displayText.matchAll(/\S+/gu)).flatMap((token, i) =>
              isSplitEditSuggestion && i === effectiveSplitSuggestionBoundaryOffset - 1 ? [{
                start: token.index!, end: token.index! + token[0].length,
                className: 'text-blue-700 underline decoration-blue-600 decoration-2 underline-offset-[6px]',
              }] : [])] : undefined}>
          {isSplitEditSuggestion && splitSuggestionTokens.length > 1 ? (
            <span data-testid={`ledger-suggested-split-inline-${rowId}-${witnessId}`}>
              {splitSuggestionTokens.map((token, tokenIndex) => {
                const isBoundaryWord = tokenIndex === effectiveSplitSuggestionBoundaryOffset - 1
                return (
                  <span key={`${rowId}:${witnessId}:suggested-split:${tokenIndex}`} className="inline align-baseline">
                    <span
                      data-testid={isBoundaryWord ? `ledger-suggested-split-word-${rowId}-${witnessId}` : undefined}
                      className={isBoundaryWord ? 'font-bold text-blue-700 underline decoration-blue-600 decoration-2 underline-offset-[6px]' : undefined}
                    >
                      {token}
                    </span>
                    {isBoundaryWord && (
                      <span
                        data-testid={`ledger-suggested-split-seam-${rowId}-${witnessId}`}
                        className="mx-1.5 inline-flex h-[1em] w-[2px] align-middle rounded-sm bg-blue-600/70"
                      />
                    )}
                    {tokenIndex < splitSuggestionTokens.length - 1 && <span>{' '}</span>}
                  </span>
                )
              })}
            </span>
          ) : displayText === text && diffMode === 'inline' && diffTokens ? diffTokens.map((token, tokenIndex) => (
            token.kind === 'space'
              ? <span key={`${rowId}:${witnessId}:space:${tokenIndex}`}>{token.text}</span>
              : (
                <span
                  key={`${rowId}:${witnessId}:${token.kind}:${tokenIndex}`}
                  data-diff-op={token.op || 'match'}
                  data-diff-morph={token.morph || undefined}
                  data-diff-strength={token.substitutionStrength || undefined}
                  data-testid={token.kind === 'ghost' && token.op === 'delete' ? `ledger-inline-deletion-ghost-${rowId}-${witnessId}-${tokenIndex}` : undefined}
                  className={`relative inline ${getDiffTokenClasses(token.op, diffAppearance, token.morph, token.substitutionStrength)}`}
                  title={
                    token.kind === 'ghost' && token.op === 'delete'
                      ? 'Deleted against reference'
                      : token.morph === 'near'
                      ? 'Morphological near-match against reference'
                      : token.op === 'insert'
                        ? 'Insertion against reference'
                        : token.op === 'substitute'
                          ? 'Lexical substitution against reference'
                          : undefined
                  }
                  style={token.op === 'substitute' && token.substitutionStrength === 'strong'
                    ? {
                        boxShadow: 'inset 0 -2px 0 rgba(180, 83, 9, 0.42), inset -3px 0 0 rgba(194, 65, 12, 0.78)'
                      }
                    : undefined}
                >
                  {token.text}
                </span>
              )
          )) : displayText === text && diffMode === 'apparatus' && apparatusTokens ? apparatusTokens.map((token, tokenIndex) => (
            token.kind === 'space'
              ? <span key={`${rowId}:${witnessId}:space:${tokenIndex}`}>{token.text}</span>
              : token.kind === 'ghost'
                ? (
                  <span
                    key={`${rowId}:${witnessId}:apparatus-ghost:${tokenIndex}`}
                    data-apparatus-kind={token.variant || undefined}
                    data-apparatus-emphasis={token.variant ? apparatusEmphasis : undefined}
                    data-testid={token.variant === 'deletion' ? `ledger-apparatus-deletion-ghost-${rowId}-${witnessId}` : undefined}
                    className="relative inline rounded-[2px] px-0.5 text-slate-400/55 opacity-75 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.12)]"
                    title={token.variant === 'deletion' ? 'Deleted against reference' : undefined}
                  >
                    {token.text}
                  </span>
                )
              : (
                <span
                  key={`${rowId}:${witnessId}:apparatus:${tokenIndex}`}
                  data-apparatus-kind={token.variant || undefined}
                  data-apparatus-emphasis={token.variant ? apparatusEmphasis : undefined}
                  className={`relative inline ${getApparatusTokenClasses(token.variant, apparatusEmphasis)}`}
                  title={token.variant
                    ? `${getApparatusVariantLabel(token.variant)} ${apparatusEmphasis === 'faint' ? 'in reference' : 'against reference'}`
                    : undefined}
                >
                  {token.text}
                </span>
              )
          )) : renderTextWithCharacterSpans(displayText, characterOccurrenceSpans, `${rowId}:${witnessId}`)}
          </LedgerVerseCell>
        </div>
      )
    ) : effectiveMetaText ? (
      <div
        data-testid={`ledger-placeholder-${rowId}-${witnessId}`}
        className="flex min-h-[52px] items-center justify-center"
        aria-label={placeholderLabel}
        title={placeholderLabel}
        data-ledger-placeholder-kind={placeholderKind}
        data-ledger-cell-state={effectiveState}
        data-ledger-meta-text={effectiveMetaText || undefined}
      >
        <span
          data-testid={transpositionInlineTestId || (isPropagationPreviewCell ? `${propagationPreviewTestPrefix}-meta-${rowId}-${witnessId}` : `ledger-gap-placeholder-${rowId}-${witnessId}`)}
          aria-hidden="true"
          className={`block h-[2px] rounded-full ${isDeliberateGapPlaceholder ? 'w-10 border-t-2 border-dotted border-amber-700/75 bg-transparent' : 'w-9 bg-sky-500/70'} ${isTranspositionGhostCell ? 'opacity-45' : ''}`}
        />
      </div>
    ) : (
      <div
        data-testid={`ledger-placeholder-${rowId}-${witnessId}`}
        className="flex min-h-[52px] items-center justify-center"
        aria-label={placeholderLabel}
        title={placeholderLabel}
        data-ledger-placeholder-kind={placeholderKind}
        data-ledger-cell-state={effectiveState}
      >
        <span
          data-testid={transpositionInlineTestId || `ledger-empty-placeholder-${rowId}-${witnessId}`}
          aria-hidden="true"
          className={`block h-[2px] w-8 rounded-full bg-slate-500/70 ${isTranspositionGhostCell ? 'opacity-45' : ''}`}
        />
      </div>
    )}
    </div>
    {inlineRibbon && (
      <div
        data-testid={`ledger-inline-ribbon-${rowId}-${witnessId}`}
        data-placement={inlineRibbonPlacement}
        className={`absolute ${inlineRibbonPlacementClass} top-full z-[70] flex h-9 w-max min-w-[calc(100%+2px)] items-stretch gap-1 overflow-visible border border-black bg-[#fffdfa] px-1.5 py-1 text-left shadow-[2px_2px_0_#000]`}
        dir="ltr"
        onClick={event => event.stopPropagation()}
      >
        <span className="flex h-7 shrink-0 items-center border-r border-dashed border-black/20 px-1.5 pr-2 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
          {inlineRibbon.label}
        </span>
        {inlineRibbon.buttons.map(button => (
          <button
            key={`${rowId}:${witnessId}:ribbon:${button.action}`}
            type="button"
            data-testid={button.testId}
            title={button.title}
            aria-label={button.ariaLabel || button.title}
            disabled={isApplying || button.disabled}
            onClick={event => {
              event.stopPropagation()
              if (button.disabled) {
                return
              }
              onRibbonAction(button.action)
            }}
            className={`inline-flex h-7 shrink-0 items-center justify-center gap-1 border border-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] transition-all active:translate-x-px active:translate-y-px disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none ${
              button.primary
                ? 'bg-black text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 active:shadow-none'
                : 'bg-[#fffdfa] text-black hover:bg-gray-100'
            }`}
          >
            {button.glyph && <span className="text-[13px] leading-none">{button.glyph}</span>}
            <span>{button.label}</span>
          </button>
        ))}
        {inlineRibbon.markMenuOpen && (
          <div
            data-testid={`ledger-mark-menu-${sourceRowId}-${witnessId}`}
            className="absolute left-1.5 top-[calc(100%+4px)] z-[90] grid min-w-[210px] gap-1 border border-black bg-[#fffdfa] p-1.5 shadow-[2px_2px_0_#000]"
          >
            <button
              type="button"
              data-testid={`ledger-mark-transposition-option-${sourceRowId}-${witnessId}`}
              className="flex items-center justify-between gap-3 border border-[#a8411b]/40 bg-[#faf3ec] px-2 py-1.5 text-left font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#7c3413] transition-colors hover:bg-[#f3e3d3]"
              onClick={event => {
                event.stopPropagation()
                onRibbonAction('mark-transposition')
              }}
            >
              <span>Transposition</span>
              <span className="font-serif text-[12px] font-semibold italic normal-case tracking-normal text-[#a8411b]">tr.</span>
            </button>
            {inlineRibbon.canUnmarkTransposition && (
              <button
                type="button"
                data-testid={`ledger-unmark-transposition-option-${sourceRowId}-${witnessId}`}
                title="Remove the transposition and return this unit to the fragment tray"
                className="flex items-center justify-between gap-3 border border-[#a8411b]/30 bg-white px-2 py-1.5 text-left font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#7c3413] transition-colors hover:bg-[#f7ede2]"
                onClick={event => {
                  event.stopPropagation()
                  onRibbonAction('unmark-transposition')
                }}
              >
                <span>Remove tr. → fragment</span>
                <span className="font-serif text-[12px] font-semibold italic normal-case tracking-normal text-[#a8411b]">tr.</span>
              </button>
            )}
            <button
              type="button"
              data-testid={`ledger-mark-gap-option-${sourceRowId}-${witnessId}`}
              className="flex items-center justify-between gap-3 border border-black/20 bg-white px-2 py-1.5 text-left font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
              disabled={effectiveState === 'mapped'}
              title={effectiveState === 'mapped' ? 'Move the mapped unit before marking this row as a deliberate gap' : 'Mark deliberate gap'}
              onClick={event => {
                event.stopPropagation()
                if (effectiveState === 'mapped') {
                  return
                }
                onRibbonAction('mark-gap')
              }}
            >
              <span>Deliberate gap</span>
              <span className="text-[10px] tracking-normal">∅</span>
            </button>
          </div>
        )}
      </div>
    )}
    {editSuggestion && isEditSuggestionActionAnchor && (
      <div
        className={`pointer-events-none absolute ${suggestionPanelPositionClass} z-[130] text-left opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/cell:pointer-events-auto group-hover/cell:opacity-100 group-focus-within/cell:pointer-events-auto group-focus-within/cell:opacity-100`}
        dir="ltr"
      >
        <div className="w-[280px] border border-[rgb(168,26,14)] bg-[#fffdfa] shadow-[2px_2px_0_#000]">
        <div className="flex items-center px-3 pr-2 pt-3">
          <span className="max-w-[92px] truncate font-mono text-[11px] font-bold uppercase text-black">
            {suggestionSourceLabel}
          </span>
          <span
            aria-hidden="true"
            className="relative mx-2 h-px flex-1 bg-black after:absolute after:right-0 after:top-[-3px] after:border-y-[3px] after:border-l-[3px] after:border-r-0 after:border-y-transparent after:border-l-black"
          />
          <span className="max-w-[92px] truncate font-mono text-[11px] font-bold uppercase text-slate-700">
            {witnessSigla}
          </span>
          <button
            type="button"
            aria-label="Dismiss suggested edit"
            data-testid={`ledger-edit-suggestion-dismiss-${editSuggestion.id}`}
            onClick={event => {
              event.stopPropagation()
              onDismissEditSuggestion(editSuggestion.id)
            }}
            disabled={isApplying}
            className="ml-2 text-slate-400 transition-colors hover:text-black disabled:text-slate-300"
          >
            <X className="h-[12px] w-[12px] stroke-[2.4]" />
          </button>
        </div>
        <div className="mt-1 px-3 font-mono text-[12px] font-bold uppercase tracking-[0.18em] text-black">
          {suggestionOperationLabel}
        </div>
        <div className="mx-3 mt-2 h-px bg-[rgba(15,23,42,0.18)]" />
        <p
          className="mt-2 overflow-hidden px-3 text-[11px] leading-snug text-slate-700"
          title={editSuggestion.reason}
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            lineClamp: 2
          }}
        >
          {editSuggestion.reason}
        </p>
        {isSplitEditSuggestion && splitSuggestionTokens.length > 1 && adjustedSplitSuggestionAfterWordIndex !== null && (
          <div className="mx-3 mt-2 border border-blue-200 bg-blue-50/70 px-2 py-2">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                aria-label="Move suggested split left"
                data-testid={`ledger-edit-suggestion-split-decrement-${editSuggestion.id}`}
                onClick={event => {
                  event.stopPropagation()
                  adjustSuggestedSplitBoundary(-1)
                }}
                disabled={isApplying || !canMoveSuggestedSplitLeft}
                className="flex h-6 w-6 items-center justify-center border border-blue-300 bg-white text-blue-700 shadow-[1px_1px_0_#000] transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 disabled:shadow-none"
              >
                <Minus className="h-[12px] w-[12px] stroke-[2.7]" />
              </button>
              <div className="min-w-0 flex-1 text-center">
                <div className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-blue-700">
                  Suggested split
                </div>
                <div className="mt-0.5 truncate text-[10px] text-slate-700" title={adjustedSplitSuggestionLeftText}>
                  after word {adjustedSplitSuggestionAfterWordIndex}
                </div>
              </div>
              <button
                type="button"
                aria-label="Move suggested split right"
                data-testid={`ledger-edit-suggestion-split-increment-${editSuggestion.id}`}
                onClick={event => {
                  event.stopPropagation()
                  adjustSuggestedSplitBoundary(1)
                }}
                disabled={isApplying || !canMoveSuggestedSplitRight}
                className="flex h-6 w-6 items-center justify-center border border-blue-300 bg-white text-blue-700 shadow-[1px_1px_0_#000] transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 disabled:shadow-none"
              >
                <Plus className="h-[12px] w-[12px] stroke-[2.7]" />
              </button>
            </div>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">
              Match
            </span>
            <span className="font-mono text-[10px] font-bold text-black">
              {suggestionMatchScore.toFixed(2)}
            </span>
            <span className="flex items-center gap-px">
              {[0, 1, 2, 3].map(index => (
                <span
                  key={`${editSuggestion.id}:match:${index}`}
                  className={`h-1 w-2 border border-black ${index < suggestionFilledMatchSegments ? 'bg-black' : 'bg-transparent'}`}
                />
              ))}
            </span>
          </div>
          <button
            type="button"
            aria-label="Apply suggested edit"
            data-testid={`ledger-edit-suggestion-apply-${editSuggestion.id}`}
            onClick={event => {
              event.stopPropagation()
              onApplyEditSuggestion(adjustedEditSuggestion || editSuggestion)
            }}
            disabled={isApplying}
            className="h-6 border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none"
          >
            {isApplying ? 'Applying…' : 'Apply'}
          </button>
        </div>
        </div>
      </div>
    )}
    {isBoundaryOverlayAnchor && boundaryPreview && (
      <div className="pointer-events-none absolute bottom-2 left-1/2 z-40 flex -translate-x-1/2 items-center gap-5">
        <button
          type="button"
          aria-label="Move one word up"
          data-testid={`ledger-boundary-decrement-${rowId}-${witnessId}`}
          onClick={event => {
            event.stopPropagation()
            onAdjustBoundaryDelta(-1)
          }}
          className="pointer-events-auto flex h-6 w-6 items-center justify-center bg-transparent p-0 text-[#374151] transition-colors hover:text-black"
        >
          <Minus className="h-[15px] w-[15px] stroke-[2.8]" />
        </button>
        <button
          type="button"
          aria-label="Move one word down"
          data-testid={`ledger-boundary-increment-${rowId}-${witnessId}`}
          onClick={event => {
            event.stopPropagation()
            onAdjustBoundaryDelta(1)
          }}
          className="pointer-events-auto flex h-6 w-6 items-center justify-center bg-transparent p-0 text-[#374151] transition-colors hover:text-black"
        >
          <Plus className="h-[15px] w-[15px] stroke-[2.8]" />
        </button>
      </div>
    )}
    {isBoundaryOverlayAnchor && boundaryPreview && (
      <div aria-hidden="true" className="mx-2 mt-3 border-t border-dashed border-blue-200/70" />
    )}
    {isBoundaryOverlayAnchor && boundaryPreview && (
      <button
        type="button"
        aria-label="Drag boundary"
        data-testid={`ledger-boundary-handle-${rowId}-${witnessId}`}
        onMouseDown={event => {
          event.preventDefault()
          event.stopPropagation()
          onBoundaryDragStart(event.clientX, boundaryPreview.boundaryWordIndex)
        }}
        className="pointer-events-auto absolute bottom-0 left-1/2 z-50 flex h-7 w-7 -translate-x-1/2 translate-y-1/2 cursor-grab items-center justify-center rounded-full border border-blue-700 bg-blue-600 text-white shadow-md transition-transform hover:scale-105 active:scale-95"
      >
        <MoveHorizontal className="h-[12px] w-[12px] stroke-[2.8]" />
      </button>
    )}
    {isBoundaryApplyAnchor && boundaryPreview && (
      <div className="pointer-events-none absolute -bottom-2 left-1/2 z-20 flex -translate-x-1/2 items-center gap-5">
        <button
          type="button"
          aria-label="Cancel boundary change"
          data-testid={`ledger-boundary-cancel-${rowId}-${witnessId}`}
          onClick={event => {
            event.stopPropagation()
            onCancelBoundary()
          }}
          className="pointer-events-auto flex h-7 w-7 items-center justify-center bg-transparent p-0 text-[#6b7280] transition-colors hover:text-[#374151]"
        >
          <X className="h-[18px] w-[18px] stroke-[2.8]" />
        </button>
        <button
          type="button"
          aria-label="Apply boundary change"
          data-testid={`ledger-boundary-apply-${rowId}-${witnessId}`}
          onClick={event => {
            event.stopPropagation()
            onApplyBoundary()
          }}
          disabled={isApplying || boundaryPreview.deltaWords === 0}
          className="pointer-events-auto flex h-7 w-7 items-center justify-center bg-transparent p-0 text-emerald-500 transition-colors hover:text-emerald-600 disabled:cursor-not-allowed disabled:text-gray-300"
        >
          <Check className="h-[18px] w-[18px] stroke-[2.8]" />
        </button>
      </div>
    )}
  </td>
  )
}, areLedgerSafeMatrixCellPropsEqual)

type LedgerSafeMatrixPreviewCell = {
  text: string
  metaText?: string
  state: LedgerSafeCellState
  translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
  outsideLocalContext?: boolean
}

type LedgerSafeMatrixApparatusDiff = {
  tokens: ApparatusOverlayToken[]
}

type LedgerSafeMatrixCharacterPresentation = {
  roleLabel: string
  spans: Array<{ localStart?: number; localEnd?: number; selected?: boolean }>
}

type LedgerSafeMatrixWitnessCellsProps = {
  displayRowKey: string
  displayRowId: string
  row: AnchoredRow
  isProjectionRow: boolean
  witnesses: Witness[]
  activeWitnessId: string | null
  witnessUnitTextLookupByWitnessId: WitnessUnitTextLookupByWitnessId
  piPreviewAfterCellByKey: ReadonlyMap<string, PiMatrixAgentWindowCell>
  semanticPreviewCellsByKey: ReadonlyMap<string, LedgerSafeMatrixPreviewCell>
  witnessPropagationPreviewCellsByKey: ReadonlyMap<string, LedgerSafeMatrixPreviewCell>
  selectedUnitRepairPreviewCellsByKey: ReadonlyMap<string, LedgerSafeMatrixPreviewCell>
  retiredMaterialIssueByCellKey: ReadonlyMap<string, LedgerSafeRetiredMaterialIssue>
  selectedCellForRow: LedgerSafeSelectedCell | null
  selectedRangeWitnessIdForRow: string | null
  selectedCharacterOccurrenceByCellKey: ReadonlyMap<string, readonly CharacterOccurrence[]>
  selectedCharacterOccurrencePresentationByCellKey: ReadonlyMap<string, LedgerSafeMatrixCharacterPresentation>
  selectedBoundaryContextForRow: LedgerSafeBoundaryContext | null
  boundaryPreviewForRow: LedgerSafeBoundaryPreview | null
  selectedSplitContextForRow: LedgerSafeUnitSplitContext | null
  splitPreviewForRow: LedgerSafeUnitSplitPreview | null
  selectedSplitBoundaryWordIndex: number | null
  inlineEditMode: InlineEditMode
  diffEnabled: boolean
  diffMode: DiffMode
  diffAppearance: DiffAppearance
  contentPresentationKey: string
  diffTokensByCellKey: ReadonlyMap<string, RichOverlayToken[]>
  apparatusDiffByCellKey: ReadonlyMap<string, LedgerSafeMatrixApparatusDiff>
  apparatusPivotTokensByCellKey: ReadonlyMap<string, ApparatusOverlayToken[]>
  isProjectedSourceRow: boolean
  transpositionTargetWitnessId: string | null
  isTranspositionEligibleRow: boolean
  isTranspositionOriginRow: boolean
  relocationWitnessId: string | null
  relocationPlacementForRow?: LedgerSafeMatrixCellProps['relocationPlacement']
  isRelocationSourceStartRow: boolean
  isRelocationTargetStartRow: boolean
  isSequenceOffenderRow: boolean
  isPiFocusedRow: boolean
  editSuggestionByCellKey: ReadonlyMap<string, LedgerSafeEditSuggestion>
  editSuggestionActionKeySet: ReadonlySet<string>
  inlineRibbonByWitnessId: ReadonlyMap<string, LedgerSafeInlineRibbonModel>
  inlineRibbonAboveWitnessIds: ReadonlySet<string>
  readOnly: boolean
  isApplying: boolean
  onCellClick: LedgerSafeMatrixCellProps['onCellClick']
  onCellHover: NonNullable<LedgerSafeMatrixCellProps['onCellHover']>
  onCellTextSelection: NonNullable<LedgerSafeMatrixCellProps['onCellTextSelection']>
  onRibbonAction: NonNullable<LedgerSafeMatrixCellProps['onRibbonAction']>
  onSelectSplitBoundaryWordIndex: LedgerSafeMatrixCellProps['onSelectSplitBoundaryWordIndex']
  onApplySplit: LedgerSafeMatrixCellProps['onApplySplit']
  onApplyBoundary: LedgerSafeMatrixCellProps['onApplyBoundary']
  onAdjustBoundaryDelta: LedgerSafeMatrixCellProps['onAdjustBoundaryDelta']
  onBoundaryDragStart: LedgerSafeMatrixCellProps['onBoundaryDragStart']
  onCancelBoundary: LedgerSafeMatrixCellProps['onCancelBoundary']
  onCancelSplit: LedgerSafeMatrixCellProps['onCancelSplit']
  onApplyEditSuggestion: NonNullable<LedgerSafeMatrixCellProps['onApplyEditSuggestion']>
  onDismissEditSuggestion: NonNullable<LedgerSafeMatrixCellProps['onDismissEditSuggestion']>
}

type LedgerSafeMatrixWitnessCellsRenderObserver = (displayRowKey: string) => void

let ledgerSafeMatrixWitnessCellsRenderObserver: LedgerSafeMatrixWitnessCellsRenderObserver | null = null

export const setLedgerSafeMatrixWitnessCellsRenderObserver = (
  observer: LedgerSafeMatrixWitnessCellsRenderObserver | null
): void => {
  ledgerSafeMatrixWitnessCellsRenderObserver = observer
}

type LedgerSafeMatrixDisplayRowBodyBuildObserver = (displayRowKey: string) => void

let ledgerSafeMatrixDisplayRowBodyBuildObserver: LedgerSafeMatrixDisplayRowBodyBuildObserver | null = null

export const setLedgerSafeMatrixDisplayRowBodyBuildObserver = (
  observer: LedgerSafeMatrixDisplayRowBodyBuildObserver | null
): void => {
  ledgerSafeMatrixDisplayRowBodyBuildObserver = observer
}

const haveSameWitnessColumns = (
  previous: Witness[],
  next: Witness[]
): boolean => (
  previous === next
  || (
    previous.length === next.length
    && previous.every((witness, index) => (
      witness.id === next[index]?.id
      && witness.sigla === next[index]?.sigla
      && witness.uploaded_file?.content === next[index]?.uploaded_file?.content
    ))
  )
)

const haveSameCellMapEntries = <Value,>(
  previous: ReadonlyMap<string, Value>,
  next: ReadonlyMap<string, Value>,
  rowId: string,
  witnesses: Witness[]
): boolean => (
  previous === next
  || witnesses.every(witness => (
    previous.get(`${rowId}:${witness.id}`) === next.get(`${rowId}:${witness.id}`)
  ))
)

const haveSameWitnessMapEntries = <Value,>(
  previous: ReadonlyMap<string, Value>,
  next: ReadonlyMap<string, Value>,
  witnesses: Witness[]
): boolean => (
  previous === next
  || witnesses.every(witness => previous.get(witness.id) === next.get(witness.id))
)

const haveSameWitnessSetMembership = (
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>,
  witnesses: Witness[]
): boolean => (
  previous === next
  || witnesses.every(witness => previous.has(witness.id) === next.has(witness.id))
)

const haveSameCellSetMembership = (
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>,
  rowId: string,
  witnesses: Witness[]
): boolean => (
  previous === next
  || witnesses.every(witness => (
    previous.has(`${rowId}:${witness.id}`) === next.has(`${rowId}:${witness.id}`)
  ))
)

const haveSameRenderedWitnessCells = (
  previous: LedgerSafeMatrixWitnessCellsProps,
  next: LedgerSafeMatrixWitnessCellsProps,
): boolean => {
  if (
    previous.row === next.row
    && previous.witnessUnitTextLookupByWitnessId === next.witnessUnitTextLookupByWitnessId
  ) {
    return true
  }
  if (previous.row.id !== next.row.id) {
    return false
  }
  return next.witnesses.every(witness => (
    getWorkspaceMappingText(
      previous.row,
      witness.id,
      previous.witnessUnitTextLookupByWitnessId
    ) === getWorkspaceMappingText(
      next.row,
      witness.id,
      next.witnessUnitTextLookupByWitnessId
    )
    && getCellMetaText(previous.row, witness.id) === getCellMetaText(next.row, witness.id)
    && getLedgerSafeCellState(previous.row, witness.id) === getLedgerSafeCellState(next.row, witness.id)
    && getAppliedTranslationStatus(previous.row, witness.id) === getAppliedTranslationStatus(next.row, witness.id)
  ))
}

const haveSameSelectedCell = (
  previous: LedgerSafeSelectedCell | null,
  next: LedgerSafeSelectedCell | null
): boolean => (
  previous === next
  || (
    previous?.rowId === next?.rowId
    && previous?.witnessId === next?.witnessId
  )
)

const haveSameLiveApplyingPresentation = (
  previous: LedgerSafeMatrixWitnessCellsProps,
  next: LedgerSafeMatrixWitnessCellsProps
): boolean => {
  if (
    previous.displayRowKey !== next.displayRowKey
    || previous.displayRowId !== next.displayRowId
    || previous.row.id !== next.row.id
    || previous.isProjectionRow !== next.isProjectionRow
    || previous.activeWitnessId !== next.activeWitnessId
    || !haveSameSelectedCell(previous.selectedCellForRow, next.selectedCellForRow)
    || previous.selectedRangeWitnessIdForRow !== next.selectedRangeWitnessIdForRow
    || previous.boundaryPreviewForRow?.deltaWords !== next.boundaryPreviewForRow?.deltaWords
    || previous.selectedSplitBoundaryWordIndex !== next.selectedSplitBoundaryWordIndex
    || previous.inlineEditMode !== next.inlineEditMode
    || previous.diffEnabled !== next.diffEnabled
    || previous.diffMode !== next.diffMode
    || previous.diffAppearance !== next.diffAppearance
    || previous.contentPresentationKey !== next.contentPresentationKey
    || previous.isProjectedSourceRow !== next.isProjectedSourceRow
    || previous.transpositionTargetWitnessId !== next.transpositionTargetWitnessId
    || previous.isTranspositionEligibleRow !== next.isTranspositionEligibleRow
    || previous.isTranspositionOriginRow !== next.isTranspositionOriginRow
    || previous.relocationWitnessId !== next.relocationWitnessId
    || previous.relocationPlacementForRow !== next.relocationPlacementForRow
    || previous.isRelocationSourceStartRow !== next.isRelocationSourceStartRow
    || previous.isRelocationTargetStartRow !== next.isRelocationTargetStartRow
    || previous.isSequenceOffenderRow !== next.isSequenceOffenderRow
    || previous.isPiFocusedRow !== next.isPiFocusedRow
    || previous.readOnly !== next.readOnly
    || previous.onCellClick !== next.onCellClick
    || previous.onCellHover !== next.onCellHover
    || previous.onCellTextSelection !== next.onCellTextSelection
    || previous.onRibbonAction !== next.onRibbonAction
    || previous.onSelectSplitBoundaryWordIndex !== next.onSelectSplitBoundaryWordIndex
    || previous.onApplySplit !== next.onApplySplit
    || previous.onApplyBoundary !== next.onApplyBoundary
    || previous.onAdjustBoundaryDelta !== next.onAdjustBoundaryDelta
    || previous.onBoundaryDragStart !== next.onBoundaryDragStart
    || previous.onCancelBoundary !== next.onCancelBoundary
    || previous.onCancelSplit !== next.onCancelSplit
    || previous.onApplyEditSuggestion !== next.onApplyEditSuggestion
    || previous.onDismissEditSuggestion !== next.onDismissEditSuggestion
    || !haveSameWitnessColumns(previous.witnesses, next.witnesses)
  ) {
    return false
  }

  const rowId = next.row.id
  const witnesses = next.witnesses
  return (
    haveSameCellMapEntries(previous.selectedCharacterOccurrenceByCellKey, next.selectedCharacterOccurrenceByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.selectedCharacterOccurrencePresentationByCellKey, next.selectedCharacterOccurrencePresentationByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.retiredMaterialIssueByCellKey, next.retiredMaterialIssueByCellKey, rowId, witnesses)
  )
}

export const areLedgerSafeMatrixWitnessCellsPropsEqual = (
  previous: LedgerSafeMatrixWitnessCellsProps,
  next: LedgerSafeMatrixWitnessCellsProps
): boolean => {
  // Row and witness stores publish independently during a durable edit. Hold
  // their text-derived content as one snapshot, while still allowing genuine
  // selection, adjustment, diff-mode, and other presentation changes through.
  if (previous.isApplying && next.isApplying) {
    return haveSameLiveApplyingPresentation(previous, next)
  }

  if (
    previous.displayRowKey !== next.displayRowKey
    || previous.displayRowId !== next.displayRowId
    || previous.row.id !== next.row.id
    || previous.isProjectionRow !== next.isProjectionRow
    || previous.activeWitnessId !== next.activeWitnessId
    || !haveSameSelectedCell(previous.selectedCellForRow, next.selectedCellForRow)
    || previous.selectedRangeWitnessIdForRow !== next.selectedRangeWitnessIdForRow
    || previous.selectedBoundaryContextForRow !== next.selectedBoundaryContextForRow
    || previous.boundaryPreviewForRow !== next.boundaryPreviewForRow
    || previous.selectedSplitContextForRow !== next.selectedSplitContextForRow
    || previous.splitPreviewForRow !== next.splitPreviewForRow
    || previous.diffEnabled !== next.diffEnabled
    || previous.diffMode !== next.diffMode
    || previous.diffAppearance !== next.diffAppearance
    || previous.contentPresentationKey !== next.contentPresentationKey
    || previous.isProjectedSourceRow !== next.isProjectedSourceRow
    || previous.transpositionTargetWitnessId !== next.transpositionTargetWitnessId
    || previous.isTranspositionEligibleRow !== next.isTranspositionEligibleRow
    || previous.isTranspositionOriginRow !== next.isTranspositionOriginRow
    || previous.relocationPlacementForRow !== next.relocationPlacementForRow
    || previous.isRelocationSourceStartRow !== next.isRelocationSourceStartRow
    || previous.isRelocationTargetStartRow !== next.isRelocationTargetStartRow
    || previous.isSequenceOffenderRow !== next.isSequenceOffenderRow
    || previous.isPiFocusedRow !== next.isPiFocusedRow
    || previous.readOnly !== next.readOnly
    || previous.isApplying !== next.isApplying
    || previous.onCellClick !== next.onCellClick
    || previous.onCellHover !== next.onCellHover
    || previous.onCellTextSelection !== next.onCellTextSelection
    || previous.onRibbonAction !== next.onRibbonAction
    || previous.onSelectSplitBoundaryWordIndex !== next.onSelectSplitBoundaryWordIndex
    || previous.onApplySplit !== next.onApplySplit
    || previous.onApplyBoundary !== next.onApplyBoundary
    || previous.onAdjustBoundaryDelta !== next.onAdjustBoundaryDelta
    || previous.onBoundaryDragStart !== next.onBoundaryDragStart
    || previous.onCancelBoundary !== next.onCancelBoundary
    || previous.onCancelSplit !== next.onCancelSplit
    || previous.onApplyEditSuggestion !== next.onApplyEditSuggestion
    || previous.onDismissEditSuggestion !== next.onDismissEditSuggestion
  ) {
    return false
  }
  if (
    (
      previous.relocationPlacementForRow
      || next.relocationPlacementForRow
      || previous.isRelocationSourceStartRow
      || next.isRelocationSourceStartRow
      || previous.isRelocationTargetStartRow
      || next.isRelocationTargetStartRow
    )
    && previous.relocationWitnessId !== next.relocationWitnessId
  ) {
    return false
  }
  if (
    (previous.selectedBoundaryContextForRow || next.selectedBoundaryContextForRow
      || previous.selectedSplitContextForRow || next.selectedSplitContextForRow)
    && previous.inlineEditMode !== next.inlineEditMode
  ) {
    return false
  }
  if (
    (previous.selectedSplitContextForRow || next.selectedSplitContextForRow)
    && previous.selectedSplitBoundaryWordIndex !== next.selectedSplitBoundaryWordIndex
  ) {
    return false
  }
  if (!haveSameWitnessColumns(previous.witnesses, next.witnesses)) {
    return false
  }

  const rowId = next.row.id
  const witnesses = next.witnesses
  if (!haveSameRenderedWitnessCells(previous, next)) {
    return false
  }

  return (
    haveSameCellMapEntries(previous.piPreviewAfterCellByKey, next.piPreviewAfterCellByKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.semanticPreviewCellsByKey, next.semanticPreviewCellsByKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.witnessPropagationPreviewCellsByKey, next.witnessPropagationPreviewCellsByKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.selectedUnitRepairPreviewCellsByKey, next.selectedUnitRepairPreviewCellsByKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.retiredMaterialIssueByCellKey, next.retiredMaterialIssueByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.selectedCharacterOccurrenceByCellKey, next.selectedCharacterOccurrenceByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.selectedCharacterOccurrencePresentationByCellKey, next.selectedCharacterOccurrencePresentationByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.diffTokensByCellKey, next.diffTokensByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.apparatusDiffByCellKey, next.apparatusDiffByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.apparatusPivotTokensByCellKey, next.apparatusPivotTokensByCellKey, rowId, witnesses)
    && haveSameCellMapEntries(previous.editSuggestionByCellKey, next.editSuggestionByCellKey, rowId, witnesses)
    && haveSameCellSetMembership(previous.editSuggestionActionKeySet, next.editSuggestionActionKeySet, rowId, witnesses)
    && haveSameWitnessMapEntries(previous.inlineRibbonByWitnessId, next.inlineRibbonByWitnessId, witnesses)
    && haveSameWitnessSetMembership(previous.inlineRibbonAboveWitnessIds, next.inlineRibbonAboveWitnessIds, witnesses)
  )
}

export type LedgerSafeSelectionTimingSample = {
  rowId: string
  witnessId: string
  commitMs: number
  nextFrameMs: number
}

type LedgerSafeSelectionTimingObserver = (sample: LedgerSafeSelectionTimingSample) => void

let ledgerSafeSelectionTimingObserver: LedgerSafeSelectionTimingObserver | null = null

export const setLedgerSafeSelectionTimingObserver = (
  observer: LedgerSafeSelectionTimingObserver | null
): void => {
  ledgerSafeSelectionTimingObserver = observer
}

const readLedgerSafeInteractionNow = (): number => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
)

const LedgerSafeMatrixWitnessCells = React.memo(({
  displayRowKey,
  displayRowId,
  row: currentRow,
  isProjectionRow,
  witnesses,
  activeWitnessId,
  witnessUnitTextLookupByWitnessId: currentWitnessUnitTextLookupByWitnessId,
  piPreviewAfterCellByKey: currentPiPreviewAfterCellByKey,
  semanticPreviewCellsByKey: currentSemanticPreviewCellsByKey,
  witnessPropagationPreviewCellsByKey: currentWitnessPropagationPreviewCellsByKey,
  selectedUnitRepairPreviewCellsByKey: currentSelectedUnitRepairPreviewCellsByKey,
  retiredMaterialIssueByCellKey,
  selectedCellForRow,
  selectedRangeWitnessIdForRow,
  selectedCharacterOccurrenceByCellKey,
  selectedCharacterOccurrencePresentationByCellKey,
  selectedBoundaryContextForRow,
  boundaryPreviewForRow,
  selectedSplitContextForRow,
  splitPreviewForRow,
  selectedSplitBoundaryWordIndex,
  inlineEditMode,
  diffEnabled,
  diffMode,
  diffAppearance,
  diffTokensByCellKey: currentDiffTokensByCellKey,
  apparatusDiffByCellKey: currentApparatusDiffByCellKey,
  apparatusPivotTokensByCellKey: currentApparatusPivotTokensByCellKey,
  isProjectedSourceRow,
  transpositionTargetWitnessId,
  isTranspositionEligibleRow,
  isTranspositionOriginRow,
  relocationWitnessId,
  relocationPlacementForRow,
  isRelocationSourceStartRow,
  isRelocationTargetStartRow,
  isSequenceOffenderRow,
  isPiFocusedRow,
  editSuggestionByCellKey: currentEditSuggestionByCellKey,
  editSuggestionActionKeySet: currentEditSuggestionActionKeySet,
  inlineRibbonByWitnessId,
  inlineRibbonAboveWitnessIds,
  readOnly,
  isApplying,
  onCellClick,
  onCellHover,
  onCellTextSelection,
  onRibbonAction,
  onSelectSplitBoundaryWordIndex,
  onApplySplit,
  onApplyBoundary,
  onAdjustBoundaryDelta,
  onBoundaryDragStart,
  onCancelBoundary,
  onCancelSplit,
  onApplyEditSuggestion,
  onDismissEditSuggestion,
}: LedgerSafeMatrixWitnessCellsProps) => {
  ledgerSafeMatrixWitnessCellsRenderObserver?.(displayRowKey)

  const committedCellContentRef = useRef({
    displayRowKey,
    row: currentRow,
    witnessUnitTextLookupByWitnessId: currentWitnessUnitTextLookupByWitnessId,
    piPreviewAfterCellByKey: currentPiPreviewAfterCellByKey,
    semanticPreviewCellsByKey: currentSemanticPreviewCellsByKey,
    witnessPropagationPreviewCellsByKey: currentWitnessPropagationPreviewCellsByKey,
    selectedUnitRepairPreviewCellsByKey: currentSelectedUnitRepairPreviewCellsByKey,
    diffTokensByCellKey: currentDiffTokensByCellKey,
    apparatusDiffByCellKey: currentApparatusDiffByCellKey,
    apparatusPivotTokensByCellKey: currentApparatusPivotTokensByCellKey,
    editSuggestionByCellKey: currentEditSuggestionByCellKey,
    editSuggestionActionKeySet: currentEditSuggestionActionKeySet,
  })
  const committedCellContentMatchesRow = (
    committedCellContentRef.current.displayRowKey === displayRowKey
    && committedCellContentRef.current.row.id === currentRow.id
  )
  const committedCellContent = isApplying && committedCellContentMatchesRow
      ? committedCellContentRef.current
      : {
        displayRowKey,
        row: currentRow,
        witnessUnitTextLookupByWitnessId: currentWitnessUnitTextLookupByWitnessId,
        piPreviewAfterCellByKey: currentPiPreviewAfterCellByKey,
        semanticPreviewCellsByKey: currentSemanticPreviewCellsByKey,
        witnessPropagationPreviewCellsByKey: currentWitnessPropagationPreviewCellsByKey,
        selectedUnitRepairPreviewCellsByKey: currentSelectedUnitRepairPreviewCellsByKey,
        diffTokensByCellKey: currentDiffTokensByCellKey,
        apparatusDiffByCellKey: currentApparatusDiffByCellKey,
        apparatusPivotTokensByCellKey: currentApparatusPivotTokensByCellKey,
        editSuggestionByCellKey: currentEditSuggestionByCellKey,
        editSuggestionActionKeySet: currentEditSuggestionActionKeySet,
      }
  const {
    row,
    witnessUnitTextLookupByWitnessId,
    piPreviewAfterCellByKey,
    semanticPreviewCellsByKey,
    witnessPropagationPreviewCellsByKey,
    selectedUnitRepairPreviewCellsByKey,
    diffTokensByCellKey,
    apparatusDiffByCellKey,
    apparatusPivotTokensByCellKey,
    editSuggestionByCellKey,
    editSuggestionActionKeySet,
  } = committedCellContent

  useLayoutEffect(() => {
    if (!isApplying || !committedCellContentMatchesRow) {
      committedCellContentRef.current = {
        displayRowKey,
        row: currentRow,
        witnessUnitTextLookupByWitnessId: currentWitnessUnitTextLookupByWitnessId,
        piPreviewAfterCellByKey: currentPiPreviewAfterCellByKey,
        semanticPreviewCellsByKey: currentSemanticPreviewCellsByKey,
        witnessPropagationPreviewCellsByKey: currentWitnessPropagationPreviewCellsByKey,
        selectedUnitRepairPreviewCellsByKey: currentSelectedUnitRepairPreviewCellsByKey,
        diffTokensByCellKey: currentDiffTokensByCellKey,
        apparatusDiffByCellKey: currentApparatusDiffByCellKey,
        apparatusPivotTokensByCellKey: currentApparatusPivotTokensByCellKey,
        editSuggestionByCellKey: currentEditSuggestionByCellKey,
        editSuggestionActionKeySet: currentEditSuggestionActionKeySet,
      }
    }
  }, [
    committedCellContentMatchesRow,
    currentApparatusDiffByCellKey,
    currentApparatusPivotTokensByCellKey,
    currentDiffTokensByCellKey,
    currentEditSuggestionActionKeySet,
    currentEditSuggestionByCellKey,
    currentPiPreviewAfterCellByKey,
    currentRow,
    currentSelectedUnitRepairPreviewCellsByKey,
    currentSemanticPreviewCellsByKey,
    currentWitnessUnitTextLookupByWitnessId,
    currentWitnessPropagationPreviewCellsByKey,
    displayRowKey,
    isApplying,
  ])

  return (
    <>
      {witnesses.map(witness => {
        const cellKey = `${row.id}:${witness.id}`
        const retiredMaterialIssue = isProjectionRow
          ? null
          : (retiredMaterialIssueByCellKey.get(cellKey) || null)
        const text = getWorkspaceMappingText(row, witness.id, witnessUnitTextLookupByWitnessId)
        const metaText = getCellMetaText(row, witness.id)
        const piPreviewCell = isProjectionRow ? null : (piPreviewAfterCellByKey.get(cellKey) || null)
        const semanticPreviewCell = isProjectionRow ? null : (semanticPreviewCellsByKey.get(cellKey) || null)
        const witnessPropagationPreviewCell = witnessPropagationPreviewCellsByKey.get(cellKey) || null
        const selectedUnitRepairPreviewCell = selectedUnitRepairPreviewCellsByKey.get(cellKey) || null
        const propagationPreviewCell = witnessPropagationPreviewCell || selectedUnitRepairPreviewCell || semanticPreviewCell
        const propagationPreviewKind: LedgerSafeMatrixCellProps['propagationPreviewKind'] = semanticPreviewCell && !witnessPropagationPreviewCell && !selectedUnitRepairPreviewCell
          ? 'semantic'
          : 'propagation'
        const isPiPreviewCell = Boolean(piPreviewCell)
        const isActive = witness.id === activeWitnessId
        const transpositionProjectionMode = (
          isProjectionRow
            ? (isActive ? 'projection-active' : 'projection-context-ghost')
            : (isActive && isProjectedSourceRow ? 'canonical-ghost' : 'none')
        ) as LedgerSafeMatrixCellProps['transpositionProjectionMode']
        const isPropagationPreviewCell = Boolean(propagationPreviewCell) && (
          transpositionProjectionMode === 'none'
          || transpositionProjectionMode === 'canonical-ghost'
          || transpositionProjectionMode === 'projection-active'
        )
        const rawAppliedTranslationStatus = getAppliedTranslationStatus(row, witness.id)
        const effectiveText = piPreviewCell
          ? preprocessLedgerText(piPreviewCell.text).workspaceDisplayText
          : propagationPreviewCell
            ? preprocessLedgerText(propagationPreviewCell.text).workspaceDisplayText
            : text
        const effectiveState = piPreviewCell
          ? piPreviewCell.state
          : propagationPreviewCell?.state || getLedgerSafeCellState(row, witness.id)
        const propagationPreviewStatus = transpositionProjectionMode === 'projection-context-ghost' || transpositionProjectionMode === 'canonical-ghost'
          ? undefined
          : propagationPreviewCell?.translationStatus
        const appliedTranslationStatus = transpositionProjectionMode === 'projection-context-ghost' || transpositionProjectionMode === 'canonical-ghost'
          ? undefined
          : rawAppliedTranslationStatus
        const effectiveMetaText = isPiPreviewCell
          ? (effectiveState === 'excluded' ? '∅ gap' : '')
          : propagationPreviewCell?.metaText || metaText
        const isInteractiveCell = !readOnly && !isProjectionRow
        const isAffected = isInteractiveCell && Boolean(
          inlineEditMode === 'boundary' &&
          selectedBoundaryContextForRow
        )
        const isSelectedCell = isInteractiveCell && selectedCellForRow?.witnessId === witness.id
        const isRangeSelectedCell = isInteractiveCell && selectedRangeWitnessIdForRow === witness.id
        const isSplitSelectedCell = isInteractiveCell && Boolean(
          inlineEditMode === 'split' &&
          witness.id === activeWitnessId &&
          selectedSplitContextForRow
        )
        const isBoundaryPreviewLeft = isInteractiveCell && Boolean(
          inlineEditMode === 'boundary' &&
          boundaryPreviewForRow &&
          witness.id === activeWitnessId &&
          row.id === selectedBoundaryContextForRow?.leftRowId
        )
        const isBoundaryPreviewRight = isInteractiveCell && Boolean(
          inlineEditMode === 'boundary' &&
          boundaryPreviewForRow &&
          witness.id === activeWitnessId &&
          row.id === selectedBoundaryContextForRow?.rightRowId
        )
        const isBoundaryOverlayAnchor = isInteractiveCell && Boolean(
          inlineEditMode === 'boundary' &&
          boundaryPreviewForRow &&
          witness.id === activeWitnessId &&
          selectedCellForRow?.witnessId === witness.id &&
          selectedBoundaryContextForRow
        )
        const diffTokens = !isProjectionRow && diffEnabled ? (diffTokensByCellKey.get(cellKey) || null) : null
        const apparatusDiff = !isProjectionRow && diffEnabled ? (apparatusDiffByCellKey.get(cellKey) || null) : null
        const pivotApparatusTokens = !apparatusDiff && !isProjectionRow && diffEnabled
          ? (apparatusPivotTokensByCellKey.get(cellKey) || null)
          : null
        const isBoundaryApplyAnchor = isInteractiveCell && Boolean(
          inlineEditMode === 'boundary' &&
          boundaryPreviewForRow &&
          witness.id === activeWitnessId &&
          row.id === selectedBoundaryContextForRow?.rightRowId
        )
        const isTranspositionTargetCandidate = isInteractiveCell &&
          Boolean(transpositionTargetWitnessId) &&
          isTranspositionEligibleRow
        const isTranspositionTargetOrigin = isInteractiveCell &&
          Boolean(transpositionTargetWitnessId) &&
          isTranspositionOriginRow &&
          witness.id === transpositionTargetWitnessId
        const isTranspositionTargetAnchor = isTranspositionTargetCandidate &&
          witness.id === transpositionTargetWitnessId
        const relocationPlacement = isInteractiveCell && witness.id === relocationWitnessId
          ? relocationPlacementForRow
          : undefined
        const isRelocationSourceStart = Boolean(
          relocationWitnessId &&
          witness.id === relocationWitnessId &&
          isRelocationSourceStartRow
        )
        const isRelocationTargetStart = Boolean(
          relocationWitnessId &&
          witness.id === relocationWitnessId &&
          isRelocationTargetStartRow
        )
        const isSequenceOffenderCell = isInteractiveCell &&
          isActive &&
          isSequenceOffenderRow
        const boundaryPreview = isBoundaryOverlayAnchor || isBoundaryApplyAnchor ? boundaryPreviewForRow : null
        const splitPreviewForCell = isSplitSelectedCell ? splitPreviewForRow : null
        const splitContextForCell = isSplitSelectedCell ? selectedSplitContextForRow : null
        const editSuggestion = (
          isInteractiveCell &&
          !isPiPreviewCell &&
          !isPropagationPreviewCell &&
          !isAffected
        ) ? (editSuggestionByCellKey.get(cellKey) || null) : null
        const isEditSuggestionActionAnchor = Boolean(
          editSuggestion &&
          editSuggestionActionKeySet.has(buildLedgerSafeEditSuggestionActionKey(editSuggestion)) &&
          buildLedgerSafeEditSuggestionActionKey(editSuggestion) === cellKey
        )
        const displayText = isPiPreviewCell
          ? effectiveText
          : isBoundaryPreviewLeft && boundaryPreviewForRow && boundaryPreviewForRow.deltaWords !== 0
            ? preprocessLedgerText(boundaryPreviewForRow.leftText).workspaceDisplayText
            : isBoundaryPreviewRight && boundaryPreviewForRow && boundaryPreviewForRow.deltaWords !== 0
              ? preprocessLedgerText(boundaryPreviewForRow.rightText).workspaceDisplayText
              : effectiveText
        const isDisplayArabic = hasArabicText(displayText)
        const inlineRibbon = isInteractiveCell
          ? (inlineRibbonByWitnessId.get(witness.id) || null)
          : null
        const cellHasApplyingControl = Boolean(
          inlineRibbon
          || isEditSuggestionActionAnchor
          || isSplitSelectedCell
          || isBoundaryOverlayAnchor
          || isBoundaryApplyAnchor
        )
        const characterOccurrencesForCell = selectedCharacterOccurrenceByCellKey.get(cellKey)
        const characterOccurrencePresentation = selectedCharacterOccurrencePresentationByCellKey.get(cellKey)

        return (
          <LedgerSafeMatrixCell
            key={`${displayRowKey}:${witness.id}`}
            rowId={displayRowId}
            sourceRowId={row.id}
            witnessId={witness.id}
            witnessSigla={witness.sigla}
            verseSourceText={witness.uploaded_file?.content || ''}
            text={text}
            effectiveText={effectiveText}
            effectiveMetaText={effectiveMetaText}
            effectiveState={effectiveState}
            displayText={displayText}
            isDisplayArabic={isDisplayArabic}
            isPiPreviewCell={isPiPreviewCell}
            isPropagationPreviewCell={isPropagationPreviewCell}
            isPropagationPreviewAdvisory={Boolean(selectedUnitRepairPreviewCell?.outsideLocalContext)}
            propagationPreviewKind={propagationPreviewKind}
            propagationPreviewStatus={propagationPreviewStatus}
            appliedTranslationStatus={appliedTranslationStatus}
            isActive={isActive}
            isAffected={isAffected}
            isSelectedCell={isSelectedCell}
            isRangeSelectedCell={isRangeSelectedCell}
            isCharacterOccurrenceCell={Boolean(characterOccurrencesForCell?.length)}
            characterOccurrenceRoleLabel={characterOccurrencePresentation?.roleLabel}
            characterOccurrenceSpans={characterOccurrencePresentation?.spans}
            isSplitSelectedCell={isSplitSelectedCell}
            isBoundaryOverlayAnchor={isBoundaryOverlayAnchor}
            isBoundaryApplyAnchor={isBoundaryApplyAnchor}
            isTranspositionTargetCandidate={isTranspositionTargetCandidate}
            isTranspositionTargetOrigin={isTranspositionTargetOrigin}
            isTranspositionTargetAnchor={isTranspositionTargetAnchor}
            relocationPlacement={relocationPlacement}
            isRelocationSourceStart={isRelocationSourceStart}
            isRelocationTargetStart={isRelocationTargetStart}
            isSequenceOffenderCell={isSequenceOffenderCell}
            retiredMaterialIssue={retiredMaterialIssue}
            hasInlineRibbonAbove={inlineRibbonAboveWitnessIds.has(witness.id)}
            inlineRibbon={inlineRibbon}
            isPiFocusedRow={!isProjectionRow && isPiFocusedRow}
            diffMode={diffMode}
            diffTokens={diffTokens}
            apparatusTokens={apparatusDiff?.tokens || pivotApparatusTokens || null}
            apparatusEmphasis={pivotApparatusTokens ? 'faint' : 'standard'}
            diffAppearance={diffAppearance}
            boundaryPreview={boundaryPreview}
            splitPreview={splitPreviewForCell}
            splitContext={splitContextForCell}
            selectedSplitBoundaryWordIndex={selectedSplitBoundaryWordIndex}
            editSuggestion={editSuggestion}
            isEditSuggestionActionAnchor={isEditSuggestionActionAnchor}
            transpositionProjectionMode={transpositionProjectionMode}
            isInteractive={isInteractiveCell}
            isApplying={isApplying && cellHasApplyingControl}
            onCellClick={onCellClick}
            onCellHover={onCellHover}
            onCellTextSelection={onCellTextSelection}
            onRibbonAction={onRibbonAction}
            onSelectSplitBoundaryWordIndex={onSelectSplitBoundaryWordIndex}
            onApplySplit={onApplySplit}
            onApplyBoundary={onApplyBoundary}
            onAdjustBoundaryDelta={onAdjustBoundaryDelta}
            onBoundaryDragStart={onBoundaryDragStart}
            onCancelBoundary={onCancelBoundary}
            onCancelSplit={onCancelSplit}
            onApplyEditSuggestion={onApplyEditSuggestion}
            onDismissEditSuggestion={onDismissEditSuggestion}
          />
        )
      })}
      <td className="border-b border-black/10" />
    </>
  )
}, areLedgerSafeMatrixWitnessCellsPropsEqual)

type LedgerSafeMatrixDisplayRowBodyProps = LedgerRowTitlePresentation & {
  displayRowKey: string
  displayRowId: string
  rowId: string
  isProjectionRow: boolean
  canonicalRowIndex: number
  rowLabel: string
  emptyRowRun: LedgerSafeEmptyRowRun | null
  emptyRunRowLabels: ReadonlyArray<{ rowId: string; label: string }>
  visibleWitnessCount: number
  isSelectedStructuralRow: boolean
  isSelectedLeftRow: boolean
  structuralBoundaryPlacementEdge: 'start' | 'end' | null
  isStructuralBoundaryPreviewRow: boolean
  isStructuralRibbonRowAnchor: boolean
  structuralRangeRibbon: React.ReactNode
  hasFragmentAnchorSelection: boolean
  isEditableContext: boolean
  isPiFocusedRow: boolean
  isPiPreviewAffectedRow: boolean
  isRetiredMaterialReviewRow: boolean
  readOnly: boolean
  isMatrixContentApplying: boolean
  witnessCellsProps: LedgerSafeMatrixWitnessCellsProps | null
  onRowElement: (
    rowId: string,
    isProjectionRow: boolean,
    node: HTMLTableRowElement | null,
  ) => void
  onStructuralRowHover: (rowId: string, hovered: boolean) => void
  onStructuralRowLabelClick: (
    rowId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void
  onAnchorFragmentBeforeRow: (rowId: string) => void
  onCompactEmptyRows?: (rowIds: string[]) => void
  onEditBoundaryBelow: (rowId: string) => void
}

const haveSameEmptyRowRun = (
  previous: LedgerSafeEmptyRowRun | null,
  next: LedgerSafeEmptyRowRun | null,
): boolean => (
  previous === next
  || (
    previous !== null
    && next !== null
    && previous.rowCount === next.rowCount
    && previous.firstRowLabel === next.firstRowLabel
    && previous.lastRowLabel === next.lastRowLabel
    && previous.label === next.label
    && previous.previousRowId === next.previousRowId
    && previous.previousRowLabel === next.previousRowLabel
    && previous.nextRowId === next.nextRowId
    && previous.nextRowLabel === next.nextRowLabel
    && previous.rowIds.length === next.rowIds.length
    && previous.rowIds.every((rowId, index) => rowId === next.rowIds[index])
  )
)

const haveSameEmptyRunRowLabels = (
  previous: LedgerSafeMatrixDisplayRowBodyProps['emptyRunRowLabels'],
  next: LedgerSafeMatrixDisplayRowBodyProps['emptyRunRowLabels'],
): boolean => (
  previous === next
  || (
    previous.length === next.length
    && previous.every((entry, index) => (
      entry.rowId === next[index]?.rowId
      && entry.label === next[index]?.label
    ))
  )
)

export const areLedgerSafeMatrixDisplayRowBodyPropsEqual = (
  previous: LedgerSafeMatrixDisplayRowBodyProps,
  next: LedgerSafeMatrixDisplayRowBodyProps,
): boolean => {
  // Row and witness publications land in phases during a durable edit. Keep
  // the derived boundary/edit-below rail affordances with the committed cell
  // content until the non-applying commit reveals both together.
  const isHoldingApplyingPresentation = Boolean(
    previous.witnessCellsProps?.isApplying
    && next.witnessCellsProps?.isApplying
  )
  const haveSameEditableContext = (
    previous.isEditableContext === next.isEditableContext
    || isHoldingApplyingPresentation
  )
  const haveSameSelectedLeftPresentation = (
    previous.isSelectedLeftRow === next.isSelectedLeftRow
    || isHoldingApplyingPresentation
  )
  const haveSameApplyingPresentation = (
    (!previous.emptyRowRun && !next.emptyRowRun)
    || previous.isMatrixContentApplying === next.isMatrixContentApplying
  )
  const haveSameWitnessCells = (
    previous.witnessCellsProps === next.witnessCellsProps
    || (
      previous.witnessCellsProps !== null
      && next.witnessCellsProps !== null
      && areLedgerSafeMatrixWitnessCellsPropsEqual(
        previous.witnessCellsProps,
        next.witnessCellsProps,
      )
    )
  )
  const areEqual = (
    previous.displayRowKey === next.displayRowKey
    && previous.displayRowId === next.displayRowId
    && previous.rowId === next.rowId
    && previous.isProjectionRow === next.isProjectionRow
    && previous.canonicalRowIndex === next.canonicalRowIndex
    && previous.rowLabel === next.rowLabel
    && previous.primaryTitle === next.primaryTitle
    && previous.parallelTitle === next.parallelTitle
    && previous.titleOrigin === next.titleOrigin
    && previous.onEditTitle === next.onEditTitle
    && haveSameEmptyRowRun(previous.emptyRowRun, next.emptyRowRun)
    && haveSameEmptyRunRowLabels(previous.emptyRunRowLabels, next.emptyRunRowLabels)
    && previous.visibleWitnessCount === next.visibleWitnessCount
    && previous.isSelectedStructuralRow === next.isSelectedStructuralRow
    && haveSameSelectedLeftPresentation
    && previous.structuralBoundaryPlacementEdge === next.structuralBoundaryPlacementEdge
    && previous.isStructuralBoundaryPreviewRow === next.isStructuralBoundaryPreviewRow
    && previous.isStructuralRibbonRowAnchor === next.isStructuralRibbonRowAnchor
    && previous.structuralRangeRibbon === next.structuralRangeRibbon
    && previous.hasFragmentAnchorSelection === next.hasFragmentAnchorSelection
    && haveSameEditableContext
    && previous.isPiFocusedRow === next.isPiFocusedRow
    && previous.isPiPreviewAffectedRow === next.isPiPreviewAffectedRow
    && previous.isRetiredMaterialReviewRow === next.isRetiredMaterialReviewRow
    && previous.readOnly === next.readOnly
    && haveSameApplyingPresentation
    && previous.onRowElement === next.onRowElement
    && previous.onStructuralRowHover === next.onStructuralRowHover
    && previous.onStructuralRowLabelClick === next.onStructuralRowLabelClick
    && previous.onAnchorFragmentBeforeRow === next.onAnchorFragmentBeforeRow
    && previous.onCompactEmptyRows === next.onCompactEmptyRows
    && previous.onEditBoundaryBelow === next.onEditBoundaryBelow
    && haveSameWitnessCells
  )

  return areEqual
}

const LedgerSafeMatrixDisplayRowBody = React.memo(({
  primaryTitle, parallelTitle, titleOrigin, onEditTitle,
  displayRowKey,
  displayRowId,
  rowId,
  isProjectionRow,
  canonicalRowIndex,
  rowLabel,
  emptyRowRun,
  emptyRunRowLabels,
  visibleWitnessCount,
  isSelectedStructuralRow,
  isSelectedLeftRow,
  structuralBoundaryPlacementEdge,
  isStructuralBoundaryPreviewRow,
  isStructuralRibbonRowAnchor,
  structuralRangeRibbon,
  hasFragmentAnchorSelection,
  isEditableContext,
  isPiFocusedRow,
  isPiPreviewAffectedRow,
  isRetiredMaterialReviewRow,
  readOnly,
  isMatrixContentApplying,
  witnessCellsProps,
  onRowElement,
  onStructuralRowHover,
  onStructuralRowLabelClick,
  onAnchorFragmentBeforeRow,
  onCompactEmptyRows,
  onEditBoundaryBelow,
}: LedgerSafeMatrixDisplayRowBodyProps) => {
  ledgerSafeMatrixDisplayRowBodyBuildObserver?.(displayRowKey)

  if (emptyRowRun) {
    const isMultiRowRun = emptyRowRun.rowCount > 1
    const firstRowId = emptyRowRun.rowIds[0]
    return (
      <tr
        ref={node => onRowElement(rowId, false, node)}
        data-testid={`ledger-empty-row-run-${emptyRowRun.rowIds.join('-')}`}
        data-structural-boundary-preview={isStructuralBoundaryPreviewRow ? 'true' : undefined}
        className={`group ${isStructuralBoundaryPreviewRow ? 'bg-[#f8eee1]' : 'bg-[#fffdfa]'}`}
      >
        <td
          colSpan={visibleWitnessCount + 1}
          className={`border-b border-black/10 p-0 ${
            isSelectedStructuralRow || isStructuralBoundaryPreviewRow
              ? 'border-l-2 border-l-black'
              : ''
          }`}
        >
          <div className="ledger-heading">
          <div data-testid={`ledger-empty-row-label-${firstRowId}`} className="ledger-heading-reference">
          <button
            type="button"
            data-structural-row-id={firstRowId}
            disabled={readOnly}
            aria-pressed={isSelectedStructuralRow}
            title={structuralBoundaryPlacementEdge
              ? `Place the ${structuralBoundaryPlacementEdge} boundary at ${emptyRowRun.label}`
              : 'Select row for a structural range; Shift-click to extend the range'}
            onMouseEnter={() => onStructuralRowHover(firstRowId, true)}
            onMouseLeave={() => onStructuralRowHover(firstRowId, false)}
            onClick={event => onStructuralRowLabelClick(firstRowId, event)}
            className={`ledger-row-citation w-full border px-0.5 py-0.5 text-[10px] font-bold ${
              isSelectedStructuralRow
                ? 'border-black bg-black text-white'
                : 'border-transparent bg-transparent text-gray-400 hover:border-black/30 hover:text-black'
            } disabled:cursor-default`}
          >
            {formatLedgerCitation(emptyRowRun.label)}
          </button>
          <div className="mt-1 text-[8px] uppercase tracking-widest text-gray-400">
            {isMultiRowRun ? `${emptyRowRun.rowCount} rows` : 'Empty row'}
          </div>
          {hasFragmentAnchorSelection && (
            <button
              type="button"
              data-testid={`ledger-fragment-anchor-before-${firstRowId}`}
              aria-label={`Anchor fragment before ${emptyRowRun.label}`}
              title={`Anchor fragment before ${emptyRowRun.label}`}
              onClick={() => onAnchorFragmentBeforeRow(firstRowId)}
              className="mx-auto mt-1 flex h-6 w-6 items-center justify-center border border-sky-700 bg-sky-50 text-sky-800 shadow-[1px_1px_0_#000] transition-colors hover:bg-sky-100"
            >
              <AnchorIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
            </button>
          )}
          </div>
          <div className="ledger-heading-content">
            {primaryTitle ? <div className="ledger-heading-title">{formatEditorialTitleForDisplay(primaryTitle)}</div> : <span className="ledger-heading-meta">Empty {isMultiRowRun ? 'rows' : 'row'}</span>}
            {parallelTitle ? <div dir="rtl" className="font-reader-arabic text-[13px]">{parallelTitle}</div> : null}
          </div>
          {!readOnly && onEditTitle ? <button type="button" title="Edit row title" aria-label={`Edit title for ${rowLabel}`} onClick={() => onEditTitle(rowId)} className="h-6 w-6 border border-black"><Pencil className="mx-auto h-3 w-3" /></button> : null}
          </div>
          {isStructuralRibbonRowAnchor ? structuralRangeRibbon : null}
        <div
          className={`relative border-b border-black/10 ${isMultiRowRun ? 'py-0' : 'py-5'} align-middle`}
          style={GAP_CELL_STYLE}
        >
          {isMultiRowRun ? (
            <div className="relative grid min-h-[72px] grid-cols-[28px_1fr]">
              <div className="relative border-r border-black/10">
                <div className="absolute bottom-2 left-2 top-2 w-1.5 border border-black border-r-0" />
                {!readOnly && onCompactEmptyRows && (
                  <button
                    type="button"
                    onClick={() => onCompactEmptyRows(emptyRowRun.rowIds)}
                    disabled={isMatrixContentApplying}
                    data-testid={`ledger-empty-run-compact-${firstRowId}`}
                    className="absolute left-[18px] top-1/2 z-10 -translate-y-1/2 whitespace-nowrap border border-black bg-black px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[calc(-50%+2px)] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                  >
                    ⊟ Compact {emptyRowRun.rowCount} Rows
                  </button>
                )}
              </div>
              <div>
                {emptyRunRowLabels.map(entry => (
                  <div
                    key={entry.rowId}
                    className="flex min-h-[36px] items-center border-b border-dashed border-black/15 px-14 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 last:border-b-0"
                  >
                    — empty row {entry.label} —
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-5">
              <span className="min-w-0 flex-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">
                — empty row —
              </span>
              {!readOnly && onCompactEmptyRows && (
                <button
                  type="button"
                  onClick={() => onCompactEmptyRows(emptyRowRun.rowIds)}
                  disabled={isMatrixContentApplying}
                  data-testid={`ledger-empty-row-compact-${rowId}`}
                  className="h-[24px] border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  ⊟ Compact Row
                </button>
              )}
            </div>
          )}
        </div>
        </td>
      </tr>
    )
  }

  if (!witnessCellsProps) {
    return null
  }

  const headingControls = (
      <div
        data-testid={`ledger-row-label-${displayRowId}`}
        title={`Current row position: ${canonicalRowIndex + 1}`}
        className="ledger-heading-reference"
      >
        {isProjectionRow ? (
          <div
            data-testid={`ledger-transposition-projection-label-${rowId}`}
            className="space-y-1 text-[#7c3413]"
          >
            <div className="font-serif text-[12px] font-semibold italic leading-none" aria-hidden="true">
              tr.
            </div>
            <div className="text-[10px] font-bold opacity-80">
              {rowLabel}
            </div>
            <div className="text-[8px] uppercase tracking-widest opacity-70">
              witness order
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              data-structural-row-id={rowId}
              disabled={readOnly}
              data-testid={`ledger-structural-select-${rowId}`}
              aria-pressed={isSelectedStructuralRow}
              title={structuralBoundaryPlacementEdge
                ? `Place the ${structuralBoundaryPlacementEdge} boundary at ${rowLabel}`
                : 'Select row for a structural range; Shift-click to extend the range'}
              onMouseEnter={() => onStructuralRowHover(rowId, true)}
              onMouseLeave={() => onStructuralRowHover(rowId, false)}
              onClick={event => onStructuralRowLabelClick(rowId, event)}
              aria-label={`Select row ${formatLedgerCitation(rowLabel)}`}
              className={`ledger-row-citation w-full border px-0.5 py-0.5 text-left text-[11px] font-semibold ${
                isSelectedStructuralRow
                  ? 'border-black bg-black text-white'
                  : `border-transparent bg-transparent ${isSelectedLeftRow ? 'text-blue-600' : 'text-black'} hover:border-black/30 hover:text-black`
              } disabled:cursor-default`}
            >
              {formatLedgerCitation(rowLabel)}
            </button>
            {hasFragmentAnchorSelection && (
              <button
                type="button"
                data-testid={`ledger-fragment-anchor-before-${rowId}`}
                aria-label={`Anchor fragment before ${rowLabel}`}
                title={`Anchor fragment before ${rowLabel}`}
                onClick={() => onAnchorFragmentBeforeRow(rowId)}
                className="mx-auto mt-1 flex h-6 w-6 items-center justify-center border border-sky-700 bg-sky-50 text-sky-800 shadow-[1px_1px_0_#000] transition-colors hover:bg-sky-100"
              >
                <AnchorIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
              </button>
            )}
            {!readOnly && isEditableContext && (
              <button
                type="button"
                className="sr-only"
                onClick={() => onEditBoundaryBelow(rowId)}
              >
                Edit below
              </button>
            )}
          </>
        )}
      </div>
  )
  return (
    <>
      <LedgerRowHeading rowId={displayRowId} columnCount={visibleWitnessCount + 1}
        controls={headingControls} rowLabel={rowLabel} readOnly={readOnly || isProjectionRow}
        primaryTitle={primaryTitle} parallelTitle={parallelTitle} titleOrigin={titleOrigin}
        onEditTitle={onEditTitle} ribbon={isStructuralRibbonRowAnchor ? structuralRangeRibbon : null} />
      <tr
        ref={node => onRowElement(rowId, isProjectionRow, node)}
        data-testid={`ledger-row-${displayRowId}`}
        data-pi-focused={!isProjectionRow && isPiFocusedRow ? 'true' : 'false'}
        data-pi-preview={!isProjectionRow && isPiPreviewAffectedRow ? 'true' : 'false'}
        data-structural-boundary-preview={isStructuralBoundaryPreviewRow ? 'true' : undefined}
        className={`group ${isProjectionRow ? 'bg-[#faf5ec]' : 'hover:bg-blue-50/30'} ${!isProjectionRow && isSelectedStructuralRow ? 'bg-amber-50/70' : ''} ${isStructuralBoundaryPreviewRow ? 'bg-[#f8eee1]' : ''} ${!isProjectionRow && isPiFocusedRow ? 'bg-blue-50/40' : ''} ${!isProjectionRow && isPiPreviewAffectedRow ? 'bg-violet-50/40' : ''} ${!isProjectionRow && isRetiredMaterialReviewRow ? 'bg-amber-50/35' : ''}`}
      >
        <LedgerSafeMatrixWitnessCells {...witnessCellsProps} />
      </tr>
    </>
  )
}, areLedgerSafeMatrixDisplayRowBodyPropsEqual)

const EMPTY_LEDGER_INLINE_RIBBON_BY_WITNESS_ID = new Map<string, LedgerSafeInlineRibbonModel>()
const EMPTY_LEDGER_WITNESS_ID_SET = new Set<string>()
// Keep the loading selector stable until the stored chapter rows arrive.
const EMPTY_LEDGER_CHAPTER_ROWS: AnchoredRow[] = []
// Selection changes must not invalidate the full occupancy map when no rows collapse.
const EMPTY_HIDDEN_WITNESS_ROW_RUNS: LedgerSafeHiddenWitnessRowRun[] = []
const EMPTY_LEDGER_DISPLAY_ROW_LABELS: ReadonlyArray<{ rowId: string; label: string }> = []

const LEDGER_OCCUPANCY_WINDOW_THRESHOLD = 160
const LEDGER_OCCUPANCY_WINDOW_OVERSCAN_PX = 360

interface LedgerOccupancyWindowRow {
  rowId: string
  rowHeightPx: number
  rowIsStructurallyEmpty: boolean
  sceneHeader: unknown | null
  hiddenWitnessRun?: LedgerSafeHiddenWitnessRowRun | null
}

interface LedgerOccupancyWindowProps<Row extends LedgerOccupancyWindowRow> {
  rows: Row[]
  layer: LedgerOccupancyMapLayer
  renderRow: (row: Row) => React.ReactNode
}

const getLedgerOccupancyWindowRowHeight = (
  row: LedgerOccupancyWindowRow,
  layer: LedgerOccupancyMapLayer,
): number => {
  const contentHeight = row.hiddenWitnessRun
    ? 18
    : row.rowIsStructurallyEmpty
    ? 12
    : layer === 'height' || layer === 'spine'
      ? row.rowHeightPx
      : layer === 'bead'
        ? 18
        : layer === 'thin-line' || layer === 'drift'
          ? 12
          : 6
  const sceneMarkerHeight = row.sceneHeader ? 22 : 0
  return sceneMarkerHeight + contentHeight + 2
}

const findLedgerOccupancyWindowIndex = (
  offsets: number[],
  targetOffset: number,
): number => {
  let low = 0
  let high = Math.max(0, offsets.length - 1)
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if ((offsets[middle + 1] ?? Number.POSITIVE_INFINITY) < targetOffset) {
      low = middle + 1
    } else {
      high = middle
    }
  }
  return low
}

export function LedgerOccupancyWindow<Row extends LedgerOccupancyWindowRow>({
  rows,
  layer,
  renderRow,
}: LedgerOccupancyWindowProps<Row>) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const frameRef = useRef<number | null>(null)
  const [viewport, setViewport] = useState({ scrollTop: 0, height: 0 })
  const isWindowed = rows.length > LEDGER_OCCUPANCY_WINDOW_THRESHOLD
  const offsets = useMemo(() => {
    const next = [0]
    rows.forEach(row => {
      next.push(next[next.length - 1] + getLedgerOccupancyWindowRowHeight(row, layer))
    })
    return next
  }, [layer, rows])

  useLayoutEffect(() => {
    if (!isWindowed) {
      return undefined
    }
    const host = hostRef.current
    const scroller = host?.closest<HTMLElement>('[data-ledger-occupancy-scroll="true"]')
    if (!host || !scroller) {
      return undefined
    }
    const measure = () => {
      frameRef.current = null
      const scrollerRect = scroller.getBoundingClientRect()
      const hostRect = host.getBoundingClientRect()
      const nextViewport = {
        scrollTop: Math.max(0, scrollerRect.top + scroller.clientTop - hostRect.top),
        height: scroller.clientHeight,
      }
      setViewport(previous => (
        previous.scrollTop === nextViewport.scrollTop && previous.height === nextViewport.height
          ? previous
          : nextViewport
      ))
    }
    const scheduleMeasure = () => {
      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(measure)
      }
    }
    measure()
    scroller.addEventListener('scroll', scheduleMeasure, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure)
    observer?.observe(scroller)
    window.addEventListener('resize', scheduleMeasure)
    return () => {
      scroller.removeEventListener('scroll', scheduleMeasure)
      observer?.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [isWindowed, layer, rows.length])

  if (!isWindowed) {
    return (
      <div ref={hostRef} className="flex flex-col gap-[2px]">
        {rows.map(row => <React.Fragment key={row.rowId}>{renderRow(row)}</React.Fragment>)}
      </div>
    )
  }

  const minimumOffset = Math.max(0, viewport.scrollTop - LEDGER_OCCUPANCY_WINDOW_OVERSCAN_PX)
  const maximumOffset = viewport.scrollTop + Math.max(viewport.height, 480) + LEDGER_OCCUPANCY_WINDOW_OVERSCAN_PX
  const startIndex = Math.min(rows.length, findLedgerOccupancyWindowIndex(offsets, minimumOffset))
  const endIndex = Math.min(
    rows.length,
    findLedgerOccupancyWindowIndex(offsets, maximumOffset) + 1,
  )
  const visibleRows = rows.slice(startIndex, endIndex)
  const totalHeight = offsets[offsets.length - 1] || 0

  return (
    <div
      ref={hostRef}
      data-testid="ledger-occupancy-window"
      data-windowed="true"
      className="relative"
      style={{ height: `${totalHeight}px` }}
    >
      <div
        className="absolute inset-x-0 top-0"
        style={{ transform: `translateY(${offsets[startIndex] || 0}px)` }}
      >
        {visibleRows.map(row => (
          <div
            key={row.rowId}
            style={{ height: `${getLedgerOccupancyWindowRowHeight(row, layer)}px` }}
          >
            {renderRow(row)}
          </div>
        ))}
      </div>
    </div>
  )
}

export const LedgerSafeManualStudio: React.FC<LedgerSafeManualStudioProps> = ({
  analysisData,
  witnesses,
  onSelectWitness,
  initialSidePanelTab,
  initialPropagationMode,
  projectLabel,
  contextLabel,
  ledgerId,
  ledgerName,
  headerActions,
  mastheadActions,
  mastheadSupplementalActions,
  ledgerAreaFloatingAction,
  readOnly = false,
  readOnlyRows,
  readOnlyStructuralRangeLayer,
  witnessLabels,
  crossTraditionBooks = [],
  crossTraditionChapters = [],
  crossTraditionWitnesses = [],
  onCreateCrossTraditionWitness,
  initialReaderResumeTarget = null,
  initialViewMode = 'ledger',
  onOpenReader,
  onCloseReader,
  onOpenCamera,
  onCloseCamera,
  onCameraInquiry,
}) => {
  // Editorial siglum for display (per-work when assigned, else operational).
  // Never use this as an identity key — interactions stay keyed on witness.sigla.
  const displaySigla = (witness: Witness): string => witnessLabels?.get(witness.id)?.siglum || witness.sigla
  const displayShelfmark = (witness: Witness): string | undefined => witnessLabels?.get(witness.id)?.shelfmark
  const runtimeEnablePiDock = useRuntimeFeatureFlag('ledgerSafe.piDock', true)
  const runtimeEnableLogTab = useRuntimeFeatureFlag('ledgerSafe.logTab', true)
  const runtimeEnablePropagation = useRuntimeFeatureFlag('ledgerSafe.propagation', process.env.NODE_ENV !== 'production')
  const runtimeEnableEditSuggestions = useRuntimeFeatureFlag('ledgerSafe.editSuggestions', true)
  const runtimeEnableExperimentalMaps = useRuntimeFeatureFlag('ledgerSafe.experimentalMaps', true)
  const runtimeEnableNewWitnessButton = useRuntimeFeatureFlag('ledgerSafe.newWitnessButton', true)
  const runtimeEnableOracleExport = useRuntimeFeatureFlag('ledgerSafe.oracleExport', true)
  const runtimeEnableSceneTitles = useRuntimeFeatureFlag('ledgerSafe.sceneTitles', true)
  const runtimeEnableRowTitleGeneration = useRuntimeFeatureFlag('ledgerSafe.rowTitleGeneration', process.env.NODE_ENV !== 'production')
  const runtimeEnableRowTitleSidecarImport = useRuntimeFeatureFlag('ledgerSafe.rowTitleSidecarImport', process.env.NODE_ENV !== 'production')
  const runtimeEnableStructuralRanges = useRuntimeFeatureFlag('ledgerSafe.structuralRanges', true)
  const runtimeEnableStructuralRangeGeneration = useRuntimeFeatureFlag('ledgerSafe.structuralRangeGeneration', process.env.NODE_ENV !== 'production')
  const runtimeEnableSnapshots = useRuntimeFeatureFlag('ledgerSafe.snapshots', process.env.NODE_ENV !== 'production')
  const runtimeEnableTraditionReader = useRuntimeFeatureFlag('ledgerSafe.traditionReader', process.env.NODE_ENV !== 'production')
  const runtimeEnableCharacterIndex = useRuntimeFeatureFlag('ledgerSafe.characterIndex', process.env.NODE_ENV !== 'production')
  const runtimeEnableCameraLucida = useRuntimeFeatureFlag('ledgerSafe.cameraLucida', process.env.NODE_ENV !== 'production')
  const runtimeEnablePostgresTrial = useRuntimeFeatureFlag('ledgerSafe.postgresTrial', false)
  const [selectedScopeId, setSelectedScopeId] = useState('')
  const [activeWitnessSigla, setActiveWitnessSigla] = useState('')
  const activeWitnessIntentSiglaRef = useRef('')
  const [selectedLeftRowId, setSelectedLeftRowId] = useState('')
  const [selectedCell, setSelectedCell] = useState<LedgerSafeSelectedCell | null>(null)
  const [markMenuCell, setMarkMenuCell] = useState<LedgerSafeSelectedCell | null>(null)
  const [cellPlacementMode, setCellPlacementMode] = useState<LedgerSafeCellPlacementMode | null>(null)
  const [relocationHoverTargetRowId, setRelocationHoverTargetRowId] = useState<string | null>(null)
  const [relocationUndo, setRelocationUndo] = useState<LedgerSafeRelocationUndoState | null>(null)
  const transpositionTarget = cellPlacementMode?.kind === 'transposition' ? cellPlacementMode : null
  const relocationTarget = cellPlacementMode?.kind === 'relocation' ? cellPlacementMode : null
  const setTranspositionTarget = useCallback((
    target: { originRowId: string; witnessId: string } | null
  ) => {
    setCellPlacementMode(target ? { kind: 'transposition', ...target } : null)
    setRelocationHoverTargetRowId(null)
  }, [])
  const setRelocationTarget = useCallback((
    target: Omit<Extract<LedgerSafeCellPlacementMode, { kind: 'relocation' }>, 'kind'> | null
  ) => {
    setCellPlacementMode(target ? { kind: 'relocation', ...target } : null)
    setRelocationHoverTargetRowId(null)
  }, [])
  const [selectionAnchorCell, setSelectionAnchorCell] = useState<LedgerSafeSelectedCell | null>(null)
  const [selectedRange, setSelectedRange] = useState<{ witnessId: string; rowIds: string[] } | null>(null)
  const [structuralSelectionAnchorRowId, setStructuralSelectionAnchorRowId] = useState('')
  const [selectedStructuralRowIds, setSelectedStructuralRowIds] = useState<string[]>([])
  const [hiddenWitnessIds, setHiddenWitnessIds] = useState<string[]>([])
  const [hydratedColumnFilterSignature, setHydratedColumnFilterSignature] = useState('')
  const [columnOrderWitnessIds, setColumnOrderWitnessIds] = useState<string[]>([])
  const [hydratedColumnOrderSignature, setHydratedColumnOrderSignature] = useState('')
  const [draggedColumnWitnessId, setDraggedColumnWitnessId] = useState<string | null>(null)
  const [dragOverColumnWitnessId, setDragOverColumnWitnessId] = useState<string | null>(null)
  const [columnFilterTemplates, setColumnFilterTemplates] = useState<LedgerColumnFilterTemplate[]>([])
  const [hydratedColumnFilterTemplatesKey, setHydratedColumnFilterTemplatesKey] = useState('')
  const columnOrderMutationVersionRef = useRef(0)
  const columnDragSuppressClickRef = useRef(false)
  const columnFilterMutationVersionRef = useRef(0)
  const columnFilterTemplateMutationVersionRef = useRef(0)
  const [isColumnFilterDialogOpen, setIsColumnFilterDialogOpen] = useState(false)
  const [columnFilterTemplateName, setColumnFilterTemplateName] = useState('')
  const [pendingDeltaWords, setPendingDeltaWords] = useState(0)
  const [toastMessage, setToastMessage] = useState<LedgerSafeToastMessage | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [isLedgerApplying, setIsLedgerApplying] = useState(false)
  const applyingSegmentationMatrixRowIdsRef = useRef<ReadonlySet<string>>(new Set())
  const applyingLedgerMatrixRowIdsRef = useRef<ReadonlySet<string>>(new Set())
  const applyingPiMatrixRowIdsRef = useRef<ReadonlySet<string>>(new Set())
  const [isFragmentReviewMode, setIsFragmentReviewMode] = useState(false)
  const [isRetiredMaterialReviewMode, setIsRetiredMaterialReviewMode] = useState(false)
  const [recentFragmentReviewRowIds, setRecentFragmentReviewRowIds] = useState<string[]>([])
  const [fragmentAnchorDrafts, setFragmentAnchorDrafts] = useState<LedgerSafeFragmentAnchorDraft[]>([])
  const [fragmentAnchorScopeKey, setFragmentAnchorScopeKey] = useState('')
  const [fragmentAnchorHydratedContextId, setFragmentAnchorHydratedContextId] = useState('')
  const [fragmentAnchorSelection, setFragmentAnchorSelection] = useState<{
    witnessId: string
    unitUuid: string
  } | null>(null)
  const [isWitnessReplayApplying, setIsWitnessReplayApplying] = useState(false)
  const [diffEnabled, setDiffEnabled] = useState(false)
  const [diffMode, setDiffMode] = useState<DiffMode>('inline')
  const [apparatusScope, setApparatusScope] = useState<ApparatusScope>('chapter')
  const [diffPivotSigla, setDiffPivotSigla] = useState('')
  const [diffAppearance, setDiffAppearance] = useState<DiffAppearance>('arabic')
  const [boundaryDragState, setBoundaryDragState] = useState<BoundaryDragState | null>(null)
  const [inlineEditMode, setInlineEditMode] = useState<InlineEditMode>('idle')
  const [selectedSplitBoundaryWordIndex, setSelectedSplitBoundaryWordIndex] = useState<number | null>(null)
  const [piSessionId, setPiSessionId] = useState('')
  const [piSnapshotId, setPiSnapshotId] = useState('')
  const [isPiBusy, setIsPiBusy] = useState(false)
  const [piDriftWindows, setPiDriftWindows] = useState<PiDriftWindow[]>([])
  const [piFocusedWindow, setPiFocusedWindow] = useState<PiDriftWindow | null>(null)
  const [piPreview, setPiPreview] = useState<PiMatrixAgentPreview | null>(null)
  const [piPreviewEnvelope, setPiPreviewEnvelope] = useState<PiMatrixAgentPreviewEnvelope | null>(null)
  const [piPreviewSummary, setPiPreviewSummary] = useState('')
  const [piAgentChangeSet, setPiAgentChangeSet] = useState<LedgerSafeAgentChangeSetRecord | null>(null)
  const [piAuthorityHead, setPiAuthorityHead] = useState<LedgerSafeAuthorityHeadRecord | null>(null)
  const [piAuthorityHydratedChapterId, setPiAuthorityHydratedChapterId] = useState('')
  const [piError, setPiError] = useState<string | null>(null)
  const [piReviewLog, setPiReviewLog] = useState<PiReviewLogEntry[]>([])
  const [editorialEvents, setEditorialEvents] = useState<LedgerSafeEditorialEvent[]>([])
  const [dismissedEditSuggestionIds, setDismissedEditSuggestionIds] = useState<string[]>([])
  const [ledgerSnapshots, setLedgerSnapshots] = useState<LedgerSafeChapterSnapshot[]>([])
  const [isPiDockExpanded, setIsPiDockExpanded] = useState(readStoredPiDockExpanded)
  const [piDockHeight, setPiDockHeight] = useState(readStoredPiDockHeight)
  const [isPiSettingsOpen, setIsPiSettingsOpen] = useState(false)
  const [piAgentSessionId, setPiAgentSessionId] = useState('')
  const [piAgentModelProvider, setPiAgentModelProvider] = useState('google')
  const [piAgentModelId, setPiAgentModelId] = useState('gemini-3.1-pro-preview')
  const [piAgentThinkingLevel, setPiAgentThinkingLevel] = useState<PiMatrixAgentThinkingLevel>('medium')
  const [piAgentSession, setPiAgentSession] = useState<PiMatrixAgentAgentSession | null>(null)
  const [piAgentModels, setPiAgentModels] = useState<PiMatrixAgentModelOption[]>([])
  const [piAgentReport, setPiAgentReport] = useState<PiMatrixAgentAgentReport | null>(null)
  const [isPiAgentRegisterBusy, setIsPiAgentRegisterBusy] = useState(false)
  const [piAgentPrompt, setPiAgentPrompt] = useState('')
  const [piAgentReply, setPiAgentReply] = useState<PiMatrixAgentAgentReply | null>(null)
  const [piOAuthProviders, setPiOAuthProviders] = useState<PiMatrixAgentOAuthProvider[]>([])
  const [piOAuthLoginAttempt, setPiOAuthLoginAttempt] = useState<PiMatrixAgentOAuthLoginAttempt | null>(null)
  const [piOAuthLoginInput, setPiOAuthLoginInput] = useState('')
  const [isPiAuthBusy, setIsPiAuthBusy] = useState(false)
  const [piAuthError, setPiAuthError] = useState<string | null>(null)
  const [sidePanelTab, setSidePanelTab] = useState<SidePanelTab>(() => normalizeInitialSidePanelTab(initialSidePanelTab))
  const [leftPanelTab, setLeftPanelTab] = useState<LeftPanelTab>(() => normalizeInitialLeftPanelTab(initialSidePanelTab))
  const [ledgerSafeViewMode, setLedgerSafeViewMode] = useState<'ledger' | 'tradition' | 'camera'>(() => (
    initialViewMode === 'camera' ? 'camera' : initialReaderResumeTarget ? 'tradition' : 'ledger'
  ))
  const [traditionSourceWitnessId, setTraditionSourceWitnessId] = useState(
    initialReaderResumeTarget?.witnessId || '',
  )
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false)
  const [selectedCharacterId, setSelectedCharacterId] = useState(
    initialReaderResumeTarget?.characterId || '',
  )
  const [characterRoleDraft, setCharacterRoleDraft] = useState('')
  const [characterNoteDraft, setCharacterNoteDraft] = useState('')
  const [characterNoteTarget, setCharacterNoteTarget] = useState<CharacterNoteTargetKind>('character')
  const [selectedCharacterSpan, setSelectedCharacterSpan] = useState<CharacterSpanSelection | null>(null)
  const [selectedCharacterOccurrenceId, setSelectedCharacterOccurrenceId] = useState('')
  const [characterDetectorId, setCharacterDetectorId] = useState('llm-character-index')
  const [characterExtractionUnit, setCharacterExtractionUnit] = useState<CharacterExtractionUnit>('visible-ledger')
  const [characterIndexListScope, setCharacterIndexListScope] = useState<CharacterIndexListScope>('witness')
  const [characterExtractionWitnessId, setCharacterExtractionWitnessId] = useState('')
  const [characterExtractionSceneId, setCharacterExtractionSceneId] = useState('')
  const [characterFocalizationFigureSource, setCharacterFocalizationFigureSource] = useState<CharacterFocalizationFigureSource>('character-index')
  const [characterModelId, setCharacterModelId] = useState(CHARACTER_INDEX_DEFAULT_MODEL_ID)
  const [characterTemperature, setCharacterTemperature] = useState(CHARACTER_INDEX_DEFAULT_TEMPERATURE)
  const [characterThinkingLevel, setCharacterThinkingLevel] = useState<CharacterThinkingLevel>(CHARACTER_INDEX_DEFAULT_THINKING_LEVEL)
  const [isCharacterDetectorRunning, setIsCharacterDetectorRunning] = useState(false)
  const [characterDetectorRunMode, setCharacterDetectorRunMode] = useState<CharacterDetectorRunMode | null>(null)
  const [characterDetectorFeedback, setCharacterDetectorFeedback] = useState<CharacterDetectorFeedback | null>(null)
  const [characterDetectorRunHistory, setCharacterDetectorRunHistory] = useState<CharacterDetectorRunRecord[]>([])
  const [selectedCharacterDetectorRunId, setSelectedCharacterDetectorRunId] = useState('')
  const [lastCopiedCharacterModelPayload, setLastCopiedCharacterModelPayload] = useState<CharacterIndexExternalModelPayload | null>(null)
  /* Windowed extractions are copied one call at a time and their results pasted back
     incrementally; progress is keyed to the payload's call signature so a changed
     scope or detector resets the sequence. */
  const [characterModelCallProgress, setCharacterModelCallProgress] = useState<{
    signature: string
    copiedCallId: string | null
    importedCallIds: string[]
  }>({ signature: '', copiedCallId: null, importedCallIds: [] })
  const activeCharacterDetectorRunRef = useRef<{ runId: string; controller: AbortController } | null>(null)
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false)
  const [occupancyMapLayer, setOccupancyMapLayer] = useState<LedgerOccupancyMapLayer>('structural')
  const [activeOccupancyMapWitnessId, setActiveOccupancyMapWitnessId] = useState<string | null>(null)
  const [sceneTitleEditor, setSceneTitleEditor] = useState<LedgerSafeSceneTitleEditor | null>(null)
  const [rowTitleEditor, setRowTitleEditor] = useState<LedgerSafeRowTitleEditor | null>(null)
  const [rowTitleSidecarPreview, setRowTitleSidecarPreview] = useState<CollationRowTitleSidecarPreview | null>(null)
  const [rowTitleImportError, setRowTitleImportError] = useState<string | null>(null)
  const rowTitleSidecarInputRef = useRef<HTMLInputElement | null>(null)
  const [rowTitleGenerationRun, setRowTitleGenerationRun] = useState<LedgerSafeRowTitleGenerationRun | null>(null)
  const [rowTitleGenerationModelId, setRowTitleGenerationModelId] = useState(() => (
    normalizeLedgerSafeRowTitleModelId(safeStorageGet(LEDGER_SAFE_ROW_TITLE_MODEL_STORAGE_KEY))
  ))
  const [rowTitleGenerationThinkingLevel, setRowTitleGenerationThinkingLevel] = useState<PiMatrixAgentThinkingLevel>(() => (
    normalizeLedgerSafeRowTitleThinkingLevel(safeStorageGet(LEDGER_SAFE_ROW_TITLE_THINKING_STORAGE_KEY))
  ))
  const [isRowTitleGenerating, setIsRowTitleGenerating] = useState(false)
  const [rowTitleGenerationError, setRowTitleGenerationError] = useState<string | null>(null)
  const [structuralRangeEditor, setStructuralRangeEditor] = useState<LedgerSafeStructuralRangeEditor | null>(null)
  const [structuralRangeMenu, setStructuralRangeMenu] = useState<LedgerSafeStructuralRangeMenu | null>(null)
  const [structuralRangeRibbonAnchor, setStructuralRangeRibbonAnchor] = useState<LedgerSafeStructuralRangeRibbonAnchor | null>(null)
  const [structuralRangeRibbonPosition, setStructuralRangeRibbonPosition] = useState<LedgerSafeStructuralRangeRibbonPosition | null>(null)
  const [structuralBoundaryPlacement, setStructuralBoundaryPlacement] = useState<LedgerSafeStructuralBoundaryPlacement | null>(null)
  const [structuralBoundaryHoverRowId, setStructuralBoundaryHoverRowId] = useState('')
  const structuralRangeMenuRef = useRef<HTMLDivElement | null>(null)
  const [structuralRangeError, setStructuralRangeError] = useState<string | null>(null)
  const [structuralRangeGenerationRun, setStructuralRangeGenerationRun] = useState<LedgerSafeStructuralRangeGenerationRun | null>(null)
  const [structuralRangeGenerationModelId, setStructuralRangeGenerationModelId] = useState(() => (
    normalizeLedgerSafeRowTitleModelId(safeStorageGet(LEDGER_SAFE_STRUCTURE_MODEL_STORAGE_KEY))
  ))
  const [structuralRangeGenerationThinkingLevel, setStructuralRangeGenerationThinkingLevel] = useState<PiMatrixAgentThinkingLevel>(() => (
    normalizeLedgerSafeRowTitleThinkingLevel(safeStorageGet(LEDGER_SAFE_STRUCTURE_THINKING_STORAGE_KEY))
  ))
  const [isStructuralRangeGenerating, setIsStructuralRangeGenerating] = useState(false)
  const [isSceneBackfillApplying, setIsSceneBackfillApplying] = useState(false)
  const [sceneBackfillUndo, setSceneBackfillUndo] = useState<LedgerSafeSceneBackfillUndoState | null>(null)
  const [legacySceneRemovalUndo, setLegacySceneRemovalUndo] = useState<LedgerSafeLegacySceneRemovalUndoState | null>(null)
  const legacySceneEditorialEventTailRef = useRef<Promise<unknown>>(Promise.resolve())
  const [isLedgerActionsMenuOpen, setIsLedgerActionsMenuOpen] = useState(false)
  const ledgerActionsMenuRef = useRef<HTMLDivElement | null>(null)
  const [oracleExportExcludedWitnessIds, setOracleExportExcludedWitnessIds] = useState<string[]>([])
  const [isWitnessPropagationBusy, setIsWitnessPropagationBusy] = useState(false)
  const [witnessPropagationError, setWitnessPropagationError] = useState<string | null>(null)
  const [witnessPropagationSuccess, setWitnessPropagationSuccess] = useState<string | null>(null)
  const [witnessPropagationProposal, setWitnessPropagationProposal] = useState<LedgerSafeWitnessPropagationProposal | null>(null)
  const [witnessPropagationFailures, setWitnessPropagationFailures] = useState<WorkbenchPropagationFailure[]>([])
  const [witnessPropagationResumeState, setWitnessPropagationResumeState] = useState<TranslationBatchResumeState | null>(null)
  const [propagationMode, setPropagationMode] = useState<LedgerSafePropagationMode>(initialPropagationMode || 'witness')
  const [propagationModelId, setPropagationModelId] = useState(() => (
    readStoredLedgerSafeAlignmentModelId(LEDGER_SAFE_PROPAGATION_MODEL_STORAGE_KEY)
  ))
  const [propagationThinkingLevel, setPropagationThinkingLevel] = useState<WorkbenchThinkingLevel>(() => (
    readStoredLedgerSafeAlignmentThinkingLevel(LEDGER_SAFE_PROPAGATION_THINKING_STORAGE_KEY)
  ))
  const [selectedUnitRepairModelId, setSelectedUnitRepairModelId] = useState(() => (
    readStoredLedgerSafeAlignmentModelId(LEDGER_SAFE_REPAIR_MODEL_STORAGE_KEY)
  ))
  const [selectedUnitRepairThinkingLevel, setSelectedUnitRepairThinkingLevel] = useState<WorkbenchThinkingLevel>(() => (
    readStoredLedgerSafeAlignmentThinkingLevel(LEDGER_SAFE_REPAIR_THINKING_STORAGE_KEY)
  ))
  const [contextPropagationPreview, setContextPropagationPreview] = useState<WorkbenchPropagationPreview | null>(null)
  const [selectedUnitRepairPreview, setSelectedUnitRepairPreview] = useState<SelectedUnitPropagationRepairPreview | null>(null)
  const [selectedUnitRepairRun, setSelectedUnitRepairRun] = useState<SelectedUnitRepairRunState | null>(null)
  const [selectedContextPropagationTargetWitnessIds, setSelectedContextPropagationTargetWitnessIds] = useState<string[]>([])
  const [contextPropagationFailures, setContextPropagationFailures] = useState<WorkbenchPropagationFailure[]>([])
  const [contextTranslationPreviewResumeStates, setContextTranslationPreviewResumeStates] = useState<Record<string, TranslationBatchResumeState>>({})
  const [selectedUnitRepairUndo, setSelectedUnitRepairUndo] = useState<SelectedUnitRepairUndoState | null>(null)
  const [contextPropagationState, setContextPropagationState] = useState<{
    previewing: boolean
    applying: boolean
    error: string | null
    success: string | null
  }>({
    previewing: false,
    applying: false,
    error: null,
    success: null,
  })
  const [semanticPreview, setSemanticPreview] = useState<WorkbenchSemanticAlignmentPreview | null>(null)
  const [selectedSemanticCandidateId, setSelectedSemanticCandidateId] = useState<string | null>(null)
  const [semanticPreviewState, setSemanticPreviewState] = useState<{
    previewing: boolean
    applying: boolean
    error: string | null
    success: string | null
  }>({
    previewing: false,
    applying: false,
    error: null,
    success: null,
  })
  const [crossTraditionWorkId, setCrossTraditionWorkId] = useState('')
  const [crossTraditionChapterId, setCrossTraditionChapterId] = useState('')
  const [selectedCrossTraditionWitnessIds, setSelectedCrossTraditionWitnessIds] = useState<string[]>([])
  const [crossTraditionCandidates, setCrossTraditionCandidates] = useState<LedgerCrossTraditionCandidate[]>([])
  const [selectedCrossTraditionCandidateId, setSelectedCrossTraditionCandidateId] = useState<string | null>(null)
  const [crossTraditionState, setCrossTraditionState] = useState<{
    previewing: boolean
    applying: boolean
    error: string | null
    success: string | null
    modelId: string | null
    activeRunId?: string | null
  }>({
    previewing: false,
    applying: false,
    error: null,
    success: null,
    modelId: null,
    activeRunId: null,
  })
  const [crossTraditionPreviewRuns, setCrossTraditionPreviewRuns] = useState<LedgerCrossTraditionPreviewRunRecord[]>([])
  const [selectedCrossTraditionRunId, setSelectedCrossTraditionRunId] = useState<string | null>(null)
  const crossTraditionPreviewAbortControllerRef = useRef<AbortController | null>(null)
  const isMatrixContentApplying = (
    isApplying
    || isLedgerApplying
    || isPiBusy
    || isWitnessReplayApplying
    || isSceneBackfillApplying
    || isWitnessPropagationBusy
    || contextPropagationState.applying
    || semanticPreviewState.applying
    || crossTraditionState.applying
  )

  useEffect(() => {
    if (readOnly && sidePanelTab !== 'map') {
      setSidePanelTab('map')
      return
    }
    if (!runtimeEnablePiDock && sidePanelTab === 'inspector') {
      setSidePanelTab('map')
    }
    if (!runtimeEnableLogTab && sidePanelTab === 'log') {
      setSidePanelTab('map')
    }
    if (!runtimeEnablePropagation && sidePanelTab === 'propagation') {
      setSidePanelTab('map')
    }
    if (!runtimeEnableSceneTitles && sidePanelTab === 'titles') {
      setSidePanelTab('map')
    }
  }, [
    readOnly,
    runtimeEnableLogTab,
    runtimeEnablePiDock,
    runtimeEnablePropagation,
    runtimeEnableSceneTitles,
    sidePanelTab,
  ])

  useEffect(() => {
    if (leftPanelTab === 'characters' && !runtimeEnableCharacterIndex && runtimeEnableStructuralRanges && !readOnly) {
      setLeftPanelTab('structure')
      return
    }
    if (leftPanelTab === 'structure' && (!runtimeEnableStructuralRanges || readOnly) && runtimeEnableCharacterIndex) {
      setLeftPanelTab('characters')
    }
  }, [leftPanelTab, readOnly, runtimeEnableCharacterIndex, runtimeEnableStructuralRanges])

  useEffect(() => {
    if (!runtimeEnableExperimentalMaps && occupancyMapLayer !== 'structural' && occupancyMapLayer !== 'height') {
      setOccupancyMapLayer('structural')
    }
  }, [occupancyMapLayer, runtimeEnableExperimentalMaps])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_PROPAGATION_MODEL_STORAGE_KEY, propagationModelId)
  }, [propagationModelId])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_PROPAGATION_THINKING_STORAGE_KEY, propagationThinkingLevel)
  }, [propagationThinkingLevel])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_REPAIR_MODEL_STORAGE_KEY, selectedUnitRepairModelId)
  }, [selectedUnitRepairModelId])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_REPAIR_THINKING_STORAGE_KEY, selectedUnitRepairThinkingLevel)
  }, [selectedUnitRepairThinkingLevel])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_ROW_TITLE_MODEL_STORAGE_KEY, rowTitleGenerationModelId)
  }, [rowTitleGenerationModelId])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_ROW_TITLE_THINKING_STORAGE_KEY, rowTitleGenerationThinkingLevel)
  }, [rowTitleGenerationThinkingLevel])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_STRUCTURE_MODEL_STORAGE_KEY, structuralRangeGenerationModelId)
  }, [structuralRangeGenerationModelId])

  useEffect(() => {
    safeStorageSet(LEDGER_SAFE_STRUCTURE_THINKING_STORAGE_KEY, structuralRangeGenerationThinkingLevel)
  }, [structuralRangeGenerationThinkingLevel])

  useEffect(() => {
    if (!isLedgerActionsMenuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (target instanceof Node && ledgerActionsMenuRef.current?.contains(target)) {
        return
      }
      setIsLedgerActionsMenuOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLedgerActionsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isLedgerActionsMenuOpen])

  useEffect(() => {
    if (!runtimeEnableTraditionReader && ledgerSafeViewMode === 'tradition') {
      setLedgerSafeViewMode('ledger')
      setTraditionSourceWitnessId('')
    }
  }, [ledgerSafeViewMode, runtimeEnableTraditionReader])

  useEffect(() => {
    if (!runtimeEnableCameraLucida && ledgerSafeViewMode === 'camera') {
      setLedgerSafeViewMode('ledger')
    }
  }, [ledgerSafeViewMode, runtimeEnableCameraLucida])

  useEffect(() => {
    if (initialViewMode === 'camera' && runtimeEnableCameraLucida) {
      setLedgerSafeViewMode('camera')
    } else if (initialViewMode === 'ledger' && ledgerSafeViewMode === 'camera') {
      setLedgerSafeViewMode('ledger')
    }
  }, [initialViewMode, ledgerSafeViewMode, runtimeEnableCameraLucida])

  const diffOverlayCacheRef = useRef(new Map<string, {
    pivotText: string
    witnessText: string
    tokens: RichOverlayToken[]
  }>())
  const apparatusOverlayCacheRef = useRef(new Map<string, {
    pivotText: string
    witnessText: string
    spans: ApparatusSpan[]
    tokens: ApparatusOverlayToken[]
  }>())
  const matrixViewportRef = useRef<HTMLDivElement | null>(null)
  const pendingMatrixViewportAnchorRef = useRef<{
    anchor: LedgerSafeViewportAnchor
    fallbackRowIds: string[]
  } | null>(null)
  const pendingSelectionTimingRef = useRef<{
    rowId: string
    witnessId: string
    startedAt: number
  } | null>(null)
  const pendingActiveWitnessTimingRef = useRef<{
    witnessId: string
    witnessSigla: string
    startedAt: number
  } | null>(null)
  const rowElementRefs = useRef(new Map<string, HTMLTableRowElement>())
  const pendingMatrixRowScrollRef = useRef<{
    rowId: string
    options: ScrollIntoViewOptions
  } | null>(null)
  const piDockInputRef = useRef<HTMLTextAreaElement | null>(null)
  const segmentationOperationGateRef = useRef(createLatestOperationGate())
  const ledgerOperationGateRef = useRef(createLatestOperationGate())
  const piOperationGateRef = useRef(createLatestOperationGate())
  const piSnapshotCancellationRef = useRef<Promise<void>>(Promise.resolve())
  const activeCameraInquiryRef = useRef<{ sessionId: string; operationToken: number } | null>(null)
  const witnessPropagationResumeHydrationRef = useRef<string | null>(null)
  const selectedUnitRepairResumeHydrationRef = useRef<string | null>(null)
  const piAuthPollTimeoutRef = useRef<number | null>(null)
  const piAuthWindowRef = useRef<Window | null>(null)
  const storedWitnesses = useBooksStore(state => state.witnesses)

  useLayoutEffect(() => {
    const pending = pendingSelectionTimingRef.current
    if (
      !pending
      || !selectedCell
      || pending.rowId !== selectedCell.rowId
      || pending.witnessId !== selectedCell.witnessId
    ) {
      return
    }

    const commitMs = readLedgerSafeInteractionNow() - pending.startedAt
    const viewport = matrixViewportRef.current
    if (process.env.NODE_ENV !== 'production') {
      viewport?.setAttribute('data-ledger-selection-commit-ms', commitMs.toFixed(2))
    }

    let frameHandle: number | null = null
    const reportFrame = () => {
      const nextFrameMs = readLedgerSafeInteractionNow() - pending.startedAt
      const sample: LedgerSafeSelectionTimingSample = {
        rowId: pending.rowId,
        witnessId: pending.witnessId,
        commitMs,
        nextFrameMs,
      }
      if (process.env.NODE_ENV !== 'production') {
        viewport?.setAttribute('data-ledger-selection-frame-ms', nextFrameMs.toFixed(2))
        viewport?.setAttribute('data-ledger-selection-row-id', pending.rowId)
        viewport?.setAttribute('data-ledger-selection-witness-id', pending.witnessId)
      }
      ledgerSafeSelectionTimingObserver?.(sample)
      if (pendingSelectionTimingRef.current === pending) {
        pendingSelectionTimingRef.current = null
      }
    }

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      frameHandle = window.requestAnimationFrame(reportFrame)
    } else {
      reportFrame()
    }

    return () => {
      if (frameHandle !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(frameHandle)
      }
    }
  }, [selectedCell])

  useLayoutEffect(() => {
    const pending = pendingActiveWitnessTimingRef.current
    if (!pending || pending.witnessSigla !== activeWitnessSigla) {
      return
    }
    if (process.env.NODE_ENV !== 'production') {
      const viewport = matrixViewportRef.current
      viewport?.setAttribute(
        'data-ledger-active-witness-commit-ms',
        (readLedgerSafeInteractionNow() - pending.startedAt).toFixed(2)
      )
      viewport?.setAttribute('data-ledger-active-witness-id', pending.witnessId)
    }
    pendingActiveWitnessTimingRef.current = null
  }, [activeWitnessSigla])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(LEDGER_SAFE_PI_DOCK_EXPANDED_STORAGE_KEY, String(isPiDockExpanded))
    } catch {
      // UI preference persistence is best-effort.
    }
  }, [isPiDockExpanded])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(LEDGER_SAFE_PI_DOCK_HEIGHT_STORAGE_KEY, String(piDockHeight))
    } catch {
      // UI preference persistence is best-effort.
    }
  }, [piDockHeight])

  const effectiveWitnesses = useMemo(() => {
    const storedById = new Map(storedWitnesses.map(witness => [witness.id, witness]))
    return witnesses.map(witness => {
      const storedWitness = storedById.get(witness.id)
      if (
        storedWitness &&
        storedWitness.chapter_id === witness.chapter_id &&
        storedWitness.book_id === witness.book_id
      ) {
        return storedWitness
      }
      return witness
    })
  }, [storedWitnesses, witnesses])

  const updateWitnessPropagationProposalWithFailure = useCallback(({
    notes,
    diagnostics,
  }: {
    notes?: string
    diagnostics?: WorkbenchPropagationDiagnostics
  }) => {
    setWitnessPropagationProposal(previous => {
      if (!previous) {
        return previous
      }
      return {
        ...previous,
        notes,
        diagnostics,
      }
    })
  }, [])

  const scopeOptions = useMemo(
    () => buildManualStudioScopeOptions(analysisData, effectiveWitnesses),
    [analysisData, effectiveWitnesses]
  )
  const witnessBySigla = useMemo(() => {
    const map = new Map<string, Witness>()
    effectiveWitnesses.forEach(witness => {
      map.set(witness.sigla, witness)
    })
    return map
  }, [effectiveWitnesses])
  const chapterId = useMemo(
    () => effectiveWitnesses.find(witness => witness.chapter_id)?.chapter_id
      || readOnlyRows?.find(row => row.chapterId)?.chapterId
      || readOnlyStructuralRangeLayer?.chapterId
      || '',
    [effectiveWitnesses, readOnlyRows, readOnlyStructuralRangeLayer]
  )
  useEffect(() => {
    setRowTitleEditor(null)
    setRowTitleSidecarPreview(null)
    setRowTitleImportError(null)
    setRowTitleGenerationError(null)
    setStructuralSelectionAnchorRowId('')
    setSelectedStructuralRowIds([])
    setStructuralRangeEditor(null)
    setStructuralRangeMenu(null)
    setStructuralRangeRibbonAnchor(null)
    setStructuralRangeRibbonPosition(null)
    setStructuralBoundaryPlacement(null)
    setStructuralBoundaryHoverRowId('')
    setStructuralRangeError(null)
    if (!chapterId || typeof window === 'undefined') {
      setRowTitleGenerationRun(null)
      setStructuralRangeGenerationRun(null)
      return
    }
    try {
      setRowTitleGenerationRun(loadLedgerSafeRowTitleGenerationRun(chapterId))
    } catch (error) {
      console.warn('Failed to load retained row-title generation run', error)
      setRowTitleGenerationRun(null)
    }
    try {
      setStructuralRangeGenerationRun(loadLedgerSafeStructuralRangeGenerationRun(chapterId))
    } catch (error) {
      console.warn('Failed to load retained structural-range generation run', error)
      setStructuralRangeGenerationRun(null)
    }
  }, [chapterId])
  const bookId = useMemo(
    () => effectiveWitnesses.find(witness => witness.book_id)?.book_id
      || readOnlyStructuralRangeLayer?.bookId
      || '',
    [effectiveWitnesses, readOnlyStructuralRangeLayer]
  )
  const scopeStorageKey = chapterId ? `ledgerSafeStudio:selectedScope::${chapterId}` : ''
  const witnessStorageKey = chapterId ? `ledgerSafeStudio:selectedWitness::${chapterId}` : ''

  const loadChapterRows = useAnchoredRowsStore(state => state.loadChapterRows)
  const chapterRows = useAnchoredRowsStore(state => (
    chapterId ? (state.chapterRows.get(chapterId) || EMPTY_LEDGER_CHAPTER_ROWS) : EMPTY_LEDGER_CHAPTER_ROWS
  ))
  const upsertRowsDirect = useAnchoredRowsStore(state => state.upsertRows)
  const replaceResolvedRowsDirect = useAnchoredRowsStore(state => state.replaceResolvedRows)
  const storedCharacterLayer = useCharacterLayerStore(state => chapterId ? state.chapterLayers.get(chapterId) : undefined)
  const loadChapterCharacterLayer = useCharacterLayerStore(state => state.loadChapterCharacterLayer)
  const setChapterCharacterLayer = useCharacterLayerStore(state => state.setChapterCharacterLayer)
  const saveChapterCharacterLayer = useCharacterLayerStore(state => state.saveChapterCharacterLayer)
  const reviewCharacter = useCharacterLayerStore(state => state.reviewCharacter)
  const reviewCharacterOccurrence = useCharacterLayerStore(state => state.reviewOccurrence)
  const storedSceneLayer = useSceneStore(state => chapterId ? state.canonicalLayers.get(chapterId) : undefined)
  const loadChapterSceneLayer = useSceneStore(state => state.loadChapterSceneLayer)
  const storedStructuralRangeLayer = useStructuralRangeStore(
    state => chapterId ? state.layers.get(chapterId) : undefined
  )
  const loadStructuralRangeLayer = useStructuralRangeStore(state => state.loadLayer)
  const saveStructuralRangeLayer = useStructuralRangeStore(state => state.saveLayer)
  const updateWitnessProcessingResultsBatch = useBooksStore(state => state.updateWitnessProcessingResultsBatch)
  const effectiveChapterRows = readOnlyRows || chapterRows

  useEffect(() => {
    if (!chapterId || readOnly) {
      return
    }
    void loadStructuralRangeLayer(chapterId, bookId).catch(error => {
      console.error('Failed to load the chapter structural range layer', error)
      setStructuralRangeError('The chapter structure could not be loaded.')
    })
  }, [bookId, chapterId, loadStructuralRangeLayer, readOnly])

  useEffect(() => {
    if (IS_LEDGER_OCCUPANCY_VARIANCE_ENABLED || occupancyMapLayer !== 'variance') {
      return
    }
    setOccupancyMapLayer('structural')
  }, [occupancyMapLayer])

  useEffect(() => {
    if (scopeOptions.length === 0) {
      setSelectedScopeId('')
      return
    }
    const valid = selectedScopeId && scopeOptions.some(option => option.clusterId === selectedScopeId)
    if (valid) {
      return
    }
    const persisted = scopeStorageKey ? safeStorageGet(scopeStorageKey) : ''
    const next = scopeOptions.some(option => option.clusterId === persisted) ? persisted : scopeOptions[0].clusterId
    setSelectedScopeId(next)
  }, [scopeOptions, selectedScopeId, scopeStorageKey])

  useEffect(() => {
    if (readOnly || !scopeStorageKey || !selectedScopeId) {
      return
    }
    safeStorageSet(scopeStorageKey, selectedScopeId)
  }, [readOnly, scopeStorageKey, selectedScopeId])

  const activeScope = useMemo(
    () => scopeOptions.find(option => option.clusterId === selectedScopeId) || null,
    [scopeOptions, selectedScopeId]
  )
  const snapshotScopeId = activeScope?.clusterId || '__all__'
  const snapshotScopeLabel = describeManualStudioScope(activeScope) || 'All witnesses'
  const columnOrderStorageKey = chapterId ? `ledgerSafeStudio:columnOrder::${chapterId}::${snapshotScopeId}` : ''
  const columnFilterStorageKey = chapterId ? `ledgerSafeStudio:hiddenColumns::${chapterId}::${snapshotScopeId}` : ''
  const columnFilterTemplatesStorageKey = chapterId ? `ledgerSafeStudio:columnTemplates::${chapterId}` : ''
  const editSuggestionStorageKey = chapterId ? `ledgerSafeStudio:dismissedEditSuggestions::${chapterId}::${snapshotScopeId}` : ''

  const scopeWitnesses = useMemo(() => {
    if (!activeScope) {
      return [] as Witness[]
    }
    return activeScope.members
      .map(sigla => witnessBySigla.get(sigla))
      .filter((witness): witness is Witness => Boolean(witness))
  }, [activeScope, witnessBySigla])
  const nativeScopeWitnessIds = useMemo(
    () => scopeWitnesses.map(witness => witness.id),
    [scopeWitnesses]
  )
  const orderedScopeWitnessIds = useMemo(
    () => columnOrderWitnessIds.length > 0
      ? sanitizeWitnessOrder(nativeScopeWitnessIds, columnOrderWitnessIds)
      : nativeScopeWitnessIds,
    [columnOrderWitnessIds, nativeScopeWitnessIds]
  )
  const columnOrderHydrationSignature = `${columnOrderStorageKey}::${nativeScopeWitnessIds.join(',')}`
  const columnFilterHydrationSignature = `${columnFilterStorageKey}::${orderedScopeWitnessIds.join(',')}`
  const scopeWitnessById = useMemo(
    () => new Map(scopeWitnesses.map(witness => [witness.id, witness])),
    [scopeWitnesses]
  )

  useEffect(() => {
    if (!editSuggestionStorageKey) {
      setDismissedEditSuggestionIds([])
      return
    }
    try {
      const stored = safeStorageGet(editSuggestionStorageKey)
      const parsed = stored ? JSON.parse(stored) : []
      setDismissedEditSuggestionIds(Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : [])
    } catch {
      setDismissedEditSuggestionIds([])
    }
  }, [editSuggestionStorageKey])

  useEffect(() => {
    if (!columnOrderStorageKey || nativeScopeWitnessIds.length === 0) {
      setColumnOrderWitnessIds([])
      setHydratedColumnOrderSignature('')
      return
    }
    let cancelled = false
    const hydrationVersion = columnOrderMutationVersionRef.current
    void (async () => {
      const stored = await getPersistedUiPreference(columnOrderStorageKey)
      if (cancelled || columnOrderMutationVersionRef.current !== hydrationVersion) {
        return
      }
      const parsed = parseStoredWitnessOrder(stored)
      const sanitized = parsed.length > 0
        ? sanitizeWitnessOrder(nativeScopeWitnessIds, parsed)
        : []
      setColumnOrderWitnessIds(previous => areStringArraysEqual(previous, sanitized) ? previous : sanitized)
      setHydratedColumnOrderSignature(columnOrderHydrationSignature)
    })()
    return () => {
      cancelled = true
    }
  }, [chapterId, columnOrderHydrationSignature, columnOrderStorageKey, nativeScopeWitnessIds, snapshotScopeId])

  useEffect(() => {
    if (!columnFilterStorageKey || orderedScopeWitnessIds.length === 0) {
      return
    }
    let cancelled = false
    const hydrationVersion = columnFilterMutationVersionRef.current
    void (async () => {
      const stored = await getPersistedUiPreference(columnFilterStorageKey)
      if (cancelled || columnFilterMutationVersionRef.current !== hydrationVersion) {
        return
      }
      const nextHiddenWitnessIds = parseStoredHiddenWitnessIds(stored)
      const sanitized = sanitizeLedgerColumnHiddenWitnessIds(orderedScopeWitnessIds, nextHiddenWitnessIds)
      logLedgerFilterDebug('hydrate-hidden-columns', {
        chapterId,
        scopeId: snapshotScopeId,
        key: columnFilterStorageKey,
        stored,
        sanitized,
        orderedScopeWitnessIds
      })
      setHiddenWitnessIds(previous => areStringArraysEqual(previous, sanitized) ? previous : sanitized)
      setHydratedColumnFilterSignature(columnFilterHydrationSignature)
    })()
    return () => {
      cancelled = true
    }
  }, [chapterId, columnFilterHydrationSignature, columnFilterStorageKey, orderedScopeWitnessIds, snapshotScopeId])

  useEffect(() => {
    if (
      readOnly ||
      !columnOrderStorageKey ||
      nativeScopeWitnessIds.length === 0 ||
      hydratedColumnOrderSignature !== columnOrderHydrationSignature ||
      columnOrderWitnessIds.length === 0
    ) {
      return
    }
    const sanitized = sanitizeWitnessOrder(nativeScopeWitnessIds, columnOrderWitnessIds)
    void setPersistedUiPreference(columnOrderStorageKey, JSON.stringify(sanitized))
  }, [
    columnOrderHydrationSignature,
    columnOrderStorageKey,
    columnOrderWitnessIds,
    hydratedColumnOrderSignature,
    nativeScopeWitnessIds,
    readOnly
  ])

  useEffect(() => {
    if (
      readOnly ||
      !columnFilterStorageKey ||
      orderedScopeWitnessIds.length === 0 ||
      hydratedColumnFilterSignature !== columnFilterHydrationSignature
    ) {
      return
    }
    logLedgerFilterDebug('effect-persist-hidden-columns', {
      chapterId,
      scopeId: snapshotScopeId,
      key: columnFilterStorageKey,
      hiddenWitnessIds
    })
    void setPersistedUiPreference(columnFilterStorageKey, JSON.stringify(hiddenWitnessIds))
  }, [chapterId, columnFilterHydrationSignature, columnFilterStorageKey, hiddenWitnessIds, hydratedColumnFilterSignature, orderedScopeWitnessIds.length, readOnly, snapshotScopeId])

  useEffect(() => {
    let cancelled = false
    const hydrationVersion = columnFilterTemplateMutationVersionRef.current
    void (async () => {
      const stored = await getPersistedUiPreference(columnFilterTemplatesStorageKey)
      if (cancelled || columnFilterTemplateMutationVersionRef.current !== hydrationVersion) {
        return
      }
      const nextTemplates = parseLedgerColumnFilterTemplates(stored)
      logLedgerFilterDebug('hydrate-column-templates', {
        chapterId,
        key: columnFilterTemplatesStorageKey,
        stored,
        templateCount: nextTemplates.length
      })
      setColumnFilterTemplates(previous => JSON.stringify(previous) === JSON.stringify(nextTemplates) ? previous : nextTemplates)
      setHydratedColumnFilterTemplatesKey(columnFilterTemplatesStorageKey)
    })()
    return () => {
      cancelled = true
    }
  }, [chapterId, columnFilterTemplatesStorageKey])

  useEffect(() => {
    if (readOnly || !columnFilterTemplatesStorageKey || hydratedColumnFilterTemplatesKey !== columnFilterTemplatesStorageKey) {
      return
    }
    safeStorageSet(columnFilterTemplatesStorageKey, serializeLedgerColumnFilterTemplates(columnFilterTemplates))
  }, [columnFilterTemplates, columnFilterTemplatesStorageKey, hydratedColumnFilterTemplatesKey, readOnly])

  const visibleScopeWitnessIds = useMemo(
    () => buildVisibleWitnessIds(orderedScopeWitnessIds, hiddenWitnessIds),
    [orderedScopeWitnessIds, hiddenWitnessIds]
  )
  const visibleScopeWitnessIdSet = useMemo(
    () => new Set(visibleScopeWitnessIds),
    [visibleScopeWitnessIds]
  )
  const visibleScopeWitnesses = useMemo(
    () => visibleScopeWitnessIds
      .map(witnessId => scopeWitnessById.get(witnessId))
      .filter((witness): witness is Witness => Boolean(witness)),
    [scopeWitnessById, visibleScopeWitnessIds]
  )
  const interactiveScopeWitnesses = useMemo(
    () => (visibleScopeWitnesses.length > 0 ? visibleScopeWitnesses : scopeWitnesses),
    [scopeWitnesses, visibleScopeWitnesses]
  )
  const diffScopeWitnesses = useMemo(
    () => interactiveScopeWitnesses.filter(isArabicDiffWitness),
    [interactiveScopeWitnesses]
  )
  const diffScopeWitnessSiglaSet = useMemo(
    () => new Set(diffScopeWitnesses.map(witness => witness.sigla)),
    [diffScopeWitnesses]
  )
  const previousWitnessTextIndexesRef = useRef<WitnessUnitTextLookupByWitnessId>(new Map())
  const traditionWitnessUnitTextLookupByWitnessId = useMemo<WitnessUnitTextLookupByWitnessId>(() => {
    const previous = previousWitnessTextIndexesRef.current
    const next = new Map(scopeWitnesses.map(witness => [
      witness.id,
      buildWitnessUnitTextIndex(witness, previous.get(witness.id)),
    ]))
    // Retain only the current scope. Removed witnesses and replaced text are
    // released, and both editor views share the same cleaned unit records.
    previousWitnessTextIndexesRef.current = next
    return next
  }, [scopeWitnesses])
  const witnessUnitTextLookupByWitnessId = useMemo<WitnessUnitTextLookupByWitnessId>(
    () => new Map(interactiveScopeWitnesses.map(witness => [
      witness.id,
      traditionWitnessUnitTextLookupByWitnessId.get(witness.id)!,
    ])),
    [interactiveScopeWitnesses, traditionWitnessUnitTextLookupByWitnessId]
  )
  const crossTraditionSourceOptions = useMemo(
    () => buildLedgerCrossTraditionSourceOptions({
      books: crossTraditionBooks,
      chapters: crossTraditionChapters,
      witnesses: crossTraditionWitnesses,
      currentBookId: bookId,
      currentChapterId: chapterId,
    }),
    [bookId, chapterId, crossTraditionBooks, crossTraditionChapters, crossTraditionWitnesses]
  )
  const crossTraditionWorks = useMemo(() => {
    const seen = new Set<string>()
    return crossTraditionSourceOptions
      .map(option => ({ id: option.workId, title: option.workTitle }))
      .filter(work => {
        if (seen.has(work.id)) {
          return false
        }
        seen.add(work.id)
        return true
      })
  }, [crossTraditionSourceOptions])
  const crossTraditionChaptersForWork = useMemo(() => {
    const seen = new Set<string>()
    return crossTraditionSourceOptions
      .filter(option => !crossTraditionWorkId || option.workId === crossTraditionWorkId)
      .map(option => ({
        id: option.chapterId,
        title: option.chapterTitle,
        workId: option.workId,
        workTitle: option.workTitle,
      }))
      .filter(nextChapter => {
        if (seen.has(nextChapter.id)) {
          return false
        }
        seen.add(nextChapter.id)
        return true
      })
  }, [crossTraditionSourceOptions, crossTraditionWorkId])
  const crossTraditionWitnessOptions = useMemo(
    () => crossTraditionSourceOptions.filter(option => (
      (!crossTraditionWorkId || option.workId === crossTraditionWorkId)
      && (!crossTraditionChapterId || option.chapterId === crossTraditionChapterId)
    )),
    [crossTraditionChapterId, crossTraditionSourceOptions, crossTraditionWorkId]
  )
  const selectedCrossTraditionSourceOptions = useMemo(
    () => selectedCrossTraditionWitnessIds
      .map(witnessId => crossTraditionSourceOptions.find(option => option.witnessId === witnessId) || null)
      .filter((option): option is LedgerCrossTraditionSourceOption => Boolean(option)),
    [crossTraditionSourceOptions, selectedCrossTraditionWitnessIds]
  )
  const selectedCrossTraditionCandidate = useMemo(
    () => crossTraditionCandidates.find(candidate => candidate.candidateId === selectedCrossTraditionCandidateId) || null,
    [crossTraditionCandidates, selectedCrossTraditionCandidateId]
  )
  const crossTraditionReviewSummary = useMemo(() => ({
    total: crossTraditionCandidates.length,
    accepted: crossTraditionCandidates.filter(candidate => candidate.reviewStatus === 'accepted').length,
    deferred: crossTraditionCandidates.filter(candidate => candidate.reviewStatus === 'deferred').length,
    rejected: crossTraditionCandidates.filter(candidate => candidate.reviewStatus === 'rejected').length,
  }), [crossTraditionCandidates])
  const crossTraditionPreviewRunStorageKey = useMemo(
    () => getLedgerCrossTraditionPreviewRunStorageKey(chapterId),
    [chapterId]
  )
  const selectedCrossTraditionPreviewRun = useMemo(
    () => crossTraditionPreviewRuns.find(run => run.runId === selectedCrossTraditionRunId) || null,
    [crossTraditionPreviewRuns, selectedCrossTraditionRunId]
  )
  const rememberCrossTraditionPreviewRuns = useCallback((
    updater: LedgerCrossTraditionPreviewRunRecord[] | ((previous: LedgerCrossTraditionPreviewRunRecord[]) => LedgerCrossTraditionPreviewRunRecord[])
  ) => {
    setCrossTraditionPreviewRuns(previous => {
      const next = typeof updater === 'function' ? updater(previous) : updater
      serializeLedgerCrossTraditionPreviewRunHistory(crossTraditionPreviewRunStorageKey, next)
      return next
    })
  }, [crossTraditionPreviewRunStorageKey])

  useEffect(() => {
    const storedRuns = parseLedgerCrossTraditionPreviewRunHistory(crossTraditionPreviewRunStorageKey)
    setCrossTraditionPreviewRuns(storedRuns)
    setSelectedCrossTraditionRunId(storedRuns[0]?.runId || null)
  }, [crossTraditionPreviewRunStorageKey])

  useEffect(() => () => {
    crossTraditionPreviewAbortControllerRef.current?.abort()
    crossTraditionPreviewAbortControllerRef.current = null
  }, [])

  useEffect(() => {
    if (crossTraditionWorkId && crossTraditionWorks.some(work => work.id === crossTraditionWorkId)) {
      return
    }
    const nextWorkId = crossTraditionWorks[0]?.id || ''
    if (crossTraditionWorkId !== nextWorkId) {
      setCrossTraditionWorkId(nextWorkId)
    }
  }, [crossTraditionWorkId, crossTraditionWorks])

  useEffect(() => {
    if (crossTraditionChapterId && crossTraditionChaptersForWork.some(nextChapter => nextChapter.id === crossTraditionChapterId)) {
      return
    }
    const nextChapterId = crossTraditionChaptersForWork[0]?.id || ''
    if (crossTraditionChapterId !== nextChapterId) {
      setCrossTraditionChapterId(nextChapterId)
    }
  }, [crossTraditionChapterId, crossTraditionChaptersForWork])

  useEffect(() => {
    const availableWitnessIds = new Set(crossTraditionWitnessOptions.map(option => option.witnessId))
    setSelectedCrossTraditionWitnessIds(previous => {
      const next = previous.filter(witnessId => availableWitnessIds.has(witnessId))
      return next.length === previous.length && next.every((witnessId, index) => witnessId === previous[index])
        ? previous
        : next
    })
  }, [crossTraditionWitnessOptions])
  const selectedOccupancyMapWitness = useMemo(
    () => (selectedCell ? visibleScopeWitnesses.find(witness => witness.id === selectedCell.witnessId) || null : null),
    [selectedCell, visibleScopeWitnesses]
  )
  const activeOccupancyMapWitness = useMemo(
    () => visibleScopeWitnesses.find(witness => witness.id === activeOccupancyMapWitnessId) || selectedOccupancyMapWitness || null,
    [activeOccupancyMapWitnessId, selectedOccupancyMapWitness, visibleScopeWitnesses]
  )

  useEffect(() => {
    if (sidePanelTab !== 'map') {
      setActiveOccupancyMapWitnessId(null)
      return
    }
    if (activeOccupancyMapWitnessId && !visibleScopeWitnessIdSet.has(activeOccupancyMapWitnessId)) {
      setActiveOccupancyMapWitnessId(null)
    }
  }, [activeOccupancyMapWitnessId, sidePanelTab, visibleScopeWitnessIdSet])

  useEffect(() => {
    const scopeWitnessIdSet = new Set(scopeWitnesses.map(witness => witness.id))
    setOracleExportExcludedWitnessIds(previous => previous.filter(witnessId => scopeWitnessIdSet.has(witnessId)))
  }, [scopeWitnesses])

  useEffect(() => {
    if (readOnly || !chapterId) {
      return
    }
    loadChapterRows(chapterId).catch(error => {
      console.error('Failed to load chapter rows for Variorum', error)
    })
  }, [chapterId, loadChapterRows, readOnly])

  useEffect(() => {
    if (!runtimeEnableCharacterIndex || !chapterId) {
      return
    }
    loadChapterCharacterLayer(chapterId).catch(error => {
      console.error('Failed to load Variorum character layer', error)
    })
  }, [chapterId, loadChapterCharacterLayer, runtimeEnableCharacterIndex])

  useEffect(() => {
    if (!runtimeEnableCameraLucida || !chapterId) {
      return
    }
    loadChapterSceneLayer(chapterId).catch(error => {
      console.error('Failed to load Variorum scene layer for Camera Lucida', error)
    })
  }, [chapterId, loadChapterSceneLayer, runtimeEnableCameraLucida])

  useEffect(() => {
    if (runtimeEnableCharacterIndex) {
      return
    }
    setSelectedCharacterId('')
    setSelectedCharacterOccurrenceId('')
    setSelectedCharacterSpan(null)
    setCharacterDetectorFeedback(null)
    setCharacterExtractionUnit('visible-ledger')
    setCharacterExtractionWitnessId('')
    setCharacterExtractionSceneId('')
  }, [runtimeEnableCharacterIndex])

  const resolvedCharacterLayer = useMemo<ChapterCharacterLayer>(
    () => storedCharacterLayer || createEmptyChapterCharacterLayer({
      chapterId,
      bookId,
    }),
    [bookId, chapterId, storedCharacterLayer]
  )
  const hydratedCharacterRunHistoryChapterRef = useRef<string | null>(null)

  useEffect(() => {
    if (!storedCharacterLayer || !chapterId) {
      if (hydratedCharacterRunHistoryChapterRef.current === chapterId) {
        hydratedCharacterRunHistoryChapterRef.current = null
      }
      return
    }
    if (hydratedCharacterRunHistoryChapterRef.current === chapterId) {
      return
    }

    const persistedRuns = readDurableCharacterDetectorRuns(storedCharacterLayer)
      .slice(0, CHARACTER_DETECTOR_RUN_HISTORY_LIMIT)
      .map(run => ({
      runId: run.runId,
      runMode: run.runMode,
      status: run.status,
      startedAt: run.startedAt,
      ...(run.completedAt ? { completedAt: run.completedAt } : {}),
      ...(run.resumedFromRunId ? { resumedFromRunId: run.resumedFromRunId } : {}),
      ...(run.retriedFromRunId ? { retriedFromRunId: run.retriedFromRunId } : {}),
      provenance: run.provenance,
      ...(run.resultSet ? { resultSet: run.resultSet } : {}),
      ...(run.modelCallFingerprints.length > 0
        ? { diagnostics: { modelCallFingerprints: run.modelCallFingerprints } }
        : {})
    })) as CharacterDetectorRunRecord[]

    hydratedCharacterRunHistoryChapterRef.current = chapterId
    setCharacterDetectorRunHistory(persistedRuns)
    setSelectedCharacterDetectorRunId(previous => (
      persistedRuns.some(run => run.runId === previous) ? previous : persistedRuns[0]?.runId || ''
    ))
  }, [chapterId, storedCharacterLayer])
  const readerAdjudicationLayerRef = useRef(resolvedCharacterLayer)
  const readerAdjudicationSaveQueueRef = useRef<Promise<void>>(Promise.resolve())
  const readerAdjudicationPendingCountRef = useRef(0)

  useEffect(() => {
    if (readerAdjudicationPendingCountRef.current === 0) {
      readerAdjudicationLayerRef.current = resolvedCharacterLayer
    }
  }, [resolvedCharacterLayer])

  useEffect(() => {
    if (readOnly || !chapterId) {
      setEditorialEvents([])
      return
    }
    let cancelled = false
    void listLedgerSafeEditorialEventsForChapter(chapterId, { limit: 12 })
      .then(events => {
        if (!cancelled) {
          setEditorialEvents(events.slice(0, 12))
        }
      })
      .catch(error => {
        console.error('Failed to load Variorum editorial events', error)
      })
    return () => {
      cancelled = true
    }
  }, [chapterId, readOnly])

  useEffect(() => {
    if (
      readOnly
      || !chapterId
      || !runtimeEnableSnapshots
      || sidePanelTab !== 'log'
    ) {
      setLedgerSnapshots([])
      return
    }
    setLedgerSnapshots([])
    let cancelled = false
    const loadSnapshots = async () => {
      try {
        const snapshots = await listLedgerSafeChapterSnapshots(
          chapterId,
          snapshotScopeId,
          { limit: 8 },
        )
        if (!cancelled) {
          setLedgerSnapshots(Array.isArray(snapshots) ? snapshots : [])
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load Variorum snapshots', error)
        }
      }
    }
    void loadSnapshots()
    return () => {
      cancelled = true
    }
  }, [chapterId, readOnly, runtimeEnableSnapshots, sidePanelTab, snapshotScopeId])

  useEffect(() => {
    if (interactiveScopeWitnesses.length === 0) {
      setActiveWitnessSigla('')
      setSelectedCell(null)
      return
    }
    const valid = activeWitnessSigla && interactiveScopeWitnesses.some(witness => witness.sigla === activeWitnessSigla)
    if (valid) {
      return
    }
    const persisted = witnessStorageKey ? safeStorageGet(witnessStorageKey) : ''
    const next = interactiveScopeWitnesses.some(witness => witness.sigla === persisted)
      ? persisted
      : (activeScope?.groundTruthSigla && interactiveScopeWitnesses.some(witness => witness.sigla === activeScope.groundTruthSigla)
        ? activeScope.groundTruthSigla
        : interactiveScopeWitnesses[0].sigla)
    setActiveWitnessSigla(next)
  }, [activeScope?.groundTruthSigla, activeWitnessSigla, interactiveScopeWitnesses, witnessStorageKey])

  useEffect(() => {
    if (readOnly || !witnessStorageKey || !activeWitnessSigla) {
      return
    }
    safeStorageSet(witnessStorageKey, activeWitnessSigla)
  }, [activeWitnessSigla, readOnly, witnessStorageKey])

  useEffect(() => {
    activeWitnessIntentSiglaRef.current = activeWitnessSigla
  }, [activeWitnessSigla])

  useEffect(() => {
    if (diffScopeWitnesses.length === 0) {
      setDiffPivotSigla('')
      return
    }
    const valid = diffPivotSigla && diffScopeWitnessSiglaSet.has(diffPivotSigla)
    if (valid) {
      return
    }
    const fallbackSigla = activeScope?.groundTruthSigla && diffScopeWitnessSiglaSet.has(activeScope.groundTruthSigla)
      ? activeScope.groundTruthSigla
      : diffScopeWitnesses[0].sigla
    setDiffPivotSigla(fallbackSigla)
  }, [activeScope?.groundTruthSigla, diffPivotSigla, diffScopeWitnesses, diffScopeWitnessSiglaSet])

  const activeWitness = useMemo(
    () => interactiveScopeWitnesses.find(witness => witness.sigla === activeWitnessSigla) || interactiveScopeWitnesses[0],
    [activeWitnessSigla, interactiveScopeWitnesses]
  )
  const activeWitnessPresentation = pendingActiveWitnessTimingRef.current
    ? interactiveScopeWitnesses.find(witness => witness.id === pendingActiveWitnessTimingRef.current?.witnessId) || activeWitness
    : activeWitness
  const diffPivotWitness = useMemo(
    () => diffScopeWitnesses.find(witness => witness.sigla === diffPivotSigla) || null,
    [diffPivotSigla, diffScopeWitnesses]
  )
  const activeVisibleWitness = useMemo(
    () => visibleScopeWitnesses.find(witness => witness.id === activeWitness?.id) || visibleScopeWitnesses[0] || activeWitness || null,
    [activeWitness, visibleScopeWitnesses]
  )
  const activeTraditionSourceWitness = useMemo(
    () => scopeWitnesses.find(witness => witness.id === traditionSourceWitnessId) || activeVisibleWitness || scopeWitnesses[0] || null,
    [activeVisibleWitness, scopeWitnesses, traditionSourceWitnessId]
  )
  useEffect(() => {
    if (!runtimeEnableCharacterIndex) {
      return
    }
    const witnessExists = characterExtractionWitnessId && scopeWitnessById.has(characterExtractionWitnessId)
    if (witnessExists) {
      return
    }
    setCharacterExtractionWitnessId(activeWitness?.id || scopeWitnesses[0]?.id || '')
  }, [activeWitness?.id, characterExtractionWitnessId, runtimeEnableCharacterIndex, scopeWitnessById, scopeWitnesses])
  const handleSetActiveWitnessFromColumn = useEventCallback((sigla: string) => {
    activeWitnessIntentSiglaRef.current = sigla
    setActiveWitnessSigla(sigla)
    setSelectedLeftRowId('')
    setInlineEditMode('idle')
    setSelectedSplitBoundaryWordIndex(null)
    setPendingDeltaWords(0)
    setBoundaryDragState(null)
  })
  useEffect(() => {
    if (ledgerSafeViewMode !== 'tradition') {
      return
    }
    if (scopeWitnesses.length === 0) {
      if (shouldPreserveReaderIntentDuringScopeHydration({
        readerTarget: initialReaderResumeTarget,
        scopedWitnessCount: scopeWitnesses.length,
      })) {
        return
      }
      setLedgerSafeViewMode('ledger')
      setTraditionSourceWitnessId('')
      return
    }
    if (!activeTraditionSourceWitness) {
      setTraditionSourceWitnessId(scopeWitnesses[0].id)
    }
  }, [activeTraditionSourceWitness, initialReaderResumeTarget, ledgerSafeViewMode, scopeWitnesses])
  useEffect(() => {
    if (!runtimeEnableCharacterIndex || ledgerSafeViewMode !== 'tradition' || !activeTraditionSourceWitness) {
      return
    }
    if (characterExtractionUnit !== 'witness') {
      setCharacterExtractionUnit('witness')
      setCharacterDetectorFeedback(null)
    }
    if (characterExtractionWitnessId !== activeTraditionSourceWitness.id) {
      setCharacterExtractionWitnessId(activeTraditionSourceWitness.id)
      setCharacterDetectorFeedback(null)
    }
  }, [
    activeTraditionSourceWitness,
    characterExtractionUnit,
    characterExtractionWitnessId,
    ledgerSafeViewMode,
    runtimeEnableCharacterIndex,
  ])
  const activeWitnessSegmentationState = useMemo(() => {
    if (!activeWitness?.segmentation_result?.chunk_results?.length) {
      return 'unsegmented'
    }
    const unitCount = activeWitness.segmentation_result.chunk_results.reduce(
      (sum, chunk) => sum + (chunk.micro_units?.length || 0),
      0
    )
    if (unitCount === 0) {
      return 'unsegmented'
    }
    return isAutoPropagatedSegmentation(activeWitness.segmentation_result) ? 'propagated' : 'manual'
  }, [activeWitness])
  const contextPropagationSourceCandidates = useMemo(
    () => scopeWitnesses.filter(witness => (
      Boolean(witness.segmentation_result?.chunk_results?.length)
      && !isTranslationAlignmentWitness(witness)
    )),
    [scopeWitnesses]
  )
  const fallbackContextPropagationAnchor = useMemo(
    () => contextPropagationSourceCandidates[0] || null,
    [contextPropagationSourceCandidates]
  )
  const selectedContextPropagationTargetWitnessIdSet = useMemo(
    () => new Set(selectedContextPropagationTargetWitnessIds),
    [selectedContextPropagationTargetWitnessIds]
  )

  useEffect(() => {
    if (selectedCell && !visibleScopeWitnessIdSet.has(selectedCell.witnessId)) {
      setSelectedCell(null)
    }
    if (selectionAnchorCell && !visibleScopeWitnessIdSet.has(selectionAnchorCell.witnessId)) {
      setSelectionAnchorCell(null)
    }
    if (selectedRange && !visibleScopeWitnessIdSet.has(selectedRange.witnessId)) {
      setSelectedRange(null)
    }
  }, [selectedCell, selectedRange, selectionAnchorCell, visibleScopeWitnessIdSet])
  const witnessPropagationExemplarSummary = useMemo(() => {
    if (!witnessPropagationProposal || witnessPropagationProposal.exemplarWitnessIds.length === 0) {
      return 'Auto-selected from the accepted collation'
    }
    return witnessPropagationProposal.exemplarWitnessIds
      .map(witnessId => scopeWitnesses.find(witness => witness.id === witnessId)?.sigla || witnessId)
      .join(', ')
  }, [scopeWitnesses, witnessPropagationProposal])
  const handleSetDiffPivotSigla = useEventCallback((sigla: string) => {
    if (isMatrixContentApplying || !sigla || !diffScopeWitnessSiglaSet.has(sigla)) {
      return
    }
    setDiffEnabled(true)
    setDiffPivotSigla(sigla)
  })
  const handleWitnessColumnHeaderClick = useEventCallback((sigla: string) => {
    if (isMatrixContentApplying || columnDragSuppressClickRef.current) {
      return
    }
    if (diffEnabled) {
      if (diffScopeWitnessSiglaSet.has(sigla)) {
        handleSetDiffPivotSigla(sigla)
      }
      return
    }
    if (!readOnly) {
      handleSetActiveWitnessFromColumn(sigla)
    }
  })

  useEffect(() => {
    if (activeWitness?.sigla && onSelectWitness) {
      onSelectWitness(activeWitness.sigla)
    }
  }, [activeWitness, onSelectWitness])

  useEffect(() => {
    setWitnessPropagationProposal(null)
    setWitnessPropagationResumeState(null)
    setWitnessPropagationError(null)
    setWitnessPropagationSuccess(null)
    setWitnessPropagationFailures([])
    witnessPropagationResumeHydrationRef.current = null
  }, [activeWitness?.id, chapterId, selectedScopeId])

  useEffect(() => {
    setIsFragmentReviewMode(false)
    setRecentFragmentReviewRowIds([])
    setFragmentAnchorSelection(null)
  }, [activeWitness?.id, chapterId, selectedScopeId])

  useEffect(() => {
    if (readOnly || !chapterId) {
      setFragmentAnchorDrafts([])
      setFragmentAnchorScopeKey('')
      setFragmentAnchorHydratedContextId(chapterId)
      return undefined
    }
    let cancelled = false
    setFragmentAnchorHydratedContextId('')
    void loadLedgerSafeFragmentAnchorDrafts(chapterId, ledgerId)
      .then(result => {
        if (cancelled) {
          return
        }
        setFragmentAnchorDrafts(result.anchors)
        setFragmentAnchorScopeKey(result.scopeKey)
        setFragmentAnchorHydratedContextId(chapterId)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error('Failed to restore fragment anchors', error)
        setFragmentAnchorDrafts([])
        setFragmentAnchorScopeKey('')
        setFragmentAnchorHydratedContextId(chapterId)
      })
    return () => {
      cancelled = true
    }
  }, [chapterId, ledgerId, ledgerName, readOnly])

  const persistFragmentAnchorDrafts = useCallback(async (
    nextDrafts: LedgerSafeFragmentAnchorDraft[]
  ): Promise<void> => {
    setFragmentAnchorDrafts(nextDrafts)
    if (!fragmentAnchorScopeKey) {
      return
    }
    const persisted = await saveLedgerSafeFragmentAnchorDrafts({
      scopeKey: fragmentAnchorScopeKey,
      anchors: nextDrafts
    })
    if (!persisted) {
      throw new Error('The fragment anchor could not be saved on this device')
    }
  }, [fragmentAnchorScopeKey])

  useEffect(() => {
    setContextPropagationPreview(null)
    setSelectedUnitRepairPreview(null)
    setSelectedUnitRepairRun(null)
    setSelectedContextPropagationTargetWitnessIds([])
    setContextPropagationFailures([])
    setContextTranslationPreviewResumeStates({})
    setContextPropagationState({
      previewing: false,
      applying: false,
      error: null,
      success: null,
    })
    selectedUnitRepairResumeHydrationRef.current = null
  }, [chapterId, selectedScopeId])

  const resolvedRows = useMemo(() => {
    if (!chapterId) {
      return [] as AnchoredRow[]
    }
    return resolveStudioAnchorRows(effectiveChapterRows, {
      chapterId,
      clusterId: activeScope && !activeScope.isAggregate ? activeScope.clusterId : ''
    })
  }, [activeScope, chapterId, effectiveChapterRows])
  const retiredMaterialAudit = useMemo(
    () => buildLedgerSafeRetiredMaterialAudit({
      rows: resolvedRows,
      witnesses: scopeWitnesses,
    }),
    [resolvedRows, scopeWitnesses],
  )
  const captureMatrixViewportContinuity = useEventCallback((preferredRowIds: string[]) => {
    if (pendingMatrixViewportAnchorRef.current) {
      return pendingMatrixViewportAnchorRef.current
    }
    const viewport = matrixViewportRef.current
    if (!viewport) {
      return null
    }
    const pending = {
      anchor: captureLedgerSafeViewportAnchor({
        viewport,
        orderedRowIds: resolvedRows.map(row => row.id),
        rowElements: rowElementRefs.current,
        preferredRowIds,
      }),
      fallbackRowIds: preferredRowIds,
    }
    pendingMatrixViewportAnchorRef.current = pending
    return pending
  })
  const upsertRows = useEventCallback(async (...args: Parameters<typeof upsertRowsDirect>) => {
    const [targetChapterId, nextRows] = args
    const pending = targetChapterId === chapterId
      ? captureMatrixViewportContinuity(nextRows.map(row => row.id))
      : null
    try {
      return await upsertRowsDirect(...args)
    } catch (error) {
      if (pendingMatrixViewportAnchorRef.current === pending) {
        pendingMatrixViewportAnchorRef.current = null
      }
      throw error
    }
  })
  const replaceResolvedRows = useEventCallback(async (...args: Parameters<typeof replaceResolvedRowsDirect>) => {
    const targetChapterId = args[0]
    const nextRows = args[2]
    const pending = targetChapterId === chapterId
      ? captureMatrixViewportContinuity(nextRows.map(row => row.id))
      : null
    try {
      return await replaceResolvedRowsDirect(...args)
    } catch (error) {
      if (pendingMatrixViewportAnchorRef.current === pending) {
        pendingMatrixViewportAnchorRef.current = null
      }
      throw error
    }
  })
  useLayoutEffect(() => {
    const pending = pendingMatrixViewportAnchorRef.current
    const viewport = matrixViewportRef.current
    if (!pending || !viewport) {
      return
    }
    restoreLedgerSafeViewportAnchor({
      viewport,
      anchor: pending.anchor,
      rowElements: rowElementRefs.current,
      fallbackRowIds: pending.fallbackRowIds,
    })
    if (!isMatrixContentApplying) {
      pendingMatrixViewportAnchorRef.current = null
    }
  }, [inlineEditMode, isMatrixContentApplying, resolvedRows, selectedRange])
  const rowProjectionScopeKey = `${readOnly ? 'read' : 'edit'}:${chapterId}:${selectedScopeId}`
  const previousStudioRowProjectionRef = useRef<LedgerSafeStudioRowProjection | null>(null)
  const studioRowProjection = useMemo(
    () => reconcileLedgerSafeStudioRowProjection({
      previous: previousStudioRowProjectionRef.current,
      scopeKey: rowProjectionScopeKey,
      chapterId,
      rows: resolvedRows,
    }),
    [chapterId, resolvedRows, rowProjectionScopeKey]
  )
  useLayoutEffect(() => {
    previousStudioRowProjectionRef.current = studioRowProjection
  }, [studioRowProjection])
  const {
    rowProjection: {
      canonicalRowIndexById,
      structuralRowOrderById,
      orderedStructuralRows,
      rowById,
    },
    ledgerSceneHeaders,
    hasBuildImportRows,
    hasStagedImportFingerprint,
    sceneBackfillSourceRun,
    sceneBackfillMissingRowCount,
    enrollmentMappedWitnessIdSet,
    emptyLedgerRows,
    emptyLedgerRowRuns,
  } = studioRowProjection
  const structuralRangeLayer = useMemo(
    () => readOnlyStructuralRangeLayer
      || storedStructuralRangeLayer
      || createChapterStructuralRangeLayer({ chapterId, bookId }),
    [bookId, chapterId, readOnlyStructuralRangeLayer, storedStructuralRangeLayer]
  )
  const structuralRangeById = useMemo(
    () => new Map(structuralRangeLayer.ranges.map(range => [range.id, range])),
    [structuralRangeLayer]
  )
  const structuralDisplayNumbers = useMemo(
    () => buildStructuralRangeDisplayNumbers(structuralRangeLayer.ranges, buildStructuralRowOrder(effectiveChapterRows)),
    [structuralRangeLayer.ranges, effectiveChapterRows]
  )
  const structuralBoundaryRangeCountByRowId = useMemo(() => {
    const counts = new Map<string, number>()
    structuralRangeLayer.ranges
      .filter(range => range.status !== 'rejected')
      .forEach(range => {
        counts.set(range.startRowId, (counts.get(range.startRowId) || 0) + 1)
        if (range.endRowId !== range.startRowId) {
          counts.set(range.endRowId, (counts.get(range.endRowId) || 0) + 1)
        }
      })
    return counts
  }, [structuralRangeLayer.ranges])
  const structuralRangeDepthById = useMemo(() => {
    const depths = new Map<string, number>()
    const resolveDepth = (range: StructuralRange, visiting = new Set<string>()): number => {
      const cached = depths.get(range.id)
      if (cached !== undefined) return cached
      if (visiting.has(range.id)) return 0
      const nextVisiting = new Set(visiting).add(range.id)
      const parent = range.parentRangeId ? structuralRangeById.get(range.parentRangeId) : null
      const depth = parent ? resolveDepth(parent, nextVisiting) + 1 : 0
      depths.set(range.id, depth)
      return depth
    }
    structuralRangeLayer.ranges.forEach(range => resolveDepth(range))
    return depths
  }, [structuralRangeById, structuralRangeLayer.ranges])
  const structuralRangesByStartRowId = useMemo(() => {
    const byStart = new Map<string, StructuralRange[]>()
    structuralRangeLayer.ranges
      .filter(range => range.status !== 'rejected' && structuralRowOrderById.has(range.startRowId))
      .forEach(range => {
        const ranges = byStart.get(range.startRowId) || []
        ranges.push(range)
        byStart.set(range.startRowId, ranges)
      })
    byStart.forEach(ranges => ranges.sort((left, right) => (
      (structuralRowOrderById.get(right.endRowId) ?? -1)
      - (structuralRowOrderById.get(left.endRowId) ?? -1)
      || left.title.localeCompare(right.title)
    )))
    return byStart
  }, [structuralRangeLayer.ranges, structuralRowOrderById])
  const selectedStructuralRows = useMemo(
    () => {
      const byId = new Map(resolvedRows.map(row => [row.id, row]))
      return selectedStructuralRowIds
        .map(rowId => byId.get(rowId))
        .filter((row): row is AnchoredRow => Boolean(row))
    },
    [resolvedRows, selectedStructuralRowIds]
  )
  const structuralBoundaryPreviewRowIds = useMemo(() => {
    if (!structuralBoundaryPlacement || !structuralRangeEditor || !structuralBoundaryHoverRowId) {
      return new Set<string>()
    }
    const targetIndex = structuralRowOrderById.get(structuralBoundaryHoverRowId)
    const oppositeRowId = structuralBoundaryPlacement.edge === 'start'
      ? structuralRangeEditor.endRowId
      : structuralRangeEditor.startRowId
    const oppositeIndex = structuralRowOrderById.get(oppositeRowId)
    if (targetIndex === undefined || oppositeIndex === undefined) return new Set<string>()
    if (
      (structuralBoundaryPlacement.edge === 'start' && targetIndex > oppositeIndex)
      || (structuralBoundaryPlacement.edge === 'end' && targetIndex < oppositeIndex)
    ) {
      return new Set<string>()
    }
    return new Set(
      orderedStructuralRows
        .slice(Math.min(targetIndex, oppositeIndex), Math.max(targetIndex, oppositeIndex) + 1)
        .map(row => row.id)
    )
  }, [
    orderedStructuralRows,
    structuralBoundaryHoverRowId,
    structuralBoundaryPlacement,
    structuralRangeEditor,
    structuralRowOrderById,
  ])
  const legacySceneMigrationPreview = useMemo(
    () => previewLegacySceneStructuralRangeMigration({
      rows: resolvedRows,
      layer: structuralRangeLayer,
    }),
    [resolvedRows, structuralRangeLayer]
  )
  const sidecarStructuralRangePreview = useMemo(
    () => rowTitleSidecarPreview
      ? previewSidecarStructuralRangeMigration({
          preview: rowTitleSidecarPreview,
          layer: structuralRangeLayer,
        })
      : null,
    [rowTitleSidecarPreview, structuralRangeLayer]
  )
  const structuralRangeDiagnostics = useMemo(
    () => validateStructuralRangeLayer({
      layer: structuralRangeLayer,
      rowOrderById: structuralRowOrderById,
    }),
    [structuralRangeLayer, structuralRowOrderById]
  )
  const migratedLegacySceneIdSet = useMemo(
    () => new Set(structuralRangeLayer.ranges.flatMap(range => {
      if (range.provenance?.sourceKind === 'legacy-row-scene' && range.provenance.sourceRangeId) {
        return [range.provenance.sourceRangeId]
      }
      if (
        range.provenance?.sourceKind === 'html-sidecar'
        && range.provenance.sourceHash
        && range.provenance.sourceRangeId
      ) {
        const sceneOrder = range.provenance.sourceRangeId.split(':').pop()
        return sceneOrder
          ? [`html-sidecar:${range.provenance.sourceHash.slice(0, 12)}:${sceneOrder}`]
          : []
      }
      return []
    })),
    [structuralRangeLayer.ranges]
  )
  const ledgerSceneHeaderByRowId = ledgerSceneHeaders.byRowId
  const canAttachSceneMetadata = Boolean(
    chapterId
    && hasStagedImportFingerprint
    && sceneBackfillMissingRowCount > 0
  )
  const sceneBackfillReadinessText = useMemo(() => {
    if (!hasStagedImportFingerprint) {
      return 'Scene navigation needs a staged build import or source-run fingerprint on the rows.'
    }
    if (sceneBackfillMissingRowCount === 0) {
      return 'Scene navigation is already attached to the rows in this view.'
    }
    if (sceneBackfillSourceRun) {
      return `Scene source: ${sceneBackfillSourceRun}. ${sceneBackfillMissingRowCount} row${sceneBackfillMissingRowCount === 1 ? '' : 's'} missing scene navigation.`
    }
    return `Scene source: auto-detect from saved staged runs. ${sceneBackfillMissingRowCount} row${sceneBackfillMissingRowCount === 1 ? '' : 's'} missing scene navigation.`
  }, [hasStagedImportFingerprint, sceneBackfillMissingRowCount, sceneBackfillSourceRun])

  useEffect(() => {
    setSceneBackfillUndo(null)
    setLegacySceneRemovalUndo(null)
    legacySceneEditorialEventTailRef.current = Promise.resolve()
  }, [chapterId, snapshotScopeId])

  const enrollmentRepresentativeWitnesses = useMemo(() => {
    if (!fallbackContextPropagationAnchor) {
      return [] as Witness[]
    }
    return deriveLedgerRepresentativeWitnesses({
      rows: resolvedRows,
      scopeWitnesses,
      targetWitnessIds: [],
      fallbackWitness: fallbackContextPropagationAnchor,
    })
  }, [fallbackContextPropagationAnchor, resolvedRows, scopeWitnesses])
  const enrollmentQueue = useMemo((): EnrollmentQueueItem[] => {
    if (!fallbackContextPropagationAnchor || resolvedRows.length === 0) {
      return []
    }
    const representativeWitnessIds = new Set(enrollmentRepresentativeWitnesses.map(witness => witness.id))
    const targetWitnesses = scopeWitnesses.filter(witness => (
      Boolean(witness.uploaded_file?.content?.trim())
      && witness.id !== fallbackContextPropagationAnchor.id
    ))
    return rankEnrollmentTargetsByRepresentativeDistance({
      targets: targetWitnesses,
      representatives: enrollmentRepresentativeWitnesses,
    }).map((item, index) => ({
      ...item,
      rank: index + 1,
      status: (
        representativeWitnessIds.has(item.witness.id) ||
        enrollmentMappedWitnessIdSet.has(item.witness.id) ||
        hasManualOrUnknownSegmentation(item.witness.segmentation_result)
      ) ? 'rerun' : 'pending',
    }))
  }, [enrollmentMappedWitnessIdSet, enrollmentRepresentativeWitnesses, fallbackContextPropagationAnchor, resolvedRows.length, scopeWitnesses])
  const selectableEnrollmentQueueItems = useMemo(
    () => enrollmentQueue.filter(item => item.status === 'pending' || item.status === 'rerun'),
    [enrollmentQueue]
  )
  const selectableEnrollmentQueueWitnessIds = useMemo(
    () => selectableEnrollmentQueueItems.map(item => item.witness.id),
    [selectableEnrollmentQueueItems]
  )
  const selectableEnrollmentQueueWitnessIdSet = useMemo(
    () => new Set(selectableEnrollmentQueueWitnessIds),
    [selectableEnrollmentQueueWitnessIds]
  )
  const enrollmentQueueEmptyReason = useMemo(() => {
    if (resolvedRows.length === 0) {
      return 'Load or accept Ledger Safe rows before enrolling witnesses.'
    }
    if (!fallbackContextPropagationAnchor) {
      return 'No local fallback anchor is available for this scope.'
    }
    const candidateCount = scopeWitnesses.filter(witness => (
      Boolean(witness.uploaded_file?.content?.trim())
      && witness.id !== fallbackContextPropagationAnchor.id
    )).length
    if (candidateCount === 0) {
      return 'No other witnesses in this scope have source text to enroll or rerun.'
    }
    return 'No runnable witnesses are available in this scope.'
  }, [fallbackContextPropagationAnchor, resolvedRows.length, scopeWitnesses])
  useEffect(() => {
    if (propagationMode !== 'context' || contextPropagationPreview) {
      return
    }
    setSelectedContextPropagationTargetWitnessIds(previous => {
      const retained = previous.filter(witnessId => selectableEnrollmentQueueWitnessIdSet.has(witnessId))
      if (retained.length > 0) {
        return retained
      }
      return selectableEnrollmentQueueWitnessIds[0] ? [selectableEnrollmentQueueWitnessIds[0]] : []
    })
  }, [
    contextPropagationPreview,
    propagationMode,
    selectableEnrollmentQueueWitnessIds,
    selectableEnrollmentQueueWitnessIdSet,
  ])
  const activeWitnessProjectionAssignments = useMemo(() => {
    if (!activeWitness || resolvedRows.length === 0) {
      return [] as WorkbenchPropagationRowAssignment[]
    }
    if (witnessPropagationProposal?.targetWitnessId === activeWitness.id) {
      return witnessPropagationProposal.rowAssignments
    }
    return buildAppliedWitnessRowAssignments({
      rows: resolvedRows,
      witness: activeWitness,
    })
  }, [activeWitness, resolvedRows, witnessPropagationProposal])
  const transpositionDisplayModel = useMemo(
    () => buildLedgerSafeTranspositionDisplayModel({
      rows: resolvedRows,
      rowAssignments: activeWitnessProjectionAssignments,
    }),
    [activeWitnessProjectionAssignments, resolvedRows]
  )
  const matrixCellBehaviorActiveWitnessId = (
    inlineEditMode !== 'idle'
    || sidePanelTab === 'sequence'
    || Boolean(transpositionTarget)
    || transpositionDisplayModel.projectedSourceRowIds.size > 0
  )
    ? activeWitness?.id || null
    : null
  // A transposition is a relation between distinct source and target loci.
  // The source row remains visible in target mode but is never a valid target.
  const transpositionEligibleTargetRowIds = useMemo(() => {
    if (!transpositionTarget) {
      return new Set<string>()
    }
    const eligible = new Set<string>()
    resolvedRows.forEach(row => {
      if (row.id === transpositionTarget.originRowId) {
        return
      }
      const hasActiveMapping = row.unitMappings.some(mapping =>
        mapping.witnessId === transpositionTarget.witnessId && !mapping.excluded
      )
      if (!hasActiveMapping) {
        eligible.add(row.id)
      }
    })
    return eligible
  }, [resolvedRows, transpositionTarget])
  const relocationSourceRowIdSet = useMemo(
    () => new Set(relocationTarget?.sourceRowIds || []),
    [relocationTarget]
  )
  const relocationDestinationByStartRowId = useMemo(() => {
    const destinations = new Map<string, LedgerSafeRelocationDestination>()
    if (!relocationTarget || relocationTarget.sourceRowIds.length === 0) {
      return destinations
    }
    const orderedRows = [...resolvedRows].sort((left, right) => {
      if (left.position !== right.position) {
        return left.position - right.position
      }
      return left.labelNumber - right.labelNumber
    })
    const rangeLength = relocationTarget.sourceRowIds.length
    const sourceStartRowId = relocationTarget.sourceRowIds[0]
    orderedRows.forEach((row, startIndex) => {
      const targetRows = orderedRows.slice(startIndex, startIndex + rangeLength)
      const overflow = targetRows.length !== rangeLength
      const sameLocation = row.id === sourceStartRowId
      const conflictRowIds = overflow
        ? [row.id]
        : targetRows
            .filter(targetRow => targetRow.unitMappings.some(mapping => (
              mapping.witnessId === relocationTarget.witnessId
              && !mapping.excluded
              && !relocationSourceRowIdSet.has(targetRow.id)
            )))
            .map(targetRow => targetRow.id)
      const reason = overflow
        ? 'The selected range would extend past the final chapter row.'
        : sameLocation
          ? 'The selected cells are already in this location.'
          : conflictRowIds.length > 0
            ? `${conflictRowIds.length} destination row${conflictRowIds.length === 1 ? '' : 's'} already contain mapped units for this witness.`
            : null
      destinations.set(row.id, {
        startRowId: row.id,
        targetRowIds: targetRows.map(targetRow => targetRow.id),
        eligible: !reason,
        sameLocation,
        conflictRowIds,
        reason,
      })
    })
    return destinations
  }, [relocationSourceRowIdSet, relocationTarget, resolvedRows])
  const relocationHoverDestination = useMemo(
    () => relocationHoverTargetRowId
      ? (relocationDestinationByStartRowId.get(relocationHoverTargetRowId) || null)
      : null,
    [relocationDestinationByStartRowId, relocationHoverTargetRowId]
  )
  const relocationPreviewTargetRowIdSet = useMemo(
    () => new Set(relocationHoverDestination?.targetRowIds || []),
    [relocationHoverDestination]
  )
  const relocationPreviewConflictRowIdSet = useMemo(
    () => new Set(relocationHoverDestination?.conflictRowIds || []),
    [relocationHoverDestination]
  )

  const persistWitnessPropagationResume = useEventCallback(async ({
    witness,
    resumeState,
    failure = null,
  }: {
    witness: Witness
    resumeState: TranslationBatchResumeState
    failure?: WorkbenchPropagationFailure | null
  }) => {
    if (!chapterId) {
      return
    }
    const latestDiagnostics = failure?.diagnostics || resumeState.batchDiagnostics[resumeState.batchDiagnostics.length - 1]
    const generationBatchSize = latestDiagnostics?.generationConfig?.batchSize
    await dbStorage.saveTranslationPreviewResume(buildTranslationPreviewResumeRecord({
      id: buildLedgerSafeTranslationPreviewResumeId(chapterId, witness.id, selectedScopeId || null),
      surface: 'ledger-safe',
      contextId: chapterId,
      witnessId: witness.id,
      scopeId: selectedScopeId || null,
      rowIds: resolvedRows.map(row => row.id),
      witnessText: witness.uploaded_file?.content || '',
      resumeState,
      failure,
      modelId: latestDiagnostics?.modelId || null,
      batchSize: typeof generationBatchSize === 'number' ? generationBatchSize : null,
    }))
  })

  const clearWitnessPropagationResumeRecord = useEventCallback(async (witnessId: string, scopeId: string | null = selectedScopeId || null) => {
    if (!chapterId) {
      return
    }
    await dbStorage.deleteTranslationPreviewResume(buildLedgerSafeTranslationPreviewResumeId(chapterId, witnessId, scopeId))
  })

  const persistWitnessPropagationResumeSafely = useEventCallback(async (input: {
    witness: Witness
    resumeState: TranslationBatchResumeState
    failure?: WorkbenchPropagationFailure | null
  }) => {
    try {
      await persistWitnessPropagationResume(input)
    } catch (error) {
      console.warn('Failed to persist Variorum translation preview state.', error)
    }
  })

  const clearWitnessPropagationResumeRecordSafely = useEventCallback(async (witnessId: string, scopeId: string | null = selectedScopeId || null) => {
    try {
      await clearWitnessPropagationResumeRecord(witnessId, scopeId)
    } catch (error) {
      console.warn('Failed to clear Variorum translation preview state.', error)
    }
  })

  useEffect(() => {
    if (!chapterId || !activeWitness || resolvedRows.length === 0) {
      return
    }
    const hydrationKey = `${chapterId}:${selectedScopeId || 'aggregate'}:${activeWitness.id}`
    if (witnessPropagationResumeHydrationRef.current === hydrationKey) {
      return
    }
    if (witnessPropagationProposal || witnessPropagationResumeState) {
      witnessPropagationResumeHydrationRef.current = hydrationKey
      return
    }

    let cancelled = false
    witnessPropagationResumeHydrationRef.current = hydrationKey

    void (async () => {
      try {
        const record = await dbStorage.getTranslationPreviewResume(
          buildLedgerSafeTranslationPreviewResumeId(chapterId, activeWitness.id, selectedScopeId || null)
        )
        if (!record) {
          return
        }
        if (!isTranslationPreviewResumeRecordCurrent({
          record,
          contextId: chapterId,
          witnessId: activeWitness.id,
          scopeId: selectedScopeId || null,
          rowIds: resolvedRows.map(row => row.id),
          witnessText: activeWitness.uploaded_file?.content || '',
        })) {
          await dbStorage.deleteTranslationPreviewResume(record.id)
          return
        }

        const derivedPlan = deriveTranslationPropagationPlan({
          text: activeWitness.uploaded_file?.content || '',
          charAssignments: record.resumeState.charAssignments,
        })
        const coverageSummary = summarizeTranslationFragmentDiagnostics(derivedPlan.fragmentDiagnostics)
        const referenceWitness = (
          (diffPivotWitness && diffPivotWitness.id !== activeWitness.id ? diffPivotWitness : null)
          || scopeWitnesses.find(witness => witness.id !== activeWitness.id)
          || activeWitness
        )
        const application = buildWitnessScopedPropagationApplication({
          proposal: {
            targetWitness: activeWitness,
            referenceWitness,
            splitIndices: derivedPlan.splitIndices,
            rowAssignments: derivedPlan.rowAssignments,
          },
          contextId: chapterId,
          rows: resolvedRows,
        })

        if (cancelled) {
          return
        }

        setWitnessPropagationResumeState(record.resumeState)
        setWitnessPropagationFailures(record.failure ? [record.failure] : [])
        setWitnessPropagationProposal({
          targetWitnessId: activeWitness.id,
          method: 'gemini-translation-alignment',
          segmentCount: derivedPlan.splitIndices.length + 1,
          splitIndices: derivedPlan.splitIndices,
          exemplarWitnessIds: [],
          rowAssignments: derivedPlan.rowAssignments,
          application,
          notes: mergeTranslationPlanNotes(
            describeTranslationResumeState({ resumeState: record.resumeState, failure: record.failure }),
            summarizeTranslationBatchRetryHistory(record.resumeState.retryHistory),
            coverageSummary,
          ),
          translationProgress: {
            completedBatches: getCompletedTranslationBatchCount(record.resumeState),
            totalBatches: record.resumeState.totalBatches,
            complete: false,
          },
          diagnostics: record.failure?.diagnostics || combineTranslationAlignmentDiagnostics(record.resumeState.batchDiagnostics),
        })
        setWitnessPropagationError(record.failure?.error || null)
        setWitnessPropagationSuccess(
          record.failure
            ? null
            : describeTranslationResumeState({ resumeState: record.resumeState, failure: record.failure })
        )
      } catch (error) {
        console.warn('Failed to restore persisted Variorum translation preview state.', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [activeWitness, chapterId, diffPivotWitness, resolvedRows, scopeWitnesses, selectedScopeId, witnessPropagationProposal, witnessPropagationResumeState])

  const witnessPropagationPreviewCellsByKey = useMemo(() => {
    if (!witnessPropagationProposal) {
      return new Map<string, {
        text: string
        metaText: string
        state: LedgerSafeCellState
        translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
        outsideLocalContext?: boolean
      }>()
    }

    const previewRowsById = new Map(
      witnessPropagationProposal.application.rowUpdates.map(row => [row.id, row])
    )
    const translationStatusByRowId = new Map(
      witnessPropagationProposal.rowAssignments.map(assignment => [assignment.rowId, assignment.translationStatus])
    )
    const nextPreviewCells = new Map<string, {
      text: string
      metaText: string
      state: LedgerSafeCellState
      translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
    }>()

    resolvedRows.forEach(row => {
      const previewRow = previewRowsById.get(row.id)
      const translationStatus = translationStatusByRowId.get(row.id)
      if (!previewRow) {
        if (translationStatus !== 'transposed') {
          return
        }
      }
      const currentText = getWorkspaceMappingText(row, witnessPropagationProposal.targetWitnessId, witnessUnitTextLookupByWitnessId)
      const previewText = previewRow
        ? (getSnapshotMappingText(previewRow, witnessPropagationProposal.targetWitnessId)?.workspaceDisplayText || '')
        : currentText
      const currentMetaText = getCellMetaText(row, witnessPropagationProposal.targetWitnessId)
      const previewMetaText = previewRow ? getCellMetaText(previewRow, witnessPropagationProposal.targetWitnessId) : currentMetaText
      const currentState = getLedgerSafeCellState(row, witnessPropagationProposal.targetWitnessId)
      const previewState = previewRow ? getLedgerSafeCellState(previewRow, witnessPropagationProposal.targetWitnessId) : currentState
      if (
        currentText === previewText &&
        currentMetaText === previewMetaText &&
        currentState === previewState &&
        translationStatus !== 'transposed'
      ) {
        return
      }
      nextPreviewCells.set(`${row.id}:${witnessPropagationProposal.targetWitnessId}`, {
        text: previewText,
        metaText: previewMetaText,
        state: previewState,
        ...(translationStatus ? { translationStatus } : {}),
      })
    })

    return nextPreviewCells
  }, [resolvedRows, witnessPropagationProposal, witnessUnitTextLookupByWitnessId])

  const selectedUnitRepairApplication = useMemo(() => {
    if (!chapterId || !selectedUnitRepairPreview) {
      return null
    }
    return buildSelectedUnitPropagationRepairApplication({
      preview: selectedUnitRepairPreview,
      contextId: chapterId,
      rows: resolvedRows,
    })
  }, [chapterId, resolvedRows, selectedUnitRepairPreview])

  const selectedUnitRepairPreviewCellsByKey = useMemo(() => {
    if (!selectedUnitRepairPreview || !selectedUnitRepairApplication) {
      return new Map<string, {
        text: string
        metaText: string
        state: LedgerSafeCellState
        translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
        outsideLocalContext?: boolean
      }>()
    }

    const targetWitnessId = selectedUnitRepairPreview.targetWitness.id
    const previewRowsById = new Map(
      selectedUnitRepairApplication.rowUpdates.map(row => [row.id, row])
    )
    const outOfScopeAssignmentRowIdSet = new Set(selectedUnitRepairApplication.outOfScopeAssignmentRowIds || [])
    const outsideLocalContextAssignmentRowIdSet = new Set(
      selectedUnitRepairApplication.outsideLocalContextAssignmentRowIds || []
    )
    const translationStatusByRowId = new Map(
      selectedUnitRepairPreview.rowAssignments
        .filter(assignment => !outOfScopeAssignmentRowIdSet.has(assignment.rowId))
        .map(assignment => [assignment.rowId, assignment.translationStatus])
    )
    const nextPreviewCells = new Map<string, {
      text: string
      metaText: string
      state: LedgerSafeCellState
      translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
      outsideLocalContext?: boolean
    }>()

    resolvedRows.forEach(row => {
      const previewRow = previewRowsById.get(row.id)
      const translationStatus = translationStatusByRowId.get(row.id)
      if (!previewRow && translationStatus !== 'transposed') {
        return
      }
      const currentText = getWorkspaceMappingText(row, targetWitnessId, witnessUnitTextLookupByWitnessId)
      const previewText = previewRow
        ? (getSnapshotMappingText(previewRow, targetWitnessId)?.workspaceDisplayText || '')
        : currentText
      const currentMetaText = getCellMetaText(row, targetWitnessId)
      const previewMetaText = previewRow ? getCellMetaText(previewRow, targetWitnessId) : currentMetaText
      const currentState = getLedgerSafeCellState(row, targetWitnessId)
      const previewState = previewRow ? getLedgerSafeCellState(previewRow, targetWitnessId) : currentState
      if (
        currentText === previewText &&
        currentMetaText === previewMetaText &&
        currentState === previewState &&
        translationStatus !== 'transposed'
      ) {
        return
      }
      nextPreviewCells.set(`${row.id}:${targetWitnessId}`, {
        text: previewText,
        metaText: previewMetaText,
        state: previewState,
        ...(translationStatus ? { translationStatus } : {}),
        ...(outsideLocalContextAssignmentRowIdSet.has(row.id) ? { outsideLocalContext: true } : {}),
      })
    })

    return nextPreviewCells
  }, [
    resolvedRows,
    selectedUnitRepairApplication,
    selectedUnitRepairPreview,
    witnessUnitTextLookupByWitnessId,
  ])

  const semanticPreviewCandidatesByCellKey = useMemo(() => {
    const nextCandidates = new Map<string, WorkbenchSemanticAlignmentCandidate>()
    if (!semanticPreview) {
      return nextCandidates
    }

    semanticPreview.candidates.forEach(candidate => {
      const sourceRow = rowById.get(candidate.sourceRowId)
      if (!sourceRow) {
        return
      }
      if (getLedgerSafeCellState(sourceRow, candidate.targetWitnessId) === 'mapped') {
        return
      }
      const key = `${candidate.sourceRowId}:${candidate.targetWitnessId}`
      const existing = nextCandidates.get(key)
      if (!existing || candidate.score > existing.score) {
        nextCandidates.set(key, candidate)
      }
    })

    return nextCandidates
  }, [rowById, semanticPreview])

  const selectedSemanticCandidateFromId = useMemo(() => {
    if (!semanticPreview || !selectedSemanticCandidateId) {
      return null
    }
    return semanticPreview.candidates.find(candidate => candidate.candidateId === selectedSemanticCandidateId) || null
  }, [selectedSemanticCandidateId, semanticPreview])

  const semanticPreviewCellsByKey = useMemo(() => {
    const nextPreviewCells = new Map<string, {
      text: string
      metaText: string
      state: LedgerSafeCellState
      translationStatus?: WorkbenchPropagationRowAssignment['translationStatus']
      candidate: WorkbenchSemanticAlignmentCandidate
    }>()
    const selectedCandidateKey = selectedSemanticCandidateFromId
      ? `${selectedSemanticCandidateFromId.sourceRowId}:${selectedSemanticCandidateFromId.targetWitnessId}`
      : null

    semanticPreviewCandidatesByCellKey.forEach((candidate, key) => {
      const displayCandidate = selectedCandidateKey === key && selectedSemanticCandidateFromId
        ? selectedSemanticCandidateFromId
        : candidate
      nextPreviewCells.set(key, {
        text: displayCandidate.targetText,
        metaText: `${Math.round(displayCandidate.score * 100)}% match`,
        state: 'mapped',
        candidate: displayCandidate
      })
    })

    return nextPreviewCells
  }, [selectedSemanticCandidateFromId, semanticPreviewCandidatesByCellKey])

  const selectedSemanticCandidate = useMemo(() => {
    if (
      selectedCell &&
      selectedSemanticCandidateFromId &&
      selectedSemanticCandidateFromId.sourceRowId === selectedCell.rowId &&
      selectedSemanticCandidateFromId.targetWitnessId === selectedCell.witnessId
    ) {
      return selectedSemanticCandidateFromId
    }
    if (!selectedCell) {
      return null
    }
    return semanticPreviewCandidatesByCellKey.get(`${selectedCell.rowId}:${selectedCell.witnessId}`) || null
  }, [selectedCell, selectedSemanticCandidateFromId, semanticPreviewCandidatesByCellKey])

  const emptyLedgerRowRunByHeadRowId = useMemo(() => {
    const runs = new Map<string, LedgerSafeEmptyRowRun>()
    emptyLedgerRowRuns.forEach(run => {
      const headRowId = run.rowIds[0]
      if (headRowId) {
        runs.set(headRowId, run)
      }
    })
    return runs
  }, [emptyLedgerRowRuns])
  const multiEmptyLedgerRunMemberRowIds = useMemo(() => {
    const rowIds = new Set<string>()
    emptyLedgerRowRuns.forEach(run => {
      if (run.rowCount > 1) {
        run.rowIds.forEach(rowId => rowIds.add(rowId))
      }
    })
    return rowIds
  }, [emptyLedgerRowRuns])
  const visibleFragmentAnchorRowIdByRowId = useMemo(() => {
    const rowIds = new Map<string, string>()
    emptyLedgerRowRuns.forEach(run => {
      const headRowId = run.rowIds[0]
      if (!headRowId) {
        return
      }
      run.rowIds.forEach(rowId => rowIds.set(rowId, headRowId))
    })
    return rowIds
  }, [emptyLedgerRowRuns])
  const latestLedgerSnapshot = ledgerSnapshots[0] || null

  const invalidatePiSnapshot = useCallback(() => {
    piOperationGateRef.current.begin()
    const activeInquiry = activeCameraInquiryRef.current
    if (!activeInquiry) {
      piSnapshotCancellationRef.current = Promise.resolve()
      setIsPiBusy(false)
      return
    }
    setIsPiBusy(true)
    const cancellation = abortPiMatrixAgentAgent({ sessionId: activeInquiry.sessionId })
      .then(() => undefined)
      .catch(error => {
        setPiError(error instanceof Error ? error.message : 'Pi inquiry cancellation failed')
      })
    piSnapshotCancellationRef.current = cancellation
    void cancellation.finally(() => {
      if (piSnapshotCancellationRef.current === cancellation) setIsPiBusy(false)
    })
  }, [])
  const piAuthorityRevisionId = piAuthorityHead?.authorityRevisionId
  const piAuthorityContentHash = piAuthorityHead?.contentHash
  const piSnapshotSource = useMemo(() => ({
    chapterId,
    chapterLabel: `Variorum · ${describeManualStudioScope(activeScope) || chapterId || 'chapter'}`,
    rows: resolvedRows,
    witnesses: scopeWitnesses,
    diffEnabled,
    diffPivotWitnessId: diffPivotWitness?.id,
    characterIndexEnabled: runtimeEnableCharacterIndex,
    characterLayer: resolvedCharacterLayer,
    structuralRangeLayer,
  }), [
    activeScope,
    chapterId,
    diffEnabled,
    diffPivotWitness?.id,
    resolvedCharacterLayer,
    resolvedRows,
    runtimeEnableCharacterIndex,
    scopeWitnesses,
    structuralRangeLayer,
  ])
  const piSnapshotAuthority = useMemo(
    () => piAuthorityRevisionId && piAuthorityContentHash
      ? {
          authorityRevisionId: piAuthorityRevisionId,
          contentHash: piAuthorityContentHash,
        }
      : undefined,
    [piAuthorityContentHash, piAuthorityRevisionId],
  )
  const {
    snapshot: piContentSnapshot,
    pending: piSnapshotPending,
    getCurrentSnapshot: getCurrentPiContentSnapshot,
  } = useScheduledPiMatrixAgentSnapshot({
    source: piSnapshotSource,
    authority: piSnapshotAuthority,
    onSourceInvalidated: invalidatePiSnapshot,
  })
  const piSnapshot = useMemo(() => ({
    ...piContentSnapshot,
    selection: selectedCell
      ? {
          witnessId: selectedCell.witnessId,
          rowId: selectedCell.rowId,
        }
      : undefined,
  }), [piContentSnapshot, selectedCell])
  const latestPiSnapshotRef = useRef(piSnapshot)
  const latestPiSelectionRef = useRef(selectedCell)
  useLayoutEffect(() => {
    latestPiSnapshotRef.current = piSnapshot
    latestPiSelectionRef.current = selectedCell
  }, [piSnapshot, selectedCell])

  const getCurrentPiSnapshot = useCallback(async () => {
    const contentSnapshot = await getCurrentPiContentSnapshot()
    const selection = latestPiSelectionRef.current
    const currentSnapshot = {
      ...contentSnapshot,
      selection: selection
        ? { witnessId: selection.witnessId, rowId: selection.rowId }
        : undefined,
    }
    latestPiSnapshotRef.current = currentSnapshot
    return currentSnapshot
  }, [getCurrentPiContentSnapshot])

  useEffect(() => {
    if (readOnly || !chapterId) {
      setPiAgentChangeSet(null)
      setPiAuthorityHead(null)
      setPiAuthorityHydratedChapterId(chapterId)
      return
    }
    let cancelled = false
    setPiAuthorityHydratedChapterId('')
    void Promise.all([
      getLedgerSafeAuthorityHead(chapterId),
      listLedgerSafeAgentChangeSets(chapterId),
    ]).then(([head, changeSets]) => {
      if (cancelled) return
      const resumable = changeSets.find(changeSet => changeSet.status === 'awaiting_approval')
        || changeSets.find(changeSet => changeSet.status === 'applied')
        || null
      setPiAuthorityHead(head || null)
      setPiAgentChangeSet(resumable)
      if (resumable?.status === 'awaiting_approval') {
        const presentation = buildPiMatrixAgentChangeSetPreview(resumable)
        setPiPreview(presentation.preview)
        setPiPreviewEnvelope(presentation.envelope)
        setPiPreviewSummary(resumable.steps.length === 1
          ? resumable.steps[0].summary
          : presentation.preview.summary || '')
      }
      setPiAuthorityHydratedChapterId(chapterId)
    }).catch(error => {
      if (!cancelled) {
        console.error('Failed to restore the Ledger Safe agent review state', error)
        setPiAuthorityHydratedChapterId(chapterId)
      }
    })
    return () => {
      cancelled = true
    }
  }, [chapterId, readOnly])

  useEffect(() => {
    if (
      readOnly
      || !chapterId
      || piAuthorityHydratedChapterId !== chapterId
      || piSnapshotPending
    ) return
    let cancelled = false
    void synchronizeLedgerSafeAuthorityHead({
      chapterId,
      bookId,
      snapshot: piContentSnapshot,
      expectedAuthority: piAuthorityHead,
    }).then(head => {
      if (!cancelled) setPiAuthorityHead(head)
    }).catch(error => {
      if (!cancelled) console.error('Failed to synchronize the Ledger Safe authority head', error)
    })
    return () => {
      cancelled = true
    }
  }, [bookId, chapterId, piAuthorityHead, piAuthorityHydratedChapterId, piContentSnapshot, piSnapshotPending, readOnly])

  const previousActiveWitnessProjectionRef = useRef<LedgerSafeActiveWitnessProjectionState | null>(null)
  const activeWitnessProjectionState = useMemo(
    () => reconcileLedgerSafeActiveWitnessProjection({
      previous: previousActiveWitnessProjectionRef.current,
      rowProjection: studioRowProjection.rowProjection,
      witness: activeWitness,
      readOnly,
      observer: activeWitnessProjectionObserver
    }),
    [activeWitness, readOnly, studioRowProjection.rowProjection]
  )
  useLayoutEffect(() => {
    previousActiveWitnessProjectionRef.current = activeWitnessProjectionState
  }, [activeWitnessProjectionState])
  const {
    preparedContext: preparedWitnessContext,
    rawFragments: derivedUnassignedFragments,
    sequenceAudit: activeWitnessSequenceAudit,
    editabilityAudit: activeWitnessEditabilityAudit
  } = activeWitnessProjectionState.projection
  const activeFragmentAnchorDrafts = useMemo(
    () => fragmentAnchorDrafts.filter(anchor => anchor.witnessId === activeWitness?.id),
    [activeWitness?.id, fragmentAnchorDrafts]
  )
  const automaticallyAnchoredFragments = useMemo(
    () => inferLedgerSafeFragmentAnchorsFromSequenceAudit({
      fragments: derivedUnassignedFragments,
      sequenceAudit: activeWitnessSequenceAudit,
      rows: resolvedRows
    }),
    [activeWitnessSequenceAudit, derivedUnassignedFragments, resolvedRows]
  )
  const fragmentAnchorResolution = useMemo(() => resolveLedgerSafeFragmentAnchors({
    fragments: automaticallyAnchoredFragments,
    anchors: activeFragmentAnchorDrafts,
    rows: resolvedRows
  }), [activeFragmentAnchorDrafts, automaticallyAnchoredFragments, resolvedRows])
  const unassignedFragments = fragmentAnchorResolution.fragments
  const unassignedFragmentByUnitUuid = useMemo(
    () => new Map(unassignedFragments.map(fragment => [fragment.unitUuid, fragment])),
    [unassignedFragments]
  )
  const invalidFragmentAnchorKeySignature = fragmentAnchorResolution.invalidAnchorKeys.join('|')
  useEffect(() => {
    if (
      !activeWitness
      || fragmentAnchorHydratedContextId !== chapterId
      || fragmentAnchorResolution.invalidAnchorKeys.length === 0
    ) {
      return
    }
    const nextDrafts = removeLedgerSafeFragmentAnchors(
      fragmentAnchorDrafts,
      fragmentAnchorResolution.invalidAnchorKeys
    )
    if (nextDrafts.length === fragmentAnchorDrafts.length) {
      return
    }
    void persistFragmentAnchorDrafts(nextDrafts).catch(error => {
      console.error('Failed to discard stale fragment anchors', error)
    })
  }, [
    activeWitness,
    chapterId,
    fragmentAnchorDrafts,
    fragmentAnchorHydratedContextId,
    fragmentAnchorResolution.invalidAnchorKeys,
    invalidFragmentAnchorKeySignature,
    persistFragmentAnchorDrafts
  ])
  const retiredMaterialMappingKeySet = useMemo(
    () => new Set(retiredMaterialAudit.issues.map(issue => `${issue.rowId}:${issue.witnessId}:${issue.unitUuid}`)),
    [retiredMaterialAudit.issues]
  )
  const visibleActiveWitnessStaleMappings = useMemo(
    () => (activeWitnessSequenceAudit?.staleMappings || []).filter(mapping => !retiredMaterialMappingKeySet.has(
      `${mapping.rowId}:${activeWitness?.id || ''}:${mapping.unitUuid}`
    )),
    [activeWitness?.id, activeWitnessSequenceAudit?.staleMappings, retiredMaterialMappingKeySet]
  )
  const sequenceAuditIssueCount = (
    activeWitnessSequenceAudit
      ? activeWitnessSequenceAudit.offenders.length
        + visibleActiveWitnessStaleMappings.length
        + activeWitnessSequenceAudit.unplacedUnits.length
      : 0
  ) + retiredMaterialAudit.unresolvedCount
  const inlineFragmentsAfterRowId = useMemo(() => {
    const grouped = new Map<string, LedgerSafeUnassignedFragment[]>()
    unassignedFragments.forEach(fragment => {
      if (!fragment.previousRowId) {
        return
      }
      const anchorRowId = visibleFragmentAnchorRowIdByRowId.get(fragment.previousRowId) || fragment.previousRowId
      grouped.set(anchorRowId, [...(grouped.get(anchorRowId) || []), fragment])
    })
    return grouped
  }, [unassignedFragments, visibleFragmentAnchorRowIdByRowId])
  const inlineFragmentsBeforeRowId = useMemo(() => {
    const grouped = new Map<string, LedgerSafeUnassignedFragment[]>()
    unassignedFragments.forEach(fragment => {
      if (fragment.previousRowId || !fragment.nextRowId) {
        return
      }
      const anchorRowId = visibleFragmentAnchorRowIdByRowId.get(fragment.nextRowId) || fragment.nextRowId
      grouped.set(anchorRowId, [...(grouped.get(anchorRowId) || []), fragment])
    })
    return grouped
  }, [unassignedFragments, visibleFragmentAnchorRowIdByRowId])
  const fragmentReviewTargetRowIds = useMemo(() => {
    const rowIds = new Set<string>()
    unassignedFragments.forEach(fragment => {
      if (fragment.previousRowId) {
        rowIds.add(fragment.previousRowId)
      }
      if (fragment.nextRowId) {
        rowIds.add(fragment.nextRowId)
      }
    })
    recentFragmentReviewRowIds.forEach(rowId => rowIds.add(rowId))
    witnessPropagationProposal?.application.changedRowIds.forEach(rowId => rowIds.add(rowId))
    return rowIds
  }, [recentFragmentReviewRowIds, unassignedFragments, witnessPropagationProposal])
  const fragmentReviewVisibleRowIds = useMemo(() => {
    if (!isFragmentReviewMode || fragmentReviewTargetRowIds.size === 0) {
      return null
    }
    const visibleRowIds = new Set<string>()
    fragmentReviewTargetRowIds.forEach(rowId => {
      const rowIndex = canonicalRowIndexById.get(rowId)
      if (rowIndex === undefined) {
        return
      }
      const startIndex = Math.max(0, rowIndex - LEDGER_SAFE_FRAGMENT_REVIEW_CONTEXT_RADIUS)
      const endIndex = Math.min(resolvedRows.length - 1, rowIndex + LEDGER_SAFE_FRAGMENT_REVIEW_CONTEXT_RADIUS)
      for (let index = startIndex; index <= endIndex; index += 1) {
        const contextRowId = resolvedRows[index]?.id
        if (contextRowId) {
          visibleRowIds.add(contextRowId)
        }
      }
    })
    return visibleRowIds
  }, [canonicalRowIndexById, fragmentReviewTargetRowIds, isFragmentReviewMode, resolvedRows])
  const retiredMaterialReviewVisibleRowIds = useMemo(() => {
    if (!isRetiredMaterialReviewMode || retiredMaterialAudit.affectedRowIds.size === 0) {
      return null
    }
    const visibleRowIds = new Set<string>()
    retiredMaterialAudit.affectedRowIds.forEach(rowId => {
      const rowIndex = canonicalRowIndexById.get(rowId)
      if (rowIndex === undefined) return
      const startIndex = Math.max(0, rowIndex - LEDGER_SAFE_FRAGMENT_REVIEW_CONTEXT_RADIUS)
      const endIndex = Math.min(resolvedRows.length - 1, rowIndex + LEDGER_SAFE_FRAGMENT_REVIEW_CONTEXT_RADIUS)
      for (let index = startIndex; index <= endIndex; index += 1) {
        const contextRowId = resolvedRows[index]?.id
        if (contextRowId) visibleRowIds.add(contextRowId)
      }
    })
    return visibleRowIds
  }, [
    canonicalRowIndexById,
    isRetiredMaterialReviewMode,
    resolvedRows,
    retiredMaterialAudit.affectedRowIds,
  ])
  const focusedReviewVisibleRowIds = useMemo(() => {
    if (!fragmentReviewVisibleRowIds && !retiredMaterialReviewVisibleRowIds) return null
    return new Set([
      ...Array.from(fragmentReviewVisibleRowIds || []),
      ...Array.from(retiredMaterialReviewVisibleRowIds || []),
    ])
  }, [fragmentReviewVisibleRowIds, retiredMaterialReviewVisibleRowIds])
  const isReadOnlyAppliedAuditActive = Boolean(
    witnessPropagationProposal?.readOnly
    && activeWitness
    && witnessPropagationProposal.targetWitnessId === activeWitness.id
  )

  useEffect(() => {
    if (isFragmentReviewMode && fragmentReviewTargetRowIds.size === 0) {
      setIsFragmentReviewMode(false)
    }
  }, [fragmentReviewTargetRowIds.size, isFragmentReviewMode])

  useEffect(() => {
    if (isRetiredMaterialReviewMode && retiredMaterialAudit.affectedRowIds.size === 0) {
      setIsRetiredMaterialReviewMode(false)
    }
  }, [isRetiredMaterialReviewMode, retiredMaterialAudit.affectedRowIds.size])

  useEffect(() => {
    if (!readOnly && piDriftWindows.length > 0) {
      setSidePanelTab('inspector')
    }
  }, [piDriftWindows.length, readOnly])

  useEffect(() => {
    if (piOAuthLoginAttempt || piAgentReply || piPreview || piAgentChangeSet?.status === 'applied' || piError) {
      setIsPiDockExpanded(true)
    }
  }, [piAgentChangeSet?.status, piAgentReply, piError, piOAuthLoginAttempt, piPreview])

  useEffect(() => {
    const shouldRestorePiAuth = runtimeEnableCameraLucida && ledgerSafeViewMode === 'camera'
    if (!shouldRestorePiAuth) return undefined

    let cancelled = false
    setIsPiAuthBusy(true)
    setPiAuthError(null)
    listPiMatrixAgentOAuthProviders()
      .then(({ providers }) => {
        if (cancelled) return
        const restoredProviders = Array.isArray(providers) ? providers : []
        setPiOAuthProviders(restoredProviders)
        const storedCodexAuth = restoredProviders.some(provider => (
          provider.id === 'openai-codex' && provider.hasAuth
        ))
        if (storedCodexAuth && ledgerSafeViewMode === 'camera') {
          setPiAgentModelProvider('openai-codex')
          setPiAgentModelId(current => (
            current && current !== 'gemini-3.1-pro-preview' ? current : 'gpt-5.4'
          ))
        }
      })
      .catch(error => {
        if (cancelled) return
        setPiAuthError(error instanceof Error ? error.message : 'Pi authentication status could not be restored')
      })
      .finally(() => {
        if (!cancelled) setIsPiAuthBusy(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    ledgerSafeViewMode,
    runtimeEnableCameraLucida,
  ])

  const editableContextsByRowId = useMemo(() => {
    return preparedWitnessContext?.boundaryContextsByLeftRowId || new Map<string, LedgerSafeBoundaryContext>()
  }, [preparedWitnessContext])

  useEffect(() => {
    if (!selectedLeftRowId) {
      return
    }
    if (editableContextsByRowId.has(selectedLeftRowId)) {
      return
    }
    setSelectedLeftRowId('')
    setPendingDeltaWords(0)
  }, [editableContextsByRowId, selectedLeftRowId])

  useEffect(() => {
    if (!selectedCell) {
      setSelectionAnchorCell(null)
      setSelectedRange(null)
      setInlineEditMode('idle')
      setSelectedSplitBoundaryWordIndex(null)
      setMarkMenuCell(null)
      setTranspositionTarget(null)
      return
    }
    const row = resolvedRows.find(candidate => candidate.id === selectedCell.rowId)
    const witnessExists = interactiveScopeWitnesses.some(witness => witness.id === selectedCell.witnessId)
    if (!row || !witnessExists) {
      setSelectedCell(null)
      setSelectionAnchorCell(null)
      setSelectedRange(null)
      setInlineEditMode('idle')
      setSelectedSplitBoundaryWordIndex(null)
      setMarkMenuCell(null)
      setTranspositionTarget(null)
    }
  }, [interactiveScopeWitnesses, resolvedRows, selectedCell, setTranspositionTarget])

  useEffect(() => {
    if (!selectedRange) {
      return
    }
    if (activeWitness && selectedRange.witnessId !== activeWitness.id) {
      setSelectedRange(null)
      return
    }
    const witnessExists = interactiveScopeWitnesses.some(witness => witness.id === selectedRange.witnessId)
    const rowsExist = selectedRange.rowIds.every(rowId => rowById.has(rowId))
    if (!witnessExists || !rowsExist) {
      setSelectedRange(null)
    }
  }, [activeWitness, interactiveScopeWitnesses, rowById, selectedRange])

  useEffect(() => {
    const validRowIds = selectedStructuralRowIds.filter(rowId => rowById.has(rowId))
    if (validRowIds.length !== selectedStructuralRowIds.length) {
      setSelectedStructuralRowIds(validRowIds)
    }
    if (structuralSelectionAnchorRowId && !rowById.has(structuralSelectionAnchorRowId)) {
      setStructuralSelectionAnchorRowId('')
    }
    if (
      structuralRangeEditor
      && (
        !rowById.has(structuralRangeEditor.startRowId)
        || !rowById.has(structuralRangeEditor.endRowId)
      )
    ) {
      setStructuralRangeEditor(null)
      setStructuralRangeMenu(null)
      setStructuralRangeRibbonAnchor(null)
      setStructuralRangeRibbonPosition(null)
      setStructuralBoundaryPlacement(null)
      setStructuralBoundaryHoverRowId('')
      setStructuralRangeError('The range editor closed because one of its boundary rows no longer exists.')
    }
    if (
      structuralRangeRibbonAnchor?.kind === 'row'
      && !rowById.has(structuralRangeRibbonAnchor.id)
    ) {
      setStructuralRangeRibbonAnchor(null)
      setStructuralRangeRibbonPosition(null)
      setStructuralRangeMenu(null)
    }
    if (
      structuralRangeRibbonAnchor?.kind === 'range'
      && !structuralRangeById.has(structuralRangeRibbonAnchor.id)
    ) {
      setStructuralRangeRibbonAnchor(null)
      setStructuralRangeRibbonPosition(null)
      setStructuralRangeMenu(null)
    }
  }, [
    rowById,
    selectedStructuralRowIds,
    structuralRangeById,
    structuralRangeEditor,
    structuralRangeRibbonAnchor,
    structuralSelectionAnchorRowId,
  ])

  useEffect(() => {
    if (!structuralRangeMenu) return undefined
    const handlePointerDown = (event: PointerEvent) => {
      if (structuralRangeMenuRef.current?.contains(event.target as Node)) return
      setStructuralRangeMenu(null)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [structuralRangeMenu])

  useEffect(() => {
    if (!structuralRangeMenu || structuralRangeMenu === 'title') return undefined
    const frameId = requestAnimationFrame(() => {
      const menu = structuralRangeMenuRef.current?.querySelector<HTMLElement>('[role="menu"]')
      const selectedItem = menu?.querySelector<HTMLElement>('[aria-checked="true"]')
      const firstItem = menu?.querySelector<HTMLElement>('[role^="menuitem"]')
      ;(selectedItem || firstItem)?.focus()
    })
    return () => cancelAnimationFrame(frameId)
  }, [structuralRangeMenu])

  useLayoutEffect(() => {
    if (!structuralRangeEditor || !structuralRangeRibbonAnchor) {
      setStructuralRangeRibbonPosition(null)
      return undefined
    }
    const findAnchorElement = (): HTMLElement | null => {
      const attribute = structuralRangeRibbonAnchor.kind === 'row'
        ? 'data-structural-row-id'
        : 'data-structural-range-id'
      return Array.from(document.querySelectorAll<HTMLElement>(`[${attribute}]`))
        .find(element => element.getAttribute(attribute) === structuralRangeRibbonAnchor.id)
        || null
    }
    const updatePosition = () => {
      const anchorElement = findAnchorElement()
      if (!anchorElement) {
        setStructuralRangeRibbonPosition(null)
        return
      }
      const anchorRect = anchorElement.getBoundingClientRect()
      const viewportPadding = 12
      const minimumWorkingWidth = 280
      let left = structuralRangeRibbonAnchor.kind === 'row'
        ? anchorRect.right + 2
        : anchorRect.left + 12
      let maxWidth = window.innerWidth - left - viewportPadding
      if (maxWidth < minimumWorkingWidth) {
        left = viewportPadding
        maxWidth = window.innerWidth - (viewportPadding * 2)
      }
      const ribbonHeight = structuralRangeMenuRef.current?.offsetHeight || 38
      let top = structuralRangeRibbonAnchor.kind === 'row'
        ? anchorRect.top - 8
        : anchorRect.bottom - 1
      if (top + ribbonHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, anchorRect.top - ribbonHeight - 4)
      }
      const nextPosition = {
        left: Math.round(Math.max(viewportPadding, left)),
        top: Math.round(Math.max(viewportPadding, top)),
        maxWidth: Math.round(Math.max(200, maxWidth)),
      }
      setStructuralRangeRibbonPosition(current => (
        current
        && current.left === nextPosition.left
        && current.top === nextPosition.top
        && current.maxWidth === nextPosition.maxWidth
          ? current
          : nextPosition
      ))
    }
    updatePosition()
    const frameId = requestAnimationFrame(updatePosition)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [
    structuralBoundaryPlacement,
    structuralRangeEditor,
    structuralRangeMenu,
    structuralRangeRibbonAnchor,
  ])

  useEffect(() => {
    if (!structuralRangeEditor) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (structuralBoundaryPlacement) {
        setStructuralBoundaryPlacement(null)
        setStructuralBoundaryHoverRowId('')
        return
      }
      if (structuralRangeMenu) {
        setStructuralRangeMenu(null)
        return
      }
      setStructuralRangeEditor(null)
      setStructuralRangeRibbonAnchor(null)
      setStructuralRangeRibbonPosition(null)
      setSelectedStructuralRowIds([])
      setStructuralSelectionAnchorRowId('')
      setStructuralRangeError(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [structuralBoundaryPlacement, structuralRangeEditor, structuralRangeMenu])

  useEffect(() => {
    if (!transpositionTarget) {
      return
    }
    const witnessExists = interactiveScopeWitnesses.some(witness => witness.id === transpositionTarget.witnessId)
    if (!witnessExists || !rowById.has(transpositionTarget.originRowId)) {
      setTranspositionTarget(null)
    }
  }, [interactiveScopeWitnesses, rowById, setTranspositionTarget, transpositionTarget])

  useEffect(() => {
    if (!relocationTarget) {
      return
    }
    const witnessExists = interactiveScopeWitnesses.some(
      witness => witness.id === relocationTarget.witnessId
    )
    const sourceStillMatches = relocationTarget.sourceRowIds.every((rowId, index) => (
      rowById.get(rowId)?.unitMappings.some(mapping => (
        mapping.witnessId === relocationTarget.witnessId
        && !mapping.excluded
        && mapping.unitUuid === relocationTarget.unitUuids[index]
      ))
    ))
    if (!witnessExists || !sourceStillMatches) {
      setRelocationTarget(null)
    }
  }, [interactiveScopeWitnesses, relocationTarget, rowById, setRelocationTarget])

  const selectedLedgerRow = useMemo(
    () => selectedCell ? (resolvedRows.find(row => row.id === selectedCell.rowId) || null) : null,
    [resolvedRows, selectedCell]
  )
  const selectedLedgerWitness = useMemo(
    () => selectedCell ? (interactiveScopeWitnesses.find(witness => witness.id === selectedCell.witnessId) || null) : null,
    [interactiveScopeWitnesses, selectedCell]
  )
  const selectedLedgerCellState = useMemo(() => {
    if (!selectedLedgerRow || !selectedLedgerWitness) {
      return null
    }
    return getLedgerSafeCellState(selectedLedgerRow, selectedLedgerWitness.id)
  }, [selectedLedgerRow, selectedLedgerWitness])
  const selectedLedgerActiveMapping = useMemo(() => {
    if (!selectedLedgerRow || !selectedLedgerWitness) {
      return null
    }
    return selectedLedgerRow.unitMappings.find(mapping => (
      mapping.witnessId === selectedLedgerWitness.id && !mapping.excluded
    )) || null
  }, [selectedLedgerRow, selectedLedgerWitness])
  const selectedRangeRows = useMemo(
    () => selectedRange ? selectedRange.rowIds.map(rowId => rowById.get(rowId)).filter(Boolean) as AnchoredRow[] : [],
    [rowById, selectedRange]
  )
  const selectedRangeSummary = useMemo(() => {
    if (!selectedRange || selectedRangeRows.length < 2) {
      return null
    }
    const mappedRowIds = selectedRangeRows
      .filter(row => getLedgerSafeCellState(row, selectedRange.witnessId) === 'mapped')
      .map(row => row.id)
    return {
      witnessId: selectedRange.witnessId,
      fromLabel: getRowLabel(selectedRangeRows[0]),
      toLabel: getRowLabel(selectedRangeRows[selectedRangeRows.length - 1]),
      allMapped: mappedRowIds.length === selectedRangeRows.length,
      mappedRowIds,
      mappedCount: mappedRowIds.length
    }
  }, [selectedRange, selectedRangeRows])
  const titledLedgerRowCount = useMemo(
    () => resolvedRows.filter(row => Boolean(getCollationRowPrimaryTitle(row))).length,
    [resolvedRows]
  )
  const selectedRowTitleScopeRows = useMemo(() => {
    if (selectedRange && selectedRangeRows.length > 0) {
      return selectedRangeRows
    }
    if (selectedLedgerRow) {
      return [selectedLedgerRow]
    }
    return resolvedRows.filter(row => !getCollationRowPrimaryTitle(row))
  }, [resolvedRows, selectedLedgerRow, selectedRange, selectedRangeRows])
  const rowTitleGenerationScopeLabel = selectedRange && selectedRangeRows.length > 0
    ? `${selectedRangeRows.length} selected rows`
    : selectedLedgerRow
      ? getRowLabel(selectedLedgerRow)
      : `${selectedRowTitleScopeRows.length} untitled rows`
  const readyRowTitleGenerationBatchCount = rowTitleGenerationRun?.batches.filter(
    batch => batch.status === 'ready'
  ).length || 0
  const structuralRangeReviewCount = structuralRangeLayer.ranges.filter(
    range => range.status === 'suggested' || range.status === 'unclassified'
  ).length + structuralRangeDiagnostics.filter(diagnostic => diagnostic.severity === 'error').length
  const rowTitleImportAttentionCount = rowTitleSidecarPreview
    ? rowTitleSidecarPreview.counts.ready
      + rowTitleSidecarPreview.counts.conflict
      + (sidecarStructuralRangePreview?.candidates.length || 0)
      + rowTitleSidecarPreview.staleImportedTitleRowIds.length
    : 0
  const rowTitleRoleAwareRepairCount = rowTitleSidecarPreview
    ? rowTitleSidecarPreview.repairableTitleCount
      + (sidecarStructuralRangePreview?.candidates.length || 0)
      + rowTitleSidecarPreview.staleImportedTitleRowIds.length
    : 0
  const rowTitleSidecarNeedsRoleAwareRepair = Boolean(
    rowTitleSidecarPreview
    && (
      Boolean(sidecarStructuralRangePreview?.candidates.length)
      || rowTitleSidecarPreview.staleImportedTitleRowIds.length > 0
      || rowTitleSidecarPreview.matches.some(match => match.status === 'conflict' && match.safeToReplace)
    )
  )

  useEffect(() => {
    if (sidePanelTab !== 'titles' || !selectedLedgerRow) {
      return
    }
    setRowTitleEditor(current => current?.rowId === selectedLedgerRow.id
      ? current
      : {
          rowId: selectedLedgerRow.id,
          primaryText: getCollationRowPrimaryTitle(selectedLedgerRow),
          parallelText: getCollationRowParallelTitle(selectedLedgerRow),
        })
  }, [selectedLedgerRow, sidePanelTab])

  const selectedUnitRepairScope = useMemo((): SelectedUnitRepairScope => {
    const selectedRows = selectedRange && selectedRangeRows.length > 0
      ? selectedRangeRows
      : (selectedLedgerRow ? [selectedLedgerRow] : [])
    const witnessId = selectedRange?.witnessId || selectedLedgerWitness?.id || selectedCell?.witnessId || ''
    const witness = witnessId
      ? (interactiveScopeWitnesses.find(candidate => candidate.id === witnessId) || null)
      : null
    const sortedRows = [...resolvedRows].sort((left, right) => {
      if (left.position !== right.position) {
        return left.position - right.position
      }
      return left.labelNumber - right.labelNumber
    })
    const selectedRowIdSet = new Set(selectedRows.map(row => row.id))
    const selectedIndexes = sortedRows
      .map((row, index) => selectedRowIdSet.has(row.id) ? index : -1)
      .filter(index => index >= 0)
    const firstIndex = selectedIndexes.length > 0 ? Math.min(...selectedIndexes) : -1
    const lastIndex = selectedIndexes.length > 0 ? Math.max(...selectedIndexes) : -1
    const contextRows = firstIndex >= 0
      ? sortedRows.slice(Math.max(0, firstIndex - 3), Math.min(sortedRows.length, lastIndex + 4))
      : []
    const anchorRows: AnchoredRow[] = []
    if (witness && firstIndex > 0) {
      for (let index = firstIndex - 1; index >= 0; index -= 1) {
        const row = sortedRows[index]
        if (row && getLedgerSafeCellState(row, witness.id) === 'mapped') {
          anchorRows.push(row)
          break
        }
      }
    }
    if (witness && lastIndex >= 0 && lastIndex < sortedRows.length - 1) {
      for (let index = lastIndex + 1; index < sortedRows.length; index += 1) {
        const row = sortedRows[index]
        if (row && getLedgerSafeCellState(row, witness.id) === 'mapped') {
          anchorRows.push(row)
          break
        }
      }
    }
    const requestRows = sortedRows
    const label = selectedRows.length > 1
      ? `${getRowLabel(selectedRows[0])}–${getRowLabel(selectedRows[selectedRows.length - 1])}`
      : (selectedRows[0] ? getRowLabel(selectedRows[0]) : 'No row')
    let reason: string | null = null
    if (!witness) {
      reason = 'Select a witness cell or same-witness row range first.'
    } else if (!witness.uploaded_file?.content?.trim()) {
      reason = `${witness.sigla} has no source text to resegment.`
    } else if (!witness.segmentation_result) {
      reason = `${witness.sigla} has no current segmentation to repair.`
    } else if (selectedRows.length === 0) {
      reason = 'Select a mapped Ledger unit before preparing a scoped repair.'
    } else if (selectedRows.some(row => getLedgerSafeCellState(row, witness.id) !== 'mapped')) {
      reason = 'Selected repair currently requires mapped cells for the target witness.'
    }

    return {
      witness,
      selectedRows,
      requestRows,
      contextRowIds: contextRows.map(row => row.id).filter(rowId => !selectedRowIdSet.has(rowId)),
      anchorRowIds: anchorRows.map(row => row.id),
      label,
      reason,
    }
  }, [
    interactiveScopeWitnesses,
    resolvedRows,
    selectedCell?.witnessId,
    selectedLedgerRow,
    selectedLedgerWitness?.id,
    selectedRange,
    selectedRangeRows,
  ])

  const selectedUnitRepairTargetSpan = useMemo(() => {
    if (!selectedUnitRepairScope.witness || selectedUnitRepairScope.selectedRows.length === 0) {
      return null
    }
    return deriveSelectedRepairTargetSpan({
      witness: selectedUnitRepairScope.witness,
      selectedRows: selectedUnitRepairScope.selectedRows,
    })
  }, [selectedUnitRepairScope])

  const selectedUnitRepairResumeScopeId = useMemo(() => {
    if (!selectedUnitRepairTargetSpan) {
      return null
    }
    return buildLedgerSafeSelectedRepairResumeScopeId({
      baseScopeId: selectedScopeId || null,
      selectedRowIds: selectedUnitRepairScope.selectedRows.map(row => row.id),
      startChar: selectedUnitRepairTargetSpan.startChar,
      endChar: selectedUnitRepairTargetSpan.endChar,
      spanText: selectedUnitRepairTargetSpan.text,
      modelId: selectedUnitRepairModelId,
      thinkingLevel: selectedUnitRepairThinkingLevel,
    })
  }, [
    selectedScopeId,
    selectedUnitRepairModelId,
    selectedUnitRepairScope.selectedRows,
    selectedUnitRepairTargetSpan,
    selectedUnitRepairThinkingLevel,
  ])

  const buildSelectedUnitRepairPreviewFromRun = useEventCallback((run: SelectedUnitRepairRunState) => {
    const pendingResumeState = run.pendingResumeState
    if (!pendingResumeState) {
      return null
    }
    const targetWitness = (
      interactiveScopeWitnesses.find(witness => witness.id === run.targetWitnessId)
      || scopeWitnesses.find(witness => witness.id === run.targetWitnessId)
      || null
    )
    if (!targetWitness) {
      return null
    }
    const cumulativePlan = deriveTranslationPropagationPlan({
      text: targetWitness.uploaded_file?.content || '',
      charAssignments: pendingResumeState.charAssignments,
    })
    const pendingPlan = deriveTranslationPropagationPlan({
      text: targetWitness.uploaded_file?.content || '',
      charAssignments: pendingResumeState.charAssignments.slice(run.resumeState.charAssignments.length),
    })
    const committedPlan = deriveTranslationPropagationPlan({
      text: targetWitness.uploaded_file?.content || '',
      charAssignments: run.resumeState.charAssignments,
    })
    if (pendingPlan.rowAssignments.length === 0) {
      return null
    }
    const mappedRepairAssignments = pendingPlan.rowAssignments.filter(
      assignment => typeof assignment.segmentIndex === 'number'
    )
    const completedBatches = getCompletedTranslationBatchCount(pendingResumeState)
    const complete = (
      pendingResumeState.batchIndex >= pendingResumeState.totalBatches
      && getTranslationResumeRowOffset(pendingResumeState) === 0
    )
    return buildSelectedUnitPropagationRepairPreview({
      targetWitness,
      rows: resolvedRows,
      selectedRowIds: run.selectedRowIds,
      contextRowIds: run.contextRowIds,
      anchorRowIds: run.anchorRowIds,
      proposedSplitIndices: cumulativePlan.splitIndices,
      rowAssignments: pendingPlan.rowAssignments,
      repairTargetSpan: run.repairTargetSpan,
      previouslyAppliedRowIds: committedPlan.rowAssignments
        .filter(assignment => typeof assignment.segmentIndex === 'number')
        .map(assignment => assignment.rowId),
      notes: mergeTranslationPlanNotes(
        complete
          ? `Final selected repair batch ${completedBatches}/${pendingResumeState.totalBatches} is ready for review.`
          : `Selected repair batch ${completedBatches}/${pendingResumeState.totalBatches} is ready for review.`,
        summarizeTranslationBatchRetryHistory(pendingResumeState.retryHistory),
        `Scoped repair: selected span mapped into ${mappedRepairAssignments.length} spine row${mappedRepairAssignments.length === 1 ? '' : 's'}.`,
        'Chapter spine sent with one non-target exemplar per row; only the selected target span is segmented.',
      ),
      diagnostics: combineTranslationAlignmentDiagnostics(
        pendingResumeState.batchDiagnostics.slice(run.resumeState.batchDiagnostics.length)
      ),
    })
  })

  const persistSelectedUnitRepairRun = useEventCallback(async (run: SelectedUnitRepairRunState) => {
    if (!chapterId) {
      return
    }
    const targetWitness = (
      interactiveScopeWitnesses.find(witness => witness.id === run.targetWitnessId)
      || scopeWitnesses.find(witness => witness.id === run.targetWitnessId)
      || null
    )
    if (!targetWitness) {
      return
    }
    await dbStorage.saveTranslationPreviewResume(buildTranslationPreviewResumeRecord({
      id: buildLedgerSafeTranslationPreviewResumeId(chapterId, run.targetWitnessId, run.resumeScopeId),
      surface: 'ledger-safe',
      contextId: chapterId,
      witnessId: run.targetWitnessId,
      scopeId: run.resumeScopeId,
      rowIds: run.requestRowIds,
      witnessText: targetWitness.uploaded_file?.content || '',
      resumeState: run.resumeState,
      pendingResumeState: run.pendingResumeState,
      selectedRepair: {
        selectedRowIds: [...run.selectedRowIds],
        requestRowIds: [...run.requestRowIds],
        repairCandidateRowIds: [...run.repairCandidateRowIds],
        contextRowIds: [...run.contextRowIds],
        anchorRowIds: [...run.anchorRowIds],
        repairTargetSpan: { ...run.repairTargetSpan },
        modelId: run.modelId,
        thinkingLevel: run.thinkingLevel,
      },
      failure: run.failure,
      modelId: run.modelId,
      batchSize: LEDGER_SAFE_SELECTED_REPAIR_BATCH_SIZE,
    }))
  })

  const clearSelectedUnitRepairRunRecord = useEventCallback(async (run: SelectedUnitRepairRunState) => {
    if (!chapterId) {
      return
    }
    await dbStorage.deleteTranslationPreviewResume(
      buildLedgerSafeTranslationPreviewResumeId(chapterId, run.targetWitnessId, run.resumeScopeId)
    )
  })

  useEffect(() => {
    if (
      !chapterId
      || !selectedUnitRepairScope.witness
      || selectedUnitRepairScope.selectedRows.length === 0
      || selectedUnitRepairRun
      || selectedUnitRepairPreview
    ) {
      return
    }
    const targetWitness = selectedUnitRepairScope.witness
    const currentSelectedRowIds = selectedUnitRepairScope.selectedRows.map(row => row.id)
    const hydrationKey = `${chapterId}:${currentSelectedRowIds.join(',')}:${targetWitness.id}`
    if (selectedUnitRepairResumeHydrationRef.current === hydrationKey) {
      return
    }
    selectedUnitRepairResumeHydrationRef.current = hydrationKey
    let cancelled = false

    void (async () => {
      try {
        const exactRecordId = selectedUnitRepairResumeScopeId
          ? buildLedgerSafeTranslationPreviewResumeId(
              chapterId,
              targetWitness.id,
              selectedUnitRepairResumeScopeId
            )
          : null
        const records = await dbStorage.listTranslationPreviewResumesForContext('ledger-safe', chapterId)
        const record = records
          .filter(candidate => candidate.witnessId === targetWitness.id)
          .filter(candidate => {
            if (exactRecordId && candidate.id === exactRecordId) {
              return true
            }
            const metadata = candidate.selectedRepair
            return Boolean(
              metadata
              && metadata.selectedRowIds.length === currentSelectedRowIds.length
              && metadata.selectedRowIds.every((rowId, index) => rowId === currentSelectedRowIds[index])
            )
          })
          .sort((left, right) => right.updatedAt - left.updatedAt)
          .find(candidate => {
            const metadata = candidate.selectedRepair
            return isTranslationPreviewResumeRecordCurrent({
              record: candidate,
              contextId: chapterId,
              witnessId: targetWitness.id,
              scopeId: candidate.scopeId,
              rowIds: metadata?.requestRowIds || selectedUnitRepairScope.requestRows.map(row => row.id),
              witnessText: targetWitness.uploaded_file?.content || '',
            })
          })
        if (!record) {
          return
        }
        const metadata = record.selectedRepair
        const repairTargetSpan = metadata?.repairTargetSpan || selectedUnitRepairTargetSpan
        const resumeScopeId = record.scopeId || selectedUnitRepairResumeScopeId
        if (!repairTargetSpan || !resumeScopeId) {
          return
        }
        const requestRowIds = metadata?.requestRowIds || selectedUnitRepairScope.requestRows.map(row => row.id)
        const repairCandidateRowIds = recoverLedgerSafeRepairCandidateRowIds({
          candidateRowIds: metadata?.repairCandidateRowIds,
          requestRowIds,
          resumeState: record.resumeState,
        })
        const restoredRun: SelectedUnitRepairRunState = {
          resumeScopeId,
          targetWitnessId: targetWitness.id,
          selectedRowIds: metadata?.selectedRowIds || currentSelectedRowIds,
          requestRowIds,
          repairCandidateRowIds,
          contextRowIds: metadata?.contextRowIds || [...selectedUnitRepairScope.contextRowIds],
          anchorRowIds: metadata?.anchorRowIds || [...selectedUnitRepairScope.anchorRowIds],
          repairTargetSpan,
          modelId: metadata?.modelId || selectedUnitRepairModelId,
          thinkingLevel: normalizeLedgerSafeAlignmentThinkingLevel(
            metadata?.thinkingLevel || selectedUnitRepairThinkingLevel
          ),
          resumeState: record.resumeState,
          pendingResumeState: record.pendingResumeState || null,
          failure: record.failure,
        }
        const restoredPreview = buildSelectedUnitRepairPreviewFromRun(restoredRun)
        if (cancelled) {
          return
        }
        setSelectedUnitRepairRun(restoredRun)
        setSelectedUnitRepairPreview(restoredPreview)
        setSelectedUnitRepairModelId(normalizeLedgerSafeAlignmentModelId(restoredRun.modelId))
        setSelectedUnitRepairThinkingLevel(restoredRun.thinkingLevel)
        const completedBatches = getCompletedTranslationBatchCount(restoredRun.resumeState)
        setContextPropagationState({
          previewing: false,
          applying: false,
          error: restoredRun.failure?.error || null,
          success: restoredRun.pendingResumeState
            ? `Restored selected repair batch ${getCompletedTranslationBatchCount(restoredRun.pendingResumeState)}/${restoredRun.pendingResumeState.totalBatches} for review.`
            : `Restored selected repair for ${targetWitness.sigla} with ${completedBatches}/${restoredRun.resumeState.totalBatches} batches applied.`,
        })
      } catch (error) {
        console.warn('Failed to restore persisted selected repair state.', error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    buildSelectedUnitRepairPreviewFromRun,
    chapterId,
    selectedUnitRepairModelId,
    selectedUnitRepairPreview,
    selectedUnitRepairResumeScopeId,
    selectedUnitRepairRun,
    selectedUnitRepairScope,
    selectedUnitRepairTargetSpan,
    selectedUnitRepairThinkingLevel,
  ])

  useEffect(() => {
    if (!selectedUnitRepairRun || selectedUnitRepairPreview) {
      return
    }
    const restoredPreview = buildSelectedUnitRepairPreviewFromRun(selectedUnitRepairRun)
    if (restoredPreview) {
      setSelectedUnitRepairPreview(restoredPreview)
    }
  }, [buildSelectedUnitRepairPreviewFromRun, selectedUnitRepairPreview, selectedUnitRepairRun])

  const selectedUnitRepairCompletedBatches = selectedUnitRepairRun
    ? getCompletedTranslationBatchCount(selectedUnitRepairRun.resumeState)
    : 0
  const selectedUnitRepairPendingBatchNumber = selectedUnitRepairRun?.pendingResumeState
    ? getCompletedTranslationBatchCount(selectedUnitRepairRun.pendingResumeState)
    : null
  const selectedUnitRepairNextBatchNumber = selectedUnitRepairPendingBatchNumber
    || (selectedUnitRepairRun ? selectedUnitRepairRun.resumeState.batchIndex + 1 : null)
  const selectedUnitRepairRunComplete = Boolean(
    selectedUnitRepairRun
    && !selectedUnitRepairRun.pendingResumeState
    && selectedUnitRepairRun.resumeState.batchIndex >= selectedUnitRepairRun.resumeState.totalBatches
    && getTranslationResumeRowOffset(selectedUnitRepairRun.resumeState) === 0
  )

  const activeCharacters = useMemo(
    () => getActiveCharacters(resolvedCharacterLayer),
    [resolvedCharacterLayer]
  )
  const activeCharacterOccurrences = useMemo(
    () => getActiveCharacterOccurrences(resolvedCharacterLayer),
    [resolvedCharacterLayer]
  )
  const activeCharacterOccurrencesByCharacterId = useMemo(() => {
    const map = new Map<string, CharacterOccurrence[]>()
    activeCharacterOccurrences.forEach(occurrence => {
      getCharacterOccurrenceReferentIds(occurrence).forEach(characterId => {
        const bucket = map.get(characterId) || []
        bucket.push(occurrence)
        map.set(characterId, bucket)
      })
    })
    return map
  }, [activeCharacterOccurrences])
  const characterIndexRunningIndex = useMemo(
    () => projectChapterCharacterLayerToRunningIndex({
      layer: resolvedCharacterLayer,
      workId: bookId || resolvedCharacterLayer.book_id || 'work',
      chapterId: chapterId || resolvedCharacterLayer.chapter_id,
    }),
    [bookId, chapterId, resolvedCharacterLayer]
  )
  const characterIndexEntryById = useMemo(
    () => new Map(characterIndexRunningIndex.entries.map(entry => [entry.id, entry])),
    [characterIndexRunningIndex]
  )
  const characterExtractionSceneOptions = useMemo<CharacterExtractionSceneOption[]>(
    () => Array.from(ledgerSceneHeaders.bySceneKey.values()).map(scene => ({
      sceneId: scene.sceneKey,
      label: scene.label,
      title: scene.title,
      rowCount: scene.rowIds.length,
    })),
    [ledgerSceneHeaders]
  )
  const selectedLedgerSceneId = useMemo(() => {
    if (!selectedLedgerRow) {
      return ''
    }
    const scene = getAnchoredRowSceneMetadata(selectedLedgerRow)
    return scene ? getLedgerSafeSceneKey(scene) : ''
  }, [selectedLedgerRow])
  useEffect(() => {
    if (!runtimeEnableCharacterIndex || characterExtractionUnit !== 'scene') {
      return
    }
    const sceneIds = new Set(characterExtractionSceneOptions.map(option => option.sceneId))
    if (characterExtractionSceneId && sceneIds.has(characterExtractionSceneId)) {
      return
    }
    const nextSceneId = selectedLedgerSceneId && sceneIds.has(selectedLedgerSceneId)
      ? selectedLedgerSceneId
      : characterExtractionSceneOptions[0]?.sceneId || ''
    setCharacterExtractionSceneId(nextSceneId)
  }, [
    characterExtractionSceneId,
    characterExtractionSceneOptions,
    characterExtractionUnit,
    runtimeEnableCharacterIndex,
    selectedLedgerSceneId,
  ])
  const characterExtractionRowSelection = useMemo(() => {
    if (selectedRange?.rowIds.length) {
      return {
        witnessId: selectedRange.witnessId,
        rowIds: selectedRange.rowIds,
      }
    }
    if (selectedCell) {
      return {
        witnessId: selectedCell.witnessId,
        rowIds: [selectedCell.rowId],
      }
    }
    return {
      witnessId: '',
      rowIds: [] as string[],
    }
  }, [selectedCell, selectedRange])
  const characterExtractionEffectiveWitnessId = characterExtractionUnit === 'rows' && characterExtractionRowSelection.witnessId
    ? characterExtractionRowSelection.witnessId
    : characterExtractionWitnessId
  const characterExtractionWitness = useMemo(
    () => scopeWitnessById.get(characterExtractionEffectiveWitnessId) || activeWitness || scopeWitnesses[0] || null,
    [activeWitness, characterExtractionEffectiveWitnessId, scopeWitnessById, scopeWitnesses]
  )
  const characterReadingLayerWitnessIds = useMemo(() => {
    const ids = new Set<string>()
    resolvedCharacterLayer.notes.forEach(note => {
      const layer = note.metadata?.character_index_layer
      if (
        note.anchor.kind === 'span' &&
        (layer === 'focalization' || layer === 'characterization') &&
        note.anchor.locator.witnessId
      ) {
        ids.add(note.anchor.locator.witnessId)
      }
    })
    return ids
  }, [resolvedCharacterLayer.notes])
  useEffect(() => {
    if (
      !runtimeEnableCharacterIndex ||
      ledgerSafeViewMode !== 'tradition' ||
      characterReadingLayerWitnessIds.size === 0
    ) {
      return
    }
    if (traditionSourceWitnessId && scopeWitnessById.has(traditionSourceWitnessId)) {
      return
    }
    if (activeTraditionSourceWitness && characterReadingLayerWitnessIds.has(activeTraditionSourceWitness.id)) {
      return
    }
    const preferredWitnessId = (
      characterExtractionEffectiveWitnessId &&
      characterReadingLayerWitnessIds.has(characterExtractionEffectiveWitnessId)
    )
      ? characterExtractionEffectiveWitnessId
      : Array.from(characterReadingLayerWitnessIds).find(witnessId => scopeWitnessById.has(witnessId)) || ''
    if (preferredWitnessId && preferredWitnessId !== traditionSourceWitnessId) {
      setTraditionSourceWitnessId(preferredWitnessId)
    }
  }, [
    activeTraditionSourceWitness,
    characterExtractionEffectiveWitnessId,
    characterReadingLayerWitnessIds,
    ledgerSafeViewMode,
    runtimeEnableCharacterIndex,
    scopeWitnessById,
    traditionSourceWitnessId,
  ])
  const characterExtractionScope = useMemo<CharacterExtractionScope | null>(() => {
    const witnessId = characterExtractionWitness?.id || ''
    if (!witnessId || characterExtractionUnit === 'visible-ledger') {
      return null
    }
    if (characterExtractionUnit === 'witness') {
      return {
        kind: 'witness',
        witnessId,
      }
    }
    if (characterExtractionUnit === 'scene') {
      return characterExtractionSceneId
        ? {
          kind: 'scene',
          witnessId,
          sceneId: characterExtractionSceneId,
        }
        : null
    }
    return characterExtractionRowSelection.rowIds.length > 0
      ? {
        kind: 'rows',
        witnessId,
        rowIds: characterExtractionRowSelection.rowIds,
      }
      : null
  }, [
    characterExtractionRowSelection,
    characterExtractionSceneId,
    characterExtractionUnit,
    characterExtractionWitness?.id,
  ])
  const selectedCharacterDetectorIsCoreferenceV1 = characterDetectorId === 'llm-coreference-chain'
  const selectedCharacterDetectorIsCoreferenceV2 = characterDetectorId === 'llm-coreference-chain-v2'
  const selectedCharacterDetectorIsFocalization = characterDetectorId === 'llm-focalization-characterization'
  const selectedCharacterDetectorRequiresPreparedExtraction =
    selectedCharacterDetectorIsCoreferenceV1 || selectedCharacterDetectorIsCoreferenceV2 || selectedCharacterDetectorIsFocalization
  const characterExtractionUnitLabel = CHARACTER_EXTRACTION_UNIT_LABELS[characterExtractionUnit]
  const focalizationSourceRows = useMemo<CharacterIndexSyntheticFigureSourceRow[]>(() => {
    if (!characterExtractionScope || !characterExtractionWitness) {
      return []
    }
    const usableRows = (rowIds: readonly string[]) => (
      rowIds.flatMap(rowId => {
        const row = rowById.get(rowId)
        if (!row) {
          return []
        }
        const text = getWorkspaceMappingText(row, characterExtractionScope.witnessId, traditionWitnessUnitTextLookupByWitnessId).trim()
        if (!text) {
          return []
        }
        return [{
          rowId,
          rowLabel: getRowLabel(row),
          witnessId: characterExtractionScope.witnessId,
          text,
        }]
      })
    )
    if (characterExtractionScope.kind === 'rows') {
      return usableRows(characterExtractionScope.rowIds)
    }
    if (characterExtractionScope.kind === 'scene') {
      const scene = ledgerSceneHeaders.bySceneKey.get(characterExtractionScope.sceneId)
      return usableRows(scene?.rowIds || [])
    }
    return usableRows(resolvedRows.map(row => row.id))
  }, [
    characterExtractionScope,
    characterExtractionWitness,
    ledgerSceneHeaders,
    resolvedRows,
    rowById,
    traditionWitnessUnitTextLookupByWitnessId,
  ])
  const focalizationCoreferenceSourceRowIds = useMemo(
    () => focalizationSourceRows.map(row => row.rowId),
    [focalizationSourceRows]
  )
  const characterIndexFiguresForFocalization = useMemo(
    () => buildCharacterIndexSyntheticFigures({
      characters: activeCharacters,
      occurrences: activeCharacterOccurrences,
      sourceRows: focalizationSourceRows,
    }),
    [activeCharacterOccurrences, activeCharacters, focalizationSourceRows]
  )
  const latestCoreferenceV2RunForFocalization = useMemo(
    () => characterDetectorRunHistory.find(run => (
      run.status === 'completed' &&
      run.provenance.detectorId === 'llm-coreference-chain-v2'
    )) || null,
    [characterDetectorRunHistory]
  )
  const latestCoreferenceV2ResolutionForFocalization = useMemo(
    () => resolveCoreferenceV2FiguresForFocalization(latestCoreferenceV2RunForFocalization?.diagnostics, {
      sourceRowIds: focalizationCoreferenceSourceRowIds,
    }),
    [focalizationCoreferenceSourceRowIds, latestCoreferenceV2RunForFocalization]
  )
  const compatibleCoreferenceV2MatchForFocalization = useMemo(() => {
    for (const run of characterDetectorRunHistory) {
      if (run.status !== 'completed' || run.provenance.detectorId !== 'llm-coreference-chain-v2') {
        continue
      }
      const resolution = resolveCoreferenceV2FiguresForFocalization(run.diagnostics, {
        sourceRowIds: focalizationCoreferenceSourceRowIds,
      })
      const sameExtractionUnit = run.provenance.extractionUnit === characterExtractionUnit
      if (resolution.figures.length > 0 && (sameExtractionUnit || resolution.matchedWindowId)) {
        return { run, resolution }
      }
    }
    return null
  }, [characterDetectorRunHistory, characterExtractionUnit, focalizationCoreferenceSourceRowIds])
  const compatibleCoreferenceV2RunForFocalization = compatibleCoreferenceV2MatchForFocalization?.run || null
  const compatibleCoreferenceV2FiguresForFocalization = useMemo(
    () => compatibleCoreferenceV2MatchForFocalization?.resolution.figures || [],
    [compatibleCoreferenceV2MatchForFocalization]
  )
  const focalizationFiguresForPreparedExtraction = characterFocalizationFigureSource === 'coreference-v2'
    ? compatibleCoreferenceV2FiguresForFocalization
    : characterIndexFiguresForFocalization
  const focalizationFigureSourceLabel = CHARACTER_FOCALIZATION_FIGURE_SOURCE_LABELS[characterFocalizationFigureSource]
  const focalizationCoreferenceV2Message = useMemo(() => {
    if (!selectedCharacterDetectorIsFocalization || compatibleCoreferenceV2FiguresForFocalization.length > 0) {
      return ''
    }
    if (!latestCoreferenceV2RunForFocalization) {
      return 'Run Coreference chains v2 for this unit first.'
    }
    if (
      latestCoreferenceV2RunForFocalization.provenance.extractionUnit !== characterExtractionUnit &&
      !latestCoreferenceV2ResolutionForFocalization.matchedWindowId
    ) {
      return `The latest Coreference chains v2 run was for ${CHARACTER_EXTRACTION_UNIT_LABELS[latestCoreferenceV2RunForFocalization.provenance.extractionUnit]}. Run v2 for ${characterExtractionUnitLabel} before focalization.`
    }
    return latestCoreferenceV2ResolutionForFocalization.message || 'Run Coreference chains v2 for this unit first.'
  }, [
    characterExtractionUnit,
    characterExtractionUnitLabel,
    compatibleCoreferenceV2FiguresForFocalization.length,
    latestCoreferenceV2ResolutionForFocalization,
    latestCoreferenceV2RunForFocalization,
    selectedCharacterDetectorIsFocalization,
  ])
  const focalizationCharacterIndexMessage = useMemo(() => {
    if (!selectedCharacterDetectorIsFocalization || characterIndexFiguresForFocalization.length > 0) {
      return ''
    }
    if (activeCharacters.length === 0) {
      return 'Add or accept character index entries before focalization.'
    }
    return 'No Character Index figures are available for this source. Select rows with indexed occurrences or add a scoped occurrence first.'
  }, [
    activeCharacters.length,
    characterIndexFiguresForFocalization.length,
    selectedCharacterDetectorIsFocalization,
  ])
  const preparedCharacterExtraction = useMemo(() => {
    if (!characterExtractionScope || !characterExtractionWitness) {
      return null
    }
    try {
      const args = {
        projectId: projectLabel || 'local-project',
        workId: bookId || 'work',
        workTitle: ledgerName || contextLabel || bookId || 'Untitled work',
        bookId: bookId || 'book',
        chapterId,
        chapterLabel: contextLabel || snapshotScopeLabel || chapterId || 'Chapter',
        witnesses: scopeWitnesses,
        rows: resolvedRows,
        textLookupByWitnessId: traditionWitnessUnitTextLookupByWitnessId,
        chapterLayer: resolvedCharacterLayer,
        scope: characterExtractionScope,
      }
      if (selectedCharacterDetectorIsCoreferenceV2) {
        return prepareLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction(args)
      }
      if (selectedCharacterDetectorIsCoreferenceV1) {
        return prepareLedgerSafeSingleWitnessCoreferenceChainWindowExtraction(args)
      }
      if (selectedCharacterDetectorIsFocalization) {
        return prepareLedgerSafeSingleWitnessFocalizationCharacterizationExtraction({
          ...args,
          figures: focalizationFiguresForPreparedExtraction,
        })
      }
      return prepareLedgerSafeSingleWitnessCharacterExtraction(args)
    } catch (error) {
      console.warn('Failed to prepare single-witness character extraction', error)
      return null
    }
  }, [
    bookId,
    chapterId,
    characterExtractionScope,
    characterExtractionWitness,
    contextLabel,
    focalizationFiguresForPreparedExtraction,
    ledgerName,
    projectLabel,
    resolvedCharacterLayer,
    resolvedRows,
    scopeWitnesses,
    selectedCharacterDetectorIsCoreferenceV1,
    selectedCharacterDetectorIsCoreferenceV2,
    selectedCharacterDetectorIsFocalization,
    snapshotScopeLabel,
    traditionWitnessUnitTextLookupByWitnessId,
  ])
  const characterDetectorAdapters = useMemo<CharacterLayerDetectorAdapter[]>(
    () => [
      createWorkbenchCharacterIndexDetectorAdapter(),
      createWorkbenchCoreferenceChainDetectorAdapter(),
      createWorkbenchCoreferenceChainV2DetectorAdapter(),
      createWorkbenchFocalizationCharacterizationDetectorAdapter(),
      createNameSweepCharacterLayerDetectorAdapter(),
    ],
    []
  )
  const selectedCharacterDetectorAdapter = useMemo(
    () => characterDetectorAdapters.find(adapter => adapter.id === characterDetectorId) || characterDetectorAdapters[0],
    [characterDetectorAdapters, characterDetectorId]
  )
  const coreferenceCharacterDetectorAdapter = useMemo(
    () => characterDetectorAdapters.find(adapter => adapter.id === 'llm-coreference-chain') || null,
    [characterDetectorAdapters]
  )
  const focalizationCharacterDetectorAdapter = useMemo(
    () => characterDetectorAdapters.find(adapter => adapter.id === 'llm-focalization-characterization') || null,
    [characterDetectorAdapters]
  )
  const selectedCharacterDetectorUsesModel = selectedCharacterDetectorAdapter?.kind === 'llm'
  const selectedCharacterDetectorNeedsEntries = selectedCharacterDetectorAdapter?.id === 'name-alias-sweep'
  useEffect(() => {
    if (selectedCharacterDetectorRequiresPreparedExtraction && characterExtractionUnit === 'visible-ledger') {
      setCharacterExtractionUnit('witness')
      setCharacterDetectorFeedback(null)
    }
  }, [characterExtractionUnit, selectedCharacterDetectorRequiresPreparedExtraction])
  const visibleLedgerCharacterDetectionRows = useMemo<CharacterLayerDetectionRow[]>(() => {
    const rows: CharacterLayerDetectionRow[] = []
    resolvedRows.forEach(row => {
      scopeWitnesses.forEach(witness => {
        const text = getWorkspaceMappingText(row, witness.id, traditionWitnessUnitTextLookupByWitnessId)
        if (!text.trim()) {
          return
        }
        rows.push({
          witnessId: witness.id,
          witnessSigla: witness.sigla,
          rowId: row.id,
          rowLabel: getRowLabel(row),
          text,
        })
      })
    })
    return rows
  }, [resolvedRows, scopeWitnesses, traditionWitnessUnitTextLookupByWitnessId])
  const characterDetectionRows = useMemo<CharacterLayerDetectionRow[]>(() => {
    if (preparedCharacterExtraction?.isValid && characterExtractionWitness) {
      return buildPreparedCharacterDetectionRowsFromCalls({
        calls: isPreparedCoreferenceWindowExtraction(preparedCharacterExtraction)
          ? [...preparedCharacterExtraction.calls]
          : [preparedCharacterExtraction.call],
        witness: characterExtractionWitness,
      })
    }
    if (selectedCharacterDetectorRequiresPreparedExtraction) {
      return []
    }
    if (characterExtractionUnit !== 'visible-ledger') {
      return []
    }
    return visibleLedgerCharacterDetectionRows
  }, [
    characterExtractionUnit,
    characterExtractionWitness,
    preparedCharacterExtraction,
    selectedCharacterDetectorRequiresPreparedExtraction,
    visibleLedgerCharacterDetectionRows,
  ])
  const characterExtractionValidationMessage = useMemo(() => {
    if (selectedCharacterDetectorRequiresPreparedExtraction && characterExtractionUnit === 'visible-ledger') {
      return 'Select Witness, Scene, or Rows for coreference chains.'
    }
    if (characterExtractionUnit === 'visible-ledger') {
      return ''
    }
    if (characterExtractionUnit === 'rows' && characterExtractionRowSelection.rowIds.length === 0) {
      return 'Select a cell or row range first.'
    }
    if (characterExtractionUnit === 'scene' && characterExtractionSceneOptions.length === 0) {
      return 'No scene metadata in this view.'
    }
    if (selectedCharacterDetectorIsFocalization && focalizationFiguresForPreparedExtraction.length === 0) {
      return characterFocalizationFigureSource === 'coreference-v2'
        ? focalizationCoreferenceV2Message
        : focalizationCharacterIndexMessage
    }
    if (preparedCharacterExtraction && !preparedCharacterExtraction.isValid) {
      return preparedCharacterExtraction.validationIssues[0]?.message || 'Prepared extraction is not valid.'
    }
    if (!preparedCharacterExtraction) {
      return 'Select an extraction unit.'
    }
    return ''
  }, [
    characterExtractionRowSelection.rowIds.length,
    characterExtractionSceneOptions.length,
    characterExtractionUnit,
    characterFocalizationFigureSource,
    focalizationCharacterIndexMessage,
    focalizationCoreferenceV2Message,
    focalizationFiguresForPreparedExtraction.length,
    preparedCharacterExtraction,
    selectedCharacterDetectorIsFocalization,
    selectedCharacterDetectorRequiresPreparedExtraction,
  ])
  const characterDetectorEligibleCount = useMemo(
    () => activeCharacters.filter(character => character.status === 'confirmed' || character.status === 'edited').length,
    [activeCharacters]
  )
  const characterExtractionWindowCount = getPreparedCharacterExtractionWindowCount(preparedCharacterExtraction)
  const characterDetectorReadyLabel = selectedCharacterDetectorNeedsEntries
    ? `${characterDetectorEligibleCount} reviewed entr${characterDetectorEligibleCount === 1 ? 'y' : 'ies'} available`
    : selectedCharacterDetectorIsFocalization
      ? `${characterExtractionUnitLabel} · ${characterDetectionRows.length} row${characterDetectionRows.length === 1 ? '' : 's'} · ${focalizationFiguresForPreparedExtraction.length} ${focalizationFigureSourceLabel} figure${focalizationFiguresForPreparedExtraction.length === 1 ? '' : 's'}`
    : `${characterExtractionUnitLabel} · ${characterDetectionRows.length} row${characterDetectionRows.length === 1 ? '' : 's'}${characterExtractionWindowCount > 1 ? ` · ${characterExtractionWindowCount} windows` : ''}`
  const selectedCharacterDetectorRun = useMemo(
    () => characterDetectorRunHistory.find(run => run.runId === selectedCharacterDetectorRunId) || characterDetectorRunHistory[0] || null,
    [characterDetectorRunHistory, selectedCharacterDetectorRunId]
  )
  const selectedCoreferenceV2DiagnosticSummary = useMemo(
    () => selectedCharacterDetectorRun?.provenance.detectorId === 'llm-coreference-chain-v2'
      ? summarizeCoreferenceV2RunDiagnostics(selectedCharacterDetectorRun.diagnostics)
      : null,
    [selectedCharacterDetectorRun]
  )
  const selectedCoreferenceV2DiagnosticSummaryText = useMemo(
    () => selectedCoreferenceV2DiagnosticSummary
      ? formatCoreferenceV2RunDiagnosticSummary(selectedCoreferenceV2DiagnosticSummary)
      : '',
    [selectedCoreferenceV2DiagnosticSummary]
  )
  const characterDetectorCanCancel = isCharacterDetectorRunning && Boolean(activeCharacterDetectorRunRef.current)
  const characterDetectorCanResume = Boolean(
    !isCharacterDetectorRunning &&
    selectedCharacterDetectorRun &&
    (selectedCharacterDetectorRun.status === 'canceled' || selectedCharacterDetectorRun.status === 'stale')
  )
  const characterDetectorCanRetry = Boolean(
    !isCharacterDetectorRunning &&
    selectedCharacterDetectorRun &&
    selectedCharacterDetectorRun.runMode !== 'external-import' &&
    selectedCharacterDetectorRun.status !== 'running' &&
    selectedCharacterDetectorRun.status !== 'canceling'
  )
  const updateCharacterDetectorRunRecord = useEventCallback((
    runId: string,
    updater: (run: CharacterDetectorRunRecord) => CharacterDetectorRunRecord
  ) => {
    setCharacterDetectorRunHistory(previous => previous.map(run => (
      run.runId === runId ? updater(run) : run
    )))
  })
  const buildCharacterDetectorRunProvenance = useEventCallback((options: {
    detectorAdapter?: CharacterLayerDetectorAdapter | null
    detectionRows?: CharacterLayerDetectionRow[]
    preparedExtraction?: PreparedCharacterExtractionState | null
    preparedCoreferenceCalls?: PreparedSingleWitnessCoreferenceChainWindowCall[]
    focusedCharacter?: CharacterEntity | null
  } = {}): CharacterDetectorRunProvenance => {
    const activePreparedExtraction = options.preparedExtraction ?? preparedCharacterExtraction
    const activeDetectionRows = options.detectionRows || characterDetectionRows
    const activeDetectorAdapter = options.detectorAdapter || selectedCharacterDetectorAdapter
    const isWindowed = isPreparedCoreferenceWindowExtraction(activePreparedExtraction)
    const firstCall = options.preparedCoreferenceCalls?.[0] || (isWindowed ? activePreparedExtraction.calls[0] : activePreparedExtraction?.call)
    const modelCallContexts: CharacterDetectorRunModelCallContext[] = isWindowed
      ? (options.preparedCoreferenceCalls || activePreparedExtraction.calls).map(call => ({
        promptText: call.promptText,
        sourceText: call.sourceText,
        ...(call.promptVersion ? { promptVersion: call.promptVersion } : {}),
        sourceTextLength: call.sourceText.length,
        windowId: call.window.windowId,
        windowIndex: call.window.windowIndex,
        windowCount: call.window.windowCount,
        rowLabelRange: call.window.rowLabelRange,
        sourceRowIds: call.window.sourceRowIds,
      }))
      : firstCall?.promptText
        ? [{
          promptText: firstCall.promptText,
          sourceText: firstCall.sourceText,
          ...(firstCall.promptVersion ? { promptVersion: firstCall.promptVersion } : {}),
          sourceTextLength: firstCall.sourceText.length,
          sourceRowIds: activeDetectionRows.map(row => row.rowId),
        }]
        : []
    const rowLabels = activeDetectionRows.map(row => row.rowLabel || row.rowId)
    const extractionScene = characterExtractionSceneOptions.find(scene => scene.sceneId === characterExtractionSceneId)
    const witnessLabels = characterExtractionUnit === 'visible-ledger'
      ? Array.from(new Set(activeDetectionRows.map(row => row.witnessSigla || row.witnessId))).filter(Boolean)
      : characterExtractionWitness
        ? [`${characterExtractionWitness.sigla} (${characterExtractionWitness.id})`]
        : []
    const sourceLabel = characterExtractionUnit === 'scene'
      ? extractionScene?.title || extractionScene?.label || characterExtractionUnitLabel
      : characterExtractionUnit === 'rows'
        ? `${characterDetectionRows.length} selected row${characterDetectionRows.length === 1 ? '' : 's'}`
        : characterExtractionUnitLabel
    const promptPreview = firstCall?.promptText
      ? firstCall.promptText.slice(0, 900)
      : ''
    return {
      detectorId: activeDetectorAdapter?.id || characterDetectorId,
      detectorLabel: activeDetectorAdapter?.label || characterDetectorId,
      extractionUnit: characterExtractionUnit,
      sourceLabel,
      witnessLabels,
      ...(activeDetectorAdapter?.kind === 'llm' ? {
        modelId: characterModelId,
        scorer: activeDetectorAdapter?.label || 'Model detector',
        temperature: characterTemperature,
        thinkingLevel: characterThinkingLevel,
      } : {
        scorer: activeDetectorAdapter?.label || 'Local detector',
      }),
      ...(firstCall?.promptVersion ? { promptVersion: firstCall.promptVersion } : {}),
      ...(promptPreview ? { promptPreview } : {}),
      ...(modelCallContexts.length > 0 ? { modelCallContexts } : {}),
      ...(activeDetectorAdapter?.id === 'llm-focalization-characterization' ? {
        figureSource: characterFocalizationFigureSource,
        figureCount: focalizationFiguresForPreparedExtraction.length,
      } : {}),
      ...(options.focusedCharacter ? {
        focusedCharacterId: options.focusedCharacter.character_id,
        focusedCharacterName: options.focusedCharacter.display_name,
      } : {}),
      rowCount: activeDetectionRows.length,
      windowCount: isWindowed ? activePreparedExtraction.calls.length : 0,
      rowLabels,
    }
  })
  const characterDetectorRefinementSourceText = getCharacterDetectorRefinementSourceText(characterDetectorFeedback?.diagnostics)
  const characterDetectorCanRefine = Boolean(
    selectedCharacterDetectorUsesModel &&
    selectedCharacterDetectorAdapter &&
    characterDetectorFeedback?.detectorId === selectedCharacterDetectorAdapter.id &&
    characterDetectorRefinementSourceText &&
    characterDetectionRows.length > 0 &&
    !characterExtractionValidationMessage &&
    !isCharacterDetectorRunning
  )
  const characterDetectorFeedbackClass = characterDetectorFeedback?.tone === 'error'
    ? 'border-red-300 bg-red-50 text-red-800'
    : characterDetectorFeedback?.tone === 'warning'
      ? 'border-amber-300 bg-amber-50 text-amber-800'
      : characterDetectorFeedback?.tone === 'success'
        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
        : 'border-black/15 bg-white text-gray-500'
  const selectedCharacter = useMemo(
    () => activeCharacters.find(character => character.character_id === selectedCharacterId) || null,
    [activeCharacters, selectedCharacterId]
  )
  const selectedCharacterOccurrences = useMemo(
    () => selectedCharacter ? activeCharacterOccurrencesByCharacterId.get(selectedCharacter.character_id) || [] : [],
    [activeCharacterOccurrencesByCharacterId, selectedCharacter]
  )
  const selectedCharacterOccurrencesSorted = useMemo(
    () => [...selectedCharacterOccurrences].sort((left, right) => {
      const leftRowIndex = canonicalRowIndexById.get(left.locator.rowId) ?? Number.MAX_SAFE_INTEGER
      const rightRowIndex = canonicalRowIndexById.get(right.locator.rowId) ?? Number.MAX_SAFE_INTEGER
      if (leftRowIndex !== rightRowIndex) {
        return leftRowIndex - rightRowIndex
      }
      return left.locator.witnessId.localeCompare(right.locator.witnessId)
    }),
    [canonicalRowIndexById, selectedCharacterOccurrences]
  )
  const selectedCharacterCoreferenceEvidence = useMemo<FocusedCoreferenceOccurrenceInput[]>(
    () => selectedCharacterOccurrencesSorted.map(occurrence => {
      const witness = scopeWitnessById.get(occurrence.locator.witnessId)
      const row = rowById.get(occurrence.locator.rowId)
      return {
        witnessSigla: witness?.sigla || occurrence.locator.witnessId,
        rowLabel: row ? getRowLabel(row) : occurrence.locator.rowId,
        ...(occurrence.label ? { label: occurrence.label } : {}),
        ...(occurrence.summary ? { summary: occurrence.summary } : {}),
        roleLabels: occurrence.role_labels,
      }
    }),
    [rowById, scopeWitnessById, selectedCharacterOccurrencesSorted]
  )
  const focusedCharacterCoreferenceExtraction = useMemo(() => {
    if (!selectedCharacter || !characterExtractionScope || !characterExtractionWitness) {
      return null
    }
    try {
      return prepareLedgerSafeSingleWitnessCoreferenceChainWindowExtraction({
        projectId: projectLabel || 'local-project',
        workId: bookId || 'work',
        workTitle: ledgerName || contextLabel || bookId || 'Untitled work',
        bookId: bookId || 'book',
        chapterId,
        chapterLabel: contextLabel || snapshotScopeLabel || chapterId || 'Chapter',
        witnesses: scopeWitnesses,
        rows: resolvedRows,
        textLookupByWitnessId: traditionWitnessUnitTextLookupByWitnessId,
        chapterLayer: resolvedCharacterLayer,
        scope: characterExtractionScope,
      })
    } catch (error) {
      console.warn('Failed to prepare focused character coreference extraction', error)
      return null
    }
  }, [
    bookId,
    chapterId,
    characterExtractionScope,
    characterExtractionWitness,
    contextLabel,
    ledgerName,
    projectLabel,
    resolvedCharacterLayer,
    resolvedRows,
    scopeWitnesses,
    selectedCharacter,
    snapshotScopeLabel,
    traditionWitnessUnitTextLookupByWitnessId,
  ])
  const focusedCharacterCoreferenceCalls = useMemo(
    () => focusedCharacterCoreferenceExtraction?.isValid && selectedCharacter
      ? buildFocusedCoreferenceChainWindowCalls({
        calls: focusedCharacterCoreferenceExtraction.calls,
        character: {
          characterId: selectedCharacter.character_id,
          displayName: selectedCharacter.display_name,
          aliases: selectedCharacter.aliases || [],
          transliteration: selectedCharacter.metadata?.transliteration,
        },
        currentOccurrences: selectedCharacterCoreferenceEvidence,
      })
      : [],
    [focusedCharacterCoreferenceExtraction, selectedCharacter, selectedCharacterCoreferenceEvidence]
  )
  const focusedCharacterDetectionRows = useMemo(
    () => focusedCharacterCoreferenceCalls.length > 0 && characterExtractionWitness
      ? buildPreparedCharacterDetectionRowsFromCalls({
        calls: focusedCharacterCoreferenceCalls,
        witness: characterExtractionWitness,
      })
      : [],
    [characterExtractionWitness, focusedCharacterCoreferenceCalls]
  )
  const focusedCharacterValidationMessage = useMemo(() => {
    if (!selectedCharacter) {
      return 'Select a character first.'
    }
    if (characterExtractionUnit === 'visible-ledger') {
      return 'Select Witness, Scene, or Rows for focused chain improvement.'
    }
    if (!focusedCharacterCoreferenceExtraction) {
      return 'Select an extraction unit.'
    }
    if (!focusedCharacterCoreferenceExtraction.isValid) {
      return focusedCharacterCoreferenceExtraction.validationIssues[0]?.message || 'Focused extraction is not valid.'
    }
    if (focusedCharacterDetectionRows.length === 0) {
      return 'No rows available for focused chain improvement.'
    }
    return ''
  }, [
    characterExtractionUnit,
    focusedCharacterCoreferenceExtraction,
    focusedCharacterDetectionRows.length,
    selectedCharacter,
  ])
  const characterDetectorCanImproveSelectedChain = Boolean(
    !readOnly &&
    !isCharacterDetectorRunning &&
    selectedCharacter &&
    coreferenceCharacterDetectorAdapter &&
    focusedCharacterCoreferenceCalls.length > 0 &&
    focusedCharacterDetectionRows.length > 0 &&
    !focusedCharacterValidationMessage
  )
  const selectedCharacterFocalizationFigureId = useMemo(() => {
    if (!selectedCharacter) {
      return ''
    }
    const metadata = selectedCharacter.metadata || {}
    const candidateIds = new Set([
      selectedCharacter.character_id,
      metadata.figure_id,
      metadata.package_figure_id,
      metadata.coreference_v2_figure_id,
      metadata.coreference_figure_id,
    ].map(normalizeCharacterIndexIdentifier).filter(Boolean))
    const directMatch = focalizationFiguresForPreparedExtraction.find(figure => candidateIds.has(figure.id))
    if (directMatch) {
      return directMatch.id
    }
    const surfaceForms = new Set([
      selectedCharacter.display_name,
      ...(selectedCharacter.aliases || []),
    ].map(normalizeCharacterIndexIdentifier).filter(Boolean))
    const surfaceMatches = focalizationFiguresForPreparedExtraction.filter(figure => surfaceForms.has(normalizeCharacterIndexIdentifier(figure.anchor.name)))
    return surfaceMatches.length === 1 ? surfaceMatches[0].id : ''
  }, [focalizationFiguresForPreparedExtraction, selectedCharacter])
  const focusedCharacterLayerExtraction = useMemo(() => {
    if (!selectedCharacter || !selectedCharacterFocalizationFigureId || !characterExtractionScope || !characterExtractionWitness) {
      return null
    }
    try {
      return prepareLedgerSafeSingleWitnessFocalizationCharacterizationExtraction({
        projectId: projectLabel || 'local-project',
        workId: bookId || 'work',
        workTitle: ledgerName || contextLabel || bookId || 'Untitled work',
        bookId: bookId || 'book',
        chapterId,
        chapterLabel: contextLabel || snapshotScopeLabel || chapterId || 'Chapter',
        witnesses: scopeWitnesses,
        rows: resolvedRows,
        textLookupByWitnessId: traditionWitnessUnitTextLookupByWitnessId,
        chapterLayer: resolvedCharacterLayer,
        scope: characterExtractionScope,
        figures: focalizationFiguresForPreparedExtraction,
        focusFigureId: selectedCharacterFocalizationFigureId,
      })
    } catch (error) {
      console.warn('Failed to prepare focused character reading-layer extraction', error)
      return null
    }
  }, [
    bookId,
    chapterId,
    characterExtractionScope,
    characterExtractionWitness,
    contextLabel,
    focalizationFiguresForPreparedExtraction,
    ledgerName,
    projectLabel,
    resolvedCharacterLayer,
    resolvedRows,
    scopeWitnesses,
    selectedCharacter,
    selectedCharacterFocalizationFigureId,
    snapshotScopeLabel,
    traditionWitnessUnitTextLookupByWitnessId,
  ])
  const focusedCharacterLayerDetectionRows = useMemo(
    () => focusedCharacterLayerExtraction?.isValid && characterExtractionWitness
      ? buildPreparedCharacterDetectionRowsFromCalls({
        calls: [focusedCharacterLayerExtraction.call],
        witness: characterExtractionWitness,
      })
      : [],
    [characterExtractionWitness, focusedCharacterLayerExtraction]
  )
  const focusedCharacterLayerValidationMessage = useMemo(() => {
    if (!selectedCharacter) {
      return 'Select a character first.'
    }
    if (characterExtractionUnit === 'visible-ledger') {
      return 'Select Witness, Scene, or Rows for focused layer refinement.'
    }
    if (focalizationFiguresForPreparedExtraction.length === 0) {
      return characterFocalizationFigureSource === 'coreference-v2'
        ? focalizationCoreferenceV2Message
        : focalizationCharacterIndexMessage || 'No figures are available for focused layer refinement.'
    }
    if (!selectedCharacterFocalizationFigureId) {
      return `${selectedCharacter.display_name} is not mapped to the current figure source.`
    }
    if (!focusedCharacterLayerExtraction) {
      return 'Select an extraction unit.'
    }
    if (!focusedCharacterLayerExtraction.isValid) {
      return focusedCharacterLayerExtraction.validationIssues[0]?.message || 'Focused layer extraction is not valid.'
    }
    if (focusedCharacterLayerDetectionRows.length === 0) {
      return 'No rows available for focused layer refinement.'
    }
    return ''
  }, [
    characterExtractionUnit,
    characterFocalizationFigureSource,
    focalizationCharacterIndexMessage,
    focalizationCoreferenceV2Message,
    focalizationFiguresForPreparedExtraction.length,
    focusedCharacterLayerDetectionRows.length,
    focusedCharacterLayerExtraction,
    selectedCharacter,
    selectedCharacterFocalizationFigureId,
  ])
  const characterDetectorCanRefineSelectedLayers = Boolean(
    !readOnly &&
    !isCharacterDetectorRunning &&
    selectedCharacter &&
    focalizationCharacterDetectorAdapter &&
    focusedCharacterLayerExtraction?.isValid &&
    focusedCharacterLayerDetectionRows.length > 0 &&
    !focusedCharacterLayerValidationMessage
  )
  const focusedCharacterLayerModelExchangeContext = useMemo<CharacterExternalModelExchangeContext | null>(() => {
    if (
      !selectedCharacter ||
      !focalizationCharacterDetectorAdapter ||
      !focusedCharacterLayerExtraction?.isValid ||
      focusedCharacterLayerDetectionRows.length === 0
    ) {
      return null
    }
    return {
      detectorAdapter: focalizationCharacterDetectorAdapter,
      runMode: 'focus-layers',
      preparedExtraction: focusedCharacterLayerExtraction,
      calls: [focusedCharacterLayerExtraction.call],
      detectionRows: focusedCharacterLayerDetectionRows,
      validationMessage: focusedCharacterLayerValidationMessage,
      focusedCharacter: selectedCharacter,
    }
  }, [
    focalizationCharacterDetectorAdapter,
    focusedCharacterLayerDetectionRows,
    focusedCharacterLayerExtraction,
    focusedCharacterLayerValidationMessage,
    selectedCharacter,
  ])
  const focusedCharacterLayerRefineStatusLabel = useMemo(() => {
    if (!selectedCharacter) {
      return ''
    }
    if (readOnly) {
      return 'Refine unavailable: Read-only view.'
    }
    if (isCharacterDetectorRunning) {
      return 'Refine unavailable: Wait for the current Character Index run to finish.'
    }
    if (!focalizationCharacterDetectorAdapter) {
      return 'Refine unavailable: Focalization/characterization detector is unavailable.'
    }
    if (focusedCharacterLayerValidationMessage) {
      const reason = focusedCharacterLayerValidationMessage === 'Select Witness, Scene, or Rows for focused layer refinement.'
        ? 'Select Witness, Scene, or Rows.'
        : focusedCharacterLayerValidationMessage
      return `Refine unavailable: ${reason}`
    }
    const witnessLabel = characterExtractionWitness?.sigla || 'Witness'
    return `Ready: ${witnessLabel} · ${focusedCharacterLayerDetectionRows.length} row${focusedCharacterLayerDetectionRows.length === 1 ? '' : 's'} · ${selectedCharacter.display_name}.`
  }, [
    characterExtractionWitness?.sigla,
    focalizationCharacterDetectorAdapter,
    focusedCharacterLayerDetectionRows.length,
    focusedCharacterLayerValidationMessage,
    isCharacterDetectorRunning,
    readOnly,
    selectedCharacter,
  ])
  const characterExternalModelExchangeContext = useMemo<CharacterExternalModelExchangeContext | null>(() => {
    if (!selectedCharacterDetectorAdapter || selectedCharacterDetectorAdapter.kind !== 'llm') {
      return null
    }
    if (
      selectedCharacterDetectorAdapter.id === 'llm-focalization-characterization' &&
      focusedCharacterLayerModelExchangeContext
    ) {
      return focusedCharacterLayerModelExchangeContext
    }
    if (
      selectedCharacter &&
      selectedCharacterDetectorAdapter.id === 'llm-coreference-chain' &&
      coreferenceCharacterDetectorAdapter &&
      focusedCharacterCoreferenceExtraction?.isValid &&
      focusedCharacterCoreferenceCalls.length > 0 &&
      focusedCharacterDetectionRows.length > 0
    ) {
      return {
        detectorAdapter: coreferenceCharacterDetectorAdapter,
        runMode: 'improve-chain',
        preparedExtraction: focusedCharacterCoreferenceExtraction,
        calls: focusedCharacterCoreferenceCalls,
        detectionRows: focusedCharacterDetectionRows,
        validationMessage: focusedCharacterValidationMessage,
        focusedCharacter: selectedCharacter,
      }
    }
    if (!preparedCharacterExtraction?.isValid || characterDetectionRows.length === 0) {
      return null
    }
    return {
      detectorAdapter: selectedCharacterDetectorAdapter,
      runMode: 'suggest',
      preparedExtraction: preparedCharacterExtraction,
      calls: isPreparedCoreferenceWindowExtraction(preparedCharacterExtraction)
        ? [...preparedCharacterExtraction.calls]
        : [preparedCharacterExtraction.call],
      detectionRows: characterDetectionRows,
      validationMessage: characterExtractionValidationMessage,
      focusedCharacter: null,
    }
  }, [
    characterDetectionRows,
    characterExtractionValidationMessage,
    coreferenceCharacterDetectorAdapter,
    focusedCharacterCoreferenceCalls,
    focusedCharacterCoreferenceExtraction,
    focusedCharacterDetectionRows,
    focusedCharacterLayerModelExchangeContext,
    focusedCharacterValidationMessage,
    preparedCharacterExtraction,
    selectedCharacter,
    selectedCharacterDetectorAdapter,
  ])
  const buildCharacterExternalModelPayload = useCallback((context: CharacterExternalModelExchangeContext): CharacterIndexExternalModelPayload => {
    const outputContract = getCharacterDetectorOutputContract(context.detectorAdapter.id)
    const provenance = buildCharacterDetectorRunProvenance({
      detectorAdapter: context.detectorAdapter,
      detectionRows: context.detectionRows,
      preparedExtraction: context.preparedExtraction,
      preparedCoreferenceCalls: context.calls.filter((call): call is PreparedSingleWitnessCoreferenceChainWindowCall => (
        'window' in call && context.detectorAdapter.id === 'llm-coreference-chain'
      )),
      focusedCharacter: context.focusedCharacter || null,
    })
    return {
      export_kind: CHARACTER_INDEX_EXTERNAL_MODEL_PAYLOAD_KIND,
      schema_version: 1,
      created_at: new Date().toISOString(),
      detector_id: context.detectorAdapter.id,
      detector_label: context.detectorAdapter.label,
      run_mode: context.runMode,
      output_contract: outputContract,
      model: {
        model_id: characterModelId,
        temperature: characterTemperature,
        thinking_level: characterThinkingLevel,
      },
      provenance,
      ...(context.focusedCharacter ? {
        focused_character: {
          character_id: context.focusedCharacter.character_id,
          display_name: context.focusedCharacter.display_name,
          aliases: context.focusedCharacter.aliases || [],
        },
      } : {}),
      calls: context.calls.map((call, index) => ({
        call_id: 'window' in call ? call.window.windowId : `call-${index + 1}`,
        prompt_text: call.promptText,
        ...(call.promptVersion ? { prompt_version: call.promptVersion } : {}),
        output_contract: outputContract,
        source_text_length: call.sourceText.length,
        source_row_ids: call.scope.selectedRowIds,
        ...('window' in call ? { window: call.window as unknown as Record<string, unknown> } : {}),
        prepared_extraction_call: call,
      })),
      import_shape: {
        single_call: 'Paste the JSON result object returned by the model.',
        windowed: 'For multi-window payloads, paste {"windows":[{"window_id":"...","raw_extraction":{...}}]} or {"window_results":[...]} with one model result per window.',
      },
    }
  }, [
    buildCharacterDetectorRunProvenance,
    characterModelId,
    characterTemperature,
    characterThinkingLevel,
  ])
  const characterExternalModelPayload = useMemo<CharacterIndexExternalModelPayload | null>(() => (
    characterExternalModelExchangeContext
      ? buildCharacterExternalModelPayload(characterExternalModelExchangeContext)
      : null
  ), [buildCharacterExternalModelPayload, characterExternalModelExchangeContext])
  const focusedCharacterLayerExternalModelPayload = useMemo<CharacterIndexExternalModelPayload | null>(() => (
    focusedCharacterLayerModelExchangeContext && !focusedCharacterLayerModelExchangeContext.validationMessage
      ? buildCharacterExternalModelPayload(focusedCharacterLayerModelExchangeContext)
      : null
  ), [buildCharacterExternalModelPayload, focusedCharacterLayerModelExchangeContext])
  const canCopyFocusedCharacterLayerPrompt = Boolean(
    !isCharacterDetectorRunning &&
    focusedCharacterLayerExternalModelPayload &&
    focusedCharacterLayerExternalModelPayload.calls.length > 0
  )
  const selectedCharacterOccurrence = useMemo(
    () => selectedCharacterOccurrences.find(occurrence => occurrence.occurrence_id === selectedCharacterOccurrenceId) || null,
    [selectedCharacterOccurrenceId, selectedCharacterOccurrences]
  )
  const selectedCharacterRowIdSet = useMemo(
    () => new Set(selectedCharacterOccurrences.map(occurrence => occurrence.locator.rowId)),
    [selectedCharacterOccurrences]
  )
  const selectedCharacterOccurrenceByCellKey = useMemo(() => {
    const map = new Map<string, typeof selectedCharacterOccurrences>()
    selectedCharacterOccurrences.forEach(occurrence => {
      const key = buildCharacterCellKey(occurrence.locator)
      const bucket = map.get(key) || []
      bucket.push(occurrence)
      map.set(key, bucket)
    })
    return map
  }, [selectedCharacterOccurrences])
  const selectedCharacterOccurrencePresentationByCellKey = useMemo(() => {
    const map = new Map<string, {
      roleLabel: string
      spans: Array<{ localStart?: number; localEnd?: number; selected?: boolean }>
    }>()
    selectedCharacterOccurrenceByCellKey.forEach((occurrences, cellKey) => {
      map.set(cellKey, {
        roleLabel: occurrences
          .flatMap(occurrence => occurrence.role_labels)
          .filter(Boolean)
          .slice(0, 3)
          .join(', '),
        spans: occurrences.map(occurrence => ({
          localStart: occurrence.locator.localStart,
          localEnd: occurrence.locator.localEnd,
          selected: occurrence.occurrence_id === selectedCharacterOccurrenceId,
        })),
      })
    })
    return map
  }, [selectedCharacterOccurrenceByCellKey, selectedCharacterOccurrenceId])
  const characterCoverageById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof buildCharacterCoverageSummary>>()
    activeCharacters.forEach(character => {
      map.set(character.character_id, buildCharacterCoverageSummary({
        layer: resolvedCharacterLayer,
        characterId: character.character_id,
        witnessIds: nativeScopeWitnessIds,
      }))
    })
    return map
  }, [activeCharacters, nativeScopeWitnessIds, resolvedCharacterLayer])
  const activeWitnessCharacterIndexCharacterIds = useMemo(() => {
    const witnessId = activeWitness?.id || ''
    const ids = new Set<string>()
    if (!witnessId) {
      return ids
    }
    activeCharacterOccurrences.forEach(occurrence => {
      if (occurrence.locator.witnessId === witnessId) {
        ids.add(occurrence.character_id)
      }
    })
    resolvedCharacterLayer.notes.forEach(note => {
      const layer = note.metadata?.character_index_layer
      const characterId = typeof note.metadata?.character_id === 'string'
        ? note.metadata.character_id
        : ''
      if (
        characterId &&
        note.anchor.kind === 'span' &&
        note.anchor.locator.witnessId === witnessId &&
        (layer === 'focalization' || layer === 'characterization')
      ) {
        ids.add(characterId)
      }
    })
    return ids
  }, [activeCharacterOccurrences, activeWitness?.id, resolvedCharacterLayer.notes])
  const characterIndexListSceneId = characterExtractionSceneId || selectedLedgerSceneId || characterExtractionSceneOptions[0]?.sceneId || ''
  const characterIndexListScene = characterIndexListSceneId
    ? ledgerSceneHeaders.bySceneKey.get(characterIndexListSceneId) || null
    : null
  const characterIndexListSceneRowIds = useMemo(
    () => new Set(characterIndexListScene?.rowIds || []),
    [characterIndexListScene]
  )
  const characterIndexListSelectedRowIds = useMemo(
    () => new Set(characterExtractionRowSelection.rowIds),
    [characterExtractionRowSelection.rowIds]
  )
  const characterIndexListScopeEnabled = useMemo<Record<CharacterIndexListScope, boolean>>(() => ({
    ledger: true,
    witness: Boolean(activeWitness),
    scene: characterIndexListSceneRowIds.size > 0,
    rows: characterIndexListSelectedRowIds.size > 0,
  }), [activeWitness, characterIndexListSceneRowIds, characterIndexListSelectedRowIds])
  useEffect(() => {
    if (characterIndexListScope === 'witness' && !activeWitness) {
      return
    }
    if (!characterIndexListScopeEnabled[characterIndexListScope]) {
      setCharacterIndexListScope('ledger')
    }
  }, [activeWitness, characterIndexListScope, characterIndexListScopeEnabled])
  const characterMatchesListScope = useCallback((characterId: string, scope: CharacterIndexListScope): boolean => {
    if (scope === 'ledger') {
      return true
    }
    const occurrences = activeCharacterOccurrencesByCharacterId.get(characterId) || []
    if (scope === 'witness') {
      return activeWitnessCharacterIndexCharacterIds.has(characterId)
    }
    if (scope === 'scene') {
      return characterIndexListSceneRowIds.size > 0 && occurrences.some(occurrence => characterIndexListSceneRowIds.has(occurrence.locator.rowId))
    }
    return characterIndexListSelectedRowIds.size > 0 && occurrences.some(occurrence => characterIndexListSelectedRowIds.has(occurrence.locator.rowId))
  }, [
    activeCharacterOccurrencesByCharacterId,
    activeWitnessCharacterIndexCharacterIds,
    characterIndexListSceneRowIds,
    characterIndexListSelectedRowIds,
  ])
  const getCharacterListActivityTime = useCallback((characterId: string, fallback: string): number => {
    const occurrenceTimes = (activeCharacterOccurrencesByCharacterId.get(characterId) || [])
      .map(occurrence => Date.parse(occurrence.updated_at || occurrence.created_at || ''))
      .filter(Number.isFinite)
    const fallbackTime = Date.parse(fallback)
    return Math.max(
      Number.isFinite(fallbackTime) ? fallbackTime : 0,
      ...occurrenceTimes,
    )
  }, [activeCharacterOccurrencesByCharacterId])
  const sortedActiveCharacters = useMemo(
    () => [...activeCharacters].sort((left, right) => {
      const leftTime = getCharacterListActivityTime(left.character_id, left.updated_at || left.created_at || '')
      const rightTime = getCharacterListActivityTime(right.character_id, right.updated_at || right.created_at || '')
      if (leftTime !== rightTime) {
        return rightTime - leftTime
      }
      return left.display_name.localeCompare(right.display_name)
    }),
    [activeCharacters, getCharacterListActivityTime]
  )
  const characterIndexListCounts = useMemo<Record<CharacterIndexListScope, number>>(() => ({
    ledger: activeCharacters.length,
    witness: activeCharacters.filter(character => characterMatchesListScope(character.character_id, 'witness')).length,
    scene: activeCharacters.filter(character => characterMatchesListScope(character.character_id, 'scene')).length,
    rows: activeCharacters.filter(character => characterMatchesListScope(character.character_id, 'rows')).length,
  }), [activeCharacters, characterMatchesListScope])
  const visibleCharacterIndexCharacters = useMemo(
    () => sortedActiveCharacters.filter(character => characterMatchesListScope(character.character_id, characterIndexListScope)),
    [characterIndexListScope, characterMatchesListScope, sortedActiveCharacters]
  )
  const characterIndexListScopeSummary = useMemo(() => {
    if (characterIndexListScope === 'ledger') {
      return `${activeCharacters.length} total character${activeCharacters.length === 1 ? '' : 's'}`
    }
    if (characterIndexListScope === 'witness') {
      return `${activeWitness?.sigla || 'Witness'} · ${characterIndexListCounts.witness} character${characterIndexListCounts.witness === 1 ? '' : 's'}`
    }
    if (characterIndexListScope === 'scene') {
      return `${characterIndexListScene?.title || characterIndexListScene?.label || 'Scene'} · ${characterIndexListCounts.scene} character${characterIndexListCounts.scene === 1 ? '' : 's'}`
    }
    return `${characterExtractionRowSelection.rowIds.length || 0} selected row${characterExtractionRowSelection.rowIds.length === 1 ? '' : 's'} · ${characterIndexListCounts.rows} character${characterIndexListCounts.rows === 1 ? '' : 's'}`
  }, [
    activeCharacters.length,
    activeWitness?.sigla,
    characterExtractionRowSelection.rowIds.length,
    characterIndexListCounts,
    characterIndexListScene?.label,
    characterIndexListScene?.title,
    characterIndexListScope,
  ])
  const characterScopedBaseDisplayRows = useMemo(
    () => selectedCharacter
      ? transpositionDisplayModel.displayRows.filter(displayRow => selectedCharacterRowIdSet.has(displayRow.sourceRowId))
      : transpositionDisplayModel.displayRows,
    [selectedCharacter, selectedCharacterRowIdSet, transpositionDisplayModel.displayRows]
  )
  const characterScopedDisplayRows = useMemo(
    () => focusedReviewVisibleRowIds
      ? characterScopedBaseDisplayRows.filter(displayRow => focusedReviewVisibleRowIds.has(displayRow.sourceRowId))
      : characterScopedBaseDisplayRows,
    [characterScopedBaseDisplayRows, focusedReviewVisibleRowIds]
  )
  const selectedCharacterNotes = useMemo(
    () => selectedCharacter
      ? resolvedCharacterLayer.notes
        .filter(note => note.anchor.kind === 'character' && note.anchor.characterId === selectedCharacter.character_id)
        .slice(-3)
        .reverse()
      : [],
    [resolvedCharacterLayer.notes, selectedCharacter]
  )
  const activeSelectedCharacterSpan = useMemo(
    () => selectedCharacterSpan && selectedCell &&
      selectedCharacterSpan.rowId === selectedCell.rowId &&
      selectedCharacterSpan.witnessId === selectedCell.witnessId
      ? selectedCharacterSpan
      : null,
    [selectedCell, selectedCharacterSpan]
  )
  const characterNoteTargets = useMemo(() => {
    const targets: Array<{ id: CharacterNoteTargetKind; label: string }> = []
    if (selectedCharacter) {
      targets.push({ id: 'character', label: 'Character' })
    }
    if (activeSelectedCharacterSpan) {
      targets.push({ id: 'span', label: 'Span' })
    }
    if (selectedCell) {
      targets.push({ id: 'cell', label: 'Cell' })
      targets.push({ id: 'collation-row', label: 'Row' })
    }
    if (selectedRange?.rowIds.length) {
      targets.push({ id: 'row-selection', label: 'Range' })
    }
    if (selectedLedgerWitness) {
      targets.push({ id: 'witness', label: 'Witness' })
    }
    return targets
  }, [activeSelectedCharacterSpan, selectedCell, selectedCharacter, selectedLedgerWitness, selectedRange])

  useEffect(() => {
    if (selectedCharacterId && !activeCharacters.some(character => character.character_id === selectedCharacterId)) {
      setSelectedCharacterId('')
      setSelectedCharacterOccurrenceId('')
      return
    }
    if (
      selectedCharacterOccurrenceId &&
      !selectedCharacterOccurrences.some(occurrence => occurrence.occurrence_id === selectedCharacterOccurrenceId)
    ) {
      setSelectedCharacterOccurrenceId('')
    }
    if (!characterNoteTargets.some(target => target.id === characterNoteTarget)) {
      setCharacterNoteTarget(characterNoteTargets[0]?.id || 'character')
    }
  }, [activeCharacters, characterNoteTarget, characterNoteTargets, selectedCharacterId, selectedCharacterOccurrenceId, selectedCharacterOccurrences])

  const persistCharacterLayer = useEventCallback(async (layer: ChapterCharacterLayer) => {
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly) {
      return
    }
    await runBackupRelevantWrite(() => saveChapterCharacterLayer(layer))
  })

  const enqueueReaderCharacterLayerSave = useEventCallback((layer: ChapterCharacterLayer): Promise<void> => {
    readerAdjudicationPendingCountRef.current += 1
    const save = readerAdjudicationSaveQueueRef.current.then(() => persistCharacterLayer(layer))
    readerAdjudicationSaveQueueRef.current = save
      .catch(() => undefined)
      .finally(() => {
        readerAdjudicationPendingCountRef.current = Math.max(0, readerAdjudicationPendingCountRef.current - 1)
      })
    return save
  })

  const handleReaderAdjudicationCommand = useEventCallback(async (command: LectioAdjudicationCommand) => {
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly) {
      return
    }
    const nextLayer = applyLectioAdjudicationCommand(readerAdjudicationLayerRef.current, command, {
      reviewer: 'local-user',
    })
    readerAdjudicationLayerRef.current = nextLayer
    /* The reader must reflect the editor's hand immediately. Durable IndexedDB
       persistence remains serialized below, off the interaction's critical path. */
    setChapterCharacterLayer(nextLayer)
    await enqueueReaderCharacterLayerSave(nextLayer)
  })

  const handleRunCharacterDetector = useEventCallback(async (
    runMode: CharacterDetectorRunMode = 'suggest',
    runOptions: {
      resumedFromRunId?: string
      retriedFromRunId?: string
      detectorAdapter?: CharacterLayerDetectorAdapter | null
      preparedExtraction?: PreparedCharacterExtractionState | null
      preparedCoreferenceCalls?: PreparedSingleWitnessCoreferenceChainWindowCall[]
      detectionRows?: CharacterLayerDetectionRow[]
      focusedCharacter?: CharacterEntity | null
      validationMessage?: string
    } = {}
  ) => {
    const activeDetectorAdapter = runOptions.detectorAdapter || selectedCharacterDetectorAdapter
    const activePreparedExtraction = runOptions.preparedExtraction ?? preparedCharacterExtraction
    const activeDetectionRows = runOptions.detectionRows || characterDetectionRows
    const activeValidationMessage = runOptions.validationMessage ?? characterExtractionValidationMessage
    const activeDetectorUsesModel = activeDetectorAdapter?.kind === 'llm'
    const activeDetectorNeedsEntries = activeDetectorAdapter?.id === 'name-alias-sweep'
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly || isCharacterDetectorRunning || !activeDetectorAdapter) {
      return
    }
    const refinementSourceText = runMode === 'refine'
      ? getCharacterDetectorRefinementSourceText(characterDetectorFeedback?.diagnostics)
      : ''
    if (runMode === 'refine' && (!activeDetectorUsesModel || !refinementSourceText)) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Run a model suggestion first.',
      })
      return
    }
    if ((runMode === 'improve-chain' || runMode === 'focus-layers') && !runOptions.focusedCharacter) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Select a character first.',
      })
      return
    }
    if (activeDetectionRows.length === 0) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'No rows available.',
      })
      return
    }
    if (activeDetectorNeedsEntries && characterDetectorEligibleCount === 0) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Add or accept an entry first.',
      })
      return
    }
    if (activeValidationMessage) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: activeValidationMessage,
      })
      return
    }

    const sourceRunId = createCharacterDetectorRunId(activeDetectorAdapter.id)
    const controller = new AbortController()
    activeCharacterDetectorRunRef.current = {
      runId: sourceRunId,
      controller,
    }
    const startedAt = new Date().toISOString()
    const runRecord: CharacterDetectorRunRecord = {
      runId: sourceRunId,
      runMode,
      status: 'running',
      startedAt,
      ...(runOptions.resumedFromRunId ? { resumedFromRunId: runOptions.resumedFromRunId } : {}),
      ...(runOptions.retriedFromRunId ? { retriedFromRunId: runOptions.retriedFromRunId } : {}),
      provenance: buildCharacterDetectorRunProvenance({
        detectorAdapter: activeDetectorAdapter,
        detectionRows: activeDetectionRows,
        preparedExtraction: activePreparedExtraction,
        preparedCoreferenceCalls: runOptions.preparedCoreferenceCalls,
        focusedCharacter: runOptions.focusedCharacter,
      }),
    }
    setCharacterDetectorRunHistory(previous => [
      runRecord,
      ...previous.filter(candidate => candidate.runId !== runRecord.runId),
    ].slice(0, CHARACTER_DETECTOR_RUN_HISTORY_LIMIT))
    setSelectedCharacterDetectorRunId(sourceRunId)
    setIsCharacterDetectorRunning(true)
    setCharacterDetectorRunMode(runMode)
    setCharacterDetectorFeedback({
      tone: 'neutral',
      message: runMode === 'improve-chain'
        ? `Improving ${runOptions.focusedCharacter?.display_name || 'selected character'}...`
        : runMode === 'focus-layers'
          ? `Refining reading layers for ${runOptions.focusedCharacter?.display_name || 'selected character'}...`
          : runMode === 'refine' ? 'Refining entries...' : 'Suggesting entries...',
      detectorId: activeDetectorAdapter.id,
      diagnostics: characterDetectorFeedback?.diagnostics,
    })
    try {
      const preparedExtractionMetadata = (() => {
        if (
          !(
            activeDetectorAdapter.id === 'llm-character-index' ||
            activeDetectorAdapter.id === 'llm-coreference-chain' ||
            activeDetectorAdapter.id === 'llm-coreference-chain-v2' ||
            activeDetectorAdapter.id === 'llm-focalization-characterization'
          ) ||
          !activePreparedExtraction?.isValid
        ) {
          return {}
        }
        if (
          (activeDetectorAdapter.id === 'llm-coreference-chain' || activeDetectorAdapter.id === 'llm-coreference-chain-v2') &&
          isPreparedCoreferenceWindowExtraction(activePreparedExtraction)
        ) {
          const coreferenceCalls = runOptions.preparedCoreferenceCalls || activePreparedExtraction.calls
          const firstCall = coreferenceCalls[0]
          const outputContract = activeDetectorAdapter.id === 'llm-coreference-chain-v2'
            ? PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT
            : PACKAGE_COREFERENCE_CHAIN_OUTPUT_CONTRACT
          return {
            prompt_version: firstCall?.promptVersion,
            output_contract: outputContract,
            coreference_window_calls: coreferenceCalls,
            ...(runOptions.focusedCharacter ? {
              focused_character: {
                character_id: runOptions.focusedCharacter.character_id,
                display_name: runOptions.focusedCharacter.display_name,
                aliases: runOptions.focusedCharacter.aliases || [],
              },
            } : {}),
            extraction_scope: {
              ...(firstCall?.window.sourceScope || {}),
              unit: characterExtractionUnit,
              ...(runOptions.focusedCharacter ? {
                focused_character_id: runOptions.focusedCharacter.character_id,
                focused_character_name: runOptions.focusedCharacter.display_name,
              } : {}),
              windowed: true,
              window_count: coreferenceCalls.length,
              source_row_count: activeDetectionRows.length,
              source_text_length: coreferenceCalls.reduce((total, call) => total + call.sourceText.length, 0),
            },
          }
        }
        if (!isPreparedCoreferenceWindowExtraction(activePreparedExtraction)) {
          const outputContract = activeDetectorAdapter.id === 'llm-focalization-characterization'
            ? PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT
            : PACKAGE_CHARACTER_EVIDENCE_OUTPUT_CONTRACT
          return {
            prompt_text: activePreparedExtraction.call.promptText,
            prompt_version: activePreparedExtraction.call.promptVersion,
            output_contract: outputContract,
            prepared_extraction_call: activePreparedExtraction.call,
            ...(runOptions.focusedCharacter ? {
              focused_character: {
                character_id: runOptions.focusedCharacter.character_id,
                display_name: runOptions.focusedCharacter.display_name,
                aliases: runOptions.focusedCharacter.aliases || [],
              },
            } : {}),
            extraction_scope: {
              ...activePreparedExtraction.call.scope,
              unit: characterExtractionUnit,
              ...(runOptions.focusedCharacter ? {
                focused_character_id: runOptions.focusedCharacter.character_id,
                focused_character_name: runOptions.focusedCharacter.display_name,
              } : {}),
              ...(activeDetectorAdapter.id === 'llm-focalization-characterization' ? {
                figure_source: characterFocalizationFigureSource,
                figure_source_label: focalizationFigureSourceLabel,
                figure_count: focalizationFiguresForPreparedExtraction.length,
                focused_figure_id: 'focusFigureId' in activePreparedExtraction.call
                  ? activePreparedExtraction.call.focusFigureId
                  : undefined,
                character_index_figure_count: characterIndexFiguresForFocalization.length,
                coreference_v2_source_run_id: compatibleCoreferenceV2RunForFocalization?.runId,
                coreference_v2_figure_count: compatibleCoreferenceV2FiguresForFocalization.length,
              } : {}),
              source_row_count: activePreparedExtraction.call.scope.selectedRowIds.length,
              source_text_length: activePreparedExtraction.call.sourceText.length,
            },
          }
        }
        return {}
      })()
      const output = await activeDetectorAdapter.detect({
        chapterId,
        bookId,
        rows: activeDetectionRows,
        existingLayer: resolvedCharacterLayer,
        signal: controller.signal,
        metadata: {
          detector_id: activeDetectorAdapter.id,
          scope_id: snapshotScopeId,
          extraction_unit: characterExtractionUnit,
          detector_run_mode: runMode,
          ...(activeDetectorUsesModel ? {
            model_id: characterModelId,
            temperature: characterTemperature,
            thinking_level: characterThinkingLevel,
          } : {}),
          ...(runMode === 'refine' && refinementSourceText ? {
            refinement_source_text: refinementSourceText,
            refinement_instruction: buildCharacterIndexRefinementInstruction(activeDetectorAdapter.id),
          } : {}),
          ...preparedExtractionMetadata,
        },
      })
      if (controller.signal.aborted) {
        updateCharacterDetectorRunRecord(sourceRunId, run => ({
          ...run,
          status: 'canceled',
          completedAt: new Date().toISOString(),
          errorMessage: 'Canceled before results were applied.',
        }))
        setCharacterDetectorFeedback({
          tone: 'warning',
          message: 'Character index run canceled before applying results.',
          detectorId: activeDetectorAdapter.id,
        })
        return
      }
      if (activeCharacterDetectorRunRef.current?.runId !== sourceRunId) {
        updateCharacterDetectorRunRecord(sourceRunId, run => ({
          ...run,
          status: 'stale',
          completedAt: new Date().toISOString(),
          errorMessage: 'Ignored because a newer run became active.',
        }))
        return
      }
      const { layer, report } = mergeCharacterLayerDetectorOutput({
        existingLayer: resolvedCharacterLayer,
        output,
        detectorId: activeDetectorAdapter.id,
        sourceRunId,
      })
      setCharacterIndexListScope(CHARACTER_INDEX_LIST_SCOPE_FROM_EXTRACTION_UNIT[characterExtractionUnit])
      const isLayeredReadingRun = activeDetectorAdapter.id === 'llm-focalization-characterization'
      const layeredRunSourceWitnessId = (
        isLayeredReadingRun &&
        activePreparedExtraction &&
        !isPreparedCoreferenceWindowExtraction(activePreparedExtraction)
      )
        ? activePreparedExtraction.call.scope.witnessId
        : ''
      if (isLayeredReadingRun && layeredRunSourceWitnessId) {
        setTraditionSourceWitnessId(layeredRunSourceWitnessId)
      }
      const addedCount = report.added_character_ids.length + report.added_occurrence_ids.length + report.added_note_ids.length
      const skippedCount = report.skipped_occurrence_ids.length + report.suppressed_character_ids.length
      const layerNoteCount = output.notes?.length || 0
      const addedLayerNoteCount = report.added_note_ids.length
      const runFeedbackMessage = isLayeredReadingRun
        ? addedLayerNoteCount > 0
          ? `${addedLayerNoteCount} reading layer note${addedLayerNoteCount === 1 ? '' : 's'} added.`
          : layerNoteCount > 0
            ? `${layerNoteCount} reading layer note${layerNoteCount === 1 ? '' : 's'} already present.`
            : `No grounded reading layer spans${skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}.`
        : addedCount > 0
          ? `${report.added_occurrence_ids.length} occurrence suggestion${report.added_occurrence_ids.length === 1 ? '' : 's'} added.`
          : `No new suggestions${skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}.`
      const toastFeedbackMessage = isLayeredReadingRun
        ? addedLayerNoteCount > 0
          ? `Added ${addedLayerNoteCount} focalization/characterization layer note${addedLayerNoteCount === 1 ? '' : 's'}.`
          : layerNoteCount > 0
            ? 'Focalization/characterization layer notes are already present.'
            : 'No grounded focalization/characterization spans were found.'
        : addedCount > 0
          ? `Added ${report.added_occurrence_ids.length} character index occurrence suggestion${report.added_occurrence_ids.length === 1 ? '' : 's'}.`
          : 'Character index suggestions are up to date.'
      const completedRunRecord: CharacterDetectorRunRecord = {
        ...runRecord,
        status: 'completed',
        completedAt: new Date().toISOString(),
        resultSet: {
          characterCount: output.characters.length,
          occurrenceCount: output.occurrences.length,
          noteCount: output.notes?.length || 0,
          addedCharacterCount: report.added_character_ids.length,
          addedOccurrenceCount: report.added_occurrence_ids.length,
          addedNoteCount: report.added_note_ids.length,
          skippedOccurrenceCount: report.skipped_occurrence_ids.length,
        },
        diagnostics: output.metadata?.diagnostics,
      }
      const layerWithRunProvenance = await appendDurableCharacterDetectorRun(layer, completedRunRecord)
      await persistCharacterLayer(layerWithRunProvenance)
      updateCharacterDetectorRunRecord(sourceRunId, () => completedRunRecord)
      setCharacterDetectorFeedback({
        tone: addedCount > 0 ? 'success' : 'neutral',
        message: runFeedbackMessage,
        report,
        detectorId: activeDetectorAdapter.id,
        diagnostics: output.metadata?.diagnostics,
      })
      setToastMessage({ message: toastFeedbackMessage })
    } catch (error) {
      const canceled = controller.signal.aborted || isCharacterRunAbortError(error)
      const stale = activeCharacterDetectorRunRef.current?.runId !== sourceRunId && !canceled
      updateCharacterDetectorRunRecord(sourceRunId, run => ({
        ...run,
        status: canceled ? 'canceled' : stale ? 'stale' : 'failed',
        completedAt: new Date().toISOString(),
        errorMessage: canceled
          ? 'Canceled at a safe interruption point.'
          : error instanceof Error ? error.message : 'Detector failed.',
      }))
      if (stale) {
        return
      }
      if (!canceled) {
        console.error('Failed to run Variorum character detector', error)
      }
      setCharacterDetectorFeedback({
        tone: canceled ? 'warning' : 'error',
        message: canceled
          ? 'Character index run canceled.'
          : error instanceof Error ? error.message : 'Detector failed.',
        detectorId: activeDetectorAdapter.id,
      })
    } finally {
      if (activeCharacterDetectorRunRef.current?.runId === sourceRunId) {
        activeCharacterDetectorRunRef.current = null
        setIsCharacterDetectorRunning(false)
        setCharacterDetectorRunMode(null)
      }
    }
  })

  const handleCancelCharacterDetectorRun = useEventCallback(() => {
    const activeRun = activeCharacterDetectorRunRef.current
    if (!activeRun) {
      return
    }
    activeRun.controller.abort()
    updateCharacterDetectorRunRecord(activeRun.runId, run => ({
      ...run,
      status: 'canceling',
      errorMessage: 'Cancel requested; waiting for a safe interruption point.',
    }))
    setCharacterDetectorFeedback({
      tone: 'warning',
      message: 'Canceling at the next safe interruption point...',
      detectorId: selectedCharacterDetectorAdapter?.id,
    })
  })

  const handleResumeCharacterDetectorRun = useEventCallback(() => {
    if (!selectedCharacterDetectorRun || !characterDetectorCanResume) {
      return
    }
    void handleRunCharacterDetector(selectedCharacterDetectorRun.runMode, {
      resumedFromRunId: selectedCharacterDetectorRun.runId,
    })
  })

  const handleRetryCharacterDetectorRun = useEventCallback(() => {
    if (!selectedCharacterDetectorRun || !characterDetectorCanRetry) {
      return
    }
    void handleRunCharacterDetector(selectedCharacterDetectorRun.runMode, {
      retriedFromRunId: selectedCharacterDetectorRun.runId,
    })
  })

  const handleImproveSelectedCharacterChain = useEventCallback(() => {
    if (!selectedCharacter) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Select a character first.',
      })
      return
    }
    if (!coreferenceCharacterDetectorAdapter) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Coreference chain detector is unavailable.',
      })
      return
    }
    if (focusedCharacterValidationMessage) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: focusedCharacterValidationMessage,
        detectorId: coreferenceCharacterDetectorAdapter.id,
      })
      return
    }
    void handleRunCharacterDetector('improve-chain', {
      detectorAdapter: coreferenceCharacterDetectorAdapter,
      preparedExtraction: focusedCharacterCoreferenceExtraction,
      preparedCoreferenceCalls: focusedCharacterCoreferenceCalls,
      detectionRows: focusedCharacterDetectionRows,
      focusedCharacter: selectedCharacter,
      validationMessage: focusedCharacterValidationMessage,
    })
  })

  const handleRefineSelectedCharacterLayers = useEventCallback(() => {
    if (!selectedCharacter) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Select a character first.',
      })
      return
    }
    if (!focalizationCharacterDetectorAdapter) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Focalization/characterization detector is unavailable.',
      })
      return
    }
    if (focusedCharacterLayerValidationMessage) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: focusedCharacterLayerValidationMessage,
        detectorId: focalizationCharacterDetectorAdapter.id,
      })
      return
    }
    void handleRunCharacterDetector('focus-layers', {
      detectorAdapter: focalizationCharacterDetectorAdapter,
      preparedExtraction: focusedCharacterLayerExtraction,
      detectionRows: focusedCharacterLayerDetectionRows,
      focusedCharacter: selectedCharacter,
      validationMessage: focusedCharacterLayerValidationMessage,
    })
  })

  const handleCreateReaderCharacter = useEventCallback((
    requestedDisplayName: string,
    kind: CharacterIndexFigureKind = 'person',
  ): string => {
    const displayName = requestedDisplayName.trim()
    if (!runtimeEnableCharacterIndex || !displayName || readOnly || !chapterId) {
      return ''
    }
    const normalizedName = displayName.toLocaleLowerCase()
    const existing = activeCharacters.find(character => (
      character.display_name.trim().toLocaleLowerCase() === normalizedName ||
      (character.aliases || []).some(alias => alias.trim().toLocaleLowerCase() === normalizedName)
    ))
    if (existing) {
      setSelectedCharacterId(existing.character_id)
      setSelectedCharacterOccurrenceId('')
      setToastMessage({ message: `Attending the existing ${existing.display_name} record.` })
      return existing.character_id
    }
    const { layer, character } = createManualCharacter({
      layer: readerAdjudicationLayerRef.current,
      displayName,
      kind,
    })
    readerAdjudicationLayerRef.current = layer
    setChapterCharacterLayer(layer)
    void enqueueReaderCharacterLayerSave(layer).catch(error => {
      setToastMessage({ message: error instanceof Error ? error.message : 'The character record could not be saved.' })
    })
    setSelectedCharacterId(character.character_id)
    setSelectedCharacterOccurrenceId('')
    setLeftPanelTab('characters')
    setIsLeftPanelCollapsed(false)
    setToastMessage({ message: `Added ${character.display_name} to the character index.` })
    return character.character_id
  })

  const handleRenameReaderCharacter = useEventCallback((
    characterId: string,
    requestedDisplayName: string,
    requestedKind: CharacterIndexFigureKind = 'person',
  ): void => {
    const displayName = requestedDisplayName.trim()
    if (!runtimeEnableCharacterIndex || !displayName || readOnly || !chapterId) {
      throw new Error('The canonical name cannot be changed in this context.')
    }
    const currentLayer = readerAdjudicationLayerRef.current
    const character = currentLayer.characters.find(item => item.character_id === characterId)
    if (!character) {
      throw new Error('The character record is no longer available.')
    }
    const normalizedName = displayName.toLocaleLowerCase()
    const duplicate = currentLayer.characters.find(item => (
      item.character_id !== characterId &&
      item.status !== 'rejected' &&
      (
        item.display_name.trim().toLocaleLowerCase() === normalizedName ||
        (item.aliases || []).some(alias => alias.trim().toLocaleLowerCase() === normalizedName)
      )
    ))
    if (duplicate) {
      throw new Error(`${duplicate.display_name} already uses that canonical name.`)
    }
    if (character.display_name === displayName && character.kind === requestedKind) {
      return
    }
    const layer = reviewCharacterEntity({
      layer: currentLayer,
      characterId,
      nextStatus: 'edited',
      reviewer: 'local-user',
      updates: {
        display_name: displayName,
        kind: requestedKind,
        metadata: {
          ...(character.metadata || {}),
          figure_kind: requestedKind,
        },
      },
    })
    readerAdjudicationLayerRef.current = layer
    setChapterCharacterLayer(layer)
    void enqueueReaderCharacterLayerSave(layer).catch(error => {
      setToastMessage({ message: error instanceof Error ? error.message : 'The canonical name could not be saved.' })
    })
    setToastMessage({ message: `Updated ${displayName} as ${requestedKind}.` })
  })

  const handleDeleteReaderCharacter = useEventCallback((characterId: string): void => {
    if (!runtimeEnableCharacterIndex || readOnly || !chapterId) {
      throw new Error('The character record cannot be deleted in this context.')
    }
    const currentLayer = readerAdjudicationLayerRef.current
    const character = currentLayer.characters.find(item => (
      item.character_id === characterId && item.status !== 'rejected'
    ))
    if (!character) {
      throw new Error('The character record is no longer available.')
    }
    const layer = deleteCharacterRecord({
      layer: currentLayer,
      characterId,
      reviewer: 'local-user',
    })
    readerAdjudicationLayerRef.current = layer
    setChapterCharacterLayer(layer)
    void enqueueReaderCharacterLayerSave(layer).catch(error => {
      setToastMessage({ message: error instanceof Error ? error.message : 'The character deletion could not be saved.' })
    })
    setSelectedCharacterId('')
    setSelectedCharacterOccurrenceId('')
    setToastMessage({ message: `Deleted ${character.display_name} from the working character index.` })
  })

  const handleReviewCharacter = useEventCallback(async (
    characterId: string,
    nextStatus: 'confirmed' | 'edited' | 'rejected'
  ) => {
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly) {
      return
    }
    await reviewCharacter({
      chapterId,
      characterId,
      nextStatus,
      reviewer: 'local-user',
    })
    if (nextStatus === 'rejected' && selectedCharacterId === characterId) {
      setSelectedCharacterId('')
    }
  })

  const handleReviewCharacterOccurrence = useEventCallback(async (
    occurrenceId: string,
    nextStatus: 'confirmed' | 'edited' | 'rejected'
  ) => {
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly) {
      return
    }
    await reviewCharacterOccurrence({
      chapterId,
      occurrenceId,
      nextStatus,
      reviewer: 'local-user',
    })
  })

  const handleSelectCharacterOccurrence = useEventCallback((occurrence: CharacterOccurrence) => {
    if (
      !runtimeEnableCharacterIndex
      || (
        isMatrixContentApplying
        && diffEnabled
        && diffMode === 'apparatus'
        && apparatusScope === 'row'
      )
    ) {
      return
    }
    const nextCell = {
      rowId: occurrence.locator.rowId,
      witnessId: occurrence.locator.witnessId,
    }
    setSelectedCharacterOccurrenceId(occurrence.occurrence_id)
    setSelectedCell(nextCell)
    setSelectionAnchorCell(nextCell)
    setSelectedRange(null)

    const row = rowById.get(occurrence.locator.rowId)
    const hasOffsets = typeof occurrence.locator.localStart === 'number' && typeof occurrence.locator.localEnd === 'number'
    if (row && hasOffsets) {
      const text = getWorkspaceMappingText(row, occurrence.locator.witnessId, witnessUnitTextLookupByWitnessId)
      const localStart = Math.max(0, Math.min(text.length, Math.floor(occurrence.locator.localStart || 0)))
      const localEnd = Math.max(0, Math.min(text.length, Math.floor(occurrence.locator.localEnd || 0)))
      if (localEnd > localStart) {
        setSelectedCharacterSpan({
          witnessId: occurrence.locator.witnessId,
          rowId: occurrence.locator.rowId,
          localStart,
          localEnd,
          selectedText: text.slice(localStart, localEnd),
        })
        return
      }
    }
    setSelectedCharacterSpan(null)
  })

  const handleToggleCharacterOccurrenceLink = useEventCallback(async (targetOccurrenceId: string) => {
    if (!runtimeEnableCharacterIndex || !chapterId || readOnly || !selectedCharacterOccurrence || selectedCharacterOccurrence.occurrence_id === targetOccurrenceId) {
      return
    }
    const sourceId = selectedCharacterOccurrence.occurrence_id
    const target = selectedCharacterOccurrences.find(occurrence => occurrence.occurrence_id === targetOccurrenceId)
    if (!target) {
      return
    }
    const timestamp = new Date().toISOString()
    const sourceAlreadyLinked = (selectedCharacterOccurrence.linked_occurrence_ids || []).includes(targetOccurrenceId)
    const updateLinks = (occurrence: CharacterOccurrence): CharacterOccurrence => {
      if (occurrence.occurrence_id !== sourceId && occurrence.occurrence_id !== targetOccurrenceId) {
        return occurrence
      }
      const otherId = occurrence.occurrence_id === sourceId ? targetOccurrenceId : sourceId
      const currentLinks = occurrence.linked_occurrence_ids || []
      const nextLinks = sourceAlreadyLinked
        ? currentLinks.filter(linkedId => linkedId !== otherId)
        : Array.from(new Set([...currentLinks, otherId]))
      return {
        ...occurrence,
        linked_occurrence_ids: nextLinks,
        updated_at: timestamp,
      }
    }
    await persistCharacterLayer({
      ...resolvedCharacterLayer,
      occurrences: resolvedCharacterLayer.occurrences.map(updateLinks),
      updated_at: timestamp,
    })
    setToastMessage({ message: sourceAlreadyLinked ? 'Disconnected character index occurrences.' : 'Connected character index occurrences.' })
  })

  const handleCharacterCellTextSelection = useEventCallback((selection: CharacterSpanSelection) => {
    if (
      !runtimeEnableCharacterIndex
      || (
        isMatrixContentApplying
        && diffEnabled
        && diffMode === 'apparatus'
        && apparatusScope === 'row'
      )
    ) {
      return
    }
    setSelectedCharacterSpan(selection)
    setSelectedRange(null)
    setSelectedCell({
      rowId: selection.rowId,
      witnessId: selection.witnessId,
    })
    setSelectionAnchorCell({
      rowId: selection.rowId,
      witnessId: selection.witnessId,
    })
  })

  const handleMarkCharacterOccurrence = useEventCallback(async () => {
    if (!runtimeEnableCharacterIndex || !selectedCharacter || readOnly || !chapterId) {
      return
    }
    const locators = selectedRange?.rowIds.length
      ? selectedRange.rowIds.map(rowId => ({
        witnessId: selectedRange.witnessId,
        rowId,
      }))
      : selectedCell
        ? [{
          witnessId: selectedCell.witnessId,
          rowId: selectedCell.rowId,
          ...(activeSelectedCharacterSpan ? {
            localStart: activeSelectedCharacterSpan.localStart,
            localEnd: activeSelectedCharacterSpan.localEnd,
          } : {}),
        }]
        : []
    if (locators.length === 0) {
      return
    }
    const roleLabels = characterRoleDraft.split(',').map(label => label.trim()).filter(Boolean)
    let nextLayer = resolvedCharacterLayer
    let markedOccurrenceId = ''
    locators.forEach(locator => {
      const result = upsertManualCharacterOccurrence({
        layer: nextLayer,
        characterId: selectedCharacter.character_id,
        locator,
        roleLabels,
      })
      nextLayer = result.layer
      markedOccurrenceId = result.occurrence.occurrence_id
    })
    await persistCharacterLayer(nextLayer)
    setCharacterRoleDraft('')
    setSelectedCharacterSpan(null)
    if (markedOccurrenceId) {
      setSelectedCharacterOccurrenceId(markedOccurrenceId)
    }
    setToastMessage({ message: `Marked ${locators.length} ${locators.length === 1 ? 'row' : 'rows'} for ${selectedCharacter.display_name}.` })
  })

  const buildCharacterNoteAnchor = useEventCallback((): CharacterLayerNoteAnchor | null => {
    if (!runtimeEnableCharacterIndex) {
      return null
    }
    if (characterNoteTarget === 'character' && selectedCharacter) {
      return { kind: 'character', characterId: selectedCharacter.character_id }
    }
    if (characterNoteTarget === 'span' && activeSelectedCharacterSpan) {
      return {
        kind: 'span',
        locator: {
          witnessId: activeSelectedCharacterSpan.witnessId,
          rowId: activeSelectedCharacterSpan.rowId,
          localStart: activeSelectedCharacterSpan.localStart,
          localEnd: activeSelectedCharacterSpan.localEnd,
        },
      }
    }
    if (characterNoteTarget === 'cell' && selectedCell) {
      return { kind: 'witness-row', witnessId: selectedCell.witnessId, rowId: selectedCell.rowId }
    }
    if (characterNoteTarget === 'collation-row' && selectedCell) {
      return { kind: 'collation-row', rowId: selectedCell.rowId }
    }
    if (characterNoteTarget === 'row-selection' && selectedRange?.rowIds.length) {
      return { kind: 'row-selection', witnessId: selectedRange.witnessId, rowIds: selectedRange.rowIds }
    }
    if (characterNoteTarget === 'witness' && selectedLedgerWitness) {
      return { kind: 'witness', witnessId: selectedLedgerWitness.id }
    }
    return null
  })

  const handleAddCharacterNote = useEventCallback(async () => {
    const text = characterNoteDraft.trim()
    if (!runtimeEnableCharacterIndex || !text || readOnly || !chapterId) {
      return
    }
    const anchor = buildCharacterNoteAnchor()
    if (!anchor) {
      return
    }
    const { layer } = appendCharacterLayerNote({
      layer: resolvedCharacterLayer,
      anchor,
      text,
    })
    await persistCharacterLayer(layer)
    setCharacterNoteDraft('')
    setToastMessage({ message: 'Saved character-layer note.' })
  })

  const selectedBoundaryContext = useMemo(
    () => selectedLeftRowId ? (editableContextsByRowId.get(selectedLeftRowId) || null) : null,
    [editableContextsByRowId, selectedLeftRowId]
  )
  const isBoundaryTrayScopedToSelection = useMemo(
    () => Boolean(
      selectedLedgerRow &&
      selectedLedgerWitness &&
      activeWitness &&
      selectedLedgerWitness.id === activeWitness.id &&
      selectedLedgerCellState === 'mapped'
    ),
    [activeWitness, selectedLedgerCellState, selectedLedgerRow, selectedLedgerWitness]
  )
  const selectedBoundaryOptions = useMemo<BoundaryOption[]>(() => {
    if (!isBoundaryTrayScopedToSelection || !selectedLedgerRow || !activeWitness) {
      return []
    }

    const options: BoundaryOption[] = []
    const selectedRowIndex = resolvedRows.findIndex(row => row.id === selectedLedgerRow.id)
    const selectedRowIndexFromContext = preparedWitnessContext?.rowIndexById.get(selectedLedgerRow.id)
    const selectedRowIndexResolved = typeof selectedRowIndexFromContext === 'number'
      ? selectedRowIndexFromContext
      : selectedRowIndex
    if (selectedRowIndexResolved < 0) {
      return options
    }

    for (let index = selectedRowIndexResolved - 1; index >= 0; index -= 1) {
      const candidate = resolvedRows[index]
      if (!getWorkspaceMappingText(candidate, activeWitness.id, witnessUnitTextLookupByWitnessId)) {
        continue
      }
      const context = editableContextsByRowId.get(candidate.id)
      if (context && context.rightRowId === selectedLedgerRow.id) {
        options.push({
          id: `above:${candidate.id}`,
          label: 'Above selected unit',
          helperText: `${context.leftUnitLabel} → ${context.rightUnitLabel}`,
          context
        })
      }
      break
    }

    const belowContext = editableContextsByRowId.get(selectedLedgerRow.id)
    if (belowContext) {
      options.push({
        id: `below:${selectedLedgerRow.id}`,
        label: 'Below selected unit',
        helperText: `${belowContext.leftUnitLabel} → ${belowContext.rightUnitLabel}`,
        context: belowContext
      })
    }

    return options
  }, [activeWitness, editableContextsByRowId, isBoundaryTrayScopedToSelection, preparedWitnessContext, resolvedRows, selectedLedgerRow, witnessUnitTextLookupByWitnessId])
  const selectedEditabilityBlockReason = useMemo(() => {
    if (
      !activeWitnessEditabilityAudit ||
      !selectedLedgerRow ||
      !selectedLedgerWitness ||
      !activeWitness ||
      selectedLedgerWitness.id !== activeWitness.id ||
      selectedLedgerCellState !== 'mapped'
    ) {
      return ''
    }
    const boundaryBlock = activeWitnessEditabilityAudit.blockedBoundaries.find(block =>
      block.leftRowId === selectedLedgerRow.id || block.rightRowId === selectedLedgerRow.id
    )
    if (boundaryBlock) {
      return boundaryBlock.reason
    }
    const splitBlock = activeWitnessEditabilityAudit.blockedSplitRows.find(block => block.rowId === selectedLedgerRow.id)
    return splitBlock?.reason || ''
  }, [
    activeWitness,
    activeWitnessEditabilityAudit,
    selectedLedgerCellState,
    selectedLedgerRow,
    selectedLedgerWitness
  ])
  const selectedSplitContext = useMemo<LedgerSafeUnitSplitContext | null>(() => {
    if (
      !selectedLedgerRow ||
      !selectedLedgerWitness ||
      selectedLedgerWitness.id !== activeWitness?.id ||
      selectedLedgerCellState !== 'mapped' ||
      !activeWitness?.segmentation_result ||
      !activeWitness.uploaded_file?.content
    ) {
      return null
    }

    const prepared = preparedWitnessContext?.getSplitContext(selectedLedgerRow.id)
    if (prepared) {
      return prepared
    }

    return createLedgerSafeUnitSplitContext({
      rows: resolvedRows,
      witnessId: selectedLedgerWitness.id,
      rowId: selectedLedgerRow.id,
      segmentationResult: activeWitness.segmentation_result,
      sourceText: activeWitness.uploaded_file.content
    })
  }, [
    activeWitness,
    preparedWitnessContext,
    resolvedRows,
    selectedLedgerCellState,
    selectedLedgerRow,
    selectedLedgerWitness
  ])
  const selectedSplitContextKey = selectedSplitContext
    ? `${selectedSplitContext.witnessId}:${selectedSplitContext.rowId}:${selectedSplitContext.unitUuid}`
    : ''
  const previousSelectedSplitContextKeyRef = useRef('')

  const diffTokensByCellKey = useMemo(() => {
    const tokensByCellKey = new Map<string, RichOverlayToken[]>()
    if (!diffEnabled || diffMode !== 'inline' || !diffPivotWitness) {
      return tokensByCellKey
    }

    const nearMatchClassifier = (pivotToken: string, witnessToken: string) => (
      isMorphNearMatch(pivotToken, witnessToken, { ignoreDiacritics: true })
    )

      resolvedRows.forEach(row => {
      const pivotText = getComparisonMappingText(row, diffPivotWitness.id, witnessUnitTextLookupByWitnessId)
      if (!pivotText.trim()) {
        return
      }
      diffScopeWitnesses.forEach(witness => {
        if (witness.id === diffPivotWitness.id) {
          return
        }
        const witnessText = getComparisonMappingText(row, witness.id, witnessUnitTextLookupByWitnessId)
        if (!witnessText.trim()) {
          return
        }
        const tokenCount = tokenizeComparisonWords(witnessText).length
        if (tokenCount > 500) {
          return
        }
        try {
          const cacheKey = `inline-deletion-ghosts:${row.id}:${diffPivotWitness.id}:${witness.id}`
          const cached = diffOverlayCacheRef.current.get(cacheKey)
          const tokens = cached && cached.pivotText === pivotText && cached.witnessText === witnessText
            ? cached.tokens
            : buildRichInlineOverlay(pivotText, witnessText, {
              classifyNear: nearMatchClassifier,
              treatNearAsUnchanged: false,
              smallEditThreshold: 1,
              includeDeletionGhosts: true
            }).tokens
          diffOverlayCacheRef.current.set(cacheKey, {
            pivotText,
            witnessText,
            tokens
          })
          tokensByCellKey.set(`${row.id}:${witness.id}`, tokens)
        } catch (_error) {
          // No-op: diff overlay should never block editing.
        }
      })
    })
    return tokensByCellKey
  }, [diffEnabled, diffMode, diffPivotWitness, diffScopeWitnesses, resolvedRows, witnessUnitTextLookupByWitnessId])

  const liveApparatusSelectedRowId = (
    diffEnabled
    && diffMode === 'apparatus'
    && apparatusScope === 'row'
  )
    ? selectedLedgerRow?.id || null
    : null
  const apparatusSelectedRowIdRef = useRef<string | null>(liveApparatusSelectedRowId)
  useLayoutEffect(() => {
    if (!isMatrixContentApplying) {
      apparatusSelectedRowIdRef.current = liveApparatusSelectedRowId
    }
  }, [isMatrixContentApplying, liveApparatusSelectedRowId])
  const apparatusSelectedRowId = isMatrixContentApplying
    ? apparatusSelectedRowIdRef.current
    : liveApparatusSelectedRowId
  const apparatusDiffByCellKey = useMemo(() => {
    const diffByCellKey = new Map<string, {
      rowId: string
      pivotText: string
      witnessText: string
      spans: ApparatusSpan[]
      tokens: ApparatusOverlayToken[]
    }>()
    if (!diffEnabled || diffMode !== 'apparatus' || !diffPivotWitness) {
      return diffByCellKey
    }

    const selectedRowId = apparatusScope === 'row' ? apparatusSelectedRowId : null
    if (apparatusScope === 'row' && !selectedRowId) {
      return diffByCellKey
    }

    resolvedRows.forEach(row => {
      if (selectedRowId && row.id !== selectedRowId) {
        return
      }
      const pivotText = getComparisonMappingText(row, diffPivotWitness.id, witnessUnitTextLookupByWitnessId)
      if (!pivotText.trim()) {
        return
      }
      diffScopeWitnesses.forEach(witness => {
        if (witness.id === diffPivotWitness.id) {
          return
        }
        const witnessText = getComparisonMappingText(row, witness.id, witnessUnitTextLookupByWitnessId)
        if (!witnessText.trim()) {
          return
        }
        const tokenCount = tokenizeComparisonWords(pivotText).length + tokenizeComparisonWords(witnessText).length
        if (apparatusScope !== 'row' && tokenCount > 220) {
          return
        }
        try {
          const cacheKey = `${row.id}:${diffPivotWitness.id}:${witness.id}`
          const cached = apparatusOverlayCacheRef.current.get(cacheKey)
          const cachedMatches = cached && cached.pivotText === pivotText && cached.witnessText === witnessText
          const spans = cachedMatches ? cached.spans : buildApparatusSpans(pivotText, witnessText)
          const tokens = cachedMatches ? cached.tokens : buildApparatusOverlayTokens(pivotText, witnessText, spans, 'right')
          apparatusOverlayCacheRef.current.set(cacheKey, { pivotText, witnessText, spans, tokens })
          diffByCellKey.set(`${row.id}:${witness.id}`, {
            rowId: row.id,
            pivotText,
            witnessText,
            spans,
            tokens
          })
        } catch (_error) {
          // No-op: apparatus diff should not block editing.
        }
      })
    })

    return diffByCellKey
  }, [apparatusScope, apparatusSelectedRowId, diffEnabled, diffMode, diffPivotWitness, diffScopeWitnesses, resolvedRows, witnessUnitTextLookupByWitnessId])

  const apparatusPivotTokensByCellKey = useMemo(() => {
    const tokensByCellKey = new Map<string, ApparatusOverlayToken[]>()
    if (!diffEnabled || diffMode !== 'apparatus' || !diffPivotWitness) {
      return tokensByCellKey
    }
    const spansByRowId = new Map<string, { pivotText: string; spans: ApparatusSpan[] }>()
    apparatusDiffByCellKey.forEach(payload => {
      const existing = spansByRowId.get(payload.rowId)
      if (existing) {
        existing.spans.push(...payload.spans)
      } else {
        spansByRowId.set(payload.rowId, {
          pivotText: payload.pivotText,
          spans: payload.spans.slice()
        })
      }
    })
    spansByRowId.forEach((payload, rowId) => {
      const tokens = buildPivotApparatusTokens(payload.pivotText, payload.spans)
      if (tokens) {
        tokensByCellKey.set(`${rowId}:${diffPivotWitness.id}`, tokens)
      }
    })
    return tokensByCellKey
  }, [apparatusDiffByCellKey, diffEnabled, diffMode, diffPivotWitness])

  const preview = useMemo(() => {
    const sourceText = preparedWitnessContext?.sourceText || activeWitness?.uploaded_file?.content || ''
    if (inlineEditMode !== 'boundary' || !selectedBoundaryContext || !sourceText) {
      return null
    }
    return previewLedgerSafeBoundaryShift({
      context: selectedBoundaryContext,
      sourceText,
      deltaWords: pendingDeltaWords,
      segmentableWordSpans: preparedWitnessContext?.segmentableWordSpans
    })
  }, [activeWitness?.uploaded_file?.content, inlineEditMode, pendingDeltaWords, preparedWitnessContext, selectedBoundaryContext])

  const splitPreview = useMemo(() => {
    const sourceText = preparedWitnessContext?.sourceText || activeWitness?.uploaded_file?.content || ''
    if (
      inlineEditMode !== 'split' ||
      !selectedSplitContext ||
      selectedSplitBoundaryWordIndex === null ||
      !sourceText
    ) {
      return null
    }
    return previewLedgerSafeUnitSplit({
      context: selectedSplitContext,
      sourceText,
      splitBoundaryWordIndex: selectedSplitBoundaryWordIndex,
      segmentableWordSpans: preparedWitnessContext?.segmentableWordSpans
    })
  }, [
    activeWitness?.uploaded_file?.content,
    inlineEditMode,
    preparedWitnessContext,
    selectedSplitBoundaryWordIndex,
    selectedSplitContext
  ])

  useEffect(() => {
    if (!boundaryDragState || !selectedBoundaryContext) {
      return undefined
    }
    if (inlineEditMode !== 'boundary') {
      return undefined
    }

    let frameId: number | null = null
    let latestClientX = boundaryDragState.startX
    const applyPointerPosition = () => {
      frameId = null
      const deltaX = latestClientX - boundaryDragState.startX
      const wordDelta = Math.round(deltaX / 26)
      const nextBoundaryWordIndex = Math.max(
        selectedBoundaryContext.minBoundaryWordIndex,
        Math.min(selectedBoundaryContext.maxBoundaryWordIndex, boundaryDragState.startBoundaryWordIndex + wordDelta)
      )
      setPendingDeltaWords(nextBoundaryWordIndex - selectedBoundaryContext.boundaryWordIndex)
    }
    const handleMouseMove = (event: MouseEvent) => {
      latestClientX = event.clientX
      if (frameId === null) {
        frameId = window.requestAnimationFrame(applyPointerPosition)
      }
    }

    const handleMouseUp = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
        applyPointerPosition()
      }
      setBoundaryDragState(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [boundaryDragState, inlineEditMode, selectedBoundaryContext])

  useEffect(() => {
    if (!isBoundaryTrayScopedToSelection) {
      return
    }
    if (selectedBoundaryOptions.length === 0) {
      if (selectedLeftRowId) {
        setSelectedLeftRowId('')
        setPendingDeltaWords(0)
      }
      return
    }
    if (selectedBoundaryOptions.some(option => option.context.leftRowId === selectedLeftRowId)) {
      return
    }
    const preferred = selectedBoundaryOptions.find(option => option.id.startsWith('below:')) || selectedBoundaryOptions[0]
    setSelectedLeftRowId(preferred.context.leftRowId)
    setPendingDeltaWords(0)
  }, [isBoundaryTrayScopedToSelection, selectedBoundaryOptions, selectedLeftRowId])

  useEffect(() => {
    if (!selectedSplitContext) {
      previousSelectedSplitContextKeyRef.current = ''
      setSelectedSplitBoundaryWordIndex(null)
      if (inlineEditMode === 'split') {
        setInlineEditMode('idle')
      }
      return
    }
    if (previousSelectedSplitContextKeyRef.current !== selectedSplitContextKey) {
      previousSelectedSplitContextKeyRef.current = selectedSplitContextKey
      setSelectedSplitBoundaryWordIndex(null)
      return
    }
    if (
      selectedSplitBoundaryWordIndex !== null &&
      !selectedSplitContext.words.some(word => word.splitBoundaryWordIndex === selectedSplitBoundaryWordIndex)
    ) {
      setSelectedSplitBoundaryWordIndex(null)
    }
  }, [inlineEditMode, selectedSplitBoundaryWordIndex, selectedSplitContext, selectedSplitContextKey])

  const showToast = useCallback((
    message: string,
    action?: LedgerSafeToastMessage['action'],
    durationMs = 2200
  ) => {
    const nextToast: LedgerSafeToastMessage = { message, action }
    setToastMessage(nextToast)
    window.setTimeout(() => {
      setToastMessage(current => current === nextToast ? null : current)
    }, durationMs)
  }, [])

  const handleDownloadCharacterDetectorRunJson = useCallback(() => {
    if (!selectedCharacterDetectorRun) {
      return
    }
    try {
      const payload = {
        export_kind: 'character-index-run-history',
        exported_at: new Date().toISOString(),
        chapter_id: chapterId,
        book_id: bookId,
        selected_run_id: selectedCharacterDetectorRun.runId,
        selected_run: selectedCharacterDetectorRun,
        run_history: characterDetectorRunHistory,
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `character-index-runs-${formatCharacterRunDownloadFilePart(chapterId || 'chapter')}-${formatCharacterRunDownloadFilePart(selectedCharacterDetectorRun.runId)}.json`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(objectUrl)
      showToast('Downloaded character index run JSON')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to download character index run JSON')
    }
  }, [bookId, chapterId, characterDetectorRunHistory, selectedCharacterDetectorRun, showToast])

  const getCharacterModelCallSignature = (payload: CharacterIndexExternalModelPayload): string => (
    [payload.detector_id, ...payload.calls.map(call => `${call.call_id}:${call.source_text_length}`)].join('|')
  )

  const copyNextCharacterModelCall = useEventCallback(async (
    payload: CharacterIndexExternalModelPayload,
    singleCallMessage: string,
  ) => {
    if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') {
      setCharacterDetectorFeedback({
        tone: 'error',
        message: 'Clipboard write is unavailable in this browser.',
        detectorId: payload.detector_id,
      })
      return
    }
    const signature = getCharacterModelCallSignature(payload)
    const progress = characterModelCallProgress.signature === signature
      ? characterModelCallProgress
      : { signature, copiedCallId: null, importedCallIds: [] as string[] }
    const nextCall = payload.calls.find(call => !progress.importedCallIds.includes(call.call_id))
    if (!nextCall) {
      setCharacterDetectorFeedback({
        tone: 'success',
        message: `All ${payload.calls.length} calls in this extraction have been copied and their results applied.`,
        detectorId: payload.detector_id,
      })
      return
    }
    try {
      await navigator.clipboard.writeText(nextCall.prompt_text || '')
      setLastCopiedCharacterModelPayload(payload)
      setCharacterModelCallProgress({ ...progress, signature, copiedCallId: nextCall.call_id })
      const callPosition = payload.calls.indexOf(nextCall) + 1
      const message = payload.calls.length === 1
        ? singleCallMessage
        : `Copied prompt ${callPosition}/${payload.calls.length} (${nextCall.call_id}). Paste its result, then copy the next call.`
      setCharacterDetectorFeedback({
        tone: 'success',
        message,
        detectorId: payload.detector_id,
      })
      showToast(message)
    } catch (error) {
      setCharacterDetectorFeedback({
        tone: 'error',
        message: error instanceof Error ? error.message : 'Failed to copy model prompt.',
        detectorId: payload.detector_id,
      })
    }
  })

  const handleCopyCharacterModelPayload = useEventCallback(async () => {
    if (!characterExternalModelPayload || characterExternalModelExchangeContext?.validationMessage) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: characterExternalModelExchangeContext?.validationMessage || 'No prepared model prompt is available.',
        detectorId: selectedCharacterDetectorAdapter?.id,
      })
      return
    }
    await copyNextCharacterModelCall(characterExternalModelPayload, 'Copied exact model prompt to clipboard.')
  })

  const handleCopyFocusedCharacterLayerPrompt = useEventCallback(async () => {
    if (!focusedCharacterLayerExternalModelPayload || focusedCharacterLayerModelExchangeContext?.validationMessage) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: focusedCharacterLayerModelExchangeContext?.validationMessage || focusedCharacterLayerValidationMessage || 'No focused refine prompt is available.',
        detectorId: focalizationCharacterDetectorAdapter?.id,
      })
      return
    }
    await copyNextCharacterModelCall(focusedCharacterLayerExternalModelPayload, 'Copied focused refine prompt to clipboard.')
  })

  const handlePasteCharacterExternalModelResult = useEventCallback(async () => {
    if (readOnly || !chapterId || !runtimeEnableCharacterIndex) {
      return
    }
    if (isCharacterDetectorRunning) {
      setCharacterDetectorFeedback({
        tone: 'warning',
        message: 'Wait for the current Character Index run to finish first.',
        detectorId: selectedCharacterDetectorAdapter?.id,
      })
      return
    }
    if (typeof navigator === 'undefined' || typeof navigator.clipboard?.readText !== 'function') {
      setCharacterDetectorFeedback({
        tone: 'error',
        message: 'Clipboard read is unavailable in this browser.',
        detectorId: selectedCharacterDetectorAdapter?.id,
      })
      return
    }
    try {
      const clipboardText = await navigator.clipboard.readText()
      const pasted = parseCharacterIndexExternalClipboardJson(clipboardText)
      const pastedPayload = getExternalModelPayloadFromUnknown(pasted)
      const payload = pastedPayload || lastCopiedCharacterModelPayload
      if (!payload || payload.calls.length === 0) {
        setCharacterDetectorFeedback({
          tone: 'warning',
          message: 'Copy the exact Character Index prompt in this browser session, or paste a result envelope that includes its prompt context.',
          detectorId: selectedCharacterDetectorAdapter?.id,
        })
        return
      }

      const outputContract = payload.output_contract || getCharacterDetectorOutputContract(payload.detector_id)
      const focusedCharacter = payload.focused_character
        ? activeCharacters.find(character => character.character_id === payload.focused_character?.character_id) || null
        : null
      if (payload.focused_character && !focusedCharacter) {
        setCharacterDetectorFeedback({
          tone: 'warning',
          message: `The pasted result targets ${payload.focused_character.display_name}, but that character is no longer available in this layer.`,
          detectorId: payload.detector_id,
        })
        return
      }
      const createdAt = new Date().toISOString()
      const rawExtraction = extractCharacterIndexExternalRawExtraction(pasted)
      const resultWindows = extractCharacterIndexExternalResultWindows(pasted)
      const adaptCallPayload = (
        callPayload: CharacterIndexExternalModelPayload['calls'][number],
        raw: unknown,
      ): CharacterLayerDetectorOutput => adaptExternalCharacterIndexRawOutput({
        chapterId,
        createdAt,
        existingLayer: resolvedCharacterLayer,
        focusedCharacter: focusedCharacter || null,
        outputContract,
        preparedCall: callPayload.prepared_extraction_call as PreparedSingleWitnessCharacterIndexCall,
        rawExtraction: raw,
      })

      const payloadSignature = getCharacterModelCallSignature(payload)
      const payloadProgress = characterModelCallProgress.signature === payloadSignature
        ? characterModelCallProgress
        : { signature: payloadSignature, copiedCallId: null, importedCallIds: [] as string[] }
      let output: CharacterLayerDetectorOutput
      let ingestedCallIds: string[] = []
      if (payload.calls.length > 1 && resultWindows.length === 0) {
        /* A single call's result pasted on its own applies incrementally to the call
           whose prompt was copied last; the remaining calls stay pending. */
        const copiedPendingCall = payloadProgress.copiedCallId && !payloadProgress.importedCallIds.includes(payloadProgress.copiedCallId)
          ? payload.calls.find(call => call.call_id === payloadProgress.copiedCallId) || null
          : null
        if (!copiedPendingCall || !rawExtraction) {
          setCharacterDetectorFeedback({
            tone: 'warning',
            message: 'This extraction is windowed. Copy one call, paste that call\'s result, and repeat for each remaining call — or paste a {"windows":[...]} envelope with one raw extraction per window.',
            detectorId: payload.detector_id,
          })
          return
        }
        const adapted = adaptCallPayload(copiedPendingCall, rawExtraction)
        output = {
          ...adapted,
          metadata: {
            ...(adapted.metadata || {}),
            external_import: true,
            windowed: true,
            incremental_call_id: copiedPendingCall.call_id,
            window: copiedPendingCall.window,
            raw_extraction: rawExtraction,
          },
        }
        ingestedCallIds = [copiedPendingCall.call_id]
      } else if (payload.calls.length > 1) {
        const adaptedWindows: Array<{
          call: CharacterIndexExternalModelPayload['calls'][number]
          rawExtraction: unknown
          output: CharacterLayerDetectorOutput
        }> = []
        const failures: Array<Record<string, unknown>> = []
        resultWindows.forEach((windowResult, index) => {
          const matchingCall = payload.calls.find(call => {
            const window = isRecord(call.window) ? call.window : {}
            return (windowResult.windowId && (call.call_id === windowResult.windowId || window.windowId === windowResult.windowId)) ||
              (typeof windowResult.windowIndex === 'number' && window.windowIndex === windowResult.windowIndex) ||
              (!windowResult.windowId && typeof windowResult.windowIndex !== 'number' && index < payload.calls.length && payload.calls[index] === call)
          }) || payload.calls[index]
          if (!matchingCall) {
            failures.push({
              window_id: windowResult.windowId,
              window_index: windowResult.windowIndex,
              message: 'No matching prepared call for pasted window result.',
            })
            return
          }
          adaptedWindows.push({
            call: matchingCall,
            rawExtraction: windowResult.rawExtraction,
            output: adaptCallPayload(matchingCall, windowResult.rawExtraction),
          })
        })
        if (adaptedWindows.length === 0) {
          setCharacterDetectorFeedback({
            tone: 'warning',
            message: 'No pasted window result could be matched to the copied payload.',
            detectorId: payload.detector_id,
          })
          return
        }
        output = {
          characters: adaptedWindows.flatMap(item => item.output.characters),
          occurrences: adaptedWindows.flatMap(item => item.output.occurrences),
          notes: adaptedWindows.flatMap(item => item.output.notes || []),
          metadata: {
            output_contract: outputContract,
            external_import: true,
            windowed: true,
            window_count: payload.calls.length,
            successful_window_count: adaptedWindows.length,
            failed_window_count: failures.length,
            windows: adaptedWindows.map(item => ({
              window: item.call.window,
              diagnostics: item.output.metadata,
            })),
            window_failures: failures,
            raw_extraction: {
              windows: adaptedWindows.map(item => ({
                window: item.call.window,
                raw_extraction: item.rawExtraction,
              })),
            },
          },
        }
        ingestedCallIds = adaptedWindows.map(item => item.call.call_id)
      } else {
        const raw = resultWindows[0]?.rawExtraction || rawExtraction
        if (!raw) {
          setCharacterDetectorFeedback({
            tone: 'warning',
            message: 'Clipboard JSON did not contain a recognizable Character Index result.',
            detectorId: payload.detector_id,
          })
          return
        }
        output = adaptCallPayload(payload.calls[0], raw)
        output = {
          ...output,
          metadata: {
            ...(output.metadata || {}),
            external_import: true,
            raw_extraction: raw,
          },
        }
        ingestedCallIds = payload.calls[0] ? [payload.calls[0].call_id] : []
      }

      const sourceRunId = createCharacterDetectorRunId(`${payload.detector_id || 'external-character-index'}_external`)
      const { layer, report } = mergeCharacterLayerDetectorOutput({
        existingLayer: resolvedCharacterLayer,
        output,
        detectorId: payload.detector_id || 'external-character-index',
        sourceRunId,
      })
      const provenance = buildExternalImportFallbackProvenance({
        payload,
        runMode: 'external-import',
      })
      const runRecord: CharacterDetectorRunRecord = {
        runId: sourceRunId,
        runMode: 'external-import',
        status: 'completed',
        startedAt: createdAt,
        completedAt: new Date().toISOString(),
        provenance,
        resultSet: {
          characterCount: output.characters.length,
          occurrenceCount: output.occurrences.length,
          noteCount: output.notes?.length || 0,
          addedCharacterCount: report.added_character_ids.length,
          addedOccurrenceCount: report.added_occurrence_ids.length,
          addedNoteCount: report.added_note_ids.length,
          skippedOccurrenceCount: report.skipped_occurrence_ids.length,
        },
        diagnostics: output.metadata || {},
      }
      const layerWithRunProvenance = await appendDurableCharacterDetectorRun(layer, runRecord)
      await persistCharacterLayer(layerWithRunProvenance)
      setCharacterIndexListScope(CHARACTER_INDEX_LIST_SCOPE_FROM_EXTRACTION_UNIT[provenance.extractionUnit])
      const addedCount = report.added_character_ids.length + report.added_occurrence_ids.length + report.added_note_ids.length
      setCharacterDetectorRunHistory(previous => [
        runRecord,
        ...previous.filter(candidate => candidate.runId !== runRecord.runId),
      ].slice(0, CHARACTER_DETECTOR_RUN_HISTORY_LIMIT))
      setSelectedCharacterDetectorRunId(sourceRunId)
      const importedCallIds = Array.from(new Set([...payloadProgress.importedCallIds, ...ingestedCallIds]))
      if (payload.calls.length > 1) {
        setCharacterModelCallProgress({
          signature: payloadSignature,
          copiedCallId: payloadProgress.copiedCallId && importedCallIds.includes(payloadProgress.copiedCallId)
            ? null
            : payloadProgress.copiedCallId,
          importedCallIds,
        })
      }
      const remainingCallCount = payload.calls.length - importedCallIds.length
      const callProgressSuffix = payload.calls.length > 1
        ? remainingCallCount > 0
          ? ` ${importedCallIds.length}/${payload.calls.length} calls applied — copy the next prompt.`
          : ` All ${payload.calls.length} calls applied.`
        : ''
      setCharacterDetectorFeedback({
        tone: addedCount > 0 ? 'success' : 'neutral',
        message: (outputContract === PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT
          ? `${report.added_note_ids.length} imported reading layer note${report.added_note_ids.length === 1 ? '' : 's'} added.`
          : `${report.added_occurrence_ids.length} imported occurrence suggestion${report.added_occurrence_ids.length === 1 ? '' : 's'} added.`) + callProgressSuffix,
        report,
        detectorId: payload.detector_id,
        diagnostics: output.metadata || {},
      })
      showToast('Imported Character Index model result from clipboard')
      if (pastedPayload) {
        setLastCopiedCharacterModelPayload(pastedPayload)
      }
    } catch (error) {
      setCharacterDetectorFeedback({
        tone: 'error',
        message: error instanceof Error ? error.message : 'Failed to import clipboard result.',
        detectorId: selectedCharacterDetectorAdapter?.id,
      })
    }
  })

  const handleStartSceneTitleEdit = useCallback((sceneHeader: LedgerSafeSceneHeaderModel) => {
    setSceneTitleEditor({
      sceneKey: sceneHeader.sceneKey,
      title: sceneHeader.title,
      arabicTitle: sceneHeader.arabicTitle
    })
  }, [])

  const handleSaveSceneTitle = useEventCallback(async (
    sceneKey: string,
    values: { title: string; arabicTitle: string }
  ) => {
    if (isMatrixContentApplying || !chapterId) {
      return
    }
    const trimmedTitle = values.title.trim()
    const trimmedArabicTitle = values.arabicTitle.trim()
    const now = new Date().toISOString()
    const changedRows = chapterRows
      .map((row): AnchoredRow | null => {
        const scene = getAnchoredRowSceneMetadata(row)
        if (!scene || getLedgerSafeSceneKey(scene) !== sceneKey) {
          return null
        }
        const nextScene: AnchoredRowSceneMetadata = {
          ...scene
        }
        nextScene.title = trimmedTitle || getLedgerSafeSceneLabel(scene)
        if (trimmedArabicTitle) {
          nextScene.arabicTitle = trimmedArabicTitle
        } else {
          delete nextScene.arabicTitle
        }
        return {
          ...row,
          metadata: {
            ...(row.metadata || {}),
            scene: nextScene
          },
          updatedAt: now
        }
      })
      .filter((row): row is AnchoredRow => Boolean(row))

    if (changedRows.length === 0) {
      setSceneTitleEditor(null)
      return
    }

    setIsLedgerApplying(true)
    try {
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      setSceneTitleEditor(null)
      showToast('Scene label saved')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to save scene label')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const downloadTranslationAlignmentDiagnostics = useCallback((diagnostics: WorkbenchPropagationDiagnostics, witnessSigla: string) => {
    const payload = {
      witness_sigla: witnessSigla,
      downloaded_at: new Date().toISOString(),
      diagnostics,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = `translation-alignment-debug-${witnessSigla}-${new Date().toISOString().slice(0, 10)}.json`
    anchor.click()
    window.URL.revokeObjectURL(objectUrl)
  }, [])

  const handleGenerateWitnessPropagationProposal = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !activeWitness || resolvedRows.length === 0) {
      setWitnessPropagationError('No accepted segmentation is available in this context yet.')
      setWitnessPropagationProposal(null)
      return
    }

    setIsWitnessPropagationBusy(true)
    setWitnessPropagationError(null)
    setWitnessPropagationSuccess(null)
    setWitnessPropagationFailures([])
    setWitnessPropagationResumeState(null)
    setSelectedUnitRepairPreview(null)
    try {
      await clearWitnessPropagationResumeRecordSafely(activeWitness.id)
    } catch (error) {
      console.warn('Failed to clear persisted Variorum alignment preview state before regeneration.', error)
    }

    try {
      const activeAlignmentStrategy = getBatchAlignmentStrategy(activeWitness)
      if (activeAlignmentStrategy) {
        let batchIndex = 0
        let totalBatches = 1
        let nextStartCharFloor = 0
        let rowOffset = 0
        let cumulativeAssignments: TranslationCharAssignment[] = []
        const batchDiagnostics: WorkbenchPropagationDiagnostics[] = []
        let retryHistory = [] as NonNullable<TranslationBatchResumeState['retryHistory']>
        let translationFailed = false

        while (batchIndex < totalBatches) {
          let automaticRetryCount = 0
          while (true) {
            try {
              const batchResult = await requestWorkbenchTranslationBatchPreview({
                witnesses: scopeWitnesses,
                rows: resolvedRows,
                targetWitnessId: activeWitness.id,
                batchIndex,
                startCharFloor: nextStartCharFloor,
                rowOffset,
                alignmentStrategy: activeAlignmentStrategy,
                ...getLedgerSafeAlignmentModelRequest(propagationModelId, propagationThinkingLevel),
              })
              const partialFailure: WorkbenchPropagationFailure | null = batchResult.partial
                ? {
                    witnessId: activeWitness.id,
                    error: batchResult.error || `Failed to finish ${getBatchAlignmentLabel(activeAlignmentStrategy)} preview batch.`,
                    ...(batchResult.diagnostics ? { diagnostics: batchResult.diagnostics } : {}),
                  }
                : null
              const shouldRetryPartial = Boolean(
                partialFailure
                && automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
                && shouldAutoRetryTranslationBatchFailure({
                  error: partialFailure!.error,
                  diagnostics: partialFailure!.diagnostics,
                  partial: true,
                })
              )

              if (shouldRetryPartial) {
                automaticRetryCount += 1
                retryHistory = appendTranslationBatchRetryAttempt(
                  retryHistory,
                  buildTranslationRetryAttemptRecord({
                    batchIndex,
                    rowOffset,
                    error: partialFailure!.error,
                    diagnostics: partialFailure!.diagnostics,
                    failedRowId: batchResult.failedRowId,
                  }),
                )
                const retryResumeState: TranslationBatchResumeState = {
                  batchIndex,
                  rowOffset,
                  totalBatches: Math.max(totalBatches, batchResult.totalBatches),
                  nextStartCharFloor,
                  charAssignments: cumulativeAssignments,
                  batchDiagnostics,
                  retryHistory,
                  ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
                }
                setWitnessPropagationResumeState(retryResumeState)
                await persistWitnessPropagationResumeSafely({
                  witness: activeWitness,
                  resumeState: retryResumeState,
                  failure: partialFailure,
                })
                setWitnessPropagationSuccess(
                  `Retrying ${activeWitness.sigla} batch ${batchIndex + 1}/${Math.max(totalBatches, batchResult.totalBatches)} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`
                )
                continue
              }

              totalBatches = batchResult.totalBatches
              nextStartCharFloor = batchResult.nextStartCharFloor
              cumulativeAssignments = [...cumulativeAssignments, ...batchResult.charAssignments]
              if (batchResult.diagnostics) {
                batchDiagnostics.push(batchResult.diagnostics)
              }

              const derivedPlan = deriveTranslationPropagationPlan({
                text: activeWitness.uploaded_file?.content || '',
                charAssignments: cumulativeAssignments,
              })
              const coverageSummary = summarizeTranslationFragmentDiagnostics(derivedPlan.fragmentDiagnostics)
              const retrySummary = summarizeTranslationBatchRetryHistory(retryHistory)
              const referenceWitness = (
                (diffPivotWitness && diffPivotWitness.id !== activeWitness.id ? diffPivotWitness : null)
                || scopeWitnesses.find(witness => witness.id !== activeWitness.id)
                || activeWitness
              )
              const application = buildWitnessScopedPropagationApplication({
                proposal: {
                  targetWitness: activeWitness,
                  referenceWitness,
                  splitIndices: derivedPlan.splitIndices,
                  rowAssignments: derivedPlan.rowAssignments,
                },
                contextId: chapterId,
                rows: resolvedRows,
              })
              const completedBatches = getCompletedTranslationBatchCount({
                batchIndex: batchResult.nextBatchIndex,
                totalBatches,
              })

              setWitnessPropagationProposal({
                targetWitnessId: activeWitness.id,
                method: batchResult.method,
                strategy: activeAlignmentStrategy,
                segmentCount: derivedPlan.splitIndices.length + 1,
                splitIndices: derivedPlan.splitIndices,
                exemplarWitnessIds: [],
                rowAssignments: derivedPlan.rowAssignments,
                application,
                notes: mergeTranslationPlanNotes(
                  batchResult.complete
                    ? getBatchAlignmentReadyMessage(activeAlignmentStrategy)
                    : (
                        batchResult.partial
                          ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                          : `Previewed ${activeWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                      ),
                  retrySummary,
                  coverageSummary,
                ),
                translationProgress: {
                  completedBatches,
                  totalBatches,
                  complete: batchResult.complete,
                },
                diagnostics: combineTranslationAlignmentDiagnostics(batchDiagnostics),
              })
              setWitnessPropagationSuccess(
                batchResult.complete
                  ? `Prepared propagation replacement to ${activeWitness.sigla}`
                  : (
                      batchResult.partial
                        ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                        : `Previewed ${activeWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                    )
              )
              if (batchResult.partial && partialFailure) {
                const resumeState: TranslationBatchResumeState = {
                  batchIndex: batchResult.nextBatchIndex,
                  rowOffset: batchResult.nextRowOffset,
                  totalBatches,
                  nextStartCharFloor,
                  charAssignments: cumulativeAssignments,
                  batchDiagnostics,
                  retryHistory,
                  ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
                }
                setWitnessPropagationResumeState(resumeState)
                await persistWitnessPropagationResumeSafely({
                  witness: activeWitness,
                  resumeState,
                  failure: partialFailure,
                })
                setWitnessPropagationFailures([partialFailure])
                setWitnessPropagationError(partialFailure!.error)
                translationFailed = true
                break
              }
              if (!batchResult.complete) {
                await persistWitnessPropagationResumeSafely({
                  witness: activeWitness,
                  resumeState: {
                    batchIndex: batchResult.nextBatchIndex,
                    rowOffset: batchResult.nextRowOffset,
                    totalBatches,
                    nextStartCharFloor,
                    charAssignments: cumulativeAssignments,
                    batchDiagnostics,
                    retryHistory,
                  },
                })
              }
              batchIndex = batchResult.nextBatchIndex
              rowOffset = batchResult.nextRowOffset
              break
            } catch (error) {
              const failure: WorkbenchPropagationFailure = error instanceof WorkbenchTranslationBatchPreviewError
                ? {
                    witnessId: error.witnessId,
                    error: error.message,
                    ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
                  }
                : {
                    witnessId: activeWitness.id,
                    error: error instanceof Error ? error.message : `Failed to generate ${getBatchAlignmentLabel(activeAlignmentStrategy)} preview.`,
                  }
              const shouldRetryFailure = automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
                && shouldAutoRetryTranslationBatchFailure({
                  error: failure.error,
                  diagnostics: failure.diagnostics,
                })

              if (shouldRetryFailure) {
                automaticRetryCount += 1
                retryHistory = appendTranslationBatchRetryAttempt(
                  retryHistory,
                  buildTranslationRetryAttemptRecord({
                    batchIndex,
                    rowOffset,
                    error: failure.error,
                    diagnostics: failure.diagnostics,
                  }),
                )
                const retryResumeState: TranslationBatchResumeState = {
                  batchIndex,
                  rowOffset,
                  totalBatches,
                  nextStartCharFloor,
                  charAssignments: cumulativeAssignments,
                  batchDiagnostics,
                  retryHistory,
                }
                setWitnessPropagationResumeState(retryResumeState)
                await persistWitnessPropagationResumeSafely({
                  witness: activeWitness,
                  resumeState: retryResumeState,
                  failure,
                })
                setWitnessPropagationSuccess(
                  `Retrying ${activeWitness.sigla} batch ${batchIndex + 1}/${totalBatches} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`
                )
                continue
              }

              const failureResumeState: TranslationBatchResumeState = {
                batchIndex,
                rowOffset,
                totalBatches,
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
              }
              setWitnessPropagationResumeState(failureResumeState)
              await persistWitnessPropagationResumeSafely({
                witness: activeWitness,
                resumeState: failureResumeState,
                failure,
              })
              setWitnessPropagationFailures([failure])
              setWitnessPropagationError(failure.error)
              translationFailed = true
              break
            }
          }

          if (translationFailed) {
            break
          }
        }
        if (translationFailed) {
          setIsWitnessPropagationBusy(false)
          return
        }
        setWitnessPropagationResumeState(null)
        await clearWitnessPropagationResumeRecordSafely(activeWitness.id)
        return
      }

      const previewResult = await requestWorkbenchPropagationPreview({
        witnesses: scopeWitnesses,
        rows: resolvedRows,
        targetWitnessIds: [activeWitness.id],
        ...getLedgerSafeAlignmentModelRequest(propagationModelId, propagationThinkingLevel),
      })
      setWitnessPropagationFailures(previewResult.failures)
      const plan = previewResult.plans.find(candidate => candidate.witnessId === activeWitness.id)
      if (!plan) {
        const failure = previewResult.failures.find(candidate => candidate.witnessId === activeWitness.id)
        if (failure) {
          throw new WorkbenchPropagationPreviewError(failure.error, [failure])
        }
        throw new Error(`No propagation proposal was returned for ${activeWitness.sigla}`)
      }

      const referenceWitness = (
        plan.exemplarWitnessIds
          .map(exemplarWitnessId => scopeWitnesses.find(witness => witness.id === exemplarWitnessId) || null)
          .find((witness): witness is Witness => Boolean(witness))
        || (diffPivotWitness && diffPivotWitness.id !== activeWitness.id ? diffPivotWitness : null)
        || scopeWitnesses.find(witness => witness.id !== activeWitness.id)
        || activeWitness
      )

      const application = buildWitnessScopedPropagationApplication({
        proposal: {
          targetWitness: activeWitness,
          referenceWitness,
          splitIndices: plan.splitIndices,
          rowAssignments: plan.rowAssignments
        },
        contextId: chapterId,
        rows: resolvedRows
      })

      setWitnessPropagationProposal({
        targetWitnessId: activeWitness.id,
        method: plan.method,
        segmentCount: plan.splitIndices.length + 1,
        splitIndices: plan.splitIndices,
        exemplarWitnessIds: plan.exemplarWitnessIds,
        rowAssignments: plan.rowAssignments,
        application,
        diagnostics: plan.diagnostics,
      })
      setWitnessPropagationSuccess(`Prepared propagation replacement to ${activeWitness.sigla}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate propagation proposal'
      setWitnessPropagationError(message)
      setWitnessPropagationProposal(null)
      if (error instanceof WorkbenchPropagationPreviewError) {
        setWitnessPropagationFailures(error.failures)
      }
      if (error instanceof WorkbenchTranslationBatchPreviewError) {
        setWitnessPropagationFailures([
          {
            witnessId: error.witnessId,
            error: error.message,
            ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
          }
        ])
      }
    } finally {
      setIsWitnessPropagationBusy(false)
    }
  })

  const handleResumeWitnessPropagationProposal = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !activeWitness || !witnessPropagationProposal || !witnessPropagationResumeState) {
      return
    }

    setIsWitnessPropagationBusy(true)
    setWitnessPropagationError(null)
    setWitnessPropagationSuccess(
      `Resuming ${activeWitness.sigla} batch ${witnessPropagationResumeState.batchIndex + 1}/${witnessPropagationResumeState.totalBatches}.`
    )
    setWitnessPropagationFailures(previous => previous.filter(candidate => candidate.witnessId !== activeWitness.id))

    try {
      const activeAlignmentStrategy = getBatchAlignmentStrategyFromMethod(witnessPropagationProposal.method, activeWitness)
      let batchIndex = witnessPropagationResumeState.batchIndex
      let totalBatches = Math.max(witnessPropagationResumeState.totalBatches, batchIndex + 1)
      let nextStartCharFloor = witnessPropagationResumeState.nextStartCharFloor
      let rowOffset = getTranslationResumeRowOffset(witnessPropagationResumeState)
      let cumulativeAssignments = [...witnessPropagationResumeState.charAssignments]
      const batchDiagnostics = [...witnessPropagationResumeState.batchDiagnostics]
      let retryHistory = [...(witnessPropagationResumeState.retryHistory || [])]
      let translationFailed = false

      while (batchIndex < totalBatches) {
        let automaticRetryCount = 0
        while (true) {
          try {
            const batchResult = await requestWorkbenchTranslationBatchPreview({
              witnesses: scopeWitnesses,
              rows: resolvedRows,
              targetWitnessId: activeWitness.id,
              batchIndex,
              startCharFloor: nextStartCharFloor,
              rowOffset,
              alignmentStrategy: activeAlignmentStrategy,
              ...getLedgerSafeAlignmentModelRequest(propagationModelId, propagationThinkingLevel),
            })
            const partialFailure: WorkbenchPropagationFailure | null = batchResult.partial
              ? {
                  witnessId: activeWitness.id,
                  error: batchResult.error || `Failed to finish ${getBatchAlignmentLabel(activeAlignmentStrategy)} preview batch.`,
                  ...(batchResult.diagnostics ? { diagnostics: batchResult.diagnostics } : {}),
                }
              : null
            const shouldRetryPartial = Boolean(
              partialFailure
              && automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
              && shouldAutoRetryTranslationBatchFailure({
                error: partialFailure!.error,
                diagnostics: partialFailure!.diagnostics,
                partial: true,
              })
            )

            if (shouldRetryPartial) {
              automaticRetryCount += 1
              retryHistory = appendTranslationBatchRetryAttempt(
                retryHistory,
                buildTranslationRetryAttemptRecord({
                  batchIndex,
                  rowOffset,
                  error: partialFailure!.error,
                  diagnostics: partialFailure!.diagnostics,
                  failedRowId: batchResult.failedRowId,
                }),
              )
              const retryResumeState: TranslationBatchResumeState = {
                batchIndex,
                rowOffset,
                totalBatches: Math.max(totalBatches, batchResult.totalBatches),
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
                ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
              }
              setWitnessPropagationResumeState(retryResumeState)
              await persistWitnessPropagationResumeSafely({
                witness: activeWitness,
                resumeState: retryResumeState,
                failure: partialFailure,
              })
              setWitnessPropagationSuccess(
                `Retrying ${activeWitness.sigla} batch ${batchIndex + 1}/${Math.max(totalBatches, batchResult.totalBatches)} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`
              )
              continue
            }

            totalBatches = batchResult.totalBatches
            nextStartCharFloor = batchResult.nextStartCharFloor
            cumulativeAssignments = [...cumulativeAssignments, ...batchResult.charAssignments]
            if (batchResult.diagnostics) {
              batchDiagnostics.push(batchResult.diagnostics)
            }

            const derivedPlan = deriveTranslationPropagationPlan({
              text: activeWitness.uploaded_file?.content || '',
              charAssignments: cumulativeAssignments,
            })
            const coverageSummary = summarizeTranslationFragmentDiagnostics(derivedPlan.fragmentDiagnostics)
            const retrySummary = summarizeTranslationBatchRetryHistory(retryHistory)
            const referenceWitness = (
              (diffPivotWitness && diffPivotWitness.id !== activeWitness.id ? diffPivotWitness : null)
              || scopeWitnesses.find(witness => witness.id !== activeWitness.id)
              || activeWitness
            )
            const application = buildWitnessScopedPropagationApplication({
              proposal: {
                targetWitness: activeWitness,
                referenceWitness,
                splitIndices: derivedPlan.splitIndices,
                rowAssignments: derivedPlan.rowAssignments,
              },
              contextId: chapterId,
              rows: resolvedRows,
            })
            const completedBatches = getCompletedTranslationBatchCount({
              batchIndex: batchResult.nextBatchIndex,
              totalBatches,
            })

              setWitnessPropagationProposal({
                targetWitnessId: activeWitness.id,
                method: batchResult.method,
                strategy: activeAlignmentStrategy,
                segmentCount: derivedPlan.splitIndices.length + 1,
              splitIndices: derivedPlan.splitIndices,
              exemplarWitnessIds: [],
              rowAssignments: derivedPlan.rowAssignments,
              application,
              notes: mergeTranslationPlanNotes(
                batchResult.complete
                  ? getBatchAlignmentReadyMessage(activeAlignmentStrategy)
                  : (
                      batchResult.partial
                        ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                        : `Previewed ${activeWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                    ),
                retrySummary,
                coverageSummary,
              ),
              translationProgress: {
                completedBatches,
                totalBatches,
                complete: batchResult.complete,
              },
              diagnostics: combineTranslationAlignmentDiagnostics(batchDiagnostics),
            })
            setWitnessPropagationSuccess(
              batchResult.complete
                ? `Prepared propagation replacement to ${activeWitness.sigla}`
                : (
                    batchResult.partial
                      ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                      : `Previewed ${activeWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                  )
            )
            if (batchResult.partial && partialFailure) {
              const nextResumeState: TranslationBatchResumeState = {
                batchIndex: batchResult.nextBatchIndex,
                rowOffset: batchResult.nextRowOffset,
                totalBatches,
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
                ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
              }
              setWitnessPropagationResumeState(nextResumeState)
              await persistWitnessPropagationResumeSafely({
                witness: activeWitness,
                resumeState: nextResumeState,
                failure: partialFailure,
              })
              setWitnessPropagationFailures([partialFailure])
              setWitnessPropagationError(partialFailure!.error)
              setWitnessPropagationSuccess(null)
              translationFailed = true
              break
            }
            if (!batchResult.complete) {
              await persistWitnessPropagationResumeSafely({
                witness: activeWitness,
                resumeState: {
                  batchIndex: batchResult.nextBatchIndex,
                  rowOffset: batchResult.nextRowOffset,
                  totalBatches,
                  nextStartCharFloor,
                  charAssignments: cumulativeAssignments,
                  batchDiagnostics,
                  retryHistory,
                },
              })
            }
            batchIndex = batchResult.nextBatchIndex
            rowOffset = batchResult.nextRowOffset
            break
          } catch (error) {
            const failure: WorkbenchPropagationFailure = error instanceof WorkbenchTranslationBatchPreviewError
              ? {
                  witnessId: error.witnessId,
                  error: error.message,
                  ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
                }
              : {
                  witnessId: activeWitness.id,
                  error: error instanceof Error ? error.message : `Failed to generate ${getBatchAlignmentLabel(activeAlignmentStrategy)} preview.`,
                }
            const shouldRetryFailure = automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
              && shouldAutoRetryTranslationBatchFailure({
                error: failure.error,
                diagnostics: failure.diagnostics,
              })

            if (shouldRetryFailure) {
              automaticRetryCount += 1
              retryHistory = appendTranslationBatchRetryAttempt(
                retryHistory,
                buildTranslationRetryAttemptRecord({
                  batchIndex,
                  rowOffset,
                  error: failure.error,
                  diagnostics: failure.diagnostics,
                }),
              )
              const retryResumeState: TranslationBatchResumeState = {
                batchIndex,
                rowOffset,
                totalBatches,
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
              }
              setWitnessPropagationResumeState(retryResumeState)
              await persistWitnessPropagationResumeSafely({
                witness: activeWitness,
                resumeState: retryResumeState,
                failure,
              })
              setWitnessPropagationSuccess(
                `Retrying ${activeWitness.sigla} batch ${batchIndex + 1}/${totalBatches} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`
              )
              continue
            }

            const failureResumeState: TranslationBatchResumeState = {
              batchIndex,
              rowOffset,
              totalBatches,
              nextStartCharFloor,
              charAssignments: cumulativeAssignments,
              batchDiagnostics,
              retryHistory,
            }
            setWitnessPropagationResumeState(failureResumeState)
            await persistWitnessPropagationResumeSafely({
              witness: activeWitness,
              resumeState: failureResumeState,
              failure,
            })
            setWitnessPropagationFailures([failure])
            updateWitnessPropagationProposalWithFailure({
              notes: mergeTranslationPlanNotes(
                witnessPropagationProposal?.notes,
                summarizeTranslationBatchRetryHistory(retryHistory),
              ),
              diagnostics: failure.diagnostics || combineTranslationAlignmentDiagnostics(batchDiagnostics),
            })
            setWitnessPropagationError(failure.error)
            setWitnessPropagationSuccess(null)
            return
          }
        }

        if (translationFailed) {
          break
        }
      }

      if (translationFailed) {
        return
      }

      setWitnessPropagationResumeState(null)
      await clearWitnessPropagationResumeRecordSafely(activeWitness.id)
      setWitnessPropagationFailures([])
      setWitnessPropagationError(null)
    } finally {
      setIsWitnessPropagationBusy(false)
    }
  })

  const handleAnalyzeWitnessAppliedPropagation = useEventCallback(() => {
    if (isMatrixContentApplying) {
      return
    }
    if (!activeWitness) {
      return
    }

    const auditPlan = buildAppliedWitnessAuditPlan({
      rows: resolvedRows,
      witness: activeWitness,
    })

    setWitnessPropagationProposal({
      targetWitnessId: activeWitness.id,
      method: 'applied-state-audit',
      segmentCount: auditPlan.segmentCount,
      splitIndices: auditPlan.splitIndices,
      exemplarWitnessIds: [],
      rowAssignments: auditPlan.rowAssignments,
      application: {
        witnessUpdates: [],
        rowUpdates: [],
        changedRowIds: [],
      },
      notes: auditPlan.notes,
      readOnly: true,
    })
    setWitnessPropagationError(null)
    setWitnessPropagationSuccess(`Read-only audit ready for ${activeWitness.sigla}`)
  })

  const handleApplyWitnessPropagationProposal = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !activeWitness || !witnessPropagationProposal || witnessPropagationProposal.readOnly) {
      return
    }

    setIsWitnessPropagationBusy(true)
    setWitnessPropagationError(null)
    setWitnessPropagationSuccess(null)

    try {
      await Promise.all([
        updateWitnessProcessingResultsBatch(witnessPropagationProposal.application.witnessUpdates),
        witnessPropagationProposal.application.rowUpdates.length > 0
          ? upsertRows(chapterId, witnessPropagationProposal.application.rowUpdates, { autoRelabel: false })
          : Promise.resolve()
      ])
      setWitnessPropagationProposal(null)
      setWitnessPropagationResumeState(null)
      setWitnessPropagationFailures([])
      await clearWitnessPropagationResumeRecordSafely(activeWitness.id)
      const message = `Applied propagation replacement to ${activeWitness.sigla}`
      setWitnessPropagationSuccess(message)
      showToast(message)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to apply propagation replacement'
      setWitnessPropagationError(message)
    } finally {
      setIsWitnessPropagationBusy(false)
    }
  })

  const toggleContextPropagationTargetWitness = useEventCallback((witnessId: string) => {
    setSelectedContextPropagationTargetWitnessIds(previous => (
      previous.includes(witnessId)
        ? previous.filter(candidate => candidate !== witnessId)
        : [...previous, witnessId]
    ))
  })

  const handleAnalyzeContextAppliedPropagation = useEventCallback(() => {
    if (!fallbackContextPropagationAnchor) {
      return
    }

    const preview = buildWorkbenchAppliedAuditPreview({
      sourceWitness: fallbackContextPropagationAnchor,
      candidateWitnesses: scopeWitnesses,
      rows: resolvedRows,
    })

    setContextTranslationPreviewResumeStates({})
    setContextPropagationFailures([])
    setSelectedContextPropagationTargetWitnessIds([])
    setSelectedUnitRepairPreview(null)
    setContextPropagationPreview(preview)
    setContextPropagationState({
      previewing: false,
      applying: false,
      error: null,
      success: `Analyzed current applied state for ${preview.plans.filter(plan => plan.witness.id !== preview.sourceWitness.id).length} witness${preview.plans.filter(plan => plan.witness.id !== preview.sourceWitness.id).length === 1 ? '' : 'es'}.`,
    })
  })

  const handleGenerateContextPropagationPreview = useEventCallback(async () => {
    if (!chapterId || !fallbackContextPropagationAnchor) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'No accepted segmentation is available in this scope yet.',
        success: null,
      })
      setContextPropagationPreview(null)
      return
    }

    setContextPropagationState({
      previewing: true,
      applying: false,
      error: null,
      success: null,
    })
    setContextPropagationFailures([])
    setContextTranslationPreviewResumeStates({})
    setSelectedUnitRepairPreview(null)

    try {
      const requestedEnrollmentIds = selectedContextPropagationTargetWitnessIds
        .filter(witnessId => selectableEnrollmentQueueWitnessIdSet.has(witnessId))
      const effectiveEnrollmentIds = requestedEnrollmentIds.length > 0
        ? requestedEnrollmentIds
        : selectableEnrollmentQueueWitnessIds.slice(0, 1)
      const effectiveEnrollmentIdSet = new Set(effectiveEnrollmentIds)
      const targetQueueItems = selectableEnrollmentQueueItems
        .filter(item => effectiveEnrollmentIdSet.has(item.witness.id))
      if (targetQueueItems.length === 0) {
        setContextPropagationState({
          previewing: false,
          applying: false,
          error: 'No runnable witnesses are available for enrollment.',
          success: null,
        })
        setContextPropagationPreview(null)
        return
      }
      const targetWitnesses = targetQueueItems.map(item => item.witness)
      const previewRepresentativeWitnesses = deriveLedgerRepresentativeWitnesses({
        rows: resolvedRows,
        scopeWitnesses,
        targetWitnessIds: effectiveEnrollmentIds,
        fallbackWitness: fallbackContextPropagationAnchor,
      })
      const previewWitnesses = [
        ...previewRepresentativeWitnesses,
        ...targetWitnesses,
      ].filter((witness, index, witnesses) => witnesses.findIndex(candidate => candidate.id === witness.id) === index)
      let preview = buildWorkbenchPropagationPreview({
        sourceWitness: fallbackContextPropagationAnchor,
        candidateWitnesses: previewWitnesses,
        groundTruthWitnessId: fallbackContextPropagationAnchor.id,
        options: {
          allowExistingSegmentationWitnessIds: effectiveEnrollmentIds,
        },
        rows: resolvedRows,
      })
      let previewFailures: WorkbenchPropagationFailure[] = []

      for (const targetWitness of targetWitnesses) {
        const targetAlignmentStrategy = getBatchAlignmentStrategy(targetWitness)
        let batchIndex = 0
        let totalBatches = 1
        let nextStartCharFloor = 0
        let rowOffset = 0
        let cumulativeAssignments: TranslationCharAssignment[] = []
        const batchDiagnostics: WorkbenchPropagationDiagnostics[] = []
        let retryHistory = [] as NonNullable<TranslationBatchResumeState['retryHistory']>
        let translationFailed = false

        while (batchIndex < totalBatches) {
          let automaticRetryCount = 0
          while (true) {
            try {
              const batchResult = await requestWorkbenchTranslationBatchPreview({
                witnesses: scopeWitnesses,
                rows: resolvedRows,
                targetWitnessId: targetWitness.id,
                batchIndex,
                startCharFloor: nextStartCharFloor,
                rowOffset,
                alignmentStrategy: targetAlignmentStrategy,
                ...getLedgerSafeAlignmentModelRequest(propagationModelId, propagationThinkingLevel),
              })
              const partialFailure: WorkbenchPropagationFailure | null = batchResult.partial
                ? {
                    witnessId: targetWitness.id,
                    error: batchResult.error || `Failed to finish ${getBatchAlignmentLabel(targetAlignmentStrategy)} preview batch.`,
                    ...(batchResult.diagnostics ? { diagnostics: batchResult.diagnostics } : {}),
                  }
                : null
              const shouldRetryPartial = Boolean(
                partialFailure
                && automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
                && shouldAutoRetryTranslationBatchFailure({
                  error: partialFailure.error,
                  diagnostics: partialFailure.diagnostics,
                  partial: true,
                })
              )

              if (shouldRetryPartial) {
                automaticRetryCount += 1
                retryHistory = appendTranslationBatchRetryAttempt(
                  retryHistory,
                  buildTranslationRetryAttemptRecord({
                    batchIndex,
                    rowOffset,
                    error: partialFailure!.error,
                    diagnostics: partialFailure!.diagnostics,
                    failedRowId: batchResult.failedRowId,
                  }),
                )
                setContextPropagationState({
                  previewing: true,
                  applying: false,
                  error: null,
                  success: `Retrying ${targetWitness.sigla} batch ${batchIndex + 1}/${Math.max(totalBatches, batchResult.totalBatches)} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
                })
                continue
              }

              totalBatches = batchResult.totalBatches
              nextStartCharFloor = batchResult.nextStartCharFloor
              cumulativeAssignments = [...cumulativeAssignments, ...batchResult.charAssignments]
              if (batchResult.diagnostics) {
                batchDiagnostics.push(batchResult.diagnostics)
              }

              const derivedPlan = deriveTranslationPropagationPlan({
                text: targetWitness.uploaded_file?.content || '',
                charAssignments: cumulativeAssignments,
              })
              const retrySummary = summarizeTranslationBatchRetryHistory(retryHistory)
              const coverageSummary = summarizeTranslationFragmentDiagnostics(derivedPlan.fragmentDiagnostics)
              const completedBatches = getCompletedTranslationBatchCount({
                batchIndex: batchResult.nextBatchIndex,
                totalBatches,
              })

              preview = replacePreviewPlan(preview, targetWitness.id, plan => ({
                ...plan,
                splitIndices: derivedPlan.splitIndices,
                segmentCount: deriveApiPlanSegmentCount({
                  splitIndices: derivedPlan.splitIndices,
                  rowAssignments: derivedPlan.rowAssignments,
                }),
                exemplarWitnessIds: [],
                rowAssignments: derivedPlan.rowAssignments,
                strategy: targetAlignmentStrategy,
                translationProgress: {
                  completedBatches,
                  totalBatches,
                  complete: batchResult.complete,
                },
                notes: mergeTranslationPlanNotes(
                  batchResult.complete
                    ? getBatchAlignmentReadyMessage(targetAlignmentStrategy)
                    : (
                        batchResult.partial
                          ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                          : `Previewed ${targetWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                      ),
                  retrySummary,
                  coverageSummary,
                ),
                diagnostics: combineTranslationAlignmentDiagnostics(batchDiagnostics),
              }))

              if (batchResult.partial && partialFailure) {
                const resumeState: TranslationBatchResumeState = {
                  batchIndex: batchResult.nextBatchIndex,
                  rowOffset: batchResult.nextRowOffset,
                  totalBatches,
                  nextStartCharFloor,
                  charAssignments: cumulativeAssignments,
                  batchDiagnostics,
                  retryHistory,
                  ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
                }
                setContextTranslationPreviewResumeStates(previous => ({
                  ...previous,
                  [targetWitness.id]: resumeState,
                }))
                previewFailures = [
                  ...previewFailures.filter(candidate => candidate.witnessId !== targetWitness.id),
                  partialFailure,
                ]
                translationFailed = true
                break
              }

              batchIndex = batchResult.nextBatchIndex
              rowOffset = batchResult.nextRowOffset
              break
            } catch (error) {
              const failure: WorkbenchPropagationFailure = error instanceof WorkbenchTranslationBatchPreviewError
                ? {
                    witnessId: error.witnessId,
                    error: error.message,
                    ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
                  }
                : {
                  witnessId: targetWitness.id,
                  error: error instanceof Error ? error.message : `Failed to generate ${getBatchAlignmentLabel(targetAlignmentStrategy)} preview.`,
                }
              const shouldRetryFailure = automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
                && shouldAutoRetryTranslationBatchFailure({
                  error: failure.error,
                  diagnostics: failure.diagnostics,
                })

              if (shouldRetryFailure) {
                automaticRetryCount += 1
                retryHistory = appendTranslationBatchRetryAttempt(
                  retryHistory,
                  buildTranslationRetryAttemptRecord({
                    batchIndex,
                    rowOffset,
                    error: failure.error,
                    diagnostics: failure.diagnostics,
                  }),
                )
                setContextPropagationState({
                  previewing: true,
                  applying: false,
                  error: null,
                  success: `Retrying ${targetWitness.sigla} batch ${batchIndex + 1}/${totalBatches} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
                })
                continue
              }

              const resumeState: TranslationBatchResumeState = {
                batchIndex,
                rowOffset,
                totalBatches,
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
              }
              setContextTranslationPreviewResumeStates(previous => ({
                ...previous,
                [targetWitness.id]: resumeState,
              }))
              previewFailures = [
                ...previewFailures.filter(candidate => candidate.witnessId !== targetWitness.id),
                failure,
              ]
              preview = replacePreviewPlan(preview, targetWitness.id, plan => ({
                ...plan,
                notes: mergeTranslationPlanNotes(
                  plan.notes,
                  summarizeTranslationBatchRetryHistory(retryHistory),
                ),
                diagnostics: failure.diagnostics || combineTranslationAlignmentDiagnostics(batchDiagnostics),
              }))
              translationFailed = true
              break
            }
          }

          if (translationFailed) {
            break
          }
        }
      }

      setSelectedContextPropagationTargetWitnessIds(deriveSelectablePropagationTargetWitnessIds(preview))
      setContextPropagationPreview(preview)
      setContextPropagationFailures(previewFailures)
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: previewFailures[0]?.error || null,
        success: `Prepared distance-ranked enrollment preview for ${preview.plans.filter(plan => !plan.protected && plan.witness.id !== fallbackContextPropagationAnchor.id).length} witness${preview.plans.filter(plan => !plan.protected && plan.witness.id !== fallbackContextPropagationAnchor.id).length === 1 ? '' : 'es'}.`,
      })
    } catch (error) {
      setContextPropagationPreview(null)
      if (error instanceof WorkbenchPropagationPreviewError) {
        setContextPropagationFailures(error.failures)
      }
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to generate context propagation preview.',
        success: null,
      })
    }
  })

  const handleResumeContextPropagationPreview = useEventCallback(async (witnessId: string) => {
    if (!chapterId || !contextPropagationPreview) {
      return
    }
    const resumeState = contextTranslationPreviewResumeStates[witnessId]
    const targetWitness = scopeWitnesses.find(witness => witness.id === witnessId)
    if (!resumeState || !targetWitness) {
      return
    }

    setContextPropagationState({
      previewing: true,
      applying: false,
      error: null,
      success: `Resuming ${targetWitness.sigla} batch ${resumeState.batchIndex + 1}/${resumeState.totalBatches}.`,
    })

    let preview = contextPropagationPreview
    let previewFailures = contextPropagationFailures.filter(candidate => candidate.witnessId !== witnessId)

    try {
      const targetPlan = contextPropagationPreview.plans.find(plan => plan.witness.id === witnessId)
      const targetAlignmentStrategy: BatchAlignmentStrategy = targetPlan?.strategy === 'ledger-span-aligned'
        ? 'ledger-span-aligned'
        : getBatchAlignmentStrategy(targetWitness)
      let batchIndex = resumeState.batchIndex
      let totalBatches = Math.max(resumeState.totalBatches, batchIndex + 1)
      let nextStartCharFloor = resumeState.nextStartCharFloor
      let rowOffset = getTranslationResumeRowOffset(resumeState)
      let cumulativeAssignments = [...resumeState.charAssignments]
      const batchDiagnostics = [...resumeState.batchDiagnostics]
      let retryHistory = [...(resumeState.retryHistory || [])]
      let translationFailed = false

      while (batchIndex < totalBatches) {
        let automaticRetryCount = 0
        while (true) {
          try {
            const batchResult = await requestWorkbenchTranslationBatchPreview({
              witnesses: scopeWitnesses,
              rows: resolvedRows,
              targetWitnessId: witnessId,
              batchIndex,
              startCharFloor: nextStartCharFloor,
              rowOffset,
              alignmentStrategy: targetAlignmentStrategy,
              ...getLedgerSafeAlignmentModelRequest(propagationModelId, propagationThinkingLevel),
            })
            const partialFailure: WorkbenchPropagationFailure | null = batchResult.partial
              ? {
                  witnessId,
                  error: batchResult.error || `Failed to finish ${getBatchAlignmentLabel(targetAlignmentStrategy)} preview batch.`,
                  ...(batchResult.diagnostics ? { diagnostics: batchResult.diagnostics } : {}),
                }
              : null
            const shouldRetryPartial = Boolean(
              partialFailure
              && automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
              && shouldAutoRetryTranslationBatchFailure({
                error: partialFailure.error,
                diagnostics: partialFailure.diagnostics,
                partial: true,
              })
            )

            if (shouldRetryPartial) {
              automaticRetryCount += 1
              retryHistory = appendTranslationBatchRetryAttempt(
                retryHistory,
                buildTranslationRetryAttemptRecord({
                  batchIndex,
                  rowOffset,
                  error: partialFailure!.error,
                  diagnostics: partialFailure!.diagnostics,
                  failedRowId: batchResult.failedRowId,
                }),
              )
              setContextPropagationState({
                previewing: true,
                applying: false,
                error: null,
                success: `Retrying ${targetWitness.sigla} batch ${batchIndex + 1}/${Math.max(totalBatches, batchResult.totalBatches)} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
              })
              continue
            }

            totalBatches = batchResult.totalBatches
            nextStartCharFloor = batchResult.nextStartCharFloor
            cumulativeAssignments = [...cumulativeAssignments, ...batchResult.charAssignments]
            if (batchResult.diagnostics) {
              batchDiagnostics.push(batchResult.diagnostics)
            }

            const derivedPlan = deriveTranslationPropagationPlan({
              text: targetWitness.uploaded_file?.content || '',
              charAssignments: cumulativeAssignments,
            })
            const retrySummary = summarizeTranslationBatchRetryHistory(retryHistory)
            const coverageSummary = summarizeTranslationFragmentDiagnostics(derivedPlan.fragmentDiagnostics)
            const completedBatches = getCompletedTranslationBatchCount({
              batchIndex: batchResult.nextBatchIndex,
              totalBatches,
            })

            preview = replacePreviewPlan(preview, witnessId, plan => ({
              ...plan,
              splitIndices: derivedPlan.splitIndices,
              segmentCount: deriveApiPlanSegmentCount({
                splitIndices: derivedPlan.splitIndices,
                rowAssignments: derivedPlan.rowAssignments,
              }),
              exemplarWitnessIds: [],
              rowAssignments: derivedPlan.rowAssignments,
              strategy: targetAlignmentStrategy,
              translationProgress: {
                completedBatches,
                totalBatches,
                complete: batchResult.complete,
              },
              notes: mergeTranslationPlanNotes(
                batchResult.complete
                  ? getBatchAlignmentReadyMessage(targetAlignmentStrategy)
                  : (
                      batchResult.partial
                        ? (batchResult.error || `Previewed into batch ${batchResult.batchIndex + 1}/${totalBatches}.`)
                        : `Previewed ${targetWitness.sigla} batch ${completedBatches}/${totalBatches}.`
                    ),
                retrySummary,
                coverageSummary,
              ),
              diagnostics: combineTranslationAlignmentDiagnostics(batchDiagnostics),
            }))

            if (batchResult.partial && partialFailure) {
              const nextResumeState: TranslationBatchResumeState = {
                batchIndex: batchResult.nextBatchIndex,
                rowOffset: batchResult.nextRowOffset,
                totalBatches,
                nextStartCharFloor,
                charAssignments: cumulativeAssignments,
                batchDiagnostics,
                retryHistory,
                ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
              }
              setContextTranslationPreviewResumeStates(previous => ({
                ...previous,
                [witnessId]: nextResumeState,
              }))
              previewFailures = [
                ...previewFailures.filter(candidate => candidate.witnessId !== witnessId),
                partialFailure,
              ]
              translationFailed = true
              break
            }

            batchIndex = batchResult.nextBatchIndex
            rowOffset = batchResult.nextRowOffset
            break
          } catch (error) {
            const failure: WorkbenchPropagationFailure = error instanceof WorkbenchTranslationBatchPreviewError
              ? {
                  witnessId: error.witnessId,
                  error: error.message,
                  ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
                }
              : {
                  witnessId,
                  error: error instanceof Error ? error.message : `Failed to generate ${getBatchAlignmentLabel(targetAlignmentStrategy)} preview.`,
                }
            const shouldRetryFailure = automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
              && shouldAutoRetryTranslationBatchFailure({
                error: failure.error,
                diagnostics: failure.diagnostics,
              })

            if (shouldRetryFailure) {
              automaticRetryCount += 1
              retryHistory = appendTranslationBatchRetryAttempt(
                retryHistory,
                buildTranslationRetryAttemptRecord({
                  batchIndex,
                  rowOffset,
                  error: failure.error,
                  diagnostics: failure.diagnostics,
                }),
              )
              setContextPropagationState({
                previewing: true,
                applying: false,
                error: null,
                success: `Retrying ${targetWitness.sigla} batch ${batchIndex + 1}/${totalBatches} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
              })
              continue
            }

            preview = replacePreviewPlan(preview, witnessId, plan => ({
              ...plan,
              notes: mergeTranslationPlanNotes(
                plan.notes,
                summarizeTranslationBatchRetryHistory(retryHistory),
              ),
              diagnostics: failure.diagnostics || combineTranslationAlignmentDiagnostics(batchDiagnostics),
            }))
            previewFailures = [
              ...previewFailures.filter(candidate => candidate.witnessId !== witnessId),
              failure,
            ]
            translationFailed = true
            break
          }
        }

        if (translationFailed) {
          break
        }
      }

      if (!translationFailed) {
        setContextTranslationPreviewResumeStates(previous => {
          const next = { ...previous }
          delete next[witnessId]
          return next
        })
      }

      setContextPropagationPreview(preview)
      setContextPropagationFailures(previewFailures)
      setSelectedContextPropagationTargetWitnessIds(previous => (
        mergeSelectablePropagationTargetWitnessIds(previous, preview, witnessId)
      ))
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: previewFailures.find(candidate => candidate.witnessId === witnessId)?.error || null,
        success: translationFailed
          ? null
          : `Updated propagation preview for ${targetWitness.sigla}.`,
      })
    } catch (error) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to resume alignment preview.',
        success: null,
      })
    }
  })

  const handleApplyContextPropagationPreview = useEventCallback(async () => {
    if (isMatrixContentApplying || !chapterId || !contextPropagationPreview) {
      return
    }

    const application = buildWorkbenchPropagationApplication({
      preview: contextPropagationPreview,
      contextId: chapterId,
      rows: resolvedRows,
      options: {
        selectedWitnessIds: selectedContextPropagationTargetWitnessIds,
      },
    })

    if (application.witnessUpdates.length === 0) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'No propagation targets are available for application.',
        success: null,
      })
      return
    }

    setContextPropagationState(previous => ({
      ...previous,
      previewing: false,
      applying: true,
      error: null,
      success: null,
    }))

    try {
      await Promise.all([
        updateWitnessProcessingResultsBatch(application.witnessUpdates),
        application.rowUpdates.length > 0
          ? upsertRows(chapterId, application.rowUpdates, { autoRelabel: false })
          : Promise.resolve(),
      ])
      setContextPropagationPreview(null)
      setSelectedUnitRepairPreview(null)
      setSelectedContextPropagationTargetWitnessIds([])
      setContextPropagationFailures([])
      setContextTranslationPreviewResumeStates({})
      const message = `Applied propagation to ${application.witnessUpdates.length} witness${application.witnessUpdates.length === 1 ? '' : 'es'}.`
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: null,
        success: message,
      })
      showToast(message)
    } catch (error) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to apply propagation.',
        success: null,
      })
    }
  })

  const runSelectedUnitRepairBatch = useEventCallback(async (
    initialRun: SelectedUnitRepairRunState,
    { replacePending = false }: { replacePending?: boolean } = {},
  ) => {
    const targetWitness = (
      interactiveScopeWitnesses.find(witness => witness.id === initialRun.targetWitnessId)
      || scopeWitnesses.find(witness => witness.id === initialRun.targetWitnessId)
      || null
    )
    if (!targetWitness) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'The selected repair witness is no longer available.',
        success: null,
      })
      return
    }
    const requestRowById = new Map(resolvedRows.map(row => [row.id, row]))
    const requestRows = initialRun.requestRowIds
      .map(rowId => requestRowById.get(rowId))
      .filter((row): row is AnchoredRow => Boolean(row))
    if (requestRows.length !== initialRun.requestRowIds.length) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'The Ledger changed after this selected repair was prepared. Discard it and prepare the selection again.',
        success: null,
      })
      return
    }
    if (
      initialRun.resumeState.batchIndex >= initialRun.resumeState.totalBatches
      && getTranslationResumeRowOffset(initialRun.resumeState) === 0
    ) {
      return
    }
    if (initialRun.pendingResumeState && !replacePending) {
      return
    }

    const targetAlignmentStrategy = getBatchAlignmentStrategy(targetWitness)
    let workingRun = initialRun
    let automaticRetryCount = 0
    setContextPropagationState({
      previewing: true,
      applying: false,
      error: null,
      success: `Preparing ${targetWitness.sigla} selected repair batch ${initialRun.resumeState.batchIndex + 1}/${initialRun.resumeState.totalBatches}.`,
    })

    while (true) {
      const {
        batchIndex,
        totalBatches,
        nextStartCharFloor,
        charAssignments,
        batchDiagnostics,
      } = workingRun.resumeState
      const rowOffset = getTranslationResumeRowOffset(workingRun.resumeState)
      try {
        const repairCandidateRowIds = recoverLedgerSafeRepairCandidateRowIds({
          candidateRowIds: workingRun.repairCandidateRowIds,
          requestRowIds: workingRun.requestRowIds,
          resumeState: workingRun.resumeState,
        })
        if (batchIndex > 0 && repairCandidateRowIds.length === 0) {
          throw new Error(
            'The selected repair checkpoint is missing its original candidate-row range. '
            + 'Discard it and prepare the selection again.'
          )
        }
        const batchResult = await requestWorkbenchTranslationBatchPreview({
          witnesses: scopeWitnesses,
          rows: requestRows,
          targetWitnessId: targetWitness.id,
          batchIndex,
          startCharFloor: nextStartCharFloor,
          rowOffset,
          batchSize: LEDGER_SAFE_SELECTED_REPAIR_BATCH_SIZE,
          repairTargetRowIds: workingRun.selectedRowIds,
          ...(repairCandidateRowIds.length > 0 ? { repairCandidateRowIds } : {}),
          repairTargetSpan: workingRun.repairTargetSpan,
          alignmentStrategy: targetAlignmentStrategy,
          ...getLedgerSafeAlignmentModelRequest(workingRun.modelId, workingRun.thinkingLevel),
        })
        const returnedRepairCandidateRowIds = batchResult.repairCandidateRowIds || []
        if (
          repairCandidateRowIds.length > 0
          && returnedRepairCandidateRowIds.length > 0
          && repairCandidateRowIds.join('\u241f') !== returnedRepairCandidateRowIds.join('\u241f')
        ) {
          throw new Error('The backend returned a different candidate-row range for this selected repair.')
        }
        const stableRepairCandidateRowIds = repairCandidateRowIds.length > 0
          ? repairCandidateRowIds
          : recoverLedgerSafeRepairCandidateRowIds({
              candidateRowIds: returnedRepairCandidateRowIds,
              requestRowIds: workingRun.requestRowIds,
              resumeState: {
                ...workingRun.resumeState,
                batchDiagnostics: [
                  ...workingRun.resumeState.batchDiagnostics,
                  ...(batchResult.diagnostics ? [batchResult.diagnostics] : []),
                ],
              },
            })
        if (
          batchResult.nextBatchIndex < batchResult.totalBatches
          && stableRepairCandidateRowIds.length === 0
        ) {
          throw new Error('The backend did not return a durable candidate-row range for this selected repair.')
        }
        const partialFailure: WorkbenchPropagationFailure | null = batchResult.partial
          ? {
              witnessId: targetWitness.id,
              error: batchResult.error || `Failed to finish ${getBatchAlignmentLabel(targetAlignmentStrategy)} selected repair batch.`,
              ...(batchResult.diagnostics ? { diagnostics: batchResult.diagnostics } : {}),
            }
          : null
        const shouldRetryPartial = Boolean(
          partialFailure
          && automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
          && shouldAutoRetryTranslationBatchFailure({
            error: partialFailure.error,
            diagnostics: partialFailure.diagnostics,
            partial: true,
          })
        )

        if (shouldRetryPartial) {
          automaticRetryCount += 1
          const retryHistory = appendTranslationBatchRetryAttempt(
            workingRun.resumeState.retryHistory,
            buildTranslationRetryAttemptRecord({
              batchIndex,
              rowOffset,
              error: partialFailure!.error,
              diagnostics: partialFailure!.diagnostics,
              failedRowId: batchResult.failedRowId,
            }),
          )
          workingRun = {
            ...workingRun,
            repairCandidateRowIds: stableRepairCandidateRowIds,
            resumeState: {
              ...workingRun.resumeState,
              totalBatches: Math.max(totalBatches, batchResult.totalBatches),
              retryHistory,
              ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
            },
            failure: partialFailure,
          }
          setSelectedUnitRepairRun(workingRun)
          try {
            await persistSelectedUnitRepairRun(workingRun)
          } catch (error) {
            console.warn('Failed to persist selected repair retry state.', error)
          }
          setContextPropagationState({
            previewing: true,
            applying: false,
            error: null,
            success: `Retrying ${targetWitness.sigla} selected repair batch ${batchIndex + 1}/${Math.max(totalBatches, batchResult.totalBatches)} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
          })
          continue
        }

        const nextResumeState: TranslationBatchResumeState = {
          batchIndex: batchResult.nextBatchIndex,
          rowOffset: batchResult.nextRowOffset,
          totalBatches: batchResult.totalBatches,
          nextStartCharFloor: batchResult.nextStartCharFloor,
          charAssignments: [...charAssignments, ...batchResult.charAssignments],
          batchDiagnostics: [
            ...batchDiagnostics,
            ...(batchResult.diagnostics ? [batchResult.diagnostics] : []),
          ],
          retryHistory: workingRun.resumeState.retryHistory || [],
          ...(batchResult.failedRowId ? { failedRowId: batchResult.failedRowId } : {}),
        }
        const nextRun: SelectedUnitRepairRunState = {
          ...workingRun,
          repairCandidateRowIds: stableRepairCandidateRowIds,
          pendingResumeState: nextResumeState,
          failure: partialFailure,
        }
        const nextPreview = buildSelectedUnitRepairPreviewFromRun(nextRun)
        setSelectedUnitRepairRun(nextRun)
        setSelectedUnitRepairPreview(nextPreview)
        try {
          await persistSelectedUnitRepairRun(nextRun)
        } catch (error) {
          setContextPropagationState({
            previewing: false,
            applying: false,
            error: `Selected repair batch ${batchIndex + 1} completed, but its checkpoint could not be saved. Do not close this chapter before retrying.`,
            success: null,
          })
          return
        }

        const completedBatches = getCompletedTranslationBatchCount(nextResumeState)
        const complete = (
          !batchResult.partial
          && batchResult.nextBatchIndex >= batchResult.totalBatches
          && batchResult.nextRowOffset === 0
        )
        setContextPropagationState({
          previewing: false,
          applying: false,
          error: partialFailure?.error || null,
          success: complete
            ? `Final selected repair batch ${completedBatches}/${batchResult.totalBatches} is ready to review and apply.`
            : `Selected repair batch ${completedBatches}/${batchResult.totalBatches} is ready to review and apply.`,
        })
        return
      } catch (error) {
        const failure: WorkbenchPropagationFailure = error instanceof WorkbenchTranslationBatchPreviewError
          ? {
              witnessId: error.witnessId,
              error: error.message,
              ...(error.diagnostics ? { diagnostics: error.diagnostics } : {}),
            }
          : {
              witnessId: targetWitness.id,
              error: error instanceof Error ? error.message : `Failed to generate ${getBatchAlignmentLabel(targetAlignmentStrategy)} selected repair.`,
            }
        const shouldRetryFailure = automaticRetryCount < AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT
          && shouldAutoRetryTranslationBatchFailure({
            error: failure.error,
            diagnostics: failure.diagnostics,
          })
        const retryHistory = shouldRetryFailure
          ? appendTranslationBatchRetryAttempt(
              workingRun.resumeState.retryHistory,
              buildTranslationRetryAttemptRecord({
                batchIndex,
                rowOffset,
                error: failure.error,
                diagnostics: failure.diagnostics,
              }),
            )
          : (workingRun.resumeState.retryHistory || [])
        workingRun = {
          ...workingRun,
          resumeState: {
            ...workingRun.resumeState,
            retryHistory,
          },
          failure,
        }
        setSelectedUnitRepairRun(workingRun)
        try {
          await persistSelectedUnitRepairRun(workingRun)
        } catch (persistenceError) {
          console.warn('Failed to persist selected repair failure state.', persistenceError)
        }

        if (shouldRetryFailure) {
          automaticRetryCount += 1
          setContextPropagationState({
            previewing: true,
            applying: false,
            error: null,
            success: `Retrying ${targetWitness.sigla} selected repair batch ${batchIndex + 1}/${totalBatches} automatically (${automaticRetryCount}/${AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT}).`,
          })
          continue
        }

        setContextPropagationState({
          previewing: false,
          applying: false,
          error: failure.error,
          success: null,
        })
        return
      }
    }
  })

  const handleGenerateSelectedUnitRepairPreview = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'No active chapter is available for selected repair.',
        success: null,
      })
      return
    }
    if (
      selectedUnitRepairScope.reason
      || !selectedUnitRepairScope.witness
      || !selectedUnitRepairTargetSpan
      || !selectedUnitRepairResumeScopeId
    ) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: selectedUnitRepairScope.reason || 'Could not derive the selected witness span from the current segmentation.',
        success: null,
      })
      return
    }

    const targetWitness = selectedUnitRepairScope.witness
    const initialRun: SelectedUnitRepairRunState = {
      resumeScopeId: selectedUnitRepairResumeScopeId,
      targetWitnessId: targetWitness.id,
      selectedRowIds: selectedUnitRepairScope.selectedRows.map(row => row.id),
      requestRowIds: selectedUnitRepairScope.requestRows.map(row => row.id),
      repairCandidateRowIds: [],
      contextRowIds: [...selectedUnitRepairScope.contextRowIds],
      anchorRowIds: [...selectedUnitRepairScope.anchorRowIds],
      repairTargetSpan: selectedUnitRepairTargetSpan,
      modelId: selectedUnitRepairModelId,
      thinkingLevel: selectedUnitRepairThinkingLevel,
      resumeState: {
        batchIndex: 0,
        rowOffset: 0,
        totalBatches: 1,
        nextStartCharFloor: 0,
        charAssignments: [],
        batchDiagnostics: [],
        retryHistory: [],
      },
      pendingResumeState: null,
      failure: null,
    }

    setSelectedUnitRepairPreview(null)
    setSelectedUnitRepairRun(initialRun)
    setContextPropagationPreview(null)
    setContextPropagationFailures([])
    setContextTranslationPreviewResumeStates({})
    setSelectedContextPropagationTargetWitnessIds([])
    setWitnessPropagationProposal(null)
    setSemanticPreview(null)
    try {
      await persistSelectedUnitRepairRun(initialRun)
    } catch (error) {
      setSelectedUnitRepairRun(null)
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: 'Could not create a durable selected repair checkpoint.',
        success: null,
      })
      return
    }
    await runSelectedUnitRepairBatch(initialRun)
  })

  const handleContinueSelectedUnitRepairPreview = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (
      !selectedUnitRepairRun
      || selectedUnitRepairRun.pendingResumeState
      || selectedUnitRepairRunComplete
    ) {
      return
    }
    await runSelectedUnitRepairBatch(selectedUnitRepairRun)
  })

  const handleRetrySelectedUnitRepairBatch = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!selectedUnitRepairRun || selectedUnitRepairRunComplete) {
      return
    }
    await runSelectedUnitRepairBatch(selectedUnitRepairRun, { replacePending: true })
  })

  const handleDiscardSelectedUnitRepairPreview = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (selectedUnitRepairRun) {
      try {
        await clearSelectedUnitRepairRunRecord(selectedUnitRepairRun)
      } catch (error) {
        console.warn('Failed to clear the selected repair checkpoint.', error)
      }
    }
    setSelectedUnitRepairPreview(null)
    setSelectedUnitRepairRun(null)
    selectedUnitRepairResumeHydrationRef.current = null
    setContextPropagationState({
      previewing: false,
      applying: false,
      error: null,
      success: null,
    })
  })

  const handleApplySelectedUnitRepairPreview = useEventCallback(async () => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots) {
      return
    }
    if (
      !chapterId
      || !selectedUnitRepairPreview
      || !selectedUnitRepairApplication
      || !selectedUnitRepairRun
      || !selectedUnitRepairRun.pendingResumeState
    ) {
      return
    }
    const pendingResumeState = selectedUnitRepairRun.pendingResumeState
    const appliedBatchNumber = getCompletedTranslationBatchCount(pendingResumeState)
    const isFinalBatch = (
      pendingResumeState.batchIndex >= pendingResumeState.totalBatches
      && getTranslationResumeRowOffset(pendingResumeState) === 0
    )
    if (!selectedUnitRepairApplication.acceptedAssignmentRowIds?.length) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: `Batch ${appliedBatchNumber} has no structurally valid row assignments. Retry this batch before applying.`,
        success: null,
      })
      return
    }
    setContextPropagationState(previous => ({
      ...previous,
      previewing: false,
      applying: true,
      error: null,
      success: null,
    }))

    try {
      const currentTargetWitness = interactiveScopeWitnesses.find(witness => witness.id === selectedUnitRepairPreview.targetWitness.id)
        || scopeWitnesses.find(witness => witness.id === selectedUnitRepairPreview.targetWitness.id)
        || null
      const witnessChangedSincePreview = currentTargetWitness
        ? serializeSelectedRepairUndoValue(currentTargetWitness.segmentation_result) !== serializeSelectedRepairUndoValue(selectedUnitRepairPreview.targetWitness.segmentation_result)
        : true
      const changedRowsSincePreview = resolvedRows
        .filter(row => Object.prototype.hasOwnProperty.call(selectedUnitRepairPreview.baseRowFingerprints || {}, row.id))
        .filter(row => selectedUnitRepairPreview.baseRowFingerprints[row.id] !== JSON.stringify(row))
        .map(row => row.id)
      if (witnessChangedSincePreview || changedRowsSincePreview.length > 0) {
        const message = 'Selected repair preview is stale because the witness or Ledger rows changed after preview. Regenerate the selected repair before applying.'
        setContextPropagationState({
          previewing: false,
          applying: false,
          error: message,
          success: null,
        })
        showToast(message)
        return
      }
      const rowUpdateById = new Map(selectedUnitRepairApplication.rowUpdates.map(row => [row.id, row]))
      const afterRows = resolvedRows.map(row => rowUpdateById.get(row.id) || row)
      const changedRowIdSet = new Set(selectedUnitRepairApplication.changedRowIds)
      const undoBeforeRows = cloneRowsForSelectedRepairUndo(resolvedRows.filter(row => changedRowIdSet.has(row.id)))
      const undoAfterRows = cloneRowsForSelectedRepairUndo(afterRows.filter(row => changedRowIdSet.has(row.id)))
      const repairUndo: SelectedUnitRepairUndoState = {
        id: `selected-repair:${selectedUnitRepairPreview.targetWitness.id}:batch-${appliedBatchNumber}:${Date.now()}`,
        witnessId: selectedUnitRepairPreview.targetWitness.id,
        witnessSigla: selectedUnitRepairPreview.targetWitness.sigla,
        previousSegmentationResult: selectedUnitRepairPreview.targetWitness.segmentation_result,
        previousProcessingStatus: selectedUnitRepairPreview.targetWitness.processing_status,
        appliedSegmentationResult: selectedUnitRepairApplication.witnessUpdates[0]?.result,
        selectedRowIds: [...selectedUnitRepairPreview.selectedRowIds],
        changedRowIds: [...selectedUnitRepairApplication.changedRowIds],
        beforeRows: undoBeforeRows,
        afterRows: undoAfterRows,
        createdAt: new Date().toISOString(),
      }
      const backup = await createLedgerSnapshot({
        reason: 'pre-selected-unit-repair',
        label: `Before selected repair batch ${appliedBatchNumber} · ${selectedUnitRepairPreview.targetWitness.sigla}`
      })
      await Promise.all([
        updateWitnessProcessingResultsBatch(selectedUnitRepairApplication.witnessUpdates),
        selectedUnitRepairApplication.rowUpdates.length > 0
          ? upsertRows(chapterId, selectedUnitRepairApplication.rowUpdates, { autoRelabel: false })
          : Promise.resolve(),
      ])
      await persistEditorialEvent({
        kind: 'selected_unit_propagation_repair',
        params: {
          witnessId: selectedUnitRepairPreview.targetWitness.id,
          selectedRowIds: selectedUnitRepairPreview.selectedRowIds,
          contextRowIds: selectedUnitRepairPreview.contextRowIds,
          anchorRowIds: selectedUnitRepairPreview.anchorRowIds,
          splitIndices: selectedUnitRepairPreview.splitIndices,
          rowAssignments: selectedUnitRepairPreview.rowAssignments,
          batchNumber: appliedBatchNumber,
          totalBatches: pendingResumeState.totalBatches,
        },
        summary: `Applied selected propagation repair batch ${appliedBatchNumber}/${pendingResumeState.totalBatches} to ${selectedUnitRepairPreview.targetWitness.sigla}`,
        affectedWitnessIds: [selectedUnitRepairPreview.targetWitness.id],
        affectedRowIds: selectedUnitRepairApplication.changedRowIds,
        beforeRows: resolvedRows,
        afterRows,
        metadata: {
          notes: selectedUnitRepairPreview.notes || null,
          diagnostics: selectedUnitRepairPreview.diagnostics || null,
          unassignedFragmentUnitUuids: selectedUnitRepairApplication.unassignedFragmentUnitUuids || [],
          structurallyBlockedAssignmentRowIds: selectedUnitRepairApplication.outOfScopeAssignmentRowIds || [],
          outsideLocalContextAssignmentRowIds: selectedUnitRepairApplication.outsideLocalContextAssignmentRowIds || [],
          backupSnapshotId: backup?.id || null,
        }
      })

      const nextRun: SelectedUnitRepairRunState = {
        ...selectedUnitRepairRun,
        resumeState: pendingResumeState,
        pendingResumeState: null,
        failure: null,
      }
      setSelectedUnitRepairUndo(repairUndo)
      setSelectedUnitRepairPreview(null)

      if (isFinalBatch) {
        const firstChangedRowId = selectedUnitRepairApplication.changedRowIds[0]
          || selectedUnitRepairPreview.selectedRowIds[0]
          || ''
        if (firstChangedRowId) {
          setSelectedCell({ rowId: firstChangedRowId, witnessId: selectedUnitRepairPreview.targetWitness.id })
          setSelectionAnchorCell({ rowId: firstChangedRowId, witnessId: selectedUnitRepairPreview.targetWitness.id })
        }
        setSelectedRange(selectedUnitRepairPreview.selectedRowIds.length > 1
          ? { witnessId: selectedUnitRepairPreview.targetWitness.id, rowIds: selectedUnitRepairPreview.selectedRowIds }
          : null
        )
        try {
          await clearSelectedUnitRepairRunRecord(selectedUnitRepairRun)
        } catch (error) {
          console.warn('Failed to clear the applied selected repair checkpoint.', error)
        }
        setSelectedUnitRepairRun(null)
        selectedUnitRepairResumeHydrationRef.current = null
      } else {
        setSelectedUnitRepairRun(nextRun)
        try {
          await persistSelectedUnitRepairRun(nextRun)
        } catch (error) {
          const message = `Applied batch ${appliedBatchNumber}, but its continuation checkpoint could not be saved. Keep this chapter open; Continue can still use the in-memory cursor.`
          setContextPropagationState({
            previewing: false,
            applying: false,
            error: message,
            success: null,
          })
          showToast(message)
          return
        }
      }

      const message = isFinalBatch
        ? `Applied final selected repair batch to ${selectedUnitRepairPreview.targetWitness.sigla}.`
        : `Applied selected repair batch ${appliedBatchNumber}/${pendingResumeState.totalBatches}. Continue when ready.`
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: null,
        success: message,
      })
      showToast(message)
    } catch (error) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to apply selected repair.',
        success: null,
      })
    }
  })

  const appendPiReviewLog = useCallback((kind: PiReviewLogEntry['kind'], message: string) => {
    setPiReviewLog(current => [
      {
        id: `${kind}:${Date.now()}:${current.length}`,
        kind,
        message
      },
      ...current
    ].slice(0, 8))
  }, [])

  const persistEditorialEvent = useEventCallback(async ({
    kind,
    params,
    summary,
    affectedWitnessIds,
    affectedRowIds,
    beforeRows,
    afterRows,
    source = 'ledger-safe-studio',
    metadata
  }: {
    kind: LedgerSafeEditorialOperationKind
    params: Record<string, unknown>
    summary: string
    affectedWitnessIds: string[]
    affectedRowIds: string[]
    beforeRows: AnchoredRow[]
    afterRows: AnchoredRow[]
    source?: LedgerSafeEditorialEventSource
    metadata?: Record<string, unknown>
  }) => {
    if (!chapterId) {
      return null
    }
    try {
      const event = await recordLedgerSafeEditorialEvent({
        chapterId,
        source,
        summary,
        operation: { kind, params },
        affectedWitnessIds,
        affectedRowIds,
        beforeRows: buildLedgerSafeEditorialRowSnapshots(beforeRows, affectedRowIds),
        afterRows: buildLedgerSafeEditorialRowSnapshots(afterRows, affectedRowIds),
        uiContext: {
          scopeWitnessIds: scopeWitnesses.map(witness => witness.id),
          selectedWitnessId: selectedCell?.witnessId || activeWitness?.id || null,
          selectedRowIds: selectedRange?.rowIds || (selectedCell?.rowId ? [selectedCell.rowId] : []),
          diffPivotWitnessId: diffPivotWitness?.id || null,
          piSessionId: piSessionId || null
        },
        metadata
      })
      setEditorialEvents(current => [event, ...current].filter(Boolean).slice(0, 12))
      if (runtimeEnablePostgresTrial) {
        void enqueueLedgerSafePostgresTrialShadowEdit({
          enabled: true,
          event,
        })
      }
      return event
    } catch (error) {
      console.error('Failed to persist Variorum editorial event', error)
      return null
    }
  })

  const handleUndoLegacySceneLabelRemoval = useEventCallback(async () => {
    if (isMatrixContentApplying || !chapterId || !legacySceneRemovalUndo) {
      return
    }

    const currentRowById = new Map(resolvedRows.map(row => [row.id, row]))
    const restorableSceneByRowId = new Map<string, AnchoredRowSceneMetadata>()
    let skippedRowCount = 0
    legacySceneRemovalUndo.removedRowScenes.forEach(entry => {
      const currentRow = currentRowById.get(entry.rowId)
      if (!currentRow || currentRow.metadata?.scene !== undefined) {
        skippedRowCount += 1
        return
      }
      restorableSceneByRowId.set(entry.rowId, entry.scene)
    })

    if (restorableSceneByRowId.size === 0) {
      setLegacySceneRemovalUndo(null)
      showToast('The removed scene label cannot be restored because its rows changed')
      return
    }

    setIsLedgerApplying(true)
    try {
      const now = new Date().toISOString()
      const updatedRows = resolvedRows.map(row => {
        const scene = restorableSceneByRowId.get(row.id)
        if (!scene) {
          return row
        }
        return {
          ...row,
          metadata: {
            ...(row.metadata || {}),
            scene: cloneAnchoredRowSceneMetadata(scene) || scene,
          },
          updatedAt: now,
        }
      })
      const changedRowIds = Array.from(restorableSceneByRowId.keys())
      const changedRowIdSet = new Set(changedRowIds)
      await upsertRows(
        chapterId,
        updatedRows.filter(row => changedRowIdSet.has(row.id)),
        { autoRelabel: false }
      )
      setLegacySceneRemovalUndo(null)
      showToast(
        skippedRowCount > 0
          ? `Restored the legacy scene label on ${changedRowIds.length} unchanged row${changedRowIds.length === 1 ? '' : 's'}; ${skippedRowCount} changed row${skippedRowCount === 1 ? ' was' : 's were'} left untouched`
          : `Restored legacy scene label “${legacySceneRemovalUndo.title}”`
      )
      const editorialEventPromise = legacySceneEditorialEventTailRef.current.then(() => persistEditorialEvent({
        kind: 'undo_remove_legacy_scene_label',
        params: {
          undoId: legacySceneRemovalUndo.id,
          sceneKey: legacySceneRemovalUndo.sceneKey,
          title: legacySceneRemovalUndo.title,
          restoredRowCount: changedRowIds.length,
          skippedRowCount,
        },
        summary: `Restored legacy scene label “${legacySceneRemovalUndo.title}”`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: changedRowIds,
        beforeRows: resolvedRows,
        afterRows: updatedRows,
        metadata: {
          undoId: legacySceneRemovalUndo.id,
          originalRemovalCreatedAt: legacySceneRemovalUndo.createdAt,
        }
      }))
      legacySceneEditorialEventTailRef.current = editorialEventPromise
      void editorialEventPromise
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to restore the legacy scene label')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleRemoveLegacySceneLabel = useEventCallback(async (
    sceneHeader: LedgerSafeSceneHeaderModel
  ) => {
    if (isMatrixContentApplying || !chapterId || readOnly) {
      return
    }
    const removedRowScenes = resolvedRows.flatMap(row => {
      const scene = getAnchoredRowSceneMetadata(row)
      if (!scene || getLedgerSafeSceneKey(scene) !== sceneHeader.sceneKey) {
        return []
      }
      return [{
        rowId: row.id,
        scene: cloneAnchoredRowSceneMetadata(scene) || scene,
      }]
    })
    if (removedRowScenes.length === 0) {
      showToast('This legacy scene label is no longer present')
      return
    }
    if (!window.confirm(
      `Remove legacy scene label “${sceneHeader.title}” from ${removedRowScenes.length} `
      + `collation row${removedRowScenes.length === 1 ? '' : 's'}? `
      + 'Structural ranges and row titles will not be changed.'
    )) {
      return
    }

    setIsLedgerApplying(true)
    try {
      const changedRowIdSet = new Set(removedRowScenes.map(entry => entry.rowId))
      const now = new Date().toISOString()
      const updatedRows = resolvedRows.map(row => {
        if (!changedRowIdSet.has(row.id)) {
          return row
        }
        const metadata = { ...(row.metadata || {}) }
        delete metadata.scene
        return {
          ...row,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
          updatedAt: now,
        }
      })
      const changedRows = updatedRows.filter(row => changedRowIdSet.has(row.id))
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      const undoState: LedgerSafeLegacySceneRemovalUndoState = {
        id: `legacy-scene-removal:${Date.now()}`,
        createdAt: now,
        sceneKey: sceneHeader.sceneKey,
        title: sceneHeader.title,
        removedRowScenes,
      }
      if (sceneTitleEditor?.sceneKey === sceneHeader.sceneKey) {
        setSceneTitleEditor(null)
      }
      setLegacySceneRemovalUndo(undoState)
      showToast(
        `Removed legacy scene label “${sceneHeader.title}”`,
        {
          label: 'Undo',
          run: () => {
            void handleUndoLegacySceneLabelRemoval()
          },
        },
        7000
      )
      const editorialEventPromise = legacySceneEditorialEventTailRef.current.then(() => persistEditorialEvent({
        kind: 'remove_legacy_scene_label',
        params: {
          removalId: undoState.id,
          sceneKey: sceneHeader.sceneKey,
          title: sceneHeader.title,
          removedRowCount: changedRows.length,
        },
        summary: `Removed legacy scene label “${sceneHeader.title}”`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: Array.from(changedRowIdSet),
        beforeRows: resolvedRows,
        afterRows: updatedRows,
        metadata: {
          removalId: undoState.id,
          structuralRangesPreserved: true,
        }
      }))
      legacySceneEditorialEventTailRef.current = editorialEventPromise
      void editorialEventPromise
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to remove the legacy scene label')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleUndoSelectedUnitRepair = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !selectedUnitRepairUndo) {
      showToast('No selected repair is available to undo')
      return
    }
    if (!window.confirm(`Undo the last selected repair for ${selectedUnitRepairUndo.witnessSigla}?`)) {
      return
    }

    setContextPropagationState(previous => ({
      ...previous,
      previewing: false,
      applying: true,
      error: null,
      success: null,
    }))

    try {
      const restoreRowById = new Map(selectedUnitRepairUndo.beforeRows.map(row => [row.id, row]))
      const expectedAfterRowById = new Map(selectedUnitRepairUndo.afterRows.map(row => [row.id, row]))
      const currentRowsById = new Map(resolvedRows.map(row => [row.id, row]))
      const staleRowIds = selectedUnitRepairUndo.changedRowIds.filter(rowId => {
        const currentRow = currentRowsById.get(rowId)
        const expectedRow = expectedAfterRowById.get(rowId)
        if (!currentRow || !expectedRow) {
          return true
        }
        return serializeSelectedRepairWitnessMappings(currentRow, selectedUnitRepairUndo.witnessId)
          !== serializeSelectedRepairWitnessMappings(expectedRow, selectedUnitRepairUndo.witnessId)
      })
      const currentWitness = scopeWitnesses.find(witness => witness.id === selectedUnitRepairUndo.witnessId)
        || interactiveScopeWitnesses.find(witness => witness.id === selectedUnitRepairUndo.witnessId)
        || null
      const currentWitnessSegmentationFingerprint = currentWitness
        ? serializeSelectedRepairSegmentationResult(currentWitness.segmentation_result)
        : null
      const appliedWitnessSegmentationFingerprint = serializeSelectedRepairSegmentationResult(selectedUnitRepairUndo.appliedSegmentationResult)
      const previousWitnessSegmentationFingerprint = serializeSelectedRepairSegmentationResult(selectedUnitRepairUndo.previousSegmentationResult)
      const witnessAtAppliedState = currentWitnessSegmentationFingerprint === appliedWitnessSegmentationFingerprint
      const witnessAlreadyRestored = currentWitnessSegmentationFingerprint === previousWitnessSegmentationFingerprint
      const witnessChanged = currentWitness
        ? !witnessAtAppliedState && !witnessAlreadyRestored
        : true
      if (staleRowIds.length > 0 || witnessChanged) {
        const message = staleRowIds.length > 0
          ? `Cannot undo selected repair because ${staleRowIds.length} affected row${staleRowIds.length === 1 ? '' : 's'} changed after the repair.`
          : 'Cannot undo selected repair because the witness segmentation changed after the repair.'
        setContextPropagationState({
          previewing: false,
          applying: false,
          error: message,
          success: null,
        })
        showToast(message)
        return
      }
      const timestamp = new Date().toISOString()
      const rowsToRestore = selectedUnitRepairUndo.changedRowIds
        .map(rowId => {
          const currentRow = currentRowsById.get(rowId)
          const beforeRow = restoreRowById.get(rowId)
          if (!currentRow || !beforeRow) {
            return null
          }
          return restoreSelectedRepairWitnessMappings({
            currentRow,
            beforeRow,
            expectedAfterRow: expectedAfterRowById.get(rowId),
            witnessId: selectedUnitRepairUndo.witnessId,
            timestamp,
          })
        })
        .filter((row): row is AnchoredRow => Boolean(row))
      const restoredRowById = new Map(rowsToRestore.map(row => [row.id, row]))
      const afterRows = resolvedRows.map(row => restoredRowById.get(row.id) || row)
      const witnessRestoreUpdates = witnessAtAppliedState
        ? [{
            witnessId: selectedUnitRepairUndo.witnessId,
            type: 'segmentation' as const,
            result: selectedUnitRepairUndo.previousSegmentationResult,
            status: selectedUnitRepairUndo.previousProcessingStatus,
          }]
        : []
      await Promise.all([
        witnessRestoreUpdates.length > 0
          ? updateWitnessProcessingResultsBatch(witnessRestoreUpdates)
          : Promise.resolve(),
        rowsToRestore.length > 0
          ? upsertRows(chapterId, rowsToRestore, { autoRelabel: false })
          : Promise.resolve(),
      ])
      await persistEditorialEvent({
        kind: 'undo_selected_unit_propagation_repair',
        params: {
          undoId: selectedUnitRepairUndo.id,
          witnessId: selectedUnitRepairUndo.witnessId,
          selectedRowIds: selectedUnitRepairUndo.selectedRowIds,
          changedRowIds: selectedUnitRepairUndo.changedRowIds,
        },
        summary: `Undid selected propagation repair for ${selectedUnitRepairUndo.witnessSigla}`,
        affectedWitnessIds: [selectedUnitRepairUndo.witnessId],
        affectedRowIds: selectedUnitRepairUndo.changedRowIds,
        beforeRows: resolvedRows,
        afterRows,
        metadata: {
          originalRepairCreatedAt: selectedUnitRepairUndo.createdAt,
          witnessAlreadyRestored,
        },
      })
      const firstRestoredRowId = selectedUnitRepairUndo.changedRowIds[0] || selectedUnitRepairUndo.selectedRowIds[0] || ''
      if (firstRestoredRowId) {
        setSelectedCell({ rowId: firstRestoredRowId, witnessId: selectedUnitRepairUndo.witnessId })
        setSelectionAnchorCell({ rowId: firstRestoredRowId, witnessId: selectedUnitRepairUndo.witnessId })
      }
      setSelectedRange(selectedUnitRepairUndo.selectedRowIds.length > 1
        ? { witnessId: selectedUnitRepairUndo.witnessId, rowIds: selectedUnitRepairUndo.selectedRowIds }
        : null
      )
      const message = `Undid selected repair for ${selectedUnitRepairUndo.witnessSigla}.`
      if (selectedUnitRepairRun) {
        try {
          await clearSelectedUnitRepairRunRecord(selectedUnitRepairRun)
        } catch (error) {
          console.warn('Failed to clear the selected repair checkpoint after undo.', error)
        }
        setSelectedUnitRepairRun(null)
        selectedUnitRepairResumeHydrationRef.current = null
      }
      setSelectedUnitRepairUndo(null)
      setSelectedUnitRepairPreview(null)
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: null,
        success: message,
      })
      showToast(message)
    } catch (error) {
      setContextPropagationState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to undo selected repair.',
        success: null,
      })
    }
  })

  const handleGenerateSemanticPreview = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || resolvedRows.length === 0) {
      setSemanticPreview(null)
      setSelectedSemanticCandidateId(null)
      setSemanticPreviewState({
        previewing: false,
        applying: false,
        error: 'No accepted Variorum rows are available for semantic preview.',
        success: null,
      })
      return
    }

    const sourceRowIds = selectedRange?.rowIds?.length
      ? selectedRange.rowIds
      : (selectedCell?.rowId ? [selectedCell.rowId] : [])
    if (sourceRowIds.length === 0) {
      setSemanticPreview(null)
      setSelectedSemanticCandidateId(null)
      setSemanticPreviewState({
        previewing: false,
        applying: false,
        error: 'Select a row or range before previewing semantic matches.',
        success: null,
      })
      return
    }

    setSemanticPreviewState({
      previewing: true,
      applying: false,
      error: null,
      success: null,
    })
    setSemanticPreview(null)
    setSelectedSemanticCandidateId(null)
    setWitnessPropagationProposal(null)
    setContextPropagationPreview(null)
    setSelectedUnitRepairPreview(null)

    try {
      const preview = await requestWorkbenchSemanticAlignmentPreview({
        witnesses: scopeWitnesses,
        rows: resolvedRows,
        sourceRowIds,
        sourceWitnessIds: diffPivotWitness ? [diffPivotWitness.id] : undefined,
        targetWitnessIds: visibleScopeWitnesses.map(witness => witness.id),
        maxCandidatesPerRow: 3,
        minScore: 0.30,
      })
      setSemanticPreview(preview)
      const firstCandidate = preview.candidates[0]
      setSelectedSemanticCandidateId(firstCandidate?.candidateId || null)
      if (firstCandidate) {
        const witness = scopeWitnesses.find(candidate => candidate.id === firstCandidate.targetWitnessId)
        setSelectedCell({ rowId: firstCandidate.sourceRowId, witnessId: firstCandidate.targetWitnessId })
        setSelectionAnchorCell({ rowId: firstCandidate.sourceRowId, witnessId: firstCandidate.targetWitnessId })
        if (witness) {
          setActiveWitnessSigla(witness.sigla)
        }
        scrollMatrixRowIntoView(firstCandidate.sourceRowId, {
          block: 'center',
          behavior: 'smooth'
        })
      }
      setSemanticPreviewState({
        previewing: false,
        applying: false,
        error: preview.candidates.length === 0 ? 'No semantic candidates cleared the current score threshold.' : null,
        success: preview.candidates.length > 0
          ? `Ghosted ${preview.candidates.length} semantic candidate${preview.candidates.length === 1 ? '' : 's'} in Variorum.`
          : null,
      })
    } catch (error) {
      setSemanticPreview(null)
      setSelectedSemanticCandidateId(null)
      setSemanticPreviewState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to build semantic preview.',
        success: null,
      })
    }
  })

  const handleRejectSemanticCandidate = useEventCallback(async (candidate: WorkbenchSemanticAlignmentCandidate | null) => {
    if (isMatrixContentApplying) {
      return
    }
    if (!candidate) {
      return
    }
    setSemanticPreview(current => current ? {
      ...current,
      candidates: current.candidates.filter(nextCandidate => nextCandidate.candidateId !== candidate.candidateId)
    } : current)
    setSelectedSemanticCandidateId(previous => previous === candidate.candidateId ? null : previous)
    await persistEditorialEvent({
      source: 'semantic-preview-apply',
      kind: 'semantic_reject_candidate',
      params: {
        candidateId: candidate.candidateId,
        sourceRowId: candidate.sourceRowId,
        originRowId: candidate.targetRowId,
        targetWitnessId: candidate.targetWitnessId,
        targetUnitUuid: candidate.targetUnitUuid,
        score: candidate.score,
        scoreMargin: candidate.scoreMargin,
        modelId: candidate.modelId,
      },
      summary: `Rejected semantic candidate for ${scopeWitnesses.find(witness => witness.id === candidate.targetWitnessId)?.sigla || candidate.targetWitnessId}`,
      affectedWitnessIds: [candidate.targetWitnessId],
      affectedRowIds: Array.from(new Set([candidate.sourceRowId, candidate.targetRowId])),
      beforeRows: resolvedRows,
      afterRows: resolvedRows,
      metadata: {
        previewId: semanticPreview?.previewId || null,
        modelId: semanticPreview?.modelId || candidate.modelId,
        candidate
      }
    })
    setSemanticPreviewState(previous => ({
      ...previous,
      error: null,
      success: 'Rejected semantic ghost candidate.'
    }))
  })

  const handleApplySemanticCandidate = useEventCallback(async (candidate: WorkbenchSemanticAlignmentCandidate | null) => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !candidate) {
      setSemanticPreviewState(previous => ({
        ...previous,
        error: 'Select a semantic ghost candidate before applying.',
        success: null,
      }))
      return
    }

    setSemanticPreviewState(previous => ({
      ...previous,
      applying: true,
      error: null,
      success: null,
    }))

    try {
      const result = applySemanticCandidateToRows({
        rows: resolvedRows,
        candidate
      })
      const changedRowIdSet = new Set(result.changedRowIds)
      const changedRows = result.updatedRows.filter(row => changedRowIdSet.has(row.id))
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        source: 'semantic-preview-apply',
        kind: 'semantic_place_candidate',
        params: {
          candidateId: candidate.candidateId,
          sourceRowId: candidate.sourceRowId,
          originRowId: candidate.targetRowId,
          targetWitnessId: candidate.targetWitnessId,
          targetUnitUuid: candidate.targetUnitUuid,
          score: candidate.score,
          scoreMargin: candidate.scoreMargin,
          modelId: candidate.modelId,
        },
        summary: `Applied semantic candidate to ${scopeWitnesses.find(witness => witness.id === candidate.targetWitnessId)?.sigla || candidate.targetWitnessId}`,
        affectedWitnessIds: [candidate.targetWitnessId],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: {
          previewId: semanticPreview?.previewId || null,
          modelId: semanticPreview?.modelId || candidate.modelId,
          candidate
        }
      })
      setSemanticPreview(current => current ? {
        ...current,
        candidates: current.candidates.filter(nextCandidate => (
          nextCandidate.candidateId !== candidate.candidateId &&
          nextCandidate.targetUnitUuid !== candidate.targetUnitUuid &&
          !(nextCandidate.sourceRowId === candidate.sourceRowId && nextCandidate.targetWitnessId === candidate.targetWitnessId)
        ))
      } : current)
      setSelectedSemanticCandidateId(null)
      setSelectedCell(result.selectedCell)
      setSelectionAnchorCell(result.selectedCell)
      setSelectedRange(null)
      const message = 'Applied semantic ghost candidate'
      setSemanticPreviewState({
        previewing: false,
        applying: false,
        error: null,
        success: message,
      })
      showToast(message)
    } catch (error) {
      setSemanticPreviewState(previous => ({
        ...previous,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to apply semantic candidate.',
        success: null,
      }))
    }
  })

  const toggleCrossTraditionWitnessSelection = useCallback((witnessId: string) => {
    setSelectedCrossTraditionWitnessIds(previous => previous.includes(witnessId)
      ? previous.filter(candidate => candidate !== witnessId)
      : [...previous, witnessId]
    )
  }, [])

  const focusCrossTraditionCandidate = useEventCallback((candidate: LedgerCrossTraditionCandidate) => {
    if (
      isMatrixContentApplying
      && diffEnabled
      && diffMode === 'apparatus'
      && apparatusScope === 'row'
    ) {
      return
    }
    setSelectedCrossTraditionCandidateId(candidate.candidateId)
    const rowId = candidate.targetRowIds[0]
    const focusWitness = activeVisibleWitness || visibleScopeWitnesses[0] || scopeWitnesses[0] || null
    if (rowId && focusWitness) {
      const nextSelection = { rowId, witnessId: focusWitness.id }
      setSelectedCell(nextSelection)
      setSelectionAnchorCell(nextSelection)
      setSelectedRange(candidate.targetRowIds.length > 1
        ? { witnessId: focusWitness.id, rowIds: candidate.targetRowIds }
        : null
      )
      scrollMatrixRowIntoView(rowId, {
        block: 'center',
        behavior: 'smooth'
      })
    }
  })

  const handleSetCrossTraditionCandidateStatus = useEventCallback((
    candidate: LedgerCrossTraditionCandidate,
    reviewStatus: LedgerCrossTraditionCandidate['reviewStatus'],
  ) => {
    const updateCandidates = (current: LedgerCrossTraditionCandidate[]) => (
      setLedgerCrossTraditionCandidateStatus(current, candidate.candidateId, reviewStatus)
    )
    setCrossTraditionCandidates(updateCandidates)
    if (selectedCrossTraditionRunId) {
      rememberCrossTraditionPreviewRuns(previous => previous.map(run => (
        run.runId === selectedCrossTraditionRunId
          ? {
            ...run,
            candidates: updateCandidates(run.candidates),
          }
          : run
      )))
    }
    setCrossTraditionState(previous => ({
      ...previous,
      error: null,
      success: reviewStatus === 'accepted'
        ? 'Marked cross-tradition candidate for materialization.'
        : reviewStatus === 'rejected'
          ? 'Rejected cross-tradition candidate.'
          : 'Deferred cross-tradition candidate.',
    }))
  })

  const handleDiscardCrossTraditionPreview = useEventCallback(() => {
    setCrossTraditionCandidates([])
    setSelectedCrossTraditionCandidateId(null)
    setCrossTraditionState({
      previewing: false,
      applying: false,
      error: null,
      success: null,
      modelId: null,
    })
  })

  const handleGenerateCrossTraditionPreview = useEventCallback(async (
    resumedRun?: LedgerCrossTraditionPreviewRunRecord
  ) => {
    if (!chapterId || resolvedRows.length === 0) {
      setCrossTraditionCandidates([])
      setSelectedCrossTraditionCandidateId(null)
      setCrossTraditionState({
        previewing: false,
        applying: false,
        error: 'No accepted Ledger rows are available for cross-tradition collation.',
        success: null,
        modelId: null,
        activeRunId: null,
      })
      return
    }
    const sourceOptionsForRun = resumedRun
      ? resumedRun.witnessIds
        .map(witnessId => crossTraditionSourceOptions.find(option => option.witnessId === witnessId) || null)
        .filter((option): option is LedgerCrossTraditionSourceOption => Boolean(option))
      : selectedCrossTraditionSourceOptions

    if (sourceOptionsForRun.length === 0) {
      setCrossTraditionState(previous => ({
        ...previous,
        error: 'Select at least one external witness.',
        success: null,
      }))
      return
    }

    const rowSpine = buildLedgerCrossTraditionRowSpineText({
      chapterId,
      contextLabel,
      rows: resolvedRows,
      witnesses: interactiveScopeWitnesses,
      textLookupByWitnessId: witnessUnitTextLookupByWitnessId,
    })
    if (rowSpine.text.segments?.length === 0) {
      setCrossTraditionCandidates([])
      setSelectedCrossTraditionCandidateId(null)
      setCrossTraditionState({
        previewing: false,
        applying: false,
        error: 'The current Ledger rows do not contain enough text to build a row spine.',
        success: null,
        modelId: null,
        activeRunId: null,
      })
      return
    }

    const runId = createLedgerCrossTraditionPreviewRunId()
    const startedAt = Date.now()
    const sourceLabels = sourceOptionsForRun.map(option => option.sigla)
    const runRecord: LedgerCrossTraditionPreviewRunRecord = {
      runId,
      status: 'running',
      startedAt,
      workId: sourceOptionsForRun[0]?.workId || crossTraditionWorkId,
      chapterId: sourceOptionsForRun[0]?.chapterId || crossTraditionChapterId,
      witnessIds: sourceOptionsForRun.map(option => option.witnessId),
      witnessLabels: sourceLabels,
      sourceContextLabel: sourceOptionsForRun[0]?.contextLabel || '',
      targetContextLabel: contextLabel || null,
      modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
      candidateCount: 0,
      candidates: [],
      error: null,
    }
    if (resumedRun) {
      setCrossTraditionWorkId(runRecord.workId)
      setCrossTraditionChapterId(runRecord.chapterId)
      setSelectedCrossTraditionWitnessIds(runRecord.witnessIds)
    }
    setSelectedCrossTraditionRunId(runId)
    rememberCrossTraditionPreviewRuns(previous => upsertLedgerCrossTraditionPreviewRun(previous, runRecord))

    const controller = new AbortController()
    crossTraditionPreviewAbortControllerRef.current?.abort()
    crossTraditionPreviewAbortControllerRef.current = controller

    setCrossTraditionState({
      previewing: true,
      applying: false,
      error: null,
      success: resumedRun ? 'Resuming cross-tradition preview run.' : null,
      modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
      activeRunId: runId,
    })
    setCrossTraditionCandidates([])
    setSelectedCrossTraditionCandidateId(null)
    setWitnessPropagationProposal(null)
    setContextPropagationPreview(null)
    setSelectedUnitRepairPreview(null)
    setSemanticPreview(null)

    try {
      const nextCandidates: LedgerCrossTraditionCandidate[] = []
      for (const sourceOption of sourceOptionsForRun) {
        if (controller.signal.aborted) {
          const abortError = new Error('Cross-tradition preview was cancelled.')
          abortError.name = 'AbortError'
          throw abortError
        }
        const sourceText = buildLedgerCrossTraditionSourceText(sourceOption)
        if ((sourceText.text.segments || []).length === 0) {
          continue
        }
        const run = await requestWorkbenchCrossTextAlignmentPreview({
          clientRunId: runId,
          texts: [sourceText.text, rowSpine.text],
          sourceTextId: sourceText.text.textId,
          targetTextIds: [rowSpine.text.textId],
          scorer: LEDGER_CROSS_TRADITION_SCORER,
          rerankModelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
          queryPrompt: [
            'Cross-tradition Ledger Safe collation.',
            'Retrieve only same narrative beats, arguments, maxims, concrete images, or close paraphrastic rewrites.',
            'The target text is an accepted row spine from the current work; each target segment is one Ledger row.',
          ].join(' '),
          windowSizes: [1, 2, 3],
          maxCandidatesPerSource: 4,
          maxGlobalCandidates: 80,
          minScore: 0.16,
          signal: controller.signal,
        })
        if (controller.signal.aborted) {
          const abortError = new Error('Cross-tradition preview was cancelled.')
          abortError.name = 'AbortError'
          throw abortError
        }
        nextCandidates.push(...mapCrossTextRunToLedgerCrossTraditionCandidates({
          run,
          sourceOption,
          sourceSegmentRefs: sourceText.segmentRefs,
          rowSpine,
        }))
      }

      const firstCandidate = nextCandidates[0] || null
      setCrossTraditionCandidates(nextCandidates)
      setSelectedCrossTraditionCandidateId(firstCandidate?.candidateId || null)
      if (firstCandidate) {
        focusCrossTraditionCandidate(firstCandidate)
      }
      rememberCrossTraditionPreviewRuns(previous => upsertLedgerCrossTraditionPreviewRun(previous, {
        ...runRecord,
        status: 'completed',
        endedAt: Date.now(),
        candidateCount: nextCandidates.length,
        candidates: nextCandidates,
        error: nextCandidates.length === 0 ? 'No cross-tradition candidates cleared the current score threshold.' : null,
      }))
      setSelectedCrossTraditionRunId(runId)
      setCrossTraditionState({
        previewing: false,
        applying: false,
        error: nextCandidates.length === 0 ? 'No cross-tradition candidates cleared the current score threshold.' : null,
        success: nextCandidates.length > 0
          ? `Prepared ${nextCandidates.length} cross-tradition candidate${nextCandidates.length === 1 ? '' : 's'} with ${LEDGER_CROSS_TRADITION_RERANK_MODEL_ID}.`
          : null,
        modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
        activeRunId: null,
      })
    } catch (error) {
      if (isAbortError(error)) {
        rememberCrossTraditionPreviewRuns(previous => upsertLedgerCrossTraditionPreviewRun(previous, {
          ...runRecord,
          status: 'cancelled',
          endedAt: Date.now(),
          error: 'Cancelled by editor.',
        }))
        setSelectedCrossTraditionRunId(runId)
        setCrossTraditionState({
          previewing: false,
          applying: false,
          error: null,
          success: 'Cancelled cross-tradition preview.',
          modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
          activeRunId: null,
        })
        return
      }
      setCrossTraditionCandidates([])
      setSelectedCrossTraditionCandidateId(null)
      rememberCrossTraditionPreviewRuns(previous => upsertLedgerCrossTraditionPreviewRun(previous, {
        ...runRecord,
        status: 'failed',
        endedAt: Date.now(),
        error: error instanceof Error ? error.message : 'Failed to build cross-tradition preview.',
      }))
      setSelectedCrossTraditionRunId(runId)
      setCrossTraditionState({
        previewing: false,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to build cross-tradition preview.',
        success: null,
        modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
        activeRunId: null,
      })
    } finally {
      if (crossTraditionPreviewAbortControllerRef.current === controller) {
        crossTraditionPreviewAbortControllerRef.current = null
      }
    }
  })

  const handleCancelCrossTraditionPreview = useEventCallback(() => {
    const activeRunId = crossTraditionState.activeRunId || null
    if (activeRunId) {
      void cancelWorkbenchCrossTextAlignmentPreview(activeRunId).catch(() => {
        // The browser abort below still prevents stale UI updates if the server has already finished.
      })
    }
    crossTraditionPreviewAbortControllerRef.current?.abort()
    crossTraditionPreviewAbortControllerRef.current = null
    if (activeRunId) {
      rememberCrossTraditionPreviewRuns(previous => {
        const existing = previous.find(run => run.runId === activeRunId)
        if (!existing || existing.status !== 'running') {
          return previous
        }
        return upsertLedgerCrossTraditionPreviewRun(previous, {
          ...existing,
          status: 'cancelled',
          endedAt: Date.now(),
          error: 'Cancelled by editor.',
        })
      })
      setSelectedCrossTraditionRunId(activeRunId)
    }
    setCrossTraditionState(previous => ({
      ...previous,
      previewing: false,
      error: null,
      success: 'Cancelled cross-tradition preview.',
      activeRunId: null,
    }))
  })

  const handleInspectCrossTraditionPreviewRun = useEventCallback((run: LedgerCrossTraditionPreviewRunRecord) => {
    if (crossTraditionState.previewing || crossTraditionState.applying) {
      return
    }
    setSelectedCrossTraditionRunId(run.runId)
    setCrossTraditionWorkId(run.workId)
    setCrossTraditionChapterId(run.chapterId)
    setSelectedCrossTraditionWitnessIds(run.witnessIds)
    setCrossTraditionCandidates(run.candidates)
    const firstCandidate = run.candidates[0] || null
    setSelectedCrossTraditionCandidateId(firstCandidate?.candidateId || null)
    if (firstCandidate) {
      focusCrossTraditionCandidate(firstCandidate)
    }
    setCrossTraditionState(previous => ({
      ...previous,
      previewing: false,
      error: run.status === 'failed' ? run.error || 'Cross-tradition preview failed.' : null,
      success: run.status === 'completed'
        ? `Loaded ${run.candidateCount} candidate${run.candidateCount === 1 ? '' : 's'} from previous cross-tradition run.`
        : run.status === 'cancelled'
          ? 'Loaded cancelled cross-tradition run. Resume it to request the preview again.'
          : 'Loaded running cross-tradition run metadata.',
      modelId: run.modelId || LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
      activeRunId: null,
    }))
  })

  const handleResumeCrossTraditionPreviewRun = useEventCallback((run: LedgerCrossTraditionPreviewRunRecord) => {
    if (crossTraditionState.previewing || crossTraditionState.applying) {
      return
    }
    void handleGenerateCrossTraditionPreview(run)
  })

  const handleApplyCrossTraditionCandidates = useEventCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !onCreateCrossTraditionWitness) {
      setCrossTraditionState(previous => ({
        ...previous,
        error: 'Cross-tradition materialization is not available in this view.',
        success: null,
      }))
      return
    }
    const acceptedCandidates = crossTraditionCandidates.filter(candidate => candidate.reviewStatus === 'accepted')
    if (acceptedCandidates.length === 0) {
      setCrossTraditionState(previous => ({
        ...previous,
        error: 'Accept at least one cross-tradition candidate before applying.',
        success: null,
      }))
      return
    }

    setCrossTraditionState(previous => ({
      ...previous,
      applying: true,
      error: null,
      success: null,
    }))

    try {
      let workingRows = resolvedRows
      const createdWitnessIds: string[] = []
      const createdProfileIds: string[] = []
      const changedRowIds = new Set<string>()
      const appliedCandidateIds = new Set<string>()
      const sourceOptionsById = new Map(crossTraditionSourceOptions.map(option => [option.witnessId, option]))
      const rowSpineTextId = `${LEDGER_CROSS_TRADITION_ROW_SPINE_TEXT_ID_PREFIX}:${chapterId}`
      const acceptedBySourceWitnessId = new Map<string, LedgerCrossTraditionCandidate[]>()
      acceptedCandidates.forEach(candidate => {
        const group = acceptedBySourceWitnessId.get(candidate.sourceWitnessId) || []
        group.push(candidate)
        acceptedBySourceWitnessId.set(candidate.sourceWitnessId, group)
      })

      for (const [sourceWitnessId, sourceCandidates] of Array.from(acceptedBySourceWitnessId.entries())) {
        const sourceOption = sourceOptionsById.get(sourceWitnessId)
        if (!sourceOption) {
          continue
        }
        const plan = buildLedgerCrossTraditionWitnessImportPlan({
          sourceOption,
          acceptedCandidates: sourceCandidates,
          existingWitnesses: [...scopeWitnesses, ...createdWitnessIds.map(id => ({ id, sigla: id } as Witness))],
          targetBookId: bookId || null,
          targetChapterId: chapterId,
          targetContextLabel: contextLabel || null,
          activeCollationLabel: ledgerName || contextLabel || snapshotScopeLabel,
          rowSpineTextId,
        })
        const materializedWitnessId = await onCreateCrossTraditionWitness(plan)
        createdWitnessIds.push(materializedWitnessId)
        createdProfileIds.push(plan.profile.profileId)
        const application = applyLedgerCrossTraditionCandidateMappings({
          rows: workingRows,
          materializedWitnessId,
          candidates: sourceCandidates,
          unitByCandidateId: plan.unitByCandidateId,
          unitByCandidateRowKey: plan.unitByCandidateRowKey,
          profile: plan.profile,
        })
        workingRows = application.updatedRows
        application.changedRowIds.forEach(rowId => changedRowIds.add(rowId))
        application.appliedCandidateIds.forEach(candidateId => appliedCandidateIds.add(candidateId))
      }

      const changedRows = workingRows.filter(row => changedRowIds.has(row.id))
      if (changedRows.length > 0) {
        await upsertRows(chapterId, changedRows, { autoRelabel: false })
        await persistEditorialEvent({
          source: 'cross-tradition-apply',
          kind: 'cross_tradition_place_candidates',
          params: {
            candidateIds: Array.from(appliedCandidateIds),
            sourceWitnessIds: Array.from(acceptedBySourceWitnessId.keys()),
            materializedWitnessIds: createdWitnessIds,
            profileIds: createdProfileIds,
            rerankModelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
            rowOwnership: 'active_collation',
            sourceTextRole: 'mediator',
          },
          summary: `Applied ${appliedCandidateIds.size} cross-tradition candidate${appliedCandidateIds.size === 1 ? '' : 's'}`,
          affectedWitnessIds: createdWitnessIds,
          affectedRowIds: Array.from(changedRowIds),
          beforeRows: resolvedRows,
          afterRows: workingRows,
          metadata: {
            candidates: acceptedCandidates.filter(candidate => appliedCandidateIds.has(candidate.candidateId)),
            profileIds: createdProfileIds,
            rerankModelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
            rowOwnership: 'active_collation',
            sourceTextRole: 'mediator',
          }
        })
      }

      setCrossTraditionCandidates(current => current.filter(candidate => !appliedCandidateIds.has(candidate.candidateId)))
      setSelectedCrossTraditionCandidateId(null)
      const message = `Materialized ${createdProfileIds.length} active projection profile${createdProfileIds.length === 1 ? '' : 's'} across ${changedRowIds.size} row${changedRowIds.size === 1 ? '' : 's'}.`
      setCrossTraditionState({
        previewing: false,
        applying: false,
        error: null,
        success: message,
        modelId: LEDGER_CROSS_TRADITION_RERANK_MODEL_ID,
      })
      showToast(message)
    } catch (error) {
      setCrossTraditionState(previous => ({
        ...previous,
        applying: false,
        error: error instanceof Error ? error.message : 'Failed to apply cross-tradition candidates.',
        success: null,
      }))
    }
  })

  const createLedgerSnapshot = useEventCallback(async ({
    reason,
    label
  }: {
    reason: LedgerSafeSnapshotReason
    label: string
  }) => {
    if (!chapterId || !runtimeEnableSnapshots) {
      return null
    }
    try {
      const snapshot = await createLedgerSafeChapterSnapshot({
        chapterId,
        scopeId: snapshotScopeId,
        scopeLabel: snapshotScopeLabel,
        label,
        reason,
        rows: resolvedRows
      })
      setLedgerSnapshots(current =>
        [snapshot, ...current]
          .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
          .slice(0, 8)
      )
      return snapshot
    } catch (error) {
      console.error('Failed to create Variorum snapshot', error)
      return null
    }
  })

  const retainRowTitleGenerationRun = useCallback((
    run: LedgerSafeRowTitleGenerationRun | null,
  ) => {
    setRowTitleGenerationRun(run)
    if (!chapterId || typeof window === 'undefined') {
      return
    }
    try {
      if (run) {
        saveLedgerSafeRowTitleGenerationRun(run)
      } else {
        clearLedgerSafeRowTitleGenerationRun(chapterId)
      }
    } catch (error) {
      console.warn('Failed to retain row-title generation run', error)
      setRowTitleGenerationError('This run is available in memory, but could not be retained across reload.')
    }
  }, [chapterId])

  const retainStructuralRangeGenerationRun = useCallback((
    run: LedgerSafeStructuralRangeGenerationRun | null,
  ) => {
    setStructuralRangeGenerationRun(run)
    if (!chapterId || typeof window === 'undefined') return
    try {
      if (run) {
        saveLedgerSafeStructuralRangeGenerationRun(run)
      } else {
        clearLedgerSafeStructuralRangeGenerationRun(chapterId)
      }
    } catch (error) {
      console.warn('Failed to retain structural-range generation run', error)
      setStructuralRangeError('The structural suggestion remains in memory but could not be retained across reload.')
    }
  }, [chapterId])

  const persistStructuralRanges = useEventCallback(async (
    ranges: StructuralRange[],
  ): Promise<ChapterStructuralRangeLayer> => {
    if (!chapterId) throw new Error('No chapter is open')
    const replacingIds = new Set(ranges.map(range => range.id))
    const now = new Date().toISOString()
    const nextLayer: ChapterStructuralRangeLayer = {
      ...structuralRangeLayer,
      bookId: structuralRangeLayer.bookId || bookId || undefined,
      version: structuralRangeLayer.version + 1,
      ranges: [
        ...structuralRangeLayer.ranges.filter(range => !replacingIds.has(range.id)),
        ...ranges,
      ],
      updatedAt: now,
    }
    const error = validateStructuralRangeLayer({
      layer: nextLayer,
      rowOrderById: structuralRowOrderById,
    }).find(diagnostic => diagnostic.severity === 'error')
    if (error) throw new Error(error.message)
    await saveStructuralRangeLayer(nextLayer)
    return nextLayer
  })

  const getStructuralRowSpanIds = useEventCallback((
    startRowId: string,
    endRowId: string,
  ): string[] => {
    const startIndex = structuralRowOrderById.get(startRowId)
    const endIndex = structuralRowOrderById.get(endRowId)
    if (startIndex === undefined || endIndex === undefined || startIndex > endIndex) return []
    return orderedStructuralRows.slice(startIndex, endIndex + 1).map(row => row.id)
  })

  const closeStructuralRangeEditor = useEventCallback(() => {
    setStructuralRangeEditor(null)
    setStructuralRangeMenu(null)
    setStructuralRangeRibbonAnchor(null)
    setStructuralRangeRibbonPosition(null)
    setStructuralBoundaryPlacement(null)
    setStructuralBoundaryHoverRowId('')
    setSelectedStructuralRowIds([])
    setStructuralSelectionAnchorRowId('')
    setStructuralRangeError(null)
  })

  const handleStructuralBoundaryTarget = useEventCallback((row: AnchoredRow) => {
    if (!structuralBoundaryPlacement || !structuralRangeEditor) return
    const targetIndex = structuralRowOrderById.get(row.id)
    const oppositeRowId = structuralBoundaryPlacement.edge === 'start'
      ? structuralRangeEditor.endRowId
      : structuralRangeEditor.startRowId
    const oppositeIndex = structuralRowOrderById.get(oppositeRowId)
    if (targetIndex === undefined || oppositeIndex === undefined) return
    if (
      (structuralBoundaryPlacement.edge === 'start' && targetIndex > oppositeIndex)
      || (structuralBoundaryPlacement.edge === 'end' && targetIndex < oppositeIndex)
    ) {
      setStructuralRangeError(
        structuralBoundaryPlacement.edge === 'start'
          ? 'The start must remain at or before the end row.'
          : 'The end must remain at or after the start row.'
      )
      return
    }
    const startRowId = structuralBoundaryPlacement.edge === 'start'
      ? row.id
      : structuralRangeEditor.startRowId
    const endRowId = structuralBoundaryPlacement.edge === 'end'
      ? row.id
      : structuralRangeEditor.endRowId
    const parentOptions = findStructuralRangeParentOptions({
      layer: structuralRangeLayer,
      rangeId: structuralRangeEditor.rangeId || undefined,
      kind: structuralRangeEditor.kind || null,
      startRowId,
      endRowId,
      rowOrderById: structuralRowOrderById,
    })
    const currentParentIsValid = parentOptions.some(option => (
      option.id === structuralRangeEditor.parentRangeId
    ))
    setStructuralRangeEditor({
      ...structuralRangeEditor,
      startRowId,
      endRowId,
      parentRangeId: currentParentIsValid
        ? structuralRangeEditor.parentRangeId
        : findDefaultStructuralRangeParentId(parentOptions) || '',
    })
    setSelectedStructuralRowIds(getStructuralRowSpanIds(startRowId, endRowId))
    setStructuralSelectionAnchorRowId(startRowId)
    setStructuralRangeRibbonAnchor({ kind: 'row', id: row.id })
    setStructuralRangeRibbonPosition(null)
    setStructuralBoundaryPlacement(null)
    setStructuralBoundaryHoverRowId('')
    setStructuralRangeMenu(null)
    setStructuralRangeError(null)
  })

  const handleStructuralRowLabelClick = useEventCallback((
    row: AnchoredRow,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (structuralBoundaryPlacement) {
      handleStructuralBoundaryTarget(row)
      return
    }
    let rowIds = [row.id]
    if (event.shiftKey && structuralSelectionAnchorRowId) {
      const anchorIndex = structuralRowOrderById.get(structuralSelectionAnchorRowId)
      const targetIndex = structuralRowOrderById.get(row.id)
      if (anchorIndex !== undefined && targetIndex !== undefined) {
        rowIds = orderedStructuralRows
          .slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
          .map(candidate => candidate.id)
      }
    } else {
      setStructuralSelectionAnchorRowId(row.id)
    }
    setSelectedStructuralRowIds(rowIds)
    setStructuralRangeEditor(current => {
      const preserveDraft = Boolean(event.shiftKey && current && !current.rangeId)
      const kind = preserveDraft ? current?.kind || '' : ''
      const parentOptions = findStructuralRangeParentOptions({
        layer: structuralRangeLayer,
        kind: kind || null,
        startRowId: rowIds[0],
        endRowId: rowIds[rowIds.length - 1],
        rowOrderById: structuralRowOrderById,
      })
      const currentParentId = preserveDraft ? current?.parentRangeId || '' : ''
      return {
        rangeId: null,
        title: preserveDraft ? current?.title || '' : '',
        kind,
        startRowId: rowIds[0],
        endRowId: rowIds[rowIds.length - 1],
        parentRangeId: parentOptions.some(option => option.id === currentParentId)
          ? currentParentId
          : findDefaultStructuralRangeParentId(parentOptions) || '',
      }
    })
    setStructuralRangeRibbonAnchor({ kind: 'row', id: row.id })
    setStructuralRangeRibbonPosition(null)
    setStructuralRangeMenu(null)
    setStructuralRangeError(null)
  })

  const openStructuralRangeEditor = useEventCallback((range: StructuralRange) => {
    const startIndex = structuralRowOrderById.get(range.startRowId)
    const endIndex = structuralRowOrderById.get(range.endRowId)
    if (startIndex === undefined || endIndex === undefined) return
    const rowIds = orderedStructuralRows.slice(startIndex, endIndex + 1).map(row => row.id)
    setStructuralSelectionAnchorRowId(range.startRowId)
    setSelectedStructuralRowIds(rowIds)
    setStructuralRangeEditor({
      rangeId: range.id,
      title: range.title,
      kind: range.kind || '',
      startRowId: range.startRowId,
      endRowId: range.endRowId,
      parentRangeId: range.parentRangeId || '',
    })
    setStructuralRangeRibbonAnchor({ kind: 'range', id: range.id })
    setStructuralRangeRibbonPosition(null)
    setStructuralRangeMenu(null)
    setStructuralBoundaryPlacement(null)
    setStructuralBoundaryHoverRowId('')
    setStructuralRangeError(null)
    requestAnimationFrame(() => {
      scrollMatrixRowIntoView(range.startRowId, { block: 'center', behavior: 'smooth' })
    })
  })

  const handleSaveStructuralRange = useEventCallback(async () => {
    if (isMatrixContentApplying || !structuralRangeEditor) return
    const title = structuralRangeEditor.title.trim()
    if (!title) {
      setStructuralRangeError('A structural range needs a title.')
      return
    }
    if (!structuralRangeEditor.kind) {
      setStructuralRangeError('Choose a structural kind before confirming the range.')
      return
    }
    setIsLedgerApplying(true)
    setStructuralRangeError(null)
    try {
      const existing = structuralRangeEditor.rangeId
        ? structuralRangeById.get(structuralRangeEditor.rangeId)
        : null
      const now = new Date().toISOString()
      const draft = existing
        ? {
            ...existing,
            title,
            kind: structuralRangeEditor.kind,
            startRowId: structuralRangeEditor.startRowId,
            endRowId: structuralRangeEditor.endRowId,
            parentRangeId: structuralRangeEditor.parentRangeId || null,
            status: 'confirmed' as const,
            updatedAt: now,
            updatedBy: 'human',
          }
        : {
            ...createStructuralRange({
              title,
              kind: structuralRangeEditor.kind,
              startRowId: structuralRangeEditor.startRowId,
              endRowId: structuralRangeEditor.endRowId,
              parentRangeId: structuralRangeEditor.parentRangeId || null,
              status: 'confirmed',
              origin: 'human',
              now,
            }),
            updatedBy: 'human',
          }
      const nextLayer = upsertStructuralRange({
        layer: structuralRangeLayer,
        range: draft,
        rowOrderById: structuralRowOrderById,
        now,
      })
      await saveStructuralRangeLayer(nextLayer)
      closeStructuralRangeEditor()
      showToast(
        `${existing ? 'Updated' : 'Created'} `
        + `${STRUCTURAL_RANGE_KIND_LABELS[structuralRangeEditor.kind as StructuralRangeKind].toLowerCase()} range`
      )
    } catch (error) {
      setStructuralRangeError(error instanceof Error ? error.message : 'The structural range could not be saved.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleDeleteStructuralRange = useEventCallback(async (range: StructuralRange) => {
    if (isMatrixContentApplying) return
    if (!window.confirm(`Delete “${range.title}”? Child ranges will move to its parent when valid.`)) return
    setIsLedgerApplying(true)
    setStructuralRangeError(null)
    try {
      const nextLayer = removeStructuralRange({
        layer: structuralRangeLayer,
        rangeId: range.id,
        rowOrderById: structuralRowOrderById,
      })
      await saveStructuralRangeLayer(nextLayer)
      closeStructuralRangeEditor()
      showToast('Deleted structural range')
    } catch (error) {
      setStructuralRangeError(error instanceof Error ? error.message : 'The structural range could not be deleted.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleImportLegacyStructuralRanges = useEventCallback(async () => {
    if (isMatrixContentApplying || !legacySceneMigrationPreview.candidates.length) return
    setIsLedgerApplying(true)
    setStructuralRangeError(null)
    try {
      await persistStructuralRanges(legacySceneMigrationPreview.candidates.map(candidate => candidate.range))
      showToast(`Imported ${legacySceneMigrationPreview.candidates.length} legacy range candidate${legacySceneMigrationPreview.candidates.length === 1 ? '' : 's'}`)
    } catch (error) {
      setStructuralRangeError(error instanceof Error ? error.message : 'Legacy ranges could not be imported.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleGenerateStructuralRanges = useEventCallback(async () => {
    if (!chapterId || !selectedStructuralRows.length || !structuralRangeEditor) {
      setStructuralRangeError('Select the structural range you want to title first.')
      return
    }
    if (selectedStructuralRows.length > STRUCTURAL_RANGE_GENERATION_MAX_ROWS) {
      setStructuralRangeError(`Select at most ${STRUCTURAL_RANGE_GENERATION_MAX_ROWS} rows for one range title.`)
      return
    }
    const target: StructuralRangeTitleTarget = {
      rangeId: structuralRangeEditor.rangeId,
      title: structuralRangeEditor.title,
      kind: structuralRangeEditor.kind,
      startRowId: structuralRangeEditor.startRowId,
      endRowId: structuralRangeEditor.endRowId,
      parentRangeId: structuralRangeEditor.parentRangeId,
    }
    setIsStructuralRangeGenerating(true)
    setStructuralRangeError(null)
    let execution: PiStructuredGenerationExecution | null = null
    try {
      execution = await requestPiStructuredGeneration(buildStructuralRangeStructuredGenerationRequest({
        rows: selectedStructuralRows,
        layer: structuralRangeLayer,
        target,
        modelId: structuralRangeGenerationModelId,
        thinkingLevel: structuralRangeGenerationThinkingLevel,
      }))
      const suggestion = parseStructuralRangeGenerationExecution({ execution })
      const run = createLedgerSafeStructuralRangeGenerationRun({
        chapterId,
        rows: selectedStructuralRows,
        layer: structuralRangeLayer,
        target,
        execution,
        suggestion,
      })
      retainStructuralRangeGenerationRun(run)
      showToast('Prepared one title for the selected range')
    } catch (error) {
      if (execution) {
        const failure = createLedgerSafeStructuralRangeGenerationFailure({
          chapterId,
          rows: selectedStructuralRows,
          layer: structuralRangeLayer,
          target,
          execution,
          error,
        })
        console.warn('Pi structural-range generation failed validation', {
          chapterId: failure.chapterId,
          generationId: failure.execution.generationId,
          schemaError: failure.execution.reply.schemaError,
          attemptCount: failure.execution.reply.attempts?.length || 1,
        })
        try {
          saveLedgerSafeStructuralRangeGenerationFailure(failure)
          setStructuralRangeError(describeStructuralRangeGenerationFailure(failure))
        } catch (storageError) {
          console.warn('Failed to retain structural-range generation diagnostic', storageError)
          setStructuralRangeError(`${failure.errorMessage}. No title was used, and the failed output could not be retained locally.`)
        }
      } else {
        setStructuralRangeError(error instanceof Error ? error.message : 'Range-title generation failed.')
      }
    } finally {
      setIsStructuralRangeGenerating(false)
    }
  })

  const handleUseStructuralRangeTitleSuggestion = useEventCallback(() => {
    if (isMatrixContentApplying) return
    const run = structuralRangeGenerationRun
    if (!run || run.status !== 'ready') return
    const scopeRows = run.rowIds.map(rowId => rowById.get(rowId)).filter(
      (row): row is AnchoredRow => Boolean(row)
    )
    const activeTarget: StructuralRangeTitleTarget = structuralRangeEditor
      ? {
          rangeId: structuralRangeEditor.rangeId,
          title: structuralRangeEditor.title,
          kind: structuralRangeEditor.kind,
          startRowId: structuralRangeEditor.startRowId,
          endRowId: structuralRangeEditor.endRowId,
          parentRangeId: structuralRangeEditor.parentRangeId,
        }
      : run.target
    if (
      scopeRows.length !== run.rowIds.length
      || fingerprintStructuralRangeGenerationInput({
        rows: scopeRows,
        layer: structuralRangeLayer,
        target: activeTarget,
      }) !== run.inputFingerprint
    ) {
      setStructuralRangeError('This title suggestion is stale because the selected range or structure changed. Dismiss it and prepare again.')
      return
    }
    setStructuralSelectionAnchorRowId(run.target.startRowId)
    setSelectedStructuralRowIds(run.rowIds)
    setStructuralRangeEditor({
      ...run.target,
      title: run.suggestion.title,
    })
    setStructuralRangeRibbonAnchor(run.target.rangeId
      ? { kind: 'range', id: run.target.rangeId }
      : { kind: 'row', id: run.target.startRowId })
    setStructuralRangeRibbonPosition(null)
    setStructuralRangeMenu('title')
    setStructuralBoundaryPlacement(null)
    setStructuralBoundaryHoverRowId('')
    setStructuralRangeError(null)
    retainStructuralRangeGenerationRun(updateLedgerSafeStructuralRangeGenerationRunStatus(run, 'used'))
    showToast('Placed the suggested title in the range editor; confirm to save')
  })

  const handleStartRowTitleEdit = useCallback((row: AnchoredRow) => {
    if (
      isMatrixContentApplying
      && diffEnabled
      && diffMode === 'apparatus'
      && apparatusScope === 'row'
    ) {
      return
    }
    setSelectedCell(current => ({
      rowId: row.id,
      witnessId: current?.witnessId || activeWitness?.id || scopeWitnesses[0]?.id || '',
    }))
    setRowTitleEditor({
      rowId: row.id,
      primaryText: getCollationRowPrimaryTitle(row),
      parallelText: getCollationRowParallelTitle(row),
    })
    setSidePanelTab('titles')
    setIsSidePanelCollapsed(false)
  }, [
    activeWitness?.id,
    apparatusScope,
    diffEnabled,
    diffMode,
    isMatrixContentApplying,
    scopeWitnesses,
    setSelectedCell,
  ])

  const handleEditHeadingTitle = useEventCallback((rowId: string) => {
    const row = rowById.get(rowId)
    if (row) handleStartRowTitleEdit(row)
  })

  const handleSaveRowTitle = useEventCallback(async () => {
    if (isMatrixContentApplying || !chapterId || !rowTitleEditor) {
      return
    }
    const row = rowById.get(rowTitleEditor.rowId)
    if (!row) {
      showToast('The selected row no longer exists')
      return
    }
    if (!rowTitleEditor.primaryText.trim()) {
      setRowTitleImportError('A primary title is required.')
      return
    }
    setIsLedgerApplying(true)
    setRowTitleImportError(null)
    try {
      const result = applyCollationRowTitleUpdates({
        rows: resolvedRows,
        updates: [{
          rowId: row.id,
          primaryText: rowTitleEditor.primaryText,
          primaryLanguage: 'en',
          parallelText: rowTitleEditor.parallelText,
          parallelLanguage: 'ar',
          origin: 'human',
          updatedBy: 'human',
        }],
      })
      if (!result.changedRowIds.length) {
        showToast('Row title is already current')
        return
      }
      const changedRows = result.updatedRows.filter(candidate => result.changedRowIds.includes(candidate.id))
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'set_row_title',
        params: {
          rowId: row.id,
          primaryText: rowTitleEditor.primaryText.trim(),
          hasParallelTitle: Boolean(rowTitleEditor.parallelText.trim()),
        },
        summary: `Set title for ${getRowLabel(row)}`,
        affectedWitnessIds: [],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
      })
      showToast(`Saved title for ${getRowLabel(row)}`)
    } catch (error) {
      setRowTitleImportError(error instanceof Error ? error.message : 'Failed to save row title.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleClearRowTitle = useEventCallback(async () => {
    if (isMatrixContentApplying || !chapterId || !rowTitleEditor) {
      return
    }
    const row = rowById.get(rowTitleEditor.rowId)
    if (!row) {
      return
    }
    setIsLedgerApplying(true)
    setRowTitleImportError(null)
    try {
      const result = clearCollationRowTitles({
        rows: resolvedRows,
        rowIds: [row.id],
      })
      if (!result.changedRowIds.length) {
        showToast('This row has no title')
        return
      }
      const changedRows = result.updatedRows.filter(candidate => result.changedRowIds.includes(candidate.id))
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'clear_row_title',
        params: { rowId: row.id },
        summary: `Cleared title from ${getRowLabel(row)}`,
        affectedWitnessIds: [],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
      })
      setRowTitleEditor({
        rowId: row.id,
        primaryText: '',
        parallelText: '',
      })
      showToast(`Cleared title from ${getRowLabel(row)}`)
    } catch (error) {
      setRowTitleImportError(error instanceof Error ? error.message : 'Failed to clear row title.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleLoadRowTitleSidecar = useEventCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }
    setRowTitleImportError(null)
    try {
      const parsed = parseCollationRowTitleSidecar(JSON.parse(await file.text()))
      const preview = previewCollationRowTitleSidecar({
        sidecar: parsed,
        rows: resolvedRows,
      })
      setRowTitleSidecarPreview(preview)
      setSidePanelTab('titles')
      setIsSidePanelCollapsed(false)
    } catch (error) {
      setRowTitleSidecarPreview(null)
      setRowTitleImportError(error instanceof Error ? error.message : 'Could not read the title sidecar.')
    }
  })

  const applyRowTitleSidecarMatches = useEventCallback(async ({
    matchStatuses,
    rowIds,
    summary,
  }: {
    matchStatuses: CollationRowTitleSidecarMatchStatus[]
    rowIds?: string[]
    summary: string
  }) => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots || !chapterId || !rowTitleSidecarPreview) {
      return
    }
    const updates = buildSidecarTitleUpdates({
      preview: rowTitleSidecarPreview,
      matchStatuses,
      rowIds,
      updatedBy: 'human',
    })
    if (!updates.length) {
      showToast('No sidecar titles are ready for this action')
      return
    }
    setIsLedgerApplying(true)
    setRowTitleImportError(null)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-row-title-import',
        label: `Before importing ${updates.length} row title${updates.length === 1 ? '' : 's'}`,
      })
      const result = applyCollationRowTitleUpdates({ rows: resolvedRows, updates })
      const changedRows = result.updatedRows.filter(row => result.changedRowIds.includes(row.id))
      if (!changedRows.length) {
        showToast('Imported titles are already current')
        return
      }
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'import_row_titles',
        params: {
          sourceName: rowTitleSidecarPreview.sidecar.source_html_name,
          sourceHash: rowTitleSidecarPreview.sidecar.source_html_sha256,
          rowIds: result.changedRowIds,
        },
        summary,
        affectedWitnessIds: [],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: { backupSnapshotId: backup?.id || null },
      })
      setRowTitleSidecarPreview(previewCollationRowTitleSidecar({
        sidecar: rowTitleSidecarPreview.sidecar,
        rows: result.updatedRows,
      }))
      showToast(summary)
    } catch (error) {
      setRowTitleImportError(error instanceof Error ? error.message : 'Failed to import row titles.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleApplyExactRowTitleImports = useCallback(() => {
    const count = rowTitleSidecarPreview?.counts.ready || 0
    void applyRowTitleSidecarMatches({
      matchStatuses: ['ready'],
      summary: `Imported ${count} exact row title${count === 1 ? '' : 's'}`,
    })
  }, [applyRowTitleSidecarMatches, rowTitleSidecarPreview?.counts.ready])

  const handleRepairRoleAwareTitleSidecar = useEventCallback(async () => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots || !chapterId || !rowTitleSidecarPreview) {
      return
    }
    const repairCount = (
      rowTitleSidecarPreview.repairableTitleCount
      + (sidecarStructuralRangePreview?.candidates.length || 0)
      + rowTitleSidecarPreview.staleImportedTitleRowIds.length
    )
    if (repairCount === 0) {
      showToast('This sidecar repair is already current')
      return
    }
    const confirmed = window.confirm(
      `Repair ${rowTitleSidecarPreview.repairableTitleCount} row title`
      + `${rowTitleSidecarPreview.repairableTitleCount === 1 ? '' : 's'}, import `
      + `${sidecarStructuralRangePreview?.candidates.length || 0} structural range candidate`
      + `${sidecarStructuralRangePreview?.candidates.length === 1 ? '' : 's'}, and clear `
      + `${rowTitleSidecarPreview.staleImportedTitleRowIds.length} stale imported title`
      + `${rowTitleSidecarPreview.staleImportedTitleRowIds.length === 1 ? '' : 's'}? `
      + 'Imported ranges will remain unclassified until you review them.'
    )
    if (!confirmed) {
      return
    }
    setIsLedgerApplying(true)
    setRowTitleImportError(null)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-title-sidecar-repair',
        label: `Before repairing titles and scenes from ${rowTitleSidecarPreview.sidecar.source_html_name}`,
      })
      const result = applyRoleAwareSidecarRepair({
        preview: rowTitleSidecarPreview,
        rows: resolvedRows,
        updatedBy: 'human',
        applyScenes: false,
      })
      const changedRowIdSet = new Set(result.changedRowIds)
      const changedRows = result.updatedRows.filter(row => changedRowIdSet.has(row.id))
      const structuralCandidates = sidecarStructuralRangePreview?.candidates || []
      if (!changedRows.length && !structuralCandidates.length) {
        showToast('This sidecar repair is already current')
        return
      }
      if (changedRows.length) {
        await upsertRows(chapterId, changedRows, { autoRelabel: false })
        await persistEditorialEvent({
          kind: 'repair_row_titles_and_scenes',
          params: {
            sourceName: rowTitleSidecarPreview.sidecar.source_html_name,
            sourceHash: rowTitleSidecarPreview.sidecar.source_html_sha256,
            appliedTitleCount: result.appliedTitleRowIds.length,
            clearedTitleCount: result.clearedTitleRowIds.length,
            importedStructuralRangeCount: structuralCandidates.length,
          },
          summary: `Repaired row titles from ${rowTitleSidecarPreview.sidecar.source_html_name}`,
          affectedWitnessIds: [],
          affectedRowIds: result.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: result.updatedRows,
          metadata: { backupSnapshotId: backup?.id || null },
        })
      }
      if (structuralCandidates.length) {
        await persistStructuralRanges(structuralCandidates.map(candidate => candidate.range))
      }
      setRowTitleSidecarPreview(previewCollationRowTitleSidecar({
        sidecar: rowTitleSidecarPreview.sidecar,
        rows: result.updatedRows,
      }))
      showToast(
        `Repaired ${result.appliedTitleRowIds.length} titles and imported `
        + `${structuralCandidates.length} structural candidate${structuralCandidates.length === 1 ? '' : 's'}`
      )
    } catch (error) {
      setRowTitleImportError(
        error instanceof Error ? error.message : 'Failed to repair titles and structural ranges.'
      )
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleApplyRowTitleImportConflict = useCallback((rowId: string) => {
    const row = rowById.get(rowId)
    if (!row) {
      return
    }
    void applyRowTitleSidecarMatches({
      matchStatuses: ['conflict'],
      rowIds: [rowId],
      summary: `Replaced the title for ${getRowLabel(row)} from the sidecar`,
    })
  }, [applyRowTitleSidecarMatches, rowById])

  const handleResolveAmbiguousRowTitleImport = useEventCallback(async ({
    match,
    targetRowId,
  }: {
    match: CollationRowTitleSidecarMatch
    targetRowId: string
  }) => {
    if (
      isMatrixContentApplying
      || !runtimeEnableSnapshots
      || !chapterId
      || !rowTitleSidecarPreview
      || match.status !== 'ambiguous'
    ) {
      return
    }
    const targetRow = rowById.get(targetRowId)
    if (!targetRow || !match.candidateRowIds.includes(targetRowId)) {
      return
    }
    setIsLedgerApplying(true)
    setRowTitleImportError(null)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-row-title-import',
        label: `Before resolving an ambiguous row title`,
      })
      const result = applyCollationRowTitleUpdates({
        rows: resolvedRows,
        updates: [{
          rowId: targetRowId,
          primaryText: match.sidecarRow.title,
          primaryLanguage: 'en',
          origin: 'import',
          updatedBy: 'human',
          provenance: {
            sourceKind: 'html-sidecar',
            sourceName: rowTitleSidecarPreview.sidecar.source_html_name,
            sourceHash: rowTitleSidecarPreview.sidecar.source_html_sha256,
            sourceUnitId: match.sidecarRow.source_unit_id || undefined,
          },
        }],
      })
      const changedRows = result.updatedRows.filter(row => result.changedRowIds.includes(row.id))
      if (changedRows.length) {
        await upsertRows(chapterId, changedRows, { autoRelabel: false })
        await persistEditorialEvent({
          kind: 'import_row_titles',
          params: {
            sourceName: rowTitleSidecarPreview.sidecar.source_html_name,
            sourceHash: rowTitleSidecarPreview.sidecar.source_html_sha256,
            rowIds: result.changedRowIds,
            manuallyResolved: true,
          },
          summary: `Imported an ambiguous title into ${getRowLabel(targetRow)}`,
          affectedWitnessIds: [],
          affectedRowIds: result.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: result.updatedRows,
          metadata: { backupSnapshotId: backup?.id || null },
        })
      }
      setRowTitleSidecarPreview(current => {
        if (!current) {
          return current
        }
        const matches = current.matches.map(candidate => (
          candidate.sidecarRow.row_order === match.sidecarRow.row_order
          && candidate.sidecarRow.source_unit_id === match.sidecarRow.source_unit_id
            ? {
                ...candidate,
                status: 'same' as const,
                rowId: targetRowId,
                candidateRowIds: [targetRowId],
                currentTitle: match.sidecarRow.title,
              }
            : candidate
        ))
        const counts: CollationRowTitleSidecarPreview['counts'] = {
          ready: 0,
          same: 0,
          conflict: 0,
          ambiguous: 0,
          unmatched: 0,
        }
        matches.forEach(candidate => {
          counts[candidate.status] += 1
        })
        return { ...current, matches, counts }
      })
      showToast(`Imported title into ${getRowLabel(targetRow)}`)
    } catch (error) {
      setRowTitleImportError(error instanceof Error ? error.message : 'Failed to resolve this title match.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleResetRowTitleGenerationRun = useCallback(() => {
    retainRowTitleGenerationRun(null)
    setRowTitleGenerationError(null)
  }, [retainRowTitleGenerationRun])

  const handleGenerateNextRowTitleBatch = useEventCallback(async () => {
    if (!chapterId) {
      return
    }
    let run = rowTitleGenerationRun
    if (!run) {
      const rowIds = selectedRowTitleScopeRows.map(row => row.id)
      if (!rowIds.length) {
        setRowTitleGenerationError('There are no rows in the current title scope.')
        return
      }
      run = createLedgerSafeRowTitleGenerationRun({
        chapterId,
        rowIds,
        modelId: rowTitleGenerationModelId,
        thinkingLevel: rowTitleGenerationThinkingLevel,
      })
      retainRowTitleGenerationRun(run)
    }
    if (run.nextRowIndex >= run.rowIds.length) {
      setRowTitleGenerationError('This run is complete. Start a new run to title another scope.')
      return
    }
    const batchRowIds = run.rowIds.slice(
      run.nextRowIndex,
      run.nextRowIndex + ROW_TITLE_GENERATION_BATCH_SIZE,
    )
    const batchRows = batchRowIds.map(rowId => rowById.get(rowId)).filter(
      (row): row is AnchoredRow => Boolean(row)
    )
    if (batchRows.length !== batchRowIds.length) {
      setRowTitleGenerationError('This retained run refers to rows that no longer exist. Start a new run.')
      return
    }
    setIsRowTitleGenerating(true)
    setRowTitleGenerationError(null)
    try {
      const execution = await requestPiStructuredGeneration(buildRowTitleStructuredGenerationRequest({
        rows: batchRows,
        modelId: run.modelId,
        thinkingLevel: run.thinkingLevel,
      }))
      const suggestions = parseRowTitleGenerationExecution({ execution, rows: batchRows })
      const nextRun = appendLedgerSafeRowTitleGenerationBatch({
        run,
        rows: batchRows,
        execution,
        suggestions,
      })
      retainRowTitleGenerationRun(nextRun)
      showToast(`Prepared ${suggestions.length} row title${suggestions.length === 1 ? '' : 's'} for review`)
    } catch (error) {
      setRowTitleGenerationError(error instanceof Error ? error.message : 'Row-title generation failed.')
    } finally {
      setIsRowTitleGenerating(false)
    }
  })

  const handleApplyGeneratedRowTitleBatch = useEventCallback(async (
    batch: LedgerSafeRowTitleGenerationBatch,
  ) => {
    if (
      isMatrixContentApplying
      || !runtimeEnableSnapshots
      || !chapterId
      || !rowTitleGenerationRun
      || batch.status !== 'ready'
    ) {
      return
    }
    const batchRows = batch.rowIds.map(rowId => rowById.get(rowId)).filter(
      (row): row is AnchoredRow => Boolean(row)
    )
    if (
      batchRows.length !== batch.rowIds.length
      || fingerprintRowTitleGenerationInput(batchRows) !== batch.inputFingerprint
    ) {
      setRowTitleGenerationError('This batch is stale because its source rows changed. Dismiss it and start a new run.')
      return
    }
    setIsLedgerApplying(true)
    setRowTitleGenerationError(null)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-generated-row-title-apply',
        label: `Before applying generated row-title batch`,
      })
      const result = applyCollationRowTitleUpdates({
        rows: resolvedRows,
        updates: batch.suggestions.map(suggestion => ({
          rowId: suggestion.rowId,
          primaryText: suggestion.title,
          primaryLanguage: 'en',
          origin: 'model',
          updatedBy: 'human',
          provenance: {
            sourceKind: 'pi-structured-generation',
            generationId: batch.generationId,
            provider: 'openai-codex',
            modelId: batch.modelId,
          },
        })),
      })
      const changedRows = result.updatedRows.filter(row => result.changedRowIds.includes(row.id))
      if (changedRows.length) {
        await upsertRows(chapterId, changedRows, { autoRelabel: false })
        await persistEditorialEvent({
          kind: 'apply_generated_row_titles',
          params: {
            batchId: batch.id,
            generationId: batch.generationId,
            modelId: batch.modelId,
            rowIds: result.changedRowIds,
          },
          summary: `Applied ${changedRows.length} generated row title${changedRows.length === 1 ? '' : 's'}`,
          affectedWitnessIds: [],
          affectedRowIds: result.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: result.updatedRows,
          source: 'semantic-preview-apply',
          metadata: { backupSnapshotId: backup?.id || null },
        })
      }
      const nextRun = updateLedgerSafeRowTitleGenerationBatchStatus({
        run: rowTitleGenerationRun,
        batchId: batch.id,
        status: 'applied',
      })
      retainRowTitleGenerationRun(nextRun)
      showToast(`Applied ${batch.suggestions.length} generated row title${batch.suggestions.length === 1 ? '' : 's'}`)
    } catch (error) {
      setRowTitleGenerationError(error instanceof Error ? error.message : 'Failed to apply generated titles.')
    } finally {
      setIsLedgerApplying(false)
    }
  })

  const handleDismissGeneratedRowTitleBatch = useCallback((
    batch: LedgerSafeRowTitleGenerationBatch,
  ) => {
    if (!rowTitleGenerationRun) {
      return
    }
    retainRowTitleGenerationRun(updateLedgerSafeRowTitleGenerationBatchStatus({
      run: rowTitleGenerationRun,
      batchId: batch.id,
      status: 'dismissed',
    }))
  }, [retainRowTitleGenerationRun, rowTitleGenerationRun])

  const handleCreateLedgerBackup = useCallback(async () => {
    const snapshot = await createLedgerSnapshot({
      reason: 'manual-backup',
      label: 'Manual backup'
    })
    if (!snapshot) {
      showToast('Failed to create collation backup')
      return
    }
    showToast(`Backed up ${snapshot.rowCount} collation row${snapshot.rowCount === 1 ? '' : 's'}`)
  }, [createLedgerSnapshot, showToast])

  const handleCompactEmptyRows = useCallback(async (rowIds?: string[]) => {
    if (isMatrixContentApplying || !chapterId) {
      return
    }
    const targetRowIdSet = rowIds && rowIds.length > 0 ? new Set(rowIds) : null
    const targetEmptyRows = targetRowIdSet
      ? emptyLedgerRows.filter(row => targetRowIdSet.has(row.id))
      : emptyLedgerRows
    const emptyCount = targetEmptyRows.length
    if (emptyCount === 0) {
      showToast('No truly empty rows to compact')
      return
    }
    const confirmation = runtimeEnableSnapshots
      ? `Compact ${emptyCount} truly empty row${emptyCount === 1 ? '' : 's'}? A backup will be created first.`
      : `Compact ${emptyCount} truly empty row${emptyCount === 1 ? '' : 's'}? Collation snapshots are off, so this change cannot be restored from the Log panel.`
    if (!window.confirm(confirmation)) {
      return
    }
    const selectedWitnessId = selectedCell?.witnessId || activeWitness?.id || scopeWitnesses[0]?.id
    if (!selectedWitnessId) {
      showToast('Select a witness before compacting empty rows')
      return
    }

    setIsLedgerApplying(true)
    try {
      const backup = runtimeEnableSnapshots
        ? await createLedgerSnapshot({
            reason: 'pre-empty-row-compaction',
            label: `Before compacting ${emptyCount} empty row${emptyCount === 1 ? '' : 's'}`
          })
        : null
      const result = compactLedgerSafeEmptyRows({
        rows: resolvedRows,
        selectedWitnessId,
        rowIds
      })
      applyingLedgerMatrixRowIdsRef.current = new Set(result.changedRowIds)

      await replaceResolvedRows(chapterId, resolvedRows, result.updatedRows, {
        autoRelabel: result.requiresRelabel
      })
      await persistEditorialEvent({
        kind: 'compact_empty_rows',
        params: {
          rowIds: result.changedRowIds,
          deletedCount: result.changedRowIds.length
        },
        summary: `Compacted ${result.changedRowIds.length} empty row${result.changedRowIds.length === 1 ? '' : 's'}`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: {
          backupSnapshotId: backup?.id || null
        }
      })
      setSelectedCell(result.selectedCell)
      setSelectionAnchorCell(result.selectedCell)
      setSelectedRange(null)
      showToast(`Compacted ${result.changedRowIds.length} empty row${result.changedRowIds.length === 1 ? '' : 's'}`)
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown persistence error'
      showToast(`Compaction stopped safely: ${detail}`)
    } finally {
      applyingLedgerMatrixRowIdsRef.current = new Set()
      setIsLedgerApplying(false)
    }
  }, [
    activeWitness?.id,
    chapterId,
    createLedgerSnapshot,
    emptyLedgerRows,
    isMatrixContentApplying,
    persistEditorialEvent,
    replaceResolvedRows,
    resolvedRows,
    runtimeEnableSnapshots,
    scopeWitnesses,
    selectedCell?.witnessId,
    showToast
  ])

  const handleRestoreLatestLedgerSnapshot = useCallback(async () => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots || !chapterId) {
      return
    }
    const latestSnapshot = latestLedgerSnapshot || await getLatestLedgerSafeChapterSnapshot(chapterId, snapshotScopeId)
    if (!latestSnapshot) {
      showToast('No collation backup available for this chapter view')
      return
    }
    if (!window.confirm(`Restore the collation from backup "${latestSnapshot.label}"? The current view will be backed up first.`)) {
      return
    }

    const selectedWitnessId = selectedCell?.witnessId || activeWitness?.id || scopeWitnesses[0]?.id
    if (!selectedWitnessId) {
      showToast('Select a witness before restoring a collation backup')
      return
    }

    setIsLedgerApplying(true)
    try {
      const preRestoreBackup = await createLedgerSnapshot({
        reason: 'pre-restore',
        label: `Before restoring ${latestSnapshot.label}`
      })

      await replaceResolvedRows(chapterId, resolvedRows, latestSnapshot.rows, {
        autoRelabel: false
      })
      await persistEditorialEvent({
        kind: 'restore_snapshot',
        params: {
          snapshotId: latestSnapshot.id,
          snapshotLabel: latestSnapshot.label,
          restoredRowCount: latestSnapshot.rowCount
        },
        summary: `Restored collation from backup "${latestSnapshot.label}"`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: Array.from(new Set([
          ...resolvedRows.map(row => row.id),
          ...latestSnapshot.rows.map(row => row.id)
        ])).sort((left, right) => left.localeCompare(right)),
        beforeRows: resolvedRows,
        afterRows: latestSnapshot.rows,
        metadata: {
          restoredSnapshotId: latestSnapshot.id,
          preRestoreSnapshotId: preRestoreBackup?.id || null
        }
      })

      const nextSelectedRow = latestSnapshot.rows[0] || null
      if (nextSelectedRow) {
        const nextCell = {
          rowId: nextSelectedRow.id,
          witnessId: selectedWitnessId
        }
        setSelectedCell(nextCell)
        setSelectionAnchorCell(nextCell)
      } else {
        setSelectedCell(null)
        setSelectionAnchorCell(null)
      }
      setSelectedRange(null)
      showToast(`Restored collation from backup "${latestSnapshot.label}"`)
    } finally {
      setIsLedgerApplying(false)
    }
  }, [
    activeWitness?.id,
    chapterId,
    createLedgerSnapshot,
    isMatrixContentApplying,
    latestLedgerSnapshot,
    persistEditorialEvent,
    replaceResolvedRows,
    resolvedRows,
    runtimeEnableSnapshots,
    scopeWitnesses,
    selectedCell?.witnessId,
    showToast,
    snapshotScopeId
  ])

  const handleReplayGeneratedWitness = useCallback(async () => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots) {
      return
    }
    if (!chapterId || !activeWitness || !activeWitness.uploaded_file?.content) {
      showToast('Select a witness with source text before replaying generated output')
      return
    }
    if (!hasBuildImportRows) {
      showToast('This collation does not look like a staged build import, so witness replay is not available')
      return
    }
    if (!window.confirm(`Restore ${activeWitness.sigla} from the latest saved staged build? The current collation will be backed up first.`)) {
      return
    }

    setIsWitnessReplayApplying(true)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-witness-replay',
        label: `Before replaying ${activeWitness.sigla}`
      })

      const replay = await requestStagedReferenceAlignmentWitnessReplay({
        contextId: chapterId,
        inputWitnesses: [
          {
            witnessId: activeWitness.id,
            sigla: activeWitness.sigla,
            label: activeWitness.description?.trim() || activeWitness.sigla,
            sourceFileName: activeWitness.uploaded_file.name || `${activeWitness.sigla}.txt`,
            rawSourceText: activeWitness.uploaded_file.content
          }
        ]
      })

      const result = await applyWorkbenchBuildArtifactWitnessReplay({
        contextId: chapterId,
        existingWitnesses: effectiveWitnesses,
        currentRows: resolvedRows,
        artifact: replay.artifact,
        upsertRows,
        updateWitnessProcessingResultsBatch
      })

      await persistEditorialEvent({
        kind: 'replay_generated_witness',
        params: {
          witnessId: activeWitness.id,
          sigla: activeWitness.sigla,
          sourceRunDir: replay.source_run_dir,
          changedRowCount: result.changedRowCount
        },
        summary: `Replayed generated output for ${activeWitness.sigla}`,
        affectedWitnessIds: [activeWitness.id],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: {
          backupSnapshotId: backup?.id || null,
          sourceRunDir: replay.source_run_dir,
          matchedWitnessSigla: replay.matched_witness_sigla
        }
      })

      setSelectedRange(null)
      showToast(`Replayed generated output for ${activeWitness.sigla}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to replay generated witness output'
      showToast(message)
    } finally {
      setIsWitnessReplayApplying(false)
    }
  }, [
    activeWitness,
    chapterId,
    createLedgerSnapshot,
    effectiveWitnesses,
    hasBuildImportRows,
    isMatrixContentApplying,
    persistEditorialEvent,
    resolvedRows,
    runtimeEnableSnapshots,
    showToast,
    updateWitnessProcessingResultsBatch,
    upsertRows
  ])

  const handleAttachSceneNavigation = useCallback(async () => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots) {
      return
    }
    if (!chapterId) {
      showToast('No active collation context is selected')
      return
    }
    if (!hasStagedImportFingerprint) {
      showToast('Scene navigation backfill needs a staged build import or source-run fingerprint')
      return
    }
    if (sceneBackfillMissingRowCount === 0) {
      showToast('This collation already has scene navigation metadata')
      return
    }

    setIsSceneBackfillApplying(true)
    try {
      const response = await requestStagedReferenceAlignmentSceneMetadata({
        contextId: chapterId,
        sourceRunDir: sceneBackfillSourceRun || undefined,
        expectedRowCount: resolvedRows.length,
      })
      const backfilledAt = new Date().toISOString()
      const metadataByRowNumber = buildSceneMetadataByRowNumber(response.scenes, {
        sourceRunDir: response.source_run_dir,
        summaryPath: response.summary_path,
        summaryHash: response.summary_hash,
        backfilledAt,
      })

      const sortedRows = [...resolvedRows].sort((left, right) => {
        if (left.position !== right.position) {
          return left.position - right.position
        }
        return left.labelNumber - right.labelNumber
      })
      const rowNumberById = new Map(
        sortedRows.map((row, index) => [
          row.id,
          Number.isFinite(row.labelNumber) && row.labelNumber > 0 ? row.labelNumber : index + 1
        ])
      )
      const changedRowScenes: LedgerSafeSceneBackfillUndoState['changedRowScenes'] = []
      const updatedRows = resolvedRows.map(row => {
        if (getAnchoredRowSceneMetadata(row)) {
          return row
        }
        const rowNumber = rowNumberById.get(row.id)
        const scene = typeof rowNumber === 'number' ? metadataByRowNumber.get(rowNumber) : undefined
        if (!scene) {
          return row
        }
        changedRowScenes.push({
          rowId: row.id,
          previousScene: cloneAnchoredRowSceneMetadata(row.metadata?.scene)
        })
        return {
          ...row,
          metadata: {
            ...(row.metadata || {}),
            scene: cloneAnchoredRowSceneMetadata(scene) || scene,
          },
          updatedAt: backfilledAt,
        }
      })
      const changedRowIdSet = new Set(changedRowScenes.map(entry => entry.rowId))
      const changedRows = updatedRows.filter(row => changedRowIdSet.has(row.id))

      if (changedRows.length === 0) {
        showToast('No collation rows could be matched to the staged scene summary')
        return
      }
      if (!window.confirm(`Attach scene navigation to ${changedRows.length} collation row${changedRows.length === 1 ? '' : 's'}? A backup will be created first.`)) {
        return
      }

      const backup = await createLedgerSnapshot({
        reason: 'pre-scene-metadata-backfill',
        label: `Before attaching scene navigation`
      })
      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'attach_scene_metadata',
        params: {
          sourceRunDir: response.source_run_dir,
          summaryPath: response.summary_path,
          summaryHash: response.summary_hash,
          sceneCount: response.scene_count,
          changedRowCount: changedRows.length,
        },
        summary: `Attached scene navigation to ${changedRows.length} collation row${changedRows.length === 1 ? '' : 's'}`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: Array.from(changedRowIdSet),
        beforeRows: resolvedRows,
        afterRows: updatedRows,
        metadata: {
          backupSnapshotId: backup?.id || null,
          sourceRunDir: response.source_run_dir,
          summaryPath: response.summary_path,
          summaryHash: response.summary_hash,
        }
      })
      setSceneBackfillUndo({
        id: `scene-backfill:${Date.now()}`,
        createdAt: backfilledAt,
        sourceRunDir: response.source_run_dir,
        summaryPath: response.summary_path,
        changedRowScenes,
      })
      showToast(`Attached scene navigation to ${changedRows.length} row${changedRows.length === 1 ? '' : 's'}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to attach scene navigation'
      showToast(message)
    } finally {
      setIsSceneBackfillApplying(false)
    }
  }, [
    chapterId,
    createLedgerSnapshot,
    hasStagedImportFingerprint,
    isMatrixContentApplying,
    persistEditorialEvent,
    resolvedRows,
    runtimeEnableSnapshots,
    sceneBackfillMissingRowCount,
    sceneBackfillSourceRun,
    scopeWitnesses,
    showToast,
    upsertRows
  ])

  const handleUndoSceneNavigationBackfill = useCallback(async () => {
    if (isMatrixContentApplying) {
      return
    }
    if (!chapterId || !sceneBackfillUndo) {
      showToast('No scene navigation backfill is available to undo')
      return
    }
    if (!window.confirm('Undo the last scene navigation backfill? This only restores scene metadata fields and preserves later text edits.')) {
      return
    }

    setIsSceneBackfillApplying(true)
    try {
      const previousSceneByRowId = new Map(
        sceneBackfillUndo.changedRowScenes.map(entry => [entry.rowId, entry.previousScene])
      )
      const changedRowIdSet = new Set(previousSceneByRowId.keys())
      const updatedAt = new Date().toISOString()
      const updatedRows = resolvedRows.map(row => {
        if (!changedRowIdSet.has(row.id)) {
          return row
        }
        const nextMetadata = { ...(row.metadata || {}) }
        const previousScene = previousSceneByRowId.get(row.id)
        if (previousScene) {
          nextMetadata.scene = cloneAnchoredRowSceneMetadata(previousScene) || previousScene
        } else {
          delete nextMetadata.scene
        }
        return {
          ...row,
          metadata: Object.keys(nextMetadata).length > 0 ? nextMetadata : undefined,
          updatedAt,
        }
      })
      const changedRows = updatedRows.filter(row => changedRowIdSet.has(row.id))
      if (changedRows.length === 0) {
        setSceneBackfillUndo(null)
        showToast('No scene navigation backfill rows remain in this view')
        return
      }

      await upsertRows(chapterId, changedRows, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'undo_scene_metadata_backfill',
        params: {
          undoId: sceneBackfillUndo.id,
          sourceRunDir: sceneBackfillUndo.sourceRunDir,
          summaryPath: sceneBackfillUndo.summaryPath,
          changedRowCount: changedRows.length,
        },
        summary: `Undid scene navigation backfill on ${changedRows.length} collation row${changedRows.length === 1 ? '' : 's'}`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: Array.from(changedRowIdSet),
        beforeRows: resolvedRows,
        afterRows: updatedRows,
        metadata: {
          undoId: sceneBackfillUndo.id,
          originalBackfillCreatedAt: sceneBackfillUndo.createdAt,
        }
      })
      setSceneBackfillUndo(null)
      showToast(`Undid scene navigation backfill on ${changedRows.length} row${changedRows.length === 1 ? '' : 's'}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to undo scene navigation backfill'
      showToast(message)
    } finally {
      setIsSceneBackfillApplying(false)
    }
  }, [
    chapterId,
    isMatrixContentApplying,
    persistEditorialEvent,
    resolvedRows,
    sceneBackfillUndo,
    scopeWitnesses,
    showToast,
    upsertRows
  ])

  const handleExportEditorialDebug = useCallback(() => {
    if (!IS_LEDGER_SAFE_DEBUG_EXPORT_ENABLED) {
      return
    }

    try {
      const payload = {
        exportedAt: new Date().toISOString(),
        chapterId,
        scope: activeScope
          ? {
            clusterId: activeScope.clusterId,
            label: describeManualStudioScope(activeScope),
            members: activeScope.members
          }
          : null,
        activeWitnessId: activeWitness?.id || null,
        selectedCell,
        selectedRange,
        diff: {
          enabled: diffEnabled,
          mode: diffMode,
          apparatusScope,
          pivotWitnessId: diffPivotWitness?.id || null
        },
        editorialEvents,
        piReviewLog,
        rows: resolvedRows
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `ledger-safe-editorial-debug-${chapterId || 'chapter'}.json`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(objectUrl)
      showToast('Exported Variorum debug journal')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export Variorum debug journal'
      showToast(message)
    }
  }, [
    activeScope,
    activeWitness,
    chapterId,
    diffEnabled,
    diffMode,
    apparatusScope,
    diffPivotWitness,
    editorialEvents,
    piReviewLog,
    resolvedRows,
    selectedCell,
    selectedRange,
    showToast
  ])

  const handleExportApparatusDiff = useCallback(() => {
    if (!diffEnabled || diffMode !== 'apparatus' || !diffPivotWitness) {
      return
    }

    const exportRows = apparatusScope === 'row'
      ? (selectedLedgerRow ? resolvedRows.filter(row => row.id === selectedLedgerRow.id) : [])
      : resolvedRows
    if (!exportRows.length) {
      return
    }

    try {
      const payload = {
        exportedAt: new Date().toISOString(),
        chapterId,
        mode: 'apparatus',
        apparatusScope,
        pivotWitness: {
          id: diffPivotWitness.id,
          sigla: diffPivotWitness.sigla
        },
        selectedRow: apparatusScope === 'row' && selectedLedgerRow
          ? {
            rowId: selectedLedgerRow.id,
            rowLabel: getRowLabel(selectedLedgerRow)
          }
          : null,
        scope: activeScope
          ? {
            clusterId: activeScope.clusterId,
            label: describeManualStudioScope(activeScope),
            members: activeScope.members
          }
          : null,
        rows: exportRows.map((row, rowIndex) => ({
          rowId: row.id,
          rowLabel: getRowLabel(row),
          rowIndex: rowIndex + 1,
          pivotText: getComparisonMappingText(row, diffPivotWitness.id, witnessUnitTextLookupByWitnessId),
          witnesses: diffScopeWitnesses
            .filter(witness => witness.id !== diffPivotWitness.id)
            .map(witness => {
              const apparatus = apparatusDiffByCellKey.get(`${row.id}:${witness.id}`)
              return {
                witnessId: witness.id,
                sigla: witness.sigla,
                cellState: getLedgerSafeCellState(row, witness.id),
                witnessText: getComparisonMappingText(row, witness.id, witnessUnitTextLookupByWitnessId),
                spans: apparatus?.spans || []
              }
            })
        }))
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `ledger-safe-apparatus-comparison-${chapterId || 'chapter'}-${diffPivotWitness.sigla}.json`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(objectUrl)
      showToast('Exported apparatus comparison results')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export apparatus comparison results'
      showToast(message)
    }
  }, [
    activeScope,
    apparatusScope,
    apparatusDiffByCellKey,
    chapterId,
    diffEnabled,
    diffMode,
    diffPivotWitness,
    diffScopeWitnesses,
    selectedLedgerRow,
    resolvedRows,
    showToast,
    witnessUnitTextLookupByWitnessId
  ])

  const toggleOracleExportWitness = useCallback((witnessId: string) => {
    setOracleExportExcludedWitnessIds(previous => (
      previous.includes(witnessId)
        ? previous.filter(candidate => candidate !== witnessId)
        : [...previous, witnessId]
    ))
  }, [])

  const handleExportPropagationOracle = useCallback(() => {
    const includedWitnessCount = scopeWitnesses.length - oracleExportExcludedWitnessIds.length
    if (!resolvedRows.length || includedWitnessCount <= 0) {
      return
    }

    try {
      const payload = buildLedgerSafePropagationOracleExport({
        chapterId,
        scope: activeScope
          ? {
            clusterId: activeScope.clusterId,
            label: describeManualStudioScope(activeScope),
            members: activeScope.members
          }
          : null,
        scopeWitnesses,
        rows: resolvedRows,
        excludedWitnessIds: oracleExportExcludedWitnessIds
      })

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `ledger-safe-propagation-oracle-${chapterId || 'chapter'}.json`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(objectUrl)
      showToast('Exported propagation oracle JSON')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export propagation oracle JSON'
      showToast(message)
    }
  }, [
    activeScope,
    chapterId,
    oracleExportExcludedWitnessIds,
    resolvedRows,
    scopeWitnesses,
    showToast
  ])

  const oracleIncludedWitnessCount = scopeWitnesses.length - oracleExportExcludedWitnessIds.length
  const canExportPropagationOracle = resolvedRows.length > 0 && oracleIncludedWitnessCount > 0
  const canResetColumnFilters = hiddenWitnessIds.length > 0
  const canSaveColumnFilterTemplate = columnFilterTemplateName.trim().length > 0 && hiddenWitnessIds.length > 0
  const formatWitnessCountLabel = (count: number) => `${count} ${count === 1 ? 'Witness' : 'Witnesses'}`
  const currentWitnessCountLabel = formatWitnessCountLabel(scopeWitnesses.length)
  const isWitnessFilterActive = visibleScopeWitnesses.length !== scopeWitnesses.length
  const mastheadWitnessCountLabel = isWitnessFilterActive
    ? `${visibleScopeWitnesses.length}/${scopeWitnesses.length} ${scopeWitnesses.length === 1 ? 'Witness' : 'Witnesses'}`
    : currentWitnessCountLabel

  const persistColumnOrderWitnessIdsToStorage = useCallback((nextOrderedWitnessIds: string[]) => {
    if (!columnOrderStorageKey || nativeScopeWitnessIds.length === 0) {
      return
    }
    const sanitized = sanitizeWitnessOrder(nativeScopeWitnessIds, nextOrderedWitnessIds)
    void setPersistedUiPreference(columnOrderStorageKey, JSON.stringify(sanitized))
  }, [columnOrderStorageKey, nativeScopeWitnessIds])

  const persistHiddenWitnessIdsToStorage = useCallback((nextHiddenWitnessIds: string[]) => {
    if (!columnFilterStorageKey || orderedScopeWitnessIds.length === 0) {
      return
    }
    const sanitized = sanitizeLedgerColumnHiddenWitnessIds(orderedScopeWitnessIds, nextHiddenWitnessIds)
    logLedgerFilterDebug('action-persist-hidden-columns', {
      chapterId,
      scopeId: snapshotScopeId,
      key: columnFilterStorageKey,
      hiddenWitnessIds: sanitized
    })
    void setPersistedUiPreference(columnFilterStorageKey, JSON.stringify(sanitized))
  }, [chapterId, columnFilterStorageKey, orderedScopeWitnessIds, snapshotScopeId])

  const persistColumnFilterTemplatesToStorage = useCallback((nextTemplates: LedgerColumnFilterTemplate[]) => {
    if (!columnFilterTemplatesStorageKey) {
      return
    }
    logLedgerFilterDebug('action-persist-column-templates', {
      chapterId,
      key: columnFilterTemplatesStorageKey,
      templateCount: nextTemplates.length,
      templateNames: nextTemplates.map(template => template.name)
    })
    void setPersistedUiPreference(
      columnFilterTemplatesStorageKey,
      serializeLedgerColumnFilterTemplates(nextTemplates)
    )
  }, [chapterId, columnFilterTemplatesStorageKey])

  const resolveColumnDropPlacement = useCallback((event: React.DragEvent<HTMLElement>): WitnessColumnDropPlacement => {
    const rect = event.currentTarget.getBoundingClientRect()
    return event.clientX > rect.left + rect.width / 2 ? 'after' : 'before'
  }, [])

  const suppressColumnClickAfterDrag = useCallback(() => {
    columnDragSuppressClickRef.current = true
    window.setTimeout(() => {
      columnDragSuppressClickRef.current = false
    }, 0)
  }, [])

  const handleWitnessColumnDragStart = useCallback((event: React.DragEvent<HTMLElement>, witnessId: string) => {
    if (readOnly || visibleScopeWitnesses.length <= 1) {
      event.preventDefault()
      return
    }
    setDraggedColumnWitnessId(witnessId)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', witnessId)
    event.dataTransfer.setData('application/x-ledger-witness-id', witnessId)
  }, [readOnly, visibleScopeWitnesses.length])

  const handleWitnessColumnDragOver = useCallback((event: React.DragEvent<HTMLElement>, targetWitnessId: string) => {
    const draggedWitnessId = event.dataTransfer.getData('application/x-ledger-witness-id') || draggedColumnWitnessId
    if (!draggedWitnessId || draggedWitnessId === targetWitnessId) {
      return
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDragOverColumnWitnessId(targetWitnessId)
  }, [draggedColumnWitnessId])

  const handleWitnessColumnDrop = useCallback((event: React.DragEvent<HTMLElement>, targetWitnessId: string) => {
    if (readOnly) {
      return
    }
    const draggedWitnessId = event.dataTransfer.getData('application/x-ledger-witness-id')
      || event.dataTransfer.getData('text/plain')
      || draggedColumnWitnessId
    setDraggedColumnWitnessId(null)
    setDragOverColumnWitnessId(null)
    if (!draggedWitnessId || draggedWitnessId === targetWitnessId) {
      return
    }
    event.preventDefault()
    suppressColumnClickAfterDrag()
    const placement = resolveColumnDropPlacement(event)
    const nextOrderedWitnessIds = moveWitnessIdInOrder(
      orderedScopeWitnessIds,
      draggedWitnessId,
      targetWitnessId,
      placement
    )
    if (areStringArraysEqual(nextOrderedWitnessIds, orderedScopeWitnessIds)) {
      return
    }
    columnOrderMutationVersionRef.current += 1
    setColumnOrderWitnessIds(nextOrderedWitnessIds)
    setHydratedColumnOrderSignature(columnOrderHydrationSignature)
    persistColumnOrderWitnessIdsToStorage(nextOrderedWitnessIds)
  }, [
    columnOrderHydrationSignature,
    draggedColumnWitnessId,
    orderedScopeWitnessIds,
    persistColumnOrderWitnessIdsToStorage,
    readOnly,
    resolveColumnDropPlacement,
    suppressColumnClickAfterDrag
  ])

  const handleWitnessColumnDragEnd = useCallback(() => {
    setDraggedColumnWitnessId(null)
    setDragOverColumnWitnessId(null)
  }, [])

  const handleToggleWitnessColumn = useCallback((witnessId: string) => {
    setHiddenWitnessIds(previous => {
      columnFilterMutationVersionRef.current += 1
      let nextHiddenWitnessIds = previous
      if (previous.includes(witnessId)) {
        nextHiddenWitnessIds = previous.filter(id => id !== witnessId)
      } else if (scopeWitnesses.length - previous.length > 1) {
        nextHiddenWitnessIds = [...previous, witnessId]
      }
      persistHiddenWitnessIdsToStorage(nextHiddenWitnessIds)
      return nextHiddenWitnessIds
    })
  }, [persistHiddenWitnessIdsToStorage, scopeWitnesses.length])

  const handleRestoreFilteredRunWitnesses = useCallback((witnessIds: string[]) => {
    const restoreWitnessIdSet = new Set(witnessIds)
    setHiddenWitnessIds(previous => {
      const nextHiddenWitnessIds = previous.filter(witnessId => !restoreWitnessIdSet.has(witnessId))
      if (areStringArraysEqual(previous, nextHiddenWitnessIds)) {
        return previous
      }
      columnFilterMutationVersionRef.current += 1
      persistHiddenWitnessIdsToStorage(nextHiddenWitnessIds)
      return nextHiddenWitnessIds
    })
  }, [persistHiddenWitnessIdsToStorage])

  const handleResetColumnFilters = useCallback(() => {
    columnFilterMutationVersionRef.current += 1
    persistHiddenWitnessIdsToStorage([])
    setHiddenWitnessIds([])
  }, [persistHiddenWitnessIdsToStorage])

  const handleOpenTraditionReader = useCallback((witnessId: string) => {
    if (!runtimeEnableTraditionReader) {
      return
    }
    if (onOpenReader) {
      onOpenReader(witnessId)
      return
    }
    setTraditionSourceWitnessId(witnessId)
    if (runtimeEnableCharacterIndex) {
      setCharacterExtractionUnit('witness')
      setCharacterExtractionWitnessId(witnessId)
      setCharacterDetectorFeedback(null)
    }
    setLedgerSafeViewMode('tradition')
    setIsColumnFilterDialogOpen(false)
    setIsLedgerActionsMenuOpen(false)
  }, [onOpenReader, runtimeEnableCharacterIndex, runtimeEnableTraditionReader])

  const handleSaveColumnFilterTemplate = useCallback(() => {
    const templateName = columnFilterTemplateName.trim()
    if (!templateName) {
      return
    }
    const existingTemplate = columnFilterTemplates.find(template => template.name.toLowerCase() === templateName.toLowerCase())
    const nextTemplate = buildLedgerColumnFilterTemplate({
      name: templateName,
      hiddenWitnessIds,
      orderedWitnessIds: orderedScopeWitnessIds,
      scopeId: snapshotScopeId,
      scopeLabel: snapshotScopeLabel,
      existingId: existingTemplate?.id
    })
    setColumnFilterTemplates(previous => {
      columnFilterTemplateMutationVersionRef.current += 1
      const withoutExisting = existingTemplate
        ? previous.filter(template => template.id !== existingTemplate.id)
        : previous
      const nextTemplates = [nextTemplate, ...withoutExisting]
      persistColumnFilterTemplatesToStorage(nextTemplates)
      return nextTemplates
    })
    setColumnFilterTemplateName('')
  }, [
    columnFilterTemplateName,
    columnFilterTemplates,
    hiddenWitnessIds,
    orderedScopeWitnessIds,
    persistColumnFilterTemplatesToStorage,
    snapshotScopeId,
    snapshotScopeLabel
  ])

  const handleApplyColumnFilterTemplate = useCallback((template: LedgerColumnFilterTemplate) => {
    columnFilterMutationVersionRef.current += 1
    const nextHiddenWitnessIds = applyLedgerColumnFilterTemplate(template, orderedScopeWitnessIds)
    persistHiddenWitnessIdsToStorage(nextHiddenWitnessIds)
    setHiddenWitnessIds(nextHiddenWitnessIds)
  }, [orderedScopeWitnessIds, persistHiddenWitnessIdsToStorage])

  const handleDeleteColumnFilterTemplate = useCallback((templateId: string) => {
    setColumnFilterTemplates(previous => {
      columnFilterTemplateMutationVersionRef.current += 1
      const nextTemplates = previous.filter(template => template.id !== templateId)
      persistColumnFilterTemplatesToStorage(nextTemplates)
      return nextTemplates
    })
  }, [persistColumnFilterTemplatesToStorage])

  const clearPiAuthPolling = useCallback(() => {
    if (piAuthPollTimeoutRef.current !== null) {
      window.clearTimeout(piAuthPollTimeoutRef.current)
      piAuthPollTimeoutRef.current = null
    }
  }, [])

  const applyPiAgentSessionState = useCallback((agentSession: PiMatrixAgentAgentSession) => {
    setPiAgentSession(agentSession)
    setPiAgentSessionId(agentSession.agentSessionId)
    if (agentSession.model?.provider) setPiAgentModelProvider(agentSession.model.provider)
    if (agentSession.model?.modelId) setPiAgentModelId(agentSession.model.modelId)
    if (agentSession.thinkingLevel) setPiAgentThinkingLevel(agentSession.thinkingLevel)
  }, [])

  const clearPiAgentSessionState = useCallback(() => {
    setPiAgentSession(null)
    setPiAgentSessionId('')
    setPiAgentReply(null)
    setPiAgentReport(current => current ? { ...current, current: null } : current)
  }, [])

  const applyPiOAuthLoginAttemptState = useCallback((attempt: PiMatrixAgentOAuthLoginAttempt) => {
    setPiOAuthLoginAttempt(attempt)
    if (attempt.agentSession) {
      applyPiAgentSessionState(attempt.agentSession)
    }
  }, [applyPiAgentSessionState])

  const handlePiOAuthTerminalState = useCallback((attempt: PiMatrixAgentOAuthLoginAttempt) => {
    applyPiOAuthLoginAttemptState(attempt)
    if (attempt.status === 'authenticated') {
      clearPiAuthPolling()
      setIsPiAuthBusy(false)
      setPiAuthError(null)
      setPiOAuthProviders(current => current.map(provider => (
        provider.id === attempt.providerId
          ? { ...provider, hasAuth: true }
          : provider
      )))
      appendPiReviewLog('auth', `Authenticated ${attempt.providerName}`)
      if (attempt.agentSession) {
        appendPiReviewLog('agent', `Started Pi agent ${attempt.agentSession.agentSessionId} on ${attempt.agentSession.model?.provider || 'default'}/${attempt.agentSession.model?.modelId || 'default'}`)
      }
      if (attempt.agentError) {
        setPiError(attempt.agentError)
        appendPiReviewLog('error', attempt.agentError)
      } else {
        showToast(`${attempt.providerName} connected`)
      }
      return true
    }
    if (attempt.status === 'failed') {
      clearPiAuthPolling()
      setIsPiAuthBusy(false)
      const message = attempt.error || `Failed to authenticate ${attempt.providerName}`
      setPiAuthError(message)
      setPiError(message)
      appendPiReviewLog('error', message)
      showToast(message)
      return true
    }
    return false
  }, [appendPiReviewLog, applyPiOAuthLoginAttemptState, clearPiAuthPolling, showToast])

  const pollPiOAuthLoginAttempt = useCallback((sessionId: string, attemptId: string) => {
    clearPiAuthPolling()
    piAuthPollTimeoutRef.current = window.setTimeout(async () => {
      try {
        const nextAttempt = await getPiMatrixAgentOAuthLoginStatus({
          sessionId,
          attemptId
        })
        if (handlePiOAuthTerminalState(nextAttempt)) {
          return
        }
        applyPiOAuthLoginAttemptState(nextAttempt)
        pollPiOAuthLoginAttempt(sessionId, attemptId)
      } catch (error) {
        setIsPiAuthBusy(false)
        const message = error instanceof Error ? error.message : 'Failed to poll OAuth login status'
        setPiAuthError(message)
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    }, 1000)
  }, [appendPiReviewLog, applyPiOAuthLoginAttemptState, clearPiAuthPolling, handlePiOAuthTerminalState, showToast])

  useEffect(() => () => {
    clearPiAuthPolling()
    try {
      piAuthWindowRef.current?.close()
    } catch {
      // ignore popup close failures
    }
  }, [clearPiAuthPolling])

  const parsePiToolPayload = useCallback((result: { content?: Array<{ text?: string }> }) => {
    const text = result.content?.[0]?.text
    if (!text) {
      throw new Error('Pi sidecar returned an empty response')
    }
    return JSON.parse(text)
  }, [])

  const ensurePiSession = useCallback(async (existingOperationToken?: number) => {
    const operationToken = existingOperationToken ?? piOperationGateRef.current.begin()
    const ownsOperationToken = existingOperationToken === undefined
    if (ownsOperationToken) {
      setIsPiBusy(true)
      setPiError(null)
    }
    try {
      await piSnapshotCancellationRef.current
      if (!piOperationGateRef.current.isCurrent(operationToken)) {
        throw new Error('Superseded Pi session bootstrap')
      }
      const currentSnapshot = await getCurrentPiSnapshot()
      if (!piOperationGateRef.current.isCurrent(operationToken)) {
        throw new Error('Superseded Pi session bootstrap')
      }
      if (!piSessionId) {
        const created = await createPiMatrixAgentSession({ snapshot: currentSnapshot })
        if (!piOperationGateRef.current.isCurrent(operationToken)) {
          throw new Error('Superseded Pi session bootstrap')
        }
        setPiSessionId(created.sessionId)
        setPiSnapshotId(created.snapshotId)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiPreviewSummary('')
        appendPiReviewLog('sync', `Synced Pi session ${created.sessionId.slice(0, 12)}`)
        return {
          sessionId: created.sessionId,
          snapshotId: created.snapshotId,
          agentRestartRequired: false,
        }
      }

      if (currentSnapshot.snapshotId !== piSnapshotId) {
        const synced = await syncPiMatrixAgentSnapshot({
          sessionId: piSessionId,
          snapshot: currentSnapshot
        })
        if (!piOperationGateRef.current.isCurrent(operationToken)) {
          throw new Error('Superseded Pi snapshot sync')
        }
        setPiSnapshotId(synced.snapshotId)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiPreviewSummary('')
        if (synced.agentRestartRequired) {
          clearPiAgentSessionState()
        }
        appendPiReviewLog('sync', `Updated Pi snapshot ${synced.snapshotId}`)
        return {
          sessionId: piSessionId,
          snapshotId: synced.snapshotId,
          agentRestartRequired: synced.agentRestartRequired,
        }
      }
      return {
        sessionId: piSessionId,
        snapshotId: piSnapshotId,
        agentRestartRequired: false,
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Pi sidecar sync failed'
      if (piOperationGateRef.current.isCurrent(operationToken)) {
        setPiError(message)
        appendPiReviewLog('error', message)
      }
      throw error
    } finally {
      if (ownsOperationToken && piOperationGateRef.current.isCurrent(operationToken)) {
        setIsPiBusy(false)
      }
    }
  }, [appendPiReviewLog, clearPiAgentSessionState, getCurrentPiSnapshot, piSessionId, piSnapshotId])

  useEffect(() => {
    const isCurrent = piAgentChangeSet?.status === 'awaiting_approval'
      ? piAgentChangeSet.baseAuthorityRevisionId === piSnapshot.snapshotId
      : isPiMatrixAgentProposalCurrent({
          proposalBaseSnapshotId: piPreview?.baseSnapshotId || '',
          currentSnapshotId: piSnapshot.snapshotId,
          syncedSnapshotId: piSnapshotId,
        })
    if (!piPreview || isCurrent) {
      return
    }
    appendPiReviewLog('reject', 'Expired stale Pi review')
    if (piAgentChangeSet?.status === 'awaiting_approval') {
      const stale: LedgerSafeAgentChangeSetRecord = {
        ...piAgentChangeSet,
        status: 'stale',
        approval: { required: true, status: 'stale' },
        updatedAt: new Date().toISOString(),
      }
      setPiAgentChangeSet(stale)
      void dbStorage.put('ledger_safe_agent_change_sets', stale)
    }
    setPiPreview(null)
    setPiPreviewEnvelope(null)
    setPiPreviewSummary('')
    setPiFocusedWindow(null)
  }, [appendPiReviewLog, piAgentChangeSet, piPreview, piSnapshot.snapshotId, piSnapshotId])

  const persistSegmentationEdit = useEventCallback(async ({
    witness,
    previousSegmentationResult,
    nextSegmentationResult,
    matrixRowLatch,
  }: {
    witness: Witness
    previousSegmentationResult: Witness['segmentation_result']
    nextSegmentationResult: Witness['segmentation_result']
    matrixRowLatch?: 'segmentation' | 'pi'
  }) => {
    const reconcileResult = reconcileLedgerSafeBoundaryEdit({
      witnessId: witness.id,
      previousSegmentationResult,
      nextSegmentationResult,
      rows: resolvedRows
    })
    if (matrixRowLatch === 'segmentation') {
      applyingSegmentationMatrixRowIdsRef.current = new Set(reconcileResult.changedRowIds)
    } else if (matrixRowLatch === 'pi') {
      applyingPiMatrixRowIdsRef.current = new Set(reconcileResult.changedRowIds)
    }

    const changedRowIds = new Set(reconcileResult.changedRowIds)
    const changedRows = reconcileResult.updatedRows.filter(row => changedRowIds.has(row.id))
    const viewportContinuity = changedRows.length > 0
      ? captureMatrixViewportContinuity(changedRows.map(row => row.id))
      : null
    const currentCharacterLayer = readerAdjudicationLayerRef.current
    const rebasedCharacterLayer = runtimeEnableCharacterIndex
      ? rebaseCharacterLayerAfterSegmentationEdit({
          layer: currentCharacterLayer,
          witnessId: witness.id,
          previousSegmentationResult,
          nextSegmentationResult,
          previousRows: resolvedRows,
          nextRows: reconcileResult.updatedRows,
        })
      : currentCharacterLayer
    const characterLayerChanged = rebasedCharacterLayer !== currentCharacterLayer
    try {
      if (characterLayerChanged) {
        await readerAdjudicationSaveQueueRef.current
      }
      const persistAtomically = () => persistLedgerSafeSegmentationEditAtomically({
        witness,
        nextSegmentationResult,
        changedRows,
        characterLayer: characterLayerChanged ? rebasedCharacterLayer : undefined,
      })
      if (characterLayerChanged) {
        await runBackupRelevantWrite(persistAtomically)
        readerAdjudicationLayerRef.current = rebasedCharacterLayer
      } else {
        await persistAtomically()
      }
    } catch (error) {
      if (pendingMatrixViewportAnchorRef.current === viewportContinuity) {
        pendingMatrixViewportAnchorRef.current = null
      }
      throw error
    }

    return reconcileResult
  })

  const applyPiPatchLocally = useCallback(async (
    patch: PiMatrixAgentPatch,
    expectedSnapshotId: string,
  ) => {
    if (piSnapshotPending) {
      throw new Error('The collation changed while this Pi proposal was open; inspect a fresh proposal')
    }
    assertPiMatrixAgentPatchCurrent(expectedSnapshotId, latestPiSnapshotRef.current.snapshotId)
    const operation = patch.operations[0]
    if (!operation) {
      throw new Error('Pi patch does not contain any operations')
    }
    const witnessId = String(operation.payload.witnessId || operation.targetWitnessIds[0] || '')
    const witness = scopeWitnesses.find(candidate => candidate.id === witnessId)

    try {
      switch (operation.kind) {
      case 'boundary_shift': {
        if (!witness?.segmentation_result || !witness.uploaded_file?.content) {
          throw new Error('Cannot apply Pi boundary patch without witness segmentation')
        }
        const leftRowId = String(operation.payload.leftRowId || '')
        const deltaWords = Number(operation.payload.deltaWords || 0)
        const boundaryResult = applyLedgerSafeBoundaryShiftToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          leftRowId,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content,
          deltaWords
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: boundaryResult.updatedSegmentationResult,
          matrixRowLatch: 'pi',
        })
        await persistEditorialEvent({
          source: 'pi-preview-apply',
          kind: 'boundary_shift',
          params: {
            witnessId: witness.id,
            leftRowId,
            deltaWords
          },
          summary: `Applied Pi boundary preview to ${witness.sigla}`,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata: {
            patchId: patch.patchId,
            baseSnapshotId: patch.baseSnapshotId
          }
        })
        setSelectedCell({ rowId: leftRowId, witnessId: witness.id })
        setSelectionAnchorCell({ rowId: leftRowId, witnessId: witness.id })
        setSelectedRange(null)
        setPendingDeltaWords(0)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiFocusedWindow(null)
        setPiPreviewSummary('')
        showToast(`Applied Pi boundary preview to ${witness.sigla}`)
        return
      }
      case 'split_unit': {
        if (!witness?.segmentation_result || !witness.uploaded_file?.content) {
          throw new Error('Cannot apply Pi split patch without witness segmentation')
        }
        const rowId = String(operation.payload.rowId || '')
        const afterWordIndex = Number(operation.payload.afterWordIndex || 0)
        const splitResult = applyLedgerSafeUnitSplitToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          rowId,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content,
          splitBoundaryWordIndex: afterWordIndex
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: splitResult.updatedSegmentationResult,
          matrixRowLatch: 'pi',
        })
        await persistEditorialEvent({
          source: 'pi-preview-apply',
          kind: 'split_unit',
          params: {
            witnessId: witness.id,
            rowId,
            afterWordIndex
          },
          summary: `Applied Pi split preview to ${witness.sigla}`,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata: {
            patchId: patch.patchId,
            baseSnapshotId: patch.baseSnapshotId
          }
        })
        setSelectedCell({ rowId, witnessId: witness.id })
        setSelectionAnchorCell({ rowId, witnessId: witness.id })
        setSelectedRange(null)
        setSelectedSplitBoundaryWordIndex(null)
        setInlineEditMode('idle')
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiFocusedWindow(null)
        setPiPreviewSummary('')
        showToast(`Applied Pi split preview to ${witness.sigla}`)
        return
      }
      case 'merge_units': {
        if (!witness?.segmentation_result || !witness.uploaded_file?.content) {
          throw new Error('Cannot apply Pi merge patch without witness segmentation')
        }
        const rowIds = Array.isArray(operation.payload.rowIds) ? operation.payload.rowIds.map(value => String(value)) : operation.targetRowIds
        const mergeResult = applyLedgerSafeMergeUnitsToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          rowIds,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: mergeResult.updatedSegmentationResult,
          matrixRowLatch: 'pi',
        })
        await persistEditorialEvent({
          source: 'pi-preview-apply',
          kind: 'merge_units',
          params: {
            witnessId: witness.id,
            rowIds
          },
          summary: `Applied Pi merge preview to ${witness.sigla}`,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata: {
            patchId: patch.patchId,
            baseSnapshotId: patch.baseSnapshotId
          }
        })
        const survivorRowId = rowIds[0]
        setSelectedCell({ rowId: survivorRowId, witnessId: witness.id })
        setSelectionAnchorCell({ rowId: survivorRowId, witnessId: witness.id })
        setSelectedRange(null)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiFocusedWindow(null)
        setPiPreviewSummary('')
        showToast(`Applied Pi merge preview to ${witness.sigla}`)
        return
      }
      case 'shift_range': {
        const rowIds = Array.isArray(operation.payload.rowIds) ? operation.payload.rowIds.map(value => String(value)) : operation.targetRowIds
        const direction = operation.payload.direction === 'up' ? 'up' : 'down'
        const result = applyLedgerSafeShiftRange({
          rows: resolvedRows,
          witnessId,
          rowIds,
          direction
        })
        applyingPiMatrixRowIdsRef.current = new Set(result.changedRowIds)
        await upsertRows(chapterId, result.updatedRows, {
          autoRelabel: result.requiresRelabel
        })
        await persistEditorialEvent({
          source: 'pi-preview-apply',
          kind: 'shift_range',
          params: {
            witnessId,
            rowIds,
            direction
          },
          summary: `Applied Pi range shift to ${witness?.sigla || witnessId}`,
          affectedWitnessIds: [witnessId],
          affectedRowIds: result.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: result.updatedRows,
          metadata: {
            patchId: patch.patchId,
            baseSnapshotId: patch.baseSnapshotId
          }
        })
        setSelectedCell(result.selectedCell)
        setSelectionAnchorCell(result.selectedCell)
        setSelectedRange(result.selectedRowIds ? { witnessId, rowIds: result.selectedRowIds } : null)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiFocusedWindow(null)
        setPiPreviewSummary('')
        showToast(`Applied Pi range shift to ${witness?.sigla || witnessId}`)
        return
      }
      case 'mark_gap':
      case 'clear_gap': {
        const rowIds = Array.isArray(operation.payload.rowIds)
          ? operation.payload.rowIds.map(value => String(value))
          : operation.targetRowIds
        let nextRows = resolvedRows
        const changedRowIds = new Set<string>()
        rowIds.forEach(rowId => {
          const result = operation.kind === 'mark_gap'
            ? applyLedgerSafeMarkGap({ rows: nextRows, witnessId, rowId })
            : applyLedgerSafeClearGap({ rows: nextRows, witnessId, rowId })
          nextRows = result.updatedRows
          result.changedRowIds.forEach(changedRowId => changedRowIds.add(changedRowId))
        })
        applyingPiMatrixRowIdsRef.current = changedRowIds
        await upsertRows(chapterId, nextRows, { autoRelabel: false })
        await persistEditorialEvent({
          source: 'pi-preview-apply',
          kind: operation.kind,
          params: { witnessId, rowIds },
          summary: `${operation.kind === 'mark_gap' ? 'Marked' : 'Cleared'} Pi gap proposal for ${witness?.sigla || witnessId}`,
          affectedWitnessIds: [witnessId],
          affectedRowIds: Array.from(changedRowIds),
          beforeRows: resolvedRows,
          afterRows: nextRows,
          metadata: {
            patchId: patch.patchId,
            baseSnapshotId: patch.baseSnapshotId,
          },
        })
        const selectedRowId = rowIds[0]
        if (selectedRowId) {
          setSelectedCell({ rowId: selectedRowId, witnessId })
          setSelectionAnchorCell({ rowId: selectedRowId, witnessId })
        }
        setSelectedRange(rowIds.length > 1 ? { witnessId, rowIds } : null)
        setPiPreview(null)
        setPiPreviewEnvelope(null)
        setPiFocusedWindow(null)
        setPiPreviewSummary('')
        showToast(`${operation.kind === 'mark_gap' ? 'Marked' : 'Cleared'} gap for ${witness?.sigla || witnessId}`)
        return
      }
      default:
        throw new Error(`Unsupported Pi patch operation: ${operation.kind}`)
      }
    } finally {
      applyingPiMatrixRowIdsRef.current = new Set()
    }
  }, [chapterId, persistEditorialEvent, persistSegmentationEdit, piSnapshotPending, resolvedRows, scopeWitnesses, showToast, upsertRows])

  const applyLedgerRowOperation = useEventCallback(async (
    operation: () =>
      | ReturnType<typeof applyLedgerSafeMoveMapping>
      | ReturnType<typeof applyLedgerSafeInsertRowAndMove>
      | ReturnType<typeof applyLedgerSafeMarkGap>
      | ReturnType<typeof applyLedgerSafeMarkTransposition>
      | ReturnType<typeof applyLedgerSafeUnmarkTransposition>
      | ReturnType<typeof applyLedgerSafeDetachMapping>
      | ReturnType<typeof applyLedgerSafeDetachMappings>
      | ReturnType<typeof applyLedgerSafeDetachRetiredMaterial>
      | ReturnType<typeof applyLedgerSafeRetiredMaterialDisposition>
      | ReturnType<typeof applyLedgerSafeRetainEmptyRowAfterReview>
      | ReturnType<typeof applyLedgerSafeClearGap>
      | ReturnType<typeof applyLedgerSafePlaceFragmentInRow>
      | ReturnType<typeof applyLedgerSafeInsertRowForFragment>
      | ReturnType<typeof applyLedgerSafeAutoPlaceFragments>,
    successMessage: string,
    editorialDescriptor: LedgerSafeEditorialDescriptor
  ) => {
    if (isMatrixContentApplying) {
      return null
    }
    const operationToken = ledgerOperationGateRef.current.begin()
    try {
      const result = operation()
      applyingLedgerMatrixRowIdsRef.current = new Set(result.changedRowIds)
      setIsLedgerApplying(true)
      const rowsToPersist = selectLedgerSafeRowEditUpserts({
        previousRows: resolvedRows,
        result,
      })
      await upsertRows(chapterId, result.updatedRows, {
        autoRelabel: result.requiresRelabel,
        changedRowIds: rowsToPersist.map(row => row.id),
      })
      await persistEditorialEvent({
        source: editorialDescriptor.source || 'ledger-safe-studio',
        kind: editorialDescriptor.kind,
        params: editorialDescriptor.params,
        summary: successMessage,
        affectedWitnessIds: editorialDescriptor.affectedWitnessIds || [result.selectedCell.witnessId],
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: editorialDescriptor.metadata
      })
      if (!ledgerOperationGateRef.current.isCurrent(operationToken)) {
        return
      }
      setSelectedCell(result.selectedCell)
      setSelectionAnchorCell(result.selectedCell)
      setSelectedRange(
        result.selectedRowIds && result.selectedRowIds.length > 1
          ? {
            witnessId: result.selectedCell.witnessId,
            rowIds: result.selectedRowIds
          }
          : null
      )
      showToast(successMessage)
      return result
    } catch (error) {
      if (!ledgerOperationGateRef.current.isCurrent(operationToken)) {
        return null
      }
      const message = error instanceof Error ? error.message : 'Collation edit failed'
      showToast(message)
      return null
    } finally {
      if (ledgerOperationGateRef.current.isCurrent(operationToken)) {
        applyingLedgerMatrixRowIdsRef.current = new Set()
        setIsLedgerApplying(false)
      }
    }
  })

  const handleSetRetiredMaterialDisposition = useEventCallback(async (
    issue: LedgerSafeRetiredMaterialIssue,
    disposition: 'retained' | 'hidden',
  ) => {
    const row = rowById.get(issue.rowId)
    const rowLabel = row ? getRowLabel(row) : issue.rowId
    await applyLedgerRowOperation(
      () => applyLedgerSafeRetiredMaterialDisposition({
        rows: resolvedRows,
        rowId: issue.rowId,
        witnessId: issue.witnessId,
        unitUuid: issue.unitUuid,
        disposition,
        kind: issue.kind,
        reason: issue.reason,
      }),
      disposition === 'retained'
        ? `Kept ${issue.witnessSigla} source remnant as paratext in ${rowLabel}`
        : `Hid ${issue.witnessSigla} source remnant in ${rowLabel}`,
      {
        kind: 'resolve_retired_material',
        params: {
          rowId: issue.rowId,
          witnessId: issue.witnessId,
          unitUuid: issue.unitUuid,
          disposition,
          materialKind: issue.kind,
        },
        affectedWitnessIds: [issue.witnessId],
      },
    )
  })

  const handleDetachRetiredMaterial = useEventCallback(async (
    issue: LedgerSafeRetiredMaterialIssue,
  ) => {
    const row = rowById.get(issue.rowId)
    const rowLabel = row ? getRowLabel(row) : issue.rowId
    await applyLedgerRowOperation(
      () => applyLedgerSafeDetachRetiredMaterial({
        rows: resolvedRows,
        rowId: issue.rowId,
        witnessId: issue.witnessId,
        unitUuid: issue.unitUuid,
      }),
      `Detached ${issue.witnessSigla} source remnant from ${rowLabel}`,
      {
        kind: 'detach_mapping',
        params: {
          rowId: issue.rowId,
          witnessId: issue.witnessId,
          unitUuid: issue.unitUuid,
          reason: 'retired-source-material',
        },
        affectedWitnessIds: [issue.witnessId],
      },
    )
  })

  const handleRetainEmptyRowAfterReview = useEventCallback(async (
    emptyIssue: LedgerSafeEmptyReadingRowIssue,
  ) => {
    const selectedWitnessId = selectedCell?.witnessId || activeWitness?.id || scopeWitnesses[0]?.id
    if (!selectedWitnessId) {
      showToast('Select a witness before retaining this row')
      return
    }
    const row = rowById.get(emptyIssue.rowId)
    const rowLabel = row ? getRowLabel(row) : emptyIssue.rowId
    await applyLedgerRowOperation(
      () => applyLedgerSafeRetainEmptyRowAfterReview({
        rows: resolvedRows,
        rowId: emptyIssue.rowId,
        selectedWitnessId,
      }),
      `Retained reviewed empty row ${rowLabel}`,
      {
        kind: 'resolve_retired_material',
        params: {
          rowId: emptyIssue.rowId,
          disposition: 'retained',
          scope: 'empty-row',
        },
        affectedWitnessIds: [],
      },
    )
  })

  const handleDeleteEmptyRowAfterReview = useEventCallback(async (
    emptyIssue: LedgerSafeEmptyReadingRowIssue,
  ) => {
    if (isMatrixContentApplying || !runtimeEnableSnapshots || !chapterId || readOnly) {
      return
    }
    const structuralDependencyCount = structuralBoundaryRangeCountByRowId.get(emptyIssue.rowId) || 0
    if (structuralDependencyCount > 0) {
      showToast('Move the structural range boundary before deleting this row')
      return
    }
    const row = rowById.get(emptyIssue.rowId)
    if (!row) {
      showToast('The empty row is no longer present')
      return
    }
    const rowLabel = getRowLabel(row)
    const metadataWarnings = [
      emptyIssue.hasRowTitle ? 'a row title' : '',
      emptyIssue.sceneTitle ? `legacy scene metadata (“${emptyIssue.sceneTitle}”)` : '',
    ].filter(Boolean)
    const warningText = metadataWarnings.length > 0
      ? ` This will also remove ${metadataWarnings.join(' and ')}.`
      : ''
    if (!window.confirm(
      `Delete ${rowLabel}? It has no witness readings.${warningText} A chapter backup will be created first.`,
    )) {
      return
    }
    const selectedWitnessId = selectedCell?.witnessId || activeWitness?.id || scopeWitnesses[0]?.id
    if (!selectedWitnessId) {
      showToast('Select a witness before deleting this row')
      return
    }

    setIsLedgerApplying(true)
    try {
      const backup = await createLedgerSnapshot({
        reason: 'pre-empty-row-compaction',
        label: `Before deleting reviewed empty row ${rowLabel}`,
      })
      const result = deleteLedgerSafeEmptyRowAfterReview({
        rows: resolvedRows,
        rowId: emptyIssue.rowId,
        selectedWitnessId,
      })
      applyingLedgerMatrixRowIdsRef.current = new Set(result.changedRowIds)
      await replaceResolvedRows(chapterId, resolvedRows, result.updatedRows, {
        autoRelabel: result.requiresRelabel,
      })
      await persistEditorialEvent({
        kind: 'delete_empty_row_after_review',
        params: {
          rowId: emptyIssue.rowId,
          removedRowTitle: emptyIssue.hasRowTitle,
          removedLegacySceneMetadata: Boolean(emptyIssue.sceneTitle),
        },
        summary: `Deleted reviewed empty row ${rowLabel}`,
        affectedWitnessIds: scopeWitnesses.map(witness => witness.id),
        affectedRowIds: result.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: result.updatedRows,
        metadata: { backupSnapshotId: backup?.id || null },
      })
      setSelectedCell(result.selectedCell)
      setSelectionAnchorCell(result.selectedCell)
      setSelectedRange(null)
      showToast(`Deleted reviewed empty row ${rowLabel}`)
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown persistence error'
      showToast(`Row deletion stopped safely: ${detail}`)
    } finally {
      applyingLedgerMatrixRowIdsRef.current = new Set()
      setIsLedgerApplying(false)
    }
  })

  const dismissLedgerSafeEditSuggestion = useCallback((suggestionId: string) => {
    if (!suggestionId) {
      return
    }
    setDismissedEditSuggestionIds(current => {
      const next = Array.from(new Set([...current, suggestionId])).slice(-200)
      if (editSuggestionStorageKey) {
        safeStorageSet(editSuggestionStorageKey, JSON.stringify(next))
      }
      return next
    })
  }, [editSuggestionStorageKey])

  const handleApplyEditSuggestion = useEventCallback(async (suggestion: LedgerSafeEditSuggestion) => {
    if (isMatrixContentApplying) {
      return
    }
    const witness = scopeWitnesses.find(candidate => candidate.id === suggestion.targetWitnessId)
    if (!witness) {
      showToast('Suggested edit target witness is no longer available')
      dismissLedgerSafeEditSuggestion(suggestion.id)
      return
    }

    const metadata = {
      suggestedFromEventId: suggestion.sourceEventId,
      suggestionId: suggestion.id,
      suggestionReason: suggestion.reason
    }

    if (suggestion.kind === 'boundary_shift') {
      if (!witness.segmentation_result || !witness.uploaded_file?.content) {
        showToast('Suggested boundary edit is no longer available')
        dismissLedgerSafeEditSuggestion(suggestion.id)
        return
      }
      const operationToken = segmentationOperationGateRef.current.begin()
      setIsApplying(true)
      try {
        const leftRowId = String(suggestion.params.leftRowId || '')
        const deltaWords = Number(suggestion.params.deltaWords || 0)
        const boundaryResult = applyLedgerSafeBoundaryShiftToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          leftRowId,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content,
          deltaWords
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: boundaryResult.updatedSegmentationResult,
          matrixRowLatch: 'segmentation',
        })
        if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
          return
        }
        const successMessage = `Applied suggested boundary edit to ${witness.sigla}`
        await persistEditorialEvent({
          kind: 'boundary_shift',
          params: {
            witnessId: witness.id,
            leftRowId,
            deltaWords
          },
          summary: successMessage,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata
        })
        dismissLedgerSafeEditSuggestion(suggestion.id)
        setSelectedCell({ rowId: leftRowId, witnessId: witness.id })
        setSelectionAnchorCell({ rowId: leftRowId, witnessId: witness.id })
        setSelectedRange(null)
        showToast(successMessage)
      } catch (error) {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          showToast(error instanceof Error ? error.message : 'Suggested boundary edit failed')
        }
      } finally {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          applyingSegmentationMatrixRowIdsRef.current = new Set()
          setIsApplying(false)
        }
      }
      return
    }

    if (suggestion.kind === 'split_unit') {
      if (!witness.segmentation_result || !witness.uploaded_file?.content) {
        showToast('Suggested split is no longer available')
        dismissLedgerSafeEditSuggestion(suggestion.id)
        return
      }
      const operationToken = segmentationOperationGateRef.current.begin()
      setIsApplying(true)
      try {
        const rowId = String(suggestion.params.rowId || suggestion.rowIds[0] || '')
        const afterWordIndex = Number(suggestion.params.afterWordIndex || 0)
        const splitResult = applyLedgerSafeUnitSplitToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          rowId,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content,
          splitBoundaryWordIndex: afterWordIndex
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: splitResult.updatedSegmentationResult,
          matrixRowLatch: 'segmentation',
        })
        if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
          return
        }
        const nextUnassignedCount = reconcileResult.unassignedFragments.length
        const fragmentSuffix = nextUnassignedCount > 0
          ? ` · ${nextUnassignedCount} unassigned fragment${nextUnassignedCount === 1 ? '' : 's'}`
          : ''
        const successMessage = `Applied suggested split to ${witness.sigla}${fragmentSuffix}`
        await persistEditorialEvent({
          kind: 'split_unit',
          params: {
            witnessId: witness.id,
            rowId,
            afterWordIndex
          },
          summary: successMessage,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata
        })
        dismissLedgerSafeEditSuggestion(suggestion.id)
        setSelectedCell({ rowId, witnessId: witness.id })
        setSelectionAnchorCell({ rowId, witnessId: witness.id })
        setSelectedRange(null)
        setSelectedSplitBoundaryWordIndex(null)
        setInlineEditMode('idle')
        showToast(successMessage)
      } catch (error) {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          showToast(error instanceof Error ? error.message : 'Suggested split failed')
        }
      } finally {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          applyingSegmentationMatrixRowIdsRef.current = new Set()
          setIsApplying(false)
        }
      }
      return
    }

    if (suggestion.kind === 'merge_units') {
      if (!witness.segmentation_result || !witness.uploaded_file?.content) {
        showToast('Suggested merge is no longer available')
        dismissLedgerSafeEditSuggestion(suggestion.id)
        return
      }
      const operationToken = segmentationOperationGateRef.current.begin()
      setIsApplying(true)
      try {
        const rowIds = Array.isArray(suggestion.params.rowIds)
          ? suggestion.params.rowIds.map(value => String(value))
          : suggestion.rowIds
        const mergeResult = applyLedgerSafeMergeUnitsToSegmentation({
          rows: resolvedRows,
          witnessId: witness.id,
          rowIds,
          segmentationResult: witness.segmentation_result,
          sourceText: witness.uploaded_file.content
        })
        const reconcileResult = await persistSegmentationEdit({
          witness,
          previousSegmentationResult: witness.segmentation_result,
          nextSegmentationResult: mergeResult.updatedSegmentationResult,
          matrixRowLatch: 'segmentation',
        })
        if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
          return
        }
        const successMessage = `Applied suggested merge to ${witness.sigla}`
        await persistEditorialEvent({
          kind: 'merge_units',
          params: {
            witnessId: witness.id,
            rowIds
          },
          summary: successMessage,
          affectedWitnessIds: [witness.id],
          affectedRowIds: reconcileResult.changedRowIds,
          beforeRows: resolvedRows,
          afterRows: reconcileResult.updatedRows,
          metadata
        })
        dismissLedgerSafeEditSuggestion(suggestion.id)
        setSelectedCell({ rowId: rowIds[0], witnessId: witness.id })
        setSelectionAnchorCell({ rowId: rowIds[0], witnessId: witness.id })
        setSelectedRange(null)
        showToast(successMessage)
      } catch (error) {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          showToast(error instanceof Error ? error.message : 'Suggested merge failed')
        }
      } finally {
        if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
          applyingSegmentationMatrixRowIdsRef.current = new Set()
          setIsApplying(false)
        }
      }
      return
    }

    const rowIds = suggestion.rowIds
    const rowId = String(suggestion.params.rowId || rowIds[0] || '')
    if (suggestion.kind === 'move_mapping') {
      const direction = suggestion.params.direction === 'up' ? 'up' : 'down'
      await applyLedgerRowOperation(() => applyLedgerSafeMoveMapping({
        rows: resolvedRows,
        witnessId: witness.id,
        rowId,
        direction
      }), `Applied suggested move to ${witness.sigla}`, {
        kind: 'move_mapping',
        params: { witnessId: witness.id, rowId, direction },
        affectedWitnessIds: [witness.id],
        metadata
      })
      dismissLedgerSafeEditSuggestion(suggestion.id)
      return
    }
    if (suggestion.kind === 'shift_range') {
      const direction = suggestion.params.direction === 'up' ? 'up' : 'down'
      await applyLedgerRowOperation(() => applyLedgerSafeShiftRange({
        rows: resolvedRows,
        witnessId: witness.id,
        rowIds,
        direction
      }), `Applied suggested range shift to ${witness.sigla}`, {
        kind: 'shift_range',
        params: { witnessId: witness.id, rowIds, direction },
        affectedWitnessIds: [witness.id],
        metadata
      })
      dismissLedgerSafeEditSuggestion(suggestion.id)
      return
    }
    if (suggestion.kind === 'insert_row_and_move') {
      const placement = suggestion.params.placement === 'above' ? 'above' : 'below'
      await applyLedgerRowOperation(() => applyLedgerSafeInsertRowAndMove({
        rows: resolvedRows,
        witnessId: witness.id,
        rowId,
        placement
      }), `Applied suggested row insertion to ${witness.sigla}`, {
        kind: 'insert_row_and_move',
        params: { witnessId: witness.id, rowId, placement },
        affectedWitnessIds: [witness.id],
        metadata
      })
      dismissLedgerSafeEditSuggestion(suggestion.id)
      return
    }
    if (suggestion.kind === 'mark_gap') {
      await applyLedgerRowOperation(() => applyLedgerSafeMarkGap({
        rows: resolvedRows,
        witnessId: witness.id,
        rowId
      }), `Applied suggested gap to ${witness.sigla}`, {
        kind: 'mark_gap',
        params: { witnessId: witness.id, rowId },
        affectedWitnessIds: [witness.id],
        metadata
      })
      dismissLedgerSafeEditSuggestion(suggestion.id)
      return
    }
    if (suggestion.kind === 'clear_gap') {
      await applyLedgerRowOperation(() => applyLedgerSafeClearGap({
        rows: resolvedRows,
        witnessId: witness.id,
        rowId
      }), `Cleared suggested gap in ${witness.sigla}`, {
        kind: 'clear_gap',
        params: { witnessId: witness.id, rowId },
        affectedWitnessIds: [witness.id],
        metadata
      })
      dismissLedgerSafeEditSuggestion(suggestion.id)
    }
  })

  const confirmFragmentAnchor = useCallback(async ({
    fragment,
    previousRowId,
    nextRowId,
    source
  }: {
    fragment: LedgerSafeUnassignedFragment
    previousRowId: string | null
    nextRowId: string | null
    source: LedgerSafeFragmentAnchorDraft['source']
  }): Promise<void> => {
    const anchorKey = buildLedgerSafeFragmentAnchorKey(fragment.witnessId, fragment.unitUuid)
    const existing = fragmentAnchorDrafts.find(candidate => (
      buildLedgerSafeFragmentAnchorKey(candidate.witnessId, candidate.unitUuid) === anchorKey
    ))
    const anchor = buildLedgerSafeFragmentAnchorDraft({
      fragment,
      previousRowId,
      nextRowId,
      source,
      existing
    })
    const validation = resolveLedgerSafeFragmentAnchors({
      fragments: [fragment],
      anchors: [anchor],
      rows: resolvedRows
    })
    if (validation.validAnchors.length !== 1) {
      throw new Error('That fragment boundary is no longer valid in the current collation order')
    }

    await persistFragmentAnchorDrafts(
      upsertLedgerSafeFragmentAnchor(fragmentAnchorDrafts, anchor)
    )
    setFragmentAnchorSelection(null)
    setRecentFragmentReviewRowIds([previousRowId, nextRowId].filter((rowId): rowId is string => Boolean(rowId)))
    setIsFragmentReviewMode(true)
    const previousLabel = previousRowId ? getRowLabel(rowById.get(previousRowId)!) : 'Start'
    const nextLabel = nextRowId ? getRowLabel(rowById.get(nextRowId)!) : 'End'
    showToast(`Reanchored ${fragment.unitLabel || fragment.unitUuid} as a fragment: ${previousLabel} → ${nextLabel}`)
  }, [
    fragmentAnchorDrafts,
    persistFragmentAnchorDrafts,
    resolvedRows,
    rowById,
    showToast
  ])

  const handleReanchorUnplacedUnit = useCallback(async (
    unplacedUnit: LedgerSafeSequenceUnplacedUnit
  ): Promise<void> => {
    if (!activeWitness) {
      return
    }
    const fragment = unassignedFragmentByUnitUuid.get(unplacedUnit.unitUuid)
    if (!fragment) {
      showToast('This unplaced unit is no longer available as a fragment')
      return
    }
    const previousRowId = unplacedUnit.belongsAfterRowId || fragment.previousRowId
    const nextRowId = unplacedUnit.belongsBeforeRowId || fragment.nextRowId
    if (!previousRowId && !nextRowId) {
      setFragmentAnchorSelection({
        witnessId: fragment.witnessId,
        unitUuid: fragment.unitUuid
      })
      showToast('Select the ledger row immediately below this fragment')
      return
    }
    try {
      await confirmFragmentAnchor({
        fragment,
        previousRowId,
        nextRowId,
        source: 'sequence-audit'
      })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Fragment reanchoring failed')
    }
  }, [activeWitness, confirmFragmentAnchor, showToast, unassignedFragmentByUnitUuid])

  const handleAnchorFragmentBeforeRow = useCallback(async (nextRowId: string): Promise<void> => {
    if (!fragmentAnchorSelection) {
      return
    }
    const fragment = unassignedFragmentByUnitUuid.get(fragmentAnchorSelection.unitUuid)
    if (!fragment) {
      setFragmentAnchorSelection(null)
      showToast('This fragment is no longer available')
      return
    }
    const sortedRows = [...resolvedRows].sort((left, right) => (
      left.position - right.position || left.labelNumber - right.labelNumber
    ))
    const nextRowIndex = sortedRows.findIndex(row => row.id === nextRowId)
    if (nextRowIndex < 0) {
      showToast('The selected row is no longer available')
      return
    }
    try {
      await confirmFragmentAnchor({
        fragment,
        previousRowId: nextRowIndex > 0 ? sortedRows[nextRowIndex - 1].id : null,
        nextRowId,
        source: 'manual'
      })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Fragment reanchoring failed')
    }
  }, [confirmFragmentAnchor, fragmentAnchorSelection, resolvedRows, showToast, unassignedFragmentByUnitUuid])

  const handleAnchorFragmentAtEnd = useCallback(async (): Promise<void> => {
    if (!fragmentAnchorSelection) {
      return
    }
    const fragment = unassignedFragmentByUnitUuid.get(fragmentAnchorSelection.unitUuid)
    const finalRow = [...resolvedRows].sort((left, right) => (
      left.position - right.position || left.labelNumber - right.labelNumber
    )).at(-1)
    if (!fragment || !finalRow) {
      setFragmentAnchorSelection(null)
      showToast('This fragment or the final ledger row is no longer available')
      return
    }
    try {
      await confirmFragmentAnchor({
        fragment,
        previousRowId: finalRow.id,
        nextRowId: null,
        source: 'manual'
      })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Fragment reanchoring failed')
    }
  }, [confirmFragmentAnchor, fragmentAnchorSelection, resolvedRows, showToast, unassignedFragmentByUnitUuid])

  const clearFragmentAnchorDrafts = useCallback(async (fragmentKeys: string[]): Promise<void> => {
    const nextDrafts = removeLedgerSafeFragmentAnchors(fragmentAnchorDrafts, fragmentKeys)
    if (nextDrafts.length === fragmentAnchorDrafts.length) {
      return
    }
    try {
      await persistFragmentAnchorDrafts(nextDrafts)
    } catch (error) {
      console.error('Failed to clear placed fragment anchors', error)
    }
  }, [fragmentAnchorDrafts, persistFragmentAnchorDrafts])

  const handleFragmentPlacement = useCallback(async ({
    fragment,
    mode
  }: {
    fragment: LedgerSafeUnassignedFragment
    mode: LedgerSafeFragmentPlacementMode
  }) => {
    const previousRowId = fragment.previousRowId
    const nextRowId = fragment.nextRowId
    setRecentFragmentReviewRowIds(Array.from(new Set([
      previousRowId || '',
      nextRowId || '',
    ].filter(Boolean))))
    setIsFragmentReviewMode(true)
    const editorialDescriptor: LedgerSafeEditorialDescriptor =
      mode === 'insert-after-previous'
        ? {
          kind: 'insert_row_for_fragment',
          params: {
            witnessId: fragment.witnessId,
            unitUuid: fragment.unitUuid,
            anchorRowId: previousRowId,
            placement: 'below'
          },
          affectedWitnessIds: [fragment.witnessId]
        }
        : mode === 'insert-before-next'
          ? {
            kind: 'insert_row_for_fragment',
            params: {
              witnessId: fragment.witnessId,
              unitUuid: fragment.unitUuid,
              anchorRowId: nextRowId,
              placement: 'above'
            },
            affectedWitnessIds: [fragment.witnessId]
          }
          : mode === 'place-in-previous'
            ? {
              kind: 'place_fragment_in_row',
              params: {
                witnessId: fragment.witnessId,
                unitUuid: fragment.unitUuid,
                rowId: previousRowId
              },
              affectedWitnessIds: [fragment.witnessId]
            }
            : {
              kind: 'place_fragment_in_row',
              params: {
                witnessId: fragment.witnessId,
                unitUuid: fragment.unitUuid,
                rowId: nextRowId
              },
              affectedWitnessIds: [fragment.witnessId]
            }

    const result = await applyLedgerRowOperation(() => {
      if (mode === 'insert-after-previous') {
        if (!previousRowId) {
          throw new Error('No previous row is available for insertion')
        }
        return applyLedgerSafeInsertRowForFragment({
          rows: resolvedRows,
          fragment,
          anchorRowId: previousRowId,
          placement: 'below'
        })
      }
      if (mode === 'insert-before-next') {
        if (!nextRowId) {
          throw new Error('No next row is available for insertion')
        }
        return applyLedgerSafeInsertRowForFragment({
          rows: resolvedRows,
          fragment,
          anchorRowId: nextRowId,
          placement: 'above'
        })
      }
      if (mode === 'place-in-previous') {
        if (!previousRowId) {
          throw new Error('No previous row is available for placement')
        }
        return applyLedgerSafePlaceFragmentInRow({
          rows: resolvedRows,
          fragment,
          rowId: previousRowId
        })
      }
      if (!nextRowId) {
        throw new Error('No next row is available for placement')
      }
      return applyLedgerSafePlaceFragmentInRow({
        rows: resolvedRows,
        fragment,
        rowId: nextRowId
      })
    }, `Placed ${fragment.unitLabel || fragment.unitUuid} in the collation`, editorialDescriptor)

    if (result) {
      await clearFragmentAnchorDrafts([
        buildLedgerSafeFragmentAnchorKey(fragment.witnessId, fragment.unitUuid)
      ])
      setRecentFragmentReviewRowIds(Array.from(new Set([
        result.selectedCell.rowId,
        ...result.changedRowIds,
        previousRowId || '',
        nextRowId || '',
      ].filter(Boolean))))
      setIsFragmentReviewMode(true)
    }
  }, [applyLedgerRowOperation, clearFragmentAnchorDrafts, resolvedRows])

  const handleAutoPlaceFragments = useCallback(async () => {
    if (!activeWitness || unassignedFragments.length === 0) {
      return
    }

    const result = await applyLedgerRowOperation(() => {
      const result = applyLedgerSafeAutoPlaceFragments({
        rows: resolvedRows,
        fragments: unassignedFragments
      })
      if (result.skippedFragments.length > 0) {
        const skippedList = result.skippedFragments
          .map(fragment => fragment.fragmentUnitUuid)
          .slice(0, 3)
          .join(', ')
        throw new Error(`Automatic placement blocked: ${result.skippedFragments.length} fragment${result.skippedFragments.length === 1 ? '' : 's'} could not be placed (${skippedList}). No fragments were saved.`)
      }
      return result
    }, `Placed ${unassignedFragments.length} fragment${unassignedFragments.length === 1 ? '' : 's'} in the collation`, {
      kind: 'auto_place_fragments',
      params: {
        witnessId: activeWitness.id,
        unitUuids: unassignedFragments.map(fragment => fragment.unitUuid)
      },
      affectedWitnessIds: [activeWitness.id],
      metadata: {
        fragmentCount: unassignedFragments.length
      }
    })
    if (result) {
      await clearFragmentAnchorDrafts(unassignedFragments.map(fragment => (
        buildLedgerSafeFragmentAnchorKey(fragment.witnessId, fragment.unitUuid)
      )))
      setRecentFragmentReviewRowIds(result.changedRowIds)
      setIsFragmentReviewMode(true)
    }
  }, [activeWitness, applyLedgerRowOperation, clearFragmentAnchorDrafts, resolvedRows, unassignedFragments])

  const renderInlineFragmentRows = useCallback((
    fragments: LedgerSafeUnassignedFragment[],
    keyScope: string
  ): React.ReactNode => {
    if (fragments.length === 0) {
      return null
    }

    return fragments.map(fragment => {
      const isArabic = hasArabicText(fragment.text)
      const previousRow = fragment.previousRowId ? rowById.get(fragment.previousRowId) || null : null
      const nextRow = fragment.nextRowId ? rowById.get(fragment.nextRowId) || null : null
      const previousState = previousRow ? getLedgerSafeCellState(previousRow, fragment.witnessId) : null
      const nextState = nextRow ? getLedgerSafeCellState(nextRow, fragment.witnessId) : null
      const previousOccupied = previousState === 'mapped'
      const nextOccupied = nextState === 'mapped'
      const badgeClasses = isReadOnlyAppliedAuditActive
        ? 'border-amber-300 bg-amber-100/80 text-amber-800'
        : 'border-sky-300 bg-sky-100/80 text-sky-800'

      return (
        <tr
          key={`fragment-inline-${keyScope}-${fragment.unitUuid}`}
          data-testid={`ledger-fragment-inline-row-${fragment.unitUuid}`}
          className="bg-white"
        >
          {visibleScopeWitnesses.map(witness => {
            const isFragmentWitnessCell = witness.id === fragment.witnessId
            if (!isFragmentWitnessCell) {
              return (
                <td
                  key={`fragment-inline-${keyScope}-${fragment.unitUuid}-${witness.id}`}
                  className="border-r border-b border-black/10 bg-white px-4 py-2 align-middle"
                  style={GAP_CELL_STYLE}
                >
                  <div className="flex h-4 items-center justify-center" aria-hidden="true">
                    <span className="block h-[2px] w-8 rounded-full bg-slate-300/60" />
                  </div>
                </td>
              )
            }

            return (
              <td
                key={`fragment-inline-${keyScope}-${fragment.unitUuid}-${witness.id}`}
                className={`border-r border-b border-black/10 px-2 py-2 align-top ring-1 ring-inset ${isReadOnlyAppliedAuditActive ? 'bg-amber-50/75 ring-amber-300/70' : 'bg-sky-50/75 ring-sky-300/70'}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-gray-500">
                  <span className="max-w-[86px] truncate">{fragment.unitLabel || fragment.unitUuid}</span>
                  <span className={`border px-1.5 py-0.5 ${badgeClasses}`}>
                    {isReadOnlyAppliedAuditActive ? 'Unmapped' : 'Fragment'}
                  </span>
                  <span className="normal-case tracking-normal text-gray-400">
                    Above {previousRow ? getRowLabel(previousRow) : 'Start'} ({getFragmentPlacementHint(previousState)}) · Below {nextRow ? getRowLabel(nextRow) : 'End'} ({getFragmentPlacementHint(nextState)})
                  </span>
                </div>
                <div
                  className={`mt-1.5 text-sm ${isArabic ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                  dir={isArabic ? 'rtl' : 'ltr'}
                >
                  {fragment.text}
                </div>
                {!isReadOnlyAppliedAuditActive && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => void handleFragmentPlacement({ fragment, mode: 'insert-after-previous' })}
                      disabled={isMatrixContentApplying || !fragment.previousRowId}
                      data-testid={`ledger-fragment-inline-new-row-below-${fragment.unitUuid}`}
                      aria-label={previousRow ? `New below ${getRowLabel(previousRow)}` : 'New below'}
                      title={previousRow ? `New below ${getRowLabel(previousRow)}` : 'New below'}
                      className="h-6 border border-black bg-white px-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                    >
                      New below
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleFragmentPlacement({ fragment, mode: 'insert-before-next' })}
                      disabled={isMatrixContentApplying || !fragment.nextRowId}
                      data-testid={`ledger-fragment-inline-new-row-above-${fragment.unitUuid}`}
                      aria-label={nextRow ? `New above ${getRowLabel(nextRow)}` : 'New above'}
                      title={nextRow ? `New above ${getRowLabel(nextRow)}` : 'New above'}
                      className="h-6 border border-black bg-white px-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                    >
                      New above
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleFragmentPlacement({ fragment, mode: 'place-in-previous' })}
                      disabled={isMatrixContentApplying || !fragment.previousRowId || previousOccupied}
                      data-testid={`ledger-fragment-inline-place-previous-${fragment.unitUuid}`}
                      aria-label={getFragmentPlacementLabel(previousRow, previousState, 'Place above')}
                      title={`${previousRow ? getRowLabel(previousRow) : 'Above'}: ${getFragmentPlacementHint(previousState)}`}
                      className="h-6 border border-black bg-white px-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Merge above
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleFragmentPlacement({ fragment, mode: 'place-in-next' })}
                      disabled={isMatrixContentApplying || !fragment.nextRowId || nextOccupied}
                      data-testid={`ledger-fragment-inline-place-next-${fragment.unitUuid}`}
                      aria-label={getFragmentPlacementLabel(nextRow, nextState, 'Place below')}
                      title={`${nextRow ? getRowLabel(nextRow) : 'Below'}: ${getFragmentPlacementHint(nextState)}`}
                      className="h-6 border border-black bg-white px-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Merge below
                    </button>
                  </div>
                )}
              </td>
            )
          })}
          <td className="border-b border-black/10 bg-white" />
        </tr>
      )
    })
  }, [
    handleFragmentPlacement,
    isMatrixContentApplying,
    isReadOnlyAppliedAuditActive,
    rowById,
    visibleScopeWitnesses
  ])

  const handleApply = useEventCallback(async () => {
    if (
      isMatrixContentApplying
      || !activeWitness?.segmentation_result
      || !activeWitness.uploaded_file?.content
      || !selectedBoundaryContext
      || !preview
    ) {
      return
    }
    if (preview.deltaWords === 0) {
      return
    }

    const operationToken = segmentationOperationGateRef.current.begin()
    try {
      const boundaryResult = applyLedgerSafeBoundaryShiftToSegmentation({
        rows: resolvedRows,
        witnessId: activeWitness.id,
        leftRowId: selectedBoundaryContext.leftRowId,
        segmentationResult: activeWitness.segmentation_result,
        sourceText: activeWitness.uploaded_file.content,
        deltaWords: pendingDeltaWords
      })
      applyingSegmentationMatrixRowIdsRef.current = new Set([
        selectedBoundaryContext.leftRowId,
        selectedBoundaryContext.rightRowId,
      ])
      setIsApplying(true)
      const reconcileResult = await persistSegmentationEdit({
        witness: activeWitness,
        previousSegmentationResult: activeWitness.segmentation_result,
        nextSegmentationResult: boundaryResult.updatedSegmentationResult,
        matrixRowLatch: 'segmentation',
      })
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }

      const nextUnassignedCount = reconcileResult.unassignedFragments.length
      const fragmentSuffix = nextUnassignedCount > 0
        ? ` · ${nextUnassignedCount} unassigned fragment${nextUnassignedCount === 1 ? '' : 's'}`
        : ''
      const successMessage = `Updated ${activeWitness.sigla} boundary across ${reconcileResult.changedRowIds.length} row${reconcileResult.changedRowIds.length === 1 ? '' : 's'}${fragmentSuffix}`
      await persistEditorialEvent({
        kind: 'boundary_shift',
        params: {
          witnessId: activeWitness.id,
          leftRowId: selectedBoundaryContext.leftRowId,
          deltaWords: pendingDeltaWords
        },
        summary: successMessage,
        affectedWitnessIds: [activeWitness.id],
        affectedRowIds: reconcileResult.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: reconcileResult.updatedRows
      })
      setSelectedRange(null)
      setPendingDeltaWords(0)
      showToast(successMessage)
    } catch (error) {
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }
      const message = error instanceof Error ? error.message : 'Boundary edit failed'
      showToast(message)
    } finally {
      if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
        applyingSegmentationMatrixRowIdsRef.current = new Set()
        setBoundaryDragState(null)
        setIsApplying(false)
      }
    }
  })

  const handleApplySplit = useEventCallback(async () => {
    if (
      isMatrixContentApplying ||
      !activeWitness?.segmentation_result ||
      !activeWitness.uploaded_file?.content ||
      !selectedSplitContext ||
      selectedSplitBoundaryWordIndex === null ||
      !splitPreview
    ) {
      return
    }

    const operationToken = segmentationOperationGateRef.current.begin()
    try {
      const splitResult = applyLedgerSafeUnitSplitToSegmentation({
        rows: resolvedRows,
        witnessId: activeWitness.id,
        rowId: selectedSplitContext.rowId,
        segmentationResult: activeWitness.segmentation_result,
        sourceText: activeWitness.uploaded_file.content,
        splitBoundaryWordIndex: selectedSplitBoundaryWordIndex
      })
      applyingSegmentationMatrixRowIdsRef.current = new Set([selectedSplitContext.rowId])
      setIsApplying(true)
      const reconcileResult = await persistSegmentationEdit({
        witness: activeWitness,
        previousSegmentationResult: activeWitness.segmentation_result,
        nextSegmentationResult: splitResult.updatedSegmentationResult,
        matrixRowLatch: 'segmentation',
      })
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }

      const nextUnassignedCount = reconcileResult.unassignedFragments.length
      const fragmentSuffix = nextUnassignedCount > 0
        ? ` · ${nextUnassignedCount} unassigned fragment${nextUnassignedCount === 1 ? '' : 's'}`
        : ''
      const successMessage = `Split ${activeWitness.sigla} unit${fragmentSuffix}`
      await persistEditorialEvent({
        kind: 'split_unit',
        params: {
          witnessId: activeWitness.id,
          rowId: selectedSplitContext.rowId,
          afterWordIndex: selectedSplitBoundaryWordIndex
        },
        summary: successMessage,
        affectedWitnessIds: [activeWitness.id],
        affectedRowIds: reconcileResult.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: reconcileResult.updatedRows
      })
      setSelectedRange(null)
      setSelectedCell({ rowId: selectedSplitContext.rowId, witnessId: activeWitness.id })
      setSelectedSplitBoundaryWordIndex(null)
      setInlineEditMode('idle')
      showToast(successMessage)
    } catch (error) {
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }
      const message = error instanceof Error ? error.message : 'Split failed'
      showToast(message)
    } finally {
      if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
        applyingSegmentationMatrixRowIdsRef.current = new Set()
        setIsApplying(false)
      }
    }
  })

  const handleApplyMergeRange = useEventCallback(async () => {
    if (
      isMatrixContentApplying ||
      !activeWitness?.segmentation_result ||
      !activeWitness.uploaded_file?.content ||
      !selectedRange ||
      !selectedRangeSummary ||
      selectedRangeSummary.mappedCount < 2
    ) {
      return
    }

    const operationToken = segmentationOperationGateRef.current.begin()
    try {
      const mergeResult = applyLedgerSafeMergeUnitsToSegmentation({
        rows: resolvedRows,
        witnessId: activeWitness.id,
        rowIds: selectedRange.rowIds,
        segmentationResult: activeWitness.segmentation_result,
        sourceText: activeWitness.uploaded_file.content
      })
      applyingSegmentationMatrixRowIdsRef.current = new Set(selectedRange.rowIds)
      setIsApplying(true)
      const reconcileResult = await persistSegmentationEdit({
        witness: activeWitness,
        previousSegmentationResult: activeWitness.segmentation_result,
        nextSegmentationResult: mergeResult.updatedSegmentationResult,
        matrixRowLatch: 'segmentation',
      })
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }

      const successMessage = `Merged ${selectedRangeSummary.mappedCount} ${activeWitness.sigla} units`
      await persistEditorialEvent({
        kind: 'merge_units',
        params: {
          witnessId: activeWitness.id,
          rowIds: selectedRange.rowIds
        },
        summary: successMessage,
        affectedWitnessIds: [activeWitness.id],
        affectedRowIds: reconcileResult.changedRowIds,
        beforeRows: resolvedRows,
        afterRows: reconcileResult.updatedRows
      })
      const survivorRowId = selectedRangeSummary.mappedRowIds[0]
      if (survivorRowId) {
        const nextSelection = { rowId: survivorRowId, witnessId: activeWitness.id }
        setSelectedCell(nextSelection)
        setSelectionAnchorCell(nextSelection)
      }
      setSelectedRange(null)
      setInlineEditMode('idle')
      setSelectedSplitBoundaryWordIndex(null)
      showToast(successMessage)
    } catch (error) {
      if (!segmentationOperationGateRef.current.isCurrent(operationToken)) {
        return
      }
      const message = error instanceof Error ? error.message : 'Merge failed'
      showToast(message)
    } finally {
      if (segmentationOperationGateRef.current.isCurrent(operationToken)) {
        applyingSegmentationMatrixRowIdsRef.current = new Set()
        setIsApplying(false)
      }
    }
  })

  const canPreviewCurrentEditWithPi = useMemo(() => {
    if (inlineEditMode === 'split') {
      return Boolean(selectedSplitContext && selectedSplitBoundaryWordIndex !== null)
    }
    if (selectedRangeSummary && selectedRangeSummary.mappedCount >= 2) {
      return true
    }
    return Boolean(selectedBoundaryContext && preview && preview.deltaWords !== 0)
  }, [inlineEditMode, preview, selectedBoundaryContext, selectedRangeSummary, selectedSplitBoundaryWordIndex, selectedSplitContext])

  const piCurrentEditSummary = useMemo(() => {
    if (inlineEditMode === 'split' && selectedSplitContext) {
      return `${selectedSplitContext.unitLabel} · split unit`
    }
    if (selectedRangeSummary && selectedRangeSummary.mappedCount >= 2) {
      return `${selectedRangeSummary.fromLabel} → ${selectedRangeSummary.toLabel} · merge/range window`
    }
    if (inlineEditMode === 'boundary' && selectedBoundaryContext) {
      return `${selectedBoundaryContext.leftUnitLabel} → ${selectedBoundaryContext.rightUnitLabel} · boundary shift`
    }
    if (selectedLedgerWitness && selectedLedgerRow) {
      return `${selectedLedgerWitness.sigla} · ${getRowLabel(selectedLedgerRow)}`
    }
    return 'No active edit selected'
  }, [
    inlineEditMode,
    selectedBoundaryContext,
    selectedLedgerRow,
    selectedLedgerWitness,
    selectedRangeSummary,
    selectedSplitContext
  ])

  const latestPiReviewEntry = piReviewLog[0] || null
  const piDockState = piPreview
    ? 'preview'
    : latestPiReviewEntry?.kind === 'apply'
      ? 'applied'
      : 'idle'
  const piDockStateLabel = isPiBusy
    ? 'BUSY'
    : piDockState === 'preview'
      ? 'PREVIEW READY'
      : piDockState === 'applied'
        ? `APPLIED${piSnapshotId ? ` · snap ${piSnapshotId.slice(0, 8)}` : ''}`
        : piSessionId
          ? 'IDLE · SYNCED'
          : 'IDLE'
  const piDockStateColorClass = piDockState === 'preview'
    ? 'text-blue-600 border-blue-600'
    : piDockState === 'applied'
      ? 'text-emerald-700 border-emerald-700'
      : 'text-slate-600 border-slate-500'
  const piDockAuthStored = Boolean(
    piOAuthProviders?.find(provider => provider.id === 'openai-codex')?.hasAuth ||
    piOAuthLoginAttempt?.status === 'authenticated'
  )
  const piDockAuthLabel = piDockAuthStored ? 'ChatGPT · stored' : 'Login required'
  const piDockModelLabel = piAgentModelId.trim() || 'model'
  const focusPiDockInput = useCallback(() => {
    setIsPiDockExpanded(true)
    window.setTimeout(() => {
      piDockInputRef.current?.focus()
    }, 0)
  }, [])

  const refreshPiAgentRegister = useCallback(async () => {
    if (isPiBusy || isPiAgentRegisterBusy) return
    setIsPiAgentRegisterBusy(true)
    setPiError(null)
    try {
      const { sessionId } = await ensurePiSession()
      const [modelCatalog, report] = await Promise.all([
        listPiMatrixAgentModels({ provider: 'openai-codex' }),
        getPiMatrixAgentAgentReport({ sessionId }),
      ])
      setPiAgentModels(modelCatalog.models)
      setPiAgentReport(report)
      if (report.current) {
        applyPiAgentSessionState(report.current)
      } else if (!piAgentSession) {
        const preferredModel = modelCatalog.models.find(model => model.available) || modelCatalog.models[0]
        if (preferredModel && piAgentModelProvider !== preferredModel.provider) {
          setPiAgentModelProvider(preferredModel.provider)
          setPiAgentModelId(preferredModel.modelId)
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not refresh the Pi agent register'
      setPiError(message)
      appendPiReviewLog('error', message)
    } finally {
      setIsPiAgentRegisterBusy(false)
    }
  }, [
    appendPiReviewLog,
    applyPiAgentSessionState,
    ensurePiSession,
    isPiAgentRegisterBusy,
    isPiBusy,
    piAgentModelProvider,
    piAgentSession,
  ])

  const handleCameraAgentStart = useCallback<NonNullable<AgentCameraLucidaViewProps['piAgent']>['onStart']>(async options => {
    if (isPiBusy || isPiAgentRegisterBusy) return
    const operationToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setIsPiAgentRegisterBusy(true)
    setPiError(null)
    try {
      const { sessionId } = await ensurePiSession(operationToken)
      const created = await createPiMatrixAgentAgentSession({
        sessionId,
        model: options.model,
        thinkingLevel: options.thinkingLevel,
        resumeAgentSessionId: options.resumeAgentSessionId,
        sessionName: `Camera Lucida · ${contextLabel || describeManualStudioScope(activeScope)}`,
        tools: [],
      })
      if (!piOperationGateRef.current.isCurrent(operationToken)) return
      applyPiAgentSessionState(created)
      const report = await getPiMatrixAgentAgentReport({ sessionId })
      if (!piOperationGateRef.current.isCurrent(operationToken)) return
      setPiAgentReport(report)
      appendPiReviewLog(
        'agent',
        `${options.resumeAgentSessionId ? 'Resumed' : 'Started'} Pi agent ${created.agentSessionId} on ${created.model?.provider || 'default'}/${created.model?.modelId || 'default'} · ${created.thinkingLevel}`,
      )
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(operationToken)) {
        const message = error instanceof Error ? error.message : 'Failed to open the Pi agent session'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(operationToken)) setIsPiBusy(false)
      setIsPiAgentRegisterBusy(false)
    }
  }, [
    activeScope,
    appendPiReviewLog,
    applyPiAgentSessionState,
    contextLabel,
    ensurePiSession,
    isPiAgentRegisterBusy,
    isPiBusy,
    showToast,
  ])

  const handlePiDockResizeStart = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsPiDockExpanded(true)
    const startY = event.clientY
    const startHeight = piDockHeight
    let frameId: number | null = null
    let latestClientY = startY
    const applyPointerPosition = () => {
      frameId = null
      setPiDockHeight(clampPiDockHeight(startHeight + startY - latestClientY))
    }
    const handleMouseMove = (moveEvent: MouseEvent) => {
      latestClientY = moveEvent.clientY
      if (frameId === null) {
        frameId = window.requestAnimationFrame(applyPointerPosition)
      }
    }
    const handleMouseUp = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
        applyPointerPosition()
      }
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }, [piDockHeight])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        focusPiDockInput()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [focusPiDockInput])

  const handlePiSyncSession = useCallback(async () => {
    try {
      const { sessionId } = await ensurePiSession()
      showToast(`Pi sidecar synced for ${sessionId}`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Pi sidecar sync failed'
      showToast(message)
    }
  }, [ensurePiSession, showToast])

  const handlePiStartAgent = useCallback(async () => {
    const toolToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    try {
      const { sessionId } = await ensurePiSession(toolToken)
      const result = await createPiMatrixAgentAgentSession({
        sessionId,
        model: piAgentModelProvider && piAgentModelId
          ? {
              provider: piAgentModelProvider,
              modelId: piAgentModelId
          }
          : undefined,
        thinkingLevel: piAgentThinkingLevel,
        sessionName: `Variorum · ${contextLabel || describeManualStudioScope(activeScope)}`,
      })
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }
      applyPiAgentSessionState(result)
      setPiAgentReply(null)
      appendPiReviewLog('agent', `Started Pi agent ${result.agentSessionId} on ${result.model?.provider || 'default'}/${result.model?.modelId || 'default'}`)
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Failed to start Pi agent'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        setIsPiBusy(false)
      }
    }
  }, [activeScope, appendPiReviewLog, applyPiAgentSessionState, contextLabel, ensurePiSession, piAgentModelId, piAgentModelProvider, piAgentThinkingLevel, showToast])

  const handlePiLoginWithChatGPT = useCallback(async () => {
    const toolToken = piOperationGateRef.current.begin()
    const requestedModelId = piAgentModelProvider === 'openai-codex' && piAgentModelId.trim()
      ? piAgentModelId.trim()
      : 'gpt-5.4'

    setIsPiBusy(true)
    setIsPiAuthBusy(true)
    setPiAuthError(null)
    setPiError(null)
    setPiOAuthLoginInput('')
    setPiAgentModelProvider('openai-codex')
    setPiAgentModelId(requestedModelId)

    if (typeof window !== 'undefined') {
      piAuthWindowRef.current = window.open('', 'pi-openai-codex-login', 'popup=yes,width=640,height=760')
      if (piAuthWindowRef.current && !piAuthWindowRef.current.closed) {
        try {
          piAuthWindowRef.current.document.title = 'ChatGPT login'
          piAuthWindowRef.current.document.body.innerHTML = '<p style="font-family:system-ui;padding:16px">Connecting to ChatGPT login…</p>'
        } catch {
          // ignore popup write failures
        }
      }
    }

    try {
      const providers = await listPiMatrixAgentOAuthProviders()
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }
      setPiOAuthProviders(providers.providers)

      const { sessionId } = await ensurePiSession(toolToken)
      const attempt = await startPiMatrixAgentOAuthLogin({
        sessionId,
        providerId: 'openai-codex',
        model: {
          provider: 'openai-codex',
          modelId: requestedModelId
        },
        thinkingLevel: piAgentThinkingLevel,
        sessionName: `Camera Lucida · ${contextLabel || describeManualStudioScope(activeScope)}`,
        autoStartAgent: true
      })
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }

      applyPiOAuthLoginAttemptState(attempt)
      appendPiReviewLog('auth', `Started ${attempt.providerName} login`)

      if (attempt.authUrl) {
        if (piAuthWindowRef.current && !piAuthWindowRef.current.closed) {
          try {
            piAuthWindowRef.current.location.href = attempt.authUrl
          } catch {
            // ignore popup navigation failures
          }
        } else if (typeof window !== 'undefined') {
          window.open(attempt.authUrl, '_blank', 'noopener,noreferrer')
        }
      }

      if (!handlePiOAuthTerminalState(attempt)) {
        pollPiOAuthLoginAttempt(sessionId, attempt.attemptId)
      }
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Failed to start ChatGPT login'
        setPiAuthError(message)
        setPiError(message)
        setIsPiAuthBusy(false)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        setIsPiBusy(false)
      }
    }
  }, [
    appendPiReviewLog,
    activeScope,
    applyPiOAuthLoginAttemptState,
    contextLabel,
    ensurePiSession,
    handlePiOAuthTerminalState,
    piAgentModelId,
    piAgentModelProvider,
    piAgentThinkingLevel,
    pollPiOAuthLoginAttempt,
    showToast
  ])

  const handlePiSubmitOAuthLoginInput = useCallback(async (submittedValue?: string) => {
    const loginInput = (submittedValue ?? piOAuthLoginInput).trim()
    if (!piSessionId || !piOAuthLoginAttempt || !loginInput) {
      return
    }

    setIsPiAuthBusy(true)
    setPiAuthError(null)
    setPiError(null)
    try {
      const attempt = await submitPiMatrixAgentOAuthLoginInput({
        sessionId: piSessionId,
        attemptId: piOAuthLoginAttempt.attemptId,
        value: loginInput
      })
      setPiOAuthLoginInput('')
      if (!handlePiOAuthTerminalState(attempt)) {
        applyPiOAuthLoginAttemptState(attempt)
        pollPiOAuthLoginAttempt(piSessionId, attempt.attemptId)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit ChatGPT authorization code'
      setPiAuthError(message)
      setPiError(message)
      setIsPiAuthBusy(false)
      appendPiReviewLog('error', message)
      showToast(message)
    }
  }, [
    appendPiReviewLog,
    applyPiOAuthLoginAttemptState,
    handlePiOAuthTerminalState,
    piOAuthLoginAttempt,
    piOAuthLoginInput,
    piSessionId,
    pollPiOAuthLoginAttempt,
    showToast
  ])

  const handlePiPromptAgent = useCallback(async () => {
    if (!piAgentPrompt.trim()) {
      return
    }
    const toolToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    try {
      const { sessionId, agentRestartRequired } = await ensurePiSession(toolToken)
      if (!piAgentSessionId || agentRestartRequired) {
        const created = await createPiMatrixAgentAgentSession({
          sessionId,
          model: piAgentModelProvider && piAgentModelId
            ? { provider: piAgentModelProvider, modelId: piAgentModelId }
            : undefined,
          thinkingLevel: piAgentThinkingLevel,
          sessionName: `Variorum · ${contextLabel || describeManualStudioScope(activeScope)}`,
        })
        if (!piOperationGateRef.current.isCurrent(toolToken)) return
        applyPiAgentSessionState(created)
        appendPiReviewLog('agent', `Started Pi agent ${created.agentSessionId} for the current snapshot`)
      }
      const result = await promptPiMatrixAgentAgent({
        sessionId,
        prompt: piAgentPrompt.trim()
      })
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }
      setPiAgentReply(result.reply)
      appendPiReviewLog('agent', `Pi replied via ${result.reply.toolCalls.length} tool call${result.reply.toolCalls.length === 1 ? '' : 's'}`)
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Pi prompt failed'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        setIsPiBusy(false)
      }
    }
  }, [
    activeScope,
    appendPiReviewLog,
    applyPiAgentSessionState,
    contextLabel,
    ensurePiSession,
    piAgentModelId,
    piAgentModelProvider,
    piAgentPrompt,
    piAgentSessionId,
    piAgentThinkingLevel,
    showToast,
  ])

  const handleCameraLucidaInquiry = useCallback<NonNullable<AgentCameraLucidaViewProps['onInquiry']>>(async (
    question,
    { emit, signal },
  ) => {
    const operationToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    let finalReply = ''
    let pendingProposalId = ''
    let pendingChangeSetId = ''
    const findings: CameraLucidaFinding[] = []
    const createdAt = new Date().toISOString()
    let inquiryAgentSession = piAgentSession
    const recordAndEmitCameraEvent = (event: CameraLucidaEvent) => {
      if (event.type === 'finding.filed' || event.type === 'reading.suggested') findings.push(event.finding)
      emit(event)
    }
    const completionGate = createCameraLucidaCompletionGate(recordAndEmitCameraEvent)
    const forwardCameraEvent = (event: CameraLucidaEvent) => completionGate.forward(event)
    try {
      const {
        sessionId,
        snapshotId: sidecarSnapshotId,
        agentRestartRequired,
      } = await ensurePiSession(operationToken)
      const inquirySnapshot = latestPiSnapshotRef.current
      if (inquirySnapshot.snapshotId !== sidecarSnapshotId) {
        throw new Error('Camera Lucida authority changed while the inquiry was starting')
      }
      if (!piAgentSessionId || agentRestartRequired) {
        const created = await createPiMatrixAgentAgentSession({
          sessionId,
          model: {
            provider: piAgentModelProvider,
            modelId: piAgentModelId,
          },
          thinkingLevel: piAgentThinkingLevel,
          sessionName: `Camera Lucida · ${contextLabel || describeManualStudioScope(activeScope)}`,
          tools: [],
        })
        if (!piOperationGateRef.current.isCurrent(operationToken)) return
        inquiryAgentSession = created
        applyPiAgentSessionState(created)
        appendPiReviewLog('agent', `Camera Lucida opened ${created.model?.provider || piAgentModelProvider}/${created.model?.modelId || piAgentModelId}`)
      }
      activeCameraInquiryRef.current = { sessionId, operationToken }
      await promptPiMatrixAgentAgentStream({
        sessionId,
        prompt: buildCameraLucidaAgentPrompt(question),
        inquiryMode: true,
        signal,
        onEvent: event => {
          if (!piOperationGateRef.current.isCurrent(operationToken)) return
          if (event.type === 'agent.completed') finalReply = event.reply.text
          pendingProposalId = proposalIdFromPiStream(event) || pendingProposalId
          pendingChangeSetId = changeSetIdFromPiStream(event) || pendingChangeSetId
          const cameraEvent = cameraLucidaEventFromPiStream(event)
          if (!cameraEvent) return
          forwardCameraEvent(cameraEvent)
        },
      })
      if (!piOperationGateRef.current.isCurrent(operationToken)) return
      const completedSummary = completionGate.complete(finalReply)
      let receivedChangeSet: PiMatrixAgentChangeSet | null = null
      if (pendingChangeSetId) {
        receivedChangeSet = await getPiMatrixAgentChangeSet({ sessionId, changeSetId: pendingChangeSetId })
        if (!piOperationGateRef.current.isCurrent(operationToken)) return
      } else if (pendingProposalId) {
        const proposal = await getPiMatrixAgentProposal({ sessionId, proposalId: pendingProposalId })
        if (!piOperationGateRef.current.isCurrent(operationToken)) return
        const preview = {
          previewId: proposal.proposalId,
          baseSnapshotId: proposal.baseSnapshotId,
          operation: proposal.operation,
          affectedRowIds: proposal.affectedRowIds,
          affectedWitnessIds: proposal.affectedWitnessIds,
          invariants: proposal.invariants,
          summary: proposal.summary,
        }
        setPiPreview(preview)
        setPiPreviewEnvelope({
          preview,
          before: proposal.before,
          after: proposal.after,
          idRemap: proposal.idRemap,
          unassignedFragments: proposal.unassignedFragments,
        })
        setPiPreviewSummary(proposal.summary)
        appendPiReviewLog('preview', proposal.summary)
      }
      try {
        const report = await getPiMatrixAgentAgentReport({ sessionId })
        if (piOperationGateRef.current.isCurrent(operationToken)) {
          setPiAgentReport(report)
          if (report.current) {
            inquiryAgentSession = report.current
            applyPiAgentSessionState(report.current)
          }
        }
      } catch (error) {
        if (piOperationGateRef.current.isCurrent(operationToken)) {
          appendPiReviewLog('error', `Camera Lucida agent register: ${error instanceof Error ? error.message : 'usage refresh failed'}`)
        }
      }
      if (signal.aborted || !piOperationGateRef.current.isCurrent(operationToken)) return
      try {
        const persistedModel = inquiryAgentSession?.model || { provider: piAgentModelProvider, modelId: piAgentModelId }
        const inquiry = await persistCameraLucidaInquiry({
          chapterId,
          bookId: bookId || undefined,
          snapshotId: sidecarSnapshotId,
          question,
          summary: completedSummary,
          findings,
          model: persistedModel ? { provider: persistedModel.provider, modelId: persistedModel.modelId } : undefined,
          agentSessionId: inquiryAgentSession?.agentSessionId || piAgentSessionId || undefined,
          changeSetId: receivedChangeSet?.changeSetId,
          authorityRevisionId: inquirySnapshot.authorityRevisionId,
          thinkingLevel: inquiryAgentSession?.thinkingLevel || piAgentThinkingLevel,
          usage: inquiryAgentSession?.usage,
          createdAt,
          completedAt: new Date().toISOString(),
        })
        if (receivedChangeSet) {
          const record = await persistLedgerSafeAgentChangeSet({
            changeSet: receivedChangeSet,
            snapshot: inquirySnapshot,
            chapterId,
            bookId: bookId || undefined,
            inquiryId: inquiry.id,
          })
          if (!piOperationGateRef.current.isCurrent(operationToken)) return
          const presentation = buildPiMatrixAgentChangeSetPreview(record)
          setPiAgentChangeSet(record)
          setPiPreview(presentation.preview)
          setPiPreviewEnvelope(presentation.envelope)
          setPiPreviewSummary(record.steps.length === 1
            ? record.steps[0].summary
            : presentation.preview.summary || '')
          setPiAuthorityHead(await getLedgerSafeAuthorityHead(chapterId) || null)
          appendPiReviewLog('preview', `${record.steps.length}-edit sequence ready for one approval`)
        }
      } catch (error) {
        if (signal.aborted || !piOperationGateRef.current.isCurrent(operationToken)) return
        const message = error instanceof Error ? error.message : 'Camera Lucida report could not be saved'
        appendPiReviewLog('error', `Camera Lucida persistence: ${message}`)
        showToast('Inquiry completed, but its report could not be saved')
      }
      if (signal.aborted || !piOperationGateRef.current.isCurrent(operationToken)) return
      appendPiReviewLog('agent', 'Camera Lucida inquiry completed')
    } catch (error) {
      if (signal.aborted || !piOperationGateRef.current.isCurrent(operationToken)) return
      const message = error instanceof Error ? error.message : 'Camera Lucida inquiry failed'
      setPiError(message)
      appendPiReviewLog('error', message)
      throw error
    } finally {
      if (activeCameraInquiryRef.current?.operationToken === operationToken) {
        activeCameraInquiryRef.current = null
      }
      if (piOperationGateRef.current.isCurrent(operationToken)) setIsPiBusy(false)
    }
  }, [
    appendPiReviewLog,
    activeScope,
    applyPiAgentSessionState,
    bookId,
    chapterId,
    contextLabel,
    ensurePiSession,
    piAgentModelId,
    piAgentModelProvider,
    piAgentSession,
    piAgentSessionId,
    piAgentThinkingLevel,
    showToast,
  ])

  const handlePiPreviewCurrentEdit = useCallback(async () => {
    const toolToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    try {
      const { sessionId } = await ensurePiSession(toolToken)
      const editSnapshot = latestPiSnapshotRef.current
      let toolName = ''
      let args: Record<string, unknown> = {}
      let readArguments: Record<string, unknown> = {}

      if (inlineEditMode === 'split' && selectedSplitContext && selectedSplitBoundaryWordIndex !== null && activeWitness) {
        toolName = 'split'
        args = { afterWordIndex: selectedSplitBoundaryWordIndex }
        readArguments = { rowIds: [selectedSplitContext.rowId], witnessIds: [activeWitness.id], detail: 'text' }
      } else if (selectedRangeSummary && selectedRangeSummary.mappedCount >= 2) {
        toolName = 'merge'
        readArguments = {
          rowIds: selectedRangeSummary.mappedRowIds,
          witnessIds: [selectedRangeSummary.witnessId],
          detail: 'text',
        }
      } else if (selectedBoundaryContext && preview && activeWitness && preview.deltaWords !== 0) {
        toolName = 'shift_boundary'
        args = { deltaWords: preview.deltaWords }
        readArguments = {
          rowIds: [selectedBoundaryContext.leftRowId, selectedBoundaryContext.rightRowId],
          witnessIds: [activeWitness.id],
          detail: 'text',
        }
      } else {
        throw new Error('No current edit is available for Pi preview')
      }

      await invokePiMatrixAgentTool({
        sessionId,
        toolName: 'read_collation',
        arguments: readArguments,
      })
      const receiptResult = await invokePiMatrixAgentTool({
        sessionId,
        toolName,
        arguments: args
      })
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }
      const changeSetId = isRecord(receiptResult.details) && typeof receiptResult.details.changeSetId === 'string'
        ? receiptResult.details.changeSetId
        : ''
      if (changeSetId) {
        const changeSet = await getPiMatrixAgentChangeSet({ sessionId, changeSetId })
        const record = await persistLedgerSafeAgentChangeSet({
          changeSet,
          snapshot: editSnapshot,
          chapterId,
          bookId: bookId || undefined,
        })
        if (!piOperationGateRef.current.isCurrent(toolToken)) return
        const presentation = buildPiMatrixAgentChangeSetPreview(record)
        setPiAgentChangeSet(record)
        setPiPreview(presentation.preview)
        setPiPreviewEnvelope(presentation.envelope)
        setPiPreviewSummary(record.steps[0]?.summary || presentation.preview.summary || '')
        setPiDriftWindows([])
        setPiFocusedWindow({
          rowStart: Math.min(...record.affectedRowIds.map(rowId => rowById.get(rowId)?.position || Number.MAX_SAFE_INTEGER)),
          rowEnd: Math.max(...record.affectedRowIds.map(rowId => rowById.get(rowId)?.position || 0)),
          rowIds: record.affectedRowIds,
          affectedWitnessIds: record.affectedWitnessIds,
        })
        appendPiReviewLog('preview', `${record.steps.length}-edit sequence ready for one approval`)
        return
      }
      const receipt = parsePiToolPayload(receiptResult)
      const proposalId = String(receipt.proposalId || '')
      if (!proposalId) throw new Error('Pi did not return an approval proposal id')
      const proposal = await getPiMatrixAgentProposal({ sessionId, proposalId })
      const nextPreview = {
        previewId: proposal.proposalId,
        baseSnapshotId: proposal.baseSnapshotId,
        operation: proposal.operation,
        affectedRowIds: proposal.affectedRowIds,
        affectedWitnessIds: proposal.affectedWitnessIds,
        invariants: proposal.invariants,
        summary: proposal.summary,
      }
      const nextEnvelope: PiMatrixAgentPreviewEnvelope = {
        preview: nextPreview,
        before: proposal.before,
        after: proposal.after,
        idRemap: proposal.idRemap,
        unassignedFragments: proposal.unassignedFragments,
      }
      setPiPreview(nextPreview)
      setPiPreviewEnvelope(nextEnvelope)
      setPiPreviewSummary(proposal.summary)
      setPiDriftWindows([])
      setPiFocusedWindow({
        rowStart: Math.min(...nextPreview.affectedRowIds.map(rowId => rowById.get(rowId)?.position || Number.MAX_SAFE_INTEGER)),
        rowEnd: Math.max(...nextPreview.affectedRowIds.map(rowId => rowById.get(rowId)?.position || 0)),
        rowIds: nextPreview.affectedRowIds,
        affectedWitnessIds: nextPreview.affectedWitnessIds
      })
      appendPiReviewLog('preview', proposal.summary)
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Pi preview failed'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        setIsPiBusy(false)
      }
    }
  }, [
    activeWitness,
    bookId,
    chapterId,
    ensurePiSession,
    inlineEditMode,
    parsePiToolPayload,
    piOperationGateRef,
    preview,
    selectedBoundaryContext,
    selectedRangeSummary,
    selectedSplitBoundaryWordIndex,
    selectedSplitContext,
    appendPiReviewLog,
    rowById,
    showToast
  ])

  const reflectCommittedAgentMutation = useCallback(({
    rows,
    witnesses,
    characterLayer,
    record,
    authority,
    editorialEvent,
  }: {
    rows: AnchoredRow[]
    witnesses: Witness[]
    characterLayer?: ChapterCharacterLayer
    record: LedgerSafeAgentChangeSetRecord
    authority: LedgerSafeAuthorityHeadRecord
    editorialEvent: LedgerSafeEditorialEvent
  }) => {
    const rowById = new Map(rows.map(row => [row.id, row]))
    const witnessById = new Map(witnesses.map(witness => [witness.id, witness]))
    if (characterLayer) readerAdjudicationLayerRef.current = characterLayer
    captureMatrixViewportContinuity(rows.map(row => row.id))

    // Project the committed authority pointer and its content in one render.
    // Publishing either side first creates a transient mismatched snapshot;
    // the authority synchronizer can otherwise mistake that intermediate UI
    // state for an external edit and replace the just-committed revision.
    flushSync(() => {
      setPiPreview(null)
      setPiPreviewEnvelope(null)
      setPiPreviewSummary('')
      setPiFocusedWindow(null)
      setInlineEditMode('idle')
      setPendingDeltaWords(0)
      setSelectedSplitBoundaryWordIndex(null)
      setBoundaryDragState(null)
      setPiAgentChangeSet(record)
      setPiAuthorityHead(authority)
      setEditorialEvents(current => [editorialEvent, ...current].slice(0, 12))
      useAnchoredRowsStore.setState(state => {
        const existing = state.chapterRows.get(chapterId) || []
        const existingIds = new Set(existing.map(row => row.id))
        const nextRows = [
          ...existing.map(row => rowById.get(row.id) || row),
          ...rows.filter(row => !existingIds.has(row.id)),
        ].sort((left, right) => left.position - right.position)
        return { chapterRows: new Map(state.chapterRows).set(chapterId, nextRows) }
      })
      useBooksStore.setState(state => ({
        witnesses: state.witnesses.map(witness => witnessById.get(witness.id) || witness),
      }))
      if (characterLayer) setChapterCharacterLayer(characterLayer)
    })
  }, [captureMatrixViewportContinuity, chapterId, setChapterCharacterLayer])

  const agentReviewUiContext = useCallback((): LedgerSafeEditorialEvent['uiContext'] => ({
    scopeWitnessIds: scopeWitnesses.map(witness => witness.id),
    selectedWitnessId: selectedCell?.witnessId || activeWitness?.id || null,
    selectedRowIds: selectedRange?.rowIds || (selectedCell?.rowId ? [selectedCell.rowId] : []),
    diffPivotWitnessId: diffPivotWitness?.id || null,
    piSessionId: piSessionId || null,
  }), [activeWitness?.id, diffPivotWitness?.id, piSessionId, scopeWitnesses, selectedCell?.rowId, selectedCell?.witnessId, selectedRange?.rowIds])

  const handleApplyPiPreview = useCallback(async () => {
    if (isMatrixContentApplying || !piPreview || (!piSessionId && !piAgentChangeSet)) {
      return
    }
    const toolToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    try {
      if (piSnapshotPending) {
        throw new Error('The collation changed while this Pi proposal was open; inspect a fresh proposal')
      }
      if (piAgentChangeSet?.status === 'awaiting_approval') {
        if (piAgentChangeSet.baseAuthorityRevisionId !== piSnapshot.snapshotId) {
          throw new Error('This Pi edit sequence no longer matches the current collation; inspect a fresh sequence')
        }
        const patch = buildLedgerSafeAgentPatch(piAgentChangeSet)
        assertPiMatrixAgentPatchCurrent(patch.baseSnapshotId, latestPiSnapshotRef.current.snapshotId)
        const sessionModel = piAgentSession?.model
        const prepared = prepareLedgerSafeAgentApplication({
          record: piAgentChangeSet,
          patch,
          chapterLabel: `Variorum · ${describeManualStudioScope(activeScope) || chapterId || 'chapter'}`,
          rows: resolvedRows,
          witnesses: scopeWitnesses,
          characterLayer: runtimeEnableCharacterIndex ? readerAdjudicationLayerRef.current : undefined,
          persistCharacterLayer: runtimeEnableCharacterIndex && Boolean(storedCharacterLayer),
          includeCharacterIndex: runtimeEnableCharacterIndex,
          model: {
            provider: sessionModel?.provider || piAgentModelProvider,
            modelId: sessionModel?.modelId || piAgentModelId,
            displayName: sessionModel?.name || sessionModel?.modelId || piAgentModelId,
            thinkingLevel: piAgentSession?.thinkingLevel || piAgentThinkingLevel,
          },
          uiContext: agentReviewUiContext(),
        })
        await commitPreparedLedgerSafeAgentApplication(prepared)
        if (!piOperationGateRef.current.isCurrent(toolToken)) return
        reflectCommittedAgentMutation({
          rows: prepared.rows,
          witnesses: prepared.witnesses,
          characterLayer: prepared.characterLayer,
          record: prepared.mutation.nextChangeSet,
          authority: prepared.mutation.nextAuthority,
          editorialEvent: prepared.mutation.editorialEvent,
        })
        appendPiReviewLog('apply', `Applied ${patch.operations.length}-edit sequence as one transaction`)
        showToast(`${patch.operations.length} Pi edit${patch.operations.length === 1 ? '' : 's'} applied as one sequence`)
        return
      }
      if (!isPiMatrixAgentProposalCurrent({
        proposalBaseSnapshotId: piPreview.baseSnapshotId,
        currentSnapshotId: piSnapshot.snapshotId,
        syncedSnapshotId: piSnapshotId,
      })) {
        await ensurePiSession(toolToken)
        throw new Error('This Pi proposal no longer matches the current collation; inspect a fresh proposal')
      }
      const patch = await applyPiMatrixAgentPreview({
        sessionId: piSessionId,
        previewId: piPreview.previewId
      })
      if (!piOperationGateRef.current.isCurrent(toolToken)) {
        return
      }
      const currentSnapshotId = latestPiSnapshotRef.current.snapshotId
      assertPiMatrixAgentPatchCurrent(patch.baseSnapshotId, currentSnapshotId)
      await applyPiPatchLocally(patch, currentSnapshotId)
      appendPiReviewLog('apply', 'Applied Pi preview')
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Failed to apply Pi preview'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        setIsPiBusy(false)
      }
    }
  }, [activeScope, agentReviewUiContext, appendPiReviewLog, applyPiPatchLocally, chapterId, ensurePiSession, isMatrixContentApplying, piAgentChangeSet, piAgentModelId, piAgentModelProvider, piAgentSession, piAgentThinkingLevel, piPreview, piSessionId, piSnapshot.snapshotId, piSnapshotId, piSnapshotPending, reflectCommittedAgentMutation, resolvedRows, runtimeEnableCharacterIndex, scopeWitnesses, showToast, storedCharacterLayer])

  const handleRejectPiPreview = useCallback(async () => {
    if (isMatrixContentApplying || !piPreview) {
      return
    }
    if (piAgentChangeSet?.status === 'awaiting_approval') {
      const rejectedAt = new Date().toISOString()
      const rejected: LedgerSafeAgentChangeSetRecord = {
        ...piAgentChangeSet,
        status: 'rejected',
        approval: { required: true, status: 'rejected' },
        updatedAt: rejectedAt,
      }
      await dbStorage.put('ledger_safe_agent_change_sets', rejected)
      setPiAgentChangeSet(rejected)
      appendPiReviewLog('reject', `Rejected ${rejected.steps.length}-edit sequence`)
    } else {
      appendPiReviewLog('reject', 'Rejected Pi preview')
    }
    setPiPreview(null)
    setPiPreviewEnvelope(null)
    setPiPreviewSummary('')
    setPiFocusedWindow(null)
  }, [appendPiReviewLog, isMatrixContentApplying, piAgentChangeSet, piPreview])

  const handleUndoPiAgentChangeSet = useCallback(async () => {
    if (isMatrixContentApplying || piAgentChangeSet?.status !== 'applied') return
    const toolToken = piOperationGateRef.current.begin()
    setIsPiBusy(true)
    setPiError(null)
    try {
      const undo = prepareLedgerSafeAgentUndo(piAgentChangeSet, agentReviewUiContext())
      await commitPreparedLedgerSafeAgentUndo(undo)
      if (!piOperationGateRef.current.isCurrent(toolToken)) return
      reflectCommittedAgentMutation({
        rows: undo.rows,
        witnesses: undo.witnesses,
        characterLayer: undo.characterLayer,
        record: undo.mutation.nextChangeSet,
        authority: undo.mutation.nextAuthority,
        editorialEvent: undo.mutation.editorialEvent,
      })
      appendPiReviewLog('apply', `Undid ${piAgentChangeSet.steps.length}-edit sequence`)
      showToast('Pi edit sequence undone')
    } catch (error) {
      if (piOperationGateRef.current.isCurrent(toolToken)) {
        const message = error instanceof Error ? error.message : 'Failed to undo Pi edit sequence'
        setPiError(message)
        appendPiReviewLog('error', message)
        showToast(message)
      }
    } finally {
      if (piOperationGateRef.current.isCurrent(toolToken)) setIsPiBusy(false)
    }
  }, [agentReviewUiContext, appendPiReviewLog, isMatrixContentApplying, piAgentChangeSet, reflectCommittedAgentMutation, showToast])

  const handleFocusPiWindow = useCallback((window: PiDriftWindow, source: 'drift' | 'preview') => {
    if (isMatrixContentApplying) {
      return
    }
    setPiFocusedWindow(window)
    appendPiReviewLog(source === 'preview' ? 'preview' : 'drift', `Focused rows ${window.rowStart}–${window.rowEnd}`)
  }, [appendPiReviewLog, isMatrixContentApplying])

  const witnessHeaderMetricsById = useMemo(() => {
    const metrics = new Map<string, { unitCount: number; occupiedRows: number; occupancyRatio: number }>()
    scopeWitnesses.forEach(witness => {
      metrics.set(witness.id, { unitCount: 0, occupiedRows: 0, occupancyRatio: 0 })
    })
    resolvedRows.forEach(row => {
      const rowWitnessIds = new Set<string>()
      row.unitMappings.forEach(mapping => {
        if (mapping.excluded) {
          return
        }
        const previous = metrics.get(mapping.witnessId) || { unitCount: 0, occupiedRows: 0, occupancyRatio: 0 }
        metrics.set(mapping.witnessId, {
          ...previous,
          unitCount: previous.unitCount + 1
        })
        rowWitnessIds.add(mapping.witnessId)
      })
      rowWitnessIds.forEach(witnessId => {
        const previous = metrics.get(witnessId) || { unitCount: 0, occupiedRows: 0, occupancyRatio: 0 }
        metrics.set(witnessId, {
          ...previous,
          occupiedRows: previous.occupiedRows + 1
        })
      })
    })
    const rowCount = Math.max(resolvedRows.length, 1)
    metrics.forEach((value, witnessId) => {
      metrics.set(witnessId, {
        ...value,
        occupancyRatio: Math.min(1, Math.max(0, value.occupiedRows / rowCount))
      })
    })
    return metrics
  }, [resolvedRows, scopeWitnesses])

  const boundaryEditableCount = editableContextsByRowId.size

  const piPreviewAfterCellByKey = useMemo(() => {
    const map = new Map<string, PiMatrixAgentWindowCell>()
    piPreviewEnvelope?.after?.rows.forEach(row => {
      row.cells.forEach(cell => {
        map.set(`${row.id}:${cell.witnessId}`, cell)
      })
    })
    return map
  }, [piPreviewEnvelope])

  const piPreviewAffectedRowIds = useMemo(
    () => new Set(piPreview?.affectedRowIds || []),
    [piPreview]
  )

  const piFocusedRowIds = useMemo(() => {
    if (piPreview?.affectedRowIds?.length) {
      return new Set(piPreview.affectedRowIds)
    }
    return new Set(piFocusedWindow?.rowIds || [])
  }, [piFocusedWindow, piPreview])

  const isHiddenWitnessRowCollapseSuspended = Boolean(
    ledgerSafeViewMode !== 'ledger'
    || selectedCharacter
    || focusedReviewVisibleRowIds
    || fragmentAnchorSelection
    || cellPlacementMode
    || inlineEditMode !== 'idle'
    || structuralBoundaryPlacement
    || isMatrixContentApplying
  )
  const hiddenWitnessRowProtectedIds = useMemo(() => {
    const rowIds = new Set<string>()
    inlineFragmentsBeforeRowId.forEach((fragments, rowId) => {
      if (fragments.length > 0) rowIds.add(rowId)
    })
    inlineFragmentsAfterRowId.forEach((fragments, rowId) => {
      if (fragments.length > 0) rowIds.add(rowId)
    })
    selectedStructuralRowIds.forEach(rowId => rowIds.add(rowId))
    structuralBoundaryPreviewRowIds.forEach(rowId => rowIds.add(rowId))
    piFocusedRowIds.forEach(rowId => rowIds.add(rowId))
    piPreviewAffectedRowIds.forEach(rowId => rowIds.add(rowId))
    selectedRange?.rowIds.forEach(rowId => rowIds.add(rowId))
    if (selectedCell?.rowId) rowIds.add(selectedCell.rowId)
    if (selectionAnchorCell?.rowId) rowIds.add(selectionAnchorCell.rowId)
    if (markMenuCell?.rowId) rowIds.add(markMenuCell.rowId)
    if (selectedLeftRowId) rowIds.add(selectedLeftRowId)
    if (selectedBoundaryContext) {
      rowIds.add(selectedBoundaryContext.leftRowId)
      rowIds.add(selectedBoundaryContext.rightRowId)
    }
    if (selectedSplitContext?.rowId) rowIds.add(selectedSplitContext.rowId)
    if (apparatusSelectedRowId) rowIds.add(apparatusSelectedRowId)
    if (structuralRangeEditor) {
      rowIds.add(structuralRangeEditor.startRowId)
      rowIds.add(structuralRangeEditor.endRowId)
    }
    if (structuralBoundaryHoverRowId) rowIds.add(structuralBoundaryHoverRowId)
    return rowIds
  }, [
    apparatusSelectedRowId,
    inlineFragmentsAfterRowId,
    inlineFragmentsBeforeRowId,
    markMenuCell?.rowId,
    piFocusedRowIds,
    piPreviewAffectedRowIds,
    selectedBoundaryContext,
    selectedCell?.rowId,
    selectedLeftRowId,
    selectedRange?.rowIds,
    selectedSplitContext?.rowId,
    selectedStructuralRowIds,
    selectionAnchorCell?.rowId,
    structuralBoundaryHoverRowId,
    structuralBoundaryPreviewRowIds,
    structuralRangeEditor,
  ])
  const hiddenWitnessRowRuns = useMemo(
    () => isHiddenWitnessRowCollapseSuspended || hiddenWitnessIds.length === 0
      ? EMPTY_HIDDEN_WITNESS_ROW_RUNS
      : deriveLedgerSafeHiddenWitnessRowRuns({
          rows: resolvedRows,
          visibleWitnessIds: visibleScopeWitnessIds,
          hiddenWitnessIds,
          protectedRowIds: hiddenWitnessRowProtectedIds,
        }),
    [
      hiddenWitnessIds,
      hiddenWitnessRowProtectedIds,
      isHiddenWitnessRowCollapseSuspended,
      resolvedRows,
      visibleScopeWitnessIds,
    ]
  )
  const hiddenWitnessRowRunByHeadRowId = useMemo(() => new Map(
    hiddenWitnessRowRuns
      .filter(run => Boolean(run.rowIds[0]))
      .map(run => [run.rowIds[0], run]),
  ), [hiddenWitnessRowRuns])
  const hiddenWitnessRowRunSupportingLabelsByHeadRowId = useMemo(() => new Map(
    hiddenWitnessRowRuns
      .filter(run => Boolean(run.rowIds[0]))
      .map(run => [
        run.rowIds[0],
        run.supportingWitnessIds.map(witnessId => {
          const supportingWitness = scopeWitnessById.get(witnessId)
          return supportingWitness
            ? witnessLabels?.get(supportingWitness.id)?.siglum || supportingWitness.sigla
            : witnessId
        }),
      ]),
  ), [hiddenWitnessRowRuns, scopeWitnessById, witnessLabels])
  const hiddenWitnessRowRunMemberIds = useMemo(() => new Set(
    hiddenWitnessRowRuns.flatMap(run => run.rowIds.slice(1)),
  ), [hiddenWitnessRowRuns])
  const hiddenWitnessCollapsedRowCount = useMemo(
    () => hiddenWitnessRowRuns.reduce((total, run) => total + run.rowCount, 0),
    [hiddenWitnessRowRuns]
  )
  const filteredHeaderProjection = useMemo(() => {
    const hiddenIds = new Set(hiddenWitnessRowRuns.flatMap(run => run.rowIds))
    const ordered = [...resolvedRows].sort((a, b) => a.position - b.position || a.labelNumber - b.labelNumber || a.id.localeCompare(b.id))
    const indices = new Map(ordered.map((row, index) => [row.id, index]))
    const ranges = Array.from(structuralRangesByStartRowId.values()).flat()
      .sort((a, b) => (indices.get(a.startRowId) ?? 0) - (indices.get(b.startRowId) ?? 0)
        || (indices.get(b.endRowId) ?? 0) - (indices.get(a.endRowId) ?? 0))
    return {
      ranges: projectLedgerHeadersPastHiddenRows(ranges.map(header => {
        const start = indices.get(header.startRowId)
        const end = indices.get(header.endRowId)
        return { header, rowIds: start !== undefined && end !== undefined && end >= start
          ? ordered.slice(start, end + 1).map(row => row.id) : [header.startRowId] }
      }), hiddenIds),
      scenes: projectLedgerHeadersPastHiddenRows(Array.from(ledgerSceneHeaderByRowId).map(([rowId, header], index, entries) => {
        const start = indices.get(rowId) ?? 0
        const end = indices.get(entries[index + 1]?.[0]) ?? ordered.length
        return { header, rowIds: header.rowIds.filter(id => {
          const position = indices.get(id)
          return position !== undefined && position >= start && position < end
        }) }
      }), hiddenIds),
    }
  }, [hiddenWitnessRowRuns, resolvedRows, structuralRangesByStartRowId, ledgerSceneHeaderByRowId])
  const matrixDisplayRows = useMemo(
    () => characterScopedDisplayRows.filter(displayRow => {
      if (displayRow.kind === 'projection') return true
      const rowId = displayRow.row.id
      const emptyRowRun = emptyLedgerRowRunByHeadRowId.get(rowId) || null
      const isHiddenEmptyRunMember = multiEmptyLedgerRunMemberRowIds.has(rowId)
        && emptyRowRun?.rowIds[0] !== rowId
      return !isHiddenEmptyRunMember && !hiddenWitnessRowRunMemberIds.has(rowId)
    }),
    [
      characterScopedDisplayRows,
      emptyLedgerRowRunByHeadRowId,
      hiddenWitnessRowRunMemberIds,
      multiEmptyLedgerRunMemberRowIds,
    ],
  )
  const matrixDisplayRowKeys = useMemo(
    () => matrixDisplayRows.map(displayRow => displayRow.key),
    [matrixDisplayRows],
  )
  const matrixDisplayRowKeyBySourceRowId = useMemo(() => {
    const keys = new Map<string, string>()
    matrixDisplayRows.forEach(displayRow => {
      if (displayRow.kind !== 'projection') keys.set(displayRow.row.id, displayRow.key)
    })
    emptyLedgerRowRuns.forEach(run => {
      const headKey = keys.get(run.rowIds[0])
      if (headKey) run.rowIds.forEach(rowId => keys.set(rowId, headKey))
    })
    hiddenWitnessRowRuns.forEach(run => {
      const headKey = keys.get(run.rowIds[0])
      if (headKey) run.rowIds.forEach(rowId => keys.set(rowId, headKey))
    })
    return keys
  }, [emptyLedgerRowRuns, hiddenWitnessRowRuns, matrixDisplayRows])
  const matrixWindow = useLedgerSafeMatrixWindow({
    rowKeys: matrixDisplayRowKeys,
    viewportRef: matrixViewportRef,
  })
  const matrixWindowDisplayRows = useMemo(
    () => matrixDisplayRows.slice(matrixWindow.startIndex, matrixWindow.endIndex),
    [matrixDisplayRows, matrixWindow.endIndex, matrixWindow.startIndex],
  )
  const scrollMatrixRowIntoView = useEventCallback((
    rowId: string,
    options: ScrollIntoViewOptions = { block: 'center', behavior: 'smooth' },
  ): void => {
    const rowElement = rowElementRefs.current.get(rowId)
    if (rowElement) {
      rowElement.scrollIntoView?.(options)
      return
    }
    const displayRowKey = matrixDisplayRowKeyBySourceRowId.get(rowId)
    if (!displayRowKey || !matrixWindow.scrollToRowKey(displayRowKey)) return
    pendingMatrixRowScrollRef.current = { rowId, options }
  })
  useLayoutEffect(() => {
    const pending = pendingMatrixRowScrollRef.current
    if (!pending) return
    const rowElement = rowElementRefs.current.get(pending.rowId)
    if (!rowElement) return
    rowElement.scrollIntoView?.(pending.options)
    pendingMatrixRowScrollRef.current = null
  }, [matrixWindow.endIndex, matrixWindow.startIndex])

  const piReviewCellKeys = useMemo(() => {
    const keys = new Set<string>()
    piPreviewAfterCellByKey.forEach((_cell, key) => {
      keys.add(key)
    })
    if (piFocusedWindow?.rowIds?.length && piFocusedWindow.affectedWitnessIds?.length) {
      piFocusedWindow.rowIds.forEach(rowId => {
        piFocusedWindow.affectedWitnessIds.forEach(witnessId => {
          keys.add(`${rowId}:${witnessId}`)
        })
      })
    }
    return keys
  }, [piFocusedWindow, piPreviewAfterCellByKey])

  const recentPiEditedCellKeys = useMemo(() => {
    const keys = new Set<string>()
    editorialEvents.forEach(event => {
      if (event.source !== 'pi-preview-apply') {
        return
      }
      event.affectedRowIds.forEach(rowId => {
        event.affectedWitnessIds.forEach(witnessId => {
          keys.add(`${rowId}:${witnessId}`)
        })
      })
    })
    return keys
  }, [editorialEvents])

  const dismissedEditSuggestionIdSet = useMemo(
    () => new Set(dismissedEditSuggestionIds),
    [dismissedEditSuggestionIds]
  )
  const editSuggestionBuilder = useMemo(() => createLedgerSafeEditSuggestionBuilder(), [])
  const editSuggestions = useMemo(
    () => runtimeEnableEditSuggestions && !readOnly
      ? editSuggestionBuilder({
        rows: resolvedRows,
        witnesses: scopeWitnesses,
        editorialEvents,
        dismissedSuggestionIds: dismissedEditSuggestionIdSet,
        maxSuggestions: 36,
        maxSourceEvents: 12
      })
      : [],
    [dismissedEditSuggestionIdSet, editSuggestionBuilder, editorialEvents, readOnly, resolvedRows, runtimeEnableEditSuggestions, scopeWitnesses]
  )
  const editSuggestionByCellKey = useMemo(
    () => indexLedgerSafeEditSuggestionsByCell(editSuggestions),
    [editSuggestions]
  )
  const editSuggestionActionKeySet = useMemo(
    () => new Set(editSuggestions.map(buildLedgerSafeEditSuggestionActionKey)),
    [editSuggestions]
  )
  const editSuggestionActionRowIdSet = useMemo(
    () => new Set(editSuggestions.map(suggestion => suggestion.primaryRowId)),
    [editSuggestions]
  )

  const ledgerOccupancySharedSiglaPrefix = useMemo(
    () => getSharedSiglaPrefix(scopeWitnesses),
    [scopeWitnesses]
  )
  const isOccupancyMapVisible = sidePanelTab === 'map'
  const ledgerVarianceMetricsCacheRef = useRef(new Map<string, {
    cacheKey: string
    metrics: LedgerVarianceMetrics
  }>())
  const ledgerVarianceMetricsByRowId = useMemo(() => {
    if (!isOccupancyMapVisible || occupancyMapLayer !== 'variance') {
      return new Map<string, LedgerVarianceMetrics>()
    }

    const nextMetricsByRowId = new Map<string, LedgerVarianceMetrics>()
    const activeRowIds = new Set<string>()

    resolvedRows.forEach(row => {
      activeRowIds.add(row.id)
      const mappedEntries = row.unitMappings
        .filter(mapping => !mapping.excluded)
        .map(mapping => ({
          witnessId: mapping.witnessId,
          text: getComparisonMappingText(row, mapping.witnessId, witnessUnitTextLookupByWitnessId)
        }))
        .filter(entry => Boolean(entry.text))
      const cacheKey = buildLedgerVarianceCacheKey(mappedEntries)
      const cached = ledgerVarianceMetricsCacheRef.current.get(row.id)
      if (cached?.cacheKey === cacheKey) {
        nextMetricsByRowId.set(row.id, cached.metrics)
        return
      }

      const metrics = getLedgerVarianceMetrics(mappedEntries.map(entry => entry.text))
      ledgerVarianceMetricsCacheRef.current.set(row.id, {
        cacheKey,
        metrics
      })
      nextMetricsByRowId.set(row.id, metrics)
    })

    ledgerVarianceMetricsCacheRef.current.forEach((_value, rowId) => {
      if (!activeRowIds.has(rowId)) {
        ledgerVarianceMetricsCacheRef.current.delete(rowId)
      }
    })

    return nextMetricsByRowId
  }, [isOccupancyMapVisible, occupancyMapLayer, resolvedRows, witnessUnitTextLookupByWitnessId])
  const ledgerOccupancyTextMetrics = useMemo(() => {
    if (!isOccupancyMapVisible) return null
    let globalMaxUnitTextLength = 0
    const witnessTotalUnitTextLengthById = new Map<string, number>()
    scopeWitnesses.forEach(witness => {
      witnessTotalUnitTextLengthById.set(witness.id, 0)
    })
    resolvedRows.forEach(row => {
      scopeWitnesses.forEach(witness => {
        const state = getLedgerSafeCellState(row, witness.id)
        if (state !== 'mapped') {
          return
        }
        const unitTextLength = getLedgerOccupancyUnitTextLength(
          getComparisonMappingText(row, witness.id, witnessUnitTextLookupByWitnessId)
        )
        globalMaxUnitTextLength = Math.max(globalMaxUnitTextLength, unitTextLength)
        witnessTotalUnitTextLengthById.set(
          witness.id,
          (witnessTotalUnitTextLengthById.get(witness.id) || 0) + unitTextLength
        )
      })
    })
    const byRowId = new Map(resolvedRows.map(row => {
      const mappedEntries = row.unitMappings
        .filter(mapping => !mapping.excluded)
        .map(mapping => ({
          witnessId: mapping.witnessId,
          text: getComparisonMappingText(row, mapping.witnessId, witnessUnitTextLookupByWitnessId)
        }))
        .filter(entry => Boolean(entry.text))
      const unitTextLengthByWitnessId = new Map<string, number>()
      let rowMaxUnitTextLength = 0
      mappedEntries.forEach(entry => {
        const unitTextLength = getLedgerOccupancyUnitTextLength(entry.text)
        unitTextLengthByWitnessId.set(entry.witnessId, unitTextLength)
        rowMaxUnitTextLength = Math.max(rowMaxUnitTextLength, unitTextLength)
      })
      return [row.id, { mappedEntries, unitTextLengthByWitnessId, rowMaxUnitTextLength }] as const
    }))
    return { globalMaxUnitTextLength, witnessTotalUnitTextLengthById, byRowId }
  }, [isOccupancyMapVisible, resolvedRows, scopeWitnesses, witnessUnitTextLookupByWitnessId])


  const ledgerOccupancyMapRows = useMemo(
    () => {
      if (!ledgerOccupancyTextMetrics) {
        return []
      }

      const { globalMaxUnitTextLength, witnessTotalUnitTextLengthById } = ledgerOccupancyTextMetrics
      const maxWitnessTotalUnitTextLength = Math.max(
        1,
        ...Array.from(witnessTotalUnitTextLengthById.values())
      )
      const runningUnitTextLengthByWitnessId = new Map<string, number>()

      return resolvedRows
        .filter(row => !hiddenWitnessRowRunMemberIds.has(row.id))
        .map(row => {
        const rowIndex = canonicalRowIndexById.get(row.id) ?? 0
        const shouldComputeVariance = IS_LEDGER_OCCUPANCY_VARIANCE_ENABLED && occupancyMapLayer === 'variance'
        const rowLabel = getRowLabel(row)
        const isFocusedRow = piFocusedRowIds.has(row.id)
        const rowIsStructurallyEmpty = isLedgerSafeRowTrulyEmpty(row)
        const { mappedEntries, unitTextLengthByWitnessId, rowMaxUnitTextLength } = ledgerOccupancyTextMetrics.byRowId.get(row.id)!
        const { rowScore: varianceScore, cellScores, referenceIndex } = shouldComputeVariance
          ? ledgerVarianceMetricsByRowId.get(row.id) || {
              rowScore: 0,
              cellScores: [],
              referenceIndex: -1
            }
          : {
              rowScore: 0,
              cellScores: [],
              referenceIndex: -1
            }
        const varianceScoreByWitnessId = new Map<string, number>()
        const referenceWitnessId = referenceIndex >= 0 ? mappedEntries[referenceIndex]?.witnessId || null : null
        mappedEntries.forEach((entry, index) => {
          varianceScoreByWitnessId.set(entry.witnessId, cellScores[index] || 0)
        })
        const varianceBucket = getLedgerVarianceBucket(varianceScore)
        const varianceLabel = getLedgerVarianceLabel(varianceBucket)
        const varianceColor = getLedgerVarianceColor(varianceScore)
        return {
          rowId: row.id,
          rowIndex,
          rowLabel,
          sceneHeader: runtimeEnableSceneTitles ? ledgerSceneHeaderByRowId.get(row.id) || null : null,
          hiddenWitnessRun: hiddenWitnessRowRunByHeadRowId.get(row.id) || null,
          rowIsStructurallyEmpty,
          varianceScore,
          varianceBucket,
          varianceColor,
          varianceLabel,
          rowMaxUnitTextLength,
          rowHeightPx: getLedgerOccupancyHeightRowPx(rowMaxUnitTextLength),
          rowMarkerLabel: isFocusedRow || rowIndex === 0 || rowIndex === resolvedRows.length - 1 || (rowIndex + 1) % 10 === 0
            ? rowLabel
            : '',
          cells: scopeWitnesses.map(witness => {
            const state = getLedgerSafeCellState(row, witness.id)
            const cellKey = `${row.id}:${witness.id}`
            const displaySigla = witness.sigla.startsWith(ledgerOccupancySharedSiglaPrefix)
              ? witness.sigla.slice(ledgerOccupancySharedSiglaPrefix.length) || witness.sigla
              : witness.sigla
            const isPreview = piPreviewAfterCellByKey.has(cellKey)
            const isCurrentPiReview = piReviewCellKeys.has(cellKey)
            const hasRecentPiEdit = recentPiEditedCellKeys.has(cellKey)
            const triageState = isCurrentPiReview
              ? 'review'
              : hasRecentPiEdit
                ? 'recent'
                : 'none'
            const cellVarianceScore = varianceScoreByWitnessId.get(witness.id) || 0
            const cellVarianceBucket = getLedgerVarianceBucket(cellVarianceScore)
            const unitTextLength = unitTextLengthByWitnessId.get(witness.id) || 0
            const previousCumulativeUnitTextLength = runningUnitTextLengthByWitnessId.get(witness.id) || 0
            const cumulativeUnitTextLength = previousCumulativeUnitTextLength + (state === 'mapped' ? unitTextLength : 0)
            runningUnitTextLengthByWitnessId.set(witness.id, cumulativeUnitTextLength)
            const unitGlobalHeightRatio = getLedgerOccupancyGlobalLengthRatio(unitTextLength, globalMaxUnitTextLength)
            const witnessTotalUnitTextLength = witnessTotalUnitTextLengthById.get(witness.id) || 0
            return {
              rowId: row.id,
              rowLabel,
              witnessId: witness.id,
              witnessSigla: witness.sigla,
              witnessDisplaySigla: displaySigla,
              state,
              displayStateLabel: getLedgerOccupancyStateLabel({
                cellState: state,
                rowIsStructurallyEmpty
              }),
              isPreview,
              isFocusedRow: piFocusedRowIds.has(row.id),
              varianceScore: cellVarianceScore,
              varianceBucket: cellVarianceBucket,
              varianceColor: getLedgerVarianceColor(cellVarianceScore),
              varianceLabel: getLedgerVarianceLabel(cellVarianceBucket),
              isVarianceReference: referenceWitnessId === witness.id && state === 'mapped',
              unitTextLength,
              unitHeightRatio: getLedgerOccupancyHeightRatio(unitTextLength, rowMaxUnitTextLength),
              unitGlobalHeightRatio,
              unitLengthTone: getLedgerOccupancyLengthTone(unitGlobalHeightRatio),
              cumulativeUnitTextLength,
              cumulativeUnitTextRatio: Math.max(0, Math.min(1, cumulativeUnitTextLength / maxWitnessTotalUnitTextLength)),
              witnessTotalUnitTextLength,
              witnessTotalUnitTextRatio: Math.max(0, Math.min(1, witnessTotalUnitTextLength / maxWitnessTotalUnitTextLength)),
              triageState
            }
          })
        }
        })
    },
    [ledgerOccupancyTextMetrics, canonicalRowIndexById, hiddenWitnessRowRunByHeadRowId, hiddenWitnessRowRunMemberIds, ledgerOccupancySharedSiglaPrefix, ledgerSceneHeaderByRowId, ledgerVarianceMetricsByRowId, occupancyMapLayer, piFocusedRowIds, piPreviewAfterCellByKey, piReviewCellKeys, recentPiEditedCellKeys, resolvedRows, runtimeEnableSceneTitles, scopeWitnesses]
  )

  useEffect(() => {
    const targetRowId = piPreview?.affectedRowIds?.[0] || piFocusedWindow?.rowIds?.[0]
    if (!targetRowId) {
      return
    }
    scrollMatrixRowIntoView(targetRowId, {
      block: 'center',
      behavior: 'smooth'
    })
  }, [piFocusedWindow, piPreview, scrollMatrixRowIntoView])

  const handleEditBoundaryBelow = useEventCallback((rowId: string) => {
    setSelectedLeftRowId(rowId)
    setInlineEditMode('boundary')
    setSelectedSplitBoundaryWordIndex(null)
    setPendingDeltaWords(0)
    setBoundaryDragState(null)
    setSelectedRange(null)
    if (activeWitness) {
      const nextSelection = { rowId, witnessId: activeWitness.id }
      setSelectedCell(nextSelection)
      setSelectionAnchorCell(nextSelection)
    }
  })

  const handleApplyManualTransposition = useEventCallback(async (targetRowId: string) => {
    if (isMatrixContentApplying || !transpositionTarget) {
      return
    }
    const witness = interactiveScopeWitnesses.find(candidate => candidate.id === transpositionTarget.witnessId)
    const originRow = rowById.get(transpositionTarget.originRowId) || null
    const targetRow = rowById.get(targetRowId) || null
    if (!witness || !originRow || !targetRow) {
      setTranspositionTarget(null)
      showToast('Transposition target is no longer available')
      return
    }
    if (targetRow.id === originRow.id) {
      showToast('Choose a different row for the transposition target')
      return
    }
    if (!transpositionEligibleTargetRowIds.has(targetRow.id)) {
      showToast(`${getRowLabel(targetRow)} already holds a mapped ${displaySigla(witness)} unit — choose an open row or press Esc`)
      return
    }
    // Leave target mode before the async apply so a second click cannot queue a
    // duplicate mark against the same row snapshot.
    setTranspositionTarget(null)
    setMarkMenuCell(null)
    await applyLedgerRowOperation(() => applyLedgerSafeMarkTransposition({
      rows: resolvedRows,
      witnessId: witness.id,
      originRowId: originRow.id,
      targetRowId: targetRow.id
    }), `Marked ${displaySigla(witness)} unit as transposed into ${getRowLabel(targetRow)}`, {
      kind: 'mark_transposition',
      params: {
        witnessId: witness.id,
        originRowId: originRow.id,
        targetRowId: targetRow.id,
        unitUuid: originRow.unitMappings.find(mapping => mapping.witnessId === witness.id && !mapping.excluded)?.unitUuid,
      },
      affectedWitnessIds: [witness.id],
      metadata: {
        affectedRowIds: [originRow.id, targetRow.id],
        originRowLabel: getRowLabel(originRow),
        targetRowLabel: getRowLabel(targetRow),
      },
    })
  })

  const handleApplyManualRelocation = useEventCallback(async ({
    targetStartRowId,
    clickedWitnessId,
  }: {
    targetStartRowId: string
    clickedWitnessId: string
  }) => {
    if (isMatrixContentApplying || !relocationTarget) {
      return
    }
    const witness = interactiveScopeWitnesses.find(
      candidate => candidate.id === relocationTarget.witnessId
    )
    if (!witness) {
      setRelocationTarget(null)
      showToast('Relocation witness is no longer available')
      return
    }
    if (clickedWitnessId !== relocationTarget.witnessId) {
      showToast(`Choose a destination in the ${displaySigla(witness)} column or press Esc`)
      return
    }
    const destination = relocationDestinationByStartRowId.get(targetStartRowId) || null
    if (!destination) {
      showToast('Relocation destination is no longer available')
      return
    }
    if (destination.sameLocation) {
      setRelocationTarget(null)
      return
    }
    if (!destination.eligible) {
      showToast(destination.reason || 'The selected relocation destination is blocked')
      return
    }

    const sourceRows = relocationTarget.sourceRowIds
      .map(rowId => rowById.get(rowId))
      .filter((row): row is AnchoredRow => Boolean(row))
    const targetRows = destination.targetRowIds
      .map(rowId => rowById.get(rowId))
      .filter((row): row is AnchoredRow => Boolean(row))
    if (
      sourceRows.length !== relocationTarget.sourceRowIds.length
      || targetRows.length !== destination.targetRowIds.length
    ) {
      setRelocationTarget(null)
      showToast('The Ledger changed after relocation mode began')
      return
    }

    const sourceRowIds = [...relocationTarget.sourceRowIds]
    const targetRowIds = [...destination.targetRowIds]
    const expectedUnitUuids = [...relocationTarget.unitUuids]
    const sourceLabel = sourceRows.length === 1
      ? getRowLabel(sourceRows[0])
      : `${getRowLabel(sourceRows[0])}–${getRowLabel(sourceRows[sourceRows.length - 1])}`
    const targetLabel = targetRows.length === 1
      ? getRowLabel(targetRows[0])
      : `${getRowLabel(targetRows[0])}–${getRowLabel(targetRows[targetRows.length - 1])}`
    const successMessage = `Relocated ${displaySigla(witness)} ${sourceRows.length === 1 ? 'unit' : `${sourceRows.length} units`} from ${sourceLabel} to ${targetLabel}`

    setRelocationTarget(null)
    const result = await applyLedgerRowOperation(() => applyLedgerSafeRelocateRange({
      rows: resolvedRows,
      witnessId: witness.id,
      sourceRowIds,
      targetStartRowId,
      expectedUnitUuids,
    }), successMessage, {
      kind: 'relocate_cells',
      params: {
        witnessId: witness.id,
        sourceRowIds,
        targetRowIds,
        unitUuids: expectedUnitUuids,
      },
      affectedWitnessIds: [witness.id],
      metadata: {
        sourceRowLabel: sourceLabel,
        targetRowLabel: targetLabel,
      },
    })
    if (!result) {
      return
    }
    const changedRowIdSet = new Set(result.changedRowIds)
    setRelocationUndo({
      id: `relocation:${Date.now()}`,
      witnessId: witness.id,
      witnessSigla: displaySigla(witness),
      sourceRowIds,
      targetRowIds,
      changedRowIds: [...result.changedRowIds],
      beforeRows: cloneRowsForSelectedRepairUndo(
        resolvedRows.filter(row => changedRowIdSet.has(row.id))
      ),
      afterRows: cloneRowsForSelectedRepairUndo(
        result.updatedRows.filter(row => changedRowIdSet.has(row.id))
      ),
      createdAt: new Date().toISOString(),
    })
  })

  const handleUndoRelocation = useEventCallback(async () => {
    if (isMatrixContentApplying || !chapterId || !relocationUndo) {
      return
    }
    const currentRowById = new Map(resolvedRows.map(row => [row.id, row]))
    const beforeRowById = new Map(relocationUndo.beforeRows.map(row => [row.id, row]))
    const afterRowById = new Map(relocationUndo.afterRows.map(row => [row.id, row]))
    const staleRowIds = relocationUndo.changedRowIds.filter(rowId => (
      serializeSelectedRepairWitnessMappings(
        currentRowById.get(rowId),
        relocationUndo.witnessId
      ) !== serializeSelectedRepairWitnessMappings(
        afterRowById.get(rowId),
        relocationUndo.witnessId
      )
    ))
    if (staleRowIds.length > 0) {
      showToast(
        `Cannot undo relocation because ${staleRowIds.length} affected row${staleRowIds.length === 1 ? '' : 's'} changed afterward`
      )
      setRelocationUndo(null)
      return
    }

    const timestamp = new Date().toISOString()
    const rowsToRestore = relocationUndo.changedRowIds
      .map(rowId => {
        const currentRow = currentRowById.get(rowId)
        const beforeRow = beforeRowById.get(rowId)
        if (!currentRow || !beforeRow) {
          return null
        }
        return restoreSelectedRepairWitnessMappings({
          currentRow,
          beforeRow,
          expectedAfterRow: afterRowById.get(rowId),
          witnessId: relocationUndo.witnessId,
          timestamp,
        })
      })
      .filter((row): row is AnchoredRow => Boolean(row))
    if (rowsToRestore.length !== relocationUndo.changedRowIds.length) {
      showToast('Cannot undo relocation because an affected row is no longer available')
      setRelocationUndo(null)
      return
    }

    const restoredRowById = new Map(rowsToRestore.map(row => [row.id, row]))
    const afterRows = resolvedRows.map(row => restoredRowById.get(row.id) || row)
    applyingLedgerMatrixRowIdsRef.current = new Set(relocationUndo.changedRowIds)
    setIsLedgerApplying(true)
    try {
      await upsertRows(chapterId, rowsToRestore, { autoRelabel: false })
      await persistEditorialEvent({
        kind: 'undo_relocate_cells',
        params: {
          undoId: relocationUndo.id,
          witnessId: relocationUndo.witnessId,
          sourceRowIds: relocationUndo.sourceRowIds,
          targetRowIds: relocationUndo.targetRowIds,
        },
        summary: `Undid relocation for ${relocationUndo.witnessSigla}`,
        affectedWitnessIds: [relocationUndo.witnessId],
        affectedRowIds: relocationUndo.changedRowIds,
        beforeRows: resolvedRows,
        afterRows,
        metadata: {
          originalRelocationCreatedAt: relocationUndo.createdAt,
        },
      })
      const sourceSelection = {
        rowId: relocationUndo.sourceRowIds[0],
        witnessId: relocationUndo.witnessId,
      }
      setSelectedCell(sourceSelection)
      setSelectionAnchorCell(sourceSelection)
      setSelectedRange(relocationUndo.sourceRowIds.length > 1
        ? {
            witnessId: relocationUndo.witnessId,
            rowIds: relocationUndo.sourceRowIds,
          }
        : null
      )
      showToast(`Undid relocation for ${relocationUndo.witnessSigla}`)
      setRelocationUndo(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to undo relocation')
    } finally {
      applyingLedgerMatrixRowIdsRef.current = new Set()
      setIsLedgerApplying(false)
    }
  })

  const handleRelocationCellHover = useEventCallback(({
    rowId,
    witnessId,
    hovered,
  }: {
    rowId: string
    witnessId: string
    hovered: boolean
  }) => {
    if (!relocationTarget || witnessId !== relocationTarget.witnessId) {
      return
    }
    setRelocationHoverTargetRowId(previous => (
      hovered ? rowId : (previous === rowId ? null : previous)
    ))
  })

  const handleDetachSequenceRows = useEventCallback(async ({
    rowIds,
    summary,
    reason
  }: {
    rowIds: string[]
    summary: string
    reason: string
  }) => {
    if (isMatrixContentApplying || !activeWitness || rowIds.length === 0) {
      return
    }
    await applyLedgerRowOperation(() => applyLedgerSafeDetachMappings({
      rows: resolvedRows,
      witnessId: activeWitness.id,
      rowIds
    }), summary, {
      kind: 'detach_mapping',
      params: {
        witnessId: activeWitness.id,
        rowIds,
        reason
      },
      affectedWitnessIds: [activeWitness.id],
      metadata: {
        affectedRowIds: rowIds
      }
    })
  })

  const handleDetachSequenceOffender = useEventCallback(async (offender: LedgerSafeSequenceOffender) => {
    if (!activeWitness) {
      return
    }
    const row = rowById.get(offender.rowId) || null
    await handleDetachSequenceRows({
      rowIds: [offender.rowId],
      summary: `Detached ${displaySigla(activeWitness)} unit from ${row ? getRowLabel(row) : 'row'} into the fragment tray`,
      reason: offender.kind
    })
  })

  const handleDetachAllSequenceOffenders = useEventCallback(async () => {
    if (!activeWitness || !activeWitnessSequenceAudit) {
      return
    }
    const rowIds = activeWitnessSequenceAudit.offenders.map(offender => offender.rowId)
    await handleDetachSequenceRows({
      rowIds,
      summary: `Detached ${rowIds.length} out-of-order ${displaySigla(activeWitness)} unit${rowIds.length === 1 ? '' : 's'} into the fragment tray`,
      reason: 'sequence-repair'
    })
  })

  const handleMatrixCellClick = useEventCallback(({
    rowId,
    witnessId,
    witnessSigla,
    shiftKey
  }: {
    rowId: string
    witnessId: string
    witnessSigla: string
    shiftKey: boolean
  }) => {
    if (
      isMatrixContentApplying
      && (
        relocationTarget
        || transpositionTarget
        || (diffEnabled && diffMode === 'apparatus' && apparatusScope === 'row')
      )
    ) {
      return
    }
    if (relocationTarget) {
      void handleApplyManualRelocation({
        targetStartRowId: rowId,
        clickedWitnessId: witnessId,
      })
      return
    }
    if (transpositionTarget) {
      void handleApplyManualTransposition(rowId)
      return
    }

    const selectionStartedAt = readLedgerSafeInteractionNow()
    pendingSelectionTimingRef.current = {
      rowId,
      witnessId,
      startedAt: selectionStartedAt,
    }

    if (
      shiftKey &&
      selectionAnchorCell &&
      selectionAnchorCell.witnessId === witnessId &&
      (
        activeWitness?.id === witnessId
        || activeWitnessIntentSiglaRef.current === witnessSigla
      )
    ) {
      const anchorIndex = resolvedRows.findIndex(candidate => candidate.id === selectionAnchorCell.rowId)
      const targetIndex = resolvedRows.findIndex(candidate => candidate.id === rowId)
      if (anchorIndex >= 0 && targetIndex >= 0) {
        const [startIndex, endIndex] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
        const rangeRows = resolvedRows.slice(startIndex, endIndex + 1)
        setSelectedRange({ witnessId, rowIds: rangeRows.map(candidate => candidate.id) })
        setSelectedCell({ rowId, witnessId })
        setSelectedLeftRowId('')
        setInlineEditMode('idle')
        setSelectedSplitBoundaryWordIndex(null)
        setPendingDeltaWords(0)
        setBoundaryDragState(null)
        return
      }
    }

    setSelectedRange(null)
    setMarkMenuCell(null)
    setSelectedCell({ rowId, witnessId })
    setSelectionAnchorCell({ rowId, witnessId })
    setInlineEditMode('idle')
    setSelectedSplitBoundaryWordIndex(null)
    setPendingDeltaWords(0)
    setBoundaryDragState(null)
    const activeWitnessIntentChanged = activeWitnessIntentSiglaRef.current !== witnessSigla
    activeWitnessIntentSiglaRef.current = witnessSigla
    if (activeWitnessIntentChanged || witnessSigla !== activeWitnessSigla) {
      pendingActiveWitnessTimingRef.current = {
        witnessId,
        witnessSigla,
        startedAt: selectionStartedAt,
      }
      setSelectedLeftRowId('')
      startTransition(() => {
        setActiveWitnessSigla(witnessSigla)
      })
    }
  })

  const handleFocusLedgerOccupancyCell = useEventCallback(({
    rowId,
    witnessId,
    witnessSigla
  }: {
    rowId: string
    witnessId: string
    witnessSigla: string
  }) => {
    handleMatrixCellClick({
      rowId,
      witnessId,
      witnessSigla,
      shiftKey: false
    })
    scrollMatrixRowIntoView(rowId, {
      block: 'center',
      behavior: occupancyMapLayer === 'structural' ? 'smooth' : 'auto'
    })
  })

  const handleInlineModeSelect = useEventCallback((mode: InlineEditMode) => {
    setInlineEditMode(mode)
    if (mode === 'boundary') {
      setSelectedSplitBoundaryWordIndex(null)
      setPendingDeltaWords(0)
      setBoundaryDragState(null)
    } else if (mode === 'split') {
      setBoundaryDragState(null)
      setPendingDeltaWords(0)
    } else {
      setSelectedSplitBoundaryWordIndex(null)
      setBoundaryDragState(null)
      setPendingDeltaWords(0)
    }
  })

  const handleLedgerRibbonAction = useEventCallback(async (action: LedgerSafeInlineRibbonAction) => {
    if (action === 'undo-relocation') {
      await handleUndoRelocation()
      return
    }

    if (action === 'relocate') {
      if (!selectedLedgerWitness) {
        return
      }
      const sourceRows = selectedRange && selectedRangeRows.length > 0
        ? [...selectedRangeRows]
        : (selectedLedgerRow ? [selectedLedgerRow] : [])
      sourceRows.sort((left, right) => {
        if (left.position !== right.position) {
          return left.position - right.position
        }
        return left.labelNumber - right.labelNumber
      })
      if (sourceRows.length === 0) {
        return
      }
      const mappings = sourceRows.map(row => row.unitMappings.find(mapping => (
        mapping.witnessId === selectedLedgerWitness.id && !mapping.excluded
      )))
      if (mappings.some(mapping => !mapping)) {
        showToast('Relocation requires every selected cell to contain a mapped unit')
        return
      }
      setMarkMenuCell(null)
      setInlineEditMode('idle')
      setRelocationTarget({
        witnessId: selectedLedgerWitness.id,
        sourceRowIds: sourceRows.map(row => row.id),
        unitUuids: mappings.map(mapping => mapping!.unitUuid),
      })
      return
    }

    if (action === 'merge') {
      await handleApplyMergeRange()
      return
    }

    if (action === 'shift-range-up' || action === 'shift-range-down') {
      if (!selectedRangeSummary || !selectedLedgerWitness || selectedRangeRows.length === 0) {
        return
      }
      if (!selectedRangeSummary.allMapped) {
        showToast('Range shifts require every selected row to be mapped in this witness')
        return
      }
      const direction = action === 'shift-range-up' ? 'up' : 'down'
      await applyLedgerRowOperation(() => applyLedgerSafeShiftRange({
        rows: resolvedRows,
        witnessId: selectedRangeSummary.witnessId,
        rowIds: selectedRangeRows.map(row => row.id),
        direction
      }), `Shifted ${selectedLedgerWitness.sigla} range ${direction} one row`, {
        kind: 'shift_range',
        params: {
          witnessId: selectedRangeSummary.witnessId,
          rowIds: selectedRangeRows.map(row => row.id),
          direction
        },
        affectedWitnessIds: [selectedRangeSummary.witnessId]
      })
      return
    }

    if (!selectedLedgerRow || !selectedLedgerWitness || !selectedLedgerCellState) {
      return
    }

    if (action === 'mark-menu') {
      setMarkMenuCell(previous => (
        previous?.rowId === selectedLedgerRow.id && previous?.witnessId === selectedLedgerWitness.id
          ? null
          : { rowId: selectedLedgerRow.id, witnessId: selectedLedgerWitness.id }
      ))
      return
    }

    if (action === 'mark-transposition') {
      if (selectedLedgerCellState !== 'mapped') {
        showToast('Select a mapped unit before marking a transposition')
        return
      }
      setMarkMenuCell(null)
      setTranspositionTarget({
        originRowId: selectedLedgerRow.id,
        witnessId: selectedLedgerWitness.id
      })
      return
    }

    if (action === 'unmark-transposition') {
      setMarkMenuCell(null)
      if (selectedLedgerCellState !== 'mapped' || selectedLedgerActiveMapping?.translationStatus !== 'transposed') {
        showToast('Select a transposed unit before clearing the mark')
        return
      }
      await applyLedgerRowOperation(() => applyLedgerSafeUnmarkTransposition({
        rows: resolvedRows,
        witnessId: selectedLedgerWitness.id,
        rowId: selectedLedgerRow.id
      }), `Removed transposition from ${selectedLedgerWitness.sigla} ${getRowLabel(selectedLedgerRow)} and returned the unit to fragments`, {
        kind: 'unmark_transposition',
        params: {
          witnessId: selectedLedgerWitness.id,
          rowId: selectedLedgerRow.id,
          unitUuid: selectedLedgerActiveMapping.unitUuid,
          detachedToFragments: true,
        },
        affectedWitnessIds: [selectedLedgerWitness.id],
        metadata: {
          affectedRowIds: [selectedLedgerRow.id],
          targetRowLabel: getRowLabel(selectedLedgerRow)
        }
      })
      return
    }

    if (action === 'split') {
      if (!selectedSplitContext) {
        showToast(selectedEditabilityBlockReason || 'The selected unit cannot be split')
        return
      }
      handleInlineModeSelect('split')
      return
    }

    if (action === 'boundary') {
      const preferred = selectedBoundaryOptions.find(option => option.id.startsWith('below:')) || selectedBoundaryOptions[0]
      if (!preferred) {
        showToast(selectedEditabilityBlockReason || 'The selected unit has no editable adjacent boundary')
        return
      }
      setSelectedLeftRowId(preferred.context.leftRowId)
      handleInlineModeSelect('boundary')
      return
    }

    if (action === 'move-up' || action === 'move-down') {
      if (selectedLedgerCellState !== 'mapped') {
        return
      }
      const direction = action === 'move-up' ? 'up' : 'down'
      await applyLedgerRowOperation(() => applyLedgerSafeMoveMapping({
        rows: resolvedRows,
        witnessId: selectedLedgerWitness.id,
        rowId: selectedLedgerRow.id,
        direction
      }), `Moved ${selectedLedgerWitness.sigla} unit ${direction} one row`, {
        kind: 'move_mapping',
        params: {
          witnessId: selectedLedgerWitness.id,
          rowId: selectedLedgerRow.id,
          direction
        },
        affectedWitnessIds: [selectedLedgerWitness.id]
      })
      return
    }

    if (action === 'insert-above' || action === 'insert-below') {
      if (selectedLedgerCellState !== 'mapped') {
        return
      }
      const placement = action === 'insert-above' ? 'above' : 'below'
      await applyLedgerRowOperation(() => applyLedgerSafeInsertRowAndMove({
        rows: resolvedRows,
        witnessId: selectedLedgerWitness.id,
        rowId: selectedLedgerRow.id,
        placement
      }), `Inserted a row ${placement} ${getRowLabel(selectedLedgerRow)}`, {
        kind: 'insert_row_and_move',
        params: {
          witnessId: selectedLedgerWitness.id,
          rowId: selectedLedgerRow.id,
          placement
        },
        affectedWitnessIds: [selectedLedgerWitness.id]
      })
      return
    }

    if (action === 'mark-gap') {
      setMarkMenuCell(null)
      if (selectedLedgerCellState === 'mapped') {
        showToast('Move the mapped unit before marking this row as a gap')
        return
      }
      if (selectedLedgerCellState === 'excluded') {
        showToast('Selected row is already marked as a deliberate gap')
        return
      }
      await applyLedgerRowOperation(() => applyLedgerSafeMarkGap({
        rows: resolvedRows,
        witnessId: selectedLedgerWitness.id,
        rowId: selectedLedgerRow.id
      }), `Marked ${selectedLedgerWitness.sigla} gap on ${getRowLabel(selectedLedgerRow)}`, {
        kind: 'mark_gap',
        params: {
          witnessId: selectedLedgerWitness.id,
          rowId: selectedLedgerRow.id
        },
        affectedWitnessIds: [selectedLedgerWitness.id]
      })
      return
    }

    if (action === 'clear-gap') {
      if (selectedLedgerCellState !== 'excluded') {
        return
      }
      await applyLedgerRowOperation(() => applyLedgerSafeClearGap({
        rows: resolvedRows,
        witnessId: selectedLedgerWitness.id,
        rowId: selectedLedgerRow.id
      }), `Cleared deliberate gap on ${getRowLabel(selectedLedgerRow)}`, {
        kind: 'clear_gap',
        params: {
          witnessId: selectedLedgerWitness.id,
          rowId: selectedLedgerRow.id
        },
        affectedWitnessIds: [selectedLedgerWitness.id]
      })
    }
  })

  const relocationUndoMatchesSelection = useMemo(() => {
    if (!relocationUndo || !selectedCell || selectedCell.witnessId !== relocationUndo.witnessId) {
      return false
    }
    if (selectedRange) {
      return (
        selectedRange.witnessId === relocationUndo.witnessId
        && selectedRange.rowIds.length === relocationUndo.targetRowIds.length
        && selectedRange.rowIds.every((rowId, index) => rowId === relocationUndo.targetRowIds[index])
      )
    }
    return (
      relocationUndo.targetRowIds.length === 1
      && selectedCell.rowId === relocationUndo.targetRowIds[0]
    )
  }, [relocationUndo, selectedCell, selectedRange])

  const inlineRibbonByCellKey = useMemo(() => {
    const ribbons = new Map<string, LedgerSafeInlineRibbonModel>()
    if (inlineEditMode !== 'idle' || cellPlacementMode) {
      return ribbons
    }

    if (selectedRange && selectedRange.rowIds.length > 0 && selectedRangeSummary) {
      const anchorRowId = selectedRange.rowIds[selectedRange.rowIds.length - 1]
      const key = `${anchorRowId}:${selectedRange.witnessId}`
      if (selectedRangeSummary.mappedCount >= 2) {
        ribbons.set(key, {
          label: `Range · ${selectedRangeSummary.mappedCount}`,
          buttons: [
            {
              action: 'merge',
              label: `Merge ${selectedRangeSummary.mappedCount}`,
              glyph: '⊟',
              title: 'Merge selected units · M',
              testId: 'ledger-merge-trigger',
              primary: true
            },
            {
              action: 'relocate',
              label: 'Relocate',
              glyph: '↗',
              title: selectedRangeSummary.allMapped ? 'Relocate selected range · R' : 'Relocation requires every selected row to be mapped',
              testId: 'ledger-relocate-trigger',
              disabled: !selectedRangeSummary.allMapped
            },
            ...(relocationUndoMatchesSelection ? [{
              action: 'undo-relocation' as const,
              label: 'Undo',
              glyph: '↶',
              title: 'Undo last relocation',
              testId: 'ledger-relocation-undo-trigger',
            }] : [])
          ]
        })
      } else {
        ribbons.set(key, {
          label: 'Range',
          buttons: [{
            action: 'merge',
            label: 'Needs 2 mapped',
            title: 'Range operations need at least two mapped units',
            testId: 'ledger-merge-trigger',
            disabled: true
          }]
        })
      }
      return ribbons
    }

    if (!selectedCell || !selectedLedgerCellState) {
      return ribbons
    }

    const key = `${selectedCell.rowId}:${selectedCell.witnessId}`
    const markMenuOpen = Boolean(
      markMenuCell?.rowId === selectedCell.rowId &&
      markMenuCell?.witnessId === selectedCell.witnessId
    )
    if (selectedLedgerCellState === 'mapped') {
      ribbons.set(key, {
        label: 'Single',
        markMenuOpen,
        canUnmarkTransposition: selectedLedgerActiveMapping?.translationStatus === 'transposed',
        buttons: [
          {
            action: 'relocate',
            label: 'Relocate',
            glyph: '↗',
            title: 'Relocate unit · R',
            testId: 'ledger-relocate-trigger'
          },
          ...(relocationUndoMatchesSelection ? [{
            action: 'undo-relocation' as const,
            label: 'Undo',
            glyph: '↶',
            title: 'Undo last relocation',
            testId: 'ledger-relocation-undo-trigger',
          }] : []),
          {
            action: 'insert-above',
            label: 'Ins↑',
            glyph: '⤒',
            title: 'Insert row above · A',
            testId: 'ledger-insert-above-trigger'
          },
          {
            action: 'insert-below',
            label: 'Ins↓',
            glyph: '⤓',
            title: 'Insert row below · B',
            testId: 'ledger-insert-below-trigger'
          },
          {
            action: 'mark-menu',
            label: 'Mark',
            glyph: '◇',
            title: 'Mark this unit or cell',
            testId: 'ledger-mark-gap-trigger',
          },
          {
            action: 'split',
            label: 'Split',
            glyph: '⊡',
            title: selectedSplitContext ? 'Enter split mode · S' : (selectedEditabilityBlockReason || 'This unit cannot be split'),
            ariaLabel: 'Split unit',
            testId: `ledger-split-trigger-${selectedCell.rowId}-${selectedCell.witnessId}`,
            disabled: !selectedSplitContext
          },
          {
            action: 'boundary',
            label: 'Bndry',
            glyph: '▸',
            title: selectedBoundaryOptions.length > 0 ? 'Edit boundary · E' : (selectedEditabilityBlockReason || 'No editable boundary is available'),
            ariaLabel: 'Edit boundary',
            testId: `ledger-boundary-trigger-${selectedCell.rowId}-${selectedCell.witnessId}`,
            disabled: selectedBoundaryOptions.length === 0
          }
        ]
      })
      return ribbons
    }

    if (selectedLedgerCellState === 'excluded') {
      ribbons.set(key, {
        label: 'Excluded',
        buttons: [{
          action: 'clear-gap',
          label: 'Clear',
          glyph: '▣',
          title: 'Clear deliberate gap · G',
          testId: 'ledger-clear-gap-trigger',
          primary: true
        }]
      })
      return ribbons
    }

    ribbons.set(key, {
      label: 'Gap',
      markMenuOpen,
      buttons: [{
        action: 'mark-menu',
        label: 'Mark',
        glyph: '▢',
        title: 'Mark this empty cell',
        testId: 'ledger-mark-gap-trigger',
        primary: true
      }]
    })
    return ribbons
  }, [
    cellPlacementMode,
    inlineEditMode,
    markMenuCell,
    selectedBoundaryOptions,
    selectedCell,
    selectedEditabilityBlockReason,
    selectedLedgerActiveMapping,
    selectedLedgerCellState,
    relocationUndoMatchesSelection,
    selectedRange,
    selectedRangeSummary,
    selectedSplitContext
  ])

  const canonicalDisplayRowLocationById = useMemo(() => {
    const locations = new Map<string, {
      displayRow: LedgerSafeTranspositionDisplayRow
      index: number
    }>()
    characterScopedDisplayRows.forEach((displayRow, index) => {
      if (displayRow.kind !== 'projection') {
        locations.set(displayRow.row.id, { displayRow, index })
      }
    })
    return locations
  }, [characterScopedDisplayRows])

  const matrixInlineRibbonPresentationByDisplayRowKey = useMemo(() => {
    const ribbonByRow = new Map<string, Map<string, LedgerSafeInlineRibbonModel>>()
    const ribbonAboveByRow = new Map<string, Set<string>>()

    inlineRibbonByCellKey.forEach((ribbon, cellKey) => {
      const separatorIndex = cellKey.lastIndexOf(':')
      if (separatorIndex < 0) {
        return
      }
      const rowId = cellKey.slice(0, separatorIndex)
      const witnessId = cellKey.slice(separatorIndex + 1)
      const location = canonicalDisplayRowLocationById.get(rowId)
      if (!location) {
        return
      }

      const rowRibbons = ribbonByRow.get(location.displayRow.key) || new Map<string, LedgerSafeInlineRibbonModel>()
      rowRibbons.set(witnessId, ribbon)
      ribbonByRow.set(location.displayRow.key, rowRibbons)

      const nextDisplayRow = characterScopedDisplayRows[location.index + 1]
      if (nextDisplayRow) {
        const rowWitnesses = ribbonAboveByRow.get(nextDisplayRow.key) || new Set<string>()
        rowWitnesses.add(witnessId)
        ribbonAboveByRow.set(nextDisplayRow.key, rowWitnesses)
      }
    })

    return { ribbonByRow, ribbonAboveByRow }
  }, [
    canonicalDisplayRowLocationById,
    characterScopedDisplayRows,
    inlineRibbonByCellKey
  ])
  const matrixCellDiffPresentationKey = [
    diffEnabled ? 'diff-enabled' : 'diff-disabled',
    diffMode,
    diffPivotWitness?.id || 'no-pivot',
    apparatusScope,
    apparatusSelectedRowId || 'all-rows',
  ].join('|')

  useEffect(() => {
    if (inlineEditMode !== 'idle') {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      if (event.key === 'Escape' && cellPlacementMode) {
        event.preventDefault()
        setCellPlacementMode(null)
        setRelocationHoverTargetRowId(null)
        return
      }
      const target = event.target
      if (target instanceof Element && target.closest('input, textarea, select, button, [contenteditable="true"]')) {
        return
      }

      let action: LedgerSafeInlineRibbonAction | null = null
      const key = event.key.toLowerCase()
      if (selectedRangeSummary) {
        if (selectedRangeSummary.mappedCount < 2) {
          return
        }
        if (key === 'm') {
          action = 'merge'
        } else if (key === 'r' && selectedRangeSummary.allMapped) {
          action = 'relocate'
        }
      } else if (selectedLedgerCellState === 'mapped') {
        if (key === 'r') {
          action = 'relocate'
        } else if (key === 'a') {
          action = 'insert-above'
        } else if (key === 'b') {
          action = 'insert-below'
        } else if (key === 's' && selectedSplitContext) {
          action = 'split'
        } else if (key === 'e' && selectedBoundaryOptions.length > 0) {
          action = 'boundary'
        }
      } else if (selectedLedgerCellState === 'excluded' && key === 'g') {
        action = 'clear-gap'
      } else if (selectedLedgerCellState === 'gap' && key === 'g') {
        action = 'mark-gap'
      }

      if (!action) {
        return
      }
      event.preventDefault()
      void handleLedgerRibbonAction(action)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    cellPlacementMode,
    handleLedgerRibbonAction,
    inlineEditMode,
    selectedBoundaryOptions.length,
    selectedLedgerCellState,
    selectedRangeSummary,
    selectedSplitContext
  ])

  const handleStableApplyBoundary = useEventCallback(() => {
    void handleApply()
  })

  const handleStableApplySplit = useEventCallback(() => {
    void handleApplySplit()
  })

  const handleStableBoundaryDelta = useEventCallback((delta: number) => {
    setPendingDeltaWords(value => value + delta)
  })

  const handleStableBoundaryDragStart = useEventCallback((startX: number, startBoundaryWordIndex: number) => {
    setBoundaryDragState({
      startX,
      startBoundaryWordIndex
    })
  })

  const handleStableCancelBoundary = useEventCallback(() => {
    setPendingDeltaWords(0)
    setBoundaryDragState(null)
    setInlineEditMode('idle')
  })

  const handleStableCancelSplit = useEventCallback(() => {
    setSelectedSplitBoundaryWordIndex(null)
    setInlineEditMode('idle')
  })

  const occupancyMapLayerMeta = useMemo(() => {
    if (occupancyMapLayer === 'variance') {
      return {
        title: 'Variance heatmap',
        helper: 'Striped ink marks the reference reading; darker ink marks rising divergence.',
        legend: [
          { id: 'variance-unified', label: 'Unified', className: 'border border-black/70', style: { backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS.unified } },
          {
            id: 'variance-reference',
            label: 'Reference reading',
            className: 'border border-black/70',
            style: {
              backgroundColor: LEDGER_OCCUPANCY_REFERENCE_READING_COLOR,
              backgroundImage: LEDGER_OCCUPANCY_REFERENCE_READING_STRIPES,
              boxShadow: `inset 0 -1px 0 ${LEDGER_OCCUPANCY_INK_COLOR}`
            }
          },
          { id: 'variance-near', label: 'Near match', className: 'border border-black/70', style: { backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS.near } },
          { id: 'variance-drifting', label: 'Drifting', className: 'border border-black/70', style: { backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS.drifting } },
          { id: 'variance-conflict', label: 'Conflict', className: 'border border-black/70', style: { backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS.conflict } },
          { id: 'variance-severe', label: 'Severe', className: 'border border-black/70', style: { backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS.severe } },
          { id: 'variance-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'variance-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'triage') {
      return {
        title: 'AI triage map',
        helper: 'Pi blue marks current review zones; Pi green marks recent Pi edits.',
        legend: [
          { id: 'triage-review', label: 'Review needed', className: 'border border-black', style: { backgroundColor: LEDGER_OCCUPANCY_PI_PREVIEW_COLOR } },
          { id: 'triage-recent', label: 'Recent Pi edit', className: 'border border-black', style: { backgroundColor: LEDGER_OCCUPANCY_PI_APPLIED_COLOR, opacity: 0.7 } },
          { id: 'triage-background', label: 'Background', className: 'border border-black bg-gray-200' },
          { id: 'triage-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'triage-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'height') {
      return {
        title: 'Textual density map',
        helper: 'Rows scale by the densest reading; each witness fills downward by relative text mass.',
        legend: [
          {
            id: 'height-full',
            label: 'Row maximum',
            className: 'border border-black',
            style: { backgroundColor: LEDGER_OCCUPANCY_INK_COLOR }
          },
          {
            id: 'height-partial',
            label: 'Shorter unit',
            className: 'border border-black',
            style: {
              background: `linear-gradient(to bottom, ${LEDGER_OCCUPANCY_INK_COLOR} 0 58%, ${LEDGER_OCCUPANCY_PAPER_COLOR} 58% 100%)`
            }
          },
          { id: 'height-gap', label: 'Deliberate gap', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
          { id: 'height-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'height-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'thin-line') {
      return {
        title: 'Thin-line silhouette',
        helper: 'Each witness reading is a right-anchored stroke; longer units extend farther left.',
        legend: [
          { id: 'thin-line-long', label: 'Long reading', className: 'border border-black bg-gray-900' },
          { id: 'thin-line-short', label: 'Short reading', className: 'border border-black bg-gray-400' },
          { id: 'thin-line-gap', label: 'Lacuna tick', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
          { id: 'thin-line-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'thin-line-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'drift') {
      return {
        title: 'Cumulative drift',
        helper: 'A faint fill tracks each witness running total; local strokes show unit length at that row.',
        legend: [
          { id: 'drift-area', label: 'Running total', className: 'border border-black bg-gray-200' },
          { id: 'drift-stroke', label: 'Local unit', className: 'border border-black bg-gray-900' },
          { id: 'drift-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'drift-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'bead') {
      return {
        title: 'Bead chain',
        helper: 'Each unit is a bead sized by global text mass; each witness column keeps its thread.',
        legend: [
          { id: 'bead-long', label: 'Large bead', className: 'rounded-full border border-black bg-gray-900' },
          { id: 'bead-short', label: 'Small bead', className: 'rounded-full border border-black bg-gray-400' },
          { id: 'bead-gap', label: 'Notched gap', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
          { id: 'bead-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'bead-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'flow') {
      return {
        title: 'Flow columns',
        helper: 'Each witness becomes an independent stack; total column height reveals overall expansion.',
        legend: [
          { id: 'flow-long', label: 'Long segment', className: 'border border-black bg-gray-900' },
          { id: 'flow-short', label: 'Short segment', className: 'border border-black bg-gray-400' },
          { id: 'flow-gap', label: 'Gap stub', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
          { id: 'flow-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE }
        ]
      }
    }
    if (occupancyMapLayer === 'spine') {
      return {
        title: 'Center spine mirror bars',
        helper: 'Rows scale by their maximum; each witness bar grows symmetrically from the row centerline.',
        legend: [
          { id: 'spine-full', label: 'Row maximum', className: 'border border-black bg-gray-900' },
          {
            id: 'spine-partial',
            label: 'Shorter unit',
            className: 'border border-black',
            style: {
              background: `linear-gradient(to bottom, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0 35%, ${LEDGER_OCCUPANCY_INK_COLOR} 35% 65%, ${LEDGER_OCCUPANCY_PAPER_COLOR} 65% 100%)`
            }
          },
          { id: 'spine-gap', label: 'Deliberate gap', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
          { id: 'spine-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
          { id: 'spine-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
        ]
      }
    }
    return {
      title: 'Occupancy map',
      helper: 'Click any band to jump.',
      legend: [
        { id: 'structural-occupied', label: 'Occupied', className: 'border border-black bg-gray-900' },
        { id: 'structural-gap', label: 'Deliberate gap', className: 'border border-black', style: LEDGER_OCCUPANCY_GAP_SLOT_STYLE },
        { id: 'structural-empty', label: 'Empty row', className: 'border border-black bg-white' },
        { id: 'structural-selected', label: 'Selected', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_SELECTED_LEGEND_STYLE },
        { id: 'structural-preview', label: 'Preview', className: 'border border-black bg-white', style: LEDGER_OCCUPANCY_PREVIEW_LEGEND_STYLE }
      ]
    }
  }, [occupancyMapLayer])

  const renderStructuralRangeRibbon = () => {
    if (!structuralRangeEditor) return null
    const parentOptions = findStructuralRangeParentOptions({
      layer: structuralRangeLayer,
      rangeId: structuralRangeEditor.rangeId || undefined,
      kind: structuralRangeEditor.kind || null,
      startRowId: structuralRangeEditor.startRowId,
      endRowId: structuralRangeEditor.endRowId,
      rowOrderById: structuralRowOrderById,
    })
    const startRow = rowById.get(structuralRangeEditor.startRowId)
    const endRow = rowById.get(structuralRangeEditor.endRowId)
    const editingRange = structuralRangeEditor.rangeId
      ? structuralRangeById.get(structuralRangeEditor.rangeId) || null
      : null
    const selectedParent = structuralRangeEditor.parentRangeId
      ? structuralRangeById.get(structuralRangeEditor.parentRangeId) || null
      : null
    const startLabel = startRow ? getRowLabel(startRow) : structuralRangeEditor.startRowId
    const endLabel = endRow ? getRowLabel(endRow) : structuralRangeEditor.endRowId
    const toggleMenu = (menu: LedgerSafeStructuralRangeMenu) => {
      setStructuralRangeMenu(current => current === menu ? null : menu)
      setStructuralRangeError(null)
    }
    const selectKind = (kind: StructuralRangeKind) => {
      const options = findStructuralRangeParentOptions({
        layer: structuralRangeLayer,
        rangeId: structuralRangeEditor.rangeId || undefined,
        kind,
        startRowId: structuralRangeEditor.startRowId,
        endRowId: structuralRangeEditor.endRowId,
        rowOrderById: structuralRowOrderById,
      })
      const currentParentIsValid = options.some(option => (
        option.id === structuralRangeEditor.parentRangeId
      ))
      setStructuralRangeEditor({
        ...structuralRangeEditor,
        kind,
        parentRangeId: currentParentIsValid
          ? structuralRangeEditor.parentRangeId
          : findDefaultStructuralRangeParentId(options) || '',
      })
      setStructuralRangeMenu(null)
    }
    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        !structuralRangeMenu
        || structuralRangeMenu === 'title'
        || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
      ) {
        return
      }
      const menu = structuralRangeMenuRef.current?.querySelector<HTMLElement>('[role="menu"]')
      const items = menu
        ? Array.from(menu.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)'))
        : []
      if (!items.length) return
      event.preventDefault()
      const currentIndex = items.indexOf(document.activeElement as HTMLElement)
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : currentIndex < 0
            ? 0
            : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
      items[nextIndex]?.focus()
    }
    return (
      <div
        ref={structuralRangeMenuRef}
        data-testid="ledger-structural-range-ribbon"
        className="fixed z-[120] w-max text-left"
        style={{
          left: structuralRangeRibbonPosition?.left || 0,
          top: structuralRangeRibbonPosition?.top || 0,
          maxWidth: structuralRangeRibbonPosition?.maxWidth || 200,
          visibility: structuralRangeRibbonPosition ? 'visible' : 'hidden',
        }}
        dir="ltr"
        onClick={event => event.stopPropagation()}
        onKeyDown={handleMenuKeyDown}
      >
        <div
          role="toolbar"
          aria-label={editingRange ? `Edit ${editingRange.title}` : `Create structure for ${startLabel} through ${endLabel}`}
          className="flex min-h-9 max-w-full flex-wrap items-stretch gap-1 border border-black bg-[#fffdfa] px-1.5 py-1 shadow-[2px_2px_0_#000]"
        >
          <span className="flex h-7 shrink-0 items-center border-r border-dashed border-black/20 px-1.5 pr-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
            {structuralBoundaryPlacement
              ? `Place ${structuralBoundaryPlacement.edge}`
              : `${startLabel}–${endLabel}`}
          </span>
          {structuralBoundaryPlacement ? (
            <>
              <span className="flex h-7 items-center px-1.5 font-serif text-[12px] font-semibold text-black">
                {structuralRangeEditor.title || 'Untitled range'}
              </span>
              <button
                type="button"
                title="Cancel boundary placement"
                aria-label="Cancel boundary placement"
                onClick={() => {
                  setStructuralBoundaryPlacement(null)
                  setStructuralBoundaryHoverRowId('')
                  setStructuralRangeError(null)
                }}
                className="flex h-7 w-7 shrink-0 items-center justify-center border border-black bg-white text-black hover:bg-gray-100"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                title={structuralRangeEditor.title ? 'Edit title' : 'Add title'}
                aria-label={structuralRangeEditor.title ? 'Edit title' : 'Add title'}
                aria-haspopup="dialog"
                aria-expanded={structuralRangeMenu === 'title'}
                onClick={() => toggleMenu('title')}
                className="flex h-7 shrink-0 items-center gap-1 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black hover:bg-gray-100"
              >
                <Pencil className="h-3 w-3" aria-hidden="true" />
                Title
              </button>
              <button
                type="button"
                title="Classify structural range"
                aria-label="Classify structural range"
                aria-haspopup="menu"
                aria-expanded={structuralRangeMenu === 'kind'}
                onClick={() => toggleMenu('kind')}
                className={`flex h-7 shrink-0 items-center gap-1 border border-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] hover:bg-gray-100 ${
                  structuralRangeEditor.kind ? 'bg-black text-white hover:bg-gray-800' : 'bg-[#fffdfa] text-black'
                }`}
              >
                <Tag className="h-3 w-3" aria-hidden="true" />
                {structuralRangeEditor.kind
                  ? STRUCTURAL_RANGE_KIND_LABELS[structuralRangeEditor.kind]
                  : 'Classify'}
              </button>
              <button
                type="button"
                title="Choose an enclosing structural range"
                aria-label="Choose parent range"
                aria-haspopup="menu"
                aria-expanded={structuralRangeMenu === 'parent'}
                onClick={() => toggleMenu('parent')}
                className="flex h-7 shrink-0 items-center gap-1 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black hover:bg-gray-100"
              >
                <GitBranch className="h-3 w-3" aria-hidden="true" />
                {selectedParent ? 'Inside' : 'Parent'}
              </button>
              <button
                type="button"
                title="Adjust range boundaries"
                aria-label="Adjust range boundaries"
                aria-haspopup="menu"
                aria-expanded={structuralRangeMenu === 'adjust'}
                onClick={() => toggleMenu('adjust')}
                className="flex h-7 shrink-0 items-center gap-1 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black hover:bg-gray-100"
              >
                <MoveHorizontal className="h-3 w-3" aria-hidden="true" />
                Adjust
              </button>
              {editingRange ? (
                <button
                  type="button"
                  title="Delete structural range"
                  aria-label="Delete structural range"
                  onClick={() => void handleDeleteStructuralRange(editingRange)}
                  disabled={isMatrixContentApplying}
                  className="flex h-7 shrink-0 items-center gap-1 border border-red-300 bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-red-800 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300"
                >
                  <Trash2 className="h-3 w-3" aria-hidden="true" />
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                title={editingRange ? 'Confirm structural range changes' : 'Create structural range'}
                disabled={isMatrixContentApplying || !structuralRangeEditor.title.trim() || !structuralRangeEditor.kind}
                onClick={() => void handleSaveStructuralRange()}
                className="flex h-7 shrink-0 items-center gap-1 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-white shadow-[1px_1px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:shadow-none"
              >
                <Check className="h-3 w-3" aria-hidden="true" />
                Confirm
              </button>
              <button
                type="button"
                title="Cancel structural range editing"
                aria-label="Cancel structural range editing"
                onClick={closeStructuralRangeEditor}
                className="flex h-7 w-7 shrink-0 items-center justify-center border border-black bg-white text-black hover:bg-gray-100"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {structuralRangeMenu === 'title' ? (
          <div
            role="dialog"
            aria-label="Structural range title"
            className="mt-1 w-[360px] max-w-full border border-black bg-[#fffdfa] p-2 shadow-[2px_2px_0_#000]"
          >
            <div className="mb-1 flex items-center justify-between gap-3">
              <label
                htmlFor="ledger-structural-range-title"
                className="font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500"
              >
                Title
              </label>
              <span className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-gray-400">
                {startLabel}–{endLabel}
              </span>
            </div>
            <input
              id="ledger-structural-range-title"
              data-testid="ledger-structural-range-title"
              autoFocus
              value={structuralRangeEditor.title}
              onChange={event => setStructuralRangeEditor({
                ...structuralRangeEditor,
                title: event.target.value,
              })}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  setStructuralRangeMenu(null)
                }
              }}
              className="h-9 w-full border border-black bg-white px-2 font-serif text-[13px] font-semibold text-black shadow-[1px_1px_0_#000]"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setStructuralRangeMenu(null)}
                className="flex h-7 items-center gap-1 border border-black bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-white hover:bg-gray-800"
              >
                <Check className="h-3 w-3" aria-hidden="true" />
                Done
              </button>
            </div>
          </div>
        ) : null}

        {structuralRangeMenu === 'kind' ? (
          <div
            role="menu"
            aria-label="Structural range classification"
            className="mt-1 max-h-[50vh] w-[230px] max-w-full overflow-y-auto border border-black bg-[#fffdfa] p-1.5 shadow-[2px_2px_0_#000]"
          >
            {STRUCTURAL_RANGE_KINDS.map(kind => (
              <button
                key={kind}
                type="button"
                role="menuitemradio"
                aria-checked={structuralRangeEditor.kind === kind}
                title={STRUCTURAL_RANGE_KIND_HELP[kind]}
                data-classification-group={(LEGACY_STRUCTURAL_RANGE_KINDS as readonly string[]).includes(kind) ? 'existing' : 'editorial'}
                onClick={() => selectKind(kind)}
                className={`flex min-h-8 w-full items-center justify-between gap-3 border px-2 text-left font-mono text-[9px] font-bold uppercase tracking-[0.1em] ${
                  structuralRangeEditor.kind === kind
                    ? 'border-black bg-black text-white'
                    : 'border-transparent bg-[#fffdfa] text-black hover:border-black/20 hover:bg-gray-100'
                }`}
              >
                <span className="whitespace-nowrap">{STRUCTURAL_RANGE_KIND_LABELS[kind]}</span>
                {structuralRangeEditor.kind === kind ? (
                  <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {structuralRangeMenu === 'parent' ? (
          <div
            role="menu"
            aria-label="Parent structural range"
            className="mt-1 w-[320px] max-w-full border border-black bg-[#fffdfa] p-1.5 shadow-[2px_2px_0_#000]"
          >
            <button
              type="button"
              role="menuitemradio"
              aria-checked={!structuralRangeEditor.parentRangeId}
              onClick={() => {
                setStructuralRangeEditor({ ...structuralRangeEditor, parentRangeId: '' })
                setStructuralRangeMenu(null)
              }}
              className={`flex min-h-8 w-full items-center justify-between gap-3 border px-2 text-left font-mono text-[9px] font-bold uppercase tracking-[0.08em] ${
                structuralRangeEditor.parentRangeId
                  ? 'border-transparent bg-[#fffdfa] text-black hover:border-black/20 hover:bg-gray-100'
                  : 'border-black bg-black text-white'
              }`}
            >
              <span>No parent</span>
              {!structuralRangeEditor.parentRangeId ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
            </button>
            {parentOptions.map(parent => (
              <button
                key={parent.id}
                type="button"
                role="menuitemradio"
                aria-checked={structuralRangeEditor.parentRangeId === parent.id}
                onClick={() => {
                  setStructuralRangeEditor({ ...structuralRangeEditor, parentRangeId: parent.id })
                  setStructuralRangeMenu(null)
                }}
                className={`flex min-h-8 w-full items-start justify-between gap-3 border px-2 py-1.5 text-left ${
                  structuralRangeEditor.parentRangeId === parent.id
                    ? 'border-black bg-black text-white'
                    : 'border-transparent bg-[#fffdfa] text-black hover:border-black/20 hover:bg-gray-100'
                }`}
              >
                <span className="min-w-0 whitespace-normal break-words font-serif text-[12px] font-semibold leading-4">
                  {formatEditorialTitleForDisplay(parent.title)}
                </span>
                <span className="shrink-0 font-mono text-[8px] font-bold uppercase tracking-[0.08em] opacity-70">
                  {formatStructuralRangeKindLabel(parent.kind, structuralDisplayNumbers.get(parent.id))}
                </span>
              </button>
            ))}
            {parentOptions.length === 0 ? (
              <div className="border-t border-black/15 px-2 py-2 font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-gray-400">
                No enclosing range
              </div>
            ) : null}
          </div>
        ) : null}

        {structuralRangeMenu === 'adjust' ? (
          <div
            role="menu"
            aria-label="Adjust structural range"
            className="mt-1 w-[230px] max-w-full border border-black bg-[#fffdfa] p-1.5 shadow-[2px_2px_0_#000]"
          >
            {(['start', 'end'] as const).map(edge => (
              <button
                key={edge}
                type="button"
                role="menuitem"
                onClick={() => {
                  setStructuralBoundaryPlacement({
                    edge,
                    rangeId: structuralRangeEditor.rangeId,
                  })
                  setStructuralBoundaryHoverRowId('')
                  setStructuralRangeMenu(null)
                  setStructuralRangeError(null)
                }}
                className="flex min-h-8 w-full items-center gap-2 border border-transparent bg-[#fffdfa] px-2 text-left font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black hover:border-black/20 hover:bg-gray-100"
              >
                <MoveHorizontal className="h-3 w-3" aria-hidden="true" />
                Move {edge}
              </button>
            ))}
          </div>
        ) : null}

        {structuralRangeError ? (
          <div
            role="alert"
            className="mt-1 w-[360px] max-w-full border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] leading-4 text-red-800 shadow-[1px_1px_0_#000]"
          >
            {structuralRangeError}
          </div>
        ) : null}
      </div>
    )
  }

  const renderStructuralRangeHeader = (range: StructuralRange) => {
    const startRow = rowById.get(range.startRowId)
    const endRow = rowById.get(range.endRowId)
    const depth = structuralRangeDepthById.get(range.id) || 0
    const kindLabel = formatStructuralRangeKindLabel(range.kind, structuralDisplayNumbers.get(range.id), 'Review')
    const statusLabel = range.status === 'confirmed'
      ? ''
      : range.status === 'suggested'
        ? 'Suggested'
        : 'Unclassified'
    const isRibbonAnchor = structuralRangeRibbonAnchor?.kind === 'range'
      && structuralRangeRibbonAnchor.id === range.id
    return (
      <tr
        key={`structural-range-header:${range.id}`}
        data-testid={`ledger-structural-range-${range.id}`}
        className="bg-[#fffdfa]"
      >
        <td
          colSpan={visibleScopeWitnesses.length + 1}
          className="relative overflow-visible border-y border-black/30 bg-[#fffdfa] px-0 py-0"
        >
          <button
            type="button"
            data-structural-range-id={range.id}
            onClick={() => openStructuralRangeEditor(range)}
            disabled={readOnly}
            className="ledger-heading ledger-heading-structure group/structure hover:bg-gray-100 disabled:cursor-default"
            title={`${readOnly ? formatEditorialTitleForDisplay(range.title) : `Edit ${kindLabel.toLowerCase()} range`} · ${startRow ? getRowLabel(startRow) : range.startRowId}–${endRow ? getRowLabel(endRow) : range.endRowId} · Hierarchy depth ${depth}`}
          >
            <span className="ledger-heading-reference ledger-heading-kind" data-testid={`ledger-structural-kind-label-${range.id}`}>
              {kindLabel}
            </span>
            <span className="ledger-heading-content">
              <span className="ledger-heading-title whitespace-normal">{formatEditorialTitleForDisplay(range.title)}</span>
              <span className="ledger-heading-meta sr-only"> · Hierarchy depth {depth} · {startRow ? getRowLabel(startRow) : range.startRowId}–{endRow ? getRowLabel(endRow) : range.endRowId}</span>
            </span>
            <span className="ledger-heading-actions">
            {statusLabel ? (
              <span className="mt-0.5 shrink-0 border border-black/30 bg-white px-1.5 py-0.5 font-mono text-[7px] font-bold uppercase tracking-[0.1em] text-gray-500">
                {statusLabel}
              </span>
            ) : null}
            {!readOnly ? (
              <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-gray-400 group-hover/structure:text-black" aria-hidden="true" />
            ) : null}
            </span>
          </button>
          {isRibbonAnchor ? renderStructuralRangeRibbon() : null}
        </td>
      </tr>
    )
  }

  const renderLedgerSceneHeader = (sceneHeader: LedgerSafeSceneHeaderModel) => {
    const isEditing = sceneTitleEditor?.sceneKey === sceneHeader.sceneKey
    const editorTitle = isEditing ? sceneTitleEditor.title : sceneHeader.title
    const editorArabicTitle = isEditing ? sceneTitleEditor.arabicTitle : sceneHeader.arabicTitle
    const sceneChipLabel = getLedgerSafeSceneChipLabel(sceneHeader.label)
    const sceneRows = sceneHeader.rowIds
      .map(rowId => rowById.get(rowId))
      .filter((row): row is AnchoredRow => Boolean(row))
    const sceneAttestedWitnessIds = new Set<string>()
    sceneRows.forEach(row => {
      row.unitMappings.forEach(mapping => {
        if (scopeWitnessById.has(mapping.witnessId)) {
          sceneAttestedWitnessIds.add(mapping.witnessId)
        }
      })
    })
    const sceneRowCount = sceneHeader.rowCount ?? sceneRows.length
    const sceneWitnessTotal = scopeWitnesses.length
    const sceneOccupancySummary = `${sceneRowCount} ${sceneRowCount === 1 ? 'row' : 'rows'} · ${sceneAttestedWitnessIds.size}/${sceneWitnessTotal} ${sceneWitnessTotal === 1 ? 'witness' : 'witnesses'}`

    return (
      <tr
        key={`ledger-scene-header:${sceneHeader.sceneKey}`}
        data-testid={`ledger-scene-header-${sceneHeader.testIdKey}`}
        className="group bg-white"
      >
        <td
          colSpan={visibleScopeWitnesses.length + 1}
          className="border-y border-black bg-white px-0 py-0"
        >
          {isEditing && !readOnly ? (
            <form
              className="flex min-h-[32px] items-center gap-2 px-3 py-0.5"
              onClick={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault()
                void handleSaveSceneTitle(sceneHeader.sceneKey, {
                  title: editorTitle,
                  arabicTitle: editorArabicTitle
                })
              }}
            >
              <span
                data-testid="ledger-scene-compact-label"
                title={sceneHeader.label}
                className="shrink-0 bg-black px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] text-white"
              >
                {sceneChipLabel}
              </span>
              <span
                data-testid="ledger-scene-occupancy-summary"
                className="shrink-0 whitespace-nowrap font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500"
              >
                {sceneOccupancySummary}
              </span>
              <label className="sr-only" htmlFor={`scene-title-${sceneHeader.testIdKey}`}>
                Scene title
              </label>
              <input
                id={`scene-title-${sceneHeader.testIdKey}`}
                aria-label={`Scene title for ${sceneHeader.title}`}
                autoFocus
                value={editorTitle}
                onChange={event => setSceneTitleEditor({
                  sceneKey: sceneHeader.sceneKey,
                  title: event.target.value,
                  arabicTitle: editorArabicTitle
                })}
                className="h-6 min-w-0 flex-[1_1_260px] border border-black bg-white px-2 font-serif text-[13px] font-semibold italic leading-5 text-black shadow-[1px_1px_0_#000]"
              />
              <label className="sr-only" htmlFor={`scene-arabic-title-${sceneHeader.testIdKey}`}>
                Arabic scene title
              </label>
              <input
                id={`scene-arabic-title-${sceneHeader.testIdKey}`}
                aria-label={`Arabic title for ${sceneHeader.title}`}
                dir="rtl"
                value={editorArabicTitle}
                onChange={event => setSceneTitleEditor({
                  sceneKey: sceneHeader.sceneKey,
                  title: editorTitle,
                  arabicTitle: event.target.value
                })}
                className="h-6 min-w-0 flex-[0_1_180px] border border-black bg-white px-2 font-reader-arabic text-[12px] leading-5 text-black shadow-[1px_1px_0_#000]"
              />
              <button
                type="submit"
                disabled={isMatrixContentApplying}
                className="h-6 border border-black bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-white disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setSceneTitleEditor(null)}
                className="h-6 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black hover:bg-gray-100"
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className="ledger-heading ledger-heading-structure">
              <span
                data-testid="ledger-scene-compact-label"
                title={sceneHeader.label}
                className="ledger-heading-reference ledger-heading-kind"
              >
                {sceneChipLabel}
              </span>
              <div className="ledger-heading-content" title={sceneOccupancySummary}>
                <span data-testid="ledger-scene-occupancy-summary" className="sr-only">{sceneOccupancySummary}</span>
                <span
                  className="ledger-heading-title whitespace-normal"
                  title={sceneHeader.description || formatEditorialTitleForDisplay(sceneHeader.title)}
                >
                  {formatEditorialTitleForDisplay(sceneHeader.title)}
                </span>
                {sceneHeader.arabicTitle ? (
                  <span
                    dir="rtl"
                    className="min-w-0 whitespace-normal break-words font-reader-arabic text-[13px] leading-5 text-gray-700"
                    title={sceneHeader.arabicTitle}
                  >
                    {sceneHeader.arabicTitle}
                  </span>
                ) : null}
              </div>
              <div className="ledger-heading-actions">
              {!readOnly && !sceneHeader.arabicTitle && (
                <button
                  type="button"
                  onClick={() => handleStartSceneTitleEdit(sceneHeader)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center border border-black bg-white font-reader-arabic text-[13px] font-bold leading-none text-black opacity-0 shadow-[1px_1px_0_#000] transition-opacity hover:bg-gray-100 focus:opacity-100 group-hover:opacity-100"
                  aria-label={`Add Arabic title for ${sceneHeader.title}`}
                  title="Add Arabic scene title"
                >
                  ع
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  title="Remove legacy scene label; structural ranges are preserved"
                  aria-label={`Remove legacy scene label for ${sceneHeader.title}`}
                  data-testid={`ledger-remove-legacy-scene-${sceneHeader.testIdKey}`}
                  onClick={() => void handleRemoveLegacySceneLabel(sceneHeader)}
                  disabled={isMatrixContentApplying}
                  className="flex h-6 w-6 shrink-0 items-center justify-center border border-black bg-white text-black opacity-0 shadow-[1px_1px_0_#000] transition-opacity hover:bg-gray-100 focus:opacity-100 disabled:cursor-not-allowed disabled:text-gray-300 group-hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  title={sceneHeader.description || `Edit scene label: ${sceneHeader.title}`}
                  aria-label={`Edit scene title for ${sceneHeader.title}`}
                  onClick={() => handleStartSceneTitleEdit(sceneHeader)}
                  className="h-6 shrink-0 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black opacity-0 shadow-[1px_1px_0_#000] transition-opacity hover:bg-gray-100 focus:opacity-100 group-hover:opacity-100"
                >
                  Edit
                </button>
              )}
              </div>
            </div>
          )}
        </td>
      </tr>
    )
  }


  const renderOracleExportCard = () => {
    if (!runtimeEnableOracleExport) {
      return null
    }
    return (
      <div data-testid="ledger-oracle-export-log-card" className="border border-black bg-gray-50 p-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Oracle export</div>
            <div className="mt-2 text-sm font-semibold text-black">Propagation oracle</div>
            <p className="mt-2 text-xs leading-5 text-gray-600">
              Export the current collation as an oracle snapshot. Excluded witnesses stay in the file as held-out oracle targets.
            </p>
          </div>
          <div className="shrink-0 text-right font-mono text-[10px] uppercase tracking-widest text-gray-500">
            {oracleIncludedWitnessCount} included<br />
            {oracleExportExcludedWitnessIds.length} excluded
          </div>
        </div>
        <div className="mt-3 border-t border-black/10 pt-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
              Exclude from context
            </div>
            <button
              type="button"
              onClick={() => setOracleExportExcludedWitnessIds([])}
              disabled={!oracleExportExcludedWitnessIds.length}
              className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:text-gray-300"
            >
              Reset
            </button>
          </div>
          <div className="mt-2 max-h-48 space-y-1 overflow-auto pr-1">
            {scopeWitnesses.map(witness => {
              const isExcluded = oracleExportExcludedWitnessIds.includes(witness.id)
              return (
                <label
                  key={witness.id}
                  className="flex cursor-pointer items-center justify-between gap-3 border border-black/10 bg-white px-2 py-2 text-[11px] text-black hover:bg-gray-50"
                >
                  <span className="font-bold uppercase tracking-widest">{witness.sigla}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-gray-400">
                      {isExcluded ? 'Held out' : 'Included'}
                    </span>
                    <input
                      type="checkbox"
                      checked={isExcluded}
                      onChange={() => toggleOracleExportWitness(witness.id)}
                      aria-label={`Exclude ${witness.sigla}`}
                      className="h-4 w-4 border-black text-black focus:ring-black"
                    />
                  </span>
                </label>
              )
            })}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-black/10 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">
            Current scope: {describeManualStudioScope(activeScope)}
          </div>
          <button
            type="button"
            onClick={handleExportPropagationOracle}
            disabled={!canExportPropagationOracle}
            className="h-8 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
          >
            Download oracle JSON
          </button>
        </div>
      </div>
    )
  }
  const renderStructurePanel = () => (
  <div
    className="space-y-4"
  >
    <section className="border-b border-black pb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
          Hierarchy
        </div>
        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
          Click to edit
        </span>
      </div>
      {structuralRangeLayer.ranges.some(range => range.status !== 'rejected') ? (
        <div className="mt-2 border-t border-black/20">
          {[...structuralRangeLayer.ranges]
            .filter(range => range.status !== 'rejected')
            .sort((left, right) => (
              (structuralRowOrderById.get(left.startRowId) ?? Number.MAX_SAFE_INTEGER)
              - (structuralRowOrderById.get(right.startRowId) ?? Number.MAX_SAFE_INTEGER)
              || (structuralRangeDepthById.get(left.id) || 0) - (structuralRangeDepthById.get(right.id) || 0)
              || left.title.localeCompare(right.title)
            ))
            .map(range => {
              const startRow = rowById.get(range.startRowId)
              const endRow = rowById.get(range.endRowId)
              const depth = structuralRangeDepthById.get(range.id) || 0
              return (
                <button
                  key={range.id}
                  type="button"
                  onClick={() => openStructuralRangeEditor(range)}
                  className="flex w-full items-start gap-2 border-b border-black/15 bg-white py-2 pr-1 text-left hover:bg-gray-50"
                  style={{ paddingLeft: `${4 + Math.min(depth, 6) * 12}px` }}
                >
                  <GitBranch className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block whitespace-normal break-words font-serif text-[12px] font-semibold leading-4 text-black">
                      {formatEditorialTitleForDisplay(range.title)}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      <StructuralKindLabel
                        kind={range.kind}
                        number={structuralDisplayNumbers.get(range.id)}
                        testId={`ledger-structure-hierarchy-kind-${range.id}`}
                      />
                      <span className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-gray-600">
                        {startRow ? getRowLabel(startRow) : range.startRowId}–{endRow ? getRowLabel(endRow) : range.endRowId}
                      </span>
                    </span>
                  </span>
                  {range.status !== 'confirmed' ? (
                    <span className="shrink-0 border border-black/30 px-1 py-0.5 font-mono text-[7px] font-bold uppercase tracking-normal text-gray-500">
                      {range.status}
                    </span>
                  ) : null}
                </button>
              )
            })}
        </div>
      ) : (
        <div className="mt-2 border border-dashed border-black/30 bg-[#fffdfa] px-3 py-4 text-center font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-gray-400">
          No chapter ranges yet
        </div>
      )}
    </section>

    {legacySceneMigrationPreview.candidates.length > 0 || legacySceneRemovalUndo ? (
      <section className="border-b border-black pb-4">
        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
          Legacy scene metadata
        </div>
        {legacySceneMigrationPreview.candidates.length > 0 ? (
          <>
            <div className="mt-1 text-[11px] leading-5 text-gray-600">
              {legacySceneMigrationPreview.candidates.length} existing range{legacySceneMigrationPreview.candidates.length === 1 ? '' : 's'} can be imported without changing row metadata. They will require classification.
            </div>
            <button
              type="button"
              onClick={() => void handleImportLegacyStructuralRanges()}
              disabled={isMatrixContentApplying}
              className="mt-2 h-8 w-full border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] hover:bg-gray-100 disabled:text-gray-300"
            >
              Import as unclassified
            </button>
          </>
        ) : null}
        {legacySceneRemovalUndo ? (
          <button
            type="button"
            onClick={() => void handleUndoLegacySceneLabelRemoval()}
            disabled={isMatrixContentApplying}
            className="mt-2 flex min-h-8 w-full items-center justify-between gap-2 border border-black bg-white px-2 py-1.5 text-left shadow-[1px_1px_0_#000] hover:bg-gray-100 disabled:text-gray-300"
          >
            <span className="min-w-0 whitespace-normal break-words font-serif text-[11px] font-semibold leading-4">
              {formatEditorialTitleForDisplay(legacySceneRemovalUndo.title)}
            </span>
            <span className="shrink-0 font-mono text-[8px] font-bold uppercase tracking-[0.1em]">
              Undo removal
            </span>
          </button>
        ) : null}
      </section>
    ) : null}

    {runtimeEnableStructuralRangeGeneration ? (
    <section data-testid="ledger-structural-range-generation">
      <div className="flex items-center gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        Range title
      </div>
      <div className="mt-1 text-[11px] leading-5 text-gray-600">
        Pi proposes one title for the selected range as a whole. It does not create or subdivide structure.
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 border-y border-black/15 py-1.5">
        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
          Scope
        </span>
        <span className="whitespace-normal text-right font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-gray-600">
          {selectedStructuralRows.length
            ? `${getRowLabel(selectedStructuralRows[0])}–${getRowLabel(selectedStructuralRows[selectedStructuralRows.length - 1])} · ${selectedStructuralRows.length}`
            : 'No range selected'}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
            Model
          </span>
          <select
            value={structuralRangeGenerationModelId}
            onChange={event => setStructuralRangeGenerationModelId(
              normalizeLedgerSafeRowTitleModelId(event.target.value)
            )}
            disabled={isStructuralRangeGenerating}
            className="h-8 w-full border border-black bg-white px-2 font-mono text-[8px] font-bold text-black"
          >
            {LEDGER_SAFE_ROW_TITLE_MODEL_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
            Thinking
          </span>
          <select
            value={structuralRangeGenerationThinkingLevel}
            onChange={event => setStructuralRangeGenerationThinkingLevel(
              normalizeLedgerSafeRowTitleThinkingLevel(event.target.value)
            )}
            disabled={isStructuralRangeGenerating}
            className="h-8 w-full border border-black bg-white px-2 font-mono text-[8px] font-bold text-black"
          >
            {LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>
      <button
        type="button"
        onClick={() => void handleGenerateStructuralRanges()}
        disabled={
          isStructuralRangeGenerating
          || isMatrixContentApplying
          || !structuralRangeEditor
          || selectedStructuralRows.length === 0
          || selectedStructuralRows.length > STRUCTURAL_RANGE_GENERATION_MAX_ROWS
        }
        className="mt-3 flex h-9 w-full items-center justify-center gap-2 border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        {isStructuralRangeGenerating
          ? 'Preparing'
          : 'Suggest range title'}
      </button>
      <div className="mt-1 font-mono text-[8px] leading-3 text-gray-400">
        Maximum {STRUCTURAL_RANGE_GENERATION_MAX_ROWS} rows per title request.
      </div>

      {structuralRangeGenerationRun ? (
        <div className="mt-3 border-t border-black pt-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
              Title suggestion
            </span>
            <button
              type="button"
              onClick={() => retainStructuralRangeGenerationRun(null)}
              className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500 hover:text-black"
            >
              Clear
            </button>
          </div>
          <div className="mt-2 border-y border-black/15 py-2">
            <div className="whitespace-normal break-words font-serif text-[12px] font-semibold leading-4 text-black">
              {formatEditorialTitleForDisplay(structuralRangeGenerationRun.suggestion.title)}
            </div>
            <div className="mt-1 font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-gray-600">
              {rowById.get(structuralRangeGenerationRun.target.startRowId)
                ? getRowLabel(rowById.get(structuralRangeGenerationRun.target.startRowId) as AnchoredRow)
                : structuralRangeGenerationRun.target.startRowId}
              –
              {rowById.get(structuralRangeGenerationRun.target.endRowId)
                ? getRowLabel(rowById.get(structuralRangeGenerationRun.target.endRowId) as AnchoredRow)
                : structuralRangeGenerationRun.target.endRowId}
              {structuralRangeGenerationRun.suggestion.confidence !== null
                ? ` · ${Math.round(structuralRangeGenerationRun.suggestion.confidence * 100)}%`
                : ''}
            </div>
          </div>
          {structuralRangeGenerationRun.status === 'ready' ? (
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
              <button
                type="button"
                onClick={handleUseStructuralRangeTitleSuggestion}
                disabled={isMatrixContentApplying || isStructuralRangeGenerating}
                className="h-8 border border-black bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-white hover:bg-gray-800 disabled:bg-gray-300"
              >
                Use title
              </button>
              <button
                type="button"
                onClick={() => retainStructuralRangeGenerationRun(
                  updateLedgerSafeStructuralRangeGenerationRunStatus(structuralRangeGenerationRun, 'dismissed')
                )}
                disabled={isMatrixContentApplying || isStructuralRangeGenerating}
                className="h-8 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black hover:bg-gray-100"
              >
                Dismiss
              </button>
            </div>
          ) : (
            <div className="mt-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
              {structuralRangeGenerationRun.status}
            </div>
          )}
        </div>
      ) : null}
    </section>
    ) : null}

    {structuralRangeError ? (
      <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800">
        {structuralRangeError}
      </div>
    ) : null}
  </div>
  )

  const leftPanelTabs = [
    ...(runtimeEnableCharacterIndex ? [
      { id: 'characters' as const, label: 'Character Index', count: activeCharacters.length },
    ] : []),
    ...(runtimeEnableStructuralRanges && !readOnly ? [
      { id: 'structure' as const, label: 'Structure', count: structuralRangeReviewCount },
    ] : []),
  ]
  const isLeftPanelEnabled = leftPanelTabs.length > 0
  const activeLeftPanelTab = leftPanelTabs.find(tab => tab.id === leftPanelTab) || leftPanelTabs[0]

  const sidePanelTabs = readOnly ? [
    { id: 'map', label: 'Map', count: 0 }
  ] as Array<{ id: SidePanelTab; label: string; count: number }> : [
    { id: 'map', label: 'Map', count: 0 },
    ...(runtimeEnablePropagation ? [
      { id: 'propagation', label: 'Propagation', count: witnessPropagationProposal || contextPropagationPreview || selectedUnitRepairPreview || semanticPreview || crossTraditionCandidates.length > 0 ? 1 : 0 }
    ] as Array<{ id: SidePanelTab; label: string; count: number }> : []),
    ...(runtimeEnableSceneTitles ? [
      {
        id: 'titles',
        label: 'Titles',
        count: (runtimeEnableRowTitleSidecarImport ? rowTitleImportAttentionCount : 0)
          + (runtimeEnableRowTitleGeneration ? readyRowTitleGenerationBatchCount : 0),
      }
    ] as Array<{ id: SidePanelTab; label: string; count: number }> : []),
    ...(runtimeEnablePiDock ? [
      { id: 'inspector', label: 'Inspector', count: piDriftWindows.length }
    ] as Array<{ id: SidePanelTab; label: string; count: number }> : []),
    ...(runtimeEnableLogTab ? [
      { id: 'log', label: 'Log', count: ledgerSnapshots.length + editorialEvents.length + piReviewLog.length }
    ] as Array<{ id: SidePanelTab; label: string; count: number }> : []),
    { id: 'sequence', label: 'Order', count: sequenceAuditIssueCount },
    { id: 'fragments', label: 'Fragments', count: unassignedFragments.length }
  ] as Array<{ id: SidePanelTab; label: string; count: number }>
  const activeSidePanelTab = sidePanelTabs.find(tab => tab.id === sidePanelTab) || sidePanelTabs[0]

  const renderVisibleWitnessFilterPanel = () => (
    <div
      data-testid="ledger-safe-witness-filter-panel"
      role="dialog"
      aria-label="Visible witnesses"
      className="pointer-events-auto absolute right-0 top-full z-[400] mt-2 w-[340px] border border-black bg-white p-3 text-left font-mono text-black shadow-[4px_4px_0_#000]"
      onMouseDown={event => event.stopPropagation()}
      onTouchStart={event => event.stopPropagation()}
    >
      {scopeOptions.length > 1 && !readOnly && (
        <div className="mb-3 border-b border-black/10 pb-3">
          <label
            className="mb-1.5 block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500"
            htmlFor="ledger-safe-scope"
          >
            Scope
          </label>
          <select
            id="ledger-safe-scope"
            aria-label="Scope"
            value={selectedScopeId}
            onChange={event => setSelectedScopeId(event.target.value)}
            className="h-8 w-full border border-black bg-white px-2 text-[10px] font-bold uppercase tracking-[0.12em] text-black"
          >
            {scopeOptions.map(option => (
              <option key={option.clusterId} value={option.clusterId}>
                {formatWitnessCountLabel(option.size)}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 border-b border-black/10 pb-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-black">Visible witnesses</div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500">{visibleScopeWitnesses.length} visible · {scopeWitnesses.length} total</div>
        </div>
        <button
          type="button"
          onClick={handleResetColumnFilters}
          disabled={!canResetColumnFilters}
          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:text-gray-300"
        >
          Reset full view
        </button>
      </div>
      <div className="mt-3 space-y-1">
        {scopeWitnesses.map(witness => {
          const isVisible = visibleScopeWitnessIdSet.has(witness.id)
          const isLastVisible = isVisible && visibleScopeWitnesses.length === 1
          return (
            <label
              key={`column-filter:${witness.id}`}
              className="flex cursor-pointer items-center justify-between gap-3 border border-black/10 px-2 py-2 text-[11px] hover:bg-gray-50"
            >
              <span className="font-bold uppercase tracking-widest text-black">{witness.sigla}</span>
              <input
                type="checkbox"
                aria-label={witness.sigla}
                checked={isVisible}
                disabled={isLastVisible}
                onChange={() => handleToggleWitnessColumn(witness.id)}
                className="h-4 w-4 border-black text-black focus:ring-black disabled:cursor-not-allowed"
              />
            </label>
          )
        })}
      </div>
      <div className="mt-3 border-t border-black/10 pt-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-black">Templates</div>
        <div className="mt-2 flex gap-2">
          <label className="sr-only" htmlFor="ledger-column-template-name">Template name</label>
          <input
            id="ledger-column-template-name"
            aria-label="Template name"
            value={columnFilterTemplateName}
            onChange={event => setColumnFilterTemplateName(event.target.value)}
            className="h-8 flex-1 border border-black px-2 text-[11px]"
            placeholder="Focus London"
          />
          <button
            type="button"
            onClick={handleSaveColumnFilterTemplate}
            disabled={!canSaveColumnFilterTemplate}
            className="h-8 border border-black bg-white px-2 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300"
          >
            Save template
          </button>
        </div>
        <div className="mt-3 space-y-1">
          {columnFilterTemplates.length === 0 ? (
            <div className="text-[10px] uppercase tracking-widest text-gray-400">No saved templates</div>
          ) : columnFilterTemplates.map(template => (
            <div
              key={template.id}
              className="flex items-center justify-between gap-2 border border-black/10 px-2 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-[11px] font-bold uppercase tracking-widest text-black">{template.name}</div>
                <div className="truncate text-[10px] uppercase tracking-widest text-gray-400">{template.scopeLabel}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={`Apply template ${template.name}`}
                  onClick={() => handleApplyColumnFilterTemplate(template)}
                  className="text-[10px] font-bold uppercase tracking-widest text-black hover:text-blue-700"
                >
                  Apply
                </button>
                <button
                  type="button"
                  aria-label={`Delete template ${template.name}`}
                  onClick={() => handleDeleteColumnFilterTemplate(template.id)}
                  className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-black"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const handleMatrixDisplayRowElement = useEventCallback((
    rowId: string,
    isProjectionRow: boolean,
    node: HTMLTableRowElement | null,
  ): void => {
    if (isProjectionRow) {
      return
    }
    if (node) {
      rowElementRefs.current.set(rowId, node)
    } else {
      rowElementRefs.current.delete(rowId)
    }
  })
  const handleFilteredRunRowElement = useEventCallback((
    rowIds: string[],
    node: HTMLTableRowElement | null,
  ): void => {
    rowIds.forEach(rowId => handleMatrixDisplayRowElement(rowId, false, node))
  })
  const handleMatrixStructuralRowHover = useEventCallback((
    rowId: string,
    hovered: boolean,
  ): void => {
    if (!runtimeEnableStructuralRanges || !structuralBoundaryPlacement) {
      return
    }
    setStructuralBoundaryHoverRowId(hovered ? rowId : '')
  })
  const handleMatrixStructuralRowLabelClick = useEventCallback((
    rowId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    if (!runtimeEnableStructuralRanges) {
      return
    }
    const currentRow = rowById.get(rowId)
    if (currentRow) {
      handleStructuralRowLabelClick(currentRow, event)
    }
  })
  const handleStableAnchorFragmentBeforeRow = useEventCallback((rowId: string): void => {
    void handleAnchorFragmentBeforeRow(rowId)
  })
  const handleStableCompactEmptyRows = useEventCallback((rowIds: string[]): void => {
    void handleCompactEmptyRows(rowIds)
  })

  if (scopeOptions.length === 0) {
    return <div className="h-full flex items-center justify-center bg-gray-50 text-sm text-gray-500">Variorum unavailable.</div>
  }

  if (runtimeEnableCameraLucida && ledgerSafeViewMode === 'camera') {
    return (
      <AgentCameraLucidaView
        rows={resolvedRows}
        witnesses={scopeWitnesses}
        chapterId={chapterId}
        characterLayer={runtimeEnableCharacterIndex ? resolvedCharacterLayer : undefined}
        sceneLayer={storedSceneLayer}
        structuralRangeLayer={structuralRangeLayer}
        contextLabel={contextLabel || describeManualStudioScope(activeScope)}
        snapshotId={piSnapshot.snapshotId}
        onInquiry={onCameraInquiry || handleCameraLucidaInquiry}
        piAuth={{
          isAuthenticated: piDockAuthStored,
          isBusy: isPiAuthBusy,
          error: piAuthError,
          attempt: piOAuthLoginAttempt,
          onLogin: handlePiLoginWithChatGPT,
          onSubmitInput: handlePiSubmitOAuthLoginInput,
        }}
        piAgent={{
          current: piAgentSession || piAgentReport?.current || null,
          configuredModel: {
            provider: piAgentModelProvider,
            modelId: piAgentModelId,
          },
          configuredThinkingLevel: piAgentThinkingLevel,
          models: piAgentModels,
          report: piAgentReport,
          isBusy: isPiBusy || isPiAgentRegisterBusy,
          error: piError,
          onRefresh: refreshPiAgentRegister,
          onStart: handleCameraAgentStart,
        }}
        piProposal={piPreview ? {
          proposalId: piPreview.previewId,
          summary: piPreviewSummary || piPreview.summary || 'Pi edit proposal',
          operation: piPreview.operation.kind,
          scope: {
            rowIds: piPreview.affectedRowIds,
            witnessIds: piPreview.affectedWitnessIds,
          },
          before: piPreviewEnvelope?.before,
          after: piPreviewEnvelope?.after,
          idRemap: piPreviewEnvelope?.idRemap,
          unassignedFragments: piPreviewEnvelope?.unassignedFragments,
          steps: piAgentChangeSet?.status === 'awaiting_approval'
            ? piAgentChangeSet.steps.map(step => ({
                index: step.index,
                summary: step.summary,
                operation: step.operation.kind,
                scope: step.scope,
                before: step.before,
                after: step.after,
                idRemap: step.idRemap,
                unassignedFragments: step.unassignedFragments,
              }))
            : undefined,
          isBusy: isPiBusy,
          onApply: handleApplyPiPreview,
          onDiscard: handleRejectPiPreview,
        } : null}
        onClose={() => {
          onCloseCamera?.()
          setLedgerSafeViewMode('ledger')
        }}
      />
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50 text-gray-900">
      <div className="relative z-[300] shrink-0 isolate border-b border-black bg-white shadow-[0_2px_0_#000]">
        <div className="grid min-h-[60px] grid-cols-1 items-center gap-x-4 gap-y-2 bg-black px-4 py-2.5 text-white md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <div className="min-w-0 justify-self-start">
            <div
              data-testid="ledger-safe-product-title"
              className="flex min-h-[28px] items-center text-[13px] font-bold uppercase tracking-widest"
            >
              <span className="sr-only">VARIORUM Fluid Textual Traditions Collation Environment</span>
              <span aria-hidden="true" className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 leading-none">
                <span className="font-serif text-[17px] leading-none tracking-[0.18em] text-[#f7f3ea]">VARIORUM</span>
                <span className="hidden h-[18px] w-px self-center bg-white/35 sm:inline-block" />
                <span className="font-mono text-[9.5px] leading-none tracking-[0.22em] text-[#c7b36f]">FLUID TEXTUAL TRADITIONS</span>
                <span className="font-mono text-[9.5px] leading-none tracking-[0.22em] text-[#b7c6d8]">COLLATION ENVIRONMENT</span>
              </span>
            </div>
          </div>
          <div data-testid="ledger-safe-masthead-center" className="flex min-h-[28px] min-w-0 items-center justify-center justify-self-center text-center">
            {(projectLabel || contextLabel) && (
              <div className="max-w-[520px] truncate font-mono text-[12px] font-bold uppercase tracking-[0.16em] text-[#d6d0c2]">
                {[projectLabel, contextLabel].filter(Boolean).join(' / ')}
              </div>
            )}
          </div>
          <div data-testid="ledger-safe-masthead-vitals" className="flex min-w-0 flex-wrap items-center justify-start gap-0 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-white/60 md:justify-end">
            <div
              data-testid="ledger-safe-witness-count-control"
              className={`relative flex min-h-[32px] items-center gap-2 border-l border-white/15 px-3 first:border-l-0 ${isColumnFilterDialogOpen ? 'z-[260]' : ''}`}
            >
              {!readOnly ? (
                <button
                  type="button"
                  aria-label={`${mastheadWitnessCountLabel}; open visible witnesses`}
                  aria-expanded={isColumnFilterDialogOpen}
                  onClick={() => setIsColumnFilterDialogOpen(value => !value)}
                  className={`inline-flex min-h-[32px] items-center gap-1.5 border px-2 font-mono text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${isWitnessFilterActive ? 'border-amber-300/70 bg-amber-300/10 text-amber-200 hover:bg-amber-300/15' : 'border-white/20 bg-transparent text-white/60 hover:bg-white/10 hover:text-white'}`}
                >
                  <span className={`text-[13px] ${isWitnessFilterActive ? 'text-amber-100' : 'text-white'}`}>
                    {isWitnessFilterActive ? `${visibleScopeWitnesses.length}/${scopeWitnesses.length}` : scopeWitnesses.length}
                  </span>
                  <span>{scopeWitnesses.length === 1 ? 'witness' : 'witnesses'}</span>
                </button>
              ) : (
                <span aria-label="Scope" className="text-white">{currentWitnessCountLabel}</span>
              )}
              {isColumnFilterDialogOpen && renderVisibleWitnessFilterPanel()}
            </div>
            <div className="flex min-h-[32px] items-center gap-1 border-l border-white/15 px-3">
              <span className="text-[13px] text-white">{resolvedRows.length}</span>
              <span>rows</span>
              {ledgerSafeViewMode === 'ledger' && hiddenWitnessCollapsedRowCount > 0 && (
                <span className="ml-1 text-amber-200">· {hiddenWitnessCollapsedRowCount} collapsed</span>
              )}
            </div>
            <div className="flex min-h-[32px] items-center gap-1 border-l border-white/15 px-3">
              <span className={readOnly ? 'text-white/75' : 'text-[13px] text-white'}>{readOnly ? 'read-only' : boundaryEditableCount}</span>
              {!readOnly && <span>boundaries</span>}
            </div>
            {(mastheadActions || headerActions || mastheadSupplementalActions) && (
              <div
                data-testid="ledger-safe-masthead-actions"
                className={`relative flex min-h-[32px] items-center gap-2 border-l border-white/15 pl-3 ${isLedgerActionsMenuOpen ? 'z-[260]' : ''}`}
              >
                {mastheadActions}
                {headerActions && (
                  <div
                    data-testid="ledger-safe-actions-popover-anchor"
                    ref={ledgerActionsMenuRef}
                    className={`relative flex items-center ${isLedgerActionsMenuOpen ? 'z-[260]' : ''}`}
                  >
                    <button
                      type="button"
                      aria-label="Collation actions"
                      aria-expanded={isLedgerActionsMenuOpen}
                      title="Collation actions"
                      onClick={() => setIsLedgerActionsMenuOpen(value => !value)}
                      className="inline-flex h-9 w-9 items-center justify-center border border-black bg-white text-black shadow-[2px_2px_0_#000] hover:bg-gray-100 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                    >
                      <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {isLedgerActionsMenuOpen && (
                      <div
                        data-testid="ledger-safe-actions-panel"
                        role="menu"
                        aria-label="Collation actions"
                        className="pointer-events-auto absolute right-0 top-full z-[400] mt-2 min-w-[220px] border border-black bg-white p-2 shadow-[4px_4px_0_#000]"
                        onMouseDown={event => event.stopPropagation()}
                        onTouchStart={event => event.stopPropagation()}
                        onClick={event => {
                          const target = event.target
                          if (target instanceof HTMLElement && target.closest('button,a,[role="menuitem"]')) {
                            setIsLedgerActionsMenuOpen(false)
                          }
                        }}
                      >
                        <div className="flex flex-col gap-1 [&>*]:w-full [&>*]:justify-start">
                          {headerActions}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {mastheadSupplementalActions}
              </div>
            )}
          </div>
        </div>
        <div
          data-testid="ledger-safe-control-strip"
          className="relative z-30 grid min-h-[48px] grid-cols-1 items-center gap-x-4 gap-y-2 overflow-visible border-b-2 border-black bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
        >
          {readOnly ? (
            <>
              {ledgerName && (
                <div data-testid="ledger-safe-ledger-name-indicator" className="inline-flex min-h-[32px] max-w-full min-w-0 items-center justify-self-start gap-2 border border-black bg-white px-3 text-left font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 shadow-[2px_2px_0_#000]">
                  <span className="shrink-0">collation version</span>
                  <span className="min-w-0 whitespace-normal break-words text-[13px] leading-tight text-black" title={ledgerName}>{ledgerName}</span>
                </div>
              )}
              <div className="inline-flex items-center gap-[7px] justify-self-center border border-black bg-white px-3 font-mono shadow-[2px_2px_0_#000] md:col-start-2">
                <span className="font-mono text-[8px] font-bold uppercase tracking-[0.2em] text-gray-400">Status</span>
                <span className="inline-flex h-8 items-center whitespace-nowrap px-2 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black">
                  Read-only preview
                </span>
              </div>
              <div data-testid="ledger-active-witness-indicator" className="inline-flex min-h-[32px] min-w-0 items-center justify-self-end gap-2 border border-black bg-white px-3 text-right font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 shadow-[2px_2px_0_#000] md:col-start-3">
                <span>active witness</span>
                <span className="max-w-[240px] truncate text-[13px] text-emerald-800">{activeWitnessPresentation?.sigla || 'none'}</span>
              </div>
            </>
          ) : (
            <>
              {ledgerName && (
                <div data-testid="ledger-safe-ledger-name-indicator" className="inline-flex min-h-[32px] max-w-full min-w-0 items-center justify-self-start gap-2 border border-black bg-white px-3 text-left font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 shadow-[2px_2px_0_#000]">
                  <span className="shrink-0">collation version</span>
                  <span className="min-w-0 whitespace-normal break-words text-[13px] leading-tight text-black" title={ledgerName}>{ledgerName}</span>
                </div>
              )}
              <div className="inline-flex min-w-0 items-center justify-self-center gap-2 md:col-start-2">
                <div
                  data-testid="ledger-safe-view-group"
                  data-diff-state={diffEnabled ? 'open' : 'closed'}
                  className={`inline-flex min-w-0 items-center justify-self-center border border-black bg-white font-mono shadow-[2px_2px_0_#000] transition-[max-width,transform,background-color] duration-200 ease-out md:col-start-2 ${diffEnabled ? 'max-w-[720px]' : 'max-w-[168px]'}`}
                >
                <button
                  type="button"
                  onClick={() => setDiffEnabled(value => !value)}
                  disabled={isMatrixContentApplying}
                  aria-label="Toggle comparison view"
                  className={`inline-flex h-8 shrink-0 items-center whitespace-nowrap px-3 text-[9px] font-bold uppercase tracking-[0.1em] transition-colors disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 ${diffEnabled ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
                >
                  Comparison View {diffEnabled ? 'On' : 'Off'}
                </button>
                {diffEnabled && (
                  <div data-testid="ledger-safe-diff-options" className="inline-flex h-8 min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap border-l border-black/20 bg-white px-2">
                    <label className="sr-only" htmlFor="ledger-safe-diff-mode">Comparison mode</label>
                    <select
                      id="ledger-safe-diff-mode"
                      aria-label="Comparison mode"
                      value={diffMode}
                      onChange={event => setDiffMode(event.target.value as DiffMode)}
                      disabled={isMatrixContentApplying}
                      className="h-6 max-w-[96px] border-0 bg-transparent p-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-black focus:outline-none"
                    >
                      <option value="inline">Inline</option>
                      <option value="apparatus">Apparatus</option>
                    </select>
                    <span className="font-mono text-[8.5px] font-bold text-black">·</span>
                    <label className="sr-only" htmlFor="ledger-safe-diff-pivot">Comparison reference</label>
                    <select
                      id="ledger-safe-diff-pivot"
                      aria-label="Comparison reference"
                      value={diffPivotSigla}
                      onChange={event => handleSetDiffPivotSigla(event.target.value)}
                      disabled={isMatrixContentApplying || diffScopeWitnesses.length === 0}
                      className="h-6 max-w-[132px] border-0 bg-transparent p-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-black focus:outline-none"
                    >
                      {diffScopeWitnesses.length === 0 && (
                        <option value="">No Arabic witnesses</option>
                      )}
                      {diffScopeWitnesses.map(witness => (
                        <option key={witness.id} value={witness.sigla}>{witness.sigla}</option>
                      ))}
                    </select>
                    <span className="font-mono text-[8.5px] font-bold text-black">·</span>
                    {diffMode === 'inline' ? (
                      <>
                        <label className="sr-only" htmlFor="ledger-safe-diff-appearance">Comparison appearance</label>
                        <select
                          id="ledger-safe-diff-appearance"
                          aria-label="Comparison appearance"
                          value={diffAppearance}
                          onChange={event => setDiffAppearance(event.target.value as DiffAppearance)}
                          disabled={isMatrixContentApplying}
                          className="h-6 max-w-[92px] border-0 bg-transparent p-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-black focus:outline-none"
                        >
                          <option value="subtle">Subtle</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="arabic">Arabic</option>
                        </select>
                      </>
                    ) : (
                      <>
                        <label className="sr-only" htmlFor="ledger-safe-apparatus-scope">Apparatus scope</label>
                        <select
                          id="ledger-safe-apparatus-scope"
                          aria-label="Apparatus scope"
                          value={apparatusScope}
                          onChange={event => setApparatusScope(event.target.value as ApparatusScope)}
                          disabled={isMatrixContentApplying}
                          className="h-6 max-w-[120px] border-0 bg-transparent p-0 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-black focus:outline-none"
                        >
                          <option value="chapter">Chapter</option>
                          <option value="row">Selected row</option>
                        </select>
                        {apparatusScope === 'row' && (
                          <span className="max-w-[116px] truncate bg-white/70 px-1.5 py-0.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-gray-700">
                            {selectedLedgerRow ? getRowLabel(selectedLedgerRow) : 'Select row'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={handleExportApparatusDiff}
                          disabled={apparatusScope === 'row' && !selectedLedgerRow}
                          className="h-6 border border-black bg-white px-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.08em] text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          Export JSON
                        </button>
                      </>
                    )}
                  </div>
                )}
                </div>
                {runtimeEnableCameraLucida && (
                  <button
                    type="button"
                    data-testid="ledger-safe-open-camera-lucida"
                    onClick={() => {
                      onOpenCamera?.()
                      setLedgerSafeViewMode('camera')
                      setIsColumnFilterDialogOpen(false)
                      setIsLedgerActionsMenuOpen(false)
                    }}
                    className="inline-flex h-8 shrink-0 items-center border border-black bg-white px-3 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                  >
                    Camera Lucida
                  </button>
                )}
                {fragmentReviewTargetRowIds.size > 0 && (
                  <button
                    type="button"
                    data-testid="ledger-fragment-review-toggle"
                    aria-pressed={isFragmentReviewMode ? 'true' : 'false'}
                    onClick={() => setIsFragmentReviewMode(value => !value)}
                    className={`inline-flex h-8 shrink-0 items-center gap-1.5 border border-black px-2 font-mono text-[8.5px] font-bold uppercase tracking-[0.12em] shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${isFragmentReviewMode ? 'bg-sky-100 text-sky-900' : 'bg-white text-black hover:bg-gray-100'}`}
                  >
                    <span>{isFragmentReviewMode ? 'Review On' : 'Review Fragments'}</span>
                    <span className="border border-black/20 bg-white px-1 text-[8px] text-black">
                      {fragmentReviewTargetRowIds.size}
                    </span>
                  </button>
                )}
              </div>
              <div data-testid="ledger-active-witness-indicator" className="inline-flex min-h-[32px] min-w-0 items-center justify-self-end gap-2 border border-black bg-white px-3 text-right font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500 shadow-[2px_2px_0_#000] md:col-start-3">
                <span>active witness</span>
                <span className="max-w-[240px] truncate text-[13px] text-emerald-800">{activeWitnessPresentation?.sigla || 'none'}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {runtimeEnableTraditionReader && ledgerSafeViewMode === 'tradition' && activeTraditionSourceWitness ? (
        <div className="min-h-0 flex-1 overflow-hidden bg-[#e8e8e8] p-[18px]">
          <LedgerSafeTraditionReaderWithStructure
            readOnly={readOnly}
            rows={resolvedRows}
            witnesses={scopeWitnesses}
            sourceWitnessId={activeTraditionSourceWitness.id}
            textLookupByWitnessId={traditionWitnessUnitTextLookupByWitnessId}
            contextLabel={contextLabel || describeManualStudioScope(activeScope)}
            initialResumeRowId={initialReaderResumeTarget?.rowId}
            initialResumeEvidenceId={initialReaderResumeTarget?.evidenceId}
            initialResumeEvidenceKind={initialReaderResumeTarget?.evidenceKind}
            characterIndexEnabled={runtimeEnableCharacterIndex}
            characterIndexEntries={runtimeEnableCharacterIndex ? characterIndexRunningIndex.entries : []}
            characterOccurrences={runtimeEnableCharacterIndex ? resolvedCharacterLayer.occurrences : []}
            characterLayerNotes={runtimeEnableCharacterIndex ? resolvedCharacterLayer.notes : []}
            characterLayer={runtimeEnableCharacterIndex ? resolvedCharacterLayer : undefined}
            characterCoverageById={runtimeEnableCharacterIndex ? characterCoverageById : undefined}
            selectedCharacterId={runtimeEnableCharacterIndex ? selectedCharacterId : ''}
            characterIndexControlPanel={runtimeEnableCharacterIndex && !readOnly ? (
              <article data-testid="reader-character-index-control-panel" className="cte-card ledger-tradition-source-card">
                <div className="cte-card-head">
                  <span>
                    Character Index · {activeTraditionSourceWitness.sigla}
                    <small>source witness controls</small>
                  </span>
                  <span className="cte-tier">{characterDetectionRows.length}</span>
                </div>
                <div className="cte-card-score">
                  <span>Source · {characterExtractionWitness?.sigla || activeTraditionSourceWitness.sigla}</span>
                  <span className="cte-score-bar"><i style={{ width: `${characterDetectionRows.length > 0 ? 100 : 0}%` }} /></span>
                  <span>{characterDetectionRows.length} rows</span>
                </div>
                <div className="ledger-character-scope-tabs" role="group" aria-label="Reader character extraction unit">
                  {([
                    ['witness', 'Witness'],
                    ['scene', 'Scene'],
                    ['rows', 'Rows'],
                  ] as Array<[CharacterExtractionUnit, string]>).map(([unit, label]) => {
                    const isActive = characterExtractionUnit === unit
                    const isDisabled = isCharacterDetectorRunning || unit !== 'witness'
                    return (
                      <button
                        key={unit}
                        type="button"
                        data-testid={`reader-character-index-extraction-unit-${unit}`}
                        className={isActive ? 'active' : ''}
                        aria-pressed={isActive ? 'true' : 'false'}
                        disabled={isDisabled}
                        title={unit === 'witness' ? `Use ${activeTraditionSourceWitness.sigla}` : 'Use the Ledger panel for scene or selected-row scopes'}
                        onClick={() => {
                          setCharacterExtractionUnit('witness')
                          setCharacterExtractionWitnessId(activeTraditionSourceWitness.id)
                          setCharacterDetectorFeedback(null)
                        }}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
                <div className="mt-3 space-y-2">
                  <label className="block">
                    <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">Detector</span>
                    <select
                      data-testid="reader-character-index-detector-select"
                      value={characterDetectorId}
                      onChange={event => {
                        setCharacterDetectorId(event.target.value)
                        setCharacterExtractionUnit('witness')
                        setCharacterExtractionWitnessId(activeTraditionSourceWitness.id)
                        setCharacterDetectorFeedback(null)
                      }}
                      disabled={isCharacterDetectorRunning}
                      className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      {characterDetectorAdapters.map(adapter => (
                        <option key={`reader-character-detector-${adapter.id}`} value={adapter.id}>
                          {adapter.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selectedCharacterDetectorUsesModel ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      <label className="col-span-3 min-w-0">
                        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">Model</span>
                        <select
                          data-testid="reader-character-index-model-select"
                          value={characterModelId}
                          onChange={event => setCharacterModelId(event.target.value)}
                          disabled={isCharacterDetectorRunning}
                          className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          {CHARACTER_INDEX_MODEL_OPTIONS.map(model => (
                            <option key={`reader-character-model-${model.id}`} value={model.id}>
                              {model.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="col-span-1 min-w-0">
                        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">Temp</span>
                        <input
                          data-testid="reader-character-index-temperature-input"
                          type="number"
                          min={0}
                          max={2}
                          step={0.1}
                          value={characterTemperature}
                          onChange={event => {
                            const nextValue = Number(event.target.value)
                            if (Number.isFinite(nextValue)) {
                              setCharacterTemperature(Math.max(0, Math.min(2, nextValue)))
                            }
                          }}
                          disabled={isCharacterDetectorRunning}
                          className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                        />
                      </label>
                      <label className="col-span-2 min-w-0">
                        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">Thinking</span>
                        <select
                          data-testid="reader-character-index-thinking-select"
                          value={characterThinkingLevel}
                          onChange={event => setCharacterThinkingLevel(event.target.value as CharacterThinkingLevel)}
                          disabled={isCharacterDetectorRunning}
                          className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-black/15 bg-white px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-500">
                      Name sweep ignores model controls.
                    </div>
                  )}
                  {selectedCharacter && (
                    <>
                      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                        <span className="border border-black bg-black px-1.5 py-0.5 text-white">Close reading</span>
                        <span>{selectedCharacter.display_name}</span>
                        <button
                          type="button"
                          data-testid="reader-character-index-improve-chain"
                          title={characterDetectorCanImproveSelectedChain ? 'Improve this character chain with the Reader source witness' : focusedCharacterValidationMessage || 'Select a character and source scope first'}
                          onClick={handleImproveSelectedCharacterChain}
                          disabled={!characterDetectorCanImproveSelectedChain}
                          className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                        >
                          Improve chain
                        </button>
                        <button
                          type="button"
                          data-testid="reader-character-index-refine-character-layers"
                          title={characterDetectorCanRefineSelectedLayers ? 'Extract this character layer set with the Reader source witness' : focusedCharacterLayerValidationMessage || 'Select a character and source scope first'}
                          onClick={handleRefineSelectedCharacterLayers}
                          disabled={!characterDetectorCanRefineSelectedLayers}
                          className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                        >
                          Refine layers
                        </button>
                        {focusedCharacterLayerExternalModelPayload && (
                          <button
                            type="button"
                            data-testid="reader-character-index-copy-focused-refine-prompt"
                            title="Copy the exact focused focalization and characterization prompt without starting a run"
                            onClick={() => void handleCopyFocusedCharacterLayerPrompt()}
                            disabled={!canCopyFocusedCharacterLayerPrompt}
                            className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                          >
                            Copy refine prompt
                          </button>
                        )}
                      </div>
                      <div
                        data-testid="reader-character-index-focused-layer-refine-status"
                        className="border border-black/15 bg-gray-50 px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-500"
                      >
                        {focusedCharacterLayerRefineStatusLabel}
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      data-testid="reader-character-index-suggest-entries"
                      onClick={() => void handleRunCharacterDetector('suggest')}
                      disabled={isCharacterDetectorRunning || characterDetectionRows.length === 0 || Boolean(characterExtractionValidationMessage) || (selectedCharacterDetectorNeedsEntries && characterDetectorEligibleCount === 0)}
                      className="h-8 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:shadow-none"
                    >
                      {characterDetectorRunMode === 'suggest' ? 'Running...' : 'Start'}
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-cancel-run"
                      onClick={handleCancelCharacterDetectorRun}
                      disabled={!characterDetectorCanCancel}
                      className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-resume-run"
                      onClick={handleResumeCharacterDetectorRun}
                      disabled={!characterDetectorCanResume}
                      className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Resume
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-retry-run"
                      onClick={handleRetryCharacterDetectorRun}
                      disabled={!characterDetectorCanRetry}
                      className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-refine-entries"
                      title={characterDetectorCanRefine ? 'Refine the last model output against the Reader source witness' : 'Run a model suggestion first'}
                      onClick={() => void handleRunCharacterDetector('refine')}
                      disabled={!characterDetectorCanRefine}
                      className="col-span-2 h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      {characterDetectorRunMode === 'refine' ? 'Refining...' : 'Refine'}
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-copy-model-payload"
                      title={characterExternalModelPayload ? 'Copy the exact prompt text that will be sent to the model' : 'Select a prepared model extraction first'}
                      onClick={() => void handleCopyCharacterModelPayload()}
                      disabled={isCharacterDetectorRunning || !characterExternalModelPayload || Boolean(characterExternalModelExchangeContext?.validationMessage)}
                      className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Copy prompt
                    </button>
                    <button
                      type="button"
                      data-testid="reader-character-index-paste-model-result"
                      title="Paste a model JSON result through the copied Character Index prompt context"
                      onClick={() => void handlePasteCharacterExternalModelResult()}
                      disabled={isCharacterDetectorRunning}
                      className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                    >
                      Paste result
                    </button>
                  </div>
                  <div className={`border px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.12em] ${characterDetectorFeedbackClass}`}>
                    {characterDetectorFeedback?.message || characterExtractionValidationMessage || characterDetectorReadyLabel}
                  </div>
                  {characterDetectorRunHistory.length > 0 && (
                    <div className="border border-black/20 bg-white p-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">Run history</div>
                        <div className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                          {characterDetectorRunHistory.length} attempt{characterDetectorRunHistory.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="mt-1 space-y-1">
                        {characterDetectorRunHistory.slice(0, 3).map(run => {
                          const isSelectedRun = selectedCharacterDetectorRun?.runId === run.runId
                          const resultSummary = run.resultSet
                            ? run.provenance.detectorId === 'llm-focalization-characterization'
                              ? `${run.resultSet.noteCount} notes · ${run.resultSet.addedNoteCount || 0} added`
                              : `${run.resultSet.occurrenceCount} occ · ${run.resultSet.addedOccurrenceCount} added`
                            : run.errorMessage || run.provenance.sourceLabel
                          return (
                            <button
                              key={`reader-character-run-${run.runId}`}
                              type="button"
                              data-testid={`reader-character-index-run-history-${run.runId}`}
                              onClick={() => setSelectedCharacterDetectorRunId(run.runId)}
                              className={`w-full border px-2 py-1.5 text-left font-mono text-[8px] font-bold uppercase tracking-[0.1em] ${
                                isSelectedRun
                                  ? 'border-black bg-black text-white'
                                  : `${getCharacterRunStatusClass(run.status)} hover:border-black`
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span>{formatCharacterRunTime(run.startedAt)} · {run.status}</span>
                                <span>{run.provenance.windowCount > 1 ? `${run.provenance.windowCount} win` : `${run.provenance.rowCount} rows`}</span>
                              </div>
                              <div className={`mt-0.5 truncate ${isSelectedRun ? 'text-white/70' : 'text-gray-500'}`}>
                                {run.provenance.detectorLabel} · {resultSummary}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ) : null}
            onAdjudicateCharacterEvidence={runtimeEnableCharacterIndex && !readOnly
              ? handleReaderAdjudicationCommand
              : undefined}
            onCreateCharacter={runtimeEnableCharacterIndex && !readOnly
              ? handleCreateReaderCharacter
              : undefined}
            onRenameCharacter={runtimeEnableCharacterIndex && !readOnly
              ? handleRenameReaderCharacter
              : undefined}
            onDeleteCharacter={runtimeEnableCharacterIndex && !readOnly
              ? handleDeleteReaderCharacter
              : undefined}
            onSelectCharacter={characterId => {
              setSelectedCharacterId(characterId)
              setSelectedCharacterOccurrenceId('')
            }}
            onClose={() => {
              setSelectedCharacterId('')
              setSelectedCharacterOccurrenceId('')
              if (onCloseReader) {
                onCloseReader()
                return
              }
              setLedgerSafeViewMode('ledger')
            }}
          />
        </div>
      ) : (
      <div
        data-testid="ledger-safe-ledger-grid"
        className="relative grid min-h-0 flex-1 overflow-hidden transition-[grid-template-columns] duration-200 ease-out"
        style={{
          gridTemplateColumns: isLeftPanelEnabled
            ? `${isLeftPanelCollapsed ? '52px' : '320px'} minmax(0, 1fr) ${isSidePanelCollapsed ? '52px' : '390px'}`
            : `minmax(0, 1fr) ${isSidePanelCollapsed ? '52px' : '390px'}`
        }}
      >
        {isLeftPanelEnabled && (
        <aside
          data-testid="ledger-left-editorial-panel"
          data-collapsed={isLeftPanelCollapsed ? 'true' : 'false'}
          className="relative flex h-full min-h-0 flex-col border-r border-black bg-white transition-colors duration-200"
        >
          <button
            type="button"
            data-testid="ledger-left-panel-edge-toggle"
            aria-label={isLeftPanelCollapsed ? 'Expand left panel' : 'Collapse left panel'}
            aria-expanded={isLeftPanelCollapsed ? 'false' : 'true'}
            aria-controls={`ledger-left-panel-tabpanel-${leftPanelTab}`}
            title={isLeftPanelCollapsed ? 'Expand left panel' : 'Collapse left panel'}
            onClick={() => setIsLeftPanelCollapsed(previous => !previous)}
            className="absolute -right-[15px] top-1/2 z-[140] flex h-8 w-7 -translate-y-1/2 items-center justify-center border border-black bg-white text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-x-[2px] active:translate-y-[calc(-50%+2px)] active:shadow-none"
          >
            {isLeftPanelCollapsed ? (
              <PanelLeftOpen className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
          {isLeftPanelCollapsed ? (
            <div data-testid="ledger-left-panel-collapsed" className="flex h-full min-h-0 flex-col items-center bg-white px-1.5 py-2">
              <button
                type="button"
                onClick={() => setIsLeftPanelCollapsed(false)}
                aria-label={`${activeLeftPanelTab?.label || 'Left panel'}, ${activeLeftPanelTab?.count || 0}`}
                className={`flex h-8 w-8 items-center justify-center border border-black font-mono text-[8px] font-bold uppercase tracking-normal transition-colors ${
                  (leftPanelTab === 'characters' && selectedCharacter) || (leftPanelTab === 'structure' && structuralRangeReviewCount > 0)
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {activeLeftPanelTab?.count || 0}
              </button>
              <div className="my-3 h-px w-7 bg-black/20" />
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <div
                  className="max-h-[220px] rotate-180 truncate whitespace-nowrap font-mono text-[9px] font-bold uppercase tracking-normal text-gray-600"
                  style={{ writingMode: 'vertical-rl' }}
                  title={leftPanelTab === 'characters' && selectedCharacter
                    ? `Reading ${selectedCharacter.display_name}`
                    : activeLeftPanelTab?.label || 'Left panel'}
                >
                  {leftPanelTab === 'characters' && selectedCharacter
                    ? `Reading ${selectedCharacter.display_name}`
                    : activeLeftPanelTab?.label || 'Left panel'}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div
                role="tablist"
                aria-label="Left editorial panels"
                className="grid shrink-0 border-b border-black bg-black gap-px"
                style={{ gridTemplateColumns: `repeat(${leftPanelTabs.length}, minmax(0, 1fr))` }}
              >
                {leftPanelTabs.map(tab => {
                  const isActiveTab = leftPanelTab === tab.id
                  return (
                    <button
                      key={tab.id}
                      id={`ledger-left-panel-tab-${tab.id}`}
                      type="button"
                      role="tab"
                      aria-selected={isActiveTab}
                      aria-controls={`ledger-left-panel-tabpanel-${tab.id}`}
                      onClick={() => setLeftPanelTab(tab.id)}
                      className={`flex min-h-9 items-center justify-center gap-1.5 px-2 font-mono text-[8px] font-bold uppercase tracking-[0.1em] transition-colors ${
                        isActiveTab
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{tab.label}</span>
                      {tab.count > 0 ? (
                        <span className={`border px-1 py-0.5 text-[7px] ${
                          isActiveTab ? 'border-white/40 text-white' : 'border-black/20 text-gray-500'
                        }`}>
                          {tab.count}
                        </span>
                      ) : null}
                    </button>
                  )
                })}
              </div>
              {leftPanelTab === 'characters' && runtimeEnableCharacterIndex ? (
                <>
              <div className="shrink-0 border-b border-black bg-white px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Character index</div>
                    <div className="mt-1 truncate font-serif text-[15px] font-bold text-black">
                      {selectedCharacter ? selectedCharacter.display_name : `${activeCharacters.length} characters`}
                    </div>
                  </div>
                  {selectedCharacter && (
                    <button
                      type="button"
                      onClick={() => setSelectedCharacterId('')}
                      className="h-7 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {selectedCharacter && (
                  <>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                      <span className="border border-black bg-black px-1.5 py-0.5 text-white">
                        Close reading
                      </span>
                      <span>
                        {selectedCharacterOccurrences.length} occurrence{selectedCharacterOccurrences.length === 1 ? '' : 's'}
                      </span>
                      <span>
                        {selectedCharacterRowIdSet.size} row{selectedCharacterRowIdSet.size === 1 ? '' : 's'}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          data-testid="ledger-character-index-improve-chain"
                          title={characterDetectorCanImproveSelectedChain ? 'Improve this character chain with the current source controls' : focusedCharacterValidationMessage || 'Select a character and source scope first'}
                          onClick={handleImproveSelectedCharacterChain}
                          disabled={!characterDetectorCanImproveSelectedChain}
                          className="ml-auto shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                        >
                          Improve chain
                        </button>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          data-testid="ledger-character-index-refine-character-layers"
                          title={characterDetectorCanRefineSelectedLayers ? 'Extract only this character’s focalization and characterization spans with the current source controls' : focusedCharacterLayerValidationMessage || 'Select a character and source scope first'}
                          onClick={handleRefineSelectedCharacterLayers}
                          disabled={!characterDetectorCanRefineSelectedLayers}
                          className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                        >
                          Refine layers
                        </button>
                      )}
                      {!readOnly && focusedCharacterLayerExternalModelPayload && (
                        <button
                          type="button"
                          data-testid="ledger-character-index-copy-focused-refine-prompt"
                          title="Copy the exact focused focalization and characterization prompt without starting a run"
                          onClick={() => void handleCopyFocusedCharacterLayerPrompt()}
                          disabled={!canCopyFocusedCharacterLayerPrompt}
                          className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[1px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                        >
                          Copy refine prompt
                        </button>
                      )}
                    </div>
                    <div
                      data-testid="ledger-character-index-focused-layer-refine-status"
                      className="mt-2 border border-black/15 bg-gray-50 px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-500"
                    >
                      {focusedCharacterLayerRefineStatusLabel}
                    </div>
                  </>
                )}
              </div>

              <div
                id="ledger-left-panel-tabpanel-characters"
                data-testid="ledger-character-index-panel-body"
                role="tabpanel"
                aria-labelledby="ledger-left-panel-tab-characters"
                className="ledger-safe-scrollbar min-h-0 flex-1 overflow-y-auto p-3"
              >
                {!readOnly && (
                  <div className="border border-black bg-white p-2 shadow-[2px_2px_0_#000]">
                    <div className="flex items-center justify-between gap-2">
                      <label htmlFor="ledger-character-index-detector" className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
                        Suggestions
                      </label>
                      <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                        {characterDetectionRows.length} rows
                      </span>
                    </div>
                    <div className="mt-2 space-y-2">
                      <div>
                        <label htmlFor="ledger-character-index-detector" className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                          Detector
                        </label>
                        <select
                          id="ledger-character-index-detector"
                          data-testid="ledger-character-index-detector-select"
                          value={characterDetectorId}
                          onChange={event => {
                            setCharacterDetectorId(event.target.value)
                            setCharacterDetectorFeedback(null)
                          }}
                          disabled={isCharacterDetectorRunning}
                          className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          {characterDetectorAdapters.map(adapter => (
                            <option key={adapter.id} value={adapter.id}>
                              {adapter.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                            Unit
                          </div>
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                            {characterExtractionUnitLabel}
                          </div>
                        </div>
                        <div
                          role="group"
                          aria-label="Character extraction unit"
                          data-testid="ledger-character-index-extraction-unit-control"
                          className="mt-1 grid grid-cols-2 border border-black bg-black gap-px"
                        >
                          {([
                            ['visible-ledger', 'Ledger'],
                            ['witness', 'Witness'],
                            ['scene', 'Scene'],
                            ['rows', 'Rows'],
                          ] as Array<[CharacterExtractionUnit, string]>).map(([unit, label]) => {
                            const isActive = characterExtractionUnit === unit
                            const isUnitDisabled = isCharacterDetectorRunning || (
                              selectedCharacterDetectorRequiresPreparedExtraction && unit === 'visible-ledger'
                            )
                            return (
                              <button
                                key={unit}
                                type="button"
                                aria-pressed={isActive ? 'true' : 'false'}
                                data-testid={`ledger-character-index-extraction-unit-${unit}`}
                                title={selectedCharacterDetectorRequiresPreparedExtraction && unit === 'visible-ledger'
                                  ? 'Coreference chains use a single witness scope'
                                  : label}
                                onClick={() => {
                                  setCharacterExtractionUnit(unit)
                                  setCharacterDetectorFeedback(null)
                                }}
                                disabled={isUnitDisabled}
                                className={`h-7 px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ${
                                  isActive
                                    ? 'bg-black text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {label}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      <div className="border border-black/20 bg-gray-50 p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                            Source
                          </div>
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                            {characterDetectionRows.length} row{characterDetectionRows.length === 1 ? '' : 's'}
                          </div>
                        </div>
                        {characterExtractionUnit === 'visible-ledger' ? (
                          <div className="mt-1 border border-black/15 bg-white px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-600">
                            Visible witnesses · {scopeWitnesses.length}
                          </div>
                        ) : characterExtractionUnit === 'rows' ? (
                          <div className="mt-1 border border-black/15 bg-white px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-600">
                            {characterExtractionRowSelection.rowIds.length
                              ? `${scopeWitnessById.get(characterExtractionEffectiveWitnessId)?.sigla || 'Witness'} · ${characterExtractionRowSelection.rowIds.length} selected`
                              : 'Select a cell or range'}
                          </div>
                        ) : (
                          <div className="mt-1 space-y-1.5">
                            <select
                              id="ledger-character-index-extraction-witness"
                              data-testid="ledger-character-index-extraction-witness-select"
                              aria-label="Extraction witness"
                              value={characterExtractionEffectiveWitnessId}
                              onChange={event => {
                                setCharacterExtractionWitnessId(event.target.value)
                                setCharacterDetectorFeedback(null)
                              }}
                              disabled={isCharacterDetectorRunning}
                              className="h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                            >
                              {scopeWitnesses.map(witness => (
                                <option key={witness.id} value={witness.id}>
                                  {witness.sigla}
                                </option>
                              ))}
                            </select>
                            {characterExtractionUnit === 'scene' && (
                              <select
                                id="ledger-character-index-extraction-scene"
                                data-testid="ledger-character-index-extraction-scene-select"
                                aria-label="Extraction scene"
                                value={characterExtractionSceneId}
                                onChange={event => {
                                  setCharacterExtractionSceneId(event.target.value)
                                  setCharacterDetectorFeedback(null)
                                }}
                                disabled={isCharacterDetectorRunning || characterExtractionSceneOptions.length === 0}
                                className="h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                {characterExtractionSceneOptions.map(scene => (
                                  <option key={scene.sceneId} value={scene.sceneId}>
                                    {formatEditorialTitleForDisplay(scene.title || scene.label)}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="border border-black/20 bg-[#fffdfa] p-2">
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                            Advanced
                          </div>
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                            {selectedCharacterDetectorUsesModel ? `Temp ${characterTemperature.toFixed(1)}` : 'Local'}
                          </div>
                        </div>
                        {selectedCharacterDetectorUsesModel ? (
                          <>
                            <select
                              id="ledger-character-index-model"
                              data-testid="ledger-character-index-model-select"
                              aria-label="Character index model"
                              value={characterModelId}
                              onChange={event => setCharacterModelId(event.target.value)}
                              disabled={isCharacterDetectorRunning}
                              className="h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                            >
                              {CHARACTER_INDEX_MODEL_OPTIONS.map(model => (
                                <option key={model.id} value={model.id}>
                                  {model.label}
                                </option>
                              ))}
                            </select>
                            {selectedCharacterDetectorIsFocalization && (
                              <label className="mt-1.5 block">
                                <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                  Figures
                                </span>
                                <select
                                  id="ledger-character-index-focalization-figures"
                                  data-testid="ledger-character-index-focalization-figure-source"
                                  aria-label="Focalization figure source"
                                  value={characterFocalizationFigureSource}
                                  onChange={event => {
                                    setCharacterFocalizationFigureSource(event.target.value as CharacterFocalizationFigureSource)
                                    setCharacterDetectorFeedback(null)
                                  }}
                                  disabled={isCharacterDetectorRunning}
                                  className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                  <option value="character-index">
                                    Character index · {characterIndexFiguresForFocalization.length}
                                  </option>
                                  <option value="coreference-v2">
                                    Coreference v2 · {compatibleCoreferenceV2FiguresForFocalization.length}
                                  </option>
                                </select>
                              </label>
                            )}
                            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                              <label className="min-w-0">
                                <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                  Temp
                                </span>
                                <input
                                  type="number"
                                  min={0}
                                  max={2}
                                  step={0.1}
                                  value={characterTemperature}
                                  onChange={event => {
                                    const nextValue = Number(event.target.value)
                                    if (Number.isFinite(nextValue)) {
                                      setCharacterTemperature(Math.max(0, Math.min(2, nextValue)))
                                    }
                                  }}
                                  disabled={isCharacterDetectorRunning}
                                  className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                                />
                              </label>
                              <label className="min-w-0">
                                <span className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                  Thinking
                                </span>
                                <select
                                  value={characterThinkingLevel}
                                  onChange={event => setCharacterThinkingLevel(event.target.value as CharacterThinkingLevel)}
                                  disabled={isCharacterDetectorRunning}
                                  className="mt-1 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-black focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                  <option value="medium">Medium</option>
                                  <option value="high">High</option>
                                </select>
                              </label>
                            </div>
                          </>
                        ) : (
                          <div className="border border-black/15 bg-white px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-500">
                            Name sweep ignores model controls.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        data-testid="ledger-character-index-suggest-entries"
                        onClick={() => void handleRunCharacterDetector('suggest')}
                        disabled={isCharacterDetectorRunning || characterDetectionRows.length === 0 || Boolean(characterExtractionValidationMessage) || (selectedCharacterDetectorNeedsEntries && characterDetectorEligibleCount === 0)}
                        className="h-8 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:shadow-none"
                      >
                        {characterDetectorRunMode === 'suggest' ? 'Running...' : 'Start'}
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-cancel-run"
                        onClick={handleCancelCharacterDetectorRun}
                        disabled={!characterDetectorCanCancel}
                        className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-resume-run"
                        onClick={handleResumeCharacterDetectorRun}
                        disabled={!characterDetectorCanResume}
                        className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Resume
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-retry-run"
                        onClick={handleRetryCharacterDetectorRun}
                        disabled={!characterDetectorCanRetry}
                        className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-refine-entries"
                        title={characterDetectorCanRefine ? 'Refine the last model output against the same source controls' : 'Run a model suggestion first'}
                        onClick={() => void handleRunCharacterDetector('refine')}
                        disabled={!characterDetectorCanRefine}
                        className="col-span-2 h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        {characterDetectorRunMode === 'refine' ? 'Refining...' : 'Refine'}
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-copy-model-payload"
                        title={characterExternalModelPayload ? 'Copy the exact prompt text that will be sent to the model' : 'Select a prepared model extraction first'}
                        onClick={() => void handleCopyCharacterModelPayload()}
                        disabled={isCharacterDetectorRunning || !characterExternalModelPayload || Boolean(characterExternalModelExchangeContext?.validationMessage)}
                        className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Copy prompt
                      </button>
                      <button
                        type="button"
                        data-testid="ledger-character-index-paste-model-result"
                        title="Paste a model JSON result through the copied Character Index prompt context"
                        onClick={() => void handlePasteCharacterExternalModelResult()}
                        disabled={readOnly || isCharacterDetectorRunning}
                        className="h-8 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Paste clipboard
                      </button>
                    </div>
                    <div className={`mt-2 border px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.12em] ${characterDetectorFeedbackClass}`}>
                      {characterDetectorFeedback?.message || characterExtractionValidationMessage || characterDetectorReadyLabel}
                    </div>
                    {characterDetectorFeedback?.report && (
                      <div className="mt-1 flex flex-wrap gap-1 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                        <span>{characterDetectorFeedback.report.matched_character_ids.length} matched</span>
                        <span>{characterDetectorFeedback.report.added_occurrence_ids.length} occurrences</span>
                        <span>{characterDetectorFeedback.report.skipped_occurrence_ids.length} skipped</span>
                      </div>
                    )}
                    {characterDetectorRunHistory.length > 0 && (
                      <div className="mt-2 border border-black/20 bg-white p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                            Run history
                          </div>
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-400">
                            {characterDetectorRunHistory.length} attempt{characterDetectorRunHistory.length === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div className="mt-1 space-y-1">
                          {characterDetectorRunHistory.map(run => {
                            const isSelectedRun = selectedCharacterDetectorRun?.runId === run.runId
                            const resultSummary = run.resultSet
                              ? run.provenance.detectorId === 'llm-focalization-characterization'
                                ? `${run.resultSet.noteCount} notes · ${run.resultSet.addedNoteCount || 0} added`
                                : `${run.resultSet.occurrenceCount} occ · ${run.resultSet.addedOccurrenceCount} added`
                              : run.errorMessage || run.provenance.sourceLabel
                            return (
                              <button
                                key={run.runId}
                                type="button"
                                data-testid={`ledger-character-index-run-history-${run.runId}`}
                                onClick={() => setSelectedCharacterDetectorRunId(run.runId)}
                                className={`w-full border px-2 py-1.5 text-left font-mono text-[8px] font-bold uppercase tracking-[0.1em] ${
                                  isSelectedRun
                                    ? 'border-black bg-black text-white'
                                    : `${getCharacterRunStatusClass(run.status)} hover:border-black`
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span>{formatCharacterRunTime(run.startedAt)} · {run.status}</span>
                                  <span>{run.provenance.windowCount > 1 ? `${run.provenance.windowCount} win` : `${run.provenance.rowCount} rows`}</span>
                                </div>
                                <div className={`mt-0.5 truncate ${isSelectedRun ? 'text-white/70' : 'text-gray-500'}`}>
                                  {run.provenance.detectorLabel} · {resultSummary}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                        {selectedCharacterDetectorRun && (
                          <div className="mt-2 border border-black/15 bg-gray-50 p-2 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-gray-500">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex flex-wrap gap-x-2 gap-y-1">
                                <span>{selectedCharacterDetectorRun.runMode}</span>
                                <span>{selectedCharacterDetectorRun.provenance.extractionUnit}</span>
                                <span>{selectedCharacterDetectorRun.provenance.modelId || 'local'}</span>
                                <span>{selectedCharacterDetectorRun.provenance.scorer || selectedCharacterDetectorRun.provenance.detectorLabel}</span>
                              </div>
                              <button
                                type="button"
                                data-testid="ledger-character-index-download-run-json"
                                onClick={handleDownloadCharacterDetectorRunJson}
                                className="h-6 shrink-0 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] transition-all hover:bg-gray-100 active:translate-y-px active:shadow-none"
                              >
                                Download JSON
                              </button>
                            </div>
                            {selectedCharacterDetectorRun.provenance.focusedCharacterName && (
                              <div className="mt-1 truncate" title={selectedCharacterDetectorRun.provenance.focusedCharacterId}>
                                Focus · {selectedCharacterDetectorRun.provenance.focusedCharacterName}
                              </div>
                            )}
                            <div className="mt-1 truncate" title={selectedCharacterDetectorRun.provenance.witnessLabels.join(', ')}>
                              Witnesses · {selectedCharacterDetectorRun.provenance.witnessLabels.join(', ') || 'none'}
                            </div>
                            <div className="mt-1 truncate" title={selectedCharacterDetectorRun.provenance.sourceLabel}>
                              Context · {selectedCharacterDetectorRun.provenance.sourceLabel} · {selectedCharacterDetectorRun.provenance.rowCount} rows{selectedCharacterDetectorRun.provenance.windowCount > 1 ? ` · ${selectedCharacterDetectorRun.provenance.windowCount} windows` : ''}
                            </div>
                            {selectedCharacterDetectorRun.provenance.figureSource && (
                              <div className="mt-1">
                                Figures · {CHARACTER_FOCALIZATION_FIGURE_SOURCE_LABELS[selectedCharacterDetectorRun.provenance.figureSource]} · {selectedCharacterDetectorRun.provenance.figureCount || 0}
                              </div>
                            )}
                            {selectedCharacterDetectorRun.provenance.promptVersion && (
                              <div className="mt-1 truncate" title={selectedCharacterDetectorRun.provenance.promptVersion}>
                                Prompt · {selectedCharacterDetectorRun.provenance.promptVersion}
                              </div>
                            )}
                            {selectedCharacterDetectorRun.resultSet && (
                              <div className="mt-1">
                                Result set · {selectedCharacterDetectorRun.resultSet.characterCount} characters · {selectedCharacterDetectorRun.resultSet.occurrenceCount} occurrences · {selectedCharacterDetectorRun.resultSet.noteCount} notes · {selectedCharacterDetectorRun.resultSet.skippedOccurrenceCount} skipped
                              </div>
                            )}
                            {selectedCoreferenceV2DiagnosticSummary && (
                              <div className="mt-1">
                                V2 raw · {selectedCoreferenceV2DiagnosticSummaryText}
                              </div>
                            )}
                            {selectedCharacterDetectorRun.errorMessage && (
                              <div className="mt-1 text-red-700">
                                Error · {selectedCharacterDetectorRun.errorMessage}
                              </div>
                            )}
                            {selectedCharacterDetectorRun.provenance.promptPreview && (
                              <details className="mt-1">
                                <summary className="cursor-pointer text-gray-500">Prompt/context</summary>
                                <pre className="ledger-safe-scrollbar mt-1 max-h-24 overflow-auto whitespace-pre-wrap border border-black/10 bg-white p-1 text-[8px] leading-snug normal-case tracking-normal text-gray-600">{selectedCharacterDetectorRun.provenance.promptPreview}</pre>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 border border-black bg-white p-2 shadow-[2px_2px_0_#000]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
                      Entries
                    </div>
                    <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                      {visibleCharacterIndexCharacters.length}/{activeCharacters.length}
                    </div>
                  </div>
                  <div
                    role="group"
                    aria-label="Character index entry filter"
                    data-testid="ledger-character-index-list-filter"
                    className="mt-2 grid grid-cols-4 border border-black bg-black gap-px"
                  >
                    {(['ledger', 'witness', 'scene', 'rows'] as CharacterIndexListScope[]).map(scope => {
                      const isActive = characterIndexListScope === scope
                      const isDisabled = !characterIndexListScopeEnabled[scope]
                      return (
                        <button
                          key={scope}
                          type="button"
                          aria-pressed={isActive ? 'true' : 'false'}
                          disabled={isDisabled}
                          data-testid={`ledger-character-index-list-filter-${scope}`}
                          title={isDisabled ? 'Scope unavailable' : `${CHARACTER_INDEX_LIST_SCOPE_LABELS[scope]} · ${characterIndexListCounts[scope]}`}
                          onClick={() => setCharacterIndexListScope(scope)}
                          className={`min-h-8 px-1.5 py-1 font-mono text-[7.5px] font-bold uppercase tracking-[0.08em] transition-colors disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ${
                            isActive
                              ? 'bg-black text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <span className="block truncate">{CHARACTER_INDEX_LIST_SCOPE_LABELS[scope]}</span>
                          <span className={`mt-0.5 block text-[7px] ${isActive ? 'text-white/60' : 'text-gray-400'}`}>
                            {characterIndexListCounts[scope]}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                  <div className="mt-2 border border-black/15 bg-gray-50 px-2 py-1.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] text-gray-500">
                    {characterIndexListScopeSummary}
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {activeCharacters.length === 0 ? (
                    <div className="border border-dashed border-black/30 bg-white px-3 py-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      No character index entries yet
                    </div>
                  ) : visibleCharacterIndexCharacters.length === 0 ? (
                    <div className="border border-dashed border-black/30 bg-white px-3 py-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      No entries in this filter
                    </div>
                  ) : visibleCharacterIndexCharacters.map(character => {
                    const coverage = characterCoverageById.get(character.character_id)
                    const rowPreview = coverage?.rowIds.slice(0, 3).map(rowId => {
                      const row = rowById.get(rowId)
                      return row ? getRowLabel(row) : rowId
                    }).join(', ')
                    const isSelectedCharacter = selectedCharacterId === character.character_id
                    const entry = characterIndexEntryById.get(character.character_id)
                    if (!entry) {
                      return null
                    }
                    return (
                      <CharacterIndexEntryCard
                        key={character.character_id}
                        entry={entry}
                        coverage={coverage}
                        rowPreview={rowPreview}
                        isSelected={isSelectedCharacter}
                        isExpanded={isSelectedCharacter}
                        onToggleSelect={() => {
                          setSelectedCharacterId(isSelectedCharacter ? '' : character.character_id)
                          setSelectedCharacterOccurrenceId('')
                        }}
                        reviewActions={character.status === 'auto' && !readOnly ? (
                          <div className="flex">
                            <button
                              type="button"
                              onClick={() => void handleReviewCharacter(character.character_id, 'confirmed')}
                              className="flex h-7 flex-1 items-center justify-center gap-1 border-r border-black bg-white font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-emerald-700 hover:bg-gray-100"
                            >
                              <Check className="h-3 w-3" aria-hidden="true" />
                              Accept
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleReviewCharacter(character.character_id, 'rejected')}
                              className="flex h-7 flex-1 items-center justify-center gap-1 bg-white font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500 hover:bg-gray-100"
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                              Reject
                            </button>
                          </div>
                        ) : null}
                      />
                    )
                  })}
                </div>

                {selectedCharacter && (
                  <div className="mt-4 border-t border-black pt-3">
                    <div className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
                      Occurrences
                    </div>
                    {!readOnly && (
                      <div className="mt-2 border border-black bg-[#fffdfa] p-2 shadow-[2px_2px_0_#000]">
                        <label htmlFor="ledger-character-index-role-labels" className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-500">
                          Role labels
                        </label>
                        <input
                          id="ledger-character-index-role-labels"
                          type="text"
                          value={characterRoleDraft}
                          onChange={event => setCharacterRoleDraft(event.target.value)}
                          placeholder="subject, addressee, quoted speaker"
                          className="mt-1 w-full border border-black bg-white px-2 py-1.5 text-xs text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-black"
                        />
                        {activeSelectedCharacterSpan && (
                          <div className="mt-2 border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-950">
                            <div className="font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-amber-700">
                              Selected span {activeSelectedCharacterSpan.localStart}-{activeSelectedCharacterSpan.localEnd}
                            </div>
                            <div className="mt-1 max-h-10 overflow-hidden">
                              {activeSelectedCharacterSpan.selectedText}
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleMarkCharacterOccurrence()}
                          disabled={!selectedCell && !selectedRange}
                          className="mt-2 h-8 w-full border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {selectedRange?.rowIds.length
                            ? `Mark ${selectedRange.rowIds.length} selected rows`
                            : selectedCell
                              ? activeSelectedCharacterSpan
                                ? 'Mark selected span'
                                : 'Mark selected cell'
                              : 'Select a cell'}
                        </button>
                      </div>
                    )}
                    <div className="mt-2 space-y-1.5">
                      {selectedCharacterOccurrencesSorted.length === 0 ? (
                        <div className="border border-dashed border-black/30 bg-white px-3 py-3 text-center font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400">
                          No occurrences marked
                        </div>
                      ) : selectedCharacterOccurrencesSorted.slice(0, 10).map(occurrence => {
                        const witness = scopeWitnessById.get(occurrence.locator.witnessId)
                        const row = rowById.get(occurrence.locator.rowId)
                        const isSelectedOccurrence = selectedCharacterOccurrenceId === occurrence.occurrence_id
                        const isLinkedToSelected = Boolean(
                          selectedCharacterOccurrence &&
                          selectedCharacterOccurrence.occurrence_id !== occurrence.occurrence_id &&
                          (selectedCharacterOccurrence.linked_occurrence_ids || []).includes(occurrence.occurrence_id)
                        )
                        const linkedCount = occurrence.linked_occurrence_ids?.length || 0
                        return (
                          <div
                            key={occurrence.occurrence_id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSelectCharacterOccurrence(occurrence)}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleSelectCharacterOccurrence(occurrence)
                              }
                            }}
                            className={`cursor-pointer border px-2 py-1.5 text-left transition-colors ${
                              isSelectedOccurrence
                                ? 'border-black bg-amber-50 shadow-[2px_2px_0_#000]'
                                : 'border-black/20 bg-white hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600">
                              <span>{witness?.sigla || occurrence.locator.witnessId}</span>
                              <span className="flex min-w-0 items-center gap-1.5">
                                {occurrence.status === 'auto' && (
                                  <span className="border border-amber-300 bg-amber-50 px-1 py-0.5 text-[7.5px] text-amber-800">
                                    Suggested
                                  </span>
                                )}
                                <span>
                                  {row ? getRowLabel(row) : occurrence.locator.rowId}
                                  {typeof occurrence.locator.localStart === 'number' && typeof occurrence.locator.localEnd === 'number'
                                    ? ` · ${occurrence.locator.localStart}-${occurrence.locator.localEnd}`
                                    : ''}
                                </span>
                              </span>
                            </div>
                            <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
                              <div className="min-w-0 truncate text-[11px] text-gray-700">
                                {occurrence.role_labels.length ? occurrence.role_labels.join(', ') : 'Role open'}
                              </div>
                              <div className="flex shrink-0 items-center gap-1 font-mono text-[7.5px] font-bold uppercase tracking-[0.1em] text-gray-400">
                                {linkedCount > 0 && <span>{linkedCount} linked</span>}
                                {selectedCharacterOccurrence && !isSelectedOccurrence && (
                                  <button
                                    type="button"
                                    onClick={event => {
                                      event.stopPropagation()
                                      void handleToggleCharacterOccurrenceLink(occurrence.occurrence_id)
                                    }}
                                    className={`border px-1 py-0.5 ${
                                      isLinkedToSelected
                                        ? 'border-black bg-black text-white'
                                        : 'border-black/20 bg-white text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >
                                    {isLinkedToSelected ? 'Linked' : 'Connect'}
                                  </button>
                                )}
                              </div>
                            </div>
                            {occurrence.status === 'auto' && !readOnly && (
                              <div className="-mx-2 -mb-1.5 mt-1.5 flex border-t border-black/20">
                                <button
                                  type="button"
                                  onClick={event => {
                                    event.stopPropagation()
                                    void handleReviewCharacterOccurrence(occurrence.occurrence_id, 'confirmed')
                                  }}
                                  className="flex h-6 flex-1 items-center justify-center gap-1 border-r border-black/20 bg-white font-mono text-[7.5px] font-bold uppercase tracking-[0.12em] text-emerald-700 hover:bg-gray-100"
                                >
                                  <Check className="h-2.5 w-2.5" aria-hidden="true" />
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  onClick={event => {
                                    event.stopPropagation()
                                    void handleReviewCharacterOccurrence(occurrence.occurrence_id, 'rejected')
                                  }}
                                  className="flex h-6 flex-1 items-center justify-center gap-1 bg-white font-mono text-[7.5px] font-bold uppercase tracking-[0.12em] text-gray-500 hover:bg-gray-100"
                                >
                                  <X className="h-2.5 w-2.5" aria-hidden="true" />
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <div className="mt-4 border border-black bg-[#fffdfa] p-2 shadow-[2px_2px_0_#000]">
                      <div className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-500">
                        Notes
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1" role="toolbar" aria-label="Note target">
                        {characterNoteTargets.map(target => (
                          <button
                            key={target.id}
                            type="button"
                            onClick={() => setCharacterNoteTarget(target.id)}
                            className={`border border-black px-1.5 py-1 font-mono text-[8px] font-bold uppercase tracking-[0.12em] ${
                              characterNoteTarget === target.id
                                ? 'bg-black text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {target.label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={characterNoteDraft}
                        onChange={event => setCharacterNoteDraft(event.target.value)}
                        disabled={readOnly}
                        rows={3}
                        placeholder="Observation"
                        className="mt-2 w-full resize-none border border-black bg-white px-2 py-1.5 text-xs text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100 disabled:text-gray-400"
                      />
                      <button
                        type="button"
                        onClick={() => void handleAddCharacterNote()}
                        disabled={readOnly || !characterNoteDraft.trim() || characterNoteTargets.length === 0}
                        className="mt-2 h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                      >
                        Save note
                      </button>
                      {selectedCharacterNotes.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {selectedCharacterNotes.map(note => (
                            <div key={note.note_id} className="border border-black/15 bg-white px-2 py-1.5 text-[11px] leading-snug text-gray-700">
                              {note.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
                </>
              ) : (
                <div
                  id="ledger-left-panel-tabpanel-structure"
                  data-testid="ledger-structure-panel-body"
                  role="tabpanel"
                  aria-labelledby="ledger-left-panel-tab-structure"
                  className="ledger-safe-scrollbar min-h-0 flex-1 overflow-y-auto p-3"
                >
                  {renderStructurePanel()}
                </div>
              )}
            </>
          )}
        </aside>
        )}

        <div
          ref={matrixViewportRef}
          data-ledger-safe-matrix-viewport="true"
          className="ledger-safe-scrollbar relative min-h-0 overflow-auto pb-[84px]"
        >
          <table className="w-max min-w-full table-auto border-separate border-spacing-0 text-xs">
            <colgroup>
              {visibleScopeWitnesses.map(witness => (
                <LedgerVerseColumn
                  key={witness.id}
                  source={witness.uploaded_file?.content || ''}
                  className={`w-[260px] ${witness.id === activeWitnessPresentation?.id ? 'bg-white' : 'bg-gray-50/40'}`}
                />
              ))}
              <col className="w-auto" />
            </colgroup>
            <thead className="sticky top-0 z-[120] bg-gray-100 shadow-[0_1px_0_rgba(0,0,0,1)]">
              <tr>
                {visibleScopeWitnesses.map((witness, visibleWitnessIndex) => {
                  const isActive = witness.id === activeWitnessPresentation?.id
                  const isPivot = witness.id === diffPivotWitness?.id
                  const isGroundTruth = witness.sigla === activeScope?.groundTruthSigla
                  const orderSiglum = witnessOrderSiglum(visibleWitnessIndex)
                  const witnessColumnLabel = witness.sigla || displaySigla(witness)
                  const witnessShelfmark = displayShelfmark(witness)
                  const witnessIdentityTitle = [orderSiglum, witnessColumnLabel, witnessShelfmark]
                    .filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index)
                    .join(' · ')
                  const headerMetrics = witnessHeaderMetricsById.get(witness.id) || { unitCount: 0, occupiedRows: 0, occupancyRatio: 0 }
                  const occupancyPercent = Math.round(headerMetrics.occupancyRatio * 100)
                  const canDragColumn = !readOnly && !isMatrixContentApplying && visibleScopeWitnesses.length > 1
                  const canUseAsDiffPivot = diffScopeWitnessSiglaSet.has(witness.sigla)
                  const columnClickTitle = diffEnabled
                    ? (canUseAsDiffPivot ? `Set ${witness.sigla} as comparison reference` : `${witness.sigla} excluded from comparison`)
                    : (readOnly ? undefined : `Set ${witness.sigla} as active witness`)
                  const columnHeaderTitle = canDragColumn && columnClickTitle
                    ? `${columnClickTitle}; drag to reorder`
                    : columnClickTitle
                  const isColumnDragging = draggedColumnWitnessId === witness.id
                  const isColumnDragOver = dragOverColumnWitnessId === witness.id && draggedColumnWitnessId !== witness.id
                  return (
                    <th
                      key={witness.id}
                      data-testid={`ledger-witness-column-header-${witness.id}`}
                      draggable={canDragColumn}
                      onDragStart={event => handleWitnessColumnDragStart(event, witness.id)}
                      onDragOver={event => handleWitnessColumnDragOver(event, witness.id)}
                      onDragLeave={() => {
                        setDragOverColumnWitnessId(previous => previous === witness.id ? null : previous)
                      }}
                      onDrop={event => handleWitnessColumnDrop(event, witness.id)}
                      onDragEnd={handleWitnessColumnDragEnd}
                      onClick={() => handleWitnessColumnHeaderClick(witness.sigla)}
                      title={columnHeaderTitle}
                      aria-grabbed={isColumnDragging}
                      data-column-drag-state={isColumnDragging ? 'dragging' : (isColumnDragOver ? 'over' : undefined)}
                      className={`group sticky top-0 z-[120] border-r border-b border-black/20 px-3 py-2 text-left transition-[box-shadow,opacity,background-color] ${canDragColumn ? 'cursor-grab active:cursor-grabbing' : (diffEnabled || !readOnly ? 'cursor-pointer' : 'cursor-default')} ${isActive ? 'bg-emerald-50/60' : 'bg-white'} ${isColumnDragging ? 'opacity-60' : ''} ${isColumnDragOver ? 'shadow-[inset_0_0_0_2px_rgba(0,0,0,0.9)]' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          data-testid={`ledger-witness-order-siglum-${witness.id}`}
                          aria-label={`Order siglum ${orderSiglum}`}
                          className="inline-flex h-6 min-w-[28px] shrink-0 items-center justify-center border border-black bg-black px-1.5 font-serif text-[13px] font-bold italic leading-none text-white shadow-[1px_1px_0_#9ca3af]"
                          title={witnessIdentityTitle}
                        >
                          {orderSiglum}
                        </span>
                        <div className="min-w-0 flex-1">
                          {readOnly ? (
                            <span
                              className={`block truncate font-serif text-[12px] font-bold uppercase tracking-[0.16em] ${isActive ? 'text-emerald-800' : 'text-black'}`}
                              title={witnessIdentityTitle}
                            >
                              {witnessColumnLabel}
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={isMatrixContentApplying}
                              onClick={event => {
                                event.stopPropagation()
                                handleWitnessColumnHeaderClick(witness.sigla)
                              }}
                              aria-label={diffEnabled
                                ? (canUseAsDiffPivot ? `Set ${witness.sigla} as comparison reference` : `${witness.sigla} excluded from comparison`)
                                : `Set ${witness.sigla} as active witness`}
                              aria-pressed={diffEnabled ? isPivot : isActive}
                              title={[
                                diffEnabled ? (canUseAsDiffPivot ? 'Set comparison reference' : 'Excluded from comparison') : 'Set active witness',
                                witnessShelfmark || witnessColumnLabel,
                              ].filter(Boolean).join(' · ')}
                              className={`block max-w-full truncate font-serif text-[12px] font-bold uppercase tracking-[0.16em] transition-colors ${canUseAsDiffPivot || !diffEnabled ? 'hover:text-emerald-700' : ''} ${isActive ? 'text-emerald-800' : 'text-black'}`}
                            >
                              {witnessColumnLabel}
                            </button>
                          )}
                        </div>
                        <span className="flex shrink-0 items-center gap-1">
                          {runtimeEnableTraditionReader && (
                            <button
                              type="button"
                              aria-label={`Open ${witness.sigla} in The Tradition`}
                              title={`Open ${witness.sigla} in The Tradition`}
                              onClick={event => {
                                event.stopPropagation()
                                handleOpenTraditionReader(witness.id)
                              }}
                              className="inline-flex h-5 w-5 items-center justify-center text-gray-300 opacity-0 transition-[color,opacity] hover:text-black focus:text-black focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-black/40 group-hover:opacity-100"
                            >
                              <BookOpen aria-hidden="true" className="h-3 w-3" strokeWidth={1.6} />
                            </button>
                          )}
                          {canDragColumn && (
                            <MoveHorizontal
                              aria-hidden="true"
                              className="h-3 w-3 text-gray-300 opacity-55 transition-opacity group-hover:opacity-100"
                              strokeWidth={1.5}
                            />
                          )}
                        </span>
                      </div>
                      <div className="mt-1.5 flex min-h-[16px] items-center gap-1.5 text-[8px] font-bold uppercase tracking-widest">
                        {isGroundTruth && (
                          <span className="border border-black/70 bg-white px-1 text-black">
                            GT
                          </span>
                        )}
                        {isPivot && (
                          <span className="border border-black bg-black px-1 text-white">
                            REFERENCE
                          </span>
                        )}
                        <span
                          data-testid={`ledger-witness-column-unit-count-${witness.id}`}
                          className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-400"
                        >
                          {headerMetrics.unitCount}u
                        </span>
                      </div>
                      <div
                        data-testid={`ledger-witness-column-occupancy-${witness.id}`}
                        aria-label={`${witness.sigla} occupancy ${occupancyPercent}%`}
                        className="mt-1 h-[3px] w-12 bg-gray-200/80"
                        title={`${headerMetrics.occupiedRows}/${resolvedRows.length} rows occupied`}
                      >
                        <div
                          className="h-full bg-gray-700"
                          style={{ width: `${occupancyPercent}%` }}
                          data-testid={`ledger-witness-column-occupancy-fill-${witness.id}`}
                        />
                      </div>
                    </th>
                  )
                })}
                <th className="sticky top-0 z-[120] border-b border-black/20 bg-gray-50" />
              </tr>
            </thead>
            {selectedCharacter && characterScopedDisplayRows.length === 0 && (
              <tbody>
                <tr data-testid="ledger-character-empty-state">
                  <td colSpan={visibleScopeWitnesses.length + 1} className="border-b border-black/10 bg-white px-5 py-6">
                    <div className="border border-dashed border-black/30 bg-[#fffdfa] px-4 py-5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      No Variorum rows predicate about {selectedCharacter.display_name}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {matrixWindow.topSpacerHeight > 0 && (
              <tbody aria-hidden="true" data-testid="ledger-matrix-window-top-spacer">
                <tr>
                  <td
                    colSpan={visibleScopeWitnesses.length + 1}
                    className="border-0 p-0"
                    style={{ height: `${matrixWindow.topSpacerHeight}px` }}
                  />
                </tr>
              </tbody>
            )}
            {(() => {
              const rowGroups = matrixWindowDisplayRows.map(displayRow => {
                const row = displayRow.row
                const isProjectionRow = displayRow.kind === 'projection'
                const displayRowId = isProjectionRow
                  ? `transposition-${displayRow.sourceRowId}${displayRow.insertAfterRowId ? `-after-${displayRow.insertAfterRowId}` : '-before-first'}`
                  : row.id
                const canonicalRowIndex = canonicalRowIndexById.get(row.id) ?? 0
                const emptyRowRun = !isProjectionRow ? emptyLedgerRowRunByHeadRowId.get(row.id) || null : null
                const isHiddenEmptyRunMember = !isProjectionRow && multiEmptyLedgerRunMemberRowIds.has(row.id) && emptyRowRun?.rowIds[0] !== row.id
                const hiddenWitnessRowRun = !isProjectionRow ? hiddenWitnessRowRunByHeadRowId.get(row.id) || null : null
                const isHiddenWitnessRowRunMember = !isProjectionRow && hiddenWitnessRowRunMemberIds.has(row.id)
                const sceneHeaderRow = runtimeEnableSceneTitles && !isProjectionRow
                  ? (filteredHeaderProjection.scenes.get(row.id) || [])
                    .filter(header => !migratedLegacySceneIdSet.has(header.sceneKey))
                    .map(renderLedgerSceneHeader)
                  : null
                const structuralRangeHeaderRows = runtimeEnableStructuralRanges && !isProjectionRow
                  ? (filteredHeaderProjection.ranges.get(row.id) || []).map(renderStructuralRangeHeader)
                  : []
                const primaryRowTitle = runtimeEnableSceneTitles && !isProjectionRow ? getCollationRowPrimaryTitle(row) : null
                const fragmentsBeforeRow = !isProjectionRow ? (inlineFragmentsBeforeRowId.get(row.id) || []) : []
                const fragmentsAfterRow = !isProjectionRow ? (inlineFragmentsAfterRowId.get(row.id) || []) : []
                const isStructuralRibbonRowAnchor = runtimeEnableStructuralRanges && !isProjectionRow
                  && structuralRangeRibbonAnchor?.kind === 'row'
                  && structuralRangeRibbonAnchor.id === row.id
                const isStructuralBoundaryPreviewRow = runtimeEnableStructuralRanges && !isProjectionRow
                  && structuralBoundaryPreviewRowIds.has(row.id)

                if (isHiddenEmptyRunMember || isHiddenWitnessRowRunMember) {
                  return null
                }

                if (hiddenWitnessRowRun) {
                  const supportingWitnessLabels = hiddenWitnessRowRunSupportingLabelsByHeadRowId.get(row.id) || hiddenWitnessRowRun.supportingWitnessIds
                  return (
                    <LedgerSafeMatrixWindowSection
                      key={displayRow.key}
                      isWindowed={matrixWindow.isWindowed}
                      rowKey={displayRow.key}
                      onMeasure={matrixWindow.measureRowGroup}
                    >
                      <LedgerSafeFilteredRowRun
                        run={hiddenWitnessRowRun}
                        visibleWitnessCount={visibleScopeWitnesses.length}
                        supportingWitnessLabels={supportingWitnessLabels}
                        onRestoreWitnesses={handleRestoreFilteredRunWitnesses}
                        onRowElement={handleFilteredRunRowElement}
                      />
                    </LedgerSafeMatrixWindowSection>
                  )
                }

                if (emptyRowRun) {
                  return (
                    <LedgerSafeMatrixWindowSection
                      key={displayRow.key}
                      isWindowed={matrixWindow.isWindowed}
                      rowKey={displayRow.key}
                      onMeasure={matrixWindow.measureRowGroup}
                    >
                      {renderInlineFragmentRows(fragmentsBeforeRow, `before-${row.id}`)}
                      {sceneHeaderRow}
                      {structuralRangeHeaderRows}
                      <LedgerSafeMatrixDisplayRowBody
                        displayRowKey={displayRow.key}
                        displayRowId={displayRowId}
                        rowId={row.id}
                        isProjectionRow={false}
                        canonicalRowIndex={canonicalRowIndex}
                        rowLabel={emptyRowRun.label}
                        primaryTitle={primaryRowTitle}
                        parallelTitle={runtimeEnableSceneTitles ? getCollationRowParallelTitle(row) : null}
                        titleOrigin={row.metadata?.rowTitle?.origin}
                        onEditTitle={runtimeEnableSceneTitles ? handleEditHeadingTitle : undefined}
                        emptyRowRun={emptyRowRun}
                        emptyRunRowLabels={emptyRowRun.rowIds.map((rowId, index) => {
                          const emptyRunRow = rowById.get(rowId)
                          return {
                            rowId,
                            label: emptyRunRow ? getRowLabel(emptyRunRow) : `${index + 1}`,
                          }
                        })}
                        visibleWitnessCount={visibleScopeWitnesses.length}
                        isSelectedStructuralRow={runtimeEnableStructuralRanges && selectedStructuralRowIds.includes(emptyRowRun.rowIds[0])}
                        isSelectedLeftRow={false}
                        structuralBoundaryPlacementEdge={structuralBoundaryPlacement?.edge || null}
                        isStructuralBoundaryPreviewRow={isStructuralBoundaryPreviewRow}
                        isStructuralRibbonRowAnchor={isStructuralRibbonRowAnchor}
                        structuralRangeRibbon={isStructuralRibbonRowAnchor ? renderStructuralRangeRibbon() : null}
                        hasFragmentAnchorSelection={Boolean(fragmentAnchorSelection)}
                        isEditableContext={false}
                        isPiFocusedRow={false}
                        isPiPreviewAffectedRow={false}
                        isRetiredMaterialReviewRow={retiredMaterialAudit.affectedRowIds.has(row.id)}
                        readOnly={readOnly}
                        isMatrixContentApplying={isMatrixContentApplying}
                        witnessCellsProps={null}
                        onRowElement={handleMatrixDisplayRowElement}
                        onStructuralRowHover={handleMatrixStructuralRowHover}
                        onStructuralRowLabelClick={handleMatrixStructuralRowLabelClick}
                        onAnchorFragmentBeforeRow={handleStableAnchorFragmentBeforeRow}
                        onCompactEmptyRows={handleStableCompactEmptyRows}
                        onEditBoundaryBelow={handleEditBoundaryBelow}
                      />
                      {renderInlineFragmentRows(fragmentsAfterRow, `after-${row.id}`)}
                    </LedgerSafeMatrixWindowSection>
                  )
                }

                const selectedCellForRow = !isProjectionRow && selectedCell?.rowId === row.id
                  ? selectedCell
                  : null
                const selectedRangeWitnessIdForRow = (
                  !isProjectionRow
                  && selectedRange?.rowIds.includes(row.id)
                )
                  ? selectedRange.witnessId
                  : null
                const selectedBoundaryContextForRow = (
                  !isProjectionRow
                  && selectedBoundaryContext
                  && (
                    row.id === selectedBoundaryContext.leftRowId
                    || row.id === selectedBoundaryContext.rightRowId
                  )
                )
                  ? selectedBoundaryContext
                  : null
                const selectedSplitContextForRow = (
                  !isProjectionRow
                  && selectedSplitContext?.rowId === row.id
                )
                  ? selectedSplitContext
                  : null
                const relocationPlacementForRow: LedgerSafeMatrixCellProps['relocationPlacement'] = (
                  !isProjectionRow
                  && relocationTarget
                )
                  ? relocationPreviewConflictRowIdSet.has(row.id)
                    ? 'conflict'
                    : relocationPreviewTargetRowIdSet.has(row.id)
                      ? 'preview'
                      : relocationSourceRowIdSet.has(row.id)
                        ? 'source'
                        : relocationDestinationByStartRowId.get(row.id)?.eligible
                          ? 'candidate'
                          : undefined
                  : undefined
                const inlineRibbonByWitnessIdForRow = (
                  matrixInlineRibbonPresentationByDisplayRowKey.ribbonByRow.get(displayRow.key)
                  || EMPTY_LEDGER_INLINE_RIBBON_BY_WITNESS_ID
                )
                const inlineRibbonAboveWitnessIdsForRow = (
                  matrixInlineRibbonPresentationByDisplayRowKey.ribbonAboveByRow.get(displayRow.key)
                  || EMPTY_LEDGER_WITNESS_ID_SET
                )
                const isApplyingForRow = Boolean(
                  (
                    isMatrixContentApplying
                    && (
                      applyingSegmentationMatrixRowIdsRef.current.has(row.id)
                      || applyingLedgerMatrixRowIdsRef.current.has(row.id)
                      || applyingPiMatrixRowIdsRef.current.has(row.id)
                      || selectedBoundaryContextForRow
                      || selectedSplitContextForRow
                      || inlineRibbonByWitnessIdForRow.size > 0
                      || (!isProjectionRow && editSuggestionActionRowIdSet.has(row.id))
                    )
                  )
                )
                const isPiFocusedDisplayRow = !isProjectionRow && piFocusedRowIds.has(row.id)
                const isPiPreviewAffectedDisplayRow = !isProjectionRow && piPreviewAffectedRowIds.has(row.id)
                const witnessCellsProps: LedgerSafeMatrixWitnessCellsProps = {
                  displayRowKey: displayRow.key,
                  displayRowId,
                  row,
                  isProjectionRow,
                  witnesses: visibleScopeWitnesses,
                  activeWitnessId: matrixCellBehaviorActiveWitnessId,
                  witnessUnitTextLookupByWitnessId,
                  piPreviewAfterCellByKey,
                  semanticPreviewCellsByKey,
                  witnessPropagationPreviewCellsByKey,
                  selectedUnitRepairPreviewCellsByKey,
                  retiredMaterialIssueByCellKey: retiredMaterialAudit.issueByCellKey,
                  selectedCellForRow,
                  selectedRangeWitnessIdForRow,
                  selectedCharacterOccurrenceByCellKey,
                  selectedCharacterOccurrencePresentationByCellKey,
                  selectedBoundaryContextForRow,
                  boundaryPreviewForRow: selectedBoundaryContextForRow ? preview : null,
                  selectedSplitContextForRow,
                  splitPreviewForRow: selectedSplitContextForRow ? splitPreview : null,
                  selectedSplitBoundaryWordIndex,
                  inlineEditMode,
                  diffEnabled,
                  diffMode,
                  diffAppearance,
                  contentPresentationKey: [
                    matrixCellDiffPresentationKey,
                    markMenuCell?.rowId === row.id
                      ? markMenuCell.witnessId
                      : 'no-mark-menu',
                    isPiFocusedDisplayRow
                      ? 'pi-focused'
                      : 'pi-idle',
                  ].join('|'),
                  diffTokensByCellKey,
                  apparatusDiffByCellKey,
                  apparatusPivotTokensByCellKey,
                  isProjectedSourceRow: !isProjectionRow && transpositionDisplayModel.projectedSourceRowIds.has(row.id),
                  transpositionTargetWitnessId: transpositionTarget?.witnessId || null,
                  isTranspositionEligibleRow: !isProjectionRow && transpositionEligibleTargetRowIds.has(row.id),
                  isTranspositionOriginRow: !isProjectionRow && row.id === transpositionTarget?.originRowId,
                  relocationWitnessId: relocationTarget?.witnessId || null,
                  relocationPlacementForRow,
                  isRelocationSourceStartRow: !isProjectionRow && row.id === relocationTarget?.sourceRowIds[0],
                  isRelocationTargetStartRow: !isProjectionRow && row.id === relocationHoverTargetRowId,
                  isSequenceOffenderRow: (
                    !isProjectionRow
                    && sidePanelTab === 'sequence'
                    && Boolean(activeWitnessSequenceAudit?.offenderRowIds.has(row.id))
                  ),
                  isPiFocusedRow: isPiFocusedDisplayRow,
                  editSuggestionByCellKey,
                  editSuggestionActionKeySet,
                  inlineRibbonByWitnessId: inlineRibbonByWitnessIdForRow,
                  inlineRibbonAboveWitnessIds: inlineRibbonAboveWitnessIdsForRow,
                  readOnly,
                  isApplying: isApplyingForRow,
                  onCellClick: handleMatrixCellClick,
                  onCellHover: handleRelocationCellHover,
                  onCellTextSelection: handleCharacterCellTextSelection,
                  onRibbonAction: handleLedgerRibbonAction,
                  onSelectSplitBoundaryWordIndex: setSelectedSplitBoundaryWordIndex,
                  onApplySplit: handleStableApplySplit,
                  onApplyBoundary: handleStableApplyBoundary,
                  onAdjustBoundaryDelta: handleStableBoundaryDelta,
                  onBoundaryDragStart: handleStableBoundaryDragStart,
                  onCancelBoundary: handleStableCancelBoundary,
                  onCancelSplit: handleStableCancelSplit,
                  onApplyEditSuggestion: handleApplyEditSuggestion,
                  onDismissEditSuggestion: dismissLedgerSafeEditSuggestion,
                }

                return (
                  <LedgerSafeMatrixWindowSection
                    key={displayRow.key}
                    isWindowed={matrixWindow.isWindowed}
                    rowKey={displayRow.key}
                    onMeasure={matrixWindow.measureRowGroup}
                  >
                    {renderInlineFragmentRows(fragmentsBeforeRow, `before-${row.id}`)}
                    {sceneHeaderRow}
                    {structuralRangeHeaderRows}
                    <LedgerSafeMatrixDisplayRowBody
                      displayRowKey={displayRow.key}
                      displayRowId={displayRowId}
                      rowId={row.id}
                      isProjectionRow={isProjectionRow}
                      canonicalRowIndex={canonicalRowIndex}
                      rowLabel={getRowLabel(row)}
                      primaryTitle={primaryRowTitle}
                      parallelTitle={runtimeEnableSceneTitles && !isProjectionRow ? getCollationRowParallelTitle(row) : null}
                      titleOrigin={row.metadata?.rowTitle?.origin}
                      onEditTitle={runtimeEnableSceneTitles && !isProjectionRow ? handleEditHeadingTitle : undefined}
                      emptyRowRun={null}
                      emptyRunRowLabels={EMPTY_LEDGER_DISPLAY_ROW_LABELS}
                      visibleWitnessCount={visibleScopeWitnesses.length}
                      isSelectedStructuralRow={runtimeEnableStructuralRanges && !isProjectionRow && selectedStructuralRowIds.includes(row.id)}
                      isSelectedLeftRow={!isProjectionRow && selectedLeftRowId === row.id}
                      structuralBoundaryPlacementEdge={structuralBoundaryPlacement?.edge || null}
                      isStructuralBoundaryPreviewRow={isStructuralBoundaryPreviewRow}
                      isStructuralRibbonRowAnchor={isStructuralRibbonRowAnchor}
                      structuralRangeRibbon={isStructuralRibbonRowAnchor ? renderStructuralRangeRibbon() : null}
                      hasFragmentAnchorSelection={Boolean(fragmentAnchorSelection)}
                      isEditableContext={!isProjectionRow && editableContextsByRowId.has(row.id)}
                      isPiFocusedRow={isPiFocusedDisplayRow}
                      isPiPreviewAffectedRow={isPiPreviewAffectedDisplayRow}
                      isRetiredMaterialReviewRow={!isProjectionRow && retiredMaterialAudit.affectedRowIds.has(row.id)}
                      readOnly={readOnly}
                      isMatrixContentApplying={isMatrixContentApplying}
                      witnessCellsProps={witnessCellsProps}
                      onRowElement={handleMatrixDisplayRowElement}
                      onStructuralRowHover={handleMatrixStructuralRowHover}
                      onStructuralRowLabelClick={handleMatrixStructuralRowLabelClick}
                      onAnchorFragmentBeforeRow={handleStableAnchorFragmentBeforeRow}
                      onCompactEmptyRows={handleStableCompactEmptyRows}
                      onEditBoundaryBelow={handleEditBoundaryBelow}
                    />
                    {renderInlineFragmentRows(fragmentsAfterRow, `after-${row.id}`)}
                  </LedgerSafeMatrixWindowSection>
                )
              })
              return matrixWindow.isWindowed ? rowGroups : <tbody>{rowGroups}</tbody>
            })()}
            {matrixWindow.bottomSpacerHeight > 0 && (
              <tbody aria-hidden="true" data-testid="ledger-matrix-window-bottom-spacer">
                <tr>
                  <td
                    colSpan={visibleScopeWitnesses.length + 1}
                    className="border-0 p-0"
                    style={{ height: `${matrixWindow.bottomSpacerHeight}px` }}
                  />
                </tr>
              </tbody>
            )}
          </table>
        </div>

        {relocationTarget && (() => {
          const relocationWitness = interactiveScopeWitnesses.find(
            witness => witness.id === relocationTarget.witnessId
          ) || null
          const sourceRows = relocationTarget.sourceRowIds
            .map(rowId => rowById.get(rowId))
            .filter((row): row is AnchoredRow => Boolean(row))
          const sourceLabel = sourceRows.length === 1
            ? getRowLabel(sourceRows[0])
            : sourceRows.length > 1
              ? `${getRowLabel(sourceRows[0])}–${getRowLabel(sourceRows[sourceRows.length - 1])}`
              : ''
          const hoveredTargetRows = relocationHoverDestination?.targetRowIds
            .map(rowId => rowById.get(rowId))
            .filter((row): row is AnchoredRow => Boolean(row)) || []
          const targetLabel = hoveredTargetRows.length === 1
            ? getRowLabel(hoveredTargetRows[0])
            : hoveredTargetRows.length > 1
              ? `${getRowLabel(hoveredTargetRows[0])}–${getRowLabel(hoveredTargetRows[hoveredTargetRows.length - 1])}`
              : ''
          const openDestinationCount = Array.from(relocationDestinationByStartRowId.values())
            .filter(destination => destination.eligible).length
          return (
            <div
              data-testid="ledger-relocation-mode-banner"
              className="absolute left-1/2 top-3 z-[150] flex max-w-[calc(100%-24px)] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-1 border border-black bg-black py-1.5 pl-3 pr-1.5 text-white shadow-[3px_3px_0_rgba(0,0,0,0.35)]"
            >
              <span aria-hidden="true" className="font-serif text-[15px] font-semibold leading-none text-emerald-300">↗</span>
              <span className="whitespace-normal font-mono text-[9px] font-bold uppercase tracking-[0.18em]">
                Relocate
                {relocationWitness ? ` · ${displaySigla(relocationWitness)}` : ''}
                {sourceLabel ? ` ${sourceLabel}` : ''}
                {` · ${relocationTarget.sourceRowIds.length} cell${relocationTarget.sourceRowIds.length === 1 ? '' : 's'}`}
              </span>
              {targetLabel && (
                <span
                  data-testid="ledger-relocation-mode-target"
                  className={`whitespace-normal font-mono text-[9px] font-bold uppercase tracking-[0.14em] ${
                    relocationHoverDestination?.eligible ? 'text-emerald-300' : 'text-red-300'
                  }`}
                >
                  {relocationHoverDestination?.eligible ? `→ ${targetLabel}` : (relocationHoverDestination?.reason || 'Blocked')}
                </span>
              )}
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/60">
                {openDestinationCount} open
              </span>
              <button
                type="button"
                data-testid="ledger-relocation-cancel"
                aria-label="Cancel relocation"
                title="Cancel relocation"
                onClick={() => setRelocationTarget(null)}
                className="flex h-6 w-6 items-center justify-center border border-white/40 bg-transparent text-white transition-colors hover:bg-white hover:text-black"
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </div>
          )
        })()}

        {transpositionTarget && (() => {
          const transpositionOriginRow = rowById.get(transpositionTarget.originRowId) || null
          const transpositionOriginWitness = interactiveScopeWitnesses.find(candidate => candidate.id === transpositionTarget.witnessId) || null
          const openTargetRowCount = transpositionEligibleTargetRowIds.size
          return (
            <div
              data-testid="ledger-transposition-mode-banner"
              className="absolute left-1/2 top-3 z-[150] flex -translate-x-1/2 items-center gap-3 border border-black bg-black py-1.5 pl-3 pr-1.5 text-white shadow-[3px_3px_0_rgba(0,0,0,0.35)]"
            >
              <span aria-hidden="true" className="font-serif text-[13px] font-semibold italic leading-none text-[#e8a37e]">tr.</span>
              <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em]">
                Transposition
                {transpositionOriginWitness ? ` · ${displaySigla(transpositionOriginWitness)}` : ''}
                {transpositionOriginRow ? ` ${getRowLabel(transpositionOriginRow)}` : ''}
                {' — choose an open row'}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-white/60">
                {openTargetRowCount} open
              </span>
              <button
                type="button"
                data-testid="ledger-transposition-cancel"
                onClick={() => setTranspositionTarget(null)}
                className="flex h-6 items-center gap-1.5 border border-white/40 bg-transparent px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-black"
              >
                <kbd className="font-mono text-[8px] tracking-normal opacity-70">Esc</kbd>
                Cancel
              </button>
            </div>
          )
        })()}

        <aside
          data-testid="ledger-side-panel"
          data-collapsed={isSidePanelCollapsed ? 'true' : 'false'}
          className={`relative flex h-full min-h-0 flex-col border-l border-black bg-white transition-colors duration-200 ${isSidePanelCollapsed ? 'pb-0' : sidePanelTab === 'map' || !runtimeEnablePiDock ? 'pb-0' : 'pb-[84px]'}`}
        >
          <button
            type="button"
            data-testid="ledger-side-panel-edge-toggle"
            aria-label={isSidePanelCollapsed ? 'Expand right panel' : 'Collapse right panel'}
            aria-expanded={isSidePanelCollapsed ? 'false' : 'true'}
            aria-controls="ledger-side-panel-body"
            title={isSidePanelCollapsed ? `Expand ${activeSidePanelTab.label}` : 'Collapse right panel'}
            onClick={() => setIsSidePanelCollapsed(previous => !previous)}
            className="absolute -left-[15px] top-1/2 z-[140] flex h-8 w-7 -translate-y-1/2 items-center justify-center border border-black bg-white text-black shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-x-[2px] active:translate-y-[calc(-50%+2px)] active:shadow-none"
          >
            {isSidePanelCollapsed ? (
              <PanelRightOpen className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <PanelRightClose className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
          {isSidePanelCollapsed ? (
            <div data-testid="ledger-side-panel-collapsed" className="flex h-full min-h-0 flex-col items-center bg-white px-1.5 py-2">
              <div
                role="tablist"
                aria-label="Variorum side panel"
                data-testid="ledger-side-panel-collapsed-tabs"
                className="flex flex-col items-center gap-1"
              >
                {sidePanelTabs.map(tab => {
                  const shortLabel = tab.label === 'Propagation'
                    ? 'Pr'
                    : tab.label === 'Inspector'
                      ? 'In'
                      : tab.label.slice(0, 2)
                  const isActiveTab = sidePanelTab === tab.id
                  return (
                    <button
                      key={tab.id}
                      id={`ledger-side-panel-tab-${tab.id}`}
                      role="tab"
                      type="button"
                      aria-selected={isActiveTab}
                      aria-controls={`ledger-side-panel-tabpanel-${tab.id}`}
                      aria-label={`${tab.label}${tab.count > 0 ? `, ${tab.count}` : ''}`}
                      title={`${tab.label}${tab.count > 0 ? ` · ${tab.count}` : ''}`}
                      onClick={() => {
                        setSidePanelTab(tab.id)
                        setIsSidePanelCollapsed(false)
                      }}
                      className={`flex h-8 w-8 items-center justify-center border border-black font-mono text-[8px] font-bold uppercase tracking-normal transition-colors ${
                        isActiveTab
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {shortLabel}
                    </button>
                  )
                })}
              </div>
              <div className="my-3 h-px w-7 bg-black/20" />
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <div
                  data-testid="ledger-side-panel-collapsed-label"
                  className="max-h-[220px] rotate-180 truncate whitespace-nowrap font-mono text-[9px] font-bold uppercase tracking-normal text-gray-600"
                  style={{ writingMode: 'vertical-rl' }}
                  title={`${activeSidePanelTab.label}${activeSidePanelTab.count > 0 ? ` · ${activeSidePanelTab.count}` : ''}`}
                >
                  {activeSidePanelTab.label}
                  {activeSidePanelTab.count > 0 ? ` · ${activeSidePanelTab.count}` : ''}
                </div>
              </div>
            </div>
          ) : (
            <>
          <div className="shrink-0 border-b border-black bg-white px-2 py-2">
            <div role="tablist" aria-label="Variorum side panel" data-testid="ledger-side-panel-tabs" className="ledger-safe-scrollbar flex flex-nowrap gap-1 overflow-x-auto pb-1">
              {sidePanelTabs.map(tab => (
                <button
                  key={tab.id}
                  id={`ledger-side-panel-tab-${tab.id}`}
                  role="tab"
                  type="button"
                  aria-selected={sidePanelTab === tab.id}
                  aria-controls={`ledger-side-panel-tabpanel-${tab.id}`}
                  onClick={() => setSidePanelTab(tab.id)}
                  className={`shrink-0 whitespace-nowrap border border-black px-2 py-1.5 text-[9px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                    sidePanelTab === tab.id
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span aria-hidden="true" className={`ml-1.5 border px-1 py-0.5 text-[8px] font-mono ${sidePanelTab === tab.id ? 'border-white/40 bg-white/10 text-white' : 'border-gray-300 bg-gray-100 text-gray-600'}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div
            data-testid="ledger-side-panel-body"
            className={`min-h-0 flex-1 ${sidePanelTab === 'map' ? 'overflow-hidden p-2' : 'ledger-safe-scrollbar overflow-y-auto p-4'}`}
          >
            {sidePanelTab === 'map' && (
              <div
                id="ledger-side-panel-tabpanel-map"
                role="tabpanel"
                aria-labelledby="ledger-side-panel-tab-map"
                className="flex h-full min-h-0 flex-col overflow-hidden"
              >
                <div
                  data-testid="ledger-occupancy-map-newsprint"
                  className="flex h-full min-h-0 flex-col overflow-hidden border border-black bg-[#fffdfa] shadow-[2px_2px_0_#000]"
                >
                  <div className="shrink-0 border-b border-black bg-[#fffdfa] px-3 py-2.5">
                    <div className="mb-2 flex items-center justify-between gap-3 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#0f172a]">
                      <span>{occupancyMapLayerMeta.title}</span>
                      <span className="font-medium text-[#475569]">{resolvedRows.length} rows • {visibleScopeWitnesses.length}/{scopeWitnesses.length} witnesses</span>
                    </div>
                    <div role="toolbar" aria-label="Occupancy map layers" className="grid grid-cols-2 border border-black bg-[#fffdfa] shadow-[2px_2px_0_#000]">
                      {([
                        { id: 'structural', label: 'Structural' },
                        { id: 'height', label: 'Density' },
                        ...(runtimeEnableExperimentalMaps ? [
                          { id: 'thin-line', label: 'Thin Line' },
                          { id: 'drift', label: 'Drift' },
                          { id: 'bead', label: 'Beads' },
                          { id: 'flow', label: 'Flow' },
                          { id: 'spine', label: 'Spine' },
                          ...(IS_LEDGER_OCCUPANCY_VARIANCE_ENABLED ? [{ id: 'variance', label: 'Variance' } as const] : []),
                          { id: 'triage', label: 'AI Triage' }
                        ] as Array<{ id: LedgerOccupancyMapLayer; label: string }> : [])
                      ] as Array<{ id: LedgerOccupancyMapLayer; label: string }>).map(layer => (
                        <button
                          key={layer.id}
                          type="button"
                          data-testid={`ledger-occupancy-layer-${layer.id}`}
                          aria-pressed={occupancyMapLayer === layer.id}
                          onClick={() => setOccupancyMapLayer(layer.id)}
                          className={`border-r border-b border-black/20 px-2 py-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.16em] even:border-r-0 ${
                            occupancyMapLayer === layer.id
                              ? 'bg-black text-white'
                              : 'bg-[#fffdfa] text-[#0f172a] hover:bg-[#f5f3ec]'
                          }`}
                        >
                          {layer.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 border-b border-black bg-[#fffdfa] px-3 py-2">
                    {occupancyMapLayer === 'variance' && (
                      <div
                        data-testid="ledger-occupancy-variance-ramp"
                        className="mb-2 flex border border-black bg-[#fffdfa]"
                        aria-hidden="true"
                      >
                        {(['unified', 'near', 'drifting', 'conflict', 'severe'] as LedgerOccupancyVarianceBucket[]).map(bucket => (
                          <div
                            key={bucket}
                            className="h-2.5 flex-1 border-r border-black/20 last:border-r-0"
                            style={{ backgroundColor: LEDGER_OCCUPANCY_VARIANCE_COLORS[bucket] }}
                          />
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-[#475569]">
                      {occupancyMapLayerMeta.legend.map(item => (
                        <span key={item.id} className="inline-flex items-center gap-1.5">
                          <span
                            className={`h-2.5 w-2.5 ${item.className}`}
                            style={item.style}
                            aria-hidden="true"
                          />
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div
                    data-ledger-occupancy-scroll="true"
                    className="ledger-safe-scrollbar min-h-0 flex-1 overflow-y-auto bg-[#fffdfa] px-3 pb-2"
                  >
                    <div className="sticky top-0 z-10 mb-2 flex h-7 items-center justify-center border-b border-black/20 bg-[#fffdfa]">
                      {activeOccupancyMapWitness ? (
                        <span
                          data-testid="ledger-occupancy-active-witness"
                          className="max-w-full truncate whitespace-nowrap font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-[#0f172a]"
                        >
                          {activeOccupancyMapWitness.sigla}
                        </span>
                      ) : null}
                    </div>
	                    {occupancyMapLayer === 'flow' ? (
	                      <div
	                        data-testid="ledger-occupancy-flow-stage"
	                        className="grid items-start gap-1"
	                        style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleScopeWitnesses.length)}, minmax(0, 1fr))` }}
	                      >
	                        {visibleScopeWitnesses.map(witness => {
	                          const segments = ledgerOccupancyMapRows
	                            .map(row => ({
	                              row,
	                              cell: row.cells.find(cell => cell.witnessId === witness.id) || null
	                            }))
	                            .filter((entry): entry is { row: typeof ledgerOccupancyMapRows[number]; cell: typeof ledgerOccupancyMapRows[number]['cells'][number] } => Boolean(entry.cell))
	                          const witnessTotal = segments[0]?.cell.witnessTotalUnitTextLength || 0
	                          const witnessTotalRatio = segments[0]?.cell.witnessTotalUnitTextRatio || 0
                          const columnHeightPx = Math.max(
                            72,
                            Math.round(96 + witnessTotalRatio * Math.max(260, ledgerOccupancyMapRows.length * 2.5))
                          )
                          return (
                            <div
                              key={`occupancy-flow-column:${witness.id}`}
                              data-testid={`ledger-occupancy-flow-column-${witness.id}`}
                              className="flex min-w-0 flex-col items-stretch"
                              onMouseEnter={() => setActiveOccupancyMapWitnessId(witness.id)}
                              onMouseLeave={() => setActiveOccupancyMapWitnessId(previous => previous === witness.id ? null : previous)}
                            >
                              <div className="truncate text-center font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-[#475569]">
                                {witness.sigla.startsWith(ledgerOccupancySharedSiglaPrefix)
                                  ? witness.sigla.slice(ledgerOccupancySharedSiglaPrefix.length) || witness.sigla
                                  : witness.sigla}
                              </div>
                              <div className="pb-1 text-center font-mono text-[7px] uppercase tracking-[0.08em] text-gray-400">
                                {witnessTotal}
                              </div>
                              <div
                                className="flex flex-col gap-px border border-black/30 bg-[#f5f3ec] p-px"
                                style={{ height: `${columnHeightPx}px` }}
                              >
	                                {segments.map(({ row, cell }) => {
	                                  if (row.hiddenWitnessRun) {
                                      const run = row.hiddenWitnessRun
                                      const supportingWitnessLabels = hiddenWitnessRowRunSupportingLabelsByHeadRowId.get(run.rowIds[0]) || run.supportingWitnessIds
                                      const witnessList = supportingWitnessLabels.join(', ')
                                      return (
                                        <button
                                          key={`occupancy-flow-filtered:${witness.id}:${run.rowIds.join(':')}`}
                                          type="button"
                                          data-testid={`ledger-occupancy-flow-filtered-row-run-${witness.id}-${run.rowIds.join('-')}`}
                                          aria-label={`${run.rowCount} ${run.rowCount === 1 ? 'row' : 'rows'} collapsed by witness filter; show ${witnessList}`}
                                          title={`${run.label} · ${run.rowCount} ${run.rowCount === 1 ? 'row' : 'rows'} attested only by ${witnessList} · click to restore`}
                                          onClick={() => handleRestoreFilteredRunWitnesses(run.supportingWitnessIds)}
                                          className="h-[6px] min-h-[6px] w-full border-y border-dashed border-black/35 bg-[#e8e3d8] hover:bg-[#d8d1c3]"
                                        >
                                          <span className="sr-only">{run.rowCount} filtered</span>
                                        </button>
                                      )
                                    }
	                                  const isDisplayedAsGap = cell.state !== 'mapped'
                                      const isSelectedOccupancyCell = (
                                        (selectedCell?.rowId === cell.rowId && selectedCell?.witnessId === cell.witnessId)
                                        || selectedCharacterOccurrenceByCellKey.has(`${cell.rowId}:${cell.witnessId}`)
                                      )
	                                  const segmentHeightPx = cell.state === 'mapped'
	                                    ? Math.max(2, Math.round((cell.unitTextLength / Math.max(1, witnessTotal)) * columnHeightPx))
	                                    : 3
                                  const flowColor = cell.unitLengthTone === 'long'
                                    ? LEDGER_OCCUPANCY_INK_COLOR
                                    : cell.unitLengthTone === 'medium'
                                      ? '#4a4438'
                                      : '#908577'
	                                  const flowStyle: React.CSSProperties = {
	                                    height: `${segmentHeightPx}px`,
	                                    minHeight: `${segmentHeightPx}px`
	                                  }
	                                  if (row.sceneHeader) {
	                                    flowStyle.borderTop = `2px solid ${LEDGER_OCCUPANCY_INK_COLOR}`
	                                  }
                                  if (isSelectedOccupancyCell) {
                                    flowStyle.outline = `1px solid ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`
                                    flowStyle.outlineOffset = '1px'
                                  }
                                  if (cell.isPreview) {
                                    flowStyle.outline = `1px dashed ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`
                                    flowStyle.outlineOffset = '1px'
                                  }
                                  if (cell.state === 'mapped') {
                                    flowStyle.backgroundColor = flowColor
                                  } else {
                                    flowStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
                                    flowStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
                                    flowStyle.boxShadow = 'inset 0 0 0 1px rgba(0,0,0,0.20)'
                                  }
                                  return (
                                    <button
                                      key={`occupancy-flow-segment:${cell.rowId}:${cell.witnessId}`}
                                      type="button"
	                                      data-testid={`ledger-occupancy-flow-segment-${cell.rowId}-${cell.witnessId}`}
	                                      aria-label={`Flow map ${cell.witnessSigla} ${cell.rowLabel} ${cell.displayStateLabel}${isSelectedOccupancyCell ? ' selected' : ''}`}
	                                      title={`${cell.witnessSigla} · ${cell.rowLabel} · ${cell.displayStateLabel} · ${cell.unitTextLength} text units${row.sceneHeader ? ` · ${row.sceneHeader.title}` : ''}`}
                                      onClick={() => handleFocusLedgerOccupancyCell(cell)}
                                      onFocus={() => setActiveOccupancyMapWitnessId(cell.witnessId)}
                                      onBlur={() => setActiveOccupancyMapWitnessId(previous => previous === cell.witnessId ? null : previous)}
                                      className="w-full"
                                      style={flowStyle}
                                    >
                                      <span className="sr-only">
                                        {isDisplayedAsGap ? 'Gap' : `${cell.unitTextLength} text units`}
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                    <LedgerOccupancyWindow
                      rows={ledgerOccupancyMapRows}
                      layer={occupancyMapLayer}
                      renderRow={row => {
                        const isSelectedRow = selectedCell?.rowId === row.rowId || selectedCharacterRowIdSet.has(row.rowId)
                        const rowMarkerLabel = isSelectedRow ? row.rowLabel : row.rowMarkerLabel
                        const rowHasReview = row.cells.some(cell => cell.triageState === 'review')
                        const rowHasRecentEdit = row.cells.some(cell => cell.triageState === 'recent')
                        const rowToneClass = isSelectedRow
                          ? 'bg-[#eff6ff] outline outline-1 outline-[#2563eb] outline-offset-1 z-[2]'
                          : ''
                        const varianceRowLabelClass = rowMarkerLabel
                          ? row.varianceBucket === 'severe'
                            ? 'opacity-100 text-[#1a1612]'
                            : row.varianceBucket === 'conflict'
                              ? 'opacity-100 text-[#4a4438]'
                              : row.varianceBucket === 'drifting'
                                ? 'opacity-100 text-[#908577]'
                                : row.varianceBucket === 'near'
                                  ? 'opacity-100 text-[#6f6759]'
                                  : 'opacity-100 text-[#948b7c]'
                          : 'opacity-0 text-[#c8c1b1] group-hover/map-row:opacity-100 group-hover/map-row:text-[#0f172a]'
                        const rowLabelClass = isSelectedRow
                          ? 'opacity-100 font-bold text-[#2563eb]'
                          : occupancyMapLayer === 'triage' && rowHasReview
                            ? 'opacity-100 text-[#2563eb]'
                              : occupancyMapLayer === 'triage' && rowHasRecentEdit
                                ? 'opacity-100 text-[#047857]'
                                : occupancyMapLayer === 'variance'
                                  ? varianceRowLabelClass
                                      : rowMarkerLabel
                                        ? 'opacity-100 text-[#475569]'
                                        : 'opacity-0 text-[#c8c1b1] group-hover/map-row:opacity-100 group-hover/map-row:text-[#0f172a]'
                        const compactMapSceneLabel = row.sceneHeader ? getLedgerSafeCompactSceneLabel(row.sceneHeader.label) : ''
                        const sceneMarker = row.sceneHeader ? (
                          <button
                            key={`occupancy-scene:${row.sceneHeader.sceneKey}`}
                            type="button"
                            data-testid={`ledger-occupancy-scene-marker-${row.sceneHeader.testIdKey}`}
                            className="mb-0.5 grid h-[20px] grid-cols-[42px_minmax(0,1fr)_34px] items-center border-y border-black bg-[#fffdfa] px-1.5 text-[8px] shadow-[1px_1px_0_#000] hover:bg-[#f5f3ec]"
                            title={row.sceneHeader.description || row.sceneHeader.title}
                            aria-label={`Jump to ${row.sceneHeader.label}: ${row.sceneHeader.title}`}
                            onClick={() => {
                              if (!activeVisibleWitness) {
                                return
                              }
                              handleFocusLedgerOccupancyCell({
                                rowId: row.rowId,
                                witnessId: activeVisibleWitness.id,
                                witnessSigla: activeVisibleWitness.sigla
                              })
                            }}
                          >
                            <span
                              data-testid={`ledger-occupancy-scene-marker-label-${row.sceneHeader.testIdKey}`}
                              className="truncate text-left font-mono font-bold uppercase tracking-normal text-gray-500"
                              title={row.sceneHeader.label}
                            >
                              {compactMapSceneLabel}
                            </span>
                            <span className="truncate px-1.5 text-center font-serif text-[9px] font-semibold text-black">
                              {formatEditorialTitleForDisplay(row.sceneHeader.title)}
                            </span>
                            <span dir="rtl" className="truncate text-right font-reader-arabic text-[9px] text-gray-500">
                              {row.sceneHeader.arabicTitle || 'ع'}
	                            </span>
	                          </button>
	                        ) : null
	                        const isVariableHeightOccupancyLayer = occupancyMapLayer === 'height' || occupancyMapLayer === 'spine'
	                        const isThinMetricOccupancyLayer = occupancyMapLayer === 'thin-line' || occupancyMapLayer === 'drift'
	                        const rowHeightClass = isVariableHeightOccupancyLayer
	                          ? 'items-stretch border-b border-black/10'
	                          : occupancyMapLayer === 'bead'
	                            ? 'h-[18px] items-center border-b border-black/10'
	                            : isThinMetricOccupancyLayer
	                              ? 'h-[12px] items-center border-b border-black/10'
	                              : 'h-[6px] items-center'
	                        const rowStyle = isVariableHeightOccupancyLayer
	                          ? { height: `${row.rowHeightPx}px` }
	                          : undefined

                            if (row.hiddenWitnessRun) {
                              const run = row.hiddenWitnessRun
                              const supportingWitnessLabels = hiddenWitnessRowRunSupportingLabelsByHeadRowId.get(run.rowIds[0]) || run.supportingWitnessIds
                              const witnessList = supportingWitnessLabels.join(', ')
                              return (
                                <button
                                  key={`occupancy-filtered:${run.rowIds.join(':')}`}
                                  type="button"
                                  data-testid={`ledger-occupancy-filtered-row-run-${run.rowIds.join('-')}`}
                                  aria-label={`${run.rowCount} ${run.rowCount === 1 ? 'row' : 'rows'} collapsed by witness filter; show ${witnessList}`}
                                  title={`${run.label} · attested only by ${witnessList} · click to restore`}
                                  onClick={() => handleRestoreFilteredRunWitnesses(run.supportingWitnessIds)}
                                  className="group/map-row flex h-[18px] w-full items-center gap-2 border-y border-dashed border-black/35 bg-[#f0ece3] pl-2 pr-1 text-left hover:bg-[#e4ded2]"
                                >
                                  <span className="shrink-0 font-mono text-[8px] font-bold text-[#475569]">
                                    {run.label}
                                  </span>
                                  <span className="h-px min-w-2 flex-1 border-t border-dashed border-black/25" aria-hidden="true" />
                                  <span className="shrink-0 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-[#6f6759]">
                                    {run.rowCount} filtered · show {supportingWitnessLabels.length === 1 ? supportingWitnessLabels[0] : `${supportingWitnessLabels.length} witnesses`}
                                  </span>
                                </button>
                              )
                            }

	                        if (row.rowIsStructurallyEmpty) {
	                          return (
	                            <React.Fragment key={`occupancy-row-fragment:${row.rowId}`}>
	                              {sceneMarker}
	                              <div
	                                className={`group/map-row relative flex h-3 items-center border-b border-dashed border-black/20 pl-7 ${rowToneClass}`}
	                                data-row={row.rowLabel}
	                              >
                                <button
                                  type="button"
                                  aria-label={`Occupancy map row ${row.rowLabel} empty row${isSelectedRow ? ' selected' : ''}`}
                                  title={`${row.rowLabel} · empty row`}
                                  onClick={() => handleFocusLedgerOccupancyCell({
                                    rowId: row.rowId,
                                    witnessId: activeVisibleWitness?.id || '',
                                    witnessSigla: activeVisibleWitness?.sigla || ''
                                  })}
                                  className="absolute inset-0 z-[1]"
                                />
                                <div className={`pointer-events-none absolute left-0 w-6 text-right font-mono text-[8px] transition-opacity ${rowLabelClass}`}>
                                  {row.rowLabel}
                                </div>
                              </div>
                            </React.Fragment>
                          )
                        }

                        return (
                          <React.Fragment key={`occupancy-row-fragment:${row.rowId}`}>
	                            {sceneMarker}
	                            <div
	                              data-testid={`ledger-occupancy-row-${row.rowId}`}
	                              className={`group/map-row relative flex pl-7 ${rowHeightClass} ${rowToneClass}`}
	                              style={rowStyle}
	                              data-row={row.rowLabel}
	                            >
                            <div className={`pointer-events-none absolute left-0 w-6 text-right font-mono text-[8px] transition-opacity ${rowLabelClass}`}>
                              {row.rowLabel}
                            </div>
                            <div className="flex h-full w-full gap-px">
                              {row.cells.filter(cell => visibleScopeWitnessIdSet.has(cell.witnessId)).map(cell => {
                                const isDisplayedAsGap = !row.rowIsStructurallyEmpty && cell.state !== 'mapped'
                                const isSelectedOccupancyCell = (
                                  (selectedCell?.rowId === cell.rowId && selectedCell?.witnessId === cell.witnessId)
                                  || selectedCharacterOccurrenceByCellKey.has(`${cell.rowId}:${cell.witnessId}`)
                                )
                                const isFocusedRowOnly = cell.isFocusedRow && !cell.isPreview
                                const boxShadows: string[] = []
                                const cellStyle: React.CSSProperties = {
                                  flex: 1,
                                  height: '100%',
                                  position: 'relative'
                                }
                                if (isSelectedOccupancyCell) {
                                  cellStyle.outline = `1px solid ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`
                                  cellStyle.outlineOffset = '1px'
                                }
                                if (cell.isPreview) {
                                  cellStyle.outline = `1px dashed ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`
                                  cellStyle.outlineOffset = '1px'
                                }
                                if (isFocusedRowOnly) {
                                  cellStyle.outline = `1px solid ${LEDGER_OCCUPANCY_INK_COLOR}`
                                  cellStyle.outlineOffset = '1px'
                                }

	                                if (occupancyMapLayer === 'height') {
	                                  cellStyle.overflow = 'hidden'
	                                  if (cell.state === 'mapped') {
	                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
	                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
                                  } else if (isDisplayedAsGap) {
                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
                                    cellStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.20)')
	                                  } else {
	                                    cellStyle.backgroundColor = 'transparent'
	                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
	                                  }
	                                } else if (occupancyMapLayer === 'spine') {
	                                  cellStyle.overflow = 'hidden'
	                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
	                                  cellStyle.backgroundImage = 'linear-gradient(to bottom, transparent calc(50% - 0.5px), rgba(0,0,0,0.16) calc(50% - 0.5px), rgba(0,0,0,0.16) calc(50% + 0.5px), transparent calc(50% + 0.5px))'
	                                  if (isDisplayedAsGap) {
	                                    cellStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
	                                  }
	                                  boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
	                                } else if (occupancyMapLayer === 'thin-line') {
	                                  cellStyle.overflow = 'hidden'
	                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
	                                  boxShadows.push('inset -1px 0 0 rgba(0,0,0,0.22)')
	                                } else if (occupancyMapLayer === 'drift') {
	                                  cellStyle.overflow = 'hidden'
	                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
	                                  boxShadows.push('inset -1px 0 0 rgba(0,0,0,0.22)')
	                                } else if (occupancyMapLayer === 'bead') {
	                                  cellStyle.overflow = 'hidden'
	                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
	                                  boxShadows.push('inset -1px 0 0 rgba(0,0,0,0.12)')
	                                } else if (occupancyMapLayer === 'variance') {
	                                  if (cell.state === 'mapped') {
	                                    if (cell.isVarianceReference) {
                                      cellStyle.backgroundColor = LEDGER_OCCUPANCY_REFERENCE_READING_COLOR
                                      cellStyle.backgroundImage = LEDGER_OCCUPANCY_REFERENCE_READING_STRIPES
                                      cellStyle.zIndex = 6
                                      boxShadows.push(`inset 0 -1px 0 ${LEDGER_OCCUPANCY_INK_COLOR}`)
                                    } else {
                                      cellStyle.backgroundColor = cell.varianceColor
                                    }
                                  } else if (isDisplayedAsGap) {
                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
                                    cellStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.20)')
                                  } else {
                                    cellStyle.backgroundColor = 'transparent'
                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
                                  }
                                } else if (occupancyMapLayer === 'triage') {
                                  if (cell.triageState === 'review') {
                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PI_PREVIEW_COLOR
                                    cellStyle.outline = `1px solid ${LEDGER_OCCUPANCY_PI_PREVIEW_COLOR}`
                                    cellStyle.outlineOffset = '1px'
                                    cellStyle.zIndex = 10
                                  } else if (cell.triageState === 'recent') {
                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PI_APPLIED_COLOR
                                    cellStyle.opacity = 0.7
                                  } else if (cell.state === 'mapped') {
                                    cellStyle.backgroundColor = '#e5e5e5'
                                  } else if (isDisplayedAsGap) {
                                    cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
                                    cellStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
                                  } else {
                                    cellStyle.backgroundColor = 'transparent'
                                    boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
                                  }
                                } else if (cell.state === 'mapped') {
                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_INK_COLOR
                                } else if (isDisplayedAsGap) {
                                  cellStyle.backgroundColor = LEDGER_OCCUPANCY_PAPER_COLOR
                                  cellStyle.backgroundImage = `repeating-linear-gradient(135deg, ${LEDGER_OCCUPANCY_PAPER_COLOR} 0, ${LEDGER_OCCUPANCY_PAPER_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 2px, ${LEDGER_OCCUPANCY_PAPER_ALT_COLOR} 4px)`
                                  boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.20)')
                                } else {
                                  cellStyle.backgroundColor = 'transparent'
                                  boxShadows.push('inset 0 0 0 1px rgba(0,0,0,0.18)')
                                }

                                if (boxShadows.length > 0) {
                                  cellStyle.boxShadow = boxShadows.join(', ')
                                }
	                                const annotationBits = [
	                                  occupancyMapLayer === 'height' && cell.state === 'mapped' ? `${cell.unitTextLength} text units` : '',
	                                  occupancyMapLayer === 'thin-line' && cell.state === 'mapped' ? `${cell.unitTextLength} text units` : '',
	                                  occupancyMapLayer === 'drift' && cell.state === 'mapped' ? `${cell.cumulativeUnitTextLength} cumulative text units` : '',
	                                  occupancyMapLayer === 'bead' && cell.state === 'mapped' ? `${cell.unitTextLength} text units` : '',
	                                  occupancyMapLayer === 'spine' && cell.state === 'mapped' ? `${cell.unitTextLength} text units` : '',
	                                  occupancyMapLayer === 'variance' && cell.isVarianceReference ? 'reference reading' : '',
	                                  occupancyMapLayer === 'variance' && cell.state === 'mapped' ? cell.varianceLabel : '',
                                  occupancyMapLayer === 'triage' && cell.triageState === 'review'
                                    ? 'review needed'
	                                    : occupancyMapLayer === 'triage' && cell.triageState === 'recent'
	                                      ? 'recent Pi edit'
	                                      : ''
	                                ].filter(Boolean)
	                                const lengthToneColor = cell.unitLengthTone === 'long'
	                                  ? LEDGER_OCCUPANCY_INK_COLOR
	                                  : cell.unitLengthTone === 'medium'
	                                    ? '#4a4438'
	                                    : '#908577'
	                                const beadSizePx = Math.max(4, Math.round(4 + cell.unitGlobalHeightRatio * 14))

	                                return (
	                                  <button
                                    key={`occupancy-cell:${cell.rowId}:${cell.witnessId}`}
                                    type="button"
                                    aria-label={`Occupancy map ${cell.witnessSigla} ${cell.rowLabel} ${cell.displayStateLabel}${annotationBits.length ? ` ${annotationBits.join(' ')}` : ''}${isSelectedOccupancyCell ? ' selected' : ''}${cell.isPreview ? ' preview' : ''}`}
                                    title={`${cell.witnessSigla} · ${cell.rowLabel} · ${cell.displayStateLabel}${annotationBits.length ? ` · ${annotationBits.join(' · ')}` : ''}`}
                                    onClick={() => handleFocusLedgerOccupancyCell(cell)}
                                    onMouseEnter={() => setActiveOccupancyMapWitnessId(cell.witnessId)}
                                    onMouseLeave={() => setActiveOccupancyMapWitnessId(previous => previous === cell.witnessId ? null : previous)}
                                    onFocus={() => setActiveOccupancyMapWitnessId(cell.witnessId)}
                                    onBlur={() => setActiveOccupancyMapWitnessId(previous => previous === cell.witnessId ? null : previous)}
                                    className="h-full flex-1"
                                    style={cellStyle}
                                  >
	                                    {occupancyMapLayer === 'height' && cell.state === 'mapped' && (
	                                      <span
	                                        data-testid={`ledger-occupancy-height-fill-${cell.rowId}-${cell.witnessId}`}
	                                        aria-hidden="true"
	                                        className="pointer-events-none absolute left-0 right-0 top-0 border-b border-black"
                                        style={{
                                          height: `${Math.round(cell.unitHeightRatio * 100)}%`,
                                          backgroundColor: LEDGER_OCCUPANCY_INK_COLOR
	                                        }}
	                                      />
	                                    )}
	                                    {occupancyMapLayer === 'spine' && cell.state === 'mapped' && (
	                                      <span
	                                        data-testid={`ledger-occupancy-spine-fill-${cell.rowId}-${cell.witnessId}`}
	                                        aria-hidden="true"
	                                        className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 border-y border-black"
	                                        style={{
	                                          height: `${Math.round(cell.unitHeightRatio * 100)}%`,
	                                          backgroundColor: LEDGER_OCCUPANCY_INK_COLOR
	                                        }}
	                                      />
	                                    )}
	                                    {occupancyMapLayer === 'thin-line' && (
	                                      cell.state === 'mapped' ? (
	                                        <span
	                                          data-testid={`ledger-occupancy-thin-line-stroke-${cell.rowId}-${cell.witnessId}`}
	                                          aria-hidden="true"
	                                          className="pointer-events-none absolute right-0 top-1/2 h-px -translate-y-1/2"
	                                          style={{
	                                            width: `${Math.round(cell.unitGlobalHeightRatio * 100)}%`,
	                                            backgroundColor: lengthToneColor
	                                          }}
	                                        />
	                                      ) : isDisplayedAsGap ? (
	                                        <span
	                                          aria-hidden="true"
	                                          className="pointer-events-none absolute right-0 top-1/2 h-px w-2 -translate-y-1/2"
	                                          style={{
	                                            backgroundImage: `repeating-linear-gradient(90deg, ${LEDGER_OCCUPANCY_INK_COLOR} 0, ${LEDGER_OCCUPANCY_INK_COLOR} 2px, transparent 2px, transparent 4px)`
	                                          }}
	                                        />
	                                      ) : null
	                                    )}
	                                    {occupancyMapLayer === 'drift' && (
	                                      <>
	                                        <span
	                                          data-testid={`ledger-occupancy-drift-area-${cell.rowId}-${cell.witnessId}`}
	                                          aria-hidden="true"
	                                          className="pointer-events-none absolute bottom-0 right-0 top-0"
	                                          style={{
	                                            width: `${Math.round(cell.cumulativeUnitTextRatio * 100)}%`,
	                                            backgroundColor: 'rgba(15, 23, 42, 0.08)'
	                                          }}
	                                        />
	                                        {cell.state === 'mapped' && (
	                                          <span
	                                            data-testid={`ledger-occupancy-drift-stroke-${cell.rowId}-${cell.witnessId}`}
	                                            aria-hidden="true"
	                                            className="pointer-events-none absolute right-0 top-1/2 h-px -translate-y-1/2"
	                                            style={{
	                                              width: `${Math.round(cell.unitGlobalHeightRatio * 100)}%`,
	                                              backgroundColor: lengthToneColor
	                                            }}
	                                          />
	                                        )}
	                                      </>
	                                    )}
	                                    {occupancyMapLayer === 'bead' && (
	                                      <>
	                                        <span
	                                          aria-hidden="true"
	                                          className="pointer-events-none absolute bottom-0 top-0 w-px bg-black/15"
	                                          style={{ left: '50%' }}
	                                        />
	                                        {cell.state === 'mapped' ? (
	                                          <span
	                                            data-testid={`ledger-occupancy-bead-${cell.rowId}-${cell.witnessId}`}
	                                            aria-hidden="true"
	                                            className="pointer-events-none absolute left-1/2 top-1/2 rounded-full border border-black -translate-x-1/2 -translate-y-1/2"
	                                            style={{
	                                              width: `${beadSizePx}px`,
	                                              height: `${beadSizePx}px`,
	                                              backgroundColor: lengthToneColor
	                                            }}
	                                          />
	                                        ) : isDisplayedAsGap ? (
	                                          <span
	                                            aria-hidden="true"
	                                            className="pointer-events-none absolute left-[15%] right-[15%] top-1/2 h-px -translate-y-1/2"
	                                            style={{
	                                              backgroundImage: `repeating-linear-gradient(90deg, ${LEDGER_OCCUPANCY_INK_COLOR} 0, ${LEDGER_OCCUPANCY_INK_COLOR} 2px, transparent 2px, transparent 4px)`
	                                            }}
	                                          />
	                                        ) : null}
	                                      </>
	                                    )}
	                                  </button>
	                                )
                              })}
                            </div>
                            </div>
                          </React.Fragment>
                        )
                      }}
                    />
                    )}
                  </div>
                </div>
              </div>
            )}

            {sidePanelTab === 'inspector' && (
              <div id="ledger-side-panel-tabpanel-inspector" role="tabpanel" aria-labelledby="ledger-side-panel-tab-inspector" className="space-y-4">
                <div className="border border-black bg-gray-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Inspector</div>
                  <div className="mt-2 text-sm font-semibold text-black">
                    {activeWitness ? `${activeWitness.sigla} · ${describeManualStudioScope(activeScope)}` : 'No witness selected'}
                  </div>
                  <div className="mt-2 text-sm font-semibold text-black">
                    Witness-local boundary edits only. Collation edits are explicit row operations on stable unit mappings.
                  </div>
                  {piDriftWindows.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {piDriftWindows.map((window, index) => (
                        <div key={`${window.rowStart}:${window.rowEnd}:${index}`} className="border border-black bg-white px-3 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                Rows {window.rowStart}–{window.rowEnd}
                              </div>
                              <div className="mt-1 text-xs leading-5 text-gray-600">
                                {window.affectedWitnessIds.length > 0
                                  ? `Affected witnesses: ${window.affectedWitnessIds.map(id => scopeWitnesses.find(witness => witness.id === id)?.sigla || id).join(', ')}`
                                  : 'No witness list returned'}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleFocusPiWindow(window, 'drift')}
                              disabled={isMatrixContentApplying}
                              className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                            >
                              Focus
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 border border-dashed border-gray-300 bg-white p-4 text-xs text-gray-500">
                      No drift windows yet. Sync the Pi session and run drift detection when you want a focused review window.
                    </div>
                  )}
                </div>
              </div>
            )}

            {runtimeEnablePropagation && sidePanelTab === 'propagation' && (
              <div id="ledger-side-panel-tabpanel-propagation" role="tabpanel" aria-labelledby="ledger-side-panel-tab-propagation" className="space-y-4">
                <div className="border border-black bg-gray-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Propagation</div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      aria-pressed={propagationMode === 'witness'}
                      onClick={() => setPropagationMode('witness')}
                      className={`h-9 border border-black px-3 text-[10px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                        propagationMode === 'witness'
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Witness
                    </button>
                    <button
                      type="button"
                      aria-pressed={propagationMode === 'context'}
                      onClick={() => setPropagationMode('context')}
                      className={`h-9 border border-black px-3 text-[10px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                        propagationMode === 'context'
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Queue
                    </button>
                    <button
                      type="button"
                      aria-pressed={propagationMode === 'semantic'}
                      onClick={() => setPropagationMode('semantic')}
                      className={`h-9 border border-black px-2 text-[10px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                        propagationMode === 'semantic'
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Semantic
                    </button>
                    <button
                      type="button"
                      aria-pressed={propagationMode === 'cross-tradition'}
                      onClick={() => setPropagationMode('cross-tradition')}
                      className={`h-9 border border-black px-2 text-[10px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                        propagationMode === 'cross-tradition'
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Cross Tradition
                    </button>
                  </div>

                  {(propagationMode === 'witness' || propagationMode === 'context') && (
                    <div
                      data-testid="ledger-propagation-model-controls"
                      className="mt-3 border border-gray-200 bg-gray-50 px-3 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Model controls</div>
                        <div className="border border-gray-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-gray-600">
                          Pi + Gemini
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div>
                          <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                              Propagation
                            </span>
                            <select
                              data-testid="ledger-propagation-model-select"
                              value={propagationModelId}
                              onChange={event => setPropagationModelId(normalizeLedgerSafeAlignmentModelId(event.target.value))}
                              disabled={isWitnessPropagationBusy || contextPropagationState.previewing || contextPropagationState.applying}
                              className="mt-1 h-9 w-full border border-gray-300 bg-white px-2 text-xs text-gray-800 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                              {LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS.map(option => (
                                <option key={`propagation-model-${option.id}`} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          {isLedgerSafePiAlignmentModel(propagationModelId) && (
                            <label className="mt-2 block">
                              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                Thinking effort
                              </span>
                              <select
                                data-testid="ledger-propagation-thinking-select"
                                value={propagationThinkingLevel}
                                onChange={event => setPropagationThinkingLevel(
                                  normalizeLedgerSafeAlignmentThinkingLevel(event.target.value)
                                )}
                                disabled={isWitnessPropagationBusy || contextPropagationState.previewing || contextPropagationState.applying}
                                className="mt-1 h-9 w-full border border-gray-300 bg-white px-2 text-xs text-gray-800 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                {LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.map(option => (
                                  <option key={`propagation-thinking-${option.value}`} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                        <div>
                          <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                              Selected repair
                            </span>
                            <select
                              data-testid="ledger-repair-model-select"
                              value={selectedUnitRepairModelId}
                              onChange={event => setSelectedUnitRepairModelId(normalizeLedgerSafeAlignmentModelId(event.target.value))}
                              disabled={Boolean(selectedUnitRepairRun) || contextPropagationState.previewing || contextPropagationState.applying}
                              className="mt-1 h-9 w-full border border-gray-300 bg-white px-2 text-xs text-gray-800 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                              {LEDGER_SAFE_ALIGNMENT_MODEL_OPTIONS.map(option => (
                                <option key={`repair-model-${option.id}`} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          {isLedgerSafePiAlignmentModel(selectedUnitRepairModelId) && (
                            <label className="mt-2 block">
                              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                Thinking effort
                              </span>
                              <select
                                data-testid="ledger-repair-thinking-select"
                                value={selectedUnitRepairThinkingLevel}
                                onChange={event => setSelectedUnitRepairThinkingLevel(
                                  normalizeLedgerSafeAlignmentThinkingLevel(event.target.value)
                                )}
                                disabled={Boolean(selectedUnitRepairRun) || contextPropagationState.previewing || contextPropagationState.applying}
                                className="mt-1 h-9 w-full border border-gray-300 bg-white px-2 text-xs text-gray-800 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                {LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.map(option => (
                                  <option key={`repair-thinking-${option.value}`} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {propagationMode === 'witness' ? (
                    activeWitness ? (
                      <div className="mt-3 space-y-3 border border-black bg-white p-3">
                        <div className="text-xs text-gray-700">
                          <span className="font-semibold">Target witness: {activeWitness.sigla}</span>
                        </div>
                        <div className="text-xs leading-5 text-gray-600">
                          Generate a new full-witness propagation proposal from the current accepted collation.
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-gray-500">
                          <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                            <div className="font-bold text-gray-700">Current state</div>
                            <div className="mt-1 text-gray-600">{activeWitnessSegmentationState}</div>
                          </div>
                          <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                            <div className="font-bold text-gray-700">Accepted rows</div>
                            <div className="mt-1 text-gray-600">{resolvedRows.length}</div>
                          </div>
                        </div>
                        {witnessPropagationError && (
                          <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                            {witnessPropagationError}
                          </div>
                        )}
                        {witnessPropagationFailures
                          .filter(failure => failure.witnessId === activeWitness.id && failure.diagnostics)
                          .map(failure => (
                            <button
                              key={`failure-diagnostics-${failure.witnessId}`}
                              type="button"
                              onClick={() => failure.diagnostics && downloadTranslationAlignmentDiagnostics(failure.diagnostics, activeWitness.sigla)}
                              className="h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                            >
                              Download raw alignment output
                            </button>
                          ))}
                        {witnessPropagationSuccess && (
                          <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                            {witnessPropagationSuccess}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleGenerateWitnessPropagationProposal()}
                          disabled={isMatrixContentApplying || isWitnessPropagationBusy || !chapterId || resolvedRows.length === 0}
                          className="h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {isWitnessPropagationBusy ? 'Generating…' : 'Generate new propagation proposal'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAnalyzeWitnessAppliedPropagation()}
                          disabled={isMatrixContentApplying || isWitnessPropagationBusy || resolvedRows.length === 0}
                          className="h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Analyze applied result
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs leading-5 text-gray-500">
                        Select a witness to generate a new propagation proposal.
                      </div>
                    )
                  ) : propagationMode === 'context' ? (
                    <div className="mt-3 space-y-3 border border-black bg-white p-3">
                      <div className="text-xs leading-5 text-gray-600">
                        Enroll remaining witnesses in distance order after the representative collation is accepted. Select one or more runnable witnesses, preview the row spans, then apply the proposal.
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-gray-500">
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Representatives</div>
                          <div className="mt-1 text-gray-600">{enrollmentRepresentativeWitnesses.length}</div>
                        </div>
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Runnable</div>
                          <div className="mt-1 text-gray-600">{selectableEnrollmentQueueItems.length}</div>
                        </div>
                      </div>
                      <div className="border border-gray-200 bg-gray-50 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Selected unit repair</div>
                            <div className="mt-2 text-xs font-semibold text-black">
                              {selectedUnitRepairPreview
                                ? `${selectedUnitRepairPreview.targetWitness.sigla} · ${selectedUnitRepairPreview.selectedRowIds
                                    .map(rowId => rowById.get(rowId))
                                    .filter(Boolean)
                                    .map(row => getRowLabel(row as AnchoredRow))
                                    .join('–')}`
                                : selectedUnitRepairScope.witness
                                  ? `${selectedUnitRepairScope.witness.sigla} · ${selectedUnitRepairScope.label}`
                                  : 'No selected witness unit'}
                            </div>
                            <div className="mt-1 text-[11px] leading-5 text-gray-600">
                              {selectedUnitRepairRun
                                ? selectedUnitRepairPendingBatchNumber
                                  ? `${selectedUnitRepairCompletedBatches}/${selectedUnitRepairRun.pendingResumeState?.totalBatches || selectedUnitRepairRun.resumeState.totalBatches} applied · batch ${selectedUnitRepairPendingBatchNumber} ready for review`
                                  : selectedUnitRepairRunComplete
                                    ? `${selectedUnitRepairCompletedBatches}/${selectedUnitRepairRun.resumeState.totalBatches} batches applied`
                                    : `${selectedUnitRepairCompletedBatches}/${selectedUnitRepairRun.resumeState.totalBatches} applied · continue when ready`
                                : selectedUnitRepairScope.reason
                                ? selectedUnitRepairScope.reason
                                : `${selectedUnitRepairScope.selectedRows.length} selected source row${selectedUnitRepairScope.selectedRows.length === 1 ? '' : 's'} · selected span segmented against chapter spine`}
                            </div>
                          </div>
                          <div className="shrink-0 border border-gray-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-gray-600">
                            {selectedUnitRepairRun
                              ? selectedUnitRepairPendingBatchNumber
                                ? `Batch ${selectedUnitRepairPendingBatchNumber} ready`
                                : selectedUnitRepairRunComplete
                                  ? 'Complete'
                                  : `${selectedUnitRepairCompletedBatches} applied`
                              : selectedUnitRepairPreview
                                ? 'Preview ready'
                                : selectedUnitRepairScope.reason
                                  ? 'Idle'
                                  : 'Runnable'}
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => void (
                              selectedUnitRepairRun
                                ? selectedUnitRepairRun.pendingResumeState
                                  ? handleRetrySelectedUnitRepairBatch()
                                  : selectedUnitRepairRun.failure
                                    ? handleRetrySelectedUnitRepairBatch()
                                    : handleContinueSelectedUnitRepairPreview()
                                : handleGenerateSelectedUnitRepairPreview()
                            )}
                            disabled={
                              isMatrixContentApplying
                              || contextPropagationState.previewing
                              || contextPropagationState.applying
                              || selectedUnitRepairRunComplete
                              || Boolean(selectedUnitRepairRun?.pendingResumeState && selectedUnitRepairPreview)
                              || (!selectedUnitRepairRun && (
                                !chapterId
                                || Boolean(selectedUnitRepairScope.reason)
                              ))
                            }
                            className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            {contextPropagationState.previewing
                              ? 'Preparing…'
                              : selectedUnitRepairRunComplete
                                ? 'Repair complete'
                                : selectedUnitRepairRun
                                  ? selectedUnitRepairRun.pendingResumeState
                                    ? selectedUnitRepairPreview
                                      ? `Review batch ${selectedUnitRepairPendingBatchNumber}`
                                      : `Retry batch ${selectedUnitRepairRun.resumeState.batchIndex + 1}`
                                    : selectedUnitRepairRun.failure
                                      ? `Retry batch ${selectedUnitRepairRun.resumeState.batchIndex + 1}`
                                      : `Continue to batch ${selectedUnitRepairRun.resumeState.batchIndex + 1}`
                                  : 'Prepare selected repair'}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDiscardSelectedUnitRepairPreview()}
                            disabled={isMatrixContentApplying || (!selectedUnitRepairPreview && !selectedUnitRepairRun) || contextPropagationState.previewing || contextPropagationState.applying}
                            className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Discard repair
                          </button>
                        </div>
                        {selectedUnitRepairUndo && (
                          <button
                            type="button"
                            onClick={() => void handleUndoSelectedUnitRepair()}
                            disabled={isMatrixContentApplying || contextPropagationState.previewing || contextPropagationState.applying}
                            className="mt-2 h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Undo last repair · {selectedUnitRepairUndo.witnessSigla}
                          </button>
                        )}
                      </div>
                      {fallbackContextPropagationAnchor && (
                        <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                          Local fallback anchor: <span className="font-semibold text-black">{fallbackContextPropagationAnchor.sigla}</span>
                        </div>
                      )}
                      {enrollmentQueue.length > 0 ? (
                        <div className="space-y-2">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Distance order</div>
                          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                            {enrollmentQueue.map(item => {
                              const selected = selectedContextPropagationTargetWitnessIdSet.has(item.witness.id)
                              const disabled = (
                                item.status !== 'pending' &&
                                item.status !== 'rerun'
                              ) || contextPropagationState.previewing || contextPropagationState.applying
                              const similarityPercent = Math.round(item.similarity * 100)
                              const rerunCandidate = item.status === 'rerun'
                              const mappedCandidate = enrollmentMappedWitnessIdSet.has(item.witness.id)
                              return (
                                <label
                                  key={`enrollment-queue-${item.witness.id}`}
                                  className={`flex items-start gap-3 border px-3 py-2 text-xs ${
                                    item.status === 'pending' || item.status === 'rerun'
                                      ? 'border-gray-200 bg-gray-50 text-gray-700'
                                      : 'border-amber-200 bg-amber-50 text-amber-800'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    disabled={disabled}
                                    onChange={() => toggleContextPropagationTargetWitness(item.witness.id)}
                                    aria-label={`Select ${item.witness.sigla} for enrollment`}
                                    className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-black focus:ring-black disabled:cursor-not-allowed"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="font-semibold text-black">
                                          {String(item.rank).padStart(2, '0')} · {item.witness.description || item.witness.sigla}
                                        </div>
                                        <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">{item.witness.sigla}</div>
                                      </div>
                                      <div className="shrink-0 border border-gray-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-gray-600">
                                        {rerunCandidate ? 'Rerun' : item.status === 'pending' ? `${similarityPercent}% close` : 'Protected'}
                                      </div>
                                    </div>
                                    <div className="mt-2 text-[11px] leading-5 text-gray-600">
                                      Closest representative: <span className="font-semibold text-gray-800">{item.closestRepresentative?.sigla || 'none'}</span>
                                      {' '}· distance {(item.distance).toFixed(2)}
                                    </div>
                                    {rerunCandidate && (
                                      <div className="mt-1 text-[11px] leading-5 text-gray-600">
                                        {mappedCandidate
                                          ? 'Existing row mappings and segmentation will be refreshed only if this proposal is applied.'
                                          : 'Existing segmentation will be replaced only if this proposal is applied.'}
                                      </div>
                                    )}
                                    {item.status === 'protected' && (
                                      <div className="mt-1 text-[11px] leading-5">
                                        Existing manual or unknown segmentation is protected. Use witness replacement if you want to overwrite it deliberately.
                                      </div>
                                    )}
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                          {enrollmentQueueEmptyReason}
                        </div>
                      )}
                      {contextPropagationState.error && (
                        <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                          {contextPropagationState.error}
                        </div>
                      )}
                      {contextPropagationState.success && (
                        <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                          {contextPropagationState.success}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleGenerateContextPropagationPreview()}
                        disabled={
                          contextPropagationState.previewing
                          || contextPropagationState.applying
                          || !chapterId
                          || resolvedRows.length === 0
                          || !fallbackContextPropagationAnchor
                          || selectedContextPropagationTargetWitnessIds.filter(witnessId => selectableEnrollmentQueueWitnessIdSet.has(witnessId)).length === 0
                        }
                        className="h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                      >
                        {contextPropagationState.previewing ? 'Generating…' : 'Prepare selected enrollment'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnalyzeContextAppliedPropagation()}
                        disabled={contextPropagationState.previewing || contextPropagationState.applying || resolvedRows.length === 0 || !fallbackContextPropagationAnchor}
                        className="h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        Analyze current applied results
                      </button>
                    </div>
                  ) : propagationMode === 'semantic' ? (
                    <div className="mt-3 space-y-3 border border-black bg-white p-3">
                      <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-gray-500">
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Source</div>
                          <div className="mt-1 text-gray-600">
                            {selectedRange?.rowIds?.length
                              ? `${selectedRange.rowIds.length} rows`
                              : selectedLedgerRow
                                ? getRowLabel(selectedLedgerRow)
                                : 'No row'}
                          </div>
                        </div>
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Visible targets</div>
                          <div className="mt-1 text-gray-600">{visibleScopeWitnesses.length}</div>
                        </div>
                      </div>
                      {semanticPreviewState.error && (
                        <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                          {semanticPreviewState.error}
                        </div>
                      )}
                      {semanticPreviewState.success && (
                        <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                          {semanticPreviewState.success}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleGenerateSemanticPreview()}
                        disabled={
                          isMatrixContentApplying
                          || semanticPreviewState.previewing
                          || semanticPreviewState.applying
                          || !chapterId
                          || resolvedRows.length === 0
                          || (!selectedCell && !selectedRange)
                        }
                        className="h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                      >
                        {semanticPreviewState.previewing ? 'Previewing…' : 'Preview semantic ghosts'}
                      </button>
                      {semanticPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSemanticPreview(null)
                            setSelectedSemanticCandidateId(null)
                            setSemanticPreviewState({
                              previewing: false,
                              applying: false,
                              error: null,
                              success: null,
                            })
                          }}
                          disabled={isMatrixContentApplying || semanticPreviewState.previewing || semanticPreviewState.applying}
                          className="h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Discard semantic ghosts
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 space-y-3 border border-black bg-white p-3" data-testid="ledger-cross-tradition-panel">
                      <div className="text-xs leading-5 text-gray-600">
                        Select one or more witnesses from another work, preview row-level parallels against the accepted Ledger spine, then materialize accepted links as active-collation projection profiles.
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-gray-500">
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Verifier</div>
                          <div className="mt-1 text-gray-600">{LEDGER_CROSS_TRADITION_RERANK_MODEL_ID}</div>
                        </div>
                        <div className="border border-gray-200 bg-gray-50 px-2 py-2">
                          <div className="font-bold text-gray-700">Selected</div>
                          <div className="mt-1 text-gray-600">{selectedCrossTraditionWitnessIds.length}</div>
                        </div>
                      </div>
                      {crossTraditionSourceOptions.length === 0 ? (
                        <div className="border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                          No external text-bearing witnesses are available outside this work.
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <label htmlFor="ledger-cross-tradition-work" className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
                              Source work
                            </label>
                            <select
                              id="ledger-cross-tradition-work"
                              data-testid="ledger-cross-tradition-work-select"
                              value={crossTraditionWorkId}
                              onChange={event => {
                                setCrossTraditionWorkId(event.target.value)
                                setCrossTraditionChapterId('')
                                setSelectedCrossTraditionWitnessIds([])
                              }}
                              disabled={crossTraditionState.previewing || crossTraditionState.applying}
                              className="h-9 w-full border border-black bg-white px-2 text-[11px] font-bold text-black"
                            >
                              {crossTraditionWorks.map(work => (
                                <option key={work.id} value={work.id}>{work.title}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="ledger-cross-tradition-chapter" className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">
                              Source chapter
                            </label>
                            <select
                              id="ledger-cross-tradition-chapter"
                              data-testid="ledger-cross-tradition-chapter-select"
                              value={crossTraditionChapterId}
                              onChange={event => {
                                setCrossTraditionChapterId(event.target.value)
                                setSelectedCrossTraditionWitnessIds([])
                              }}
                              disabled={crossTraditionState.previewing || crossTraditionState.applying}
                              className="h-9 w-full border border-black bg-white px-2 text-[11px] font-bold text-black"
                            >
                              {crossTraditionChaptersForWork.map(nextChapter => (
                                <option key={nextChapter.id} value={nextChapter.id}>{nextChapter.title}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">Witnesses</div>
                              <button
                                type="button"
                                onClick={() => setSelectedCrossTraditionWitnessIds(crossTraditionWitnessOptions.map(option => option.witnessId))}
                                disabled={crossTraditionState.previewing || crossTraditionState.applying || crossTraditionWitnessOptions.length === 0}
                                className="text-[9px] font-bold uppercase tracking-widest text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:text-gray-300"
                              >
                                Select shown
                              </button>
                            </div>
                            <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
                              {crossTraditionWitnessOptions.length === 0 ? (
                                <div className="border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                                  No witnesses are available for this source chapter.
                                </div>
                              ) : crossTraditionWitnessOptions.map(option => {
                                const selected = selectedCrossTraditionWitnessIds.includes(option.witnessId)
                                return (
                                  <label
                                    key={option.witnessId}
                                    className={`flex cursor-pointer items-start gap-3 border px-3 py-2 text-xs ${
                                      selected ? 'border-black bg-gray-50 text-black' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      disabled={crossTraditionState.previewing || crossTraditionState.applying}
                                      onChange={() => toggleCrossTraditionWitnessSelection(option.witnessId)}
                                      aria-label={`Select ${option.sigla} for cross-tradition collation`}
                                      className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-black focus:ring-black disabled:cursor-not-allowed"
                                    />
                                    <span className="min-w-0">
                                      <span className="block font-semibold text-black">{option.sigla}</span>
                                      <span className="mt-1 block truncate text-[10px] uppercase tracking-widest text-gray-500" title={option.witness.description}>
                                        {option.witness.description || option.contextLabel}
                                      </span>
                                    </span>
                                  </label>
                                )
                              })}
                            </div>
                          </div>
                        </>
                      )}
                      {crossTraditionState.error && (
                        <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                          {crossTraditionState.error}
                        </div>
                      )}
                      {crossTraditionState.success && (
                        <div className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                          {crossTraditionState.success}
                        </div>
                      )}
                      <div className="space-y-2 border border-gray-200 bg-gray-50 p-2" data-testid="ledger-cross-tradition-run-history">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">Preview runs</div>
                          <div className="text-[9px] uppercase tracking-widest text-gray-500">
                            {crossTraditionPreviewRuns.length === 0 ? 'None' : `${crossTraditionPreviewRuns.length} saved`}
                          </div>
                        </div>
                        {crossTraditionPreviewRuns.length === 0 ? (
                          <div className="border border-dashed border-gray-300 bg-white p-2 text-xs text-gray-500">
                            No previous cross-tradition preview runs yet.
                          </div>
                        ) : (
                          <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
                            {crossTraditionPreviewRuns.map(run => {
                              const isSelectedRun = selectedCrossTraditionPreviewRun?.runId === run.runId
                              const isActiveRun = crossTraditionState.activeRunId === run.runId && crossTraditionState.previewing
                              return (
                                <div
                                  key={run.runId}
                                  className={`border bg-white px-2 py-2 text-xs ${
                                    isSelectedRun ? 'border-black text-black' : 'border-gray-200 text-gray-700'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="truncate font-semibold text-black" title={run.witnessLabels.join(', ') || run.sourceContextLabel}>
                                        {run.witnessLabels.join(', ') || 'External witnesses'}
                                      </div>
                                      <div className="mt-1 truncate text-[10px] uppercase tracking-widest text-gray-500" title={run.sourceContextLabel}>
                                        {formatLedgerCrossTraditionRunTime(run.startedAt)} · {run.candidateCount} candidate{run.candidateCount === 1 ? '' : 's'}
                                      </div>
                                    </div>
                                    <div className={`shrink-0 border px-2 py-1 text-[9px] uppercase tracking-widest ${getLedgerCrossTraditionRunStatusClassName(isActiveRun ? 'running' : run.status)}`}>
                                      {getLedgerCrossTraditionRunStatusLabel(isActiveRun ? 'running' : run.status)}
                                    </div>
                                  </div>
                                  {run.error && (
                                    <div className="mt-2 line-clamp-2 text-[11px] leading-4 text-gray-500">
                                      {run.error}
                                    </div>
                                  )}
                                  <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleInspectCrossTraditionPreviewRun(run)}
                                      disabled={crossTraditionState.previewing || crossTraditionState.applying}
                                      className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                    >
                                      Inspect
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleResumeCrossTraditionPreviewRun(run)}
                                      disabled={crossTraditionState.previewing || crossTraditionState.applying || run.witnessIds.length === 0}
                                      className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                    >
                                      Resume
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                      <div className={crossTraditionState.previewing ? 'grid grid-cols-[minmax(0,1fr)_auto] gap-2' : ''}>
                        <button
                          type="button"
                          onClick={() => void handleGenerateCrossTraditionPreview()}
                          disabled={
                            crossTraditionState.previewing
                            || crossTraditionState.applying
                            || crossTraditionSourceOptions.length === 0
                            || selectedCrossTraditionWitnessIds.length === 0
                            || resolvedRows.length === 0
                          }
                          className="h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {crossTraditionState.previewing ? 'Previewing…' : 'Preview cross-tradition links'}
                        </button>
                        {crossTraditionState.previewing && (
                          <button
                            type="button"
                            onClick={() => handleCancelCrossTraditionPreview()}
                            className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                      {crossTraditionCandidates.length > 0 && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                          type="button"
                          onClick={() => void handleApplyCrossTraditionCandidates()}
                          disabled={isMatrixContentApplying || crossTraditionState.previewing || crossTraditionState.applying || crossTraditionCandidates.every(candidate => candidate.reviewStatus !== 'accepted')}
                            className="h-9 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            {crossTraditionState.applying ? 'Applying…' : 'Apply to Ledger'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDiscardCrossTraditionPreview()}
                            disabled={crossTraditionState.previewing || crossTraditionState.applying}
                            className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Discard
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {propagationMode === 'witness' && witnessPropagationProposal && activeWitness && (
                  <div className="border border-black bg-white p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Proposal summary</div>
                    <div className="mt-3 space-y-2 text-xs text-gray-700">
                      <div><span className="font-semibold">Method:</span> {witnessPropagationProposal.method}</div>
                      <div><span className="font-semibold">Exemplars:</span> {witnessPropagationExemplarSummary}</div>
                      <div>
                        <span className="font-semibold">{witnessPropagationProposal.readOnly ? 'Current segments:' : 'Proposed segments:'}</span>{' '}
                        {witnessPropagationProposal.segmentCount}
                      </div>
                      {!witnessPropagationProposal.readOnly && (
                        <div><span className="font-semibold">Rows changed:</span> {witnessPropagationProposal.application.changedRowIds.length}</div>
                      )}
                      {witnessPropagationProposal.notes && (
                        <div><span className="font-semibold">Notes:</span> {witnessPropagationProposal.notes}</div>
                      )}
                      {witnessPropagationProposal.translationProgress && (
                        <div>
                          <span className="font-semibold">Batch progress:</span>{' '}
                          {witnessPropagationProposal.translationProgress.completedBatches}/{witnessPropagationProposal.translationProgress.totalBatches}
                          {witnessPropagationProposal.translationProgress.complete ? ' complete' : ''}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                      {witnessPropagationProposal.readOnly
                        ? 'Read-only audit of the current applied state. No witness segmentation or collation rows will be changed from this panel.'
                        : 'Preview rows are ghosted directly in the collation until you apply or discard this proposal.'}
                    </div>
                    {(() => {
                      const proposalDiagnostics = witnessPropagationProposal.diagnostics
                      if (!proposalDiagnostics) {
                        return null
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => downloadTranslationAlignmentDiagnostics(proposalDiagnostics, activeWitness.sigla)}
                          className="mt-3 h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                        >
                          Download raw alignment output
                        </button>
                      )
                    })()}
                    <div className="mt-3 flex gap-2">
                      {witnessPropagationResumeState && !witnessPropagationProposal.readOnly && (
                        <button
                          type="button"
                          onClick={() => void handleResumeWitnessPropagationProposal()}
                          disabled={isMatrixContentApplying || isWitnessPropagationBusy}
                          className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          {isWitnessPropagationBusy ? 'Resuming…' : 'Resume failed batch'}
                        </button>
                      )}
                      {!witnessPropagationProposal.readOnly && (
                        <button
                          type="button"
                          onClick={() => void handleApplyWitnessPropagationProposal()}
                          disabled={isMatrixContentApplying || isWitnessPropagationBusy}
                          className="h-9 flex-1 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {witnessPropagationProposal.translationProgress && !witnessPropagationProposal.translationProgress.complete
                            ? (isWitnessPropagationBusy ? 'Applying…' : 'Apply partial replacement')
                            : (isWitnessPropagationBusy ? 'Applying…' : 'Apply replacement')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const readOnlyAudit = Boolean(witnessPropagationProposal.readOnly)
                          setWitnessPropagationProposal(null)
                          setWitnessPropagationError(null)
                          setWitnessPropagationSuccess(null)
                          if (!readOnlyAudit) {
                            setWitnessPropagationResumeState(null)
                            setWitnessPropagationFailures([])
                            if (activeWitness) {
                              void clearWitnessPropagationResumeRecordSafely(activeWitness.id)
                            }
                          }
                        }}
                        disabled={isMatrixContentApplying || isWitnessPropagationBusy}
                        className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                )}

                {propagationMode === 'context' && contextPropagationPreview && (
                  <div className="border border-black bg-white p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Enrollment preview</div>
                    {contextPropagationPreview.mode === 'applied-audit' && (
                      <div className="mt-3 border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                        Read-only audit of the current applied witness state. No segmentation or collation mappings will be changed from this panel.
                      </div>
                    )}
                    <div className="mt-3 space-y-3">
                      {contextPropagationPreview.plans.map(plan => {
                        const isAuditPreview = contextPropagationPreview.mode === 'applied-audit'
                        const isSource = plan.witness.id === contextPropagationPreview.sourceWitness.id
                        const isSelectedTarget = selectedContextPropagationTargetWitnessIdSet.has(plan.witness.id)
                        const isPropagatedWitness = isAutoPropagatedSegmentation(plan.witness.segmentation_result)
                        const isManualOrUnknownWitness = hasManualOrUnknownSegmentation(plan.witness.segmentation_result)
                        const isMappedWitness = enrollmentMappedWitnessIdSet.has(plan.witness.id)
                        const isTranslationIncomplete = Boolean(plan.translationProgress && !plan.translationProgress.complete)
                        const translationResumeState = contextTranslationPreviewResumeStates[plan.witness.id]
                        const targetStatus = plan.protected
                          ? 'Protected manual'
                          : isSource
                            ? 'Reference'
                            : isTranslationIncomplete
                              ? `${plan.translationProgress!.completedBatches}/${plan.translationProgress!.totalBatches} batches`
                              : plan.segmentCount > 0
                                ? (isManualOrUnknownWitness ? `${plan.segmentCount} replacement segments` : `${plan.segmentCount} segments`)
                                : 'No plan'

                        return (
                          <div key={`context-propagation-${plan.witness.id}`} className="border border-gray-200 bg-gray-50 px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-black">
                                  {plan.witness.description || plan.witness.sigla}
                                </div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">{plan.witness.sigla}</div>
                                <div className="mt-2 text-[11px] leading-5 text-gray-600">
                                  {plan.splitIndices.length > 0 ? `Split indices: ${plan.splitIndices.join(', ')}` : 'No split indices proposed'}
                                </div>
                                {!plan.protected && (
                                  <div className="mt-1 text-[11px] leading-5 text-gray-600">
                                    Method: {
                                      isAuditPreview
                                        ? 'applied-state audit'
                                        : isSource
                                          ? 'local fallback anchor'
                                          : plan.strategy === 'translation-aligned'
                                            ? 'translation-aligned'
                                            : plan.strategy === 'ledger-span-aligned'
                                              ? 'collation span enrollment'
                                              : (plan.strategy === 'ledger-conditioned' ? 'collation-conditioned' : 'local fallback')
                                    }
                                  </div>
                                )}
                                {plan.notes && (
                                  <div className="mt-1 text-[11px] leading-5 text-gray-600">{plan.notes}</div>
                                )}
                                {plan.translationProgress && (
                                  <div className="mt-1 text-[11px] leading-5 text-gray-600">
                                    Batch progress: {plan.translationProgress.completedBatches}/{plan.translationProgress.totalBatches || '…'}
                                    {plan.translationProgress.complete ? ' complete' : ''}
                                  </div>
                                )}
                                {!plan.protected && !isSource && (
                                  <div className="mt-1 text-[11px] leading-5 text-gray-600">
                                    Current state: {
                                      isMappedWitness
                                        ? 'existing row mappings will be refreshed on apply'
                                        : isPropagatedWitness
                                          ? 'propagated witness'
                                          : isManualOrUnknownWitness
                                            ? 'existing segmentation will be replaced on apply'
                                            : 'unsegmented witness'
                                    }
                                    {isTranslationAlignmentWitness(plan.witness) ? ' · translation witness' : ''}
                                  </div>
                                )}
                                {plan.exemplarWitnessIds && plan.exemplarWitnessIds.length > 0 && (
                                  <div className="mt-1 text-[11px] leading-5 text-gray-600">
                                    Exemplars: {plan.exemplarWitnessIds
                                      .map(exemplarWitnessId => scopeWitnesses.find(candidate => candidate.id === exemplarWitnessId)?.sigla || exemplarWitnessId)
                                      .join(', ')}
                                  </div>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                {!isAuditPreview && !plan.protected && !isSource && plan.segmentCount > 0 && (
                                  <label className="mb-2 flex items-center justify-end gap-2 text-[11px] text-gray-600">
                                    <span>{isTranslationIncomplete ? 'Apply partial' : 'Apply'}</span>
                                    <input
                                      type="checkbox"
                                      checked={isSelectedTarget}
                                      onChange={() => toggleContextPropagationTargetWitness(plan.witness.id)}
                                      aria-label={`Apply proposal for ${plan.witness.sigla}`}
                                      className="h-4 w-4 border-gray-300 text-black focus:ring-black"
                                    />
                                  </label>
                                )}
                                <div className="inline-flex border border-gray-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-gray-600">
                                  {targetStatus}
                                </div>
                              </div>
                            </div>

                            {plan.diagnostics && (
                              <button
                                type="button"
                                onClick={() => downloadTranslationAlignmentDiagnostics(plan.diagnostics!, plan.witness.sigla)}
                                className="mt-3 h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                              >
                                Download raw alignment output
                              </button>
                            )}

                            {translationResumeState && !isAuditPreview && (
                              <button
                                type="button"
                                onClick={() => void handleResumeContextPropagationPreview(plan.witness.id)}
                                disabled={contextPropagationState.previewing || contextPropagationState.applying}
                                className="mt-3 h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                              >
                                {contextPropagationState.previewing ? 'Resuming…' : 'Resume failed batch'}
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    {contextPropagationPreview.mode !== 'applied-audit' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void handleApplyContextPropagationPreview()}
                          disabled={isMatrixContentApplying || contextPropagationState.applying || selectedContextPropagationTargetWitnessIds.length === 0}
                          className="h-9 flex-1 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {contextPropagationState.applying ? 'Applying…' : 'Apply selected proposals'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setContextPropagationPreview(null)
                            setSelectedContextPropagationTargetWitnessIds([])
                            setContextPropagationFailures([])
                            setContextTranslationPreviewResumeStates({})
                            setContextPropagationState({
                              previewing: false,
                              applying: false,
                              error: null,
                              success: null,
                            })
                          }}
                          disabled={contextPropagationState.previewing || contextPropagationState.applying}
                          className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Discard
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {propagationMode === 'context' && selectedUnitRepairPreview && (
                  <div className="border border-black bg-white p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Selected repair preview</div>
                    <div className="mt-3 space-y-2 text-xs text-gray-700">
                      <div>
                        <span className="font-semibold">Witness:</span>{' '}
                        {selectedUnitRepairPreview.targetWitness.description || selectedUnitRepairPreview.targetWitness.sigla}
                      </div>
                      <div>
                        <span className="font-semibold">Rows:</span>{' '}
                        {selectedUnitRepairPreview.selectedRowIds
                          .map(rowId => rowById.get(rowId))
                          .filter(Boolean)
                          .map(row => getRowLabel(row as AnchoredRow))
                          .join(', ')}
                      </div>
                      <div>
                        <span className="font-semibold">Proposed segments:</span>{' '}
                        {selectedUnitRepairPreview.segmentCount}
                      </div>
                      <div>
                        <span className="font-semibold">Rows changed:</span>{' '}
                        {selectedUnitRepairApplication?.changedRowIds.length || 0}
                      </div>
                      {selectedUnitRepairRun && (
                        <div>
                          <span className="font-semibold">Batch progress:</span>{' '}
                          {selectedUnitRepairCompletedBatches} applied
                          {selectedUnitRepairPendingBatchNumber
                            ? ` · batch ${selectedUnitRepairPendingBatchNumber}/${selectedUnitRepairRun.pendingResumeState?.totalBatches || selectedUnitRepairRun.resumeState.totalBatches} ready`
                            : `/${selectedUnitRepairRun.resumeState.totalBatches}`}
                        </div>
                      )}
                      {selectedUnitRepairPreview.notes && (
                        <div><span className="font-semibold">Notes:</span> {selectedUnitRepairPreview.notes}</div>
                      )}
                      {selectedUnitRepairApplication?.outOfScopeAssignmentRowIds?.length ? (
                        <div className="border border-red-200 bg-red-50 px-2 py-1 text-red-800">
                          <span className="font-semibold">Blocked:</span>{' '}
                          {selectedUnitRepairApplication.outOfScopeAssignmentRowIds.length} structurally unsafe row assignment{selectedUnitRepairApplication.outOfScopeAssignmentRowIds.length === 1 ? '' : 's'} will be ignored. Retry the batch if those rows should be mapped.
                        </div>
                      ) : null}
                      {selectedUnitRepairApplication?.outsideLocalContextAssignmentRowIds?.length ? (
                        <div className="border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900">
                          <span className="font-semibold">Review distance:</span>{' '}
                          {selectedUnitRepairApplication.outsideLocalContextAssignmentRowIds.length} structurally valid row assignment{selectedUnitRepairApplication.outsideLocalContextAssignmentRowIds.length === 1 ? ' is' : 's are'} beyond the local row cluster. These cells are highlighted in amber and will be applied with this batch.
                        </div>
                      ) : null}
                      {selectedUnitRepairApplication?.unassignedFragmentUnitUuids?.length ? (
                        <div className="border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
                          <span className="font-semibold">Fragments:</span>{' '}
                          {selectedUnitRepairApplication.unassignedFragmentUnitUuids.length} outside-window unit{selectedUnitRepairApplication.unassignedFragmentUnitUuids.length === 1 ? '' : 's'} remain unassigned by this preview; apply will preserve admitted Ledger rows outside the selected repair window.
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-3 border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                      The ghosted cells in the Ledger are the row mappings proposed from the selected span. The model segments only that span against the chapter spine.
                    </div>
                    {selectedUnitRepairPreview.diagnostics && (
                      <button
                        type="button"
                        onClick={() => downloadTranslationAlignmentDiagnostics(selectedUnitRepairPreview.diagnostics!, selectedUnitRepairPreview.targetWitness.sigla)}
                        className="mt-3 h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                      >
                        Download raw alignment output
                      </button>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void handleApplySelectedUnitRepairPreview()}
                        disabled={
                          isMatrixContentApplying
                          || !runtimeEnableSnapshots
                          || contextPropagationState.applying
                          || contextPropagationState.previewing
                          || !selectedUnitRepairApplication
                          || !selectedUnitRepairRun?.pendingResumeState
                          || !selectedUnitRepairApplication.acceptedAssignmentRowIds?.length
                        }
                        className="h-9 flex-1 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                      >
                        {contextPropagationState.applying
                          ? 'Applying…'
                          : `Apply batch ${selectedUnitRepairPendingBatchNumber || ''}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleRetrySelectedUnitRepairBatch()}
                        disabled={isMatrixContentApplying || contextPropagationState.previewing || contextPropagationState.applying || !selectedUnitRepairRun}
                        className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        {contextPropagationState.previewing
                          ? 'Retrying…'
                          : `Retry batch ${selectedUnitRepairNextBatchNumber || ''}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDiscardSelectedUnitRepairPreview()}
                        disabled={isMatrixContentApplying || contextPropagationState.previewing || contextPropagationState.applying}
                        className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        Discard
                      </button>
                    </div>
                  </div>
                )}

                {propagationMode === 'semantic' && semanticPreview && (
                  <div className="border border-black bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Semantic preview</div>
                        <div className="mt-1 text-xs text-gray-600">
                          {semanticPreview.candidates.length} ghost{semanticPreview.candidates.length === 1 ? '' : 's'} · {semanticPreview.modelId}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSemanticPreview(null)
                          setSelectedSemanticCandidateId(null)
                          setSemanticPreviewState({
                            previewing: false,
                            applying: false,
                            error: null,
                            success: null,
                          })
                        }}
                        disabled={isMatrixContentApplying || semanticPreviewState.previewing || semanticPreviewState.applying}
                        className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        Discard
                      </button>
                    </div>

                    {selectedSemanticCandidate ? (
                      <div className="mt-3 border border-sky-300 bg-sky-50 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-black">
                              {scopeWitnesses.find(witness => witness.id === selectedSemanticCandidate.targetWitnessId)?.sigla || selectedSemanticCandidate.targetWitnessId}
                              {' '}· {rowById.get(selectedSemanticCandidate.sourceRowId)
                                ? getRowLabel(rowById.get(selectedSemanticCandidate.sourceRowId)!)
                                : selectedSemanticCandidate.sourceRowId}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-sky-700">
                              {Math.round(selectedSemanticCandidate.score * 100)}% match · margin +{Math.round(selectedSemanticCandidate.scoreMargin * 100)}%
                            </div>
                          </div>
                          <div className="shrink-0 border border-sky-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-sky-700">
                            Selected
                          </div>
                        </div>
                        <div
                          className={`mt-3 text-sm ${hasArabicText(selectedSemanticCandidate.targetText) ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                          dir={hasArabicText(selectedSemanticCandidate.targetText) ? 'rtl' : 'ltr'}
                        >
                          {preprocessLedgerText(selectedSemanticCandidate.targetText).workspaceDisplayText}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            onClick={() => void handleApplySemanticCandidate(selectedSemanticCandidate)}
                            disabled={isMatrixContentApplying || semanticPreviewState.applying}
                            className="h-9 flex-1 border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            {semanticPreviewState.applying ? 'Applying…' : 'Apply ghost'}
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleRejectSemanticCandidate(selectedSemanticCandidate)}
                            disabled={isMatrixContentApplying || semanticPreviewState.applying}
                            className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                        Select a semantic ghost cell in the collation.
                      </div>
                    )}

                    {semanticPreview.candidates.length > 0 && (
                      <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                        {semanticPreview.candidates.slice(0, 10).map(candidate => {
                          const targetWitness = scopeWitnesses.find(witness => witness.id === candidate.targetWitnessId)
                          const sourceRow = rowById.get(candidate.sourceRowId)
                          const originRow = rowById.get(candidate.targetRowId)
                          const isSelected = selectedSemanticCandidate?.candidateId === candidate.candidateId
                          return (
                            <button
                              key={candidate.candidateId}
                              type="button"
                              disabled={isMatrixContentApplying}
                              onClick={() => {
                                const nextSelection = { rowId: candidate.sourceRowId, witnessId: candidate.targetWitnessId }
                                setSelectedSemanticCandidateId(candidate.candidateId)
                                setSelectedCell(nextSelection)
                                setSelectionAnchorCell(nextSelection)
                                if (targetWitness) {
                                  setActiveWitnessSigla(targetWitness.sigla)
                                }
                                scrollMatrixRowIntoView(candidate.sourceRowId, {
                                  block: 'center',
                                  behavior: 'smooth'
                                })
                              }}
                              className={`w-full border px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                isSelected
                                  ? 'border-sky-400 bg-sky-50 text-sky-950'
                                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 font-semibold text-black">
                                  {targetWitness?.sigla || candidate.targetWitnessId}
                                  {' '}· {sourceRow ? getRowLabel(sourceRow) : candidate.sourceRowId}
                                </div>
                                <div className="shrink-0 text-[10px] uppercase tracking-widest text-gray-500">
                                  {Math.round(candidate.score * 100)}% · +{Math.round(candidate.scoreMargin * 100)}%
                                </div>
                              </div>
                              <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">
                                from {originRow ? getRowLabel(originRow) : candidate.targetRowId}
                              </div>
                              <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-600">
                                {preprocessLedgerText(candidate.targetText).workspaceDisplayText}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {propagationMode === 'cross-tradition' && crossTraditionCandidates.length > 0 && (
                  <div className="border border-black bg-white p-3" data-testid="ledger-cross-tradition-preview">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Cross-tradition preview</div>
                        <div className="mt-1 text-xs text-gray-600">
                          {crossTraditionReviewSummary.total} candidate{crossTraditionReviewSummary.total === 1 ? '' : 's'} · {crossTraditionReviewSummary.accepted} accepted · {LEDGER_CROSS_TRADITION_RERANK_MODEL_ID}
                        </div>
                      </div>
                      <div className="shrink-0 border border-gray-300 bg-gray-50 px-2 py-1 text-[10px] uppercase tracking-widest text-gray-600">
                        {crossTraditionReviewSummary.deferred} deferred
                      </div>
                    </div>

                    {selectedCrossTraditionCandidate ? (
                      <div className="mt-3 border border-sky-300 bg-sky-50 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-black">
                              {selectedCrossTraditionCandidate.sourceLabel}
                              {' '}→ {selectedCrossTraditionCandidate.targetRowLabels.join(', ')}
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-sky-700">
                              {Math.round(selectedCrossTraditionCandidate.score * 100)}% · {getCrossTextTierLabel(selectedCrossTraditionCandidate.tier)}
                              {selectedCrossTraditionCandidate.rerankVerdict ? ` · ${selectedCrossTraditionCandidate.rerankVerdict}` : ''}
                            </div>
                          </div>
                          <div className="shrink-0 border border-sky-300 bg-white px-2 py-1 text-[10px] uppercase tracking-widest text-sky-700">
                            {selectedCrossTraditionCandidate.reviewStatus}
                          </div>
                        </div>
                        <div className="mt-3 border border-sky-200 bg-white/70 px-3 py-2">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-gray-500">External witness</div>
                          <div
                            className={`mt-1 text-sm ${hasArabicText(selectedCrossTraditionCandidate.sourceText) ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                            dir={hasArabicText(selectedCrossTraditionCandidate.sourceText) ? 'rtl' : 'ltr'}
                          >
                            {preprocessLedgerText(selectedCrossTraditionCandidate.sourceText).workspaceDisplayText}
                          </div>
                        </div>
                        <div className="mt-2 border border-sky-200 bg-white/70 px-3 py-2">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-gray-500">Ledger row spine</div>
                          <div
                            className={`mt-1 text-sm ${hasArabicText(selectedCrossTraditionCandidate.targetText) ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                            dir={hasArabicText(selectedCrossTraditionCandidate.targetText) ? 'rtl' : 'ltr'}
                          >
                            {preprocessLedgerText(selectedCrossTraditionCandidate.targetText).workspaceDisplayText}
                          </div>
                        </div>
                        {selectedCrossTraditionCandidate.rerankReason && (
                          <div className="mt-2 text-[11px] leading-5 text-sky-900">
                            {selectedCrossTraditionCandidate.rerankReason}
                          </div>
                        )}
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => handleSetCrossTraditionCandidateStatus(selectedCrossTraditionCandidate, 'accepted')}
                            disabled={crossTraditionState.applying}
                            className="h-9 border border-black bg-black px-2 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSetCrossTraditionCandidateStatus(selectedCrossTraditionCandidate, 'deferred')}
                            disabled={crossTraditionState.applying}
                            className="h-9 border border-black bg-white px-2 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Defer
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSetCrossTraditionCandidateStatus(selectedCrossTraditionCandidate, 'rejected')}
                            disabled={crossTraditionState.applying}
                            className="h-9 border border-black bg-white px-2 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500">
                        Select a cross-tradition candidate.
                      </div>
                    )}

                    <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
                      {crossTraditionCandidates.slice(0, 20).map(candidate => {
                        const isSelected = selectedCrossTraditionCandidate?.candidateId === candidate.candidateId
                        return (
                          <button
                            key={candidate.candidateId}
                            type="button"
                            onClick={() => focusCrossTraditionCandidate(candidate)}
                            className={`w-full border px-3 py-2 text-left text-xs transition-colors ${
                              isSelected
                                ? 'border-sky-400 bg-sky-50 text-sky-950'
                                : candidate.reviewStatus === 'accepted'
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-950'
                                  : candidate.reviewStatus === 'rejected'
                                    ? 'border-gray-200 bg-gray-50 text-gray-400'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 font-semibold text-black">
                                {candidate.sourceLabel} → {candidate.targetRowLabels.join(', ')}
                              </div>
                              <div className="shrink-0 text-[10px] uppercase tracking-widest text-gray-500">
                                {Math.round(candidate.score * 100)}%
                              </div>
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">
                              {candidate.reviewStatus} · {getCrossTextTierLabel(candidate.tier)}
                              {candidate.rerankVerdict ? ` · ${candidate.rerankVerdict}` : ''}
                            </div>
                            <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-600">
                              {preprocessLedgerText(candidate.sourceText).workspaceDisplayText}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}


            {runtimeEnableSceneTitles && sidePanelTab === 'titles' && (
              <div
                id="ledger-side-panel-tabpanel-titles"
                role="tabpanel"
                aria-labelledby="ledger-side-panel-tab-titles"
                className="space-y-4"
              >
                <div className="border border-black bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                        Row titles
                      </div>
                      <div className="mt-1 text-sm font-semibold text-black">
                        {titledLedgerRowCount}/{resolvedRows.length} titled
                      </div>
                    </div>
                    <span className="border border-black bg-white px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-gray-600">
                      {selectedLedgerRow ? getRowLabel(selectedLedgerRow) : 'No row'}
                    </span>
                  </div>

                  {selectedLedgerRow && rowTitleEditor?.rowId === selectedLedgerRow.id ? (
                    <form
                      className="mt-3 space-y-3 border-t border-black/15 pt-3"
                      onSubmit={event => {
                        event.preventDefault()
                        void handleSaveRowTitle()
                      }}
                    >
                      <div>
                        <label
                          htmlFor="ledger-row-title-primary"
                          className="mb-1 block font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-500"
                        >
                          Primary title
                        </label>
                        <textarea
                          id="ledger-row-title-primary"
                          data-testid="ledger-row-title-primary-input"
                          rows={2}
                          value={rowTitleEditor.primaryText}
                          onChange={event => setRowTitleEditor(current => current
                            ? { ...current, primaryText: event.target.value }
                            : current)}
                          className="w-full resize-y border border-black bg-white px-2 py-1.5 font-serif text-[13px] leading-5 text-black shadow-[1px_1px_0_#000]"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="ledger-row-title-parallel"
                          className="mb-1 block font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-500"
                        >
                          Parallel Arabic title
                        </label>
                        <textarea
                          id="ledger-row-title-parallel"
                          data-testid="ledger-row-title-parallel-input"
                          dir="rtl"
                          rows={2}
                          value={rowTitleEditor.parallelText}
                          onChange={event => setRowTitleEditor(current => current
                            ? { ...current, parallelText: event.target.value }
                            : current)}
                          className="w-full resize-y border border-black bg-white px-2 py-1.5 text-right font-reader-arabic text-[13px] leading-6 text-black shadow-[1px_1px_0_#000]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={isMatrixContentApplying || !rowTitleEditor.primaryText.trim()}
                          className="flex h-8 flex-1 items-center justify-center gap-2 border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleClearRowTitle()}
                          disabled={isMatrixContentApplying || !getCollationRowPrimaryTitle(selectedLedgerRow)}
                          aria-label={`Clear title from ${getRowLabel(selectedLedgerRow)}`}
                          title="Clear row title"
                          className="flex h-8 w-8 items-center justify-center border border-black bg-white text-black shadow-[2px_2px_0_#000] hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300 disabled:shadow-none"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="mt-3 border border-dashed border-black/30 bg-white px-3 py-4 text-center font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-400">
                      Select a row to edit its title
                    </div>
                  )}
                </div>

                {runtimeEnableRowTitleGeneration ? (
                <div data-testid="ledger-row-title-generation" className="border border-black bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        Generate with Pi
                      </div>
                      <div className="mt-1 text-xs leading-5 text-gray-600">
                        {rowTitleGenerationRun
                          ? `${rowTitleGenerationRun.nextRowIndex}/${rowTitleGenerationRun.rowIds.length} rows prepared`
                          : `New run: ${rowTitleGenerationScopeLabel}`}
                      </div>
                    </div>
                    {rowTitleGenerationRun ? (
                      <button
                        type="button"
                        onClick={handleResetRowTitleGenerationRun}
                        disabled={isRowTitleGenerating || isMatrixContentApplying}
                        className="h-7 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                      >
                        New run
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <label
                        htmlFor="ledger-row-title-model"
                        className="mb-1 block font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500"
                      >
                        Model
                      </label>
                      <select
                        id="ledger-row-title-model"
                        value={rowTitleGenerationRun?.modelId || rowTitleGenerationModelId}
                        disabled={Boolean(rowTitleGenerationRun)}
                        onChange={event => setRowTitleGenerationModelId(
                          normalizeLedgerSafeRowTitleModelId(event.target.value)
                        )}
                        className="h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold text-black disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        {LEDGER_SAFE_ROW_TITLE_MODEL_OPTIONS.map(option => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="ledger-row-title-thinking"
                        className="mb-1 block font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-gray-500"
                      >
                        Thinking
                      </label>
                      <select
                        id="ledger-row-title-thinking"
                        value={rowTitleGenerationRun?.thinkingLevel || rowTitleGenerationThinkingLevel}
                        disabled={Boolean(rowTitleGenerationRun)}
                        onChange={event => setRowTitleGenerationThinkingLevel(
                          normalizeLedgerSafeRowTitleThinkingLevel(event.target.value)
                        )}
                        className="h-8 w-full border border-black bg-white px-2 font-mono text-[9px] font-bold text-black disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        {LEDGER_SAFE_ALIGNMENT_THINKING_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    data-testid="ledger-row-title-prepare-next"
                    onClick={() => void handleGenerateNextRowTitleBatch()}
                    disabled={
                      isRowTitleGenerating
                      || isMatrixContentApplying
                      || Boolean(rowTitleGenerationRun && rowTitleGenerationRun.nextRowIndex >= rowTitleGenerationRun.rowIds.length)
                      || (!rowTitleGenerationRun && selectedRowTitleScopeRows.length === 0)
                    }
                    className="mt-3 flex h-9 w-full items-center justify-center gap-2 border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                  >
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    {isRowTitleGenerating
                      ? 'Preparing batch…'
                      : rowTitleGenerationRun
                        ? `Prepare next ${Math.min(ROW_TITLE_GENERATION_BATCH_SIZE, rowTitleGenerationRun.rowIds.length - rowTitleGenerationRun.nextRowIndex)}`
                        : `Prepare ${Math.min(ROW_TITLE_GENERATION_BATCH_SIZE, selectedRowTitleScopeRows.length)}`}
                  </button>

                  {rowTitleGenerationRun?.batches.length ? (
                    <div className="mt-3 space-y-3 border-t border-black/15 pt-3">
                      {[...rowTitleGenerationRun.batches].reverse().map((batch, reverseIndex) => {
                        const batchRows = batch.rowIds.map(rowId => rowById.get(rowId)).filter(
                          (row): row is AnchoredRow => Boolean(row)
                        )
                        const isStale = batch.status === 'ready' && (
                          batchRows.length !== batch.rowIds.length
                          || fingerprintRowTitleGenerationInput(batchRows) !== batch.inputFingerprint
                        )
                        const batchNumber = rowTitleGenerationRun.batches.length - reverseIndex
                        return (
                          <div
                            key={batch.id}
                            data-testid={`ledger-row-title-generation-batch-${batchNumber}`}
                            className="border border-black bg-white"
                          >
                            <div className="flex items-center justify-between gap-3 border-b border-black/15 px-2 py-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
                              <span>Batch {batchNumber} · {batch.suggestions.length} rows</span>
                              <span className={isStale ? 'text-red-700' : batch.status === 'ready' ? 'text-amber-700' : 'text-gray-500'}>
                                {isStale ? 'Stale' : batch.status}
                              </span>
                            </div>
                            <div className="divide-y divide-black/10">
                              {batch.suggestions.map(suggestion => {
                                const suggestionRow = rowById.get(suggestion.rowId)
                                const currentTitle = suggestionRow ? getCollationRowPrimaryTitle(suggestionRow) : ''
                                return (
                                  <div key={suggestion.rowId} className="px-2 py-2">
                                    <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                      {suggestionRow ? getRowLabel(suggestionRow) : suggestion.rowId}
                                      {suggestion.confidence !== null ? ` · ${Math.round(suggestion.confidence * 100)}%` : ''}
                                    </div>
                                    {currentTitle && currentTitle !== suggestion.title ? (
                                      <div className="mt-1 whitespace-normal break-words border-l-2 border-gray-300 pl-2 font-serif text-[11px] leading-4 text-gray-500">
                                        {formatEditorialTitleForDisplay(currentTitle)}
                                      </div>
                                    ) : null}
                                    <div className="mt-1 whitespace-normal break-words font-serif text-[12px] font-semibold leading-5 text-black">
                                      {formatEditorialTitleForDisplay(suggestion.title)}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                            {batch.status === 'ready' ? (
                              <div className="flex gap-2 border-t border-black/15 p-2">
                                <button
                                  type="button"
                                  onClick={() => void handleApplyGeneratedRowTitleBatch(batch)}
                                  disabled={isMatrixContentApplying || !runtimeEnableSnapshots || isRowTitleGenerating || isStale}
                                  className="h-8 flex-1 border border-black bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                  Apply batch
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDismissGeneratedRowTitleBatch(batch)}
                                  disabled={isMatrixContentApplying || isRowTitleGenerating}
                                  className="h-8 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                                >
                                  Dismiss
                                </button>
                              </div>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
                ) : null}

                {runtimeEnableRowTitleSidecarImport ? (
                <div data-testid="ledger-row-title-sidecar-import" className="border border-black bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                        <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
                        HTML title sidecar
                      </div>
                      <div className="mt-1 text-xs leading-5 text-gray-600">
                        Text rows become row titles; titled spans become unclassified structural ranges.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => rowTitleSidecarInputRef.current?.click()}
                      disabled={isMatrixContentApplying}
                      className="h-8 shrink-0 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[1px_1px_0_#000] hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                    >
                      Choose JSON
                    </button>
                    <input
                      ref={rowTitleSidecarInputRef}
                      type="file"
                      accept=".json,application/json"
                      className="hidden"
                      onChange={event => void handleLoadRowTitleSidecar(event)}
                    />
                  </div>

                  {rowTitleSidecarPreview ? (
                    <div className="mt-3 border-t border-black/15 pt-3">
                      <div className="whitespace-normal break-words font-mono text-[9px] font-bold text-black">
                        {rowTitleSidecarPreview.sidecar.source_html_name}
                      </div>
                      <div className="mt-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
                        Row titles
                      </div>
                      <div className="mt-2 grid grid-cols-5 border-l border-t border-black/20">
                        {([
                          ['ready', rowTitleSidecarPreview.counts.ready],
                          ['same', rowTitleSidecarPreview.counts.same],
                          ['conflict', rowTitleSidecarPreview.counts.conflict],
                          ['ambiguous', rowTitleSidecarPreview.counts.ambiguous],
                          ['unmatched', rowTitleSidecarPreview.counts.unmatched],
                        ] as Array<[CollationRowTitleSidecarMatchStatus, number]>).map(([status, count]) => (
                          <div key={status} className="border-r border-b border-black/20 bg-white px-1 py-1.5 text-center">
                            <div className="font-mono text-[11px] font-bold text-black">{count}</div>
                            <div className="mt-0.5 font-mono text-[7px] font-bold uppercase tracking-normal text-gray-500">{status}</div>
                          </div>
                        ))}
                      </div>

                      {rowTitleSidecarPreview.sceneMatches.length > 0 ? (
                        <>
                          <div className="mt-3 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
                            Structural ranges
                          </div>
                          <div className="mt-2 grid grid-cols-5 border-l border-t border-black/20">
                            {([
                              ['ready', rowTitleSidecarPreview.sceneCounts.ready],
                              ['same', rowTitleSidecarPreview.sceneCounts.same],
                              ['conflict', rowTitleSidecarPreview.sceneCounts.conflict],
                              ['ambiguous', rowTitleSidecarPreview.sceneCounts.ambiguous],
                              ['unmatched', rowTitleSidecarPreview.sceneCounts.unmatched],
                            ] as Array<[CollationRowTitleSidecarMatchStatus, number]>).map(([status, count]) => (
                              <div key={`scene:${status}`} className="border-r border-b border-black/20 bg-white px-1 py-1.5 text-center">
                                <div className="font-mono text-[11px] font-bold text-black">{count}</div>
                                <div className="mt-0.5 font-mono text-[7px] font-bold uppercase tracking-normal text-gray-500">{status}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null}

                      {rowTitleSidecarNeedsRoleAwareRepair ? (
                        <div className="mt-3 border border-black bg-white p-2">
                          <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-500">
                            Role-aware repair
                          </div>
                          <div className="mt-1 text-[11px] leading-4 text-gray-600">
                            {rowTitleSidecarPreview.repairableTitleCount} row titles ·{' '}
                            {sidecarStructuralRangePreview?.candidates.length || 0} structural candidates
                            {rowTitleSidecarPreview.staleImportedTitleRowIds.length > 0
                              ? ` · ${rowTitleSidecarPreview.staleImportedTitleRowIds.length} shifted tail titles cleared`
                              : ''}
                          </div>
                          <button
                            type="button"
                            data-testid="ledger-row-title-sidecar-repair"
                            onClick={() => void handleRepairRoleAwareTitleSidecar()}
                            disabled={isMatrixContentApplying || !runtimeEnableSnapshots || rowTitleRoleAwareRepairCount === 0}
                            className="mt-2 h-9 w-full border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            Repair titles and import ranges
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleApplyExactRowTitleImports}
                          disabled={isMatrixContentApplying || !runtimeEnableSnapshots || rowTitleSidecarPreview.counts.ready === 0}
                          className="mt-3 h-9 w-full border border-black bg-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          Apply {rowTitleSidecarPreview.counts.ready} exact additions
                        </button>
                      )}

                      {rowTitleSidecarPreview.matches.some(match => (
                        match.status === 'conflict' && !match.safeToReplace
                      )) ? (
                        <div className="mt-3 space-y-2 border-t border-black/15 pt-3">
                          <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                            Held row-title conflicts
                          </div>
                          {rowTitleSidecarPreview.matches.filter(match => (
                            match.status === 'conflict' && !match.safeToReplace
                          )).map(match => {
                            const matchRow = match.rowId ? rowById.get(match.rowId) : null
                            return (
                              <div key={`${match.sidecarRow.row_order}:${match.rowId}`} className="border border-black bg-white p-2">
                                <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                  {matchRow ? getRowLabel(matchRow) : match.sidecarRow.row_label} · {match.method}
                                </div>
                                <div className="mt-1 whitespace-normal break-words border-l-2 border-gray-300 pl-2 font-serif text-[11px] leading-4 text-gray-500">
                                  {formatEditorialTitleForDisplay(match.currentTitle)}
                                </div>
                                <div className="mt-1 whitespace-normal break-words border-l-2 border-black pl-2 font-serif text-[12px] font-semibold leading-5 text-black">
                                  {formatEditorialTitleForDisplay(match.sidecarRow.title)}
                                </div>
                                {match.rowId ? (
                                  <button
                                    type="button"
                                    onClick={() => handleApplyRowTitleImportConflict(match.rowId as string)}
                                    disabled={isMatrixContentApplying}
                                    className="mt-2 h-7 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                                  >
                                    Use imported title
                                  </button>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      ) : null}

                      {rowTitleSidecarPreview.sceneMatches.some(match => (
                        match.status === 'conflict' && !match.safeToReplace
                      )) ? (
                        <div className="mt-3 space-y-2 border-t border-black/15 pt-3">
                          <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                            Held structural-range conflicts
                          </div>
                          {rowTitleSidecarPreview.sceneMatches.filter(match => (
                            match.status === 'conflict' && !match.safeToReplace
                          )).map(match => (
                            <div key={`scene-conflict:${match.sidecarScene.scene_order}`} className="border border-black bg-white p-2">
                              <div className="font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                {match.sidecarScene.start_row_label}–{match.sidecarScene.end_row_label}
                              </div>
                              {match.currentTitles.map(title => (
                                <div key={title} className="mt-1 whitespace-normal break-words border-l-2 border-gray-300 pl-2 font-serif text-[11px] leading-4 text-gray-500">
                                  {formatEditorialTitleForDisplay(title)}
                                </div>
                              ))}
                              <div className="mt-1 whitespace-normal break-words border-l-2 border-black pl-2 font-serif text-[12px] font-semibold leading-5 text-black">
                                {formatEditorialTitleForDisplay(match.sidecarScene.title)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {rowTitleSidecarPreview.matches.some(match => match.status === 'ambiguous') ? (
                        <div className="mt-3 space-y-2 border-t border-black/15 pt-3">
                          <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                            Choose exact row
                          </div>
                          {rowTitleSidecarPreview.matches.filter(match => match.status === 'ambiguous').map(match => (
                            <div key={`ambiguous:${match.sidecarRow.row_order}`} className="border border-black bg-white p-2">
                              <div className="whitespace-normal break-words font-serif text-[12px] font-semibold leading-5 text-black">
                                {formatEditorialTitleForDisplay(match.sidecarRow.title)}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {match.candidateRowIds.map(candidateRowId => {
                                  const candidateRow = rowById.get(candidateRowId)
                                  return (
                                    <button
                                      key={candidateRowId}
                                      type="button"
                                      onClick={() => void handleResolveAmbiguousRowTitleImport({
                                        match,
                                        targetRowId: candidateRowId,
                                      })}
                                      disabled={isMatrixContentApplying || !runtimeEnableSnapshots}
                                      className="h-7 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300"
                                    >
                                      {candidateRow ? getRowLabel(candidateRow) : candidateRowId}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {rowTitleSidecarPreview.counts.unmatched > 0 ? (
                        <div className="mt-3 border border-dashed border-black/30 bg-white px-3 py-2 font-mono text-[9px] leading-4 text-gray-500">
                          {rowTitleSidecarPreview.counts.unmatched} sidecar row{rowTitleSidecarPreview.counts.unmatched === 1 ? '' : 's'} could not be mapped and remain unapplied.
                        </div>
                      ) : null}
                      {rowTitleSidecarPreview.sceneCounts.unmatched > 0 ? (
                        <div className="mt-2 border border-dashed border-black/30 bg-white px-3 py-2 font-mono text-[9px] leading-4 text-gray-500">
                          {rowTitleSidecarPreview.sceneCounts.unmatched} structural range{rowTitleSidecarPreview.sceneCounts.unmatched === 1 ? '' : 's'} could not be mapped and remain unapplied.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                ) : null}

                {((runtimeEnableRowTitleSidecarImport && rowTitleImportError)
                  || (runtimeEnableRowTitleGeneration && rowTitleGenerationError)) ? (
                  <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800">
                    {(runtimeEnableRowTitleSidecarImport && rowTitleImportError)
                      || (runtimeEnableRowTitleGeneration && rowTitleGenerationError)}
                  </div>
                ) : null}
              </div>
            )}

            {sidePanelTab === 'sequence' && (
              <div id="ledger-side-panel-tabpanel-sequence" role="tabpanel" aria-labelledby="ledger-side-panel-tab-sequence" className="space-y-4">
                {(retiredMaterialAudit.issues.length > 0 || retiredMaterialAudit.emptyRows.length > 0) && (
                  <section
                    data-testid="ledger-retired-material-review"
                    className="border border-black bg-[#fff8e8] p-3 shadow-[3px_3px_0_#000]"
                    aria-labelledby="ledger-retired-material-review-heading"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div
                          id="ledger-retired-material-review-heading"
                          className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-black"
                        >
                          Source-remnant review
                        </div>
                        <div className="mt-1 text-xs leading-5 text-gray-700">
                          Parsed legends, side titles, artifacts, and rows without readings are shown in place.
                          These decisions never remove the source witness record.
                        </div>
                      </div>
                      <span className="shrink-0 border border-black bg-black px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-white">
                        {retiredMaterialAudit.unresolvedCount} open
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-4 border-l border-t border-black/30 font-mono text-center">
                      {([
                        ['Review', retiredMaterialAudit.issues.filter(issue => issue.status === 'review').length],
                        ['Retain', retiredMaterialAudit.issues.filter(issue => issue.status === 'retained').length
                          + retiredMaterialAudit.emptyRows.filter(issue => issue.status === 'retained').length],
                        ['Hidden', retiredMaterialAudit.issues.filter(issue => issue.status === 'hidden').length],
                        ['Empty', retiredMaterialAudit.emptyRows.length],
                      ] as Array<[string, number]>).map(([label, count]) => (
                        <div key={label} className="border-r border-b border-black/30 bg-white px-1 py-1.5">
                          <div className="text-[12px] font-bold text-black">{count}</div>
                          <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      data-testid="ledger-retired-material-review-toggle"
                      onClick={() => setIsRetiredMaterialReviewMode(value => !value)}
                      className={`mt-3 h-9 w-full border border-black px-3 font-mono text-[9px] font-bold uppercase tracking-[0.14em] shadow-[2px_2px_0_#000] transition-all active:translate-y-[2px] active:shadow-none ${
                        isRetiredMaterialReviewMode
                          ? 'bg-amber-100 text-amber-950 hover:bg-amber-200'
                          : 'bg-white text-black hover:bg-gray-100'
                      }`}
                    >
                      {isRetiredMaterialReviewMode ? 'Show all ledger rows' : 'Review affected rows in ledger'}
                    </button>

                    {retiredMaterialAudit.issues.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-600">
                          Affected cells
                        </div>
                        {retiredMaterialAudit.issues.map(issue => {
                          const issueRow = rowById.get(issue.rowId)
                          const issueRowLabel = issueRow ? getRowLabel(issueRow) : issue.rowId
                          const issueText = issue.snapshotText || issue.legacyText
                          const isArabic = hasArabicText(issueText)
                          return (
                            <article
                              key={`${issue.rowId}:${issue.witnessId}:${issue.unitUuid}`}
                              data-testid={`ledger-retired-material-card-${issue.rowId}-${issue.witnessId}`}
                              className="border border-black bg-white p-2.5"
                            >
                              <div className="flex items-start justify-between gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em]">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-black">{issueRowLabel} · {issue.witnessSigla}</span>
                                  <span className="border border-black/25 bg-gray-50 px-1 py-0.5 text-[7px] text-gray-600">
                                    {issue.kind}
                                  </span>
                                  <span className={issue.status === 'review'
                                    ? 'border border-amber-400 bg-amber-100 px-1 py-0.5 text-[7px] text-amber-900'
                                    : issue.status === 'retained'
                                      ? 'border border-sky-400 bg-sky-100 px-1 py-0.5 text-[7px] text-sky-900'
                                      : 'border border-slate-400 bg-slate-100 px-1 py-0.5 text-[7px] text-slate-700'}
                                  >
                                    {issue.status}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleFocusLedgerOccupancyCell({
                                    rowId: issue.rowId,
                                    witnessId: issue.witnessId,
                                    witnessSigla: issue.witnessSigla,
                                  })}
                                  className="shrink-0 border border-black bg-white px-1.5 py-0.5 text-[8px] text-black hover:bg-gray-100"
                                >
                                  Show ↗
                                </button>
                              </div>
                              <div className="mt-1 font-mono text-[8px] leading-4 text-gray-500">
                                {issue.unitLabel} · {issue.reason}
                              </div>
                              <div
                                dir={isArabic ? 'rtl' : 'ltr'}
                                className={`mt-2 max-h-28 overflow-auto border-l-2 border-amber-500 bg-amber-50/50 px-2 py-1.5 text-xs ${
                                  isArabic ? 'font-reader-arabic text-right leading-7' : 'leading-5'
                                }`}
                              >
                                {issue.status === 'hidden'
                                  ? 'Text hidden in the ledger; preserved in witness provenance.'
                                  : issueText || 'No surviving display text'}
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-1.5">
                                <button
                                  type="button"
                                  data-testid={`ledger-retired-material-retain-${issue.rowId}-${issue.witnessId}`}
                                  onClick={() => void handleSetRetiredMaterialDisposition(issue, 'retained')}
                                  disabled={readOnly || isMatrixContentApplying}
                                  className={`h-8 border border-black px-1 font-mono text-[8px] font-bold uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 ${
                                    issue.status === 'retained' ? 'bg-sky-100 text-sky-950' : 'bg-white text-black hover:bg-sky-50'
                                  }`}
                                >
                                  Keep as paratext
                                </button>
                                <button
                                  type="button"
                                  data-testid={`ledger-retired-material-hide-${issue.rowId}-${issue.witnessId}`}
                                  onClick={() => void handleSetRetiredMaterialDisposition(issue, 'hidden')}
                                  disabled={readOnly || isMatrixContentApplying}
                                  className={`h-8 border border-black px-1 font-mono text-[8px] font-bold uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 ${
                                    issue.status === 'hidden' ? 'bg-slate-200 text-slate-800' : 'bg-white text-black hover:bg-slate-100'
                                  }`}
                                >
                                  Hide text
                                </button>
                                <button
                                  type="button"
                                  data-testid={`ledger-retired-material-detach-${issue.rowId}-${issue.witnessId}`}
                                  onClick={() => void handleDetachRetiredMaterial(issue)}
                                  disabled={readOnly || isMatrixContentApplying}
                                  className="col-span-2 h-8 border border-black bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow-[2px_2px_0_#000] hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                                >
                                  Detach from row · keep source record
                                </button>
                              </div>
                            </article>
                          )
                        })}
                      </div>
                    )}

                    {retiredMaterialAudit.emptyRows.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-600">
                          Rows without readings
                        </div>
                        {retiredMaterialAudit.emptyRows.map(emptyIssue => {
                          const emptyRow = rowById.get(emptyIssue.rowId)
                          const rowLabel = emptyRow ? getRowLabel(emptyRow) : emptyIssue.rowId
                          const structuralDependencyCount = structuralBoundaryRangeCountByRowId.get(emptyIssue.rowId) || 0
                          const focusWitness = activeWitness || scopeWitnesses[0] || null
                          return (
                            <article
                              key={emptyIssue.rowId}
                              data-testid={`ledger-empty-reading-row-card-${emptyIssue.rowId}`}
                              className="border border-black bg-white p-2.5"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-black">
                                    {rowLabel} · no readings
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-1 font-mono text-[7px] font-bold uppercase tracking-[0.1em]">
                                    <span className={emptyIssue.status === 'retained'
                                      ? 'border border-sky-400 bg-sky-100 px-1 py-0.5 text-sky-900'
                                      : 'border border-amber-400 bg-amber-100 px-1 py-0.5 text-amber-900'}
                                    >
                                      {emptyIssue.status}
                                    </span>
                                    {emptyIssue.hasRowTitle && <span className="border border-amber-300 bg-amber-50 px-1 py-0.5 text-amber-800">row title</span>}
                                    {emptyIssue.sceneTitle && <span className="border border-amber-300 bg-amber-50 px-1 py-0.5 text-amber-800">legacy scene</span>}
                                    {structuralDependencyCount > 0 && <span className="border border-red-300 bg-red-50 px-1 py-0.5 text-red-800">{structuralDependencyCount} structure edge{structuralDependencyCount === 1 ? '' : 's'}</span>}
                                    {!emptyIssue.hasRowTitle && !emptyIssue.sceneTitle && structuralDependencyCount === 0 && <span className="border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-emerald-800">safe candidate</span>}
                                  </div>
                                  {emptyIssue.sceneTitle && (
                                    <div className="mt-1 text-[10px] leading-4 text-gray-600">{emptyIssue.sceneTitle}</div>
                                  )}
                                </div>
                                {focusWitness && (
                                  <button
                                    type="button"
                                    onClick={() => handleFocusLedgerOccupancyCell({
                                      rowId: emptyIssue.rowId,
                                      witnessId: focusWitness.id,
                                      witnessSigla: focusWitness.sigla,
                                    })}
                                    className="shrink-0 border border-black bg-white px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.1em] text-black hover:bg-gray-100"
                                  >
                                    Show ↗
                                  </button>
                                )}
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-1.5">
                                <button
                                  type="button"
                                  data-testid={`ledger-empty-reading-row-retain-${emptyIssue.rowId}`}
                                  onClick={() => void handleRetainEmptyRowAfterReview(emptyIssue)}
                                  disabled={readOnly || isMatrixContentApplying}
                                  className={`h-8 border border-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 ${
                                    emptyIssue.status === 'retained' ? 'bg-sky-100' : 'bg-white hover:bg-sky-50'
                                  }`}
                                >
                                  Keep row
                                </button>
                                <button
                                  type="button"
                                  data-testid={`ledger-empty-reading-row-delete-${emptyIssue.rowId}`}
                                  onClick={() => void handleDeleteEmptyRowAfterReview(emptyIssue)}
                                  disabled={readOnly || isMatrixContentApplying || !runtimeEnableSnapshots || structuralDependencyCount > 0}
                                  title={structuralDependencyCount > 0
                                    ? 'Move the structural range boundary before deleting this row'
                                    : 'Create a backup, then delete this reviewed row'}
                                  className="h-8 border border-black bg-white px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-black shadow-[2px_2px_0_#000] hover:bg-red-50 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                                >
                                  {structuralDependencyCount > 0 ? 'Move structure edge' : 'Delete row'}
                                </button>
                              </div>
                            </article>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )}
                {!activeWitnessSequenceAudit || !activeWitness ? (
                  <div className="border border-dashed border-black/30 bg-[#fffdfa] px-4 py-5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                    Activate a witness with segmentation to audit its source order
                  </div>
                ) : (
                  <>
                    <div data-testid="ledger-sequence-summary" className="border border-black bg-gray-50 p-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                          Source order · {displaySigla(activeWitness)}
                        </div>
                        <span className={`font-mono text-[10px] font-bold ${activeWitnessSequenceAudit.offenders.length === 0 ? 'text-emerald-700' : 'text-[#7a2e2a]'}`}>
                          {activeWitnessSequenceAudit.inOrderCount}/{activeWitnessSequenceAudit.placedCount} in order
                        </span>
                      </div>
                      <div className="mt-2 text-xs leading-5 text-gray-600">
                        Placements are checked against the witness source text. Detaching keeps the unit
                        as a fragment. Reanchor repairs a fragment&apos;s row context without placing it;
                        the Fragments controls remain responsible for placement. Nothing touches the witness text.
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em]">
                        <span className={`border px-1.5 py-0.5 ${activeWitnessSequenceAudit.offenders.length > 0 ? 'border-[#7a2e2a]/40 bg-[#f6e7e5] text-[#7a2e2a]' : 'border-black/15 bg-white text-gray-500'}`}>
                          {activeWitnessSequenceAudit.offenders.length} out of order
                        </span>
                        <span className={`border px-1.5 py-0.5 ${activeWitnessSequenceAudit.unplacedCount > 0 ? 'border-sky-300 bg-sky-50 text-sky-800' : 'border-black/15 bg-white text-gray-500'}`}>
                          {activeWitnessSequenceAudit.unplacedCount} unplaced
                        </span>
                        {visibleActiveWitnessStaleMappings.length > 0 && (
                          <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-amber-800">
                            {visibleActiveWitnessStaleMappings.length} stale
                          </span>
                        )}
                        {activeWitnessSequenceAudit.markedTranspositionRowIds.length > 0 && (
                          <span className="border border-[#a8411b]/35 bg-[#f7ede2] px-1.5 py-0.5 text-[#7c3413]">
                            {activeWitnessSequenceAudit.markedTranspositionRowIds.length} marked tr.
                          </span>
                        )}
                      </div>
                      {activeWitnessSequenceAudit.offenders.length > 0 && (
                        <button
                          type="button"
                          data-testid="ledger-sequence-detach-all"
                          onClick={() => void handleDetachAllSequenceOffenders()}
                          disabled={isMatrixContentApplying}
                          className="mt-3 h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {isLedgerApplying ? 'Detaching…' : `Detach all ${activeWitnessSequenceAudit.offenders.length} into fragments`}
                        </button>
                      )}
                    </div>
                    {fragmentAnchorSelection && (
                      <div
                        data-testid="ledger-fragment-anchor-selection"
                        className="border border-sky-500 bg-sky-50 p-3"
                      >
                        <div className="flex items-start gap-2">
                          <AnchorIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-800" strokeWidth={2} />
                          <div className="min-w-0 flex-1">
                            <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-sky-900">
                              Choose the row below this fragment
                            </div>
                            <div className="mt-1 text-xs leading-5 text-sky-900/80">
                              Scroll the ledger and select a row-anchor icon. The fragment will stay unplaced.
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="ledger-fragment-anchor-cancel"
                            onClick={() => setFragmentAnchorSelection(null)}
                            className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-[0.14em] text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            data-testid="ledger-fragment-anchor-at-end"
                            onClick={() => void handleAnchorFragmentAtEnd()}
                            disabled={resolvedRows.length === 0}
                            className="h-8 border border-black bg-black px-2 text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            Anchor at chapter end
                          </button>
                        </div>
                      </div>
                    )}
                    {activeWitnessSequenceAudit.offenders.length === 0 && visibleActiveWitnessStaleMappings.length === 0 && (
                      <div data-testid="ledger-sequence-clear" className="border border-black bg-[#fffdfa] px-4 py-5 text-center">
                        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                          Every placed unit follows the source text
                        </div>
                        {activeWitnessSequenceAudit.unplacedCount > 0 && (
                          <div className="mt-2 text-xs leading-5 text-gray-600">
                            {activeWitnessSequenceAudit.unplacedCount} unplaced unit{activeWitnessSequenceAudit.unplacedCount === 1 ? '' : 's'} remain in the Fragments tray.
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-3">
                      {activeWitnessSequenceAudit.offenders.map(offender => {
                        const offenderRow = rowById.get(offender.rowId) || null
                        const afterRow = offender.belongsAfterRowId ? rowById.get(offender.belongsAfterRowId) || null : null
                        const beforeRow = offender.belongsBeforeRowId ? rowById.get(offender.belongsBeforeRowId) || null : null
                        const landingRow = offender.landingRowId ? rowById.get(offender.landingRowId) || null : null
                        const keptRow = offender.keptRowId ? rowById.get(offender.keptRowId) || null : null
                        const locusText = offender.kind === 'duplicate'
                          ? `already placed in ${keptRow ? getRowLabel(keptRow) : 'another row'}`
                          : afterRow && beforeRow
                            ? `between ${getRowLabel(afterRow)} and ${getRowLabel(beforeRow)}`
                            : afterRow
                              ? `after ${getRowLabel(afterRow)}`
                              : beforeRow
                                ? `before ${getRowLabel(beforeRow)}`
                                : 'no in-order neighbours yet'
                        const isArabic = hasArabicText(offender.text)
                        return (
                          <div
                            key={`${offender.rowId}:${offender.unitUuid}`}
                            data-testid={`ledger-sequence-offender-${offender.rowId}`}
                            className="border border-black bg-white px-3 py-2"
                          >
                            <div className="flex items-start justify-between gap-3 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">
                              <div className="flex flex-wrap items-center gap-2">
                                <span>{offender.unitLabel || offender.unitUuid.slice(0, 8)}</span>
                                <span className={`border px-1.5 py-0.5 ${offender.kind === 'duplicate' ? 'border-amber-300 bg-amber-100/80 text-amber-800' : 'border-[#7a2e2a]/40 bg-[#f6e7e5] text-[#7a2e2a]'}`}>
                                  {offender.kind === 'duplicate' ? 'Duplicate' : 'Out of order'}
                                </span>
                              </div>
                              <button
                                type="button"
                                data-testid={`ledger-sequence-jump-${offender.rowId}`}
                                onClick={() => handleFocusLedgerOccupancyCell({
                                  rowId: offender.rowId,
                                  witnessId: activeWitness.id,
                                  witnessSigla: activeWitness.sigla
                                })}
                                className="shrink-0 border border-black bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-black transition-colors hover:bg-gray-100"
                              >
                                {offenderRow ? getRowLabel(offenderRow) : 'Row'} ↗
                              </button>
                            </div>
                            <div
                              className={`mt-2 line-clamp-3 text-sm ${isArabic ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                              dir={isArabic ? 'rtl' : 'ltr'}
                            >
                              {offender.text}
                            </div>
                            <div
                              data-testid={`ledger-sequence-locus-${offender.rowId}`}
                              className="mt-2 border-t border-dashed border-black/15 pt-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-gray-600"
                            >
                              Belongs {locusText}
                              {offender.kind === 'misplaced' && (
                                <span className="ml-1.5 normal-case tracking-normal text-gray-500">
                                  {landingRow ? `· open row ${getRowLabel(landingRow)} available` : '· needs a new row'}
                                </span>
                              )}
                            </div>
                            <div className="mt-2">
                              <button
                                type="button"
                                data-testid={`ledger-sequence-detach-${offender.rowId}`}
                                onClick={() => void handleDetachSequenceOffender(offender)}
                                disabled={isMatrixContentApplying}
                                className="h-8 border border-black bg-black px-2 text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                              >
                                Detach → fragment
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {activeWitnessSequenceAudit.unplacedUnits.length > 0 && (
                      <div className="space-y-3">
                        <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">
                          Unplaced source units
                        </div>
                        {activeWitnessSequenceAudit.unplacedUnits.map(unplacedUnit => {
                          const fragment = unassignedFragmentByUnitUuid.get(unplacedUnit.unitUuid) || null
                          const afterRow = unplacedUnit.belongsAfterRowId
                            ? rowById.get(unplacedUnit.belongsAfterRowId) || null
                            : null
                          const beforeRow = unplacedUnit.belongsBeforeRowId
                            ? rowById.get(unplacedUnit.belongsBeforeRowId) || null
                            : null
                          const currentPreviousRow = fragment?.previousRowId
                            ? rowById.get(fragment.previousRowId) || null
                            : null
                          const currentNextRow = fragment?.nextRowId
                            ? rowById.get(fragment.nextRowId) || null
                            : null
                          const hasCurrentAnchor = Boolean(fragment?.previousRowId || fragment?.nextRowId)
                          const isSelectingThisFragment = Boolean(
                            fragmentAnchorSelection?.witnessId === activeWitness.id
                            && fragmentAnchorSelection.unitUuid === unplacedUnit.unitUuid
                          )
                          const inferredLocus = afterRow && beforeRow
                            ? `${getRowLabel(afterRow)} → ${getRowLabel(beforeRow)}`
                            : afterRow
                              ? `${getRowLabel(afterRow)} → End`
                              : beforeRow
                                ? `Start → ${getRowLabel(beforeRow)}`
                                : 'Manual row selection required'
                          const currentLocus = hasCurrentAnchor
                            ? `${currentPreviousRow ? getRowLabel(currentPreviousRow) : 'Start'} → ${currentNextRow ? getRowLabel(currentNextRow) : 'End'}`
                            : 'Unanchored'
                          const isArabic = hasArabicText(unplacedUnit.text)
                          return (
                            <div
                              key={unplacedUnit.unitUuid}
                              data-testid={`ledger-sequence-unplaced-${unplacedUnit.unitUuid}`}
                              className={`border bg-white px-3 py-2 ${isSelectingThisFragment ? 'border-sky-500 ring-1 ring-sky-300' : 'border-black'}`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                <span>{unplacedUnit.unitLabel || unplacedUnit.unitUuid}</span>
                                <span className={`border px-1.5 py-0.5 ${hasCurrentAnchor ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-sky-300 bg-sky-50 text-sky-800'}`}>
                                  {hasCurrentAnchor ? 'Anchored fragment' : 'Unanchored fragment'}
                                </span>
                              </div>
                              <div
                                className={`mt-2 whitespace-pre-wrap break-words text-sm ${isArabic ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                                dir={isArabic ? 'rtl' : 'ltr'}
                              >
                                {unplacedUnit.text}
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-dashed border-black/15 pt-2 font-mono text-[9px] font-bold uppercase tracking-[0.12em]">
                                <div>
                                  <div className="text-gray-400">Order suggests</div>
                                  <div className="mt-0.5 text-gray-700">{inferredLocus}</div>
                                </div>
                                <div>
                                  <div className="text-gray-400">Fragment context</div>
                                  <div className="mt-0.5 text-gray-700">{currentLocus}</div>
                                </div>
                              </div>
                              {!hasCurrentAnchor && (
                                <button
                                  type="button"
                                  data-testid={`ledger-sequence-reanchor-${unplacedUnit.unitUuid}`}
                                  onClick={() => void handleReanchorUnplacedUnit(unplacedUnit)}
                                  disabled={
                                    isMatrixContentApplying
                                    || fragmentAnchorHydratedContextId !== chapterId
                                    || !fragment
                                    || Boolean(fragmentAnchorSelection && !isSelectingThisFragment)
                                  }
                                  className="mt-3 flex h-8 w-full items-center justify-center gap-2 border border-black bg-black px-2 text-[9px] font-bold uppercase tracking-[0.14em] text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                                >
                                  <AnchorIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
                                  {isSelectingThisFragment ? 'Select a row in the ledger' : 'Choose fragment locus'}
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {visibleActiveWitnessStaleMappings.length > 0 && (
                      <div className="border border-amber-300 bg-amber-50/60 p-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">
                          Stale mappings
                        </div>
                        <div className="mt-1 text-xs leading-5 text-amber-900/80">
                          These rows reference units that no longer exist in the witness segmentation.
                          Detaching clears the dead reference; the row keeps its other witnesses.
                        </div>
                        <div className="mt-2 space-y-2">
                          {visibleActiveWitnessStaleMappings.map(staleMapping => {
                            const staleRow = rowById.get(staleMapping.rowId) || null
                            return (
                              <div
                                key={`${staleMapping.rowId}:${staleMapping.unitUuid}`}
                                data-testid={`ledger-sequence-stale-${staleMapping.rowId}`}
                                className="border border-amber-200 bg-white px-2 py-1.5"
                              >
                                <div className="flex items-center justify-between gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-amber-800">
                                  <span>{staleRow ? getRowLabel(staleRow) : staleMapping.rowId}</span>
                                  <button
                                    type="button"
                                    data-testid={`ledger-sequence-detach-stale-${staleMapping.rowId}`}
                                    onClick={() => void handleDetachSequenceRows({
                                      rowIds: [staleMapping.rowId],
                                      summary: `Detached stale ${displaySigla(activeWitness)} mapping from ${staleRow ? getRowLabel(staleRow) : 'row'}`,
                                      reason: 'stale-mapping'
                                    })}
                                    disabled={isMatrixContentApplying}
                                    className="border border-black bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                                  >
                                    Detach
                                  </button>
                                </div>
                                {staleMapping.textSnapshot && (
                                  <div
                                    className={`mt-1 line-clamp-2 text-xs ${hasArabicText(staleMapping.textSnapshot) ? 'font-reader-arabic text-right leading-6' : 'leading-5'}`}
                                    dir={hasArabicText(staleMapping.textSnapshot) ? 'rtl' : 'ltr'}
                                  >
                                    {staleMapping.textSnapshot}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {(activeWitnessSequenceAudit.unplacedCount > 0 || unassignedFragments.length > 0) && (
                      <button
                        type="button"
                        data-testid="ledger-sequence-open-fragments"
                        onClick={() => setSidePanelTab('fragments')}
                        className="h-9 w-full border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                      >
                        Place pending fragments ({unassignedFragments.length}) →
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {sidePanelTab === 'fragments' && (
              <div id="ledger-side-panel-tabpanel-fragments" role="tabpanel" aria-labelledby="ledger-side-panel-tab-fragments" className="space-y-4">
                {unassignedFragments.length > 0 ? (
                  <div className="border border-black bg-gray-50 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                      {isReadOnlyAppliedAuditActive ? 'Unclaimed applied units' : 'Unassigned fragments'}
                    </div>
                    <div className="mt-2 text-xs leading-5 text-gray-600">
                      {isReadOnlyAppliedAuditActive
                        ? 'These units exist in the current witness segmentation but no collation row claims them yet. Audit mode keeps this panel read-only.'
                        : 'New sibling units are held out of the collation until you place them explicitly.'}
                    </div>
                    {!isReadOnlyAppliedAuditActive && (
                      <div className="mt-3 grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={() => void handleAutoPlaceFragments()}
                          disabled={isMatrixContentApplying || unassignedFragments.length === 0}
                          className="h-9 w-full border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white shadow-[2px_2px_0_#000] transition-all hover:bg-gray-800 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          {isLedgerApplying ? 'Placing…' : `Place all fragments (${unassignedFragments.length})`}
                        </button>
                        {fragmentReviewTargetRowIds.size > 0 && (
                          <button
                            type="button"
                            data-testid="ledger-fragments-panel-review-toggle"
                            onClick={() => setIsFragmentReviewMode(value => !value)}
                            className={`h-9 w-full border border-black px-3 text-[10px] font-bold uppercase tracking-widest shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none ${isFragmentReviewMode ? 'bg-sky-100 text-sky-900' : 'bg-white text-gray-700'}`}
                          >
                            {isFragmentReviewMode ? 'Show all ledger rows' : 'Review in ledger table'}
                          </button>
                        )}
                      </div>
                    )}
                    <div className="mt-3 space-y-3">
                      {unassignedFragments.map(fragment => {
                        const isArabic = hasArabicText(fragment.text)
                        const previousRow = fragment.previousRowId ? rowById.get(fragment.previousRowId) || null : null
                        const nextRow = fragment.nextRowId ? rowById.get(fragment.nextRowId) || null : null
                        const previousState = previousRow ? getLedgerSafeCellState(previousRow, fragment.witnessId) : null
                        const nextState = nextRow ? getLedgerSafeCellState(nextRow, fragment.witnessId) : null
                        const previousOccupied = previousState === 'mapped'
                        const nextOccupied = nextState === 'mapped'
                        const previousText = previousRow
                          ? getWorkspaceMappingText(previousRow, fragment.witnessId, witnessUnitTextLookupByWitnessId)
                          : ''
                        const nextText = nextRow
                          ? getWorkspaceMappingText(nextRow, fragment.witnessId, witnessUnitTextLookupByWitnessId)
                          : ''
                        return (
                          <div key={fragment.unitUuid} className="border border-black bg-white px-3 py-2">
                            <div className="flex items-start justify-between gap-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span>{fragment.unitLabel || fragment.unitUuid}</span>
                                  <span className={`border px-1.5 py-0.5 shadow-sm ${isReadOnlyAppliedAuditActive ? 'border-amber-300 bg-amber-100/80 text-amber-800' : 'border-sky-300 bg-sky-100/80 text-sky-800'}`}>
                                    {isReadOnlyAppliedAuditActive ? 'Unmapped applied unit' : 'Pending fragment'}
                                  </span>
                                </div>
                                <div className="text-[10px] font-medium normal-case tracking-normal text-gray-500">
                                  {isReadOnlyAppliedAuditActive
                                    ? 'Present in the witness segmentation, but not currently attached to any collation row.'
                                    : 'A newly created witness unit waiting for explicit collation placement.'}
                                </div>
                              </div>
                              <span>{previousRow ? getRowLabel(previousRow) : 'Start'} → {nextRow ? getRowLabel(nextRow) : 'End'}</span>
                            </div>
                            <div
                              className={`mt-2 text-sm ${isArabic ? 'font-reader-arabic text-right leading-8' : 'leading-6'}`}
                              dir={isArabic ? 'rtl' : 'ltr'}
                            >
                              {fragment.text}
                            </div>
                            {!isReadOnlyAppliedAuditActive && !fragment.previousRowId && !fragment.nextRowId && (
                              <button
                                type="button"
                                data-testid={`ledger-fragment-open-order-${fragment.unitUuid}`}
                                onClick={() => setSidePanelTab('sequence')}
                                className="mt-3 flex h-8 w-full items-center justify-center gap-2 border border-sky-700 bg-sky-50 px-2 text-[9px] font-bold uppercase tracking-[0.14em] text-sky-900 shadow-[2px_2px_0_#000] transition-all hover:bg-sky-100 active:translate-y-[2px] active:shadow-none"
                              >
                                <AnchorIcon className="h-3.5 w-3.5" strokeWidth={2.2} />
                                Open Order to reanchor
                              </button>
                            )}
                            <div className="mt-3 grid grid-cols-1 gap-2 text-[10px] text-gray-600">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="border border-black/10 bg-gray-50 px-2 py-1.5">
                                  <div className="font-bold uppercase tracking-widest text-gray-500">Row above</div>
                                  <div className="mt-1 font-semibold text-black">{previousRow ? getRowLabel(previousRow) : 'Start'}</div>
                                  <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">{getFragmentPlacementHint(previousState)}</div>
                                  <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-600">{previousText || 'No mapped unit in this witness'}</div>
                                </div>
                                <div className="border border-black/10 bg-gray-50 px-2 py-1.5">
                                  <div className="font-bold uppercase tracking-widest text-gray-500">Row below</div>
                                  <div className="mt-1 font-semibold text-black">{nextRow ? getRowLabel(nextRow) : 'End'}</div>
                                  <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-500">{getFragmentPlacementHint(nextState)}</div>
                                  <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-600">{nextText || 'No mapped unit in this witness'}</div>
                                </div>
                              </div>
                            </div>
                            {isReadOnlyAppliedAuditActive ? (
                              <div className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] leading-4 text-amber-800">
                                Audit mode is read-only. Use the placement controls after leaving the audit if you want to turn this unit into a collation action.
                              </div>
                            ) : (
                              <>
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void handleFragmentPlacement({ fragment, mode: 'insert-after-previous' })}
                                    disabled={isMatrixContentApplying || !fragment.previousRowId}
                                    className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                  >
                                    {previousRow ? `New row below ${getRowLabel(previousRow)}` : 'New row below'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleFragmentPlacement({ fragment, mode: 'insert-before-next' })}
                                    disabled={isMatrixContentApplying || !fragment.nextRowId}
                                    className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                  >
                                    {nextRow ? `New row above ${getRowLabel(nextRow)}` : 'New row above'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleFragmentPlacement({ fragment, mode: 'place-in-previous' })}
                                    disabled={isMatrixContentApplying || !fragment.previousRowId || previousOccupied}
                                    className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                  >
                                    {getFragmentPlacementLabel(previousRow, previousState, 'Place above')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleFragmentPlacement({ fragment, mode: 'place-in-next' })}
                                    disabled={isMatrixContentApplying || !fragment.nextRowId || nextOccupied}
                                    className="h-9 border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                                  >
                                    {getFragmentPlacementLabel(nextRow, nextState, 'Place below')}
                                  </button>
                                </div>
                                <div className="mt-2 text-[10px] leading-4 text-gray-500">
                                  {previousOccupied || nextOccupied
                                    ? 'Occupied rows cannot accept this fragment. Empty rows and deliberate gaps can be filled directly.'
                                    : 'Choose whether to fill a nearby empty/gap row or materialize this fragment as a new row.'}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 bg-white p-4 text-xs text-gray-500">
                    {isReadOnlyAppliedAuditActive
                      ? 'No unclaimed applied units. Every current witness unit is already attached to a collation row.'
                      : 'No unassigned fragments. Split operations that create a new sibling will land here until you place them.'}
                  </div>
                )}
              </div>
            )}

            {sidePanelTab === 'log' && (
              <div id="ledger-side-panel-tabpanel-log" role="tabpanel" aria-labelledby="ledger-side-panel-tab-log" className="space-y-4">
                <div className="border border-black bg-gray-50 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Collation safeguards</div>
                  <div className="mt-2 text-sm font-semibold text-black">Backups</div>
                  <div className="mt-2 text-xs leading-5 text-gray-600">
                    {runtimeEnableSnapshots
                      ? 'Backups are created before structural collation changes. Empty-row compaction is now handled inline on the rows themselves.'
                      : 'Collation snapshots are disabled for this deployment. Current ledger states remain persistent; database backups provide recovery.'}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AppStateBackupControls
                      exportLabel="Backup App State"
                      containerClassName="flex flex-wrap gap-2"
                      exportButtonClassName="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      onStatus={status => showToast(status.message)}
                    />
                    {runtimeEnableSnapshots && (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleCreateLedgerBackup()}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                        >
                          Snapshot Collation
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRestoreLatestLedgerSnapshot()}
                          disabled={isMatrixContentApplying || !latestLedgerSnapshot}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Restore Latest Backup
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleReplayGeneratedWitness()}
                          disabled={isMatrixContentApplying || isWitnessReplayApplying || !activeWitness || !hasBuildImportRows}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          {isWitnessReplayApplying ? 'Replaying…' : `Replay ${activeWitness?.sigla || 'Witness'}`}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleAttachSceneNavigation()}
                          disabled={isMatrixContentApplying || isSceneBackfillApplying || !canAttachSceneMetadata}
                          title={sceneBackfillReadinessText}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          {isSceneBackfillApplying ? 'Attaching…' : 'Attach Scene Nav'}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleUndoSceneNavigationBackfill()}
                          disabled={isMatrixContentApplying || isSceneBackfillApplying || !sceneBackfillUndo}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Undo Scene Nav
                        </button>
                      </>
                    )}
                  </div>
                  {runtimeEnableSnapshots && (
                    <>
                      <div className="mt-3 text-[10px] text-gray-500">
                        {latestLedgerSnapshot
                          ? `Latest backup: ${latestLedgerSnapshot.label} · ${new Date(latestLedgerSnapshot.createdAt).toLocaleString()}`
                          : 'No backup recorded for this chapter view yet.'}
                      </div>
                      <div className="mt-2 text-[10px] text-gray-500">
                        Replay restores the active witness from the latest saved staged build without rerunning the model.
                      </div>
                      <div className="mt-2 text-[10px] leading-4 text-gray-500">
                        {sceneBackfillReadinessText}
                        {sceneBackfillUndo
                          ? ' Last scene attachment can be undone in this view.'
                          : ''}
                      </div>
                    </>
                  )}
                </div>
                {renderOracleExportCard()}
                <div className="border border-black bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Editorial journal</div>
                      <div className="mt-2 text-sm font-semibold text-black">Persisted local edit events</div>
                    </div>
                    {IS_LEDGER_SAFE_DEBUG_EXPORT_ENABLED && (
                      <button
                        type="button"
                        onClick={handleExportEditorialDebug}
                        className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none"
                      >
                        Export debug JSON
                      </button>
                    )}
                  </div>
                  <div className="mt-2 text-xs leading-5 text-gray-600">
                    Every applied Variorum edit is recorded as a canonical operation with an agent-tool projection when one exists.
                  </div>
                  <div className="mt-3 space-y-2">
                    {editorialEvents.length === 0 ? (
                      <div className="text-xs text-gray-500">No persisted editorial actions yet.</div>
                    ) : editorialEvents.filter(Boolean).map(event => (
                      <div key={event.id} className="border border-black bg-white px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{event.operation?.kind || 'unknown'}</div>
                          <div className="text-[10px] text-gray-500">{new Date(event.createdAt).toLocaleString()}</div>
                        </div>
                        <div className="mt-1 text-xs leading-5 text-gray-700">{event.summary}</div>
                        <div className="mt-1 text-[10px] text-gray-500">
                          {event.toolCallProjection
                            ? `Tool projection: ${event.toolCallProjection.toolName}`
                            : 'Tool projection: manual-only'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {(runtimeEnablePiDock || piReviewLog.length > 0) && (
                  <div className="border border-black bg-gray-50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Pi review log</div>
                        <div className="mt-2 text-sm font-semibold text-black">
                          Snapshot-scoped actions
                        </div>
                      </div>
                      {(piFocusedWindow || piPreview) && (
                        <button
                          type="button"
                          onClick={() => {
                            setPiFocusedWindow(null)
                            setPiPreview(null)
                            setPiPreviewEnvelope(null)
                            setPiPreviewSummary('')
                          }}
                          disabled={isMatrixContentApplying}
                          className="h-8 border border-black bg-white px-2 text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[2px_2px_0_#000] transition-all hover:bg-gray-100 active:translate-y-[2px] active:shadow-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Clear review
                        </button>
                      )}
                    </div>
                    <div className="mt-2 text-xs leading-5 text-gray-600">
                      Pi previews stay patch-based. Nothing mutates until you apply through the local editor path.
                    </div>
                    <div className="mt-3 space-y-2">
                      {piReviewLog.length === 0 ? (
                        <div className="text-xs text-gray-500">No Pi review activity yet.</div>
                      ) : piReviewLog.map(entry => (
                        <div key={entry.id} className="border border-black bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{entry.kind}</div>
                          <div className="mt-1 text-xs leading-5 text-gray-700">{entry.message}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {toastMessage && (
            <div data-testid="ledger-safe-toast" className="shrink-0 border-t border-black bg-white p-4">
              <div className="flex items-center justify-between gap-3 border border-black bg-black px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-white shadow-[2px_2px_0_#000]">
                <span className="min-w-0 whitespace-normal break-words">{toastMessage.message}</span>
                {toastMessage.action ? (
                  <button
                    type="button"
                    disabled={isMatrixContentApplying}
                    onClick={() => {
                      const action = toastMessage.action
                      if (!action) {
                        return
                      }
                      setToastMessage(null)
                      action.run()
                    }}
                    className="h-6 shrink-0 border border-white bg-black px-2 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-white hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:border-gray-500 disabled:text-gray-500"
                  >
                    {toastMessage.action.label}
                  </button>
                ) : null}
              </div>
            </div>
          )}
            </>
          )}
        </aside>

        {runtimeEnablePiDock && !readOnly && (
          <section
            data-testid="ledger-safe-pi-dock"
            aria-label="Pi dock"
            className="absolute bottom-6 left-1/2 z-10 flex w-[clamp(420px,36%,560px)] -translate-x-1/2 flex-col border border-black bg-[#fffdfa] shadow-[4px_4px_0_#000]"
            style={isPiDockExpanded ? { height: piDockHeight } : undefined}
          >
        {isPiDockExpanded && (
          <>
            <button
              type="button"
              aria-label="Resize Pi dock"
              onMouseDown={handlePiDockResizeStart}
              className="block h-1.5 w-full shrink-0 cursor-ns-resize border-b border-black/10 bg-[linear-gradient(transparent_2px,rgba(0,0,0,0.30)_2px,rgba(0,0,0,0.30)_3px,transparent_3px)]"
            />
            {(piOAuthLoginAttempt || piAgentReply || piPreview || piAgentChangeSet?.status === 'applied' || piError) && (
              <div className="max-h-24 space-y-2 overflow-y-auto border-b border-black/10 bg-[#fffdfa] px-3 py-2">
                {piOAuthLoginAttempt && (
                  <div className="grid grid-cols-[32px_1fr] gap-2 text-xs leading-5">
                    <span className="pt-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">Auth</span>
                    <div className="border-l border-black/10 pl-2 text-slate-700">
                      <div><span className="font-semibold text-black">ChatGPT auth</span> · {piOAuthLoginAttempt.providerName} · {piOAuthLoginAttempt.status.replace(/_/g, ' ')}</div>
                      {(piOAuthLoginAttempt.instructions || piOAuthLoginAttempt.authUrl) && (
                        <div className="mt-1">{piOAuthLoginAttempt.instructions || 'Complete the ChatGPT login in the opened window.'}</div>
                      )}
                      {piOAuthLoginAttempt.authUrl && (
                        <a href={piOAuthLoginAttempt.authUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-[11px] font-semibold text-blue-700 underline underline-offset-2">
                          Open ChatGPT login
                        </a>
                      )}
                      {piOAuthLoginAttempt.manualCodeSupported && piOAuthLoginAttempt.status !== 'authenticated' && piOAuthLoginAttempt.status !== 'failed' && (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            aria-label="Paste callback URL or authorization code"
                            value={piOAuthLoginInput}
                            onChange={event => setPiOAuthLoginInput(event.target.value)}
                            className="h-8 min-w-0 flex-1 border border-black bg-white px-2 font-mono text-[11px] text-black"
                            placeholder={piOAuthLoginAttempt.prompt || 'http://localhost:1455/auth/callback?code=…'}
                          />
                          <button
                            type="button"
                            onClick={() => void handlePiSubmitOAuthLoginInput()}
                            disabled={isPiAuthBusy || !piOAuthLoginInput.trim()}
                            className="h-8 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-widest text-white shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                          >
                            Submit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {piAgentReply && (
                  <div className="grid grid-cols-[32px_1fr] gap-2 text-xs leading-5">
                    <span className="pt-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">Π</span>
                    <div className="border-l border-black/10 pl-2 text-slate-800">
                      <div className="whitespace-pre-wrap">{piAgentReply.text || 'No text returned.'}</div>
                      {piAgentReply.toolCalls.length > 0 && (
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                          Tool calls ({piAgentReply.toolCalls.length}): {piAgentReply.toolCalls.map(toolCall => toolCall.toolName).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {piPreview && (
                  <div className="grid grid-cols-[32px_1fr] gap-2 text-xs leading-5">
                    <span className="pt-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-blue-600">Pre</span>
                    <div className="flex items-center justify-between gap-3 border-l border-blue-600 bg-blue-50/40 px-2 py-1 text-slate-800">
                      <div className="min-w-0">
                        <div className="truncate">{piPreviewSummary || piPreview.summary || 'Preview ready'}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                          {piPreview.operation.kind} · {piPreview.affectedRowIds.map(rowId => {
                            const row = rowById.get(rowId)
                            return row ? getRowLabel(row) : 'unavailable row'
                          }).join(', ')}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleFocusPiWindow({
                            rowStart: Math.min(...piPreview.affectedRowIds.map(rowId => rowById.get(rowId)?.position || Number.MAX_SAFE_INTEGER)),
                            rowEnd: Math.max(...piPreview.affectedRowIds.map(rowId => rowById.get(rowId)?.position || 0)),
                            rowIds: piPreview.affectedRowIds,
                            affectedWitnessIds: piPreview.affectedWitnessIds
                          }, 'preview')}
                          disabled={isMatrixContentApplying}
                          className="h-6 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Inspect
                        </button>
                        <button
                          type="button"
                          aria-label="Apply Pi preview"
                          onClick={() => void handleApplyPiPreview()}
                          disabled={isMatrixContentApplying}
                          className="h-6 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-widest text-white shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          aria-label="Reject preview"
                          onClick={handleRejectPiPreview}
                          disabled={isMatrixContentApplying}
                          className="h-6 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {piAgentChangeSet?.status === 'applied' && (
                  <div className="grid grid-cols-[32px_1fr] gap-2 text-xs leading-5">
                    <span className="pt-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-700">Done</span>
                    <div className="flex items-center justify-between gap-3 border-l border-emerald-700 bg-emerald-50/40 px-2 py-1 text-slate-800">
                      <div className="min-w-0">
                        <div>{`${piAgentChangeSet.steps.length} Pi edit${piAgentChangeSet.steps.length === 1 ? '' : 's'} applied as one sequence`}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Ledger transaction · recoverable</div>
                      </div>
                      <button
                        type="button"
                        aria-label="Undo Pi edit sequence"
                        onClick={() => void handleUndoPiAgentChangeSet()}
                        disabled={isPiBusy}
                        className="h-6 border border-black bg-white px-2 font-mono text-[9px] font-bold uppercase tracking-widest text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:shadow-none"
                      >
                        Undo
                      </button>
                    </div>
                  </div>
                )}
                {piError && (
                  <div className="grid grid-cols-[32px_1fr] gap-2 text-xs leading-5">
                    <span className="pt-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-red-700">Err</span>
                    <div className="border-l border-red-300 bg-red-50 px-2 py-1 text-red-700">{piError}</div>
                  </div>
                )}
              </div>
            )}

            <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-end gap-2 border-b border-black/10 px-3 py-2.5">
              <textarea
                ref={piDockInputRef}
                id="ledger-safe-pi-prompt"
                aria-label="Pi prompt"
                value={piAgentPrompt}
                onChange={event => setPiAgentPrompt(event.target.value)}
                onKeyDown={event => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault()
                    void handlePiPromptAgent()
                  } else if (event.key === 'Escape') {
                    event.currentTarget.blur()
                  }
                }}
                className="min-h-[60px] max-h-[120px] w-full resize-y border border-black bg-[#fffdfa] px-3 py-2 font-mono text-xs leading-5 text-black shadow-[2px_2px_0_#000] placeholder:text-slate-500"
                placeholder={'Ask Pi about this edit, or paste an instruction…\nCmd/Ctrl + Enter to send · Cmd/Ctrl + K to focus from anywhere'}
              />
              <button
                type="button"
                aria-label="Send to Pi"
                onClick={() => void handlePiPromptAgent()}
                disabled={isPiBusy || !piSessionId || !piAgentSessionId || !piAgentPrompt.trim()}
                className="h-8 border border-black bg-black px-4 font-mono text-[10px] font-bold uppercase tracking-[0.20em] text-white shadow-[2px_2px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
              >
                Send
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  aria-label="Sync Pi session"
                  onClick={() => void handlePiSyncSession()}
                  disabled={isPiBusy}
                  className="h-6 border border-black bg-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  Sync
                </button>
                <button
                  type="button"
                  aria-label="Propose current edit with Pi"
                  onClick={() => void handlePiPreviewCurrentEdit()}
                  disabled={isPiBusy || !canPreviewCurrentEditWithPi}
                  className="h-6 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                >
                  Propose
                </button>
                <button
                  type="button"
                  aria-label="Start Pi agent"
                  onClick={() => void handlePiStartAgent()}
                  disabled={isPiBusy || !piSessionId}
                  className="h-6 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                >
                  Start
                </button>
              </div>
              <div className="relative flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Pi settings"
                  aria-expanded={isPiSettingsOpen}
                  onClick={() => setIsPiSettingsOpen(value => !value)}
                  className={`h-6 border border-black px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] shadow-[1px_1px_0_#000] ${isPiSettingsOpen ? 'bg-black text-white' : 'bg-[#fffdfa] text-gray-700'}`}
                >
                  ⚙
                </button>
                <button
                  type="button"
                  aria-label="Login with ChatGPT"
                  onClick={() => void handlePiLoginWithChatGPT()}
                  disabled={isPiBusy || isPiAuthBusy}
                  className="h-6 border border-black bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700 shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
                >
                  {piDockAuthStored ? 'Re-auth' : 'Login'}
                </button>
                {isPiSettingsOpen && (
                  <div role="dialog" aria-label="Pi settings" className="absolute bottom-9 right-0 z-[140] w-[320px] border border-black bg-[#fffdfa] p-3 shadow-[4px_4px_0_#000]">
                    <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.20em] text-black">Pi · Settings</div>
                    <div className="space-y-2 text-xs text-slate-700">
                      <label className="flex items-center justify-between gap-2 border-b border-dashed border-black/20 pb-2">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Provider</span>
                        <input
                          value={piAgentModelProvider}
                          onChange={event => setPiAgentModelProvider(event.target.value)}
                          className="h-8 w-36 border border-black bg-white px-2 font-mono text-[11px] text-black"
                        />
                      </label>
                      <label className="flex items-center justify-between gap-2 border-b border-dashed border-black/20 pb-2">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Model</span>
                        <input
                          value={piAgentModelId}
                          onChange={event => setPiAgentModelId(event.target.value)}
                          className="h-8 w-36 border border-black bg-white px-2 font-mono text-[11px] text-black"
                        />
                      </label>
                      <div className="flex items-center justify-between gap-2 border-b border-dashed border-black/20 pb-2">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Auth</span>
                        <span className="font-mono text-[10px]">{piDockAuthLabel}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 border-b border-dashed border-black/20 pb-2">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Session</span>
                        <span className="font-mono text-[10px]">{piAgentSessionId || piSessionId || 'not started'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Snapshot</span>
                        <span className="font-mono text-[10px]">{piSnapshotId || 'unsynced'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <div
          data-testid="ledger-safe-pi-dock-status"
          className={`flex min-h-[38px] items-center justify-between gap-3 bg-[#fafaf8] px-3.5 py-2 ${isPiDockExpanded ? 'border-t border-black/10' : ''}`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <span className={`inline-grid h-[22px] w-[22px] shrink-0 place-items-center border font-serif text-[13px] leading-none ${piDockStateColorClass}`}>
              Π
            </span>
            <span className={`shrink-0 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${piDockStateColorClass.replace('border-', 'text-').split(' ').filter(part => part.startsWith('text-')).join(' ') || 'text-slate-600'}`}>
              {piDockStateLabel}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs text-slate-900">
              {piCurrentEditSummary}
              {piPreview ? ' · Pi preview staged' : piAgentReply ? ' · latest reply ready' : ' · no preview staged'}
            </span>
            {piSessionId && (
              <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500 lg:inline">
                Session {piSessionId.slice(0, 12)} · {piSnapshotId || 'unsynced'}
              </span>
            )}
            {piAgentSessionId && (
              <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500 xl:inline">
                Agent {piAgentSessionId}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setIsPiDockExpanded(true)
                setIsPiSettingsOpen(value => !value)
              }}
              className="h-[22px] border border-black/30 bg-[#fafaf8] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700"
            >
              {piDockModelLabel} ⌄
            </button>
            <button
              type="button"
              onClick={() => {
                setIsPiDockExpanded(true)
                setIsPiSettingsOpen(value => !value)
              }}
              className="h-[22px] border border-black/30 bg-[#fffdfa] px-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-gray-700"
            >
              {piDockAuthLabel}
            </button>
            <button
              type="button"
              aria-label={isPiDockExpanded ? 'Collapse Pi dock' : 'Expand Pi dock'}
              onClick={() => setIsPiDockExpanded(value => !value)}
              className="h-[22px] border border-black/30 bg-[#fffdfa] px-2 font-mono text-[11px] font-bold text-gray-700"
            >
              {isPiDockExpanded ? '∧' : '∨'}
            </button>
          </div>
        </div>
          </section>
        )}
        {runtimeEnableNewWitnessButton && !readOnly && ledgerAreaFloatingAction && (
        <div
          data-testid="ledger-area-floating-action"
          className="pointer-events-none absolute bottom-6 right-[calc(390px+24px)] z-20 flex items-center"
        >
          {ledgerAreaFloatingAction}
        </div>
      )}
      </div>
      )}
    </div>
  )
}
